import * as i0 from '@angular/core';
import { inject, NgZone, PLATFORM_ID, Injectable, ElementRef, Directive, ChangeDetectorRef, HostBinding, ContentChildren, ContentChild, Input, NgModule, Pipe, Injector, EventEmitter, Component, Output, ChangeDetectionStrategy, ViewChild, ViewChildren, HostListener, InjectionToken, Renderer2, ApplicationRef, EnvironmentInjector, createComponent, ViewContainerRef, QueryList, LOCALE_ID, SimpleChange, TemplateRef, forwardRef } from '@angular/core';
import { shareReplay, startWith, switchMap, map, delay, tap, take, takeUntil, throttleTime, first, filter, skip, debounceTime, retry, distinctUntilChanged, endWith, repeatWhen, share, observeOn } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule, LocationStrategy, DOCUMENT, getLocaleDayPeriods, FormStyle, TranslationWidth, getLocaleId, getLocaleDateTimeFormat, FormatWidth, getLocaleTimeFormat, getLocaleDateFormat, formatDate, getLocaleDayNames, getLocaleMonthNames, getLocaleFirstDayOfWeek, WeekDay } from '@angular/common';
import { Observable, of, combineLatest, NEVER, Subject, merge, fromEvent, ReplaySubject, Subscription, BehaviorSubject, timer, isObservable, asyncScheduler, throwError, EMPTY } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AnimationBuilder, style, animate, trigger, transition, query } from '@angular/animations';
import { Router, ActivatedRoute, NavigationEnd, RouterModule, RouterLinkActive } from '@angular/router';
import * as i2 from '@angular/forms';
import { NG_VALIDATORS, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, NG_ASYNC_VALIDATORS, NgControl, FormGroup, FormControl } from '@angular/forms';
import { __decorate } from 'tslib';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse, HttpEventType, HttpParams } from '@angular/common/http';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import * as i1$1 from '@angular/cdk/a11y';
import { FocusKeyManager, A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import * as i3$1 from '@angular/cdk/overlay';
import { OverlayConfig, Overlay, OverlayModule, CdkOverlayOrigin } from '@angular/cdk/overlay';
import * as i1$3 from '@angular/cdk/portal';
import { ComponentPortal, PortalModule, CdkPortalOutlet } from '@angular/cdk/portal';
import * as i3 from '@angular/cdk/drag-drop';
import { moveItemInArray, DragDropModule, CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import * as i1$2 from '@angular/cdk/listbox';
import { CdkOption, CdkListboxModule, CdkListbox } from '@angular/cdk/listbox';

/**
 * A service wrapping `ResizeObserver`. This is a service for those reasons:
 * - only one `ResizeObserver` should be used for performance reason.
 * - For Angular change detection to work, explicity `ngZone` calls are necessary
 * - Observable stream
 */
class ResizeObserverService {
    constructor() {
        this.listeners = new Map();
        this.timerQueue = new Map();
        this.zone = inject(NgZone);
        const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        if (!isBrowser || !ResizeObserver) {
            return;
        }
        this.resizeObserver = new ResizeObserver((entries) => entries.forEach(entry => this.handleElement(entry.target)));
    }
    /**
     * Observe the size of an element. Returns an observable with the changes.
     * @param element The element to observe
     * @param throttle Throttle time in ms. Will emit this time after the resize
     * @param emitInitial Emit the initial size after subscribe?
     * @param emitImmediate Emit an event immediately after the size changes. Useful e.g. for visibility checks.
     */
    observe(element, throttle, emitInitial, emitImmediate) {
        let entry = this.listeners.get(element);
        if (!entry) {
            entry = { element, subscribers: [] };
            this.listeners.set(element, entry);
        }
        return new Observable(subscriber => {
            const sub = {
                sub: subscriber,
                dim: undefined,
                throttle,
                blocked: false,
                emitImmediate
            };
            this.subscriberAdded(entry, sub, emitInitial);
            return () => this.subscriberRemoved(entry, sub);
        });
    }
    subscriberAdded(entry, subscriber, emitInitial) {
        entry.subscribers.push(subscriber);
        if (entry.subscribers.length === 1) {
            this.resizeObserver?.observe(entry.element);
        }
        if (emitInitial) {
            this.schedule(0, entry.element, subscriber, false, true);
        }
    }
    subscriberRemoved(entry, subscriber) {
        const index = entry.subscribers.indexOf(subscriber);
        if (index >= 0) {
            entry.subscribers.splice(index, 1);
        }
        if (entry.subscribers.length === 0) {
            // no more subscribers, tear down everything
            this.resizeObserver?.unobserve(entry.element);
            this.listeners.delete(entry.element);
        }
        this.unschedule(subscriber);
        // close down, no re-subscription possible
        subscriber.sub.complete();
    }
    handleElement(element) {
        const entry = this.listeners.get(element);
        if (!entry) {
            this.resizeObserver?.unobserve(element);
            return;
        }
        entry.subscribers.forEach(sub => this.handleResizeSubscriber(element, sub));
    }
    handleResizeSubscriber(element, entry) {
        if (entry.blocked) {
            return;
        }
        if (entry.emitImmediate) {
            this.schedule(0, element, entry, false);
        }
        this.schedule(entry.throttle, element, entry, true);
    }
    emitSize(element, entry, force = false) {
        const dimensions = { width: element.clientWidth, height: element.clientHeight };
        if (!force && entry.dim?.width === dimensions.width && entry.dim?.height === dimensions.height) {
            // Prevent spurious emissions. Subpixels and all..
            return;
        }
        entry.dim = dimensions;
        entry.sub.next(dimensions);
    }
    schedule(timeout, element, subscriber, unblock, force = false) {
        if (unblock) {
            subscriber.blocked = true;
        }
        let queue = this.timerQueue.get(timeout);
        if (!queue) {
            queue = [];
            this.timerQueue.set(timeout, queue);
            setTimeout(() => {
                this.timerQueue.delete(timeout);
                this.processQueue(queue);
            }, timeout);
        }
        queue.push({ element, subscriber, unblock, force });
    }
    unschedule(entry) {
        const queued = this.timerQueue.get(entry.throttle);
        if (queued) {
            const index = queued.findIndex(q => q.subscriber === entry);
            if (index > -1) {
                queued.splice(index, 1);
            }
        }
    }
    processQueue(queue) {
        this.zone.run(() => {
            queue?.forEach(q => {
                if (q.unblock) {
                    q.subscriber.blocked = false;
                }
                this.emitSize(q.element, q.subscriber, q.force);
            });
        });
    }
    /**
     * check size on all observed elements. Only use in testing!
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _checkAll() {
        this.listeners.forEach(entry => this.handleElement(entry.element));
    }
}
ResizeObserverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ResizeObserverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ResizeObserverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ResizeObserverService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ResizeObserverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class SiAutoCollapsableListMeasurable {
    constructor() {
        this.elementRef = inject((ElementRef));
        this.resizeObserverService = inject(ResizeObserverService);
    }
    ngAfterViewInit() {
        this.size$ = this.resizeObserverService.observe(this.elementRef.nativeElement, 100, true).pipe(shareReplay(1));
    }
}
SiAutoCollapsableListMeasurable.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListMeasurable, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiAutoCollapsableListMeasurable.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAutoCollapsableListMeasurable, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListMeasurable, decorators: [{
            type: Directive
        }] });

class SiAutoCollapsableListItemDirective extends SiAutoCollapsableListMeasurable {
    constructor() {
        super(...arguments);
        this.visible = true;
        this.initialized = false;
        this.changeDetectorRef = inject(ChangeDetectorRef);
    }
    get visibility() {
        return (this.visible && this.initialized) ? 'visible' : 'hidden';
    }
    get position() {
        return (this.visible && this.initialized) ? '' : 'absolute';
    }
    // ensure hidden items are behind the visible ones. Otherwise, the visible ones are not clickable
    get zIndex() {
        return (this.visible && this.initialized) ? '' : '-1';
    }
    ngAfterViewInit() {
        if (getComputedStyle(this.elementRef.nativeElement).display === 'inline') {
            console.error('siAutoCollapsibleListItem does not work on items with display="inline"');
        }
        super.ngAfterViewInit();
        setTimeout(() => {
            this.initialized = true;
            this.changeDetectorRef.markForCheck();
        });
    }
}
SiAutoCollapsableListItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListItemDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiAutoCollapsableListItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAutoCollapsableListItemDirective, selector: "[siAutoCollapsableListItem]", host: { properties: { "style.visibility": "this.visibility", "style.position": "this.position", "style.z-index": "this.zIndex" } }, exportAs: ["siAutoCollapsableListItem"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListItem]',
                    exportAs: 'siAutoCollapsableListItem'
                }]
        }], propDecorators: { visibility: [{
                type: HostBinding,
                args: ['style.visibility']
            }], position: [{
                type: HostBinding,
                args: ['style.position']
            }], zIndex: [{
                type: HostBinding,
                args: ['style.z-index']
            }] } });

class SiAutoCollapsableListAdditionalContentDirective extends SiAutoCollapsableListMeasurable {
}
SiAutoCollapsableListAdditionalContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListAdditionalContentDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiAutoCollapsableListAdditionalContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAutoCollapsableListAdditionalContentDirective, selector: "[siAutoCollapsableListAdditionalContent]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListAdditionalContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListAdditionalContent]'
                }]
        }] });

class SiAutoCollapsableListOverflowItemDirective extends SiAutoCollapsableListItemDirective {
    constructor() {
        super(...arguments);
        this.hiddenItemCount = 0;
    }
}
SiAutoCollapsableListOverflowItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListOverflowItemDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiAutoCollapsableListOverflowItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAutoCollapsableListOverflowItemDirective, selector: "[siAutoCollapsableListOverflowItem]", exportAs: ["siAutoCollapsableListOverflowItem"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListOverflowItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListOverflowItem]',
                    exportAs: 'siAutoCollapsableListOverflowItem'
                }]
        }] });

class SiAutoCollapsableListDirective {
    constructor() {
        this._siAutoCollapsableList = true;
        this.elementRef = inject(ElementRef);
        this.resizeObserverService = inject(ResizeObserverService);
        this.changeDetectorRef = inject(ChangeDetectorRef);
    }
    get overflow() {
        return this._siAutoCollapsableList ? 'hidden' : '';
    }
    get siAutoCollapsableList() {
        return this._siAutoCollapsableList;
    }
    set siAutoCollapsableList(value) {
        this._siAutoCollapsableList = coerceBooleanProperty(value);
        if (!this._siAutoCollapsableList && this.resizeSubscription) {
            this.reset();
            this.resizeSubscription.unsubscribe();
            this.resizeSubscription = undefined;
        }
        else if (this._siAutoCollapsableList && !this.resizeSubscription && this.items) {
            this.setupResizeListener();
        }
    }
    ngAfterViewInit() {
        if (this.siAutoCollapsableList) {
            this.setupResizeListener();
        }
        else {
            this.reset();
        }
    }
    ngOnDestroy() {
        this.resizeSubscription?.unsubscribe();
    }
    setupResizeListener() {
        const containerSize$ = this.resizeObserverService.observe(this.elementRef.nativeElement, 100, true);
        const itemSizes$ = this.items.changes.pipe(startWith(this.items), switchMap((items) => !items.length ? of([]) : combineLatest(items.map(item => item.size$.pipe(map(({ width: size }) => ({
            size,
            directive: item
        })))))));
        const additionalContentSizes$ = this.additionalContent.changes.pipe(startWith(this.additionalContent), switchMap((additionalContent) => combineLatest(additionalContent.map(item => item.size$.pipe(map(({ width: size }) => size))))), startWith([]));
        this.resizeSubscription = combineLatest([containerSize$, this.overflowItem?.size$ ?? of({
                width: 0,
                height: 0
            }), itemSizes$, additionalContentSizes$])
            .subscribe(([containerSize, overflowItemSize, items, additionalContentSizes]) => setTimeout(() => this.updateItemVisibility(containerSize.width, overflowItemSize.width, items, additionalContentSizes)));
    }
    updateItemVisibility(size, overflowSize, items, additionalContent) {
        let remainingSpace = size - additionalContent.reduce((a, b) => a + b, 0);
        const itemsCopy = items.slice();
        while (remainingSpace >= 0 && itemsCopy.length) {
            const item = itemsCopy.shift();
            if (remainingSpace - item.size - overflowSize >= 0) {
                item.directive.visible = true;
                remainingSpace -= item.size;
            }
            else if (!itemsCopy.length && remainingSpace - item.size >= 0) {
                item.directive.visible = true;
                remainingSpace = 0;
            }
            else {
                remainingSpace = 0;
                item.directive.visible = false;
            }
        }
        itemsCopy.forEach(item => item.directive.visible = false);
        if (this.overflowItem) {
            this.overflowItem.hiddenItemCount = this.items.filter(item => !item.visible).length;
            this.overflowItem.visible = this.overflowItem.hiddenItemCount !== 0;
        }
        this.changeDetectorRef.markForCheck();
    }
    reset() {
        this.items.forEach(item => item.visible = true);
        if (this.overflowItem) {
            this.overflowItem.visible = false;
            this.overflowItem.hiddenItemCount = 0;
        }
        this.changeDetectorRef.markForCheck();
    }
}
SiAutoCollapsableListDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiAutoCollapsableListDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAutoCollapsableListDirective, selector: "[siAutoCollapsableList]", inputs: { siAutoCollapsableList: "siAutoCollapsableList" }, host: { properties: { "style.overflow": "this.overflow" } }, queries: [{ propertyName: "overflowItem", first: true, predicate: SiAutoCollapsableListOverflowItemDirective, descendants: true }, { propertyName: "items", predicate: SiAutoCollapsableListItemDirective }, { propertyName: "additionalContent", predicate: SiAutoCollapsableListAdditionalContentDirective }], exportAs: ["siAutoCollapsableList"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableList]',
                    exportAs: 'siAutoCollapsableList'
                }]
        }], propDecorators: { items: [{
                type: ContentChildren,
                args: [SiAutoCollapsableListItemDirective]
            }], overflowItem: [{
                type: ContentChild,
                args: [SiAutoCollapsableListOverflowItemDirective]
            }], additionalContent: [{
                type: ContentChildren,
                args: [SiAutoCollapsableListAdditionalContentDirective]
            }], overflow: [{
                type: HostBinding,
                args: ['style.overflow']
            }], siAutoCollapsableList: [{
                type: Input
            }] } });

class SiAutoCollapsableListModule {
}
SiAutoCollapsableListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiAutoCollapsableListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListModule, declarations: [SiAutoCollapsableListAdditionalContentDirective,
        SiAutoCollapsableListDirective,
        SiAutoCollapsableListItemDirective,
        SiAutoCollapsableListOverflowItemDirective], imports: [CommonModule], exports: [SiAutoCollapsableListAdditionalContentDirective,
        SiAutoCollapsableListDirective,
        SiAutoCollapsableListItemDirective,
        SiAutoCollapsableListOverflowItemDirective] });
SiAutoCollapsableListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAutoCollapsableListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiAutoCollapsableListAdditionalContentDirective,
                        SiAutoCollapsableListDirective,
                        SiAutoCollapsableListItemDirective,
                        SiAutoCollapsableListOverflowItemDirective
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        SiAutoCollapsableListAdditionalContentDirective,
                        SiAutoCollapsableListDirective,
                        SiAutoCollapsableListItemDirective,
                        SiAutoCollapsableListOverflowItemDirective
                    ]
                }]
        }] });

/**
 * @returns The culture language code of the browser, e.g. "en-US".
 */
const getBrowserCultureLanguage = () => window?.navigator?.languages?.[0] ?? window?.navigator?.language;
/**
 * @returns The language code of the browser, e.g. "en".
 */
const getBrowserLanguage = () => getBrowserCultureLanguage()?.split(/-|_/)[0];
/**
 * Wrapper around an actual translation framework which is meant to be used internally by Element.
 * Applications must not use this service.
 *
 * @internal
 */
class SiTranslateService {
}
SiTranslateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTranslateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateService, decorators: [{
            type: Injectable
        }] });

const arrayToRecord = (keys) => keys.reduce((acc, cur) => {
    acc[cur] = cur;
    return acc;
}, {});
/**
 * Pass through implementation of the {@link SiTranslateService} which is used as a default.
 *
 * @internal
 */
class SiNoTranslateService extends SiTranslateService {
    constructor() {
        super(...arguments);
        this.translationChange = NEVER;
    }
    get currentLanguage() {
        return 'en';
    }
    get availableLanguages() {
        return ['en'];
    }
    set availableLanguages(_lang) { }
    setCurrentLanguage(_lang) {
        return of();
    }
    getDefaultLanguage() {
        return 'en';
    }
    setDefaultLanguage(_lang) { }
    translate(keys, _params) {
        // "as any" due to Typescript not being able to handle type guards in combination with generics
        return typeof keys === 'string' ? keys : arrayToRecord(keys);
    }
    translateSync(keys, params) {
        return this.translate(keys, params);
    }
    translateAsync(keys, params) {
        return of(this.translate(keys, params));
    }
}
SiNoTranslateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
SiNoTranslateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Provide this class to build a custom translation service.
 *
 * @internal
 */
class SiTranslateServiceBuilder {
}
SiTranslateServiceBuilder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateServiceBuilder, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTranslateServiceBuilder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateServiceBuilder });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateServiceBuilder, decorators: [{
            type: Injectable
        }] });

/**
 * Always returns the same instance of {@link SiNoTranslateService}
 *
 * @internal
 */
class SiNoTranslateServiceBuilder extends SiTranslateServiceBuilder {
    constructor() {
        super(...arguments);
        this.siNoTranslateService = inject(SiNoTranslateService);
    }
    buildService() {
        return this.siNoTranslateService;
    }
}
SiNoTranslateServiceBuilder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateServiceBuilder, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
SiNoTranslateServiceBuilder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateServiceBuilder, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNoTranslateServiceBuilder, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Translates keys by using the {@link SiTranslateService}.
 * Within Element this pipe should be used instead of ngx-translates `translate` pipe.
 * Outside Element, this pipe should NOT be used.
 *
 * @internal
 */
// eslint-disable-next-line @angular-eslint/no-pipe-impure
class SiTranslatePipe {
    constructor() {
        this.value = '';
        this.siTranslateService = inject(SiTranslateService);
        this.cdRef = inject(ChangeDetectorRef);
    }
    transform(key, params) {
        if (!key) {
            return '';
        }
        const currentKeyParams = params ? `${key}-${JSON.stringify(params)}` : key;
        if (this.lastKeyParams === currentKeyParams) {
            return this.value;
        }
        this.subscription?.unsubscribe();
        const translate = this.siTranslateService.translate(key, params);
        if (typeof translate === 'string') {
            this.updateValue(currentKeyParams, translate);
        }
        else {
            this.subscription = translate.subscribe(value => this.updateValue(currentKeyParams, value));
        }
        return this.value;
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }
    updateValue(currentKeyParams, value) {
        this.lastKeyParams = currentKeyParams;
        this.value = value;
        this.cdRef.markForCheck();
    }
}
SiTranslatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SiTranslatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTranslatePipe, name: "translate", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'translate', pure: false }]
        }] });

/**
 * This provides declares SiTranslatePipe and provides a respective SiTranslateService.
 * It should be used internally of Element but NOT by any application
 *
 * @internal
 */
class SiTranslateModule {
}
SiTranslateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiTranslateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateModule, declarations: [SiTranslatePipe], exports: [SiTranslatePipe] });
SiTranslateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateModule, providers: [
        /* This is needed for ngx-translate when using the isolated mode for lazy child routes.
         * In that case a new TranslateService is created by ngx-translate which also needs to be used
         * by SiMPL components within that route. The Builder can be used to check if a new service is available
         * and then provide a corresponding SiTranslateService.
         */
        {
            provide: SiTranslateService,
            useFactory: (injector, noTranslateBuilder) => injector.get(SiTranslateServiceBuilder, noTranslateBuilder).buildService(injector),
            deps: [Injector, SiNoTranslateServiceBuilder]
        }
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTranslateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiTranslatePipe],
                    exports: [SiTranslatePipe],
                    providers: [
                        /* This is needed for ngx-translate when using the isolated mode for lazy child routes.
                         * In that case a new TranslateService is created by ngx-translate which also needs to be used
                         * by SiMPL components within that route. The Builder can be used to check if a new service is available
                         * and then provide a corresponding SiTranslateService.
                         */
                        {
                            provide: SiTranslateService,
                            useFactory: (injector, noTranslateBuilder) => injector.get(SiTranslateServiceBuilder, noTranslateBuilder).buildService(injector),
                            deps: [Injector, SiNoTranslateServiceBuilder]
                        }
                    ]
                }]
        }] });

class SiSplitPartComponent {
    constructor() {
        this._arrows = {
            h: ['arrow-start', 'arrow-end'],
            v: ['arrow-up', 'arrow-down']
        };
        this.actions = [];
        this.collapseDirection = 'start';
        /**
         * Sets the icon class that is used in the buttons of split parts to
         * collapse and uncollapse the parts.
         */
        this.collapseIconClass = 'element-double-right';
        /**
         * Collapse only to the given min size.
         */
        this.collapseToMinSize = false;
        /**
         * Minimum size in px.
         */
        this.minSize = 0;
        /**
         * When a split part is collapsed, the content gets hidden but it will
         * still remain within the DOM. If you want to remove and destroy the component
         * in collapsed mode and recreate it on un-collapse, set this property to
         * true.
         */
        this.removeContentOnCollapse = false;
        /**
         * Defines the behavior of the split part during scaling.
         * E.g. when set to `none`, the spit part will keep its current size even when the parent container grows or shrinks.
         */
        this.scale = 'auto';
        /**
         * Defines if the collapse button of a split part is displayed. Default value is true.
         */
        this.showCollapseButton = true;
        /**
         * Defines if the header of the split part is visible. Default is true.
         */
        this.showHeader = true;
        /**
         * Aria label for collapse button. Needed for a11y
         */
        this.collapseLabel = 'collapse';
        this.collapseChanged = new EventEmitter();
        this.stateChange = new EventEmitter();
        this.collapsed = false;
        this.orientation = 'vertical';
        this.trackByIndex = (index) => index;
        this._elRef = inject((ElementRef));
        this.cdRef = inject(ChangeDetectorRef);
        this.headerContext = {
            $implicit: this
        };
    }
    set expanded(value) {
        if (this.expanded !== value) {
            this.toggleCollapse();
        }
    }
    get expanded() {
        return !this.collapsed;
    }
    /**
     * Header size in px.
     */
    get headerSize() {
        const header = this._elRef.nativeElement.querySelector('.si-split-part-header');
        return header
            ? this.orientation === 'horizontal' && this.collapsed
                ? header.clientWidth
                : header.clientHeight
            : 0;
    }
    /**
     * Expanded size in px.
     */
    set size(value) {
        if (this._size !== value) {
            this._size = value;
            this.stateChange.emit(this.state);
        }
    }
    get size() {
        return this._size;
    }
    get statusContainerStyle() {
        const header = this._elRef.nativeElement.querySelector('.si-split-part-header');
        if (header) {
            // we need the header´s padding values to adjust the position of the status icon container
            const headerStyle = window.getComputedStyle(header);
            return {
                'block-size': `calc(100% + ${headerStyle.paddingTop} + ${headerStyle.paddingBottom}`,
                'margin-block-end': `-${headerStyle.paddingBottom}`,
                'margin-inline-start': `-${headerStyle.paddingInlineStart}`,
                'margin-inline-end': headerStyle.paddingInlineEnd,
                'margin-block-start': `-${headerStyle.paddingTop}`
            };
        }
        return null;
    }
    applyStyle(style) {
        Object.assign(this._elRef.nativeElement.style, style);
    }
    getCollapseArrow() {
        let tempArrows = this._arrows.h;
        if (this.orientation === 'vertical') {
            tempArrows = this._arrows.v;
        }
        let arrowIndex = 0;
        if (this.collapseDirection === 'end') {
            arrowIndex = 1 - arrowIndex;
        }
        if (this.collapsed) {
            arrowIndex = 1 - arrowIndex;
        }
        return tempArrows[arrowIndex];
    }
    getHeight() {
        return this._elRef.nativeElement.offsetHeight;
    }
    getWidth() {
        return this._elRef.nativeElement.offsetWidth;
    }
    initPart() {
        if (typeof this.minSize === 'number') {
            if (this.orientation === 'horizontal') {
                this.applyStyle({
                    'min-width': `${this.minSize}px`,
                    'min-height': 'auto'
                });
            }
            else {
                this.applyStyle({
                    'min-width': 'auto',
                    'min-height': `${this.minSize}px`
                });
            }
        }
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    /**
     * Returns true if this split part is horizontal by comparing the orientation property
     * with 'horizontal'.
     */
    isHorizontal() {
        return this.orientation === 'horizontal';
    }
    toggleCollapse() {
        this.collapsed = !this.collapsed;
        this.collapseChanged.emit(this.collapsed);
        this.stateChange.emit(this.state);
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    updateSize() {
        if (this.expanded && this.scale === 'none') {
            this.size = this.isHorizontal() ? this.getWidth() : this.getHeight();
            this.cdRef.detectChanges();
            this.cdRef.markForCheck();
        }
    }
    get state() {
        return { expanded: this.expanded, size: this.size };
    }
}
SiSplitPartComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitPartComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSplitPartComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSplitPartComponent, selector: "si-split-part", inputs: { actions: "actions", collapseDirection: "collapseDirection", collapseIconClass: "collapseIconClass", collapseToMinSize: "collapseToMinSize", headerStatusColor: "headerStatusColor", headerStatusIconClass: "headerStatusIconClass", headerTemplate: "headerTemplate", heading: "heading", minSize: "minSize", removeContentOnCollapse: "removeContentOnCollapse", scale: "scale", showCollapseButton: "showCollapseButton", showHeader: "showHeader", collapseLabel: "collapseLabel", stateId: "stateId", expanded: "expanded", size: "size" }, outputs: { collapseChanged: "collapseChanged", stateChange: "stateChange" }, host: { properties: { "class.is-collapsed": "this.collapsed" } }, ngImport: i0, template: "<div\n  *ngIf=\"!headerTemplate && showHeader\"\n  class=\"si-split-part-header\"\n  [class.is-collapsed]=\"collapsed\"\n  [class.is-horizontal]=\"orientation === 'horizontal'\"\n  [class.has-status-color]=\"headerStatusColor && !collapsed\"\n  [style.color]=\"headerStatusColor && !collapsed ? headerStatusColor : null\"\n>\n  <div *ngIf=\"headerStatusIconClass && !collapsed\" class=\"si-split-part-status-container\" [ngStyle]=\"statusContainerStyle\">\n    <i class=\"si-split-part-status\" [ngClass]=\"headerStatusIconClass\"></i>\n  </div>\n  <div class=\"si-split-part-title\">\n    <span class=\"text-truncate\">{{ heading | translate}}</span>\n  </div>\n  <div class=\"si-split-part-actions\" *ngIf=\"!collapsed\">\n    <button\n      *ngFor=\"let action of actions; trackBy: trackByIndex\"\n      type=\"button\"\n      [title]=\"action.tooltip || ''\"\n      [attr.aria-label]=\"action.tooltip\"\n      class=\"si-split-button\"\n      (click)=\"action.click($event)\"\n    >\n      <i [ngClass]=\"action.iconClass\"></i>\n    </button>\n  </div>\n  <div *ngIf=\"showCollapseButton\" class=\"si-split-part-collapse-button\">\n    <button\n      type=\"button\"\n      [attr.aria-label]=\"collapseLabel | translate\"\n      class=\"si-split-button\"\n      [class.flip-rtl]=\"orientation === 'horizontal'\"\n      (click)=\"toggleCollapse()\"\n    >\n      <i [ngClass]=\"[collapseIconClass, getCollapseArrow()]\"></i>\n    </button>\n  </div>\n</div>\n\n<ng-container *ngIf=\"headerTemplate\">\n  <ng-container *ngTemplateOutlet=\"headerTemplate; context: headerContext\"></ng-container>\n</ng-container>\n\n<div class=\"si-split-part-content\" [class.hidden]=\"!removeContentOnCollapse && collapsed && !collapseToMinSize\">\n  <ng-content *ngIf=\"!removeContentOnCollapse || !collapsed\"></ng-content>\n</div>\n", styles: [":host{display:flex;flex-grow:1;flex-shrink:1;flex-basis:var(--si-split-part-flex-basis);flex-direction:column;overflow:hidden}:host.is-collapsed{min-inline-size:auto!important;min-block-size:auto!important}:host ::ng-deep si-split{flex:1}.si-split-part-content{flex-basis:0px;flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow:hidden;inline-size:100%}.si-split-part-header{display:flex;flex-direction:row;flex:none;align-items:center;padding:var(--si-split-part-header-padding);background:var(--si-split-part-header-background);block-size:var(--si-split-part-header-height)}.si-split-part-header.is-collapsed{border-block-end-width:var(--si-split-part-header-border-width);border-style:var(--si-split-part-header-border-style);border-color:var(--si-split-part-header-border-color)}.si-split-part-header.has-status-color{box-shadow:inset 0 calc(-1 * var(--si-split-part-header-status-border-width)) 0 0}.si-split-part-header.has-status-color .si-split-part-status-container{background-color:currentColor}.si-split-part-header.is-collapsed.is-horizontal{flex:auto;flex-direction:column;max-block-size:100%;padding:var(--si-split-part-header-collapsed-horizontal-padding)}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title{inline-size:100%}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title>span{writing-mode:vertical-rl;transform:rotate(180deg);margin:0}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-collapse-button{order:-1}.si-split-part-status-container{display:inline-flex;inline-size:var(--si-split-part-header-height);max-inline-size:var(--si-split-part-header-height);padding:var(--si-split-part-header-status-container-padding)}.si-split-part-status-container .si-split-part-status{color:var(--si-split-part-header-status-icon-color);font-size:var(--si-split-part-header-status-icon-size);margin:auto}.si-split-part-title{display:flex;justify-content:center;flex:auto;font-weight:var(--si-split-part-title-font-weight);font-size:var(--si-split-part-title-font-size);color:var(--si-split-part-title-color);line-height:var(--si-split-part-title-line-height);overflow:hidden}.si-split-part-title>span{display:block;word-wrap:normal;line-height:initial;min-inline-size:0;max-inline-size:100%;margin-inline-end:auto}.si-split-part-actions{display:flex;flex:none;align-items:center;color:initial;margin-inline:var(--si-split-part-action-margin)}.si-split-button{display:inline-block;padding:var(--si-split-button-padding);color:var(--si-split-button-color);overflow:hidden;white-space:nowrap;text-overflow:var(--si-split-button-text-overflow);text-decoration:none;font-size:var(--si-split-button-font-size);line-height:var(--si-split-button-line-height);vertical-align:middle;outline:none;-webkit-user-select:none;user-select:none;background:none;border:0;cursor:pointer}.si-split-button:hover{background:var(--si-split-button-shade-background-hover)}.si-split-button.has-noflex{flex:none}.si-split-button .arrow-start{transform:rotate(180deg)}.si-split-button .arrow-down{transform:rotate(90deg)}.si-split-button .arrow-up{transform:rotate(270deg)}.hidden{display:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitPartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-split-part', template: "<div\n  *ngIf=\"!headerTemplate && showHeader\"\n  class=\"si-split-part-header\"\n  [class.is-collapsed]=\"collapsed\"\n  [class.is-horizontal]=\"orientation === 'horizontal'\"\n  [class.has-status-color]=\"headerStatusColor && !collapsed\"\n  [style.color]=\"headerStatusColor && !collapsed ? headerStatusColor : null\"\n>\n  <div *ngIf=\"headerStatusIconClass && !collapsed\" class=\"si-split-part-status-container\" [ngStyle]=\"statusContainerStyle\">\n    <i class=\"si-split-part-status\" [ngClass]=\"headerStatusIconClass\"></i>\n  </div>\n  <div class=\"si-split-part-title\">\n    <span class=\"text-truncate\">{{ heading | translate}}</span>\n  </div>\n  <div class=\"si-split-part-actions\" *ngIf=\"!collapsed\">\n    <button\n      *ngFor=\"let action of actions; trackBy: trackByIndex\"\n      type=\"button\"\n      [title]=\"action.tooltip || ''\"\n      [attr.aria-label]=\"action.tooltip\"\n      class=\"si-split-button\"\n      (click)=\"action.click($event)\"\n    >\n      <i [ngClass]=\"action.iconClass\"></i>\n    </button>\n  </div>\n  <div *ngIf=\"showCollapseButton\" class=\"si-split-part-collapse-button\">\n    <button\n      type=\"button\"\n      [attr.aria-label]=\"collapseLabel | translate\"\n      class=\"si-split-button\"\n      [class.flip-rtl]=\"orientation === 'horizontal'\"\n      (click)=\"toggleCollapse()\"\n    >\n      <i [ngClass]=\"[collapseIconClass, getCollapseArrow()]\"></i>\n    </button>\n  </div>\n</div>\n\n<ng-container *ngIf=\"headerTemplate\">\n  <ng-container *ngTemplateOutlet=\"headerTemplate; context: headerContext\"></ng-container>\n</ng-container>\n\n<div class=\"si-split-part-content\" [class.hidden]=\"!removeContentOnCollapse && collapsed && !collapseToMinSize\">\n  <ng-content *ngIf=\"!removeContentOnCollapse || !collapsed\"></ng-content>\n</div>\n", styles: [":host{display:flex;flex-grow:1;flex-shrink:1;flex-basis:var(--si-split-part-flex-basis);flex-direction:column;overflow:hidden}:host.is-collapsed{min-inline-size:auto!important;min-block-size:auto!important}:host ::ng-deep si-split{flex:1}.si-split-part-content{flex-basis:0px;flex-grow:1;flex-shrink:1;display:flex;flex-direction:column;overflow:hidden;inline-size:100%}.si-split-part-header{display:flex;flex-direction:row;flex:none;align-items:center;padding:var(--si-split-part-header-padding);background:var(--si-split-part-header-background);block-size:var(--si-split-part-header-height)}.si-split-part-header.is-collapsed{border-block-end-width:var(--si-split-part-header-border-width);border-style:var(--si-split-part-header-border-style);border-color:var(--si-split-part-header-border-color)}.si-split-part-header.has-status-color{box-shadow:inset 0 calc(-1 * var(--si-split-part-header-status-border-width)) 0 0}.si-split-part-header.has-status-color .si-split-part-status-container{background-color:currentColor}.si-split-part-header.is-collapsed.is-horizontal{flex:auto;flex-direction:column;max-block-size:100%;padding:var(--si-split-part-header-collapsed-horizontal-padding)}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title{inline-size:100%}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-title>span{writing-mode:vertical-rl;transform:rotate(180deg);margin:0}.si-split-part-header.is-collapsed.is-horizontal .si-split-part-collapse-button{order:-1}.si-split-part-status-container{display:inline-flex;inline-size:var(--si-split-part-header-height);max-inline-size:var(--si-split-part-header-height);padding:var(--si-split-part-header-status-container-padding)}.si-split-part-status-container .si-split-part-status{color:var(--si-split-part-header-status-icon-color);font-size:var(--si-split-part-header-status-icon-size);margin:auto}.si-split-part-title{display:flex;justify-content:center;flex:auto;font-weight:var(--si-split-part-title-font-weight);font-size:var(--si-split-part-title-font-size);color:var(--si-split-part-title-color);line-height:var(--si-split-part-title-line-height);overflow:hidden}.si-split-part-title>span{display:block;word-wrap:normal;line-height:initial;min-inline-size:0;max-inline-size:100%;margin-inline-end:auto}.si-split-part-actions{display:flex;flex:none;align-items:center;color:initial;margin-inline:var(--si-split-part-action-margin)}.si-split-button{display:inline-block;padding:var(--si-split-button-padding);color:var(--si-split-button-color);overflow:hidden;white-space:nowrap;text-overflow:var(--si-split-button-text-overflow);text-decoration:none;font-size:var(--si-split-button-font-size);line-height:var(--si-split-button-line-height);vertical-align:middle;outline:none;-webkit-user-select:none;user-select:none;background:none;border:0;cursor:pointer}.si-split-button:hover{background:var(--si-split-button-shade-background-hover)}.si-split-button.has-noflex{flex:none}.si-split-button .arrow-start{transform:rotate(180deg)}.si-split-button .arrow-down{transform:rotate(90deg)}.si-split-button .arrow-up{transform:rotate(270deg)}.hidden{display:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { actions: [{
                type: Input
            }], collapseDirection: [{
                type: Input
            }], collapseIconClass: [{
                type: Input
            }], collapseToMinSize: [{
                type: Input
            }], headerStatusColor: [{
                type: Input
            }], headerStatusIconClass: [{
                type: Input
            }], headerTemplate: [{
                type: Input
            }], heading: [{
                type: Input
            }], minSize: [{
                type: Input
            }], removeContentOnCollapse: [{
                type: Input
            }], scale: [{
                type: Input
            }], showCollapseButton: [{
                type: Input
            }], showHeader: [{
                type: Input
            }], collapseLabel: [{
                type: Input
            }], stateId: [{
                type: Input
            }], collapseChanged: [{
                type: Output
            }], stateChange: [{
                type: Output
            }], collapsed: [{
                type: HostBinding,
                args: ['class.is-collapsed']
            }], expanded: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

const isRTL = (elem) => getComputedStyle(elem ?? document.documentElement).direction === 'rtl';
const correctKeyRTL = (key) => {
    if (!isRTL()) {
        return key;
    }
    return key === 'ArrowLeft' ? 'ArrowRight' : key === 'ArrowRight' ? 'ArrowLeft' : key;
};

const SI_UI_STATES_STORAGE_KEY = 'si-ui-states';
/**
 * Service to save and load `UIState` objects. UI state objects contain a context id, which are used
 * as storage keys and prevents unwanted overwrites.
 * To enable the service,
 *
 * - add it to the `providers` declaration in your app module and
 * - set unique `stateId`s to the components (e.g. vertical navbar menu items or splits).
 *
  ```ts
  import { SiUIStateService } from '@simpl/element-ng';
  :
  providers: [
    { provide: SiUIStateService }
   ]
  ```
 *
 */
class SiUIStateService {
    constructor() {
        /**
         * The storage used persist the ui states. Default is localStorage, but you can also use
         * sessionStorage or implement your own.
         */
        this.storage = isPlatformBrowser(inject(PLATFORM_ID)) ? localStorage : undefined;
    }
    /**
     * Saves the provided state in the storage.
     *
     * @param state The state to be save. state with context ids are preferred to prevent unwanted overwrites.
     * @returns True if all goes well as an observable for potential asynchronous remote storage implementations.
     */
    save(state) {
        const store = this.loadStore();
        if (state.contextId) {
            if (!store.contexts) {
                store.contexts = {};
            }
            store.contexts[state.contextId] = state;
        }
        else {
            store.default = state;
        }
        this.saveStore(store);
        return of(true);
    }
    /**
     * Loads and returns the UIState for the given context id.
     * @param contextId The optional but recommended context id for which the UI state object is returned.
     * @returns The UI state object or undefined, wrapped within an observable to support asynchronous
     * remote storage implementations.
     */
    load(contextId) {
        const store = this.loadStore();
        if (contextId) {
            return of(store.contexts?.[contextId]);
        }
        return of(store.default);
    }
    loadStore() {
        const storeStr = this.storage?.getItem(SI_UI_STATES_STORAGE_KEY);
        if (storeStr) {
            return JSON.parse(storeStr);
        }
        return {
            default: undefined,
            contexts: {}
        };
    }
    saveStore(store) {
        this.storage?.setItem(SI_UI_STATES_STORAGE_KEY, JSON.stringify(store));
    }
}
SiUIStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUIStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiUIStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUIStateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUIStateService, decorators: [{
            type: Injectable
        }] });

const COLLAPSED_SIZE = 'none';
class SplitPart {
    constructor(component, _index) {
        this.component = component;
        this._index = _index;
        this._minSize = 0;
        this._size = 0;
    }
    get actualSize() {
        return this.expanded ? this.size : this.collapseToMinSize ? this.minSize : this._headerSize;
    }
    get direction() {
        return this.component.collapseDirection;
    }
    get expanded() {
        return this.component.expanded;
    }
    get hasHeader() {
        return this.component.showHeader;
    }
    get index() {
        return this._index;
    }
    get minSize() {
        return this._minSize;
    }
    get pixelSize() {
        return this.component.orientation === 'horizontal'
            ? this.component.getWidth()
            : this.component.getHeight();
    }
    get scale() {
        return this.component.scale;
    }
    get size() {
        return this._size;
    }
    set size(value) {
        this._size = !isNaN(value) ? value : 0;
    }
    get stateId() {
        return this.component.stateId;
    }
    applyStyle() {
        this.component.applyStyle(this.style);
    }
    collapsed() {
        this.component.collapsed = true;
    }
    notifySizeChange() {
        this.component.updateSize();
    }
    observeStateChange(orientation) {
        this.updateOrientation(orientation);
        return this.component.collapseChanged;
    }
    updateOrientation(orientation) {
        this.component.orientation = orientation;
        this.component.initPart();
        this.component.applyStyle(this.style);
    }
    updateSize(initialSize, totalSize, force = false) {
        this._headerSize = this.hasHeader ? this.asPercent(this.component.headerSize, totalSize) : 0;
        this._minSize = this.component.minSize ? this.asPercent(this.component.minSize, totalSize) : 0;
        this._size = this.scale === 'none' && this.component.size && !force
            ? this.asPercent(this.component.size, totalSize)
            : initialSize;
    }
    get collapseToMinSize() {
        return this.component.collapseToMinSize;
    }
    get style() {
        return {
            order: this._index * 2,
            flex: this.expanded ? `1 1 ${this.size}%` : COLLAPSED_SIZE
        };
    }
    asPercent(size, totalSize) {
        return size / (totalSize * 0.01);
    }
}
class SiSplitComponent {
    constructor() {
        this.initialSizes = [];
        this._dragging = false;
        this._mixedSize = false;
        this._unsubscribe = new Subject();
        this._zone = inject(NgZone);
        this.cdRef = inject(ChangeDetectorRef);
        this.uiStateService = inject(SiUIStateService, { optional: true });
        this.active = true;
        this.orientation = 'horizontal';
        this.sizes = [];
        this.sizesChange = new EventEmitter();
        this.gutterSize = 16;
        this.splitParts = [];
        this.trackByIndex = (index) => index;
    }
    ngAfterViewInit() {
        this.splitPartComponents.changes.pipe(startWith(undefined), delay(0), tap(() => this._unsubscribe.next()), switchMap(() => this.uiStateService && this.stateId
            ? this.uiStateService.load(this.stateId).pipe(take(1))
            : of(undefined))).subscribe(state => this.initSplitParts(state));
    }
    ngOnDestroy() {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }
    ngOnChanges(changes) {
        if (changes.orientation && !changes.orientation.firstChange) {
            this.splitParts.forEach(part => part.updateOrientation(this.orientation));
        }
        if (changes.sizes && changes.sizes.currentValue !== changes.sizes.previousValue) {
            // if the previous value is undefined, it is the initial setting of the sizes.
            if (changes.sizes.currentValue && !changes.sizes.previousValue) {
                this.initialSizes = changes.sizes.currentValue;
            }
            // Prevent recursions, we fire changes sizes on resizing, which could end
            // up here again. So do not resize again if it is the same object.
            if (this.sizesChanged(this.sizes, changes.sizes.previousValue)) {
                this.resizeSplitParts();
            }
        }
    }
    sizesChanged(a, b) {
        if (!b || a.length !== b.length) {
            return true;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return true;
            }
        }
        return false;
    }
    onResize() {
        if (this.splitParts.length && !this._dragging) {
            this.splitParts.forEach(part => part.updateSize(part.size, this.containerSize));
            this.updateView();
        }
    }
    showGutter(index) {
        return index < this.splitParts.length - 1 && this.splitParts[index].expanded && this.splitParts[index + 1].expanded;
    }
    get containerSize() {
        const container = this.splitContainer.nativeElement;
        return this.orientation === 'horizontal'
            ? container.clientWidth
            : container.clientHeight;
    }
    get totalGutterSize() {
        const gutters = this.gutters.filter((_, index) => this.showGutter(index));
        return gutters.length !== 0
            ? gutters.length * this.gutterSize
            : 0;
    }
    get unsubscribe$() {
        return this._unsubscribe.asObservable();
    }
    drag(event, status) {
        const position = this.getPosition(event);
        const diff = position - status.start;
        // handle the pixel size of the two split parts as 100%
        const totalPixel = status.startPixel1 + status.startPixel2;
        const totalPercent = status.startPercent1 + status.startPercent2;
        const relPercent1 = this.getAsPercent(status.startPercent1, totalPercent);
        const relPercent2 = 100 - relPercent1;
        let percentDiff = this.getAsPercent(diff, totalPixel);
        if (this.orientation === 'horizontal' && isRTL()) {
            percentDiff *= -1;
        }
        percentDiff = Math.min(percentDiff, relPercent2);
        percentDiff = Math.max(percentDiff, -relPercent1);
        // scale the 100% diff percentage to the total view
        percentDiff = percentDiff * (status.startPercent1 + status.startPercent2) * 0.01;
        this.splitParts[status.index].size = this.ensurePercentRange(status.startPercent1 + percentDiff);
        this.splitParts[status.index + 1].size = this.ensurePercentRange(status.startPercent2 - percentDiff);
        this.ensureMinSplitPartSizesAfterDrag(status.index);
        this.splitParts.forEach(part => {
            if (part.expanded) {
                part.applyStyle();
            }
        });
    }
    ensureMinSplitPartSizesAfterDrag(index) {
        const part1 = this.splitParts[index];
        const part2 = this.splitParts[index + 1];
        if (part1.size < part1.minSize) {
            part2.size += part1.size - part1.minSize;
            part1.size = part1.minSize;
        }
        if (part2.size < part2.minSize) {
            part1.size += part2.size - part2.minSize;
            part2.size = part2.minSize;
        }
    }
    ensurePercentRange(percentage) {
        return Math.max(0, Math.min(100, percentage));
    }
    getAsPercent(size, totalSize) {
        return size / (totalSize * 0.01);
    }
    getPosition(event) {
        if (event instanceof MouseEvent) {
            return this.orientation === 'horizontal'
                ? event.clientX
                : event.clientY;
        }
        else if (event instanceof TouchEvent) {
            return this.orientation === 'horizontal'
                ? event.touches[0].clientX
                : event.touches[0].clientY;
        }
        return 0;
    }
    fireResizeEvent() {
        this.saveUIState();
        this.cdRef.markForCheck();
    }
    initSplitParts(state) {
        this.splitParts = this.splitPartComponents.map((component, index) => {
            // load the stored ui state for a split part if
            //  - the part has an id
            //  - the part uses scale mode 'none'
            //  - the initial size has not changed to the initial size that we used at storing
            //    the state. This ensures that we can overwrite the stored sizing when setting
            //    new input sized via the API. Or, the part is collapsed (not expanded).
            if (component.stateId
                && state?.splitParts
                && component.scale === 'none'
                && (this.sizes[index] === state.splitParts[component.stateId].initialSize || !state.splitParts[component.stateId].expanded)) {
                const splitPartState = state.splitParts[component.stateId];
                this.sizes[index] = splitPartState.size;
                component.expanded = splitPartState.expanded ?? true;
            }
            const splitPart = new SplitPart(component, index);
            splitPart.updateSize(this.sizes[index], this.containerSize);
            this._mixedSize = this._mixedSize || splitPart.scale === 'none';
            return splitPart;
        });
        this.splitParts.forEach(part => {
            part.observeStateChange(this.orientation).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
                this.updateView(part);
            });
        });
        setTimeout(() => {
            this.updateView();
            this.cdRef.detectChanges();
            this.setupDragEvents();
            this.splitParts.filter(part => part.scale === 'none').forEach(part => part.notifySizeChange());
            this.cdRef.detectChanges();
        });
    }
    resizeSplitParts() {
        this.splitParts.forEach((part, index) => {
            part.updateSize(this.sizes[index], this.containerSize, true);
            this._mixedSize = this._mixedSize || part.scale === 'none';
        });
        setTimeout(() => {
            this.updateView();
            this.splitParts.filter(part => part.scale === 'none').forEach(part => part.notifySizeChange());
            this.cdRef.markForCheck();
        });
    }
    setupDragEvents() {
        this._zone.runOutsideAngular(() => {
            const mousemove$ = merge(fromEvent(document, 'mousemove'), fromEvent(document, 'touchmove'));
            const mouseup$ = merge(fromEvent(document, 'mouseup'), fromEvent(document, 'touchend'));
            this.gutters.forEach((gutter, index) => {
                const mousedown$ = merge(fromEvent(gutter.nativeElement, 'mousedown'), fromEvent(gutter.nativeElement, 'touchstart'));
                mousedown$.pipe(takeUntil(this.unsubscribe$), switchMap(startEvent => {
                    startEvent.preventDefault();
                    this._dragging = true;
                    const part1 = this.splitParts[index];
                    const part2 = this.splitParts[index + 1];
                    const status = {
                        index,
                        start: this.getPosition(startEvent),
                        startPixel1: part1.pixelSize,
                        startPixel2: part2.pixelSize,
                        startPercent1: part1.size,
                        startPercent2: part2.size
                    };
                    return mousemove$.pipe(throttleTime(5), tap(evt => this.drag(evt, status)), takeUntil(mouseup$.pipe(tap(() => {
                        this.fireResizeEvent();
                        part1.notifySizeChange();
                        part2.notifySizeChange();
                        this._dragging = false;
                        this.cdRef.detectChanges();
                    }))), throttleTime(200));
                })).subscribe(() => this.fireResizeEvent());
            });
        });
    }
    updateAutoSize(activePart) {
        let factor;
        if (activePart?.expanded) {
            factor = (100 - activePart.size) / 100;
            this.splitParts.filter((part, index) => part.expanded && activePart.index !== index).forEach(part => part.size *= factor);
        }
        let totalSize = 0;
        this.splitParts.forEach((part, index) => {
            if (activePart && !activePart.expanded) {
                if (activePart.direction === 'start' && index < activePart.index) {
                    part.collapsed();
                }
                if (activePart.direction === 'end' && index > activePart.index) {
                    part.collapsed();
                }
            }
            totalSize += part.actualSize;
        });
        // recalculate size to fill 100%
        factor = totalSize !== 0 ? 100 / totalSize : 0;
        this.splitParts.filter(part => part.expanded).forEach(part => part.size *= factor);
    }
    updateMixedSize() {
        let autoSize = 100;
        let actualSize = 0;
        this.splitParts.forEach(part => {
            if (part.scale === 'none') {
                autoSize -= part.actualSize;
            }
            else {
                actualSize += part.size;
            }
        });
        autoSize -= this.getAsPercent(this.totalGutterSize, this.containerSize);
        const autoSizeFactor = actualSize ? autoSize / actualSize : 1;
        this.splitParts.forEach(part => {
            if (part.scale === 'auto') {
                part.size *= autoSizeFactor;
            }
        });
    }
    updateView(activePart) {
        if (this._mixedSize) {
            this.updateMixedSize();
        }
        else {
            this.updateAutoSize(activePart);
        }
        this.splitParts.forEach(part => part.applyStyle());
        if (activePart) {
            setTimeout(() => this.fireResizeEvent());
        }
        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
    }
    saveUIState() {
        const state = { contextId: this.stateId, splitParts: {} };
        const newSizes = [];
        this.splitParts.forEach((part, index) => {
            newSizes.push(part.size);
            if (part.stateId) {
                state.splitParts[part.stateId] = { size: part.size, expanded: part.expanded, initialSize: this.initialSizes[index] };
            }
        });
        this.sizes = newSizes;
        this.sizesChange.emit(this.sizes);
        if (this.stateId && this.uiStateService) {
            this.uiStateService.save(state);
        }
    }
}
SiSplitComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSplitComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSplitComponent, selector: "si-split", inputs: { orientation: "orientation", sizes: "sizes", gutterSize: "gutterSize", stateId: "stateId" }, outputs: { sizesChange: "sizesChange" }, host: { listeners: { "window:resize": "onResize()" }, properties: { "class.si-split": "this.active" } }, queries: [{ propertyName: "splitPartComponents", predicate: SiSplitPartComponent }], viewQueries: [{ propertyName: "splitContainer", first: true, predicate: ["splitContainer"], descendants: true, static: true }, { propertyName: "gutters", predicate: ["gutter"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"si-split-container\" [ngClass]=\"orientation\" #splitContainer>\n  <ng-content></ng-content>\n\n  <ng-container *ngFor=\"let splitPart of splitParts; trackBy: trackByIndex; let i = index\">\n    <div\n      #gutter\n      *ngIf=\"i < splitParts.length - 1\"\n      class=\"si-split-gutter\"\n      [class.d-none]=\"!showGutter(i)\"\n      [style.order]=\"i * 2 + 1\"\n      [style.width.px]=\"orientation === 'horizontal' ? gutterSize : null\"\n      [style.height.px]=\"orientation === 'vertical' ? gutterSize : null\"\n    ></div>\n  </ng-container>\n</div>\n", styles: [":host{--si-split-gutter-border-thickness: 0;--si-split-gutter-border-style: solid;--si-split-gutter-handle-size: 6px;--si-split-gutter-handle-length: 40px;--si-split-part-header-height: 40px;--si-split-part-header-background: var(--element-base-0);--si-split-part-header-padding: 6px;--si-split-part-header-collapsed-horizontal-padding: 6px;--si-split-part-header-status-border-width: 4px;--si-split-part-header-status-container-padding: 0;--si-split-part-header-status-icon-color: var(--si-split-part-title-color, initial);--si-split-part-header-status-icon-size: 20px;--si-split-part-title-color: var(--element-text-primary);--si-split-part-title-font-size: .875rem;--si-split-part-title-font-weight: bold;--si-split-part-title-line-height: 1.143;--si-split-part-action-margin: 8px;--si-split-part-flex-basis: 50px;--si-split-button-color: var(--element-text-primary);--si-split-button-font-size: 20px;--si-split-button-line-height: 1;--si-split-button-text-overflow: ellipsis;--si-split-button-padding: 4px;--si-split-button-shade-background-hover: rgba(47, 67, 99, .4);display:flex;inline-size:100%}.si-split-container{flex:1 1 0;display:flex;overflow:hidden;inline-size:100%;flex-direction:row}.si-split-container.vertical{flex-direction:column}.si-split-gutter{flex-shrink:0;flex-grow:0;background:var(--element-base-0);cursor:ew-resize}.si-split-gutter:before{display:block;content:\"\";position:relative;inset-block-start:50%;margin-block-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inset-inline-start:50%;margin-inline-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);inline-size:var(--si-split-gutter-handle-size);block-size:var(--si-split-gutter-handle-length);background:var(--element-ui-2);border-radius:3px}.vertical .si-split-gutter{inline-size:auto;cursor:ns-resize}.vertical .si-split-gutter:before{margin-block-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);margin-inline-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inline-size:var(--si-split-gutter-handle-length);block-size:var(--si-split-gutter-handle-size)}.si-split-gutter:hover:before{background:var(--element-ui-0-hover)}.si-split-gutter:active:before{background:var(--element-ui-0)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-split', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"si-split-container\" [ngClass]=\"orientation\" #splitContainer>\n  <ng-content></ng-content>\n\n  <ng-container *ngFor=\"let splitPart of splitParts; trackBy: trackByIndex; let i = index\">\n    <div\n      #gutter\n      *ngIf=\"i < splitParts.length - 1\"\n      class=\"si-split-gutter\"\n      [class.d-none]=\"!showGutter(i)\"\n      [style.order]=\"i * 2 + 1\"\n      [style.width.px]=\"orientation === 'horizontal' ? gutterSize : null\"\n      [style.height.px]=\"orientation === 'vertical' ? gutterSize : null\"\n    ></div>\n  </ng-container>\n</div>\n", styles: [":host{--si-split-gutter-border-thickness: 0;--si-split-gutter-border-style: solid;--si-split-gutter-handle-size: 6px;--si-split-gutter-handle-length: 40px;--si-split-part-header-height: 40px;--si-split-part-header-background: var(--element-base-0);--si-split-part-header-padding: 6px;--si-split-part-header-collapsed-horizontal-padding: 6px;--si-split-part-header-status-border-width: 4px;--si-split-part-header-status-container-padding: 0;--si-split-part-header-status-icon-color: var(--si-split-part-title-color, initial);--si-split-part-header-status-icon-size: 20px;--si-split-part-title-color: var(--element-text-primary);--si-split-part-title-font-size: .875rem;--si-split-part-title-font-weight: bold;--si-split-part-title-line-height: 1.143;--si-split-part-action-margin: 8px;--si-split-part-flex-basis: 50px;--si-split-button-color: var(--element-text-primary);--si-split-button-font-size: 20px;--si-split-button-line-height: 1;--si-split-button-text-overflow: ellipsis;--si-split-button-padding: 4px;--si-split-button-shade-background-hover: rgba(47, 67, 99, .4);display:flex;inline-size:100%}.si-split-container{flex:1 1 0;display:flex;overflow:hidden;inline-size:100%;flex-direction:row}.si-split-container.vertical{flex-direction:column}.si-split-gutter{flex-shrink:0;flex-grow:0;background:var(--element-base-0);cursor:ew-resize}.si-split-gutter:before{display:block;content:\"\";position:relative;inset-block-start:50%;margin-block-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inset-inline-start:50%;margin-inline-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);inline-size:var(--si-split-gutter-handle-size);block-size:var(--si-split-gutter-handle-length);background:var(--element-ui-2);border-radius:3px}.vertical .si-split-gutter{inline-size:auto;cursor:ns-resize}.vertical .si-split-gutter:before{margin-block-start:calc(-1 * var(--si-split-gutter-handle-size) / 2);margin-inline-start:calc(-1 * var(--si-split-gutter-handle-length) / 2);inline-size:var(--si-split-gutter-handle-length);block-size:var(--si-split-gutter-handle-size)}.si-split-gutter:hover:before{background:var(--element-ui-0-hover)}.si-split-gutter:active:before{background:var(--element-ui-0)}\n"] }]
        }], propDecorators: { splitPartComponents: [{
                type: ContentChildren,
                args: [SiSplitPartComponent]
            }], active: [{
                type: HostBinding,
                args: ['class.si-split']
            }], orientation: [{
                type: Input
            }], sizes: [{
                type: Input
            }], sizesChange: [{
                type: Output
            }], gutterSize: [{
                type: Input
            }], stateId: [{
                type: Input
            }], splitContainer: [{
                type: ViewChild,
                args: ['splitContainer', { static: true }]
            }], gutters: [{
                type: ViewChildren,
                args: ['gutter']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class SiSplitModule {
}
SiSplitModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSplitModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSplitModule, declarations: [SiSplitComponent, SiSplitPartComponent], imports: [CommonModule, SiTranslateModule], exports: [SiSplitComponent, SiSplitPartComponent] });
SiSplitModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSplitModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiSplitComponent, SiSplitPartComponent],
                    exports: [SiSplitComponent, SiSplitPartComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

const SI_TOAST_AUTO_HIDE_DELAY = 6000;

/**
 * @returns Returns whether animations are disabled via the CSS var. This is either set by
 * an application on low-end devices or in case the device is configured for reduced motion
 * (i.e. `@media (prefers-reduced-motion)` matches)
 */
const areAnimationsDisabled = () => {
    const style = getComputedStyle(document.documentElement);
    const flag = style.getPropertyValue('--element-animations-enabled');
    return parseInt(flag, 10) === 0;
};

const STATUS_ICON = {
    success: {
        icon: 'element-circle-filled',
        color: 'status-success',
        stacked: 'element-state-tick smooth-auto',
        stackedColor: 'status-success-contrast',
        background: 'bg-base-success',
        severity: 5
    },
    info: {
        icon: 'element-square-filled',
        color: 'status-info',
        stacked: 'element-state-info smooth-auto',
        stackedColor: 'status-info-contrast',
        background: 'bg-base-info',
        severity: 4
    },
    caution: {
        icon: 'element-square-45-filled',
        color: 'status-caution',
        stacked: 'element-state-exclamation-mark smooth-auto',
        stackedColor: 'status-caution-contrast',
        background: 'bg-base-caution',
        severity: 3
    },
    warning: {
        icon: 'element-triangle-filled',
        color: 'status-warning',
        stacked: 'element-state-exclamation-mark smooth-auto',
        stackedColor: 'status-warning-contrast',
        background: 'bg-base-warning',
        severity: 2
    },
    danger: {
        icon: 'element-circle-filled',
        color: 'status-danger',
        stacked: 'element-state-exclamation-mark smooth-auto',
        stackedColor: 'status-danger-contrast',
        background: 'bg-base-danger',
        severity: 1
    },
    critical: {
        icon: 'element-octagon-filled',
        color: 'status-critical',
        stacked: 'element-state-exclamation-mark smooth-auto',
        stackedColor: 'status-critical-contrast',
        background: 'bg-base-danger',
        severity: 0
    }
};

class SiIconComponent {
    constructor() {
        /** Text-size class for icon size, see https://simpl.code.siemens.io/simpl-element/fundamentals/typography/#type-styles-classes */
        this.size = 'icon';
        this.altText = '';
    }
    ngOnChanges(changes) {
        if (changes.icon || changes.alt) {
            this.altText = this.alt ?? this.icon?.replace('element-', '').split('-').join(' ') ?? '';
        }
    }
}
SiIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiIconComponent, selector: "si-icon", inputs: { icon: "icon", color: "color", stackedIcon: "stackedIcon", stackedColor: "stackedColor", alt: "alt", size: "size" }, usesOnChanges: true, ngImport: i0, template: "<span\n  [attr.aria-label]=\"altText ? (altText | translate) : null\"\n  [attr.role]=\"altText ? 'img' : 'presentation'\"\n  class=\"d-inline-block position-relative\"\n  [ngClass]=\"[icon ?? '', color ?? '', size]\"\n>\n  <i\n    *ngIf=\"stackedIcon\"\n    [ngClass]=\"[stackedIcon, stackedColor ?? '']\"\n    class=\"position-absolute start-0\"\n    aria-hidden=\"true\"\n  ></i>\n</span>\n", styles: [":host,span{line-height:1}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-icon', changeDetection: ChangeDetectionStrategy.OnPush, template: "<span\n  [attr.aria-label]=\"altText ? (altText | translate) : null\"\n  [attr.role]=\"altText ? 'img' : 'presentation'\"\n  class=\"d-inline-block position-relative\"\n  [ngClass]=\"[icon ?? '', color ?? '', size]\"\n>\n  <i\n    *ngIf=\"stackedIcon\"\n    [ngClass]=\"[stackedIcon, stackedColor ?? '']\"\n    class=\"position-absolute start-0\"\n    aria-hidden=\"true\"\n  ></i>\n</span>\n", styles: [":host,span{line-height:1}\n"] }]
        }], propDecorators: { icon: [{
                type: Input
            }], color: [{
                type: Input
            }], stackedIcon: [{
                type: Input
            }], stackedColor: [{
                type: Input
            }], alt: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

class SiLinkActionService {
    constructor() {
        this.actionSubject = new Subject();
        this.action$ = this.actionSubject.asObservable();
    }
    emit(link, param) {
        this.actionSubject.next({ link, param });
    }
}
SiLinkActionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkActionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiLinkActionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkActionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkActionService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
const SI_LINK_DEFAULT_NAVIGATION_EXTRA = new InjectionToken('SI_LINK_DEFAULT_NAVIGATION_EXTRA');
class SiLinkDirective {
    constructor() {
        this.exactMatch = false;
        this.activeChange = new EventEmitter();
        // @HostBinding('attr.href') is used rather than @HostBinding() because
        // it removes the href attribute when it becomes `null`.
        this.href = null;
        this.active = false;
        this.router = inject(Router, { optional: true });
        this.activatedRoute = inject(ActivatedRoute, { optional: true });
        this.locationStrategy = inject(LocationStrategy, { optional: true });
        this.translateService = inject(SiTranslateService);
        this.actionService = inject(SiLinkActionService, { optional: true });
        this.element = inject(ElementRef);
        this.renderer = inject(Renderer2);
        this.cdRef = inject(ChangeDetectorRef);
        this.defaultNavigationExtra = inject(SI_LINK_DEFAULT_NAVIGATION_EXTRA, { optional: true });
    }
    get isAriaCurrent() {
        return this.active ? this.ariaCurrent ?? 'true' : null;
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    ngOnChanges() {
        if (!this.siLink) {
            this.href = null;
            return;
        }
        this.subscription?.unsubscribe();
        this.subscription = undefined;
        if (this.siLink.tooltip) {
            this.translateService.translateAsync(this.siLink.tooltip).pipe(first()).subscribe(text => {
                this.title = text;
                this.cdRef.markForCheck();
            });
        }
        else {
            this.title = this.siLink.tooltip;
        }
        if (this.siLink.action) {
            this.href = '';
        }
        else if (this.siLink.link) {
            this.subscribeRouter();
        }
        else if (this.siLink.href) {
            this.href = this.siLink.href;
            this.target = this.siLink.target ?? this.siLinkDefaultTarget;
        }
        else {
            // In case the siLink has no link, href or action,
            // we remove the href to avoid the mouse pointer.
            this.href = null;
        }
        this.updateActive();
    }
    ngDoCheck() {
        // this deep-checks if isActive has changed. It then updates the internal state and emits the event
        // to be symmetric with the router-link case. Promise.resolve() avoids "Expression has changed after it was checked" errors
        if (this.siLink && !this.siLink.link && this.siLink.isActive !== undefined && this.active !== this.siLink.isActive) {
            this.active = this.siLink.isActive;
            Promise.resolve().then(() => {
                this.activeChange.emit(this.active);
                this.updateActiveClass();
            });
        }
    }
    subscribeRouter() {
        if (!this.router || !this.activatedRoute) {
            return;
        }
        this.subscription = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.updateActive());
        this.subscription.add(this.activatedRoute.queryParams.subscribe(() => {
            this.updateUrlTree();
            this.updateActive();
            this.cdRef.detectChanges();
        }));
    }
    updateUrlTree() {
        if (!this.router || !this.siLink || !this.locationStrategy) {
            return;
        }
        const commands = Array.isArray(this.siLink.link) ? this.siLink.link : [this.siLink.link];
        this.navigationExtras = {
            relativeTo: this.activatedRoute,
            preserveFragment: true,
            queryParamsHandling: 'merge',
            ...this.defaultNavigationExtra,
            ...this.siLink.navigationExtras
        };
        this.urlTree = this.router.createUrlTree(commands, this.navigationExtras);
        this.href = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(this.urlTree));
        // Remove leading hash and query params
        this.compareHref = this.href.replace(/^(#)/, '').replace(/\?.*/, '');
    }
    updateActive() {
        const matchOptions = this.exactMatch
            ? { paths: 'exact', queryParams: 'exact', fragment: 'ignored', matrixParams: 'ignored' }
            : { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' };
        // Promise is used to work around Angular view check error
        Promise.resolve().then(() => {
            let active = false;
            if (this.siLink) {
                if (this.siLink.isActive) {
                    active = true;
                }
                else if (this.siLink.link && this.router) {
                    // Compare with a string so we can ignore query params
                    active = this.router.isActive(this.compareHref, matchOptions);
                }
            }
            if (active !== this.active) {
                this.active = active;
                this.activeChange.emit(active);
            }
            this.updateActiveClass();
        });
    }
    updateActiveClass() {
        if (this.activeClass) {
            if (this.active) {
                this.renderer.addClass(this.element.nativeElement, this.activeClass);
            }
            else {
                this.renderer.removeClass(this.element.nativeElement, this.activeClass);
                this.element.nativeElement.blur();
            }
        }
    }
    onClick(event) {
        if (this.siLink?.action) {
            event.preventDefault();
            if (typeof this.siLink.action === 'string') {
                this.actionService?.emit(this.siLink, this.actionParam);
            }
            else if (this.actionParam === undefined) {
                this.siLink.action();
            }
            else {
                this.siLink.action(this.actionParam);
            }
            this.updateActive();
            return;
        }
        // ignore regular links, allow user to open links in new tab or window
        if (!this.siLink || this.siLink.href || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        event.preventDefault();
        // We have links without any action, href or link. For example grouping navbar items that hold a
        // dropdown with links. That is why we need to check if the link property is set.
        if (this.siLink.link && this.router) {
            this.router.navigateByUrl(this.urlTree, this.navigationExtras);
        }
    }
}
SiLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiLinkDirective, selector: "[siLink]", inputs: { siLink: "siLink", siLinkDefaultTarget: "siLinkDefaultTarget", actionParam: "actionParam", activeClass: "activeClass", exactMatch: "exactMatch", ariaCurrent: "ariaCurrent" }, outputs: { activeChange: "activeChange" }, host: { listeners: { "click": "onClick($event)" }, properties: { "attr.href": "this.href", "attr.target": "this.target", "attr.title": "this.title", "attr.aria-current": "this.isAriaCurrent" } }, exportAs: ["siLink"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siLink]',
                    exportAs: 'siLink'
                }]
        }], propDecorators: { siLink: [{
                type: Input
            }], siLinkDefaultTarget: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], activeClass: [{
                type: Input
            }], exactMatch: [{
                type: Input
            }], ariaCurrent: [{
                type: Input
            }], activeChange: [{
                type: Output
            }], href: [{
                type: HostBinding,
                args: ['attr.href']
            }], target: [{
                type: HostBinding,
                args: ['attr.target']
            }], title: [{
                type: HostBinding,
                args: ['attr.title']
            }], isAriaCurrent: [{
                type: HostBinding,
                args: ['attr.aria-current']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class SiToastNotificationComponent {
    constructor() {
        this.statusIcon = STATUS_ICON.info;
        this.animationBuilder = inject(AnimationBuilder);
    }
    ngOnInit() {
        if (this.toast.state === 'connection') {
            this.statusIcon = STATUS_ICON.danger;
        }
        else {
            this.statusIcon = STATUS_ICON[this.toast.state];
        }
        if (!this.toast.disableAutoClose && this.toast.timeout) {
            const animation = this.animationBuilder.build([
                style({ width: '0' }),
                animate(this.toast.timeout, style({ width: '100%' }))
            ]);
            animation.create(this.autoCloseBar.nativeElement).play();
        }
    }
    close() {
        this.toast.close();
    }
}
SiToastNotificationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiToastNotificationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiToastNotificationComponent, selector: "si-toast-notification", inputs: { toast: "toast" }, viewQueries: [{ propertyName: "autoCloseBar", first: true, predicate: ["autoCloseBar"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"si-toast elevation-2\" [class.si-toast-auto-close]=\"!toast.disableAutoClose\">\n  <div class=\"si-toast-timer-bar cy-ignore\" #autoCloseBar></div>\n  <div class=\"bar\" [ngClass]=\"statusIcon.color\"></div>\n  <si-icon\n    [icon]=\"statusIcon.icon\"\n    [color]=\"statusIcon.color\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"statusIcon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"toast-content px-4\">\n    <p class=\"toast-title\"><b>{{ toast.title | translate: toast.translationParams }}</b></p>\n    <p *ngIf=\"toast.message\" class=\"mt-3\">{{ toast.message | translate: toast.translationParams }}</p>\n    <p\n      *ngIf=\"toast.action\"\n      [class.mt-3]=\"toast.message\"\n      [class.mt-4]=\"!toast.message\"\n    >\n      <a [siLink]=\"toast.action\">{{ toast.action.title! | translate: toast.translationParams }}</a>\n    </p>\n  </div>\n  <button\n    *ngIf=\"!toast.disableManualClose\"\n    type=\"button\"\n    tabindex=\"0\"\n    class=\"btn btn-circle btn-sm btn-ghost mt-n2 me-n2 element-cancel\"\n    aria-label=\"cancel\"\n    (keydown.enter)=\"close()\"\n    (click)=\"close()\"\n  ></button>\n</div>\n", styles: [".si-toast-timer-bar,.si-toast.si-toast-auto-close:before{position:absolute;inset-block-end:0;inset-inline-start:0;block-size:2px}:host{display:block}.si-toast{block-size:auto;position:relative;display:flex;align-items:flex-start;color:var(--element-text-primary);background:var(--element-base-1);border-radius:var(--element-radius-2);padding:12px;margin-block-end:20px;font-size:1rem;pointer-events:all;overflow:hidden;min-inline-size:250px;max-inline-size:calc(100vw - 40px)}@media (min-width: 768px){.si-toast{max-inline-size:400px}}.si-toast.si-toast-auto-close{padding-block-end:14px}.si-toast.si-toast-auto-close:before{content:\"\";background:var(--element-ui-4);inset-inline-end:0}.bar{inset-block-start:0;inset-inline-start:0;position:absolute;inline-size:4px;block-size:100%;background:currentColor}.si-toast-timer-bar{background-color:var(--element-ui-0)}.toast-content{flex:1;overflow:hidden;white-space:nowrap}.toast-content p{margin-block:0;line-height:1.125rem;white-space:pre-line;overflow-wrap:break-word;word-wrap:break-word}.toast-content .toast-title{line-height:1.5rem}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-toast-notification', template: "<div class=\"si-toast elevation-2\" [class.si-toast-auto-close]=\"!toast.disableAutoClose\">\n  <div class=\"si-toast-timer-bar cy-ignore\" #autoCloseBar></div>\n  <div class=\"bar\" [ngClass]=\"statusIcon.color\"></div>\n  <si-icon\n    [icon]=\"statusIcon.icon\"\n    [color]=\"statusIcon.color\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"statusIcon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"toast-content px-4\">\n    <p class=\"toast-title\"><b>{{ toast.title | translate: toast.translationParams }}</b></p>\n    <p *ngIf=\"toast.message\" class=\"mt-3\">{{ toast.message | translate: toast.translationParams }}</p>\n    <p\n      *ngIf=\"toast.action\"\n      [class.mt-3]=\"toast.message\"\n      [class.mt-4]=\"!toast.message\"\n    >\n      <a [siLink]=\"toast.action\">{{ toast.action.title! | translate: toast.translationParams }}</a>\n    </p>\n  </div>\n  <button\n    *ngIf=\"!toast.disableManualClose\"\n    type=\"button\"\n    tabindex=\"0\"\n    class=\"btn btn-circle btn-sm btn-ghost mt-n2 me-n2 element-cancel\"\n    aria-label=\"cancel\"\n    (keydown.enter)=\"close()\"\n    (click)=\"close()\"\n  ></button>\n</div>\n", styles: [".si-toast-timer-bar,.si-toast.si-toast-auto-close:before{position:absolute;inset-block-end:0;inset-inline-start:0;block-size:2px}:host{display:block}.si-toast{block-size:auto;position:relative;display:flex;align-items:flex-start;color:var(--element-text-primary);background:var(--element-base-1);border-radius:var(--element-radius-2);padding:12px;margin-block-end:20px;font-size:1rem;pointer-events:all;overflow:hidden;min-inline-size:250px;max-inline-size:calc(100vw - 40px)}@media (min-width: 768px){.si-toast{max-inline-size:400px}}.si-toast.si-toast-auto-close{padding-block-end:14px}.si-toast.si-toast-auto-close:before{content:\"\";background:var(--element-ui-4);inset-inline-end:0}.bar{inset-block-start:0;inset-inline-start:0;position:absolute;inline-size:4px;block-size:100%;background:currentColor}.si-toast-timer-bar{background-color:var(--element-ui-0)}.toast-content{flex:1;overflow:hidden;white-space:nowrap}.toast-content p{margin-block:0;line-height:1.125rem;white-space:pre-line;overflow-wrap:break-word;word-wrap:break-word}.toast-content .toast-title{line-height:1.5rem}\n"] }]
        }], propDecorators: { toast: [{
                type: Input
            }], autoCloseBar: [{
                type: ViewChild,
                args: ['autoCloseBar', { static: true }]
            }] } });

class SiToastNotificationDrawerComponent {
    constructor() {
        this.animationsDisabled = areAnimationsDisabled();
        this.trackByItem = (_index, item) => item;
    }
}
SiToastNotificationDrawerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationDrawerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiToastNotificationDrawerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiToastNotificationDrawerComponent, selector: "si-toast-notification-drawer", inputs: { toasts: "toasts" }, ngImport: i0, template: "<div class=\"si-toast-drawer\" aria-live=\"polite\">\n  <si-toast-notification\n    *ngFor=\"let toast of toasts | async; trackBy: trackByItem\"\n    class=\"position-relative\"\n    [toast]=\"toast\"\n    @toastTrigger\n    [@.disabled]=\"animationsDisabled\"\n  ></si-toast-notification>\n</div>\n", styles: [".si-toast-drawer{position:fixed;inset-block-end:0;inset-inline-end:0;padding-block:32px 0;padding-inline:32px 20px;z-index:2000;overflow:hidden;pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: SiToastNotificationComponent, selector: "si-toast-notification", inputs: ["toast"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], animations: [
        trigger('toastTrigger', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(100%)' }),
                animate('500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)', style({ opacity: 1, transform: 'translateY(0%)' }))
            ]),
            transition(':leave', [
                style({ 'opacity': 1, 'inset-inline-end': '0%' }),
                animate('300ms', style({ 'opacity': 0, 'inset-inline-end': '-100%' }))
            ])
        ])
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationDrawerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-toast-notification-drawer', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('toastTrigger', [
                            transition(':enter', [
                                style({ opacity: 0, transform: 'translateY(100%)' }),
                                animate('500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)', style({ opacity: 1, transform: 'translateY(0%)' }))
                            ]),
                            transition(':leave', [
                                style({ 'opacity': 1, 'inset-inline-end': '0%' }),
                                animate('300ms', style({ 'opacity': 0, 'inset-inline-end': '-100%' }))
                            ])
                        ])
                    ], template: "<div class=\"si-toast-drawer\" aria-live=\"polite\">\n  <si-toast-notification\n    *ngFor=\"let toast of toasts | async; trackBy: trackByItem\"\n    class=\"position-relative\"\n    [toast]=\"toast\"\n    @toastTrigger\n    [@.disabled]=\"animationsDisabled\"\n  ></si-toast-notification>\n</div>\n", styles: [".si-toast-drawer{position:fixed;inset-block-end:0;inset-inline-end:0;padding-block:32px 0;padding-inline:32px 20px;z-index:2000;overflow:hidden;pointer-events:none}\n"] }]
        }], propDecorators: { toasts: [{
                type: Input
            }] } });

class SiToastNotificationService {
    constructor() {
        this.activeToasts = [];
        this.activeToastsSubject = new Subject();
        this.queuedToastSubject = new Subject();
        this.maxToasts = 3;
        this.appRef = inject(ApplicationRef);
        this.injector = inject(Injector);
        this.environmentInjector = inject(EnvironmentInjector);
        const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.queuedToastSubject.subscribe((toast) => {
            this.activeToasts.push(toast);
            if (this.activeToasts.length > this.maxToasts) {
                this.hideToastNotification(this.activeToasts[0]);
            }
            this.activeToastsSubject.next(this.activeToasts);
            if (!toast.disableAutoClose && toast.timeout) {
                setTimeout(() => this.hideToastNotification(toast), toast.timeout);
            }
        });
        if (isBrowser) {
            this.addToastDrawer();
        }
    }
    ngOnDestroy() {
        this.componentRef?.destroy();
    }
    queueToastNotification(state, title, message, disableAutoClose, disableManualClose, action) {
        const toast = {
            state, title, message, disableAutoClose, disableManualClose, action, hidden: new Subject()
        };
        return this.showToastNotification(toast);
    }
    showToastNotification(toast) {
        toast.timeout ?? (toast.timeout = SI_TOAST_AUTO_HIDE_DELAY);
        toast.close = () => this.hideToastNotification(toast);
        this.queuedToastSubject.next(toast);
        return toast;
    }
    hideToastNotification(toast) {
        const hiddenToasts = [];
        const activeToasts = [];
        this.activeToasts.forEach(item => {
            if (!toast || item === toast) {
                hiddenToasts.push(item);
            }
            else {
                activeToasts.push(item);
            }
        });
        this.activeToasts = activeToasts;
        this.activeToastsSubject.next(this.activeToasts);
        hiddenToasts.forEach(item => {
            item.hidden?.next();
            item.hidden?.complete();
        });
    }
    addToastDrawer() {
        this.componentRef = createComponent(SiToastNotificationDrawerComponent, { environmentInjector: this.environmentInjector, elementInjector: this.buildInjector() });
        this.componentRef.instance.toasts = this.activeToastsSubject;
        this.appRef.attachView(this.componentRef.hostView);
        document.body.appendChild(this.componentRef.location.nativeElement);
    }
    // TODO remove once translation must be defined at application start
    // Notification service is provided in 'root'. If no translation is defined, SiNoTranslateService is not provided
    buildInjector() {
        let injector = this.injector;
        if (!injector.get(SiTranslateService, null)) {
            injector = Injector.create({
                providers: [{ provide: SiTranslateService, useClass: SiNoTranslateService, deps: [] }],
                parent: this.injector
            });
        }
        return injector;
    }
}
SiToastNotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiToastNotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SiLinkModule {
}
SiLinkModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLinkModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiLinkModule, declarations: [SiLinkDirective], exports: [SiLinkDirective] });
SiLinkModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiLinkDirective],
                    exports: [SiLinkDirective]
                }]
        }] });

class SiIconModule {
}
SiIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiIconModule, declarations: [SiIconComponent], imports: [CommonModule,
        SiTranslateModule], exports: [SiIconComponent] });
SiIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIconModule, imports: [CommonModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiIconComponent
                    ],
                    exports: [SiIconComponent]
                }]
        }] });

class SiToastNotificationModule {
}
SiToastNotificationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiToastNotificationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationModule, declarations: [SiToastNotificationComponent, SiToastNotificationDrawerComponent], imports: [CommonModule,
        SiIconModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiToastNotificationComponent, SiToastNotificationDrawerComponent] });
SiToastNotificationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationModule, imports: [CommonModule,
        SiIconModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiToastNotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiToastNotificationComponent, SiToastNotificationDrawerComponent],
                    exports: [SiToastNotificationComponent, SiToastNotificationDrawerComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

const RE_UPPER_CASE = /[A-Z]/;
const RE_LOWER_CASE = /[a-z]/;
const RE_DIGITS = /[0-9]/;
const RE_SPECIAL_CHARS = /[\x21-\x2F|\x3A-\x40|\x5B-\x60]/;
const RE_WHITESPACES = /\s/;
class SiPasswordStrengthDirective {
    constructor() {
        this.maxStrength = 1;
        this.noValidation = false;
        /**
         * Define Siemens password strength.
         */
        this.siPasswordStrength = {
            minLength: 8,
            uppercase: true,
            lowercase: true,
            digits: true,
            special: true
        };
        /**
         * Output callback event called when the password changes. The number
         * indicated the number of rules which still can be met. (`-2` --> 2 rules are
         * still unmet, `0` --> all met)
         */
        this.passwordStrengthChanged = new EventEmitter();
    }
    ngOnChanges() {
        this.maxStrength = 1;
        this.maxStrength += this.siPasswordStrength.uppercase ? 1 : 0;
        this.maxStrength += this.siPasswordStrength.lowercase ? 1 : 0;
        this.maxStrength += this.siPasswordStrength.digits ? 1 : 0;
        this.maxStrength += this.siPasswordStrength.special ? 1 : 0;
    }
    validate(control) {
        if (this.getStrength(control.value) >= this.maxStrength) {
            return {};
        }
        return { siPasswordStrength: true };
    }
    getStrength(password) {
        let strength = 0;
        if (password && password !== '') {
            // Strength check
            strength += (password.length >= this.siPasswordStrength.minLength) ? 1 : 0;
            strength += (this.siPasswordStrength.uppercase && password.match(RE_UPPER_CASE)) ? 1 : 0;
            strength += (this.siPasswordStrength.lowercase && password.match(RE_LOWER_CASE)) ? 1 : 0;
            strength += (this.siPasswordStrength.digits && password.match(RE_DIGITS)) ? 1 : 0;
            strength += (this.siPasswordStrength.special && password.match(RE_SPECIAL_CHARS)) ? 1 : 0;
            // Hard limit check
            strength = (password.match(RE_WHITESPACES)) ? 0 : strength;
            this.noValidation = true;
            // Notify listeners
            this.passwordStrengthChanged.emit(strength - this.maxStrength);
            return strength;
        }
        this.noValidation = false;
        this.passwordStrengthChanged.emit();
        return strength;
    }
}
SiPasswordStrengthDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiPasswordStrengthDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiPasswordStrengthDirective, selector: "[siPasswordStrength]", inputs: { siPasswordStrength: "siPasswordStrength" }, outputs: { passwordStrengthChanged: "passwordStrengthChanged" }, host: { properties: { "class.no-validation": "this.noValidation" } }, providers: [{ provide: NG_VALIDATORS, useExisting: SiPasswordStrengthDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siPasswordStrength]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: SiPasswordStrengthDirective, multi: true }]
                }]
        }], propDecorators: { noValidation: [{
                type: HostBinding,
                args: ['class.no-validation']
            }], siPasswordStrength: [{
                type: Input
            }], passwordStrengthChanged: [{
                type: Output
            }] } });
class SiPasswordStrengthComponent {
    constructor() {
        this.cdRef = inject(ChangeDetectorRef);
        this.bad = false;
        this.weak = false;
        this.medium = false;
        this.good = false;
        this.strong = false;
    }
    ngAfterViewInit() {
        this.passwordStrengthDirective.forEach(directive => {
            directive.passwordStrengthChanged.subscribe((strength) => {
                this.strong = strength === 0;
                this.good = strength === -1;
                this.medium = strength === -2;
                this.weak = strength === -3;
                this.bad = strength === -4;
                this.cdRef.markForCheck();
            });
        });
    }
}
SiPasswordStrengthComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiPasswordStrengthComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiPasswordStrengthComponent, selector: "si-password-strength", host: { properties: { "class.bad": "this.bad", "class.weak": "this.weak", "class.medium": "this.medium", "class.good": "this.good", "class.strong": "this.strong" } }, queries: [{ propertyName: "passwordStrengthDirective", predicate: SiPasswordStrengthDirective }], ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{--strength-border-radius: 0 var(--element-radius-1) var(--element-radius-1) 1px;display:block;position:relative}:host:before{background:var(--strength-background);inset-block-end:1px;content:\"\";block-size:3px;inset-inline-start:1px;position:absolute;transition:inline-size calc(.15s * var(--element-animations-enabled, 1)),background calc(.15s * var(--element-animations-enabled, 1)) ease-in-out;inline-size:calc(var(--strength-width) - 2px);border-radius:var(--strength-border-radius)}:host.bad{--strength-background: var(--element-color-bad);--strength-width: 20%}:host.weak{--strength-background: var(--element-color-poor);--strength-width: 40%}:host.medium{--strength-background: var(--element-color-average);--strength-width: 60%}:host.good{--strength-background: var(--element-color-good);--strength-width: 80%}:host.strong{--strength-background: var(--element-color-excellent);--strength-width: 100%;--strength-border-radius: 0 0 1px 1px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-password-strength', template: '<ng-content></ng-content>', styles: [":host{--strength-border-radius: 0 var(--element-radius-1) var(--element-radius-1) 1px;display:block;position:relative}:host:before{background:var(--strength-background);inset-block-end:1px;content:\"\";block-size:3px;inset-inline-start:1px;position:absolute;transition:inline-size calc(.15s * var(--element-animations-enabled, 1)),background calc(.15s * var(--element-animations-enabled, 1)) ease-in-out;inline-size:calc(var(--strength-width) - 2px);border-radius:var(--strength-border-radius)}:host.bad{--strength-background: var(--element-color-bad);--strength-width: 20%}:host.weak{--strength-background: var(--element-color-poor);--strength-width: 40%}:host.medium{--strength-background: var(--element-color-average);--strength-width: 60%}:host.good{--strength-background: var(--element-color-good);--strength-width: 80%}:host.strong{--strength-background: var(--element-color-excellent);--strength-width: 100%;--strength-border-radius: 0 0 1px 1px}\n"] }]
        }], propDecorators: { passwordStrengthDirective: [{
                type: ContentChildren,
                args: [SiPasswordStrengthDirective]
            }], bad: [{
                type: HostBinding,
                args: ['class.bad']
            }], weak: [{
                type: HostBinding,
                args: ['class.weak']
            }], medium: [{
                type: HostBinding,
                args: ['class.medium']
            }], good: [{
                type: HostBinding,
                args: ['class.good']
            }], strong: [{
                type: HostBinding,
                args: ['class.strong']
            }] } });

class SiPasswordStrengthModule {
}
SiPasswordStrengthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiPasswordStrengthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthModule, declarations: [SiPasswordStrengthComponent, SiPasswordStrengthDirective], imports: [CommonModule, FormsModule], exports: [SiPasswordStrengthComponent, SiPasswordStrengthDirective] });
SiPasswordStrengthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthModule, imports: [CommonModule, FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPasswordStrengthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: [SiPasswordStrengthComponent, SiPasswordStrengthDirective],
                    exports: [SiPasswordStrengthComponent, SiPasswordStrengthDirective]
                }]
        }] });

// ------------------------------------------------------------------------------

/* eslint-disable no-console, prefer-arrow/prefer-arrow-functions, @typescript-eslint/naming-convention */
const NG_ON_INIT_NAME = 'ngOnInit';
function requiredImpl(target, prop) {
    if (target.__requiredFields) {
        target.__requiredFields.push(prop);
    }
    else {
        target.__requiredFields = [prop];
        const ngOnInitOriginal = target[NG_ON_INIT_NAME];
        Object.defineProperty(target, NG_ON_INIT_NAME, {
            value() {
                const missing = [];
                target.__requiredFields.forEach((field) => {
                    if (this[field] === null || this[field] === undefined) {
                        missing.push(field);
                    }
                });
                if (missing.length) {
                    const error = `${target.constructor.name} - required @Input() missing: ${missing.join(', ')}`;
                    // Sometimes Angular swallows the Error
                    console.error(error);
                    throw new Error(error);
                }
                if (ngOnInitOriginal) {
                    ngOnInitOriginal.call(this);
                }
            }
        });
    }
}
// note: this _must_ be a function, not an arrow
function Required() { return requiredImpl; }

const runsInElectron = () => {
    const windowAny = window;
    const processAny = windowAny.process;
    // Renderer process
    if (typeof windowAny !== 'undefined' && typeof processAny === 'object' && processAny.type === 'renderer') {
        return true;
    }
    // Main process
    if (typeof processAny !== 'undefined' && typeof processAny.versions === 'object' && !!processAny.versions.electron) {
        return true;
    }
    // Detect the user agent when the `nodeIntegration` option is set to true
    if (typeof navigator === 'object' && typeof navigator.userAgent === 'string' && navigator.userAgent.includes('Electron')) {
        return true;
    }
    return false;
};

const AXIS_X = { axis: 'X', directionRegular: 'end', directionReverse: 'start', upperBound: 'left', lowerBound: 'right',
    size: 'width', windowSize: 'innerWidth' };
const AXIS_Y = { axis: 'Y', directionRegular: 'down', directionReverse: 'up', upperBound: 'top', lowerBound: 'bottom',
    size: 'height', windowSize: 'innerHeight' };
const BOUNDING_RECT_WINDOW = { getBoundingClientRect: () => ({
        top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth
    }) };
const resolveReference = (hostElement, reference) => {
    if (reference) {
        const childReferenceCheck = hostElement.querySelector(reference);
        if (childReferenceCheck) {
            return childReferenceCheck;
        }
        const referenceCheck = hostElement.closest(reference);
        if (referenceCheck) {
            return referenceCheck;
        }
        const all = document.querySelectorAll(reference);
        return all[all.length - 1] ?? null;
    }
    return null;
};
const getScrollParentsChain = (element, axis, scrollParentsList = []) => {
    if (element) {
        const overflowStyle = getComputedStyle(element)[axis === 'X' ? 'overflowX' : 'overflowY'] || 'visible';
        if (element[axis === 'X' ? 'clientWidth' : 'clientHeight'] && overflowStyle !== 'visible' && overflowStyle !== 'hidden') {
            scrollParentsList.push(document.documentElement === element ? BOUNDING_RECT_WINDOW : element);
        }
        if (element !== document.documentElement) {
            getScrollParentsChain(element.dropdownParentElement ?? element.parentElement ?? document.body, axis, scrollParentsList);
        }
    }
    return scrollParentsList;
};
const getCombinedBoundingClientRect = (elements) => {
    let top = 0;
    let bottom = 0;
    let left = 0;
    let right = 0;
    if (elements.length > 0) {
        const tops = [];
        const bottoms = [];
        const lefts = [];
        const rights = [];
        elements.forEach(element => {
            const rect = element.getBoundingClientRect();
            tops.push(rect.top);
            bottoms.push(rect.bottom);
            lefts.push(rect.left);
            rights.push(rect.right);
        });
        top = Math.min(...tops);
        bottom = Math.max(...bottoms);
        left = Math.min(...lefts);
        right = Math.max(...rights);
    }
    return { top, bottom, left, right };
};
const calculatePlacementRefOuterBounds = (currentAxis, placementReferenceElement) => {
    // The chain of scroll parents of the placement ref element up to and often including the body, checking for possible overlaps.
    const placementRefScrollParents = getScrollParentsChain(placementReferenceElement, currentAxis.axis);
    const placementRefScrollParentRects = placementRefScrollParents.map(item => item.getBoundingClientRect());
    // The biggest upper bound (top, left) of all scroll parents (and viewport = 0), the point before which content will be cut off.
    const placementRefScrollParentsUpperBound = Math.max(0, ...placementRefScrollParentRects.map(item => item[currentAxis.upperBound]));
    // The smallest lower bound (bottom, right) of all scroll parents (and viewport), the point after which content will be cut off.
    const placementRefScrollParentsLowerBound = Math.min(window[currentAxis.windowSize], ...placementRefScrollParentRects.map(item => item[currentAxis.lowerBound]));
    return { upper: placementRefScrollParentsUpperBound, lower: placementRefScrollParentsLowerBound };
};
const calculateDirectionFromPlacementRef = (params) => {
    const { currentAxis, positionRegular, positionReverse, contentSize, placementRefBounds, currentDirection, rtl } = params;
    // A number showing how many visible pixels would be available from the placement ref in regular / scroll (down, end) direction
    const placementRefSpaceRegular = placementRefBounds.lower - positionRegular[currentAxis.upperBound];
    // A number showing how many visible pixels would be available from the placement ref in reverse (up, start) direction
    const placementRefSpaceReverse = positionReverse[currentAxis.upperBound] - placementRefBounds.upper;
    if (placementRefSpaceReverse < contentSize && placementRefSpaceRegular < contentSize) {
        // If there would not be enough room in both directions, always prefer regular / scroll (down, end) direction,
        // since this way the element can expand and scroll while this cannot happen if there's not enough room in front.
        return rtl ? currentAxis.directionReverse : currentAxis.directionRegular;
    }
    else if (placementRefSpaceReverse < contentSize) {
        return currentAxis.directionRegular;
    }
    else if (placementRefSpaceRegular < contentSize) {
        return currentAxis.directionReverse;
    }
    // If there would be enough room in both directions, use the set direction (or the regular one if not set).
    return currentDirection;
};
const getAbsoluteContentPosition = (params) => {
    const { direction, placement, element, align, rtl } = params;
    const elementRect = element.getBoundingClientRect();
    const start = rtl ? elementRect.right : elementRect.left;
    const end = rtl ? elementRect.left : elementRect.right;
    let relativeLeftOffset = 0;
    if (direction === 'start' || direction === 'end') {
        relativeLeftOffset = (placement.includes('end') || (!placement.includes('start') && direction === 'end'))
            ? end
            : start;
    }
    else {
        if (placement.includes('start')) {
            relativeLeftOffset = start;
        }
        else {
            if (placement.includes('end') || align === 'end') {
                relativeLeftOffset = end;
            }
            else if (align === 'center') {
                relativeLeftOffset = elementRect.left + (elementRect.right - elementRect.left) / 2;
            }
            else {
                relativeLeftOffset = start;
            }
        }
    }
    let relativeTopOffset = 0;
    if (direction === 'up') {
        relativeTopOffset = placement.includes('bottom') ? elementRect.bottom : elementRect.top;
    }
    else if (direction === 'start' || direction === 'end') {
        relativeTopOffset = !placement.includes('bottom')
            ? elementRect.top
            : elementRect.bottom;
    }
    else {
        relativeTopOffset = placement.includes('top')
            ? elementRect.top
            : elementRect.bottom;
    }
    return { left: relativeLeftOffset, top: relativeTopOffset };
};
const getRelativeContentPosition = (params) => {
    const { contentElement, direction, placement, placementReferenceElement, align, rtl } = params;
    const contentParent = contentElement.offsetParent ?? document.body;
    const contentParentRect = contentParent.getBoundingClientRect();
    const relativePosition = getAbsoluteContentPosition({ direction, placement, element: placementReferenceElement, align, rtl });
    const leftOffset = relativePosition.left - contentParentRect.left + contentParent.scrollLeft;
    const topOffset = relativePosition.top - contentParentRect.top + contentParent.scrollTop;
    return { left: leftOffset, top: topOffset };
};
const getContentPositionString = (params) => {
    const { contentElement, direction, placement, placementReferenceElement, align, rtl } = params;
    const position = getRelativeContentPosition({ contentElement, direction, placement, placementReferenceElement, align, rtl: rtl ?? isRTL() });
    return `translate3d(${position.left}px, ${position.top}px, 0px)`;
};
const responsivelyCheckDirection = (params) => {
    const { isScrolling, currentDirection, contentElements, hostElement, placement, placementReferenceElement, align, responsiveDirectionToPlacement, closeOnPlacementReferenceScrollOut, closeOnContentScrollOut, minSpaceThresholdFactor, placementReverse, rtl } = params;
    const actualRtl = rtl ?? isRTL();
    let responsiveDirection;
    const actualCurrentDirection = actualRtl
        ? (currentDirection === 'start' ? 'end' : currentDirection === 'end' ? 'start' : currentDirection)
        : currentDirection;
    // Defines how the properties to access are named in the current axis.
    const currentAxis = (currentDirection === 'start' || currentDirection === 'end') ? AXIS_X : AXIS_Y;
    // The combined (enclosing) client rect for all component contents.
    const contentsRect = getCombinedBoundingClientRect(contentElements);
    // The chain of scroll parents of the container host element up to and often including the body, checking for possible overlaps.
    const containerScrollParents = getScrollParentsChain(hostElement, currentAxis.axis);
    const containerScrollParentRects = containerScrollParents.map(item => item.getBoundingClientRect());
    // The biggest upper bound (top, left) of all scroll parents (and viewport = 0), the point before which content will be cut off.
    const scrollParentsUpperBound = Math.max(0, ...containerScrollParentRects.map(item => item[currentAxis.upperBound]));
    // The smallest lower bound (bottom, right) of all scroll parents (and viewport), the point after which content will be cut off.
    const scrollParentsLowerBound = Math.min(window[currentAxis.windowSize], ...containerScrollParentRects.map(item => item[currentAxis.lowerBound]));
    // The point from which the component will be opened if opening in regular / scroll (down, end) direction.
    // Influenced by `componentPlacementReference`, `componentPlacement` and `componentAlign`.
    const positionRegular = getAbsoluteContentPosition({ direction: currentAxis.directionRegular, placement, element: placementReferenceElement, align });
    // The point from which the component will be opened if opening in reverse (up, start) direction.
    // Influenced by `componentPlacementReference`, `componentPlacement` and `componentAlign`.
    const positionReverse = getAbsoluteContentPosition({ direction: currentAxis.directionReverse, placement: placementReverse ?? placement, element: placementReferenceElement, align });
    // The enclosing size (height, width) of all contents combined
    const contentsSize = contentsRect[currentAxis.lowerBound] - contentsRect[currentAxis.upperBound];
    // A number showing how many visible pixels would be available in regular / scroll (down, end) direction
    const spaceRegular = scrollParentsLowerBound - positionRegular[currentAxis.upperBound];
    // A number showing how many visible pixels would be available in reverse (up, start) direction
    const spaceReverse = positionReverse[currentAxis.upperBound] - scrollParentsUpperBound;
    let placementRefBounds;
    if (spaceReverse < contentsSize && spaceRegular < contentsSize) {
        // If there would not be enough room in both directions, always prefer regular / scroll (down, end) direction,
        // since this way the element can expand and scroll while this cannot happen if there's not enough room in front.
        responsiveDirection = actualRtl ? currentAxis.directionReverse : currentAxis.directionRegular;
    }
    else if (minSpaceThresholdFactor) {
        const contentsSizeWithThreshold = contentsSize + minSpaceThresholdFactor * contentsSize;
        if (spaceReverse < contentsSizeWithThreshold && spaceRegular < contentsSizeWithThreshold) {
            // If a threshold is set and there would not be enough space including the threshold in both directions
            // use the direction with more space
            if (spaceReverse < spaceRegular) {
                responsiveDirection = currentAxis.directionRegular;
            }
            else {
                responsiveDirection = currentAxis.directionReverse;
            }
        }
        else if (spaceReverse < contentsSizeWithThreshold) {
            responsiveDirection = currentAxis.directionRegular;
        }
        else if (spaceRegular < contentsSizeWithThreshold) {
            responsiveDirection = currentAxis.directionReverse;
        }
    }
    if (!responsiveDirection) {
        if (spaceReverse < contentsSize) {
            responsiveDirection = currentAxis.directionRegular;
        }
        else if (spaceRegular < contentsSize) {
            responsiveDirection = currentAxis.directionReverse;
        }
        else {
            if (responsiveDirectionToPlacement && placementReferenceElement && hostElement !== placementReferenceElement) {
                placementRefBounds = calculatePlacementRefOuterBounds(currentAxis, placementReferenceElement);
                responsiveDirection = calculateDirectionFromPlacementRef({ currentAxis, positionRegular,
                    positionReverse, contentSize: contentsSize, placementRefBounds, currentDirection: actualCurrentDirection, rtl: actualRtl });
            }
            else {
                // If there would be enough room in both directions, use the set direction (or the regular one if not set).
                responsiveDirection = actualCurrentDirection;
            }
        }
    }
    let close = false;
    if (isScrolling) {
        // Check if the container host or placement reference is hidden and close the component. (Mostly done when scrolling.)
        if (closeOnPlacementReferenceScrollOut &&
            placementReferenceElement && hostElement !== placementReferenceElement) {
            if (!placementRefBounds) {
                placementRefBounds = calculatePlacementRefOuterBounds(currentAxis, placementReferenceElement);
            }
            const placementRefRect = placementReferenceElement.getBoundingClientRect();
            // A number showing how many visible pixels of the placement ref would be shown in reverse (up, start) direction
            const placementRefVisibleTop = placementRefRect[currentAxis.lowerBound] - placementRefBounds.upper;
            // A number showing how many visible pixels of the placement ref would be shown in regular / scroll (down, end) direction
            const placementRefVisibleBottom = placementRefBounds.lower - placementRefRect[currentAxis.upperBound];
            if (placementRefVisibleTop < 0 || placementRefVisibleBottom < 0) {
                // Close the component if the placement ref is not visible.
                close = true;
            }
        }
        if (!close && closeOnContentScrollOut) {
            // A number showing how many visible pixels would be available the other way if set to regular / scroll (down, end) direction
            const spaceRegularOtherSide = scrollParentsLowerBound - positionReverse[currentAxis.upperBound];
            // A number showing how many visible pixels would be available the other way if set to reverse (up, start) direction
            const spaceReverseOtherSide = positionRegular[currentAxis.upperBound] - scrollParentsUpperBound;
            if (spaceRegularOtherSide < 0 || spaceReverseOtherSide < 0) {
                // Close the component if there is not enough space even in the other direction.
                close = true;
            }
        }
    }
    responsiveDirection = actualRtl
        ? (responsiveDirection === 'start' ? 'end' : responsiveDirection === 'end' ? 'start' : responsiveDirection)
        : responsiveDirection;
    return { responsiveDirection, close };
};

/**
 * Gets the width of the scrollbar.  Nesc for windows
 * http://stackoverflow.com/a/13382873/888165
 */
class ScrollbarHelper {
    constructor() {
        this.document = inject(DOCUMENT);
        this.width = this.getWidth();
    }
    getWidth() {
        const outer = this.document.createElement('div');
        outer.style.visibility = 'hidden';
        outer.style.width = '100px';
        outer.style.msOverflowStyle = 'scrollbar';
        this.document.body.appendChild(outer);
        const widthNoScroll = outer.offsetWidth;
        outer.style.overflow = 'scroll';
        const inner = this.document.createElement('div');
        inner.style.width = '100%';
        outer.appendChild(inner);
        const widthWithScroll = inner.offsetWidth;
        outer.parentNode?.removeChild(outer);
        return widthNoScroll - widthWithScroll;
    }
}
ScrollbarHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollbarHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ScrollbarHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollbarHelper, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollbarHelper, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiAccordionHCollapseService {
    constructor() {
        this.hcollapsed$ = new ReplaySubject(1);
        this.open$ = new Subject();
    }
}
SiAccordionHCollapseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionHCollapseService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiAccordionHCollapseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionHCollapseService, providedIn: null });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionHCollapseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: null
                }]
        }] });

class SiAccordionService {
    constructor() {
        this.toggle$ = new Subject();
        this.fullHeight$ = new ReplaySubject(1);
    }
}
SiAccordionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiAccordionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionService, providedIn: null });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: null
                }]
        }] });

let controlIdCounter$1 = 1;
class SiCollapsiblePanelComponent {
    constructor() {
        this.headerCssClasses = '';
        this.contentBgClasses = '';
        this.contentCssClasses = '';
        this.opened = false;
        this.disabled = false;
        this.toggle = new EventEmitter();
        this.hcollapsed = false;
        this.fullHeight = false;
        this.cdRef = inject(ChangeDetectorRef);
        this.accordionService = inject(SiAccordionService, { optional: true });
        this.accordionHCollapseService = inject(SiAccordionHCollapseService, { optional: true });
        this.toggleSubscription = new Subscription();
        this.enableAnimation = true;
        this.animationsGloballyDisabled = areAnimationsDisabled();
        this.lastScrollPos = 0;
        if (this.accordionService) {
            this.toggleSubscription.add(this.accordionService.fullHeight$.subscribe(resp => {
                this.fullHeight = resp;
                this.cdRef.markForCheck();
            }));
            this.toggleSubscription.add(this.accordionService.toggle$.pipe(filter(item => item !== this)).subscribe(() => this.openClose(false)));
        }
        if (this.accordionHCollapseService) {
            this.toggleSubscription.add(this.accordionHCollapseService.hcollapsed$.subscribe(state => {
                this.hcollapsed = state;
                this.cdRef.markForCheck();
            }));
        }
        this.controlId = '__si-collapsible-' + controlIdCounter$1++;
        this.headerId = this.controlId + '-header';
    }
    get showHide() {
        if (this.enableAnimation && !this.animationsGloballyDisabled) {
            return this.opened ? 'show' : 'hide';
        }
        return 'disabled';
    }
    get isHCollapsible() {
        return !!this.accordionHCollapseService;
    }
    ngOnDestroy() {
        this.toggleSubscription?.unsubscribe();
    }
    openClose(open, enableAnimation = true) {
        this.opened = open;
        this.enableAnimation = enableAnimation;
        this.cdRef.markForCheck();
        if (open) {
            setTimeout(() => {
                this.contentRef.nativeElement.scrollTop = this.lastScrollPos;
            });
        }
        else {
            this.lastScrollPos = this.contentRef.nativeElement.scrollTop;
        }
    }
    doToggle(event) {
        if (this.disabled) {
            return;
        }
        event?.preventDefault();
        this.toggle.emit();
        this.openClose(this.hcollapsed || !this.opened);
        this.accordionService?.toggle$.next(this);
        if (this.hcollapsed) {
            this.accordionHCollapseService?.open$.next(this);
        }
    }
    keydown(event) {
        if (event.key === 'Enter' || event.key === 'Space' || event.key === ' ') {
            this.doToggle(undefined);
        }
    }
}
SiCollapsiblePanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCollapsiblePanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiCollapsiblePanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiCollapsiblePanelComponent, selector: "si-collapsible-panel", inputs: { heading: "heading", headerCssClasses: "headerCssClasses", contentBgClasses: "contentBgClasses", contentCssClasses: "contentCssClasses", opened: "opened", icon: "icon", disabled: "disabled", colorVariant: "colorVariant" }, outputs: { toggle: "toggle" }, host: { properties: { "class.opened": "this.opened", "class": "this.colorVariant", "class.hcollapsed": "this.hcollapsed", "class.full-height": "this.fullHeight" } }, viewQueries: [{ propertyName: "contentRef", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"collapsible-header focus-inside ps-6 pe-5\"\n  [ngClass]=\"headerCssClasses\"\n  [attr.tabindex]=\"disabled ? '' : '0'\"\n  [id]=\"headerId\"\n  [class.open]=\"opened\"\n  [class.disabled]=\"disabled\"\n  [title]=\"hcollapsed ? (heading | translate) : ''\"\n  role=\"button\"\n  [attr.aria-expanded]=\"opened && !hcollapsed\"\n  [attr.aria-disabled]=\"disabled\"\n  [attr.aria-controls]=\"controlId\"\n  [attr.aria-label]=\"hcollapsed ? (heading | translate) : ''\"\n  (keydown)=\"keydown($event)\"\n  (click)=\"doToggle($event)\"\n>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon ms-n2 me-2\"\n    aria-hidden=\"true\"\n    [class.collapsed-icon]=\"isHCollapsible\"\n    [ngClass]=\"icon\"\n  ></i>\n  <span class=\"si-title-2\">{{ heading | translate }}</span>\n  <span class=\"ms-auto overflow-hidden\">\n    <span class=\"icon dropdown-caret element-down-2\" aria-hidden=\"true\"></span>\n  </span>\n</div>\n<div\n  #content\n  [id]=\"controlId\"\n  class=\"collapsible-content\"\n  [ngClass]=\"contentBgClasses\"\n  role=\"region\"\n  [@showHide]=\"showHide\"\n  [attr.aria-labelledby]=\"headerId\"\n  [class.full-height]=\"fullHeight\"\n>\n  <div *ngIf=\"opened\">\n    <div [ngClass]=\"contentCssClasses\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;background:var(--cp-base, var(--element-base-1))}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}:host:not(:first-of-type){padding-block-start:2px}:host:not(.hcollapsed) .collapsed-icon{display:none}:host.hcollapsed .collapsible-header span,:host.hcollapsed .collapsible-content{display:none}:host.hcollapsed .collapsible-header{background:var(--element-base-1);padding-block:8px}:host.hcollapsed .collapsible-header:hover{background:var(--element-base-1-hover)}:host.full-height{transition:flex-grow calc(.5s * var(--element-animations-enabled, 1))}:host.full-height.opened,:host.full-height .collapsible-content{flex:1 1 0}:host.full-height .collapsible-content>div{flex:1 0 0}.collapsible-header{display:flex;align-items:center;background:var(--cp-header, var(--element-base-0));color:var(--element-text-primary);padding-block:12px}.collapsible-header.disabled{color:var(--element-text-disabled)}.collapsible-header:not(.disabled){cursor:pointer}.collapsible-header:not(.disabled):hover{text-decoration:none;background:var(--element-base-1-selected)}.dropdown-caret{transition:transform calc(.5s * var(--element-animations-enabled, 1));color:var(--element-ui-0)}.open .dropdown-caret{transform:rotate(180deg);color:var(--element-ui-0-hover)}.collapsible-content{overflow:hidden;display:flex;flex-direction:column}.collapsible-content.full-height{overflow:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], animations: [
        trigger('showHide', [
            transition('*=>hide', [
                style({ overflow: 'hidden' }),
                query(':leave', [style({ blockSize: '*' }), animate('0.5s ease', style({ blockSize: '0' }))], { optional: true })
            ]),
            transition('*=>show', [
                style({ overflow: 'hidden' }),
                query(':enter', [style({ blockSize: '0' }), animate('0.5s ease', style({ blockSize: '*' }))], { optional: true })
            ])
        ])
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    Required()
], SiCollapsiblePanelComponent.prototype, "heading", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCollapsiblePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-collapsible-panel', animations: [
                        trigger('showHide', [
                            transition('*=>hide', [
                                style({ overflow: 'hidden' }),
                                query(':leave', [style({ blockSize: '*' }), animate('0.5s ease', style({ blockSize: '0' }))], { optional: true })
                            ]),
                            transition('*=>show', [
                                style({ overflow: 'hidden' }),
                                query(':enter', [style({ blockSize: '0' }), animate('0.5s ease', style({ blockSize: '*' }))], { optional: true })
                            ])
                        ])
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"collapsible-header focus-inside ps-6 pe-5\"\n  [ngClass]=\"headerCssClasses\"\n  [attr.tabindex]=\"disabled ? '' : '0'\"\n  [id]=\"headerId\"\n  [class.open]=\"opened\"\n  [class.disabled]=\"disabled\"\n  [title]=\"hcollapsed ? (heading | translate) : ''\"\n  role=\"button\"\n  [attr.aria-expanded]=\"opened && !hcollapsed\"\n  [attr.aria-disabled]=\"disabled\"\n  [attr.aria-controls]=\"controlId\"\n  [attr.aria-label]=\"hcollapsed ? (heading | translate) : ''\"\n  (keydown)=\"keydown($event)\"\n  (click)=\"doToggle($event)\"\n>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon ms-n2 me-2\"\n    aria-hidden=\"true\"\n    [class.collapsed-icon]=\"isHCollapsible\"\n    [ngClass]=\"icon\"\n  ></i>\n  <span class=\"si-title-2\">{{ heading | translate }}</span>\n  <span class=\"ms-auto overflow-hidden\">\n    <span class=\"icon dropdown-caret element-down-2\" aria-hidden=\"true\"></span>\n  </span>\n</div>\n<div\n  #content\n  [id]=\"controlId\"\n  class=\"collapsible-content\"\n  [ngClass]=\"contentBgClasses\"\n  role=\"region\"\n  [@showHide]=\"showHide\"\n  [attr.aria-labelledby]=\"headerId\"\n  [class.full-height]=\"fullHeight\"\n>\n  <div *ngIf=\"opened\">\n    <div [ngClass]=\"contentCssClasses\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;background:var(--cp-base, var(--element-base-1))}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}:host:not(:first-of-type){padding-block-start:2px}:host:not(.hcollapsed) .collapsed-icon{display:none}:host.hcollapsed .collapsible-header span,:host.hcollapsed .collapsible-content{display:none}:host.hcollapsed .collapsible-header{background:var(--element-base-1);padding-block:8px}:host.hcollapsed .collapsible-header:hover{background:var(--element-base-1-hover)}:host.full-height{transition:flex-grow calc(.5s * var(--element-animations-enabled, 1))}:host.full-height.opened,:host.full-height .collapsible-content{flex:1 1 0}:host.full-height .collapsible-content>div{flex:1 0 0}.collapsible-header{display:flex;align-items:center;background:var(--cp-header, var(--element-base-0));color:var(--element-text-primary);padding-block:12px}.collapsible-header.disabled{color:var(--element-text-disabled)}.collapsible-header:not(.disabled){cursor:pointer}.collapsible-header:not(.disabled):hover{text-decoration:none;background:var(--element-base-1-selected)}.dropdown-caret{transition:transform calc(.5s * var(--element-animations-enabled, 1));color:var(--element-ui-0)}.open .dropdown-caret{transform:rotate(180deg);color:var(--element-ui-0-hover)}.collapsible-content{overflow:hidden;display:flex;flex-direction:column}.collapsible-content.full-height{overflow:auto}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { heading: [{
                type: Input
            }], headerCssClasses: [{
                type: Input
            }], contentBgClasses: [{
                type: Input
            }], contentCssClasses: [{
                type: Input
            }], opened: [{
                type: HostBinding,
                args: ['class.opened']
            }, {
                type: Input
            }], icon: [{
                type: Input
            }], disabled: [{
                type: Input
            }], colorVariant: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], toggle: [{
                type: Output
            }], hcollapsed: [{
                type: HostBinding,
                args: ['class.hcollapsed']
            }], fullHeight: [{
                type: HostBinding,
                args: ['class.full-height']
            }], contentRef: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }] } });

class SiAboutComponent {
    constructor() {
        this.http = inject(HttpClient);
        this.sanitizer = inject(DomSanitizer);
        this.cdRef = inject(ChangeDetectorRef);
        /**
         * Additional links listed in the about section.
         */
        this.links = [];
        this.licenseApi = [];
        this.trackByIndex = (index) => index;
    }
    ngOnInit() {
        if (this.licenseInfo.api) {
            this.http.get(this.licenseInfo.api, { responseType: 'json' })
                .subscribe(data => {
                this.licenseApi = data;
                if (this.licenseApi.length === 1) {
                    const apiInfo = this.licenseApi[0];
                    apiInfo.isOpen = false;
                    this.toggleLoadLicenseApi(apiInfo);
                }
                this.cdRef.markForCheck();
            });
        }
    }
    ngOnChanges() {
        if (this.licenseInfo.iframe != null) {
            this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.licenseInfo.iframe);
        }
    }
    toggleLoadLicenseApi(apiInfo) {
        if (!apiInfo.isOpen && !apiInfo.files) {
            this.http.get(apiInfo.href, { responseType: 'json' })
                .subscribe(files => {
                apiInfo.files = files;
                this.cdRef.markForCheck();
            });
        }
        apiInfo.isOpen = !apiInfo.isOpen;
        this.cdRef.markForCheck();
    }
    toggleLoadLicenseContent(apiInfo) {
        apiInfo.isOpen = !apiInfo.isOpen;
        if (!apiInfo.content) {
            this.http.get(apiInfo.href, { responseType: 'text' })
                .subscribe((content) => {
                apiInfo.content = content;
                this.cdRef.markForCheck();
            });
        }
        this.cdRef.markForCheck();
    }
}
SiAboutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAboutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiAboutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiAboutComponent, selector: "si-about", inputs: { aboutTitle: "aboutTitle", licenseInfo: "licenseInfo", icon: "icon", iconName: "iconName", appName: "appName", subheading: "subheading", acceptableUsePolicyLink: "acceptableUsePolicyLink", imprintLink: "imprintLink", privacyLink: "privacyLink", cookieNoticeLink: "cookieNoticeLink", termsLink: "termsLink", links: "links" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"row mx-6 mt-6\">\n  <div class=\"col-md-6 mb-6\">\n    <div class=\"card elevation-1 overflow-hidden\">\n      <div class=\"card-header text-truncate\">\n        {{ aboutTitle }}\n      </div>\n\n      <div>\n        <div class=\"list-group-item text-center\">\n          <img *ngIf=\"icon\" class=\"rounded-circle\" [src]=\"icon\" [alt]=\"appName + '\\'s Logo'\" height=\"150\">\n          <i *ngIf=\"!icon && iconName\" class=\"app-icon\" [ngClass]=\"iconName\" role=\"presentation\"></i>\n          <h3>{{ appName }}</h3>\n          <p *ngFor=\"let item of subheading; trackBy: trackByIndex\">{{ item }}</p>\n        </div>\n\n        <ng-container *ngIf=\"imprintLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: imprintLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"privacyLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: privacyLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"cookieNoticeLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: cookieNoticeLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"termsLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: termsLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"acceptableUsePolicyLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: acceptableUsePolicyLink }\">\n        </ng-container>\n        <ng-container *ngFor=\"let item of links; trackBy: trackByIndex\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: item }\">\n        </ng-container>\n      </div>\n    </div>\n  </div>\n\n  <div class=\"col-md-6 mb-6\">\n    <div class=\"card elevation-1 overflow-hidden\" [class.h-100]=\"licenseInfo.iframe\">\n      <div class=\"card-header text-truncate\">\n        {{ licenseInfo.title }}\n      </div>\n\n      <div class=\"h-100\">\n        <pre *ngIf=\"licenseInfo.text\" class=\"list-group-item\">{{ licenseInfo.text }}</pre>\n\n        <iframe *ngIf=\"licenseInfo.iframe\" class=\"w-100 h-100\" [src]=\"sanitizedUrl\" [title]=\"licenseInfo.title\"></iframe>\n\n        <ng-container *ngIf=\"licenseInfo.api\">\n          <ng-container *ngFor=\"let api of licenseApi; trackBy: trackByIndex\">\n            <si-collapsible-panel\n              [heading]=\"api.name\"\n              colorVariant=\"base-0\"\n              (toggle)=\"toggleLoadLicenseApi(api)\"\n            >\n              <si-collapsible-panel\n                *ngFor=\"let file of api.files; trackBy: trackByIndex\"\n                [icon]=\"licenseInfo.icon ? licenseInfo.icon : 'element-document'\"\n                [heading]=\"file.name\"\n                colorVariant=\"base-0\"\n                (toggle)=\"toggleLoadLicenseContent(file)\"\n              >\n                <pre class=\"px-6 pt-6\">{{ file.content }}</pre>\n              </si-collapsible-panel>\n            </si-collapsible-panel>\n          </ng-container>\n        </ng-container>\n      </div>\n    </div>\n  </div>\n</div>\n\n<ng-template #linkTemplate let-link=\"link\">\n  <a class=\"list-group-item si-title-2 text-body focus-inside\" [siLink]=\"link\" siLinkDefaultTarget=\"_blank\">\n    {{ (link.title ?? '') | translate }}\n  </a>\n</ng-template>\n", styles: [".list-group-item{border-width:0 0 1px;padding-inline:16px}.list-group-item.with-border:first-child{border-block-start-width:1px}.list-group-item:last-child{border-block-end-width:0}.app-icon{font-size:150px}iframe{border-style:none;min-block-size:500px;margin-block-end:-5px}pre{word-break:break-word;white-space:pre-wrap}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: SiCollapsiblePanelComponent, selector: "si-collapsible-panel", inputs: ["heading", "headerCssClasses", "contentBgClasses", "contentCssClasses", "opened", "icon", "disabled", "colorVariant"], outputs: ["toggle"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiAboutComponent.prototype, "aboutTitle", void 0);
__decorate([
    Required()
], SiAboutComponent.prototype, "licenseInfo", void 0);
__decorate([
    Required()
], SiAboutComponent.prototype, "appName", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAboutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-about', template: "<div class=\"row mx-6 mt-6\">\n  <div class=\"col-md-6 mb-6\">\n    <div class=\"card elevation-1 overflow-hidden\">\n      <div class=\"card-header text-truncate\">\n        {{ aboutTitle }}\n      </div>\n\n      <div>\n        <div class=\"list-group-item text-center\">\n          <img *ngIf=\"icon\" class=\"rounded-circle\" [src]=\"icon\" [alt]=\"appName + '\\'s Logo'\" height=\"150\">\n          <i *ngIf=\"!icon && iconName\" class=\"app-icon\" [ngClass]=\"iconName\" role=\"presentation\"></i>\n          <h3>{{ appName }}</h3>\n          <p *ngFor=\"let item of subheading; trackBy: trackByIndex\">{{ item }}</p>\n        </div>\n\n        <ng-container *ngIf=\"imprintLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: imprintLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"privacyLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: privacyLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"cookieNoticeLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: cookieNoticeLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"termsLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: termsLink }\">\n        </ng-container>\n        <ng-container *ngIf=\"acceptableUsePolicyLink\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: acceptableUsePolicyLink }\">\n        </ng-container>\n        <ng-container *ngFor=\"let item of links; trackBy: trackByIndex\" [ngTemplateOutlet]=\"linkTemplate\" [ngTemplateOutletContext]=\"{ link: item }\">\n        </ng-container>\n      </div>\n    </div>\n  </div>\n\n  <div class=\"col-md-6 mb-6\">\n    <div class=\"card elevation-1 overflow-hidden\" [class.h-100]=\"licenseInfo.iframe\">\n      <div class=\"card-header text-truncate\">\n        {{ licenseInfo.title }}\n      </div>\n\n      <div class=\"h-100\">\n        <pre *ngIf=\"licenseInfo.text\" class=\"list-group-item\">{{ licenseInfo.text }}</pre>\n\n        <iframe *ngIf=\"licenseInfo.iframe\" class=\"w-100 h-100\" [src]=\"sanitizedUrl\" [title]=\"licenseInfo.title\"></iframe>\n\n        <ng-container *ngIf=\"licenseInfo.api\">\n          <ng-container *ngFor=\"let api of licenseApi; trackBy: trackByIndex\">\n            <si-collapsible-panel\n              [heading]=\"api.name\"\n              colorVariant=\"base-0\"\n              (toggle)=\"toggleLoadLicenseApi(api)\"\n            >\n              <si-collapsible-panel\n                *ngFor=\"let file of api.files; trackBy: trackByIndex\"\n                [icon]=\"licenseInfo.icon ? licenseInfo.icon : 'element-document'\"\n                [heading]=\"file.name\"\n                colorVariant=\"base-0\"\n                (toggle)=\"toggleLoadLicenseContent(file)\"\n              >\n                <pre class=\"px-6 pt-6\">{{ file.content }}</pre>\n              </si-collapsible-panel>\n            </si-collapsible-panel>\n          </ng-container>\n        </ng-container>\n      </div>\n    </div>\n  </div>\n</div>\n\n<ng-template #linkTemplate let-link=\"link\">\n  <a class=\"list-group-item si-title-2 text-body focus-inside\" [siLink]=\"link\" siLinkDefaultTarget=\"_blank\">\n    {{ (link.title ?? '') | translate }}\n  </a>\n</ng-template>\n", styles: [".list-group-item{border-width:0 0 1px;padding-inline:16px}.list-group-item.with-border:first-child{border-block-start-width:1px}.list-group-item:last-child{border-block-end-width:0}.app-icon{font-size:150px}iframe{border-style:none;min-block-size:500px;margin-block-end:-5px}pre{word-break:break-word;white-space:pre-wrap}\n"] }]
        }], propDecorators: { aboutTitle: [{
                type: Input
            }], licenseInfo: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconName: [{
                type: Input
            }], appName: [{
                type: Input
            }], subheading: [{
                type: Input
            }], acceptableUsePolicyLink: [{
                type: Input
            }], imprintLink: [{
                type: Input
            }], privacyLink: [{
                type: Input
            }], cookieNoticeLink: [{
                type: Input
            }], termsLink: [{
                type: Input
            }], links: [{
                type: Input
            }] } });

const PANEL_MIN_HEIGHT = 100;
class SiAccordionComponent {
    constructor() {
        this.expandFirstPanel = true;
        this.fullHeight = false;
        this.hcollapsed = false;
        this.responsive = false;
        this.service = inject(SiAccordionService);
        this.resizeObserver = inject(ResizeObserverService);
        this.element = inject(ElementRef);
        this.cdRef = inject(ChangeDetectorRef);
        this.accordionHCollapseService = inject(SiAccordionHCollapseService, { optional: true });
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    ngOnChanges() {
        this.service.fullHeight$.next(this.fullHeight && !this.responsive);
    }
    ngAfterContentInit() {
        this.subscription = this.resizeObserver.observe(this.element.nativeElement, 100, true, true)
            .subscribe(() => this.calcFullHeight());
        if (this.accordionHCollapseService) {
            this.subscription.add(this.accordionHCollapseService.hcollapsed$.subscribe(state => {
                this.hcollapsed = state;
                this.cdRef.markForCheck();
            }));
        }
        if (this.panels.length) {
            this.panels.first.openClose(this.expandFirstPanel, false);
        }
    }
    calcFullHeight() {
        if (this.panels?.length) {
            const height = this.element.nativeElement.offsetHeight;
            this.responsive = !this.hcollapsed && height < this.panels.length * PANEL_MIN_HEIGHT;
            this.service.fullHeight$.next(this.fullHeight && !this.responsive);
        }
    }
}
SiAccordionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiAccordionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiAccordionComponent, selector: "si-accordion", inputs: { expandFirstPanel: "expandFirstPanel", fullHeight: "fullHeight", hcollapsed: "hcollapsed", colorVariant: "colorVariant" }, host: { properties: { "class.full-height": "this.fullHeight", "class.hcollapsed": "this.hcollapsed", "class": "this.colorVariant" } }, providers: [SiAccordionService], queries: [{ propertyName: "panels", predicate: SiCollapsiblePanelComponent }], usesOnChanges: true, ngImport: i0, template: '<div><ng-content></ng-content></div>', isInline: true, styles: [":host,:host>div{display:flex;flex-direction:column}:host.full-height{block-size:100%}:host.full-height>div{overflow-y:auto;flex:1 1 0}:host.full-height.hcollapsed>div{overflow-y:unset;flex-basis:auto}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-accordion', template: '<div><ng-content></ng-content></div>', providers: [SiAccordionService], changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host,:host>div{display:flex;flex-direction:column}:host.full-height{block-size:100%}:host.full-height>div{overflow-y:auto;flex:1 1 0}:host.full-height.hcollapsed>div{overflow-y:unset;flex-basis:auto}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}\n"] }]
        }], propDecorators: { expandFirstPanel: [{
                type: Input
            }], fullHeight: [{
                type: HostBinding,
                args: ['class.full-height']
            }, {
                type: Input
            }], hcollapsed: [{
                type: HostBinding,
                args: ['class.hcollapsed']
            }, {
                type: Input
            }], colorVariant: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], panels: [{
                type: ContentChildren,
                args: [SiCollapsiblePanelComponent]
            }] } });

class SiAccordionModule {
}
SiAccordionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiAccordionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionModule, declarations: [SiAccordionComponent, SiCollapsiblePanelComponent], imports: [CommonModule, SiTranslateModule], exports: [SiAccordionComponent, SiCollapsiblePanelComponent] });
SiAccordionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiAccordionComponent, SiCollapsiblePanelComponent],
                    exports: [SiAccordionComponent, SiCollapsiblePanelComponent]
                }]
        }] });

class SiAboutModule {
}
SiAboutModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAboutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiAboutModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiAboutModule, declarations: [SiAboutComponent], imports: [CommonModule,
        SiAccordionModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiAboutComponent] });
SiAboutModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAboutModule, imports: [CommonModule,
        SiAccordionModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAboutModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiAccordionModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiAboutComponent],
                    exports: [SiAboutComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

/**
 * Directive to emit events on element size change. Use like this:
 *   `<div (siResizeObserver)="handleResize($event)">`
 * When the size of the element changes, an event in the format
 *   `{ width: number, height: number }`
 * will be emitted. Also an initial event will be emitted on init.
 *
 * By default, events are throttled and to an event every 100ms. To change
 * this, add `[resizeThrottle]="200"` on the same element. Input in milliseconds.
 */
class SiResizeObserverDirective {
    constructor() {
        this.resizeThrottle = 100;
        this.siResizeObserver = new EventEmitter();
        this.element = inject(ElementRef);
        this.service = inject(ResizeObserverService);
    }
    ngOnInit() {
        this.subs = this.service.observe(this.element.nativeElement, this.resizeThrottle, true)
            .subscribe(this.siResizeObserver);
    }
    ngOnDestroy() {
        this.subs?.unsubscribe();
    }
}
SiResizeObserverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiResizeObserverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: { resizeThrottle: "resizeThrottle" }, outputs: { siResizeObserver: "siResizeObserver" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siResizeObserver]'
                }]
        }], propDecorators: { resizeThrottle: [{
                type: Input
            }], siResizeObserver: [{
                type: Output
            }] } });

/**
 * Defines how many items should be displayed at the end of the breadcrumb if possible.
 */
const NUMBER_OF_SHOWN_ITEMS_AT_END = 2;
/**
 * Defines how long a display item can be without it being shortened.
 * Cannot be lower than 4.
 * If this is 0, titles will not be shortened
 */
const ITEM_MAX_LENGTH = 30;
/**
 * Defines how many characters of an item are always displayed in the beginning.
 * Must be at least 2 less than ITEM_MAX_LENGTH
 */
const ITEM_CHARACTERS_ALWAYS_DISPLAYED_IN_BEGINNING = 10;
/**
 * Defines the width of the root icon in pixels.
 */
const ROOT_ICON_WIDTH = 24;
let controlIdCounter = 1;
class SiBreadcrumbComponent {
    constructor() {
        /** Shows the "root" route as the provided title string instead of an icon. */
        this.showRootAsText = false;
        /** Aria label for the main breadcrumb navigation. Needed for a11y. */
        this.ariaLabel = 'breadcrumbs';
        this.itemsProcessed = false;
        this.enumeratedItems = [];
        this.itemsShown = [];
        this.itemsHidden = [];
        this.itemsShortened = [];
        this.breadcrumbShortened = false;
        this.numberOfItems = 0;
        this.ellipsesLevel = 0;
        this.breadcrumbDropdownOpen = [];
        this.addExpandDropdown = false;
        this.controlId = `__si-breadcrumb-${controlIdCounter++}-`;
        this.trackByIndex = (index) => index;
        this.changeDetector = inject(ChangeDetectorRef);
        this.translate = inject(SiTranslateService);
    }
    ngOnChanges() {
        // Reprocess items on every change and on init
        this.processItems();
    }
    ngOnDestroy() {
        this.translationSubscription?.unsubscribe();
    }
    processItems() {
        this.numberOfItems = this.items.length;
        this.translationSubscription?.unsubscribe();
        this.translationSubscription = this.translate.translateAsync(this.items.map(item => item.title)).subscribe(translatedTitles => {
            // Add the level to the items and check if they need to be shortened.
            // If they need to be shortened, shorten them at a convenient place.
            // Set the lastItem tag to true for the last item
            let counter = -1;
            const enumeratedItems = this.items.map(item => {
                counter++;
                const title = translatedTitles[item.title];
                let shortened = false;
                let shortenedTitle = title;
                // If this is not the last item and the title too long, shorten the title
                if (counter !== this.numberOfItems - 1 && title && title.length > ITEM_MAX_LENGTH) {
                    shortened = true;
                    // This regex gets the last space, dash or underscore.
                    const regexMatch = title
                        .slice(ITEM_CHARACTERS_ALWAYS_DISPLAYED_IN_BEGINNING, ITEM_MAX_LENGTH - 2)
                        .match(/^.*[- _](?=.*?$)/);
                    if (regexMatch) {
                        shortenedTitle = title.slice(0, ITEM_CHARACTERS_ALWAYS_DISPLAYED_IN_BEGINNING + regexMatch[0].length - 1);
                    }
                    else {
                        shortenedTitle = title.slice(0, ITEM_MAX_LENGTH - 3);
                    }
                }
                // If the root element should be displayed as text, set level not to 0.
                // This is used to distinguish in the template between icon and text.
                const level = counter === 0 && this.showRootAsText ? -1 : counter;
                return {
                    ...item,
                    title,
                    level,
                    hide: false,
                    shortened,
                    shortenedTitle,
                    lastItem: (counter === this.numberOfItems - 1)
                };
            });
            this.itemsShown = enumeratedItems;
            this.itemsHidden = [];
            this.breadcrumbShortened = false;
            this.breadcrumbDropdownOpen = enumeratedItems.map(item => false);
            this.itemsProcessed = true;
            this.resetBreadcrumb();
        });
    }
    /*
     * Toggle dropdown (on click of ellipses), either for
     * the general dropdown list if itemLevel is at ellipsesLevel
     * or otherwise the name expansion at the specified item level.
     * Close any open dropdown before opening a new one.
     */
    toggleBreadcrumbDropdown(itemLevel) {
        const previousState = this.breadcrumbDropdownOpen[itemLevel];
        this.breadcrumbDropdownOpen.splice(itemLevel, 1);
        if (this.breadcrumbDropdownOpen.some(item => item)) {
            this.breadcrumbDropdownOpen = this.breadcrumbDropdownOpen.map(item => false);
        }
        this.breadcrumbDropdownOpen.splice(itemLevel, 0, !previousState);
    }
    // Close dropdown on click anywhere else
    documentClick(targetElement) {
        if (this.breadcrumbDropdownOpen.some(item => item)) {
            if (!this.breadcrumbElement?.nativeElement.contains(targetElement)) {
                this.breadcrumbDropdownOpen = this.breadcrumbDropdownOpen.map(item => false);
            }
        }
    }
    resetBreadcrumb() {
        if (this.itemsProcessed) {
            this.numberOfItems = this.items.length;
            // Add an additional the ellipses item to the end of the shownItems (breadcrumb items).
            // Disable addExpandDropdown for now, to make every item a proper SiBreadcrumbItemComponent.
            const ellipsesItem = { title: '...', level: this.numberOfItems, shortenedTitle: '' };
            this.itemsShown.push(ellipsesItem);
            if (this.breadcrumbShortened) {
                // If the breadcrumb was shortened before, remove the ellipses and add back itemsHidden (breadcrumb dropdown items).
                this.breadcrumbShortened = false;
                this.itemsShown.splice(this.ellipsesLevel, 1, ...this.itemsHidden);
                this.itemsHidden = [];
            }
            this.addExpandDropdown = false;
            // Wait for the next change detection cycle to measure the updated item length.
            this.changeDetector.detectChanges();
            this.calculateBreadcrumb();
        }
    }
    calculateBreadcrumb() {
        this.addExpandDropdown = true;
        const maxWidth = this.breadcrumbElement.nativeElement.clientWidth;
        const breadcrumbElementsList = this.breadcrumbElements.map(item => item);
        // Measure the length of the last additional ellipses item, then remove it from itemsShown (breadcrumb items).
        const ellipsesWidth = breadcrumbElementsList[this.numberOfItems].nativeElement.offsetWidth;
        this.itemsShown.splice(this.numberOfItems, 1);
        let currentWidth = this.showRootAsText ? 0 : ROOT_ICON_WIDTH;
        const numberOfItemsKeptAtEnd = Math.min(NUMBER_OF_SHOWN_ITEMS_AT_END, this.numberOfItems - 1);
        let reverseCounter = this.numberOfItems;
        // Test for numberOfItemsKeptAtEnd items from the end if they still fit, if not, set breadcrumbShortened to true.
        breadcrumbElementsList.slice(this.numberOfItems - numberOfItemsKeptAtEnd, this.numberOfItems).reverse().map(item => {
            if (!this.breadcrumbShortened) {
                const currentItemWidth = item.nativeElement.offsetWidth;
                if (currentWidth + currentItemWidth > maxWidth) {
                    this.breadcrumbShortened = true;
                    // Test if the ellipses item still fits, if not remove last (actually next in original order) item as well.
                    if (currentWidth + ellipsesWidth > maxWidth) {
                        reverseCounter++;
                    }
                }
                else {
                    currentWidth += currentItemWidth;
                    reverseCounter--;
                }
            }
        });
        const start = this.showRootAsText ? 0 : 1;
        let counter = start;
        // If breadcrumbShortened is not true yet, test for the rest of the items from the start
        // Whether they still fit, if not, set breadcrumbShortened to true.
        breadcrumbElementsList.slice(start, this.numberOfItems - numberOfItemsKeptAtEnd).map(item => {
            if (!this.breadcrumbShortened) {
                const currentItemWidth = item.nativeElement.offsetWidth;
                if (currentWidth + currentItemWidth > maxWidth) {
                    this.breadcrumbShortened = true;
                    // Test if the ellipses item still fits, if not remove last item as well.
                    // If the counter is still on 1, instead remove last (actually next in original order) from
                    // the previous reverse calculation
                    if (currentWidth + ellipsesWidth > maxWidth) {
                        if (counter > 1) {
                            counter--;
                        }
                        else {
                            reverseCounter++;
                        }
                    }
                }
                else {
                    currentWidth += currentItemWidth;
                    counter++;
                }
            }
        });
        // If breadcrumbShortened is true, move the items that do not fit to itemsHidden (breadcrumb dropdown items) and add ellipses item.
        if (this.breadcrumbShortened) {
            this.ellipsesLevel = counter;
            this.itemsHidden = this.itemsShown.slice(this.ellipsesLevel, reverseCounter);
            const ellipsesItem = { title: '...', level: this.ellipsesLevel, shortenedTitle: '' };
            this.itemsShown.splice(this.ellipsesLevel, reverseCounter - this.ellipsesLevel, ellipsesItem);
        }
        // Manually detect changes to prevent them from not being detected on language change
        this.changeDetector.detectChanges();
    }
}
SiBreadcrumbComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiBreadcrumbComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiBreadcrumbComponent, selector: "si-breadcrumb", inputs: { items: "items", showRootAsText: "showRootAsText", ariaLabel: "ariaLabel" }, host: { listeners: { "document:click": "documentClick($event.target)" } }, viewQueries: [{ propertyName: "breadcrumbElement", first: true, predicate: ["breadcrumb"], descendants: true }, { propertyName: "breadcrumbElements", predicate: ["breadcrumbItem"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: "<nav role=\"navigation\" [attr.aria-label]=\"ariaLabel | translate\">\n  <ol class=\"breadcrumb\" #breadcrumb (siResizeObserver)=\"resetBreadcrumb()\">\n    <ng-container *ngFor=\"let item of itemsShown; trackBy: trackByIndex; first as isFirst\">\n      <ng-container *ngIf=\"!breadcrumbShortened || item.level !== ellipsesLevel\">\n        <ng-container *ngIf=\"item.shortened\">\n          <li #breadcrumbItem class=\"item text-nowrap shortened\">\n            <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n            <div class=\"breadcrumb-dropdown-wrapper\">\n              <ng-container\n                *ngTemplateOutlet=\"toggleTemplate; context: { item: item, title: item.shortenedTitle }\"\n              ></ng-container>\n              <div\n                *ngIf=\"addExpandDropdown\"\n                class=\"dropdown-menu\"\n                [id]=\"controlId + item.level\"\n                [class.show]=\"breadcrumbDropdownOpen[item.level]\"\n                role=\"menu\"\n              >\n                <ng-container *ngTemplateOutlet=\"dropdownTemplate; context: { item: item }\"></ng-container>\n              </div>\n            </div>\n          </li>\n        </ng-container>\n        <li *ngIf=\"!item.shortened\" #breadcrumbItem class=\"item\">\n          <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { item: item }\"></ng-container>\n        </li>\n      </ng-container>\n      <li\n        *ngIf=\"breadcrumbShortened && item.level === ellipsesLevel\"\n        class=\"item breadcrumb-ellipses-item\"\n      >\n        <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n        <div class=\"breadcrumb-dropdown-wrapper\">\n          <ng-container *ngTemplateOutlet=\"toggleTemplate; context: { item: item, title: '' }\"></ng-container>\n          <div\n            class=\"dropdown-menu\"\n            [id]=\"controlId + item.level\"\n            [class.show]=\"breadcrumbDropdownOpen[item.level]\"\n            role=\"menu\"\n          >\n            <ng-container *ngFor=\"let item of itemsHidden; trackBy: trackByIndex\">\n              <ng-container *ngTemplateOutlet=\"dropdownTemplate; context: { item: item }\"></ng-container>\n            </ng-container>\n          </div>\n        </div>\n      </li>\n    </ng-container>\n  </ol>\n</nav>\n\n<ng-template #itemTemplate let-item=\"item\">\n  <a\n    class=\"breadcrumb-link text-nowrap\"\n    [class.disable-router-link]=\"!item.link && !item.action && !item.href\"\n    [class.text-secondary]=\"item.lastItem\"\n    [siLink]=\"item\"\n    activeClass=\"active\"\n    [exactMatch]=\"true\"\n    [attr.aria-label]=\"item.title\"\n    [attr.aria-disabled]=\"!item.link && !item.href && !item.action\"\n    ariaCurrent=\"page\"\n  >\n    <i *ngIf=\"item.level === 0\" class=\"icon element-breadcrumb-root flip-rtl\"></i>\n    <span *ngIf=\"item.level !== 0\">{{ item.title }}</span>\n  </a>\n</ng-template>\n\n<ng-template #toggleTemplate let-item=\"item\" let-title=\"title\">\n  <div\n    class=\"breadcrumb-dropdown-toggle link\"\n    role=\"button\"\n    aria-haspopup=\"true\"\n    [attr.aria-controls]=\"controlId + item.level\"\n    [attr.aria-expanded]=\"breadcrumbDropdownOpen[item.level]\"\n    (keydown.enter)=\"toggleBreadcrumbDropdown(item.level)\"\n    (click)=\"toggleBreadcrumbDropdown(item.level)\"\n    tabindex=\"0\"\n  >{{ title }}...</div>\n</ng-template>\n\n<ng-template #dropdownTemplate let-item=\"item\">\n  <a\n    class=\"dropdown-item breadcrumb-link focus-inside\"\n    [class.disable-router-link]=\"!item.link && !item.action && !item.href\"\n    [siLink]=\"item\"\n    activeClass=\"active\"\n    [exactMatch]=\"true\"\n    [attr.aria-disabled]=\"!item.link && !item.action && !item.href\"\n    ariaCurrent=\"page\"\n    role=\"menuitem\"\n  >{{ item.title }}</a>\n</ng-template>\n", styles: [".breadcrumb{flex-wrap:nowrap}.dropdown-menu.show>.dropdown-item{background-color:var(--element-base-1)}.item{display:flex;align-items:center}.separator{font-size:1.5rem;color:var(--element-ui-2);margin-block:-.25rem;margin-inline:0}.breadcrumb-dropdown-toggle{cursor:pointer}.breadcrumb-dropdown-toggle:focus{outline:none}.dropdown-item.active,.dropdown-item:active{background-color:var(--element-base-1)}.dropdown-item:hover,.dropdown-item:focus{background-color:var(--element-base-0)!important;text-decoration:underline}.disable-router-link,.disable-router-link:hover{pointer-events:none;color:var(--element-text-disabled)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "directive", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: ["resizeThrottle"], outputs: ["siResizeObserver"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiBreadcrumbComponent.prototype, "items", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-breadcrumb', template: "<nav role=\"navigation\" [attr.aria-label]=\"ariaLabel | translate\">\n  <ol class=\"breadcrumb\" #breadcrumb (siResizeObserver)=\"resetBreadcrumb()\">\n    <ng-container *ngFor=\"let item of itemsShown; trackBy: trackByIndex; first as isFirst\">\n      <ng-container *ngIf=\"!breadcrumbShortened || item.level !== ellipsesLevel\">\n        <ng-container *ngIf=\"item.shortened\">\n          <li #breadcrumbItem class=\"item text-nowrap shortened\">\n            <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n            <div class=\"breadcrumb-dropdown-wrapper\">\n              <ng-container\n                *ngTemplateOutlet=\"toggleTemplate; context: { item: item, title: item.shortenedTitle }\"\n              ></ng-container>\n              <div\n                *ngIf=\"addExpandDropdown\"\n                class=\"dropdown-menu\"\n                [id]=\"controlId + item.level\"\n                [class.show]=\"breadcrumbDropdownOpen[item.level]\"\n                role=\"menu\"\n              >\n                <ng-container *ngTemplateOutlet=\"dropdownTemplate; context: { item: item }\"></ng-container>\n              </div>\n            </div>\n          </li>\n        </ng-container>\n        <li *ngIf=\"!item.shortened\" #breadcrumbItem class=\"item\">\n          <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { item: item }\"></ng-container>\n        </li>\n      </ng-container>\n      <li\n        *ngIf=\"breadcrumbShortened && item.level === ellipsesLevel\"\n        class=\"item breadcrumb-ellipses-item\"\n      >\n        <i *ngIf=\"!isFirst\" role=\"separator\" class=\"separator element-right-2 flip-rtl\"></i>\n        <div class=\"breadcrumb-dropdown-wrapper\">\n          <ng-container *ngTemplateOutlet=\"toggleTemplate; context: { item: item, title: '' }\"></ng-container>\n          <div\n            class=\"dropdown-menu\"\n            [id]=\"controlId + item.level\"\n            [class.show]=\"breadcrumbDropdownOpen[item.level]\"\n            role=\"menu\"\n          >\n            <ng-container *ngFor=\"let item of itemsHidden; trackBy: trackByIndex\">\n              <ng-container *ngTemplateOutlet=\"dropdownTemplate; context: { item: item }\"></ng-container>\n            </ng-container>\n          </div>\n        </div>\n      </li>\n    </ng-container>\n  </ol>\n</nav>\n\n<ng-template #itemTemplate let-item=\"item\">\n  <a\n    class=\"breadcrumb-link text-nowrap\"\n    [class.disable-router-link]=\"!item.link && !item.action && !item.href\"\n    [class.text-secondary]=\"item.lastItem\"\n    [siLink]=\"item\"\n    activeClass=\"active\"\n    [exactMatch]=\"true\"\n    [attr.aria-label]=\"item.title\"\n    [attr.aria-disabled]=\"!item.link && !item.href && !item.action\"\n    ariaCurrent=\"page\"\n  >\n    <i *ngIf=\"item.level === 0\" class=\"icon element-breadcrumb-root flip-rtl\"></i>\n    <span *ngIf=\"item.level !== 0\">{{ item.title }}</span>\n  </a>\n</ng-template>\n\n<ng-template #toggleTemplate let-item=\"item\" let-title=\"title\">\n  <div\n    class=\"breadcrumb-dropdown-toggle link\"\n    role=\"button\"\n    aria-haspopup=\"true\"\n    [attr.aria-controls]=\"controlId + item.level\"\n    [attr.aria-expanded]=\"breadcrumbDropdownOpen[item.level]\"\n    (keydown.enter)=\"toggleBreadcrumbDropdown(item.level)\"\n    (click)=\"toggleBreadcrumbDropdown(item.level)\"\n    tabindex=\"0\"\n  >{{ title }}...</div>\n</ng-template>\n\n<ng-template #dropdownTemplate let-item=\"item\">\n  <a\n    class=\"dropdown-item breadcrumb-link focus-inside\"\n    [class.disable-router-link]=\"!item.link && !item.action && !item.href\"\n    [siLink]=\"item\"\n    activeClass=\"active\"\n    [exactMatch]=\"true\"\n    [attr.aria-disabled]=\"!item.link && !item.action && !item.href\"\n    ariaCurrent=\"page\"\n    role=\"menuitem\"\n  >{{ item.title }}</a>\n</ng-template>\n", styles: [".breadcrumb{flex-wrap:nowrap}.dropdown-menu.show>.dropdown-item{background-color:var(--element-base-1)}.item{display:flex;align-items:center}.separator{font-size:1.5rem;color:var(--element-ui-2);margin-block:-.25rem;margin-inline:0}.breadcrumb-dropdown-toggle{cursor:pointer}.breadcrumb-dropdown-toggle:focus{outline:none}.dropdown-item.active,.dropdown-item:active{background-color:var(--element-base-1)}.dropdown-item:hover,.dropdown-item:focus{background-color:var(--element-base-0)!important;text-decoration:underline}.disable-router-link,.disable-router-link:hover{pointer-events:none;color:var(--element-text-disabled)}\n"] }]
        }], propDecorators: { items: [{
                type: Input
            }], showRootAsText: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], breadcrumbElement: [{
                type: ViewChild,
                args: ['breadcrumb']
            }], breadcrumbElements: [{
                type: ViewChildren,
                args: ['breadcrumbItem', { read: ElementRef }]
            }], documentClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

// keep in sync with the Bootstrap variables
const BOOTSTRAP_BREAKPOINTS = {
    smMinimum: 576,
    mdMinimum: 768,
    lgMinimum: 992,
    xlMinimum: 1200,
    xxlMinimum: 1400
};
/**
 * Directive to automatically set `si-container-*` classes so Bootstrap column classes work
 * in the context of the container instead of viewport size.
 */
class SiResponsiveContainerDirective {
    constructor() {
        this.isXs = false;
        this.isSm = false;
        this.isMd = false;
        this.isLg = false;
        this.isXl = false;
        this.isXxl = false;
        this.resizeThrottle = 100;
        this.element = inject(ElementRef);
        this.service = inject(ResizeObserverService);
    }
    ngOnInit() {
        this.subs = this.service.observe(this.element.nativeElement, this.resizeThrottle, true)
            .subscribe(event => this.setResponsiveSize(event.width, event.height));
    }
    ngOnDestroy() {
        this.subs?.unsubscribe();
    }
    setResponsiveSize(width, height) {
        if (!width && !height) {
            // element is not visible, no point in changing anything
            return;
        }
        const breakpoints = this.breakpoints ?? BOOTSTRAP_BREAKPOINTS;
        this.isXs = width < breakpoints.smMinimum;
        this.isSm = width >= breakpoints.smMinimum && width < breakpoints.mdMinimum;
        this.isMd = width >= breakpoints.mdMinimum && width < breakpoints.lgMinimum;
        this.isLg = width >= breakpoints.lgMinimum && width < breakpoints.xlMinimum;
        this.isXl = width >= breakpoints.xlMinimum && width < breakpoints.xxlMinimum;
        this.isXxl = width >= breakpoints.xxlMinimum;
    }
}
SiResponsiveContainerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResponsiveContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiResponsiveContainerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiResponsiveContainerDirective, selector: "[siResponsiveContainer]", inputs: { resizeThrottle: "resizeThrottle", breakpoints: "breakpoints" }, host: { properties: { "class.si-container-xs": "this.isXs", "class.si-container-sm": "this.isSm", "class.si-container-md": "this.isMd", "class.si-container-lg": "this.isLg", "class.si-container-xl": "this.isXl", "class.si-container-xxl": "this.isXxl" } }, exportAs: ["siResponsiveContainer"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResponsiveContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siResponsiveContainer]',
                    exportAs: 'siResponsiveContainer'
                }]
        }], propDecorators: { isXs: [{
                type: HostBinding,
                args: ['class.si-container-xs']
            }], isSm: [{
                type: HostBinding,
                args: ['class.si-container-sm']
            }], isMd: [{
                type: HostBinding,
                args: ['class.si-container-md']
            }], isLg: [{
                type: HostBinding,
                args: ['class.si-container-lg']
            }], isXl: [{
                type: HostBinding,
                args: ['class.si-container-xl']
            }], isXxl: [{
                type: HostBinding,
                args: ['class.si-container-xxl']
            }], resizeThrottle: [{
                type: Input
            }], breakpoints: [{
                type: Input
            }] } });

class SiResizeObserverModule {
}
SiResizeObserverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiResizeObserverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverModule, declarations: [SiResizeObserverDirective, SiResponsiveContainerDirective], exports: [SiResizeObserverDirective, SiResponsiveContainerDirective] });
SiResizeObserverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResizeObserverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiResizeObserverDirective, SiResponsiveContainerDirective],
                    exports: [SiResizeObserverDirective, SiResponsiveContainerDirective]
                }]
        }] });

class SiBreadcrumbModule {
}
SiBreadcrumbModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiBreadcrumbModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, declarations: [SiBreadcrumbComponent], imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiResizeObserverModule,
        SiTranslateModule], exports: [SiBreadcrumbComponent] });
SiBreadcrumbModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiResizeObserverModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiLinkModule,
                        SiResizeObserverModule,
                        SiTranslateModule
                    ],
                    declarations: [SiBreadcrumbComponent],
                    exports: [SiBreadcrumbComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiComfortScoreStatusComponent {
    constructor() {
        this.size = 'medium';
    }
    get sizeClass() { return 'size-' + this.size; }
}
SiComfortScoreStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreStatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiComfortScoreStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiComfortScoreStatusComponent, selector: "si-comfort-score-status", inputs: { item: "item", size: "size" }, host: { properties: { "class": "this.sizeClass" } }, ngImport: i0, template: "<i\n  *ngIf=\"item.icon\"\n  class=\"me-4\"\n  [ngClass]=\"[item.icon, 'status-' + item.status, item.selected ? item.icon + '-filled' : '']\"\n  [class.item-disabled]=\"item.disabled\"\n  role=\"presentation\"\n></i>\n<div\n  class=\"flex-fill d-flex flex-column\"\n  [class.text-muted]=\"item.disabled\"\n  [attr.aria-disabled]=\"item.disabled\"\n>\n  <div class=\"score-title d-flex mb-3\">\n    {{ item.title | translate }}\n    <div\n      *ngIf=\"size !== 'large'\"\n      class=\"score si-caption mt-2\"\n      [class.text-secondary]=\"!item.disabled\"\n      [class.text-muted]=\"item.disabled\"\n    >\n      {{ item.value }}{{ item.unit ?? ' / 100' }}\n    </div>\n  </div>\n  <div\n    *ngIf=\"size !== 'small' && item.description\"\n    class=\"score-description\"\n  >\n    {{ item.description | translate }}\n  </div>\n</div>\n", styles: [":host{display:flex;max-inline-size:192px}:host.size-small{--title-weight: normal;--icon-size: 1.25rem;--score-dir: column;align-items:center}:host.size-medium{--title-weight: bold;--icon-size: 2rem;--title-order: 1;--score-dir: row}:host.size-large{--title-weight: bold;--title-size: 1.75rem;--icon-size: 2rem;--title-order: 0}i{font-size:var(--icon-size)}.score-title{flex-direction:var(--score-dir);align-items:baseline;justify-content:space-between;font-weight:var(--title-weight);font-size:var(--title-size);order:var(--title-order);line-height:1}.item-disabled{color:var(--element-ui-3)!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiComfortScoreStatusComponent.prototype, "item", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-comfort-score-status', template: "<i\n  *ngIf=\"item.icon\"\n  class=\"me-4\"\n  [ngClass]=\"[item.icon, 'status-' + item.status, item.selected ? item.icon + '-filled' : '']\"\n  [class.item-disabled]=\"item.disabled\"\n  role=\"presentation\"\n></i>\n<div\n  class=\"flex-fill d-flex flex-column\"\n  [class.text-muted]=\"item.disabled\"\n  [attr.aria-disabled]=\"item.disabled\"\n>\n  <div class=\"score-title d-flex mb-3\">\n    {{ item.title | translate }}\n    <div\n      *ngIf=\"size !== 'large'\"\n      class=\"score si-caption mt-2\"\n      [class.text-secondary]=\"!item.disabled\"\n      [class.text-muted]=\"item.disabled\"\n    >\n      {{ item.value }}{{ item.unit ?? ' / 100' }}\n    </div>\n  </div>\n  <div\n    *ngIf=\"size !== 'small' && item.description\"\n    class=\"score-description\"\n  >\n    {{ item.description | translate }}\n  </div>\n</div>\n", styles: [":host{display:flex;max-inline-size:192px}:host.size-small{--title-weight: normal;--icon-size: 1.25rem;--score-dir: column;align-items:center}:host.size-medium{--title-weight: bold;--icon-size: 2rem;--title-order: 1;--score-dir: row}:host.size-large{--title-weight: bold;--title-size: 1.75rem;--icon-size: 2rem;--title-order: 0}i{font-size:var(--icon-size)}.score-title{flex-direction:var(--score-dir);align-items:baseline;justify-content:space-between;font-weight:var(--title-weight);font-size:var(--title-size);order:var(--title-order);line-height:1}.item-disabled{color:var(--element-ui-3)!important}\n"] }]
        }], propDecorators: { item: [{
                type: Input
            }], size: [{
                type: Input
            }], sizeClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SiComfortScoreComponent {
    constructor() {
        this.items = [];
        this.showValues = true;
        this.labelOnly = false;
        this.gridParts = 2;
        this.selected = new EventEmitter();
        this.trackByItem = (_index, item) => item;
        this.trackByIndex = (index) => index;
        this.gridPartArray = new Array(this.gridParts);
    }
    ngOnChanges(changes) {
        if (changes.gridParts) {
            this.gridPartArray = new Array(this.gridParts);
        }
    }
}
SiComfortScoreComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiComfortScoreComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiComfortScoreComponent, selector: "si-comfort-score", inputs: { items: "items", showValues: "showValues", labelOnly: "labelOnly", gridParts: "gridParts" }, outputs: { selected: "selected" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"wrapper\" [class.labels]=\"showValues\">\n  <div\n    class=\"grid d-flex flex-column position-relative text-secondary small\"\n    [style.grid-column-end]=\"items.length + 1\"\n  >\n    <div *ngFor=\"let p of gridPartArray; trackBy: trackByIndex\" class=\"border-top flex-fill\"></div>\n    <ng-container *ngIf=\"showValues\">\n      <div class=\"axis-label position-absolute text-end label-100\">100</div>\n      <div class=\"axis-label position-absolute text-end label-0\">0</div>\n    </ng-container>\n  </div>\n  <ng-container *ngFor=\"let item of items; trackBy: trackByItem; index as i\">\n    <div\n      class=\"bar-outer position-relative\"\n      [style.grid-column]=\"i + 1\"\n    >\n      <div\n        class=\"bar position-absolute d-flex flex-column text-center\"\n        [class.detail]=\"!labelOnly\"\n        [style.height.%]=\"item.value\"\n        (click)=\"selected.emit(item)\"\n      >\n        <div\n          class=\"bar-inner flex-fill\"\n          [class.selected]=\"item.selected\"\n          [class.disabled]=\"item.disabled\"\n          [ngClass]=\"'status-' + item.status\"\n        >\n          <div *ngIf=\"showValues && !item.disabled\" class=\"value text-body small\">\n            {{ item.value }}{{ item.unit ?? '' }}\n          </div>\n        </div>\n      </div>\n    </div>\n    <div\n      class=\"status\"\n      tabindex=\"0\"\n      (click)=\"selected.emit(item)\"\n      (keydown.enter)=\"selected.emit(item)\"\n      [attr.aria-disabled]=\"item.disabled\"\n    >\n      <si-comfort-score-status\n        *ngIf=\"!labelOnly\"\n        [item]=\"item\"\n        size=\"large\"\n      ></si-comfort-score-status>\n      <div\n        *ngIf=\"labelOnly\"\n        class=\"small text-center\"\n        [class.text-muted]=\"item.disabled\"\n      >{{ item.title | translate }}</div>\n    </div>\n  </ng-container>\n</div>\n", styles: [":host{display:block}.wrapper{block-size:100%;display:grid;grid-row-gap:16px;grid-template-rows:1fr;margin-block:2rem 8px}.wrapper.labels{margin-inline-start:40px}.bar-outer{grid-row:1;min-block-size:100px}.bar,.status{cursor:pointer}.status{grid-row:2}.bar{inset-block-end:0;transition:block-size calc(.5s * var(--element-animations-enabled, 1))}.bar.detail{margin-inline-start:40px}.bar:not(.detail){inset-inline-start:calc(50% - 24px)}.bar-inner{inline-size:48px;background:var(--element-base-0);border-block-start:2px solid currentColor;margin-block-end:1px}.bar-inner.selected{background:currentColor}.bar-inner.disabled{border-block-start-color:var(--element-base-0);background:var(--element-base-0)}.value{margin-block-start:-1.25rem;line-height:1}.grid{grid-row:1;grid-column-start:1;border-block-end:1px solid var(--element-ui-1)}.axis-label{inset-inline-start:-40px;inline-size:40px;line-height:1;padding-inline-end:12px}.label-0{inset-block-end:-.5em}.label-100{inset-block-start:-.5em}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiComfortScoreStatusComponent, selector: "si-comfort-score-status", inputs: ["item", "size"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-comfort-score', template: "<div class=\"wrapper\" [class.labels]=\"showValues\">\n  <div\n    class=\"grid d-flex flex-column position-relative text-secondary small\"\n    [style.grid-column-end]=\"items.length + 1\"\n  >\n    <div *ngFor=\"let p of gridPartArray; trackBy: trackByIndex\" class=\"border-top flex-fill\"></div>\n    <ng-container *ngIf=\"showValues\">\n      <div class=\"axis-label position-absolute text-end label-100\">100</div>\n      <div class=\"axis-label position-absolute text-end label-0\">0</div>\n    </ng-container>\n  </div>\n  <ng-container *ngFor=\"let item of items; trackBy: trackByItem; index as i\">\n    <div\n      class=\"bar-outer position-relative\"\n      [style.grid-column]=\"i + 1\"\n    >\n      <div\n        class=\"bar position-absolute d-flex flex-column text-center\"\n        [class.detail]=\"!labelOnly\"\n        [style.height.%]=\"item.value\"\n        (click)=\"selected.emit(item)\"\n      >\n        <div\n          class=\"bar-inner flex-fill\"\n          [class.selected]=\"item.selected\"\n          [class.disabled]=\"item.disabled\"\n          [ngClass]=\"'status-' + item.status\"\n        >\n          <div *ngIf=\"showValues && !item.disabled\" class=\"value text-body small\">\n            {{ item.value }}{{ item.unit ?? '' }}\n          </div>\n        </div>\n      </div>\n    </div>\n    <div\n      class=\"status\"\n      tabindex=\"0\"\n      (click)=\"selected.emit(item)\"\n      (keydown.enter)=\"selected.emit(item)\"\n      [attr.aria-disabled]=\"item.disabled\"\n    >\n      <si-comfort-score-status\n        *ngIf=\"!labelOnly\"\n        [item]=\"item\"\n        size=\"large\"\n      ></si-comfort-score-status>\n      <div\n        *ngIf=\"labelOnly\"\n        class=\"small text-center\"\n        [class.text-muted]=\"item.disabled\"\n      >{{ item.title | translate }}</div>\n    </div>\n  </ng-container>\n</div>\n", styles: [":host{display:block}.wrapper{block-size:100%;display:grid;grid-row-gap:16px;grid-template-rows:1fr;margin-block:2rem 8px}.wrapper.labels{margin-inline-start:40px}.bar-outer{grid-row:1;min-block-size:100px}.bar,.status{cursor:pointer}.status{grid-row:2}.bar{inset-block-end:0;transition:block-size calc(.5s * var(--element-animations-enabled, 1))}.bar.detail{margin-inline-start:40px}.bar:not(.detail){inset-inline-start:calc(50% - 24px)}.bar-inner{inline-size:48px;background:var(--element-base-0);border-block-start:2px solid currentColor;margin-block-end:1px}.bar-inner.selected{background:currentColor}.bar-inner.disabled{border-block-start-color:var(--element-base-0);background:var(--element-base-0)}.value{margin-block-start:-1.25rem;line-height:1}.grid{grid-row:1;grid-column-start:1;border-block-end:1px solid var(--element-ui-1)}.axis-label{inset-inline-start:-40px;inline-size:40px;line-height:1;padding-inline-end:12px}.label-0{inset-block-end:-.5em}.label-100{inset-block-start:-.5em}\n"] }]
        }], propDecorators: { items: [{
                type: Input
            }], showValues: [{
                type: Input
            }], labelOnly: [{
                type: Input
            }], gridParts: [{
                type: Input
            }], selected: [{
                type: Output
            }] } });

class SiComfortScoreModule {
}
SiComfortScoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiComfortScoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreModule, declarations: [SiComfortScoreComponent, SiComfortScoreStatusComponent], imports: [CommonModule, SiTranslateModule], exports: [SiComfortScoreComponent, SiComfortScoreStatusComponent] });
SiComfortScoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiComfortScoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiComfortScoreComponent, SiComfortScoreStatusComponent],
                    exports: [SiComfortScoreComponent, SiComfortScoreStatusComponent],
                    imports: [CommonModule, SiTranslateModule]
                }]
        }] });

const CONNECTION_STRENGTH_MAP = { 'none': 0, 'low': 1, 'medium': 2, 'strong': 3 };
class SiConnectionStrengthComponent {
    constructor() {
        /**
         * Shows the signal representation in an alternative *WLAN* format
         */
        this.wlan = false;
        /**
         * Main value representing the current connection strength.
         */
        this.value = 'none';
    }
    get numberValue() {
        return CONNECTION_STRENGTH_MAP[this.value] ?? 0;
    }
}
SiConnectionStrengthComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiConnectionStrengthComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiConnectionStrengthComponent, selector: "si-connection-strength", inputs: { wlan: "wlan", value: "value" }, ngImport: i0, template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\" *ngIf=\"wlan\" [class.none]=\"numberValue < 1\">\n  <path d=\"M318.63,304a108.1,108.1,0,0,0-125.1,0,12,12,0,0,0,13.9,19.56,84.09,84.09,0,0,1,97.3,0A12,12,0,1,0,318.63,304Z\" />\n  <path [class.inactive]=\"numberValue < 2\" d=\"M377,247.56a188,188,0,0,0-240.64,0A12,12,0,1,0,151.68,266a164,164,0,0,1,209.92,0A12,12,0,0,0,377,247.56Z\" />\n  <path [class.inactive]=\"numberValue < 3\" d=\"M433.21,191a268,268,0,0,0-354.42,0,12,12,0,1,0,15.86,18,244,244,0,0,1,322.7,0,12,12,0,0,0,15.86-18Z\" />\n  <circle cx=\"256\" cy=\"372.22\" r=\"16\" />\n  <g *ngIf=\"numberValue < 1\">\n    <path d=\"M412,352a12,12,0,0,0,12-12V263.78a12,12,0,0,0-24,0V340A12,12,0,0,0,412,352Z\" />\n    <path d=\"M412.18,400H412a12,12,0,0,0,0,24h.18a12,12,0,1,0,0-24Z\" />\n  </g>\n</svg>\n\n<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\" *ngIf=\"!wlan\" [class.none]=\"numberValue < 1\">\n  <path [class.inactive]=\"numberValue < 2\" d=\"M128,228a12,12,0,0,0,0,24A132.14,132.14,0,0,1,260,384a12,12,0,0,0,24,0C284,298,214,228,128,228Z\" />\n  <path [class.inactive]=\"numberValue < 3\" d=\"M128,116a12,12,0,0,0,0,24A244,244,0,0,1,372,384a12,12,0,0,0,24,0A268,268,0,0,0,128,116Z\" />\n  <circle cx=\"144\" cy=\"368\" r=\"28\" />\n  <g *ngIf=\"numberValue < 1\">\n    <path d=\"M412,188.11a12,12,0,0,0,12-12V99.89a12,12,0,1,0-24,0v76.22A12,12,0,0,0,412,188.11Z\" />\n    <path d=\"M412.18,236.11H412a12,12,0,0,0,0,24h.18a12,12,0,0,0,0-24Z\" />\n  </g>\n</svg>\n", styles: ["svg{inline-size:1em;block-size:1em;fill:var(--element-ui-1)}svg.none,svg.none .inactive{fill:var(--element-status-danger)}svg .inactive{fill:var(--element-ui-3)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-connection-strength', changeDetection: ChangeDetectionStrategy.OnPush, template: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\" *ngIf=\"wlan\" [class.none]=\"numberValue < 1\">\n  <path d=\"M318.63,304a108.1,108.1,0,0,0-125.1,0,12,12,0,0,0,13.9,19.56,84.09,84.09,0,0,1,97.3,0A12,12,0,1,0,318.63,304Z\" />\n  <path [class.inactive]=\"numberValue < 2\" d=\"M377,247.56a188,188,0,0,0-240.64,0A12,12,0,1,0,151.68,266a164,164,0,0,1,209.92,0A12,12,0,0,0,377,247.56Z\" />\n  <path [class.inactive]=\"numberValue < 3\" d=\"M433.21,191a268,268,0,0,0-354.42,0,12,12,0,1,0,15.86,18,244,244,0,0,1,322.7,0,12,12,0,0,0,15.86-18Z\" />\n  <circle cx=\"256\" cy=\"372.22\" r=\"16\" />\n  <g *ngIf=\"numberValue < 1\">\n    <path d=\"M412,352a12,12,0,0,0,12-12V263.78a12,12,0,0,0-24,0V340A12,12,0,0,0,412,352Z\" />\n    <path d=\"M412.18,400H412a12,12,0,0,0,0,24h.18a12,12,0,1,0,0-24Z\" />\n  </g>\n</svg>\n\n<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\" *ngIf=\"!wlan\" [class.none]=\"numberValue < 1\">\n  <path [class.inactive]=\"numberValue < 2\" d=\"M128,228a12,12,0,0,0,0,24A132.14,132.14,0,0,1,260,384a12,12,0,0,0,24,0C284,298,214,228,128,228Z\" />\n  <path [class.inactive]=\"numberValue < 3\" d=\"M128,116a12,12,0,0,0,0,24A244,244,0,0,1,372,384a12,12,0,0,0,24,0A268,268,0,0,0,128,116Z\" />\n  <circle cx=\"144\" cy=\"368\" r=\"28\" />\n  <g *ngIf=\"numberValue < 1\">\n    <path d=\"M412,188.11a12,12,0,0,0,12-12V99.89a12,12,0,1,0-24,0v76.22A12,12,0,0,0,412,188.11Z\" />\n    <path d=\"M412.18,236.11H412a12,12,0,0,0,0,24h.18a12,12,0,0,0,0-24Z\" />\n  </g>\n</svg>\n", styles: ["svg{inline-size:1em;block-size:1em;fill:var(--element-ui-1)}svg.none,svg.none .inactive{fill:var(--element-status-danger)}svg .inactive{fill:var(--element-ui-3)}\n"] }]
        }], propDecorators: { wlan: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

class SiConnectionStrengthModule {
}
SiConnectionStrengthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiConnectionStrengthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthModule, declarations: [SiConnectionStrengthComponent], imports: [CommonModule], exports: [SiConnectionStrengthComponent] });
SiConnectionStrengthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConnectionStrengthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SiConnectionStrengthComponent],
                    exports: [SiConnectionStrengthComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class TextMeasureService {
    measureText(text, fontRef) {
        this.ensureCanvas();
        this.setFontStyle(fontRef);
        return Math.ceil(this.measureCanvas.measureText(text).width);
    }
    getFontStyle(element) {
        const style = getComputedStyle(element);
        // this is necessary since Firefox returns style.font as ""
        return `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
            .replace(/ +/g, ' ').trim();
    }
    ensureCanvas() {
        if (this.measureCanvas) {
            return;
        }
        const canvas = document.createElement('canvas');
        this.measureCanvas = canvas.getContext('2d');
    }
    setFontStyle(fontRef) {
        if (typeof fontRef === 'string') {
            this.measureCanvas.font = fontRef;
            return;
        }
        if (fontRef) {
            this.measureCanvas.font = this.getFontStyle(fontRef);
            return;
        }
        if (!this.defaultFont) {
            this.defaultFont = this.getFontStyle(document.body);
        }
        this.measureCanvas.font = this.defaultFont;
    }
}
TextMeasureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: TextMeasureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TextMeasureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: TextMeasureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: TextMeasureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SiDropdownContainerComponent {
    constructor() {
        this.elementTransform = '';
        this.hide = false;
        this.content = inject(ElementRef).nativeElement;
    }
    get isDropup() {
        return this.parent?.isDropup ?? false;
    }
    get isDropstart() {
        return this.parent?.isDropstart ?? false;
    }
    get isDropend() {
        return this.parent?.isDropend ?? false;
    }
    ngOnInit() {
        // Set a property of the HTML element for others to follow the chain without having to use the service.
        this.content.dropdownParentElement = this.parent.hostElement;
        this.addMenus();
    }
    ngOnDestroy() {
        this.removeMenus();
    }
    onKeyboardEvent(event) {
        if (this.parent) {
            const newEvent = new KeyboardEvent(event.type, event);
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
        }
    }
    onFocusEvent(event) {
        if (this.parent) {
            const newEvent = new FocusEvent(event.type, event);
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
        }
    }
    onMouseEvent(event) {
        if (this.parent) {
            const newEvent = new MouseEvent(event.type, event);
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
        }
    }
    removeMenus() {
        if (this.parent) {
            this.parent.menus.forEach(menuContent => menuContent.parentElement?.appendChild(menuContent.content));
        }
    }
    addMenus() {
        if (this.parent) {
            this.parent.menus.forEach(menuContent => this.menu.nativeElement.appendChild(menuContent.content));
        }
    }
    setParent(parent) {
        this.parent = parent;
    }
}
SiDropdownContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiDropdownContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownContainerComponent, selector: "si-dropdown-container", host: { listeners: { "keydown": "onKeyboardEvent($event)", "keyup": "onKeyboardEvent($event)", "keypress": "onKeyboardEvent($event)", "focus": "onFocusEvent($event)", "blur": "onFocusEvent($event)", "focusin": "onFocusEvent($event)", "focusout": "onFocusEvent($event)", "mousedown": "onMouseEvent($event)", "mouseup": "onMouseEvent($event)", "click": "onMouseEvent($event)" }, properties: { "class.hidden": "this.hide" } }, viewQueries: [{ propertyName: "menu", first: true, predicate: ["menu"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div\n  #menu\n  class=\"dropdown\"\n  [style.transform]=\"elementTransform\"\n  [class.dropup]=\"isDropup\"\n  [class.dropstart]=\"isDropstart\"\n  [class.dropend]=\"isDropend\"\n></div>\n", styles: [":host.hidden{visibility:hidden}.dropdown{position:absolute;top:0;left:0;block-size:auto;z-index:1065}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-dropdown-container', template: "<div\n  #menu\n  class=\"dropdown\"\n  [style.transform]=\"elementTransform\"\n  [class.dropup]=\"isDropup\"\n  [class.dropstart]=\"isDropstart\"\n  [class.dropend]=\"isDropend\"\n></div>\n", styles: [":host.hidden{visibility:hidden}.dropdown{position:absolute;top:0;left:0;block-size:auto;z-index:1065}\n"] }]
        }], propDecorators: { hide: [{
                type: HostBinding,
                args: ['class.hidden']
            }], menu: [{
                type: ViewChild,
                args: ['menu', { read: ElementRef, static: true }]
            }], onKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup', ['$event']]
            }, {
                type: HostListener,
                args: ['keypress', ['$event']]
            }], onFocusEvent: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }, {
                type: HostListener,
                args: ['blur', ['$event']]
            }, {
                type: HostListener,
                args: ['focusin', ['$event']]
            }, {
                type: HostListener,
                args: ['focusout', ['$event']]
            }], onMouseEvent: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['mouseup', ['$event']]
            }, {
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const RESIZE_THROTTLE_TIMEOUT$1 = 100;
const DEPRECATED_WARNING_PREFIX = '@simpl/element-ng - SiDropdownDirective: ';
class SiDropdownDirective {
    constructor() {
        /**
         * Disables the dropdown so it cannot be opened.
         */
        this.dropdownIsDisabled = false;
        /**
         * Opens the dropdown on changes, can be changed at any time.
         * To open it only on first load use {@link dropdownOpenOnLoad}
         */
        this.dropdownIsOpen = false;
        /**
         * Opens the dropdown when it first loads, to open it later use {@link dropdownIsOpen}.
         */
        this.dropdownOpenOnLoad = false;
        /**
         * Specifies whether the dropdown should be closed on selection of a dropdown item.
         */
        this.dropdownCloseOnSelect = true;
        /**
         * Specifies whether the dropdown should be closed on click outside.
         */
        this.dropdownCloseOnClickOut = true;
        /**
         * Specifies whether the dropdown should be closed on escape.
         */
        this.dropdownCloseOnEsc = true;
        this._dropdownDirection = 'down';
        this._dropdownAlign = 'start';
        /**
         * Specifies whether the dropdown menu(s) should be appended to another container.
         * It will still be positioned the same.
         */
        this.dropdownContainer = '';
        /**
         * Specifies whether the direction should automatically be flipped if there is not enough space in the container or its parents.
         */
        this.dropdownResponsiveDirection = false;
        /**
         * Specifies whether the direction should automatically be flipped also according to the placement reference and its parents.
         * Only relevant when `dropdownResponsiveDirection` is set to `true`.
         */
        this.dropdownResponsiveDirectionToPlacement = true;
        this._dropdownPlacement = '';
        /**
         * Specifies what the placement of the dropdown menu(s) should be relative to.
         */
        this.dropdownPlacementReference = '';
        /**
         * Specifies the selector to determine which elements inside the dropdown menu(s) are dropdown items.
         * The default selector targets the bootstrap `dropdown-item` class.
         */
        this.dropdownItemSelector = '.dropdown-item';
        /**
         * Specifies the selector to determine which elements inside the dropdown menu(s) are not dropdown items.
         * The default selector excepts the `dropdown-item-no-link` class.
         */
        this.dropdownItemSelectorException = '.dropdown-item-no-link';
        /**
         * Focuses the first menu on open of the dropdown.
         */
        this.dropdownFocusOnOpen = true;
        /**
         * Focuses the first toggle on close of the dropdown.
         */
        this.dropdownFocusOnClose = true;
        /**
         * Closes the dropdown when the menu(s) are not visible anymore.
         */
        this.closeOnMenuScrollOut = true;
        /**
         * Closes the dropdown when the placement reference is not visible anymore.
         * Only works when either `placementReference` or `dropdownContainer` is set.
         */
        this.closeOnPlacementReferenceScrollOut = true;
        /**
         * Emits the current dropdown state whenever it is opened or closed.
         */
        this.dropdownOnToggle = new EventEmitter();
        /**
         * Emits when any dropdown item inside the dropdown menu(s) is selected.
         * The event is the element of the dropdown item.
         */
        this.dropdownOnSelect = new EventEmitter();
        /**
         * Emits when any the direction of the dropdown is changed, automatically or manually.
         * The event contains the new direction.
         */
        this.dropdownResponsiveDirectionChange = new EventEmitter();
        /**
         * Whether the dropdown has previously been opened.
         */
        this.dropdownOpened = false;
        // the consumer facing state
        this.isOpenForConsumer = false;
        this.dropdownOpen = false;
        this.loaded = false;
        this.containerHostElement = null;
        this.stateChangeTimeout = 0;
        this.displayContainerComponent = false;
        this.containerComponentDisplayed = false;
        this.reopenAfterContainerTimeout = 0;
        this.changeDirectionTimeout = 0;
        this.closeDropdownTimeout = 0;
        this._responsiveDirection = 'down';
        this.suppressOutsideClickInCurrentTick = false;
        this.viewContainer = inject(ViewContainerRef);
        this.renderer = inject(Renderer2);
        this.resizeObserver = inject(ResizeObserverService);
        this.zone = inject(NgZone);
        this.cdRef = inject(ChangeDetectorRef);
        this.parent = inject(SiDropdownDirective, { optional: true, skipSelf: true });
        this.focusOnOpenTimeout = 0;
        this.hostElement = inject(ElementRef).nativeElement;
        this._menus = [];
        this._toggles = [];
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.children = [];
        this.dropdownClass = true;
        this.onPlacementReferenceScroll = () => this.placementReferenceScrollHandler();
        this.onClickOut = (event) => this.clickOutHandler(event);
    }
    /**
     * Specifies the direction of the dropdown, can be `down`, `up`, `start`, `end`.
     * Per default it opens downwards.
     * @defaultref {@link _dropdownDirection}
     */
    set dropdownDirection(direction) {
        const deprecatedWarning = DEPRECATED_WARNING_PREFIX
            + 'Setting dropdownDirection to "left" or "right" is deprecated, use "start" or "end" instead';
        if (direction === 'left') {
            console.warn(deprecatedWarning);
            this._dropdownDirection = 'start';
        }
        else if (direction === 'right') {
            console.warn(deprecatedWarning);
            this._dropdownDirection = 'end';
        }
        else {
            this._dropdownDirection = direction;
        }
    }
    get dropdownDirection() {
        return this._dropdownDirection;
    }
    /**
     * Aligns the dropdown to a side, only works if the direction is not `start` or `end`.
     * Can be `start`, `center` and `end`, per default it aligns to the start.
     * @defaultref {@link _dropdownAlign}
     */
    set dropdownAlign(align) {
        const deprecatedWarning = DEPRECATED_WARNING_PREFIX
            + 'Setting dropdownAlign to "left" or "right" is deprecated, use "start" or "end" instead';
        if (align === 'left') {
            console.warn(deprecatedWarning);
            this._dropdownAlign = 'start';
        }
        else if (align === 'right') {
            console.warn(deprecatedWarning);
            this._dropdownAlign = 'end';
        }
        else {
            this._dropdownAlign = align;
        }
    }
    get dropdownAlign() {
        return this._dropdownAlign;
    }
    /**
     * Specifies custom placement of the dropdown menu(s). This overrides the placement
     * specified by the direction. Can include `start`, `end`, `top` and `bottom`.
     * @defaultref {@link _dropdownPlacement}
     */
    set dropdownPlacement(placement) {
        if (placement.includes('left') || placement.includes('right')) {
            console.warn(DEPRECATED_WARNING_PREFIX
                + 'Setting dropdownPlacement to values containing "left" or "right" is deprecated, use "start" or "end" instead');
            this._dropdownPlacement = placement.replace(/left/g, 'start').replace(/right/g, 'end');
        }
        else {
            this._dropdownPlacement = placement;
        }
    }
    get dropdownPlacement() {
        return this._dropdownPlacement;
    }
    /** Whether the dropdown is actually open. */
    get isOpen() {
        return this.isOpenForConsumer;
    }
    /** Whether the dropdown appears open. */
    get appearsOpen() {
        return this._menus.every(menu => menu.content.classList.contains('show'));
    }
    /**
     * @defaultref {@link _responsiveDirection}
     */
    set responsiveDirection(direction) {
        this._responsiveDirection = direction || 'down';
        this.cdRef.markForCheck();
        this.dropdownResponsiveDirectionChange.emit(this._responsiveDirection);
    }
    get responsiveDirection() {
        return this._responsiveDirection;
    }
    get menus() {
        return this._menus;
    }
    get toggles() {
        return this._toggles;
    }
    get isDropup() {
        return this.responsiveDirection === 'up';
    }
    get isDropstart() {
        return this.responsiveDirection === 'start';
    }
    get isDropend() {
        return this.responsiveDirection === 'end';
    }
    get dropdownShowClass() {
        return this.dropdownOpen;
    }
    ngOnInit() {
        this.parent?.addChild(this);
        this.updateInputs();
    }
    ngOnChanges() {
        this.updateInputs();
    }
    ngOnDestroy() {
        clearTimeout(this.changeDirectionTimeout);
        clearTimeout(this.stateChangeTimeout);
        clearTimeout(this.reopenAfterContainerTimeout);
        clearTimeout(this.focusOnOpenTimeout);
        clearTimeout(this.closeDropdownTimeout);
        this.parent?.removeChild(this);
        this.removeContainer();
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
            this.zone.runOutsideAngular(() => {
                removeEventListener('resize', this.onPlacementReferenceScroll);
                if (this.containerComponentDisplayed) {
                    removeEventListener('scroll', this.onPlacementReferenceScroll, true);
                }
            });
        }
        this.resizeSubs?.unsubscribe();
    }
    updateInputs() {
        let close = false;
        if (!this.loaded) {
            this.loaded = true;
            if ((this.dropdownOpenOnLoad || this.dropdownIsOpen) && !this.dropdownIsDisabled) {
                this.open();
            }
        }
        else {
            if (this.dropdownOpen && this.dropdownIsDisabled) {
                this.close();
                close = true;
            }
            else if (!this.dropdownOpen && this.dropdownIsOpen) {
                this.open();
            }
        }
        this.responsiveDirection = this.dropdownDirection;
        this.resetContainerAndPlacement(close);
    }
    getPlacementReferenceElement() {
        return resolveReference(this.hostElement, this.dropdownPlacementReference) ?? this.hostElement;
    }
    resetContainerAndPlacement(close = false) {
        const wasOpen = this.dropdownOpen;
        if (wasOpen) {
            this.closeDropdown();
        }
        this.displayContainerComponent = false;
        const containerHost = resolveReference(this.hostElement, this.dropdownContainer);
        if (containerHost) {
            this.containerHostElement = containerHost;
            this.displayContainerComponent = true;
        }
        else if (this.dropdownResponsiveDirection || this.dropdownPlacement || this.dropdownPlacementReference ||
            this.dropdownAlign === 'end' || this.dropdownAlign === 'center') {
            this.containerHostElement = this.hostElement;
            this.displayContainerComponent = true;
        }
        else {
            this.containerHostElement = null;
        }
        if (wasOpen && !close) {
            this.reopenAfterContainerTimeout = setTimeout(() => this.attemptDropdownOpen());
        }
    }
    responsivelyChangeDirection(placementReferenceElement, isScrolling = false) {
        if (this.dropdownResponsiveDirection && this.containerComponentDisplayed && this.dropdownOpen) {
            const { responsiveDirection, close } = responsivelyCheckDirection({
                isScrolling,
                currentDirection: this.dropdownDirection,
                contentElements: this._menus.map(menu => menu.content),
                hostElement: this.containerHostElement,
                placement: this.dropdownPlacement,
                placementReferenceElement,
                align: this.dropdownAlign,
                responsiveDirectionToPlacement: this.dropdownResponsiveDirectionToPlacement,
                closeOnPlacementReferenceScrollOut: this.closeOnPlacementReferenceScrollOut,
                closeOnContentScrollOut: this.closeOnMenuScrollOut
            });
            if (responsiveDirection && responsiveDirection !== this.responsiveDirection) {
                // wont emit but value is there for position update; emit in zone
                this._responsiveDirection = responsiveDirection;
                this.zone.run(() => {
                    if (close) {
                        this.closeDropdown();
                    }
                    this.responsiveDirection = responsiveDirection;
                });
            }
            else if (close) {
                this.zone.run(() => this.closeDropdown());
            }
        }
    }
    addMenu(menu) {
        this._menus.push(menu);
        if (this.containerComponentDisplayed) {
            this.containerComponent.removeMenus();
            this.containerComponent.addMenus();
        }
        menu.updateState(this.dropdownOpen, this.getActualAlign());
    }
    removeMenu(menu) {
        const menuIndex = this._menus.indexOf(menu);
        if (menuIndex >= 0) {
            this._menus.splice(menuIndex, 1);
            if (this.containerComponentDisplayed) {
                this.containerComponent.removeMenus();
                this.containerComponent.addMenus();
            }
        }
    }
    placementReferenceScrollHandler() {
        if (this.dropdownOpen && this.containerComponentDisplayed) {
            const placementReferenceElement = this.getPlacementReferenceElement();
            this.responsivelyChangeDirection(placementReferenceElement, true);
            const position = getContentPositionString({
                contentElement: this.containerComponent.content,
                direction: this.responsiveDirection,
                placement: this.dropdownPlacement,
                placementReferenceElement,
                align: this.dropdownAlign
            });
            if (position !== this.containerComponent.elementTransform) {
                this.zone.run(() => {
                    this.containerComponent.elementTransform = position;
                    this.cdRef.markForCheck();
                });
            }
        }
    }
    addContainer() {
        if (!this.containerComponentDisplayed) {
            this.containerComponentRef = this.viewContainer.createComponent(SiDropdownContainerComponent);
            this.containerComponent = this.containerComponentRef.instance;
            this.containerComponent.setParent(this);
            this.renderer.appendChild(this.containerHostElement, this.containerComponent.content);
            this.containerComponentDisplayed = true;
            // Set a property of the HTML element for others to follow the chain without having to use the service.
            this.hostElement.dropdownChildren = this.containerComponent.content.children;
        }
    }
    removeContainer() {
        if (this.containerComponentDisplayed) {
            if (this.containerHostElement.contains(this.containerComponent.content)) {
                this.renderer.removeChild(this.containerHostElement, this.containerComponent.content);
            }
            this.containerComponentRef.destroy();
            this.containerComponentDisplayed = false;
            delete this.hostElement.dropdownChildren;
        }
    }
    clickOutHandler(event) {
        const target = event.target;
        if (!this.suppressOutsideClickInCurrentTick
            && this.dropdownOpen
            && this.dropdownCloseOnClickOut
            && !this.isElementOfAnyDropdownInHierarchy(target)) {
            this.closeDropdown();
        }
    }
    onKeydown(event) {
        const target = event.target;
        if (this.dropdownCloseOnEsc && !this.isElementOfAnyChildDropdownInHierarchy(target)) {
            this.closeDropdown();
        }
    }
    /** Toggles the dropdown. */
    toggle() {
        if (!this.dropdownIsDisabled) {
            if (this.dropdownOpen) {
                this.closeDropdown();
            }
            else {
                this.attemptDropdownOpen();
            }
        }
    }
    /** Opens the dropdown. */
    open() {
        if (!this.dropdownIsDisabled) {
            this.attemptDropdownOpen();
        }
    }
    /** Closes the dropdown. */
    close() {
        this.closeDropdown();
    }
    addToggle(toggle) {
        this._toggles.push(toggle);
    }
    removeToggle(toggle) {
        this._toggles.splice(this._toggles.indexOf(toggle));
    }
    addChild(child) {
        this.children.push(child);
    }
    removeChild(child) {
        this.children.splice(this.children.indexOf(child));
    }
    waitForVisibilityChange() {
        if (this.containerHostElement && (this.containerHostElement.offsetParent
            || (window.getComputedStyle(this.containerHostElement).position === 'fixed'
                && window.getComputedStyle(this.containerHostElement).display !== 'none')
            || this.containerHostElement === document.body || this.containerHostElement === document.documentElement)) {
            this.resizeSubs?.unsubscribe();
            this.openDropdown();
        }
    }
    attemptDropdownOpen() {
        if (this.containerHostElement
            && this.containerHostElement !== document.body && this.containerHostElement !== document.documentElement
            && !(this.containerHostElement.offsetParent || ((window.getComputedStyle(this.containerHostElement).position === 'fixed'
                || window.getComputedStyle(this.containerHostElement).display !== 'none')))) {
            this.resizeSubs?.unsubscribe();
            this.resizeSubs = this.resizeObserver
                .observe(this.containerHostElement, RESIZE_THROTTLE_TIMEOUT$1, true, true)
                .subscribe(() => this.waitForVisibilityChange());
        }
        else {
            this.openDropdown();
        }
    }
    openDropdown() {
        this.suppressOutsideClickInCurrentTick = true;
        setTimeout(() => this.suppressOutsideClickInCurrentTick = false);
        if (!this.dropdownOpen) {
            if (this.displayContainerComponent) {
                this.addContainer();
                if (this.dropdownResponsiveDirection) {
                    this.containerComponent.hide = true;
                }
            }
            this.responsiveDirection = this.dropdownDirection;
            clearTimeout(this.closeDropdownTimeout);
            this.dropdownOpen = true;
            this.dropdownOpened = true;
            if (this.displayContainerComponent) {
                setTimeout(() => {
                    this.isOpenForConsumer = true;
                    this.dropdownOnToggle.emit(this.isOpenForConsumer);
                });
            }
            else {
                this.isOpenForConsumer = true;
                this.dropdownOnToggle.emit(this.isOpenForConsumer);
            }
            setTimeout(() => addEventListener('click', this.onClickOut));
            this._toggles.forEach(dropdownToggle => dropdownToggle.updateState(true));
            const align = this.getActualAlign();
            this._menus.forEach(dropdownMenu => dropdownMenu.updateState(true, align));
            if (this.containerComponentDisplayed) {
                this.zone.runOutsideAngular(() => {
                    // Add event listener to window as capturing to handle tested element scrolling
                    addEventListener('scroll', this.onPlacementReferenceScroll, true);
                    // here we _actually_ want window.resize, nothing else since this is about a fixed position
                    addEventListener('resize', this.onPlacementReferenceScroll);
                });
                this.containerComponent.elementTransform = getContentPositionString({
                    contentElement: this.containerComponent.content,
                    direction: this.responsiveDirection,
                    placement: this.dropdownPlacement,
                    placementReferenceElement: this.getPlacementReferenceElement(),
                    align: this.dropdownAlign
                });
            }
            this.changeDirectionTimeout = setTimeout(() => {
                this.changeDirectionTimeout = 0;
                const placementReferenceElement = this.getPlacementReferenceElement();
                this.responsivelyChangeDirection(placementReferenceElement);
                if (this.containerComponentDisplayed) {
                    this.containerComponent.elementTransform = getContentPositionString({
                        contentElement: this.containerComponent.content,
                        direction: this.responsiveDirection,
                        placement: this.dropdownPlacement,
                        placementReferenceElement,
                        align: this.dropdownAlign
                    });
                }
                if (this.containerComponent) {
                    this.containerComponent.hide = false;
                    this.cdRef.detectChanges();
                }
            });
            if (this.dropdownFocusOnOpen) {
                this.focusOnOpenTimeout = window.setTimeout(() => {
                    this.focusOnOpenTimeout = 0;
                    if (this._menus.length) {
                        this._menus[0].content.focus();
                    }
                });
            }
        }
    }
    closeDropdown() {
        if (this.dropdownOpen) {
            this.responsiveDirection = this.dropdownDirection;
            clearTimeout(this.closeDropdownTimeout);
            this.dropdownOpen = false;
            this.closeDropdownTimeout = setTimeout(() => {
                this.isOpenForConsumer = false;
                this.dropdownOnToggle.emit(this.isOpenForConsumer);
            });
            removeEventListener('click', this.onClickOut);
            this._toggles.forEach(dropdownToggle => dropdownToggle.updateState(false));
            this._menus.forEach(dropdownMenu => dropdownMenu.updateState(false));
            if (this.containerComponentDisplayed) {
                this.zone.runOutsideAngular(() => {
                    removeEventListener('scroll', this.onPlacementReferenceScroll, true);
                    removeEventListener('resize', this.onPlacementReferenceScroll);
                });
            }
            if (this.displayContainerComponent) {
                this.removeContainer();
            }
            if (this.dropdownFocusOnClose && this._toggles.length) {
                this._toggles[0].content.focus();
            }
            this.children.forEach(child => child.closeDropdown());
            this.cdRef.markForCheck();
        }
    }
    getActualAlign() {
        return (this.responsiveDirection === 'start' || this.responsiveDirection === 'end') ? 'none' : this.dropdownAlign;
    }
    /** Selects a dropdown item. */
    selectItem(item) {
        this.dropdownOnSelect.emit(item);
        if (this.isElementOfDropdown(item)
            && !this.isDropdownToggleForDirectChild(item)
            && !this.isElementOfAnyChildDropdownInHierarchy(item)) {
            this.close();
        }
    }
    isElementOfAnyDropdownInHierarchy(item) {
        return this.isElementOfDropdown(item) || this.isElementOfAnyChildDropdownInHierarchy(item);
    }
    isElementOfAnyChildDropdownInHierarchy(item) {
        return this.children.some(child => child.isElementOfAnyDropdownInHierarchy(item));
    }
    isElementOfDropdown(item) {
        return this._menus.some(menu => menu.content.contains(item));
    }
    isDropdownToggleForDirectChild(item) {
        return this.children.some(child => child.toggles.some(toggle => toggle.content.contains(item)));
    }
}
SiDropdownDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiDropdownDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownDirective, selector: "[siDropdown]", inputs: { dropdownIsDisabled: "dropdownIsDisabled", dropdownIsOpen: "dropdownIsOpen", dropdownOpenOnLoad: "dropdownOpenOnLoad", dropdownCloseOnSelect: "dropdownCloseOnSelect", dropdownCloseOnClickOut: "dropdownCloseOnClickOut", dropdownCloseOnEsc: "dropdownCloseOnEsc", dropdownDirection: "dropdownDirection", dropdownAlign: "dropdownAlign", dropdownContainer: "dropdownContainer", dropdownResponsiveDirection: "dropdownResponsiveDirection", dropdownResponsiveDirectionToPlacement: "dropdownResponsiveDirectionToPlacement", dropdownPlacement: "dropdownPlacement", dropdownPlacementReference: "dropdownPlacementReference", dropdownItemSelector: "dropdownItemSelector", dropdownItemSelectorException: "dropdownItemSelectorException", dropdownFocusOnOpen: "dropdownFocusOnOpen", dropdownFocusOnClose: "dropdownFocusOnClose", closeOnMenuScrollOut: "closeOnMenuScrollOut", closeOnPlacementReferenceScrollOut: "closeOnPlacementReferenceScrollOut" }, outputs: { dropdownOnToggle: "dropdownOnToggle", dropdownOnSelect: "dropdownOnSelect", dropdownResponsiveDirectionChange: "dropdownResponsiveDirectionChange" }, host: { listeners: { "keydown.escape": "onKeydown($event)" }, properties: { "class.dropdown": "this.dropdownClass", "class.dropup": "this.isDropup", "class.dropstart": "this.isDropstart", "class.dropend": "this.isDropend", "class.show": "this.dropdownShowClass" } }, exportAs: ["si-dropdown"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdown]',
                    exportAs: 'si-dropdown'
                }]
        }], propDecorators: { dropdownIsDisabled: [{
                type: Input
            }], dropdownIsOpen: [{
                type: Input
            }], dropdownOpenOnLoad: [{
                type: Input
            }], dropdownCloseOnSelect: [{
                type: Input
            }], dropdownCloseOnClickOut: [{
                type: Input
            }], dropdownCloseOnEsc: [{
                type: Input
            }], dropdownDirection: [{
                type: Input
            }], dropdownAlign: [{
                type: Input
            }], dropdownContainer: [{
                type: Input
            }], dropdownResponsiveDirection: [{
                type: Input
            }], dropdownResponsiveDirectionToPlacement: [{
                type: Input
            }], dropdownPlacement: [{
                type: Input
            }], dropdownPlacementReference: [{
                type: Input
            }], dropdownItemSelector: [{
                type: Input
            }], dropdownItemSelectorException: [{
                type: Input
            }], dropdownFocusOnOpen: [{
                type: Input
            }], dropdownFocusOnClose: [{
                type: Input
            }], closeOnMenuScrollOut: [{
                type: Input
            }], closeOnPlacementReferenceScrollOut: [{
                type: Input
            }], dropdownOnToggle: [{
                type: Output
            }], dropdownOnSelect: [{
                type: Output
            }], dropdownResponsiveDirectionChange: [{
                type: Output
            }], dropdownClass: [{
                type: HostBinding,
                args: ['class.dropdown']
            }], isDropup: [{
                type: HostBinding,
                args: ['class.dropup']
            }], isDropstart: [{
                type: HostBinding,
                args: ['class.dropstart']
            }], isDropend: [{
                type: HostBinding,
                args: ['class.dropend']
            }], dropdownShowClass: [{
                type: HostBinding,
                args: ['class.show']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }] } });

class SiDropdownMenuDirective {
    constructor() {
        this.content = inject(ElementRef).nativeElement;
        this.siDropdownDirective = inject(SiDropdownDirective);
        /**
         * Whether the dropdown has previously been opened.
         */
        this.dropdownOpened = false;
        this.dropdownMenuClass = true;
        this.dropdownOpen = false;
        this.alignStart = true;
        this.alignEnd = false;
        this.alignCenter = false;
        this.tabindex = -1;
    }
    ngOnInit() {
        this.siDropdownDirective.addMenu(this);
        this.parentElement = this.content.parentElement;
    }
    ngOnDestroy() {
        this.siDropdownDirective.removeMenu(this);
    }
    updateState(state, align = '') {
        this.alignCenter = align === 'center';
        this.alignStart = !align || align === 'start';
        this.alignEnd = align === 'end';
        this.dropdownOpen = state;
        if (state) {
            this.dropdownOpened = true;
        }
    }
    onClickOrKeydown(event) {
        let currentTarget = event.target;
        const itemSelector = this.siDropdownDirective.dropdownItemSelector;
        const itemSelectorException = this.siDropdownDirective.dropdownItemSelectorException;
        while (currentTarget && currentTarget !== this.content
            && (!currentTarget.matches(itemSelector) || currentTarget.matches(itemSelectorException))) {
            currentTarget = currentTarget.parentElement;
        }
        if (currentTarget && currentTarget !== this.content
            && currentTarget.matches(itemSelector) && !currentTarget.matches(itemSelectorException)) {
            if (!currentTarget.classList.contains('disabled')) {
                this.siDropdownDirective.selectItem(currentTarget);
            }
        }
    }
}
SiDropdownMenuDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownMenuDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiDropdownMenuDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: { tabindex: "tabindex" }, host: { listeners: { "click": "onClickOrKeydown($event)", "keydown.enter": "onClickOrKeydown($event)" }, properties: { "class.dropdown-menu": "this.dropdownMenuClass", "class.show": "this.dropdownOpen", "class.align-start": "this.alignStart", "class.align-end": "this.alignEnd", "class.align-center": "this.alignCenter", "tabindex": "this.tabindex" } }, exportAs: ["si-dropdown-menu"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownMenu]',
                    exportAs: 'si-dropdown-menu'
                }]
        }], propDecorators: { dropdownMenuClass: [{
                type: HostBinding,
                args: ['class.dropdown-menu']
            }], dropdownOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], alignStart: [{
                type: HostBinding,
                args: ['class.align-start']
            }], alignEnd: [{
                type: HostBinding,
                args: ['class.align-end']
            }], alignCenter: [{
                type: HostBinding,
                args: ['class.align-center']
            }], tabindex: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['tabindex']
            }], onClickOrKeydown: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }] } });

class SiDropdownToggleDirective {
    constructor(element, siDropdownDirective) {
        this.siDropdownDirective = siDropdownDirective;
        this.toggleExecutedInTick = false;
        this.dropdownOpen = false;
        this.content = element.nativeElement;
    }
    ngAfterViewInit() {
        this.setA11y(false);
    }
    ngOnDestroy() {
        this.siDropdownDirective.removeToggle(this);
    }
    ngOnInit() {
        this.siDropdownDirective.addToggle(this);
    }
    updateState(state) {
        this.dropdownOpen = state;
        this.setA11y(state);
    }
    setA11y(state) {
        const menuItem = this.content.querySelector('[role=menuitem]');
        if (menuItem) {
            menuItem.setAttribute('aria-expanded', `${state}`);
            menuItem.setAttribute('aria-haspopup', 'true');
        }
        else {
            this.content.setAttribute('aria-expanded', `${state}`);
            this.content.setAttribute('aria-haspopup', 'true');
        }
    }
    open() {
        this.siDropdownDirective.open();
    }
    close() {
        this.siDropdownDirective.close();
    }
    onClick() {
        // when toggle is used on a button, enter also fires a click event --> suppressing it.
        if (!this.toggleExecutedInTick) {
            this.siDropdownDirective.toggle();
            this.toggleExecutedInTick = true;
            setTimeout(() => this.toggleExecutedInTick = false);
        }
    }
}
SiDropdownToggleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownToggleDirective, deps: [{ token: i0.ElementRef }, { token: SiDropdownDirective }], target: i0.ɵɵFactoryTarget.Directive });
SiDropdownToggleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", host: { listeners: { "click": "onClick()", "keydown.enter": "onClick()" }, properties: { "class.show": "this.dropdownOpen" } }, exportAs: ["si-dropdown-toggle"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownToggle]',
                    exportAs: 'si-dropdown-toggle'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: SiDropdownDirective }]; }, propDecorators: { dropdownOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class SiListInteractionService {
    constructor() {
        this.interactableLists = [];
        this.interactableListItems = [];
        this.changeQueue = [];
    }
    /**
     * Add an interactable list and add it to `listsAndItemsChanges`.
     * Return the interactable list object.
     */
    addInteractableList(directive, element, triggerDefined, position, orientation, focusLastFromParent, isSubList, calculatedCallback) {
        const list = {
            directive,
            element,
            items: [],
            integratedNestedItems: [],
            integratedParentItem: null,
            allItemsQueryList: new QueryList(),
            nested: [],
            nestedDisconnected: [],
            parent: null,
            parentDisconnected: null,
            trigger: null,
            triggerDefined: new BehaviorSubject(triggerDefined),
            position: new BehaviorSubject(position),
            orientation: new BehaviorSubject(orientation),
            focusLastFromParent: new BehaviorSubject(focusLastFromParent),
            changeToProcess: true,
            isSubList,
            calculatedCallback
        };
        this.enqueueChange({ object: list, type: 'list', change: 'add' });
        return list;
    }
    /**
     * Remove an interactable list and add it to `listsAndItemsChanges`.
     */
    removeInteractableList(list) {
        this.enqueueChange({ object: list, type: 'list', change: 'remove' });
    }
    /**
     * Add an interactable list item and add it to `listsAndItemsChanges`.
     * Return the interactable list item object.
     */
    addInteractableListItem(directive, element) {
        const item = { directive, element, parent: null, child: null, parentIntegrationIndex: -1,
            nestedIntegration: false };
        this.enqueueChange({ object: item, type: 'item', change: 'add' });
        return item;
    }
    /**
     * Remove an interactable list item and add it to `listsAndItemsChanges`.
     */
    removeInteractableListItem(item) {
        this.enqueueChange({ object: item, type: 'item', change: 'remove' });
    }
    /**
     * Check if a HTMLElement is a child of a interactable list item.
     * First properly follows any possible dropdown containers through the DOM chain,
     * then continues of the list interaction chain.
     */
    isChildElementOfItem(item, element) {
        let currentItem = this.getClosestRelativeItem(element, true);
        if (currentItem) {
            do {
                if (currentItem.element === item.element) {
                    return true;
                }
                currentItem = currentItem.parent
                    ? (currentItem.parent.trigger ?? (currentItem.parent.parentDisconnected ? currentItem.parent.parentDisconnected.trigger : null))
                    : null;
            } while (currentItem);
        }
        return false;
    }
    enqueueChange(change) {
        this.changeQueue.push(change);
        if (this.queueTimer) {
            return;
        }
        this.queueTimer = setTimeout(() => {
            this.processChanges(this.changeQueue);
            this.changeQueue = [];
            this.queueTimer = undefined;
        });
    }
    /**
     * Process the changes after the buffer and queue up all the necessary
     * operations and callbacks. Then execute them.
     */
    processChanges(changes) {
        const operations = ['listSetChildItems', 'listSetNestedLists', 'listSetTriggerItem', 'listSetIntegratedItems'];
        const allOperations = {
            'listSetChildItems': [],
            'listSetNestedLists': [],
            'listSetTriggerItem': [],
            'listSetIntegratedItems': []
        };
        const operationsToProcess = {
            'listSetChildItems': { lists: [], callbacks: [] },
            'listSetNestedLists': { lists: [], callbacks: [] },
            'listSetTriggerItem': { lists: [], callbacks: [] },
            'listSetIntegratedItems': { lists: [], callbacks: [] }
        };
        /**
         * Add a callback function to an operation in `operationsToProcess`.
         */
        const addOperationCallback = (operation, callback) => {
            operationsToProcess[operation].callbacks.push(callback);
        };
        /**
         * Add a list to an operation in `operationsToProcess`, only if there is not
         * already one inside.
         */
        const addOperation = (operation, list, callback) => {
            const operationObject = operationsToProcess[operation];
            const allArray = allOperations[operation];
            if (!allArray.find(operationList => operationList.element === list.element)) {
                operationObject.lists.push(list);
                allArray.push(list);
            }
            if (operation === 'listSetChildItems' || operation === 'listSetNestedLists') {
                removeOperation('listSetIntegratedItems', list);
            }
            if (callback) {
                addOperationCallback(operation, callback);
            }
        };
        /**
         * Remove a list from an operation in `allOperations`.
         */
        const removeOperation = (operation, list) => {
            const operationObject = allOperations[operation];
            const listIndex = operationObject.findIndex(searchList => searchList.element === list.element);
            if (listIndex !== -1) {
                operationObject.splice(listIndex, 1);
            }
        };
        /**
         * The precedence of the changes.
         */
        const changePrecedence = {
            'remove': 1,
            'add': 2,
            'changes': 3
        };
        /**
         * The changes to process after they have been filtered to remove
         * duplicate (and unnecessary) changes. Sort them according to
         * `changePrecedence`.
         */
        const sortedAndFilteredChanges = changes.filter((change, index) => {
            const isChanges = change.change === 'changes';
            const duplicates = [];
            const duplicateAddRemove = [];
            const duplicateChange = [];
            changes.forEach((searchChange, searchChangeIndex) => {
                if (searchChange.object.element === change.object.element) {
                    const item = { change: searchChange, index: searchChangeIndex };
                    duplicates.push(item);
                    if (searchChange.change !== 'changes') {
                        duplicateAddRemove.push(item);
                    }
                    else if (isChanges) {
                        duplicateChange.push(item);
                    }
                }
            });
            if (duplicates.length > 1) {
                const addRemoveStart = duplicateAddRemove.length ? duplicateAddRemove[0].change.change : '';
                const addRemoveEnd = duplicateAddRemove.length ? duplicateAddRemove[duplicateAddRemove.length - 1].change.change : '';
                if (isChanges) {
                    if (addRemoveStart !== '' && addRemoveStart === addRemoveEnd) {
                        return false;
                    }
                    else if (duplicateChange[duplicateChange.length - 1].index !== index) {
                        return false;
                    }
                }
                else {
                    if (addRemoveStart === '' || addRemoveStart !== addRemoveEnd) {
                        return false;
                    }
                    else if (duplicates[duplicates.length - 1].index !== index) {
                        return false;
                    }
                }
            }
            return true;
        }).sort((a, b) => (changePrecedence[a.change] - changePrecedence[b.change]));
        /**
         * Go through the changes in `sortedAndFilteredChanges` and add operations
         * and callbacks for every change.
         */
        sortedAndFilteredChanges.forEach(change => {
            const actualChange = change.change;
            switch (actualChange) {
                case 'add':
                    if (change.type === 'list') {
                        const list = change.object;
                        this.interactableLists.push(list);
                        list.triggerDefined.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.position.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.orientation.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.focusLastFromParent.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        addOperation('listSetChildItems', list);
                        addOperation('listSetNestedLists', list, () => list.nested.forEach(nestedList => addOperation('listSetTriggerItem', nestedList)));
                        addOperation('listSetIntegratedItems', list, () => {
                            if (list.isSubList) {
                                const parent = list.parent ?? this.getClosestParent(list.element);
                                if (parent) {
                                    addOperation('listSetNestedLists', parent);
                                    addOperation('listSetTriggerItem', list);
                                    addOperation('listSetIntegratedItems', parent);
                                }
                            }
                            else {
                                const parentDisconnected = list.parentDisconnected ?? this.getClosestParent(list.element);
                                if (parentDisconnected) {
                                    addOperation('listSetNestedLists', parentDisconnected);
                                    addOperation('listSetIntegratedItems', parentDisconnected);
                                }
                            }
                        });
                    }
                    else {
                        const item = change.object;
                        this.interactableListItems.push(item);
                        addOperationCallback('listSetChildItems', () => {
                            const parent = item.parent ?? this.getClosestParent(item.element);
                            if (parent) {
                                addOperation('listSetChildItems', parent);
                                addOperationCallback('listSetNestedLists', () => {
                                    parent.nested.forEach(parentNestedList => addOperation('listSetTriggerItem', parentNestedList));
                                    const grandparent = parent.parent;
                                    if (grandparent) {
                                        addOperation('listSetIntegratedItems', grandparent);
                                    }
                                });
                                addOperation('listSetIntegratedItems', parent);
                            }
                        });
                    }
                    break;
                case 'remove':
                    if (change.type === 'list') {
                        const listIndex = this.interactableLists.findIndex(interactableList => interactableList.element === change.object.element);
                        const list = change.object;
                        this.interactableLists.splice(listIndex, 1);
                        list.nested.forEach(nestedList => addOperation('listSetTriggerItem', nestedList));
                        list.items.forEach(item => item.parent = null);
                        list.integratedNestedItems.forEach(item => item.parentIntegrationIndex = -1);
                        const integratedParentItem = list.integratedParentItem;
                        if (integratedParentItem) {
                            integratedParentItem.nestedIntegration = false;
                        }
                        const parent = list.parent;
                        if (parent) {
                            addOperation('listSetChildItems', parent);
                            addOperation('listSetNestedLists', parent);
                            addOperation('listSetIntegratedItems', parent);
                            const grandparent = parent.parent;
                            if (grandparent) {
                                addOperation('listSetIntegratedItems', grandparent);
                            }
                            list.parent = null;
                        }
                        else {
                            const parentDisconnected = list.parent;
                            if (parentDisconnected) {
                                addOperation('listSetChildItems', parentDisconnected);
                                addOperation('listSetNestedLists', parentDisconnected);
                                addOperation('listSetIntegratedItems', parentDisconnected);
                                list.parentDisconnected = null;
                            }
                            list.triggerDefined.complete();
                            list.position.complete();
                            list.orientation.complete();
                            list.focusLastFromParent.complete();
                        }
                        const parentItem = list.trigger;
                        if (parentItem) {
                            const listTrigger = list.trigger;
                            if (listTrigger && listTrigger.child?.element === list.element) {
                                listTrigger.child = null;
                            }
                        }
                    }
                    else {
                        const itemIndex = this.interactableListItems.findIndex(interactableListItem => (interactableListItem.element === change.object.element));
                        const item = change.object;
                        this.interactableListItems.splice(itemIndex, 1);
                        const parent = item.parent;
                        if (parent) {
                            addOperation('listSetChildItems', parent);
                            const grandparent = parent.parent;
                            if (grandparent) {
                                addOperation('listSetIntegratedItems', grandparent);
                            }
                            const childList = item.child;
                            if (childList) {
                                item.child = null;
                                addOperation('listSetTriggerItem', childList);
                                addOperation('listSetIntegratedItems', parent);
                            }
                        }
                    }
                    break;
                case 'changes':
                    const changeList = change.object;
                    addOperation('listSetTriggerItem', changeList, () => {
                        const changeParent = changeList.parent;
                        if (changeParent) {
                            addOperation('listSetIntegratedItems', changeParent);
                        }
                    });
                    addOperation('listSetIntegratedItems', changeList);
                    break;
            }
        });
        /**
         * Execute the queued operations and callbacks in order.
         * (Important) Callbacks can add operations, also for previously
         * executed operations.
         */
        const executeOperations = () => {
            for (let i = 0; i < operations.length; i++) {
                const currentOperation = operations[i];
                const operationObject = operationsToProcess[currentOperation];
                operationObject.lists.forEach(list => this[currentOperation](list));
                operationObject.lists = [];
                operationObject.callbacks.forEach(callback => callback());
                operationObject.callbacks = [];
                for (let u = 0; u < i + 1; u++) {
                    const currentCheckOperation = operations[u];
                    if (operationsToProcess[currentCheckOperation].lists.length > 0) {
                        executeOperations();
                        break;
                    }
                }
            }
        };
        executeOperations();
        /**
         * Execute `listSetAllItemsQueryList` for every list that had
         * relevant changes.
         */
        this.interactableLists.forEach(interactableList => {
            if (interactableList.changeToProcess) {
                interactableList.changeToProcess = false;
                this.listSetAllItemsQueryList(interactableList);
            }
        });
        sortedAndFilteredChanges.forEach(change => {
            if (change.change === 'add' && change.type === 'list' && change.object.calculatedCallback) {
                change.object.calculatedCallback();
            }
        });
    }
    /**
     * Set all the child items of an interactable list.
     */
    listSetChildItems(list) {
        list.items.forEach(item => {
            if (item.parent?.element === list?.element) {
                item.parent = null;
            }
        });
        list.changeToProcess = true;
        list.items = this.getChildItems(list.element);
        list.items.forEach(item => {
            const oldParent = item.parent;
            if (oldParent && oldParent.element !== list.element) {
                const oldParentChildIndex = oldParent.items.findIndex(parentChildItem => parentChildItem.element === item.element);
                if (oldParentChildIndex >= 0) {
                    oldParent.changeToProcess = true;
                    oldParent.items.splice(oldParentChildIndex, 1);
                }
            }
            item.parent = list;
        });
    }
    /**
     * Set all the nested lists of an interactable list.
     */
    listSetNestedLists(list) {
        list.nested.forEach(nestedList => {
            if (nestedList.parent?.element === list?.element) {
                nestedList.parent = null;
            }
        });
        list.nestedDisconnected.forEach(nestedList => {
            if (nestedList.parentDisconnected?.element === list?.element) {
                nestedList.parentDisconnected = null;
            }
        });
        const subList = [];
        const nonSubList = [];
        for (const item of this.getNestedLists(list.element)) {
            if (item.isSubList) {
                subList.push(item);
            }
            else {
                nonSubList.push(item);
            }
        }
        list.nested = subList;
        subList.forEach(nestedList => {
            const oldParent = nestedList.parent;
            if (oldParent) {
                const oldParentNestedListIndex = oldParent.nested.findIndex(parentNestedList => parentNestedList.element === nestedList.element);
                if (oldParentNestedListIndex >= 0) {
                    oldParent.nested.splice(oldParentNestedListIndex, 1);
                }
            }
            nestedList.parent = list;
        });
        list.nestedDisconnected = nonSubList;
        nonSubList.forEach(nestedList => {
            const oldParent = nestedList.parentDisconnected;
            if (oldParent) {
                const oldParentNestedListIndex = oldParent.nestedDisconnected.findIndex(parentNestedList => (parentNestedList.element === nestedList.element));
                if (oldParentNestedListIndex >= 0) {
                    oldParent.nestedDisconnected.splice(oldParentNestedListIndex, 1);
                }
            }
            nestedList.parentDisconnected = list;
        });
    }
    /**
     * Set the trigger/parent item of an interactable list.
     */
    listSetTriggerItem(list) {
        const listTrigger = list.trigger;
        if (listTrigger && listTrigger.child?.element === list.element) {
            listTrigger.child = null;
        }
        list.trigger = null;
        const setParentItem = list.triggerDefined.value;
        let parentItem;
        if (setParentItem) {
            const setParentItemHost = setParentItem.getHostElement();
            parentItem = this.interactableListItems.find(item => item.element === setParentItemHost);
        }
        else {
            parentItem = this.getClosestRelativeItem(list.element);
        }
        if (parentItem) {
            const oldChild = parentItem.child;
            if (oldChild) {
                oldChild.trigger = null;
            }
            parentItem.child = list;
            list.trigger = parentItem;
        }
        else {
            list.trigger = null;
        }
    }
    /**
     * Set the integrated items (nested or parent) of an interactable list.
     * This is used to connect sub/parent interactable lists depending on
     * the position of the nested interactable lists.
     */
    listSetIntegratedItems(list) {
        list.integratedNestedItems.forEach(integratedNestedItem => {
            integratedNestedItem.parentIntegrationIndex = -1;
        });
        list.integratedNestedItems = [];
        list.changeToProcess = true;
        list.nested.forEach(nestedList => {
            const integratedParentItem = nestedList.integratedParentItem;
            if (integratedParentItem) {
                integratedParentItem.nestedIntegration = false;
            }
            nestedList.integratedParentItem = null;
            nestedList.changeToProcess = true;
            const nestedListTrigger = nestedList.trigger;
            if (nestedListTrigger && nestedList.items.length > 0) {
                if (this.areParentIntegratedItemsAppropriate(nestedList)) {
                    if (nestedList.position.value === 'integrated') {
                        let actualReverse = nestedList.orientation.value === 'ets';
                        if (nestedList.focusLastFromParent.value) {
                            actualReverse = !actualReverse;
                        }
                        const integratedNestedItem = (actualReverse ? nestedList.items.slice().reverse() : nestedList.items)
                            .find(nestedListItem => !nestedListItem.child);
                        if (integratedNestedItem) {
                            const parentIntegrationIndex = list.items.findIndex(item => item.element === nestedListTrigger.element);
                            if (parentIntegrationIndex >= 0) {
                                integratedNestedItem.parentIntegrationIndex = parentIntegrationIndex;
                                list.integratedNestedItems.push(integratedNestedItem);
                            }
                        }
                    }
                    else {
                        nestedList.integratedParentItem = nestedListTrigger;
                        nestedListTrigger.nestedIntegration = true;
                    }
                }
            }
        });
    }
    /**
     * Set the `allItemsQueryList` of an interactable list.
     */
    listSetAllItemsQueryList(list) {
        const allItems = list.items.slice();
        let indexAddition = 0;
        const iter = (integratedItem) => {
            allItems.splice((integratedItem.parentIntegrationIndex === -1
                ? (list.orientation.value === 'ets' ? allItems.length : -1)
                : integratedItem.parentIntegrationIndex) + indexAddition + 1, 0, integratedItem);
            indexAddition++;
        };
        if (list.orientation.value !== 'ets' && list.integratedParentItem) {
            iter(list.integratedParentItem);
        }
        list.integratedNestedItems.forEach(iter);
        if (list.orientation.value === 'ets' && list.integratedParentItem) {
            iter(list.integratedParentItem);
        }
        list.allItemsQueryList.reset(allItems.map(item => item.directive));
    }
    /**
     * Check if integrated items are appropriate in the parent of an interactable list.
     */
    areParentIntegratedItemsAppropriate(list) {
        const parent = list.parent;
        if (parent) {
            const orientation = list.orientation.value;
            const parentOrientation = parent.orientation.value;
            return orientation === 'vert' && parentOrientation !== 'vert' || orientation !== 'vert' && parentOrientation === 'vert';
        }
        return false;
    }
    /**
     * Get the child items of an interactable list.
     */
    getChildItems(element, ret = []) {
        if (this.interactableListItems.length) {
            const iter = (child) => {
                const childResult = this.interactableListItems.find(item => (item.element === child));
                if (childResult) {
                    ret.push(childResult);
                }
                else if (!this.interactableLists.find(list => (list.element === child))) {
                    this.getChildItems(child, ret);
                }
            };
            Array.from(element.children).forEach(iter);
            Array.from(element.dropdownChildren || []).forEach(iter);
        }
        return ret;
    }
    /**
     * Get the nested lists of an interactable list.
     */
    getNestedLists(element, ret = []) {
        if (this.interactableLists.length) {
            const iter = (child) => {
                const childResult = this.interactableLists.find(list => (list.element === child));
                if (childResult) {
                    ret.push(childResult);
                }
                else {
                    this.getNestedLists(child, ret);
                }
            };
            Array.from(element.children).forEach(iter);
            Array.from(element.dropdownChildren || []).forEach(iter);
        }
        return ret;
    }
    /**
     * Get the closest relative/parent/trigger item of an interactable list.
     * @param onlyCheckParent Determines if the children of parents should also be checked or ignored.
     */
    getClosestRelativeItem(element, onlyCheckParent = false) {
        if (this.interactableListItems.length) {
            let previousElement;
            let currentElement = element;
            let outOfMenu = false;
            while (currentElement.parentElement && !outOfMenu) {
                previousElement = currentElement;
                const checkElement = currentElement = currentElement.dropdownParentElement ?? currentElement.parentElement;
                const item = this.interactableListItems.find(interactableListItem => interactableListItem.element === checkElement
                    && interactableListItem.parent);
                if (item) {
                    return item;
                }
                if (!onlyCheckParent) {
                    const childResult = this.getClosestChildItemIndex(currentElement, previousElement);
                    if (childResult !== null && childResult >= 0) {
                        return this.interactableListItems[childResult];
                    }
                    if (currentElement !== element && this.interactableLists.find(interactableList => interactableList.element === checkElement)) {
                        outOfMenu = true;
                    }
                }
            }
        }
        return null;
    }
    /**
     * Get the closest parent list of an interactable list.
     */
    getClosestParent(element) {
        if (this.interactableLists.length > 0) {
            let currentElement = element;
            while (currentElement.parentElement) {
                const checkElement = currentElement = currentElement.dropdownParentElement ?? currentElement.parentElement;
                const parent = this.interactableLists.find(interactableList => interactableList.element === checkElement);
                if (parent) {
                    return parent;
                }
            }
        }
        return null;
    }
    /**
     * Get the index of the closest child item.
     */
    getClosestChildItemIndex(element, currentChild, reverse = false) {
        if (this.interactableListItems.length < 0) {
            return null;
        }
        if (!element.children.length && !element.dropdownChildren?.length) {
            return null;
        }
        const children = [...Array.from(element.children), ...Array.from(element.dropdownChildren || [])];
        if (currentChild) {
            const currentElementIndex = children.indexOf(currentChild);
            let currentChildAppeared = false;
            const result = [];
            children.forEach((child, index) => {
                if (child !== currentChild) {
                    const childIndex = this.interactableListItems.findIndex(item => (item.element === child));
                    if (childIndex === -1) {
                        const subResult = this.getClosestChildItemIndex(child, undefined, !currentChildAppeared);
                        if (subResult !== null) {
                            result.push({ itemIndex: subResult, elementIndex: index });
                        }
                    }
                    else {
                        result.push({ itemIndex: childIndex, elementIndex: index });
                    }
                }
                else {
                    currentChildAppeared = true;
                }
            });
            result.sort((firstItem, secondItem) => (Math.abs(currentElementIndex - firstItem.elementIndex) - Math.abs(currentElementIndex - secondItem.elementIndex)));
            return result.length ? result[0].itemIndex : null;
        }
        else {
            const correctedChildren = reverse ? children.reverse() : children;
            for (const child of correctedChildren) {
                const childIndex = this.interactableListItems.findIndex(item => (item.element === child));
                if (childIndex === -1) {
                    const subResult = this.getClosestChildItemIndex(child, undefined, reverse);
                    if (subResult !== null) {
                        return subResult;
                    }
                }
                else {
                    return childIndex;
                }
            }
        }
        return null;
    }
}
SiListInteractionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiListInteractionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiListInteractionDirective {
    constructor() {
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this._elementRef = inject((ElementRef));
        this._changeDetector = inject(ChangeDetectorRef);
        this.listInteractionService = inject(SiListInteractionService);
        /**
         * Specifies whether sub lists are displayed on the start side.
         */
        this.listInteractionSubListStart = false;
        /**
         * Specifies whether the component should automatically be focused as soon as it is loaded.
         */
        this.listInteractionFocusOnLoad = false;
        /**
         * Specifies if any and which item should be focused when the component is focused,
         */
        this.listInteractionFocus = '';
        this._focusLastFromParent = false;
        this.listInteractionAutofocusFromParent = false;
        /**
         * Specifies whether the list is a sub list and a parent item should automatically be searched or be defined.
         */
        this.listInteractionIsSubList = false;
        this._parentItem = null;
        this._position = 'integrated';
        this._orientation = 'ste';
        /**
         * Automatically refocus after pressing enter on one of the items.
         * Use when losing focus causes issues with keyboard interaction.
         */
        this.listInteractionRefocusAfterEnter = false;
        /**
         * Wether a hidden `dropdown-menu` or `si-menu` sub-menu
         * should be shown when the parent item is focused.
         */
        this.listInteractionShowSubListOnParentFocus = true;
        /**
         * Specifies whether the currently open item should be closed on click outside.
         */
        this.listInteractionCloseOnClickOut = true;
        this.hadShowClassBefore = false;
        this.keepOpen = false;
        this.tabindex = '';
        this.connectionsCalculated = false;
        this._ignoreKeydown = undefined;
        this.ignoreNextChange = false;
        this.onClickOut = (event) => this.clickOutHandler(event);
    }
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link listInteractionSubListStart}
     * @deprecated Use the new Input {@link listInteractionSubListStart}
     */
    set listInteractionSubListLeft(value) {
        this.listInteractionSubListStart = value;
    }
    get listInteractionSubListLeft() {
        return this.listInteractionSubListStart;
    }
    get start() {
        return this.orientation === 'vert' && this.listInteractionSubListStart;
    }
    /**
     * Specifies if the last item should be focused when navigating to this list from a parent list (instead of the first).
     * @defaultref {@link _focusLastFromParent}
     */
    set listInteractionFocusLastFromParent(focusLastFromParent) {
        this._focusLastFromParent = focusLastFromParent;
        if (this.interactableList) {
            this.interactableList.focusLastFromParent.next(focusLastFromParent);
        }
    }
    get listInteractionFocusLastFromParent() {
        return this._focusLastFromParent;
    }
    get autofocusFromParent() {
        return this.listInteractionAutofocusFromParent;
    }
    /**
     * Specifies a specific item that is the parent of the menu if the automatic one is incorrect.
     * @defaultref {@link _parentItem}
     */
    set listInteractionParentItem(item) {
        this._parentItem = item;
        if (this.interactableList) {
            this.interactableList.triggerDefined.next(item);
        }
    }
    get listInteractionParentItem() {
        if (!this._parentItem && this.interactableList && this.interactableList.trigger) {
            return this.interactableList.trigger.directive;
        }
        return this._parentItem;
    }
    get parentItem() {
        return this.listInteractionParentItem;
    }
    /**
     * Specifies the position of the list relative to the parent.
     * Only relevant if this is a sub list and the parent has a different orientation.
     * @defaultref {@link _position}
     */
    set listInteractionPosition(position) {
        this._position = position;
        if (this.interactableList) {
            this.interactableList.position.next(position);
        }
    }
    get listInteractionPosition() {
        return this._position;
    }
    /**
     * Specifies the orientation of the list, which signifies how it should be navigated.
     * The default is horizontal start to end.
     * @defaultref {@link _orientation}
     */
    set listInteractionOrientation(orientation) {
        const deprecatedMessage = '@simpl/element-ng - SiListInteractionDirective: \
Setting listInteractionOrientation to "ltr" or "rtl" is deprecated, use "ste" or "ets" instead';
        if (orientation === 'ltr') {
            console.warn(deprecatedMessage);
            this._orientation = 'ste';
        }
        else if (orientation === 'rtl') {
            console.warn(deprecatedMessage);
            this._orientation = 'ets';
        }
        else {
            this._orientation = orientation;
        }
        if (this.interactableList) {
            this.interactableList.orientation.next(this._orientation);
        }
    }
    get listInteractionOrientation() {
        return this._orientation;
    }
    get orientation() {
        return this._orientation;
    }
    setKeepOpen(state) {
        if (state && !this.keepOpen) {
            this.keepOpen = true;
            this.hadShowClassBefore = this._elementRef.nativeElement.classList.contains('show');
            this._elementRef.nativeElement.classList.add('show');
        }
        else if (!state && this.keepOpen) {
            this.keepOpen = false;
            if (!this.hadShowClassBefore) {
                this._elementRef.nativeElement.classList.remove('show');
            }
            this.hadShowClassBefore = false;
        }
    }
    get parentList() {
        if (this.interactableList?.parent) {
            return this.interactableList.parent.directive;
        }
        return null;
    }
    get parentListAll() {
        if (this.interactableList && (this.interactableList.parent || this.interactableList.parentDisconnected)) {
            return this.interactableList.parent ? this.interactableList.parent.directive : this.interactableList.parentDisconnected.directive;
        }
        return null;
    }
    ngAfterViewInit() {
        this._keyManager = new FocusKeyManager(this.interactableList.allItemsQueryList).withWrap().withAllowedModifierKeys(['shiftKey']);
        this._orientationChangeSubscription = this.interactableList.orientation.subscribe(orientation => {
            this._orientation = orientation;
            if (orientation === 'vert') {
                this._keyManager.withHorizontalOrientation(null).withVerticalOrientation();
            }
            else {
                this._keyManager.withVerticalOrientation(false).withHorizontalOrientation(orientation === 'ste' ? 'ltr' : 'rtl');
            }
        });
        this._changeSubscription = this._keyManager.change.subscribe(() => {
            if (this.listInteractionShowSubListOnParentFocus && !this.ignoreNextChange) {
                this.openCloseAndFocusChildren();
                addEventListener('click', this.onClickOut);
            }
            this.ignoreNextChange = false;
        });
        this._tabOutSubscription = this._keyManager.tabOut.subscribe(() => {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        });
    }
    ngOnInit() {
        this.interactableList = this.listInteractionService.addInteractableList(this, this._elementRef.nativeElement, this._parentItem, this._position, this._orientation, this._focusLastFromParent, this.listInteractionIsSubList, () => this.calculated());
        const newTabindex = !this.listInteractionIsSubList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
    }
    ngOnDestroy() {
        this._orientationChangeSubscription?.unsubscribe();
        this._changeSubscription?.unsubscribe();
        this._tabOutSubscription?.unsubscribe();
        if (this.interactableList) {
            this.listInteractionService.removeInteractableList(this.interactableList);
        }
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
        }
    }
    calculated() {
        this.connectionsCalculated = true;
        const newTabindex = (!this.listInteractionIsSubList || !this.parentList) ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
        if (this.isBrowser && document.activeElement === this.getHostElement()) {
            if (this.listInteractionFocus) {
                this.focusFirstItem('program', this.listInteractionFocus === 'last');
            }
        }
        else if (this.listInteractionFocusOnLoad) {
            this._elementRef.nativeElement.focus();
        }
    }
    /** Ignore the next keydown in this and all parent lists. Called from sub list. */
    ignoreKeydown(event) {
        if (this.parentListAll) {
            this.parentListAll.ignoreKeydown(event);
        }
        this._ignoreKeydown = event;
    }
    /** Handle a keyboard event from the list, delegating to the appropriate action. */
    handleKeydown(event) {
        if (this._ignoreKeydown?.key === event.key && this._ignoreKeydown.target === event.target) {
            this._ignoreKeydown = undefined;
            return;
        }
        if (this.parentListAll) {
            // If this is a sub list ignore the keydown in all parent lists (due to event bubbling).
            this.parentListAll.ignoreKeydown(event);
        }
        const manager = this._keyManager;
        const rtlCorrectedKey = correctKeyRTL(event.key);
        switch (rtlCorrectedKey) {
            case 'Escape':
                if (!event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                    event.preventDefault();
                    this.goDownLevel();
                }
                break;
            case 'ArrowUp':
                if (this._orientation !== 'vert') {
                    this.goDownLevel();
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowDown':
                if (this.orientation !== 'vert') {
                    this.goUpLevel();
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowLeft':
                if (this._orientation === 'vert') {
                    if (this.listInteractionSubListStart) {
                        this.goUpLevel(false, true);
                    }
                    else {
                        this.goDownLevel();
                    }
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowRight':
                if (this._orientation === 'vert') {
                    if (this.listInteractionSubListStart) {
                        this.goDownLevel(false, true);
                    }
                    else {
                        this.goUpLevel();
                    }
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'Enter':
                if (this.listInteractionRefocusAfterEnter) {
                    setTimeout(() => {
                        if (this.isBrowser && (!document.activeElement || document.activeElement === document.body)) {
                            this.focusActiveItem();
                            this.openCloseAndFocusChildren(true);
                        }
                    });
                }
                break;
            default:
                manager.onKeydown(event);
        }
    }
    clickOutHandler(event) {
        const target = event.target;
        if (this.listInteractionCloseOnClickOut && this.interactableList
            && !this._keyManager?.activeItem?.hasChildElement(target)) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        }
    }
    closePreviousChild() {
        if (this._previousChild) {
            this._previousChild.close();
            this._previousChild = undefined;
            this._changeDetector.detectChanges();
        }
    }
    openCloseAndFocusChildren(skipOpenClose = false) {
        if (!skipOpenClose) {
            this.closePreviousChild();
        }
        const active = this._keyManager.activeItem;
        if (active) {
            if (active.dropdownToggle) {
                active.dropdownToggle.open();
                this._previousChild = { close: () => active.dropdownToggle?.close() };
                this._changeDetector.detectChanges();
            }
            else {
                const activeList = this.getItemChildren(active);
                if (activeList && activeList.getHostElement() !== this.getHostElement()) {
                    if (!skipOpenClose) {
                        activeList.open();
                        this._previousChild = activeList;
                        this._changeDetector.detectChanges();
                    }
                    if (activeList.autofocusFromParent) {
                        Promise.resolve().then(() => activeList.focusFirstItem());
                    }
                }
            }
        }
    }
    open() {
        this.setKeepOpen(true);
    }
    close() {
        this.setKeepOpen(false);
    }
    setActiveItem(activeItem, ignoreNextChange = false) {
        if (!this._keyManager.activeItem || activeItem.getHostElement() !== this._keyManager.activeItem.getHostElement()) {
            this.ignoreNextChange = ignoreNextChange;
            this._keyManager.setActiveItem(activeItem);
        }
    }
    focusActiveItem(origin = 'program', reverse = false, nestedItem) {
        let active = this._keyManager.activeItem;
        if (!active && nestedItem && this.interactableList) {
            const foundActiveItem = this.interactableList.allItemsQueryList
                .find(listItem => nestedItem.getHostElement() === listItem.getHostElement());
            if (foundActiveItem) {
                this._keyManager.setActiveItem(foundActiveItem);
                active = this._keyManager.activeItem;
            }
        }
        if (active) {
            if (active && nestedItem
                && nestedItem.getHostElement() === active?.getHostElement()) {
                if (reverse) {
                    this._keyManager.setNextItemActive();
                }
                else {
                    this._keyManager.setPreviousItemActive();
                }
            }
            const activeIndex = this._keyManager.activeItemIndex;
            const newActive = this._keyManager.activeItem;
            if (newActive) {
                if (activeIndex && this.interactableList?.allItemsQueryList.get(activeIndex)?.getHostElement() !== newActive?.getHostElement()) {
                    this._keyManager.setActiveItem(newActive);
                }
                else {
                    newActive.focus();
                }
            }
        }
    }
    /**
     * Focus the first item in the list.
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusFirstItem(origin = 'program', reverse = false, fromParent = false) {
        const itemIndexes = Array.from(this.interactableList.allItemsQueryList).map((item, index) => {
            if (item.parentList && !item.disabled && item.parentList.getHostElement() === this.getHostElement()) {
                return index;
            }
            else {
                return -1;
            }
        }).filter(index => index !== -1);
        if (itemIndexes.length > 0) {
            let actualReverse = reverse;
            if (this.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (fromParent && this.listInteractionFocusLastFromParent) {
                actualReverse = !actualReverse;
            }
            const actualIndex = actualReverse ? itemIndexes[itemIndexes.length - 1] : itemIndexes[0];
            const activeItem = this.interactableList.allItemsQueryList.get(actualIndex);
            const activeItemHost = activeItem.getHostElement();
            if (this.interactableList.parent?.integratedNestedItems.find(item => item.element === activeItemHost)) {
                this.parentList.setActiveItem(activeItem);
            }
            else if (this.interactableList.integratedParentItem) {
                this.parentList.setActiveItem(this.interactableList.integratedParentItem.directive);
            }
            if (this._keyManager.activeItem?.getHostElement() === activeItem.getHostElement()) {
                this.focusActiveItem();
                this.openCloseAndFocusChildren();
                addEventListener('click', this.onClickOut);
            }
            else {
                this._keyManager.setFocusOrigin(origin).setActiveItem(activeItem);
            }
        }
    }
    getItemChildren(item) {
        return item.childList;
    }
    goDownLevel(reverse = false, start = false) {
        if (this.parentList) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
            const nestedOrParentItem = this.interactableList
                ? (this.interactableList.integratedParentItem ? this.interactableList.integratedParentItem
                    : this.interactableList.items.find(item => item.parentIntegrationIndex !== -1 || item.nestedIntegration))?.directive
                : undefined;
            let actualReverse = reverse;
            const parent = this.parentList;
            if (parent.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (start && !parent.start) {
                actualReverse = !actualReverse;
            }
            parent.focusActiveItem('program', actualReverse, nestedOrParentItem);
        }
        else {
            this.goUpLevel(true);
        }
    }
    goUpLevel(reverse = false, start = false) {
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = this.getItemChildren(active);
            if (activeList) {
                activeList.focusFirstItem('program', reverse, true);
            }
            else if (this.parentList) {
                this.goDownLevel(true, start);
            }
        }
    }
    focusInEvent(target) {
        if (this.connectionsCalculated) {
            if (target === this.getHostElement()) {
                if (this.listInteractionFocus) {
                    this.focusFirstItem('program', this.listInteractionFocus === 'last');
                }
            }
            else {
                if (this.interactableList) {
                    const foundItem = this.interactableList.items.find(item => item.element.contains(target));
                    if (foundItem) {
                        this.setActiveItem(foundItem.directive, true);
                    }
                }
                Promise.resolve().then(() => {
                    if (this.tabindex !== '-1') {
                        this.tabindex = '-1';
                    }
                });
            }
        }
    }
    focusOutEvent() {
        if (this.tabindex === '-1') {
            Promise.resolve().then(() => this.tabindex = (!this.listInteractionIsSubList || !this.parentList) ? '0' : '');
        }
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
}
SiListInteractionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiListInteractionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: { listInteractionSubListLeft: "listInteractionSubListLeft", listInteractionSubListStart: "listInteractionSubListStart", listInteractionFocusOnLoad: "listInteractionFocusOnLoad", listInteractionFocus: "listInteractionFocus", listInteractionFocusLastFromParent: "listInteractionFocusLastFromParent", listInteractionAutofocusFromParent: "listInteractionAutofocusFromParent", listInteractionIsSubList: "listInteractionIsSubList", listInteractionParentItem: "listInteractionParentItem", listInteractionPosition: "listInteractionPosition", listInteractionOrientation: "listInteractionOrientation", listInteractionRefocusAfterEnter: "listInteractionRefocusAfterEnter", listInteractionShowSubListOnParentFocus: "listInteractionShowSubListOnParentFocus", listInteractionCloseOnClickOut: "listInteractionCloseOnClickOut" }, host: { listeners: { "keydown": "handleKeydown($event)", "focusin": "focusInEvent($event.target)", "focusout": "focusOutEvent()" }, properties: { "attr.tabindex": "this.tabindex" } }, exportAs: ["si-list-interaction"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siListInteraction]',
                    exportAs: 'si-list-interaction'
                }]
        }], propDecorators: { listInteractionSubListLeft: [{
                type: Input
            }], listInteractionSubListStart: [{
                type: Input
            }], listInteractionFocusOnLoad: [{
                type: Input
            }], listInteractionFocus: [{
                type: Input
            }], listInteractionFocusLastFromParent: [{
                type: Input
            }], listInteractionAutofocusFromParent: [{
                type: Input
            }], listInteractionIsSubList: [{
                type: Input
            }], listInteractionParentItem: [{
                type: Input
            }], listInteractionPosition: [{
                type: Input
            }], listInteractionOrientation: [{
                type: Input
            }], listInteractionRefocusAfterEnter: [{
                type: Input
            }], listInteractionShowSubListOnParentFocus: [{
                type: Input
            }], listInteractionCloseOnClickOut: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], focusInEvent: [{
                type: HostListener,
                args: ['focusin', ['$event.target']]
            }], focusOutEvent: [{
                type: HostListener,
                args: ['focusout']
            }] } });

class SiMenuItemComponent {
    constructor() {
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this._elementRef = inject((ElementRef));
        this.listInteractionService = inject(SiListInteractionService);
        /**
         * Specifies the badge style.
         */
        this.badgeStyle = '';
        this._dropdownCaret = '';
        /**
         * If item has children, do not allow it to be clicked.
         */
        this.disableParentClick = false;
        /**
         * If item does not have a link, action or children, do not allow it to be clicked.
         */
        this.disableLinklessClick = false;
        /**
         * Whether to disable the list interactability, used when only using this
         * for the styling or `MenuItem` interface. Also enables this to be manually focused.
         */
        this.disableListInteraction = false;
        /**
         * Specifies whether the menu item focus styling should be inside instead of outside.
         */
        this.focusStylingInside = false;
        /**
         * Forces the active styling
         */
        this.forceActive = false;
        /**
         * Force-show title, when iconOnly is set
         */
        this.forceTitle = false;
        /**
         * Class to use on the link
         */
        this.itemClass = 'dropdown-item';
        this.activeChange = new EventEmitter();
        this.active = false;
    }
    /**
     * Specifies the dropdown caret direction.
     * @defaultref {@link _dropdownCaret}
     */
    set dropdownCaret(caret) {
        const deprecatedMessage = '@simpl/element-ng - SiMenuItemComponent: \
Setting dropdownCaret to "left" or "right" is deprecated, use "start" or "end" instead';
        if (caret === 'left') {
            console.warn(deprecatedMessage);
            this._dropdownCaret = 'start';
        }
        else if (caret === 'right') {
            console.warn(deprecatedMessage);
            this._dropdownCaret = 'end';
        }
        else {
            this._dropdownCaret = caret;
        }
    }
    get dropdownCaret() {
        return this._dropdownCaret;
    }
    get disabled() {
        return !(this._elementRef.nativeElement.offsetParent
            || (this.isBrowser && window.getComputedStyle(this._elementRef.nativeElement).position === 'fixed'
                && window.getComputedStyle(this._elementRef.nativeElement).display !== 'none'))
            || this.item.disabled || this.item.title === '-' || this.item.isHeading
            || ((!this.item.items || this.item.items.length === 0)
                && !this.item.action && !this.item.link && !this.item.href && this.disableLinklessClick);
    }
    get theBadgeStyle() {
        return this.item.badgeStyle ?? this.badgeStyle;
    }
    get iconOnly() {
        return !!this.item.iconOnly && !this.forceTitle;
    }
    // The badge character limit is restricted to three characters.
    // In case when the badge is of type `string` and the number of characters are more
    // than three, then only the first three characters will be displayed
    get badgeText() {
        const badge = this.item.badge;
        return typeof badge === 'string' && badge.length > 3 ? badge.slice(0, 3) :
            typeof badge === 'number' && badge > 99 ? '99+' : badge;
    }
    linkActiveChange(active) {
        this.active = active;
        this.activeChange.emit(active);
    }
    get parentList() {
        if (this.interactableListItem?.parent) {
            return this.interactableListItem.parent.directive;
        }
        return null;
    }
    get childList() {
        if (this.interactableListItem?.child
            && this.interactableListItem.child.trigger?.element === this.interactableListItem.element) {
            return this.interactableListItem.child.directive;
        }
        return null;
    }
    get iconClass() {
        let iconClass = this.item.icon;
        // Use -filled icon if item is active
        // Keep the default icon in case that icon does not have a filled version
        if (this.item.isActive || this.link?.active || this.getHostElement().classList.contains('show')) {
            iconClass = iconClass + ' ' + iconClass + '-filled';
        }
        return iconClass;
    }
    ngOnInit() {
        if (!this.disableListInteraction) {
            this.interactableListItem = this.listInteractionService.addInteractableListItem(this, this._elementRef.nativeElement);
        }
    }
    ngOnDestroy() {
        if (this.interactableListItem) {
            this.listInteractionService.removeInteractableListItem(this.interactableListItem);
        }
    }
    /** Focuses the menu item. */
    focus(origin, options) {
        if (this.interactableListItem?.parent) {
            this.interactableListItem.parent.directive.setActiveItem(this);
        }
        this.getItemElement().focus(options);
    }
    /** Returns the item DOM element. */
    getItemElement() {
        return this.menuItem.nativeElement;
    }
    /** Returns the parent DOM element. */
    getParentElement() {
        return this._elementRef.nativeElement.parentElement;
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
    hasChildElement(element) {
        return this.interactableListItem
            ? this.listInteractionService.isChildElementOfItem(this.interactableListItem, element)
            : this._elementRef.nativeElement.contains(element);
    }
    getItemRole(item) {
        if (item.selectionState === 'check' || item.type === 'check') {
            return 'menuitemcheckbox';
        }
        if (item.selectionState === 'radio' || item.type === 'radio') {
            return 'menuitemradio';
        }
        return 'menuitem';
    }
    getAriaChecked(item) {
        if (item.selectionState === 'check' || item.selectionState === 'radio') {
            return true;
        }
        else if (item.type === 'check' || item.type === 'radio') {
            return false;
        }
        return null;
    }
    handleSpace() {
        if (this.item.type) {
            this.menuItem.nativeElement.click();
        }
    }
}
SiMenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiMenuItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiMenuItemComponent, selector: "si-menu-item", inputs: { item: "item", badgeStyle: "badgeStyle", dropdownCaret: "dropdownCaret", actionParam: "actionParam", disableParentClick: "disableParentClick", disableLinklessClick: "disableLinklessClick", disableListInteraction: "disableListInteraction", focusStylingInside: "focusStylingInside", forceActive: "forceActive", forceTitle: "forceTitle", itemClass: "itemClass", dropdownToggle: "dropdownToggle" }, outputs: { activeChange: "activeChange" }, viewQueries: [{ propertyName: "menuItem", first: true, predicate: ["menuItem"], descendants: true }, { propertyName: "link", first: true, predicate: SiLinkDirective, descendants: true }], ngImport: i0, template: "<a *ngIf=\"(!disableParentClick || !item.items?.length) && (!disableLinklessClick || item.action || item.link || item.href) && !item.isHeading\"\n  #menuItem\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-icon-only]=\"iconOnly || !item.title && item.icon && !item.selectionState && !dropdownCaret && (!item.items || item.items.length === 0)\"\n  [class.dropdown-item-no-link]=\"item.type\"\n  [siLink]=\"item\"\n  [actionParam]=\"actionParam\"\n  (activeChange)=\"linkActiveChange($event)\"\n  [attr.tabindex]=\"disableListInteraction ? '': '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n  (keydown.space)=\"handleSpace()\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n<a *ngIf=\"disableParentClick && item.items?.length || disableLinklessClick && !item.action && !item.link && !item.href || item.isHeading\"\n  #menuItem\n  [class.dropdown-header]=\"item.isHeading\"\n  [ngClass]=\"itemClass\"\n  class=\"dropdown-item-no-link\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-static]=\"(!item.items || item.items.length === 0) && (disableLinklessClick && !item.action && !item.link && !item.href || item.isHeading)\"\n  [class.dropdown-item-icon-only]=\"iconOnly || !item.title && item.icon && !item.selectionState && !dropdownCaret && !item.items?.length\"\n  [attr.tabindex]=\"disableListInteraction ? '': '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n\n<ng-template #itemTemplate>\n  <span *ngIf=\"item.icon\" class=\"item-icon icon\" [ngClass]=\"iconClass!\" [class.badge-dot]=\"item.badgeDot\"></span>\n  <span\n    *ngIf=\"(theBadgeStyle === 'dot' || !theBadgeStyle && (iconOnly || (!item.title && item.icon))) && item.badge && item.icon\"\n    class=\"badge-text\"\n  >{{ badgeText }}</span>\n  <span\n    *ngIf=\"!iconOnly && (item.title || !item.icon || item.selectionState || dropdownCaret || item.items?.length)\"\n    class=\"item-title text-truncate\"\n  >{{ item.title! | translate }}</span>\n  <span\n    *ngIf=\"(theBadgeStyle === 'inline' || !theBadgeStyle && !iconOnly && (item.title || !item.icon)) && item.badge\"\n    class=\"badge me-n2\"\n    [ngClass]=\"'bg-' + (item.badgeColor || 'default')\"\n  >{{ item.badge }}</span>\n  <span *ngIf=\"!iconOnly && item.selectionState && !dropdownCaret && !item.items?.length\"\n    class=\"icon menu-end-icon ms-2\"\n    [class.element-ok]=\"item.selectionState === 'check'\"\n    [class.element-record-filled]=\"item.selectionState === 'radio'\"\n  ></span>\n  <span *ngIf=\"!iconOnly && (item.items?.length || dropdownCaret) && dropdownCaret !== 'none'\"\n    class=\"icon menu-end-icon element-down-2 dropdown-caret-animation flip-rtl ms-2\"\n    [class.dropdown-caret-end]=\"dropdownCaret === 'end'\"\n    [class.dropdown-caret-start]=\"dropdownCaret === 'start'\"\n    [class.dropdown-caret-up]=\"dropdownCaret === 'up'\"\n  ></span>\n</ng-template>\n", styles: [".dropdown-caret-animation:before{display:inline-block;transform:rotate(var(--caret-rotate, 0deg));transition:transform calc(.4s * var(--element-animations-enabled, 1))}:host.show .dropdown-caret-animation:before{transform:rotate(calc(var(--caret-rotate, 0deg) + 180deg))}.dropdown-caret-end{--caret-rotate: -90deg}.dropdown-caret-start{--caret-rotate: 90deg}.dropdown-caret-up{--caret-rotate: 180deg}.dropdown-item:not(.dropdown-header):not(.dropdown-item-static):not(.disabled){cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiMenuItemComponent.prototype, "item", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-menu-item', template: "<a *ngIf=\"(!disableParentClick || !item.items?.length) && (!disableLinklessClick || item.action || item.link || item.href) && !item.isHeading\"\n  #menuItem\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-icon-only]=\"iconOnly || !item.title && item.icon && !item.selectionState && !dropdownCaret && (!item.items || item.items.length === 0)\"\n  [class.dropdown-item-no-link]=\"item.type\"\n  [siLink]=\"item\"\n  [actionParam]=\"actionParam\"\n  (activeChange)=\"linkActiveChange($event)\"\n  [attr.tabindex]=\"disableListInteraction ? '': '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n  (keydown.space)=\"handleSpace()\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n<a *ngIf=\"disableParentClick && item.items?.length || disableLinklessClick && !item.action && !item.link && !item.href || item.isHeading\"\n  #menuItem\n  [class.dropdown-header]=\"item.isHeading\"\n  [ngClass]=\"itemClass\"\n  class=\"dropdown-item-no-link\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-static]=\"(!item.items || item.items.length === 0) && (disableLinklessClick && !item.action && !item.link && !item.href || item.isHeading)\"\n  [class.dropdown-item-icon-only]=\"iconOnly || !item.title && item.icon && !item.selectionState && !dropdownCaret && !item.items?.length\"\n  [attr.tabindex]=\"disableListInteraction ? '': '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n\n<ng-template #itemTemplate>\n  <span *ngIf=\"item.icon\" class=\"item-icon icon\" [ngClass]=\"iconClass!\" [class.badge-dot]=\"item.badgeDot\"></span>\n  <span\n    *ngIf=\"(theBadgeStyle === 'dot' || !theBadgeStyle && (iconOnly || (!item.title && item.icon))) && item.badge && item.icon\"\n    class=\"badge-text\"\n  >{{ badgeText }}</span>\n  <span\n    *ngIf=\"!iconOnly && (item.title || !item.icon || item.selectionState || dropdownCaret || item.items?.length)\"\n    class=\"item-title text-truncate\"\n  >{{ item.title! | translate }}</span>\n  <span\n    *ngIf=\"(theBadgeStyle === 'inline' || !theBadgeStyle && !iconOnly && (item.title || !item.icon)) && item.badge\"\n    class=\"badge me-n2\"\n    [ngClass]=\"'bg-' + (item.badgeColor || 'default')\"\n  >{{ item.badge }}</span>\n  <span *ngIf=\"!iconOnly && item.selectionState && !dropdownCaret && !item.items?.length\"\n    class=\"icon menu-end-icon ms-2\"\n    [class.element-ok]=\"item.selectionState === 'check'\"\n    [class.element-record-filled]=\"item.selectionState === 'radio'\"\n  ></span>\n  <span *ngIf=\"!iconOnly && (item.items?.length || dropdownCaret) && dropdownCaret !== 'none'\"\n    class=\"icon menu-end-icon element-down-2 dropdown-caret-animation flip-rtl ms-2\"\n    [class.dropdown-caret-end]=\"dropdownCaret === 'end'\"\n    [class.dropdown-caret-start]=\"dropdownCaret === 'start'\"\n    [class.dropdown-caret-up]=\"dropdownCaret === 'up'\"\n  ></span>\n</ng-template>\n", styles: [".dropdown-caret-animation:before{display:inline-block;transform:rotate(var(--caret-rotate, 0deg));transition:transform calc(.4s * var(--element-animations-enabled, 1))}:host.show .dropdown-caret-animation:before{transform:rotate(calc(var(--caret-rotate, 0deg) + 180deg))}.dropdown-caret-end{--caret-rotate: -90deg}.dropdown-caret-start{--caret-rotate: 90deg}.dropdown-caret-up{--caret-rotate: 180deg}.dropdown-item:not(.dropdown-header):not(.dropdown-item-static):not(.disabled){cursor:pointer}\n"] }]
        }], propDecorators: { item: [{
                type: Input
            }], badgeStyle: [{
                type: Input
            }], dropdownCaret: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], disableParentClick: [{
                type: Input
            }], disableLinklessClick: [{
                type: Input
            }], disableListInteraction: [{
                type: Input
            }], focusStylingInside: [{
                type: Input
            }], forceActive: [{
                type: Input
            }], forceTitle: [{
                type: Input
            }], itemClass: [{
                type: Input
            }], dropdownToggle: [{
                type: Input
            }], activeChange: [{
                type: Output
            }], menuItem: [{
                type: ViewChild,
                args: ['menuItem']
            }], link: [{
                type: ViewChild,
                args: [SiLinkDirective]
            }] } });

class SiMenuComponent {
    constructor() {
        /**
         * Array of menu items to display.
         */
        this.items = [];
        /**
         * Specifies whether sub menus should be displayed on the start side.
         */
        this.dropstart = false;
        /**
         * If `true`, the sub-menus will automatically open on the other side, if there is not enough space on the preferred one.
         */
        this.responsiveDropstart = true;
        /**
         * If `true`, the sub-menus will automatically open upwards, if there is not enough space below.
         */
        this.responsivelyDropUpwards = true;
        /**
         * Specifies whether the component should automatically be focused as soon as it is loaded.
         */
        this.focusOnLoad = false;
        /**
         * Specifies if any and which item should be focused when the component is focused.
         */
        this.focus = '';
        this._focusLastFromParent = false;
        /**
         * Specifies whether the menu is a sub list and a parent item should automatically be searched or be defined.
         */
        this.isSubList = false;
        this._parentItem = null;
        /**
         * Automatically refocus after pressing enter on one of the items.
         * Use when losing focus causes issues with keyboard interaction.
         */
        this.refocusAfterEnter = true;
        this.orientation = 'vert';
        this._position = 'integrated';
        this.autofocusFromParent = false;
        /**
         * Specifies whether the menu should use relative positioning and be open by default.
         * Used when not combined with a dropdown.
         */
        this.relativePositioning = false;
        /**
         * Force-show title, when iconOnly is set
         */
        this.forceTitle = false;
        this.activeChange = new EventEmitter();
        this.hostDropdownMenu = true;
        this.keepOpen = false;
        this.tabindex = '';
        this.active = false;
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.connectionsCalculated = false;
        this.ignoreNextChange = false;
        this._ignoreKeydown = undefined;
        this.childrenHidden = false;
        this.listenersSet = false;
        this.entries = [];
        this._elementRef = inject((ElementRef));
        this._changeDetector = inject(ChangeDetectorRef);
        this.listInteractionService = inject(SiListInteractionService);
        this.zone = inject(NgZone);
        /**
         * Indicate whether the child menu open to left or right.
         */
        this.actualDropstart = false;
        this.trackByIndex = (index) => index;
        this.onParentScrollOrResize = () => {
            if (this.responsiveDropstart || this.responsivelyDropUpwards) {
                const visibleEntries = this.entries.filter(item => item.isVisible);
                if (visibleEntries.length) {
                    setTimeout(() => {
                        this.zone.run(() => {
                            if (this.responsiveDropstart) {
                                this.responsivelyChangeDirection(visibleEntries.map(item => item.element));
                            }
                            if (this.responsivelyDropUpwards) {
                                visibleEntries.forEach(entry => {
                                    this.responsivelyCheckIfUpwards(entry.element, entry.itemElement, entry.item);
                                });
                            }
                        });
                    });
                }
            }
        };
        this.onClickOut = (event) => this.clickOutHandler(event);
    }
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link dropstart}
     * @deprecated Use the new Input {@link dropstart}
     */
    set dropleft(value) {
        this.dropstart = value;
    }
    get dropleft() {
        return this.dropstart;
    }
    get start() {
        return this.actualDropstart;
    }
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link responsiveDropstart}
     * @deprecated Use the new Input {@link responsiveDropstart}
     */
    set responsiveDropleft(value) {
        this.responsiveDropstart = value;
    }
    get responsiveDropleft() {
        return this.responsiveDropstart;
    }
    /**
     * Specifies if the last item should be focused when navigating to this menu from a parent list (instead of the first).
     * @defaultref {@link _focusLastFromParent}
     */
    set focusLastFromParent(focusLastFromParent) {
        this._focusLastFromParent = focusLastFromParent;
        if (this.interactableList) {
            this.interactableList.focusLastFromParent.next(focusLastFromParent);
        }
    }
    get focusLastFromParent() {
        return this._focusLastFromParent;
    }
    /**
     * Specifies a specific item that is the parent of the menu if the automatic one is incorrect.
     * @defaultref {@link _parentItem}
     */
    set parentItem(item) {
        this._parentItem = item;
        if (this.interactableList) {
            this.interactableList.triggerDefined.next(item);
        }
    }
    get parentItem() {
        if (!this._parentItem && this.interactableList && this.interactableList.trigger) {
            return this.interactableList.trigger.directive;
        }
        return this._parentItem;
    }
    /**
     * Specifies the position of the list relative to the parent.
     * Only relevant if this is a sub list and the parent has a different orientation.
     * @defaultref {@link _position}
     */
    set position(position) {
        this._position = position;
        if (this.interactableList) {
            this.interactableList.position.next(position);
        }
    }
    get position() {
        return this._position;
    }
    linkActiveChange(active) {
        const newActive = active || this.interactableList.items.some(item => item.directive.active)
            || this.interactableList.nested.some(list => list.directive.active) || false;
        if (newActive !== this.active) {
            this.active = newActive;
            this.activeChange.emit(this.active);
        }
    }
    get parentList() {
        if (this.interactableList?.parent) {
            return this.interactableList.parent.directive;
        }
        return null;
    }
    get parentListAll() {
        if (this.interactableList && (this.interactableList.parent || this.interactableList.parentDisconnected)) {
            return this.interactableList.parent ? this.interactableList.parent.directive : this.interactableList.parentDisconnected.directive;
        }
        return null;
    }
    ngAfterViewInit() {
        this._keyManager = new FocusKeyManager(this.interactableList.allItemsQueryList).withWrap();
        this._changeSubscription = this._keyManager.change.subscribe(() => {
            if (!this.ignoreNextChange) {
                this.openAndCloseChildren();
                addEventListener('click', this.onClickOut);
            }
            this.ignoreNextChange = false;
        });
        this._tabOutSubscription = this._keyManager.tabOut.subscribe(() => {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        });
    }
    ngOnInit() {
        this.interactableList = this.listInteractionService.addInteractableList(this, this._elementRef.nativeElement, this._parentItem, this._position, 'vert', this.focusLastFromParent, this.isSubList, () => this.calculated());
        this.interactableList.orientation.next('vert');
        const newTabindex = !this.isSubList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
    }
    ngOnDestroy() {
        this._changeSubscription?.unsubscribe();
        this._tabOutSubscription?.unsubscribe();
        if (this.interactableList) {
            this.listInteractionService.removeInteractableList(this.interactableList);
        }
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
            this.zone.runOutsideAngular(() => {
                removeEventListener('scroll', this.onParentScrollOrResize, true);
                removeEventListener('resize', this.onParentScrollOrResize);
            });
        }
    }
    ngOnChanges(changes) {
        if (changes.items || changes.dropstart || changes.responsiveDropstart || changes.responsivelyDropUpwards) {
            this.actualDropstart = this.dropstart;
            this.observer?.disconnect();
            this.observer = undefined;
            this.childrenHidden = false;
            setTimeout(() => {
                this.entries = [];
                if ((!this.responsiveDropstart && !this.responsivelyDropUpwards)
                    || !this.hasAnyChildren() || !this.isBrowser) {
                    return;
                }
                const menu = this.getHostElement();
                if (!menu?.firstElementChild) {
                    return;
                }
                const children = Array.from(menu.firstElementChild.children);
                if (!children) {
                    return;
                }
                this.items
                    .forEach((item, index) => {
                    const found = (item.items?.length)
                        ? children[index]?.querySelector('SI-MENU')
                        : undefined;
                    if (found) {
                        const foundItem = children[index]?.querySelector('SI-MENU-ITEM');
                        if (foundItem) {
                            this.entries.push({ element: found, itemElement: foundItem, item, isVisible: false });
                        }
                    }
                });
                if (!this.entries.length) {
                    return;
                }
                this.childrenHidden = true;
                this.observer = new IntersectionObserver(visibilities => {
                    let didChanges = false;
                    visibilities.forEach(visibility => {
                        const entry = this.entries.find(item => item.element === visibility.target);
                        if (entry) {
                            if (visibility.isIntersecting) {
                                if (!entry.isVisible) {
                                    if (entry.itemElement !== this._keyManager.activeItem?.getHostElement()) {
                                        this.closePreviousChild();
                                    }
                                    entry.isVisible = true;
                                    didChanges = true;
                                    if (this.responsivelyDropUpwards) {
                                        this.responsivelyCheckIfUpwards(entry.element, entry.itemElement, entry.item);
                                    }
                                }
                            }
                            else if (entry.isVisible) {
                                entry.isVisible = false;
                                didChanges = true;
                            }
                        }
                    });
                    if (didChanges && (this.responsiveDropstart || this.responsivelyDropUpwards)) {
                        const visibleEntries = this.entries.filter(item => item.isVisible).map(item => item.element);
                        if (visibleEntries.length) {
                            if (this.responsiveDropstart) {
                                this.responsivelyChangeDirection(visibleEntries);
                            }
                            if (this.childrenHidden) {
                                this.childrenHidden = false;
                            }
                            if (!this.listenersSet && this.isBrowser) {
                                this.zone.runOutsideAngular(() => {
                                    // Add event listener to window as capturing to handle tested element scrolling
                                    addEventListener('scroll', this.onParentScrollOrResize, true);
                                    // here we _actually_ want window.resize, nothing else since this is about a fixed position
                                    addEventListener('resize', this.onParentScrollOrResize);
                                });
                                this.listenersSet = true;
                            }
                        }
                        else if (!this.childrenHidden) {
                            this.childrenHidden = true;
                        }
                    }
                });
                this.entries.forEach(entry => {
                    this.observer.observe(entry.element);
                });
            });
        }
    }
    calculated() {
        this.connectionsCalculated = true;
        const newTabindex = (!this.isSubList || !this.parentList) ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
        if (this.isBrowser && document.activeElement === this.getHostElement()) {
            if (this.focus) {
                this.focusFirstItem('program', this.focus === 'last');
            }
        }
        else if (this.focusOnLoad) {
            this._elementRef.nativeElement.focus();
        }
    }
    /** Ignore the next keydown in this and all parent menus. Called from sub menu. */
    ignoreKeydown(event) {
        if (this.parentListAll) {
            this.parentListAll.ignoreKeydown(event);
        }
        this._ignoreKeydown = event;
    }
    /** Handle a keyboard event from the menu, delegating to the appropriate action. */
    handleKeydown(event) {
        if (this._ignoreKeydown?.key === event.key && this._ignoreKeydown.target === event.target) {
            this._ignoreKeydown = undefined;
        }
        else {
            if (this.parentListAll) {
                // If this is a sub menu ignore the keydown in all parent menus (due to event bubbling).
                this.parentListAll.ignoreKeydown(event);
            }
            const key = event.key;
            const manager = this._keyManager;
            const rtlCorrectedKey = correctKeyRTL(key);
            switch (rtlCorrectedKey) {
                case 'Escape':
                    if (!event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                        event.preventDefault();
                        this.goDownLevel();
                    }
                    break;
                case 'ArrowLeft':
                    if (this.actualDropstart) {
                        this.goUpLevel(false, true);
                    }
                    else {
                        this.goDownLevel();
                    }
                    break;
                case 'ArrowRight':
                    if (this.actualDropstart) {
                        this.goDownLevel(false, true);
                    }
                    else {
                        this.goUpLevel();
                    }
                    break;
                case 'Enter':
                    if (this.refocusAfterEnter && this.isBrowser) {
                        setTimeout(() => {
                            if (!document.activeElement || document.activeElement === document.body) {
                                this.focusActiveItem();
                            }
                        });
                    }
                    break;
                default:
                    manager.onKeydown(event);
            }
        }
    }
    hasChildren(item) {
        return item.items?.length && item.items.some(child => child.title && child.title !== '-');
    }
    closePreviousChild() {
        if (this._previousChild) {
            this._previousChild.close();
            this._previousChild = undefined;
            this._changeDetector.detectChanges();
        }
    }
    openAndCloseChildren() {
        this.closePreviousChild();
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = active.childList;
            if (activeList && activeList.getHostElement() !== this.getHostElement()) {
                activeList.open();
                this._previousChild = activeList;
                this._changeDetector.detectChanges();
            }
        }
    }
    open() {
        this.keepOpen = true;
    }
    close() {
        this.keepOpen = false;
    }
    setActiveItem(activeItem, ignoreNextChange = false) {
        if (!this._keyManager.activeItem || activeItem.getHostElement() !== this._keyManager.activeItem.getHostElement()) {
            this.ignoreNextChange = ignoreNextChange;
            this._keyManager.setActiveItem(activeItem);
        }
    }
    focusActiveItem(origin = 'program') {
        this._keyManager.activeItem?.focus();
    }
    /**
     * Focus the first item in the menu.
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusFirstItem(origin = 'program', reverse = false, fromParent = false) {
        const itemIndexes = Array.from(this.interactableList.allItemsQueryList).map((item, index) => {
            if (item.parentList && !item.disabled && item.parentList.getHostElement() === this.getHostElement()) {
                return index;
            }
            else {
                return -1;
            }
        }).filter(index => index !== -1);
        if (itemIndexes.length > 0) {
            let actualReverse = reverse;
            if (fromParent && this.focusLastFromParent) {
                actualReverse = !actualReverse;
            }
            const actualIndex = actualReverse ? itemIndexes[itemIndexes.length - 1] : itemIndexes[0];
            const activeItem = this.interactableList.allItemsQueryList.get(actualIndex);
            if (this._keyManager.activeItemIndex === actualIndex) {
                this.focusActiveItem();
                this.openAndCloseChildren();
                addEventListener('click', this.onClickOut);
            }
            else {
                this._keyManager.setFocusOrigin(origin).setActiveItem(activeItem);
            }
            const activeItemHost = activeItem.getHostElement();
            if (this.interactableList.parent?.integratedNestedItems.find(item => item.element === activeItemHost)) {
                this.parentList.setActiveItem(activeItem);
            }
            else if (this.interactableList.integratedParentItem) {
                this.parentList.setActiveItem(this.interactableList.integratedParentItem.directive);
            }
        }
    }
    hasAnyChildren() {
        return this.items.some(item => item.items?.length);
    }
    levelOfChildren(counter = -1, items = this.entries.filter(entry => entry.isVisible).map(entry => entry.item)) {
        return Math.max(0, ...items.map(subitem => {
            if (!subitem.disabled && subitem.items?.length) {
                return this.levelOfChildren(counter + 1, subitem.items);
            }
            else {
                return counter + 1;
            }
        }));
    }
    responsivelyChangeDirection(elements) {
        const menu = this.getHostElement();
        const levelOfChildren = this.levelOfChildren();
        const { responsiveDirection } = responsivelyCheckDirection({
            isScrolling: false, currentDirection: this.dropstart ? 'start' : 'end', contentElements: elements,
            hostElement: menu, placement: '', placementReferenceElement: menu, align: 'center',
            responsiveDirectionToPlacement: false, closeOnPlacementReferenceScrollOut: false, closeOnContentScrollOut: false,
            minSpaceThresholdFactor: levelOfChildren > 1 ? levelOfChildren - 1 : undefined
        });
        const newDropstart = responsiveDirection === 'start';
        if (this.actualDropstart !== newDropstart) {
            this.actualDropstart = newDropstart;
        }
        this._changeDetector.markForCheck();
    }
    responsivelyCheckIfUpwards(element, itemElement, item) {
        const { responsiveDirection } = responsivelyCheckDirection({
            isScrolling: false, currentDirection: item.dropUpwards ? 'up' : 'down', contentElements: [element],
            hostElement: itemElement, placement: 'top', placementReferenceElement: itemElement, align: 'center',
            responsiveDirectionToPlacement: false, closeOnPlacementReferenceScrollOut: false,
            closeOnContentScrollOut: false, placementReverse: 'bottom', rtl: false
        });
        const newDropUpwards = responsiveDirection === 'up';
        if (item.dropUpwards !== newDropUpwards) {
            item.dropUpwards = newDropUpwards;
            const recursivelyApply = (subitem) => {
                if (subitem.items?.length) {
                    subitem.items?.forEach(subsubitem => {
                        subsubitem.dropUpwards = newDropUpwards;
                        if (!subsubitem.disabled) {
                            recursivelyApply(subsubitem);
                        }
                    });
                }
            };
            recursivelyApply(item);
        }
    }
    goDownLevel(reverse = false, start = false) {
        if (this.parentList) {
            this.closePreviousChild();
            const nestedOrParentItem = this.interactableList
                ? (this.interactableList.integratedParentItem ? this.interactableList.integratedParentItem
                    : this.interactableList.items.find(item => item.parentIntegrationIndex !== -1 || item.nestedIntegration))?.directive
                : undefined;
            let actualReverse = reverse;
            const parent = this.parentList;
            if (parent.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (start && !parent.start) {
                actualReverse = !actualReverse;
            }
            parent.focusActiveItem('program', actualReverse, nestedOrParentItem);
        }
        else {
            this.goUpLevel(true);
        }
    }
    goUpLevel(reverse = false, start = false) {
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = active.childList;
            if (activeList) {
                activeList.focusFirstItem('program', reverse, true);
            }
            else if (this.parentList) {
                this.goDownLevel(true, start);
            }
        }
    }
    clickOutHandler(event) {
        const target = event.target;
        if (this.interactableList
            && !this._keyManager?.activeItem?.hasChildElement(target)) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        }
    }
    focusInEvent(target) {
        if (this.connectionsCalculated) {
            if (target === this.getHostElement()) {
                if (this.focus) {
                    this.focusFirstItem('program', this.focus === 'last');
                }
            }
            else {
                if (this.interactableList) {
                    const foundItem = this.interactableList.items.find(item => item.element.contains(target));
                    if (foundItem) {
                        this.setActiveItem(foundItem.directive);
                    }
                }
                if (this.tabindex !== '-1') {
                    this.tabindex = '-1';
                }
            }
        }
    }
    focusOutEvent() {
        if (this.tabindex === '-1') {
            Promise.resolve().then(() => this.tabindex = (!this.isSubList || !this.parentList) ? '0' : '');
        }
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
}
SiMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiMenuComponent, selector: "si-menu", inputs: { items: "items", dropleft: "dropleft", dropstart: "dropstart", responsiveDropleft: "responsiveDropleft", responsiveDropstart: "responsiveDropstart", responsivelyDropUpwards: "responsivelyDropUpwards", actionParam: "actionParam", focusOnLoad: "focusOnLoad", focus: "focus", focusLastFromParent: "focusLastFromParent", isSubList: "isSubList", parentItem: "parentItem", refocusAfterEnter: "refocusAfterEnter", position: "position", autofocusFromParent: "autofocusFromParent", relativePositioning: "relativePositioning", forceTitle: "forceTitle" }, outputs: { activeChange: "activeChange" }, host: { listeners: { "keydown": "handleKeydown($event)", "focusin": "focusInEvent($event.target)", "focusout": "focusOutEvent()" }, properties: { "class.dropdown-menu-relative": "this.relativePositioning", "class.dropdown-menu": "this.hostDropdownMenu", "class.show": "this.keepOpen", "attr.tabindex": "this.tabindex" } }, usesOnChanges: true, ngImport: i0, template: "<ul class=\"dropdown-menu-child\" role=\"menu\">\n  <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n    <li\n      *ngIf=\"!hasChildren(item); else childrenTemplate\"\n      [attr.role]=\"item.title === '-' ? 'separator' : 'presentation'\"\n      [class.dropdown-divider]=\"item.title === '-'\"\n    >\n      <si-menu-item\n        *ngIf=\"item.title !== '-'\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"true\"\n        [disableLinklessClick]=\"true\"\n        [forceTitle]=\"forceTitle\"\n        badgeStyle=\"inline\"\n        (activeChange)=\"linkActiveChange($event)\"\n      ></si-menu-item>\n    </li>\n    <ng-template #childrenTemplate>\n      <li\n        role=\"presentation\"\n        [class.dropstart]=\"actualDropstart\"\n        [class.dropend]=\"!actualDropstart\"\n        [class.disabled]=\"item.disabled\"\n      >\n        <si-menu-item\n          #parentItem\n          [item]=\"item\"\n          [actionParam]=\"actionParam\"\n          [disableParentClick]=\"true\"\n          [disableLinklessClick]=\"true\"\n          badgeStyle=\"inline\"\n          dropdownCaret=\"end\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu-item>\n        <si-menu\n          [class.hidden]=\"childrenHidden\"\n          [items]=\"item.items!\"\n          [dropstart]=\"actualDropstart\"\n          [responsiveDropstart]=\"responsiveDropstart\"\n          [class.dropupwards]=\"item.dropUpwards\"\n          [focusLastFromParent]=\"!!item.dropUpwards\"\n          [responsivelyDropUpwards]=\"responsivelyDropUpwards\"\n          [actionParam]=\"actionParam\"\n          [isSubList]=\"true\"\n          [parentItem]=\"parentItem\"\n          [forceTitle]=\"forceTitle\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu>\n      </li>\n    </ng-template>\n  </ng-container>\n</ul>\n", styles: [":host{outline:none}:host.dropdown-menu-relative{display:inline-block;position:relative;inset-block-start:0}:host.dropupwards{inset-block:auto 0}:host.hidden{visibility:hidden}.dropdown-menu-child .dropdown-menu{margin-inline:0}.dropdown-menu-child>li:not(.disabled):active>.dropdown-menu,.dropdown-menu-child>li:not(.disabled):hover>.dropdown-menu{display:block}.dropdown-menu-child{list-style:none;padding:0;margin:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }] });
__decorate([
    Required()
], SiMenuComponent.prototype, "items", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-menu', template: "<ul class=\"dropdown-menu-child\" role=\"menu\">\n  <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n    <li\n      *ngIf=\"!hasChildren(item); else childrenTemplate\"\n      [attr.role]=\"item.title === '-' ? 'separator' : 'presentation'\"\n      [class.dropdown-divider]=\"item.title === '-'\"\n    >\n      <si-menu-item\n        *ngIf=\"item.title !== '-'\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"true\"\n        [disableLinklessClick]=\"true\"\n        [forceTitle]=\"forceTitle\"\n        badgeStyle=\"inline\"\n        (activeChange)=\"linkActiveChange($event)\"\n      ></si-menu-item>\n    </li>\n    <ng-template #childrenTemplate>\n      <li\n        role=\"presentation\"\n        [class.dropstart]=\"actualDropstart\"\n        [class.dropend]=\"!actualDropstart\"\n        [class.disabled]=\"item.disabled\"\n      >\n        <si-menu-item\n          #parentItem\n          [item]=\"item\"\n          [actionParam]=\"actionParam\"\n          [disableParentClick]=\"true\"\n          [disableLinklessClick]=\"true\"\n          badgeStyle=\"inline\"\n          dropdownCaret=\"end\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu-item>\n        <si-menu\n          [class.hidden]=\"childrenHidden\"\n          [items]=\"item.items!\"\n          [dropstart]=\"actualDropstart\"\n          [responsiveDropstart]=\"responsiveDropstart\"\n          [class.dropupwards]=\"item.dropUpwards\"\n          [focusLastFromParent]=\"!!item.dropUpwards\"\n          [responsivelyDropUpwards]=\"responsivelyDropUpwards\"\n          [actionParam]=\"actionParam\"\n          [isSubList]=\"true\"\n          [parentItem]=\"parentItem\"\n          [forceTitle]=\"forceTitle\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu>\n      </li>\n    </ng-template>\n  </ng-container>\n</ul>\n", styles: [":host{outline:none}:host.dropdown-menu-relative{display:inline-block;position:relative;inset-block-start:0}:host.dropupwards{inset-block:auto 0}:host.hidden{visibility:hidden}.dropdown-menu-child .dropdown-menu{margin-inline:0}.dropdown-menu-child>li:not(.disabled):active>.dropdown-menu,.dropdown-menu-child>li:not(.disabled):hover>.dropdown-menu{display:block}.dropdown-menu-child{list-style:none;padding:0;margin:0}\n"] }]
        }], propDecorators: { items: [{
                type: Input
            }], dropleft: [{
                type: Input
            }], dropstart: [{
                type: Input
            }], responsiveDropleft: [{
                type: Input
            }], responsiveDropstart: [{
                type: Input
            }], responsivelyDropUpwards: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], focusOnLoad: [{
                type: Input
            }], focus: [{
                type: Input
            }], focusLastFromParent: [{
                type: Input
            }], isSubList: [{
                type: Input
            }], parentItem: [{
                type: Input
            }], refocusAfterEnter: [{
                type: Input
            }], position: [{
                type: Input
            }], autofocusFromParent: [{
                type: Input
            }], relativePositioning: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.dropdown-menu-relative']
            }], forceTitle: [{
                type: Input
            }], activeChange: [{
                type: Output
            }], hostDropdownMenu: [{
                type: HostBinding,
                args: ['class.dropdown-menu']
            }], keepOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], focusInEvent: [{
                type: HostListener,
                args: ['focusin', ['$event.target']]
            }], focusOutEvent: [{
                type: HostListener,
                args: ['focusout']
            }] } });

class SiContentActionBarMenuComponent {
    constructor() {
        /**
         * Secondary toggle icon aria label, required for a11y
         */
        this.toggleItemLabel = 'toggle';
        this.secondaryToggleItem = { title: this.toggleItemLabel, icon: 'element-options-vertical', iconOnly: true };
        this.trackByIndex = (index) => index;
    }
    ngOnChanges(changes) {
        if (changes.toggleItemLabel) {
            this.secondaryToggleItem.title = this.toggleItemLabel;
        }
    }
}
SiContentActionBarMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiContentActionBarMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiContentActionBarMenuComponent, selector: "[si-content-action-bar-menu]", inputs: { primaryActions: "primaryActions", secondaryActions: "secondaryActions", actionParam: "actionParam", toggleItemLabel: "toggleItemLabel" }, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"primaryActions?.length\">\n  <ng-container *ngFor=\"let item of primaryActions | slice:0:4; trackBy: trackByIndex\">\n    <div *ngIf=\"!item.items\" class=\"main-menu\">\n      <si-menu-item [item]=\"item\" [actionParam]=\"actionParam\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n    <div\n      *ngIf=\"item.items\"\n      class=\"dropdown main-menu\"\n      [class.disabled]=\"item.disabled\"\n      [class.active]=\"item.isActive\"\n      siDropdown\n      dropdownAlign=\"end\"\n      dropdownContainer=\"body\"\n      [dropdownResponsiveDirection]=\"true\"\n      [dropdownFocusOnOpen]=\"false\"\n      #dropdownPrimary=\"si-dropdown\"\n    >\n      <si-menu-item\n        *ngIf=\"!item.disabled\"\n        siDropdownToggle\n        #dropdownToggle=\"si-dropdown-toggle\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"false\"\n        [dropdownToggle]=\"dropdownToggle\"\n        [focusStylingInside]=\"true\"\n      ></si-menu-item>\n      <si-menu-item\n        *ngIf=\"item.disabled\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"false\"\n        [focusStylingInside]=\"true\"\n      ></si-menu-item>\n      <si-menu\n        *ngIf=\"dropdownPrimary.isOpen\"\n        [items]=\"item.items\"\n        [dropstart]=\"true\"\n        [actionParam]=\"actionParam\"\n        position=\"adjacent\"\n        [isSubList]=\"true\"\n        [forceTitle]=\"true\"\n        siDropdownMenu\n      ></si-menu>\n    </div>\n  </ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"secondaryActions?.length\">\n  <div\n    class=\"dropdown main-menu menu-icon\"\n    siDropdown\n    dropdownAlign=\"end\"\n    dropdownContainer=\"body\"\n    [dropdownResponsiveDirection]=\"true\"\n    [dropdownFocusOnOpen]=\"false\"\n    #dropdownSecondary=\"si-dropdown\"\n  >\n    <si-menu-item\n      class=\"secondary-toggle\"\n      siDropdownToggle\n      #dropdownToggle=\"si-dropdown-toggle\"\n      [item]=\"secondaryToggleItem\"\n      [actionParam]=\"actionParam\"\n      [dropdownToggle]=\"dropdownToggle\"\n      [focusStylingInside]=\"true\"\n    ></si-menu-item>\n    <si-menu\n      *ngIf=\"dropdownSecondary.isOpen\"\n      [items]=\"secondaryActions!\"\n      [dropstart]=\"true\"\n      [actionParam]=\"actionParam\"\n      position=\"adjacent\"\n      [isSubList]=\"true\"\n      [forceTitle]=\"true\"\n      siDropdownMenu\n    ></si-menu>\n  </div>\n</ng-container>\n", styles: [".main-menu~.main-menu{margin-inline-start:1px}.main-menu si-menu-item::ng-deep .dropdown-item{padding-block:6px;padding-inline:8px;block-size:32px;line-height:1.143;border-radius:var(--cab-radius)}.main-menu si-menu-item::ng-deep .dropdown-item.dropdown-item-icon-only{padding-inline:8px}.main-menu si-menu-item::ng-deep .dropdown-item.dropdown-item-icon-only .item-icon{margin-inline:0}.main-menu si-menu-item::ng-deep .dropdown-item .item-icon{margin-inline-start:-4px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }, { kind: "pipe", type: i1.SlicePipe, name: "slice" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: '[si-content-action-bar-menu]', template: "<ng-container *ngIf=\"primaryActions?.length\">\n  <ng-container *ngFor=\"let item of primaryActions | slice:0:4; trackBy: trackByIndex\">\n    <div *ngIf=\"!item.items\" class=\"main-menu\">\n      <si-menu-item [item]=\"item\" [actionParam]=\"actionParam\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n    <div\n      *ngIf=\"item.items\"\n      class=\"dropdown main-menu\"\n      [class.disabled]=\"item.disabled\"\n      [class.active]=\"item.isActive\"\n      siDropdown\n      dropdownAlign=\"end\"\n      dropdownContainer=\"body\"\n      [dropdownResponsiveDirection]=\"true\"\n      [dropdownFocusOnOpen]=\"false\"\n      #dropdownPrimary=\"si-dropdown\"\n    >\n      <si-menu-item\n        *ngIf=\"!item.disabled\"\n        siDropdownToggle\n        #dropdownToggle=\"si-dropdown-toggle\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"false\"\n        [dropdownToggle]=\"dropdownToggle\"\n        [focusStylingInside]=\"true\"\n      ></si-menu-item>\n      <si-menu-item\n        *ngIf=\"item.disabled\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"false\"\n        [focusStylingInside]=\"true\"\n      ></si-menu-item>\n      <si-menu\n        *ngIf=\"dropdownPrimary.isOpen\"\n        [items]=\"item.items\"\n        [dropstart]=\"true\"\n        [actionParam]=\"actionParam\"\n        position=\"adjacent\"\n        [isSubList]=\"true\"\n        [forceTitle]=\"true\"\n        siDropdownMenu\n      ></si-menu>\n    </div>\n  </ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"secondaryActions?.length\">\n  <div\n    class=\"dropdown main-menu menu-icon\"\n    siDropdown\n    dropdownAlign=\"end\"\n    dropdownContainer=\"body\"\n    [dropdownResponsiveDirection]=\"true\"\n    [dropdownFocusOnOpen]=\"false\"\n    #dropdownSecondary=\"si-dropdown\"\n  >\n    <si-menu-item\n      class=\"secondary-toggle\"\n      siDropdownToggle\n      #dropdownToggle=\"si-dropdown-toggle\"\n      [item]=\"secondaryToggleItem\"\n      [actionParam]=\"actionParam\"\n      [dropdownToggle]=\"dropdownToggle\"\n      [focusStylingInside]=\"true\"\n    ></si-menu-item>\n    <si-menu\n      *ngIf=\"dropdownSecondary.isOpen\"\n      [items]=\"secondaryActions!\"\n      [dropstart]=\"true\"\n      [actionParam]=\"actionParam\"\n      position=\"adjacent\"\n      [isSubList]=\"true\"\n      [forceTitle]=\"true\"\n      siDropdownMenu\n    ></si-menu>\n  </div>\n</ng-container>\n", styles: [".main-menu~.main-menu{margin-inline-start:1px}.main-menu si-menu-item::ng-deep .dropdown-item{padding-block:6px;padding-inline:8px;block-size:32px;line-height:1.143;border-radius:var(--cab-radius)}.main-menu si-menu-item::ng-deep .dropdown-item.dropdown-item-icon-only{padding-inline:8px}.main-menu si-menu-item::ng-deep .dropdown-item.dropdown-item-icon-only .item-icon{margin-inline:0}.main-menu si-menu-item::ng-deep .dropdown-item .item-icon{margin-inline-start:-4px}\n"] }]
        }], propDecorators: { primaryActions: [{
                type: Input
            }], secondaryActions: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], toggleItemLabel: [{
                type: Input
            }] } });

const RESIZE_THROTTLE_TIMEOUT = 100;
const MIN_TEXT_WIDTH = 20;
const MAX_TEXT_WIDTH = 180;
class SiContentActionBarComponent {
    constructor() {
        /**
         * Selection of view type as 'collapsible', 'expanded' or 'mobile'.
         */
        this.viewType = 'expanded';
        /**
         * Specifies whether the component should automatically be focused as soon as it is loaded.
         */
        this.focusOnLoad = false;
        /**
         * Toggle icon aria-label, required for a11y
         */
        this.toggleItemLabel = 'toggle';
        /**
         * Option to remove all icons from dropdown menus of the content action bar.
         *
         * Some apps provide only few actions with icons, located in the set of primary actions.
         * The icons are visible in the `collapsible` and `expanded` view type. On reduced space,
         * primary actions are relocated in the same dropdown menu as the secondary actions. The
         * dropdown menu can look unbalanced, if a large number of secondary actions without
         * icons are presented with few actions with icons. This option balances the look and feel
         * by removing all icons from actions in the dropdown menu.
         */
        this.preventIconsInDropdownMenus = false;
        this.isMobile = false;
        this.isExpanded = false;
        this.isCollapsible = false;
        this.isExpandedCollapsible = false;
        this.toggleItem = {
            title: this.toggleItemLabel,
            icon: 'element-options-vertical',
            iconOnly: true,
            action: () => {
                if (this.isCollapsible) {
                    this.isExpandedCollapsible = !this.isExpandedCollapsible;
                    this.toggleItem.icon = this.isExpandedCollapsible ? 'element-cancel' : 'element-options-vertical';
                    this.changeDetectorRef.markForCheck();
                }
            }
        };
        /**
         * Holds all primary and secondary menu items that
         * are displayed in the mobile menu on small width.
         */
        this.mobileActions = [];
        this.resizeObserver = inject(ResizeObserverService);
        this.translate = inject(SiTranslateService);
        this.measureService = inject(TextMeasureService);
        this.changeDetectorRef = inject(ChangeDetectorRef);
    }
    ngOnInit() {
        if (this.viewType === 'mobile') {
            // if mobile mode is requested initially, directly apply for instant display
            this.actualViewType = 'mobile';
            this.isMobile = true;
        }
        else {
            this.setResponsiveSize();
            this.setupObservers();
        }
    }
    ngOnDestroy() {
        this.resizeSubs?.unsubscribe();
        this.grandParentSubs?.unsubscribe();
    }
    visibilityChange() {
        const grandParent = this.elementRef.nativeElement.parentElement?.parentElement;
        if (grandParent) {
            if (grandParent === this.grandParent) {
                // no change, already observing the right thing
                return;
            }
            // change in grand parent (also on init): forget old, observe new
            this.grandParentSubs?.unsubscribe();
            this.grandParentSubs = this.resizeObserver
                .observe(grandParent, RESIZE_THROTTLE_TIMEOUT, true, true)
                .subscribe(() => {
                this.setResponsiveSize();
                this.changeDetectorRef.markForCheck();
            });
        }
        else {
            // grand parent gone away, i.e. outer container is invisible (e.g. inactive tab)
            this.grandParentSubs?.unsubscribe();
            this.grandParentSubs = undefined;
        }
        this.grandParent = grandParent;
        this.changeDetectorRef.markForCheck();
    }
    ngOnChanges(changes) {
        if (changes.toggleItemLabel) {
            this.toggleItem.title = this.toggleItemLabel;
        }
        if (changes.viewType && !changes.viewType.firstChange) {
            if (!this.viewType) {
                this.viewType = 'expanded';
            }
            if (this.mobileDropdown) {
                this.mobileDropdown.close();
            }
            this.setResponsiveSize();
            this.setupObservers();
        }
        if (changes.primaryActions && !changes.primaryActions.firstChange) {
            this.setResponsiveSize();
        }
        if (changes.secondaryActions || changes.preventIconsInDropdownMenus) {
            this.updateSecondaryMenuItems();
        }
        if (this.primaryActions?.length && this.secondaryActions?.length) {
            this.mobileActions = this.primaryActions.slice();
            this.mobileActions.push({ title: '-' });
            this.mobileActions.push(...this.secondaryActions);
        }
        else if (this.primaryActions?.length) {
            this.mobileActions = this.primaryActions;
        }
        else if (this.secondaryActions?.length) {
            this.mobileActions = this.secondaryActions;
        }
        if (this.preventIconsInDropdownMenus && this.mobileActions.length > 0) {
            this.mobileActions = this.mobileActions.slice().map(item => ({ ...item, icon: undefined }));
        }
        this.changeDetectorRef.markForCheck();
    }
    mobileDropdownToggle(state) {
        this.toggleItem.icon = state ? 'element-cancel' : 'element-options-vertical';
    }
    updateSecondaryMenuItems() {
        if (this.secondaryActions) {
            this.internalSecondaryActions = this.preventIconsInDropdownMenus ?
                this.secondaryActions.slice().map(item => ({ ...item, icon: undefined })) : [...this.secondaryActions];
        }
        else {
            this.internalSecondaryActions = undefined;
        }
    }
    setupObservers() {
        this.resizeSubs?.unsubscribe();
        this.grandParentSubs?.unsubscribe();
        if (this.viewType && this.viewType !== 'mobile') {
            this.resizeSubs = this.resizeObserver
                .observe(this.elementRef.nativeElement, RESIZE_THROTTLE_TIMEOUT, true, true)
                .subscribe(() => this.visibilityChange());
        }
    }
    setResponsiveSize() {
        const grandParent = this.elementRef.nativeElement.parentElement?.parentElement;
        let grandParentPadding = 0;
        if (grandParent) {
            const style = getComputedStyle(grandParent);
            grandParentPadding = (parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)) || 0;
        }
        let parentWidth = Math.max((grandParent?.clientWidth ?? 0) - grandParentPadding, 0);
        const parentHeight = grandParent?.clientHeight ?? 0;
        let requiredWidth = 0;
        let minRequiredWidth = 0;
        if (parentWidth && this.primaryActions?.length && this.viewType !== 'mobile') {
            requiredWidth += this.calcAdditionalWidth();
            minRequiredWidth = requiredWidth;
            for (const item of this.primaryActions) {
                requiredWidth += this.calcItemWidth(item, MAX_TEXT_WIDTH);
                minRequiredWidth += this.calcItemWidth(item, MIN_TEXT_WIDTH);
            }
        }
        if (!parentWidth && !parentHeight || !this.primaryActions || !this.primaryActions.length) {
            // not visible or no primary items -> fall back to mobile
            parentWidth = 0;
        }
        if (parentWidth && requiredWidth > parentWidth && minRequiredWidth < parentWidth) {
            // only look at the real thing if there isn't enough space using max. values but stuff
            // _might_ fit anyway.
            this.calculateActualRequiredWidth(parentWidth, requiredWidth);
        }
        else {
            this.applyResponsiveSize(parentWidth, requiredWidth);
        }
        this.changeDetectorRef.markForCheck();
    }
    calcAdditionalWidth() {
        let width = -1; // last item
        if (this.internalSecondaryActions?.length) {
            width += 41;
        }
        if (this.viewType === 'collapsible') {
            width += 41;
        }
        return width;
    }
    calcItemWidth(item, textWidth) {
        let width = 1; // spacer between items
        if (item.iconOnly) {
            width += 40;
        }
        else {
            width += Math.min(textWidth, MAX_TEXT_WIDTH);
            if (item.icon) {
                width += 28; // icon, negative left margin, spacing to text
            }
            width += 16; // padding
        }
        if (item.items?.length) {
            width += 22; // the dropdown arrow, margin left, negative margin right
        }
        return width;
    }
    calculateActualRequiredWidth(parentWidth, maxReqWidth) {
        const keys = this.primaryActions?.map(item => item.title ?? '');
        if (!keys?.length) {
            this.applyResponsiveSize(parentWidth, maxReqWidth);
            return;
        }
        if (keys.length > 4) {
            keys.length = 4;
        }
        this.translate.translateAsync(keys).subscribe(translations => {
            const requiredWidth = keys.reduce((acc, item, index) => {
                const textWidth = item ? this.measureService.measureText(translations[item]) : 0;
                const itemWidth = this.calcItemWidth(this.primaryActions[index], textWidth);
                return acc + itemWidth;
            }, this.calcAdditionalWidth());
            this.applyResponsiveSize(parentWidth, requiredWidth);
        });
    }
    applyResponsiveSize(parentWidth, requiredWidth) {
        const enoughWidth = parentWidth > requiredWidth || !this.hasMobileMenu();
        this.isMobile = this.viewType === 'mobile' || !enoughWidth;
        this.isExpanded = !this.isMobile && this.viewType === 'expanded' && enoughWidth;
        this.isCollapsible = !this.isMobile && this.viewType === 'collapsible' && enoughWidth;
        if (!this.isCollapsible) {
            this.isExpandedCollapsible = false;
        }
        this.toggleItem.icon = this.isExpandedCollapsible ? 'element-cancel' : 'element-options-vertical';
        const newActualViewType = this.isMobile ? 'mobile' : this.isExpanded ? 'expanded' : 'collapsible';
        if (newActualViewType !== this.actualViewType) {
            this.actualViewType = newActualViewType;
            if (this.mobileDropdown) {
                this.mobileDropdown.close();
            }
        }
        this.changeDetectorRef.markForCheck();
    }
    hasMobileMenu() {
        if (!(this.mobileActions?.length !== 0)) {
            return false;
        }
        else if (this.mobileActions.length === 1) {
            // If we only have one item with icon only,
            // it is the menu toggle action toggleItem.
            return !this.mobileActions[0].iconOnly;
        }
        else {
            return true;
        }
    }
}
SiContentActionBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiContentActionBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiContentActionBarComponent, selector: "si-content-action-bar", inputs: { primaryActions: "primaryActions", secondaryActions: "secondaryActions", actionParam: "actionParam", viewType: "viewType", focusOnLoad: "focusOnLoad", toggleItemLabel: "toggleItemLabel", preventIconsInDropdownMenus: "preventIconsInDropdownMenus" }, viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["main"], descendants: true, static: true }, { propertyName: "mobileDropdown", first: true, predicate: ["mobileDropdown"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div #main\n  [class.mobile]=\"isMobile\"\n  [class.expanded]=\"isExpanded\"\n  [class.collapsible]=\"isCollapsible\"\n  siListInteraction\n  [listInteractionFocusOnLoad]=\"focusOnLoad\"\n  [listInteractionFocus]=\"isCollapsible ? 'last' : (isMobile ? 'first' : !internalSecondaryActions?.length && primaryActions?.length === 1 ? 'first' : '')\"\n  [listInteractionRefocusAfterEnter]=\"isCollapsible || isMobile\"\n>\n  <div *ngIf=\"isMobile\"\n    class=\"main-container\"\n    siDropdown\n    dropdownAlign=\"end\"\n    dropdownContainer=\"body\"\n    dropdownPlacementReference=\".menu-nav.toggle.mobile\"\n    [dropdownResponsiveDirection]=\"true\"\n    [dropdownFocusOnOpen]=\"false\"\n    (dropdownOnToggle)=\"mobileDropdownToggle($event)\"\n    #mobileDropdown=\"si-dropdown\"\n  >\n    <si-menu\n      *ngIf=\"mobileDropdown.isOpen\"\n      [items]=\"mobileActions\"\n      [dropstart]=\"true\"\n      [actionParam]=\"actionParam\"\n      [isSubList]=\"true\"\n      position=\"adjacent\"\n      [forceTitle]=\"true\"\n      siDropdownMenu\n    ></si-menu>\n    <div\n      *ngIf=\"primaryActions?.length || internalSecondaryActions?.length\"\n      siDropdownToggle\n      class=\"menu-nav toggle mobile menu-icon expand-toggle\"\n      role=\"menu\"\n    >\n      <si-menu-item [item]=\"toggleItem\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n  </div>\n  <div *ngIf=\"actualViewType && !isMobile\" class=\"main-container\">\n    <div class=\"menu-nav menu-links\"\n      [class.expanded]=\"isExpanded\"\n      [class.active]=\"isExpanded || isExpandedCollapsible\"\n      si-content-action-bar-menu\n      #contentActionBarMenu\n      [actionParam]=\"actionParam\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"internalSecondaryActions\"\n      [toggleItemLabel]=\"toggleItemLabel\"\n      role=\"menu\"\n    ></div>\n    <div\n      class=\"menu-nav toggle menu-icon expand-toggle\"\n      [class.active]=\"isExpanded || isExpandedCollapsible\"\n      role=\"menu\"\n    >\n      <si-menu-item [item]=\"toggleItem\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n  </div>\n</div>\n", styles: [":host{--cab-radius: calc(var(--element-input-radius) - var(--element-radius-1))}:host>div{block-size:32px}.main-container{display:flex;position:relative;flex-wrap:nowrap}.main-container>.menu-nav{block-size:32px}.expand-toggle si-menu-item{border-radius:var(--cab-radius)}.expand-toggle ::ng-deep .dropdown-item{padding-block:6px;padding-inline:8px;block-size:32px;border-radius:var(--cab-radius)}.expand-toggle.active:not(.mobile) si-menu-item{border-start-start-radius:0;border-end-start-radius:0}.menu-nav ::ng-deep si-menu-item .dropdown-item:hover:not(.dropdown-item-static),.menu-nav ::ng-deep si-menu-item.show .dropdown-item{background:var(--element-action-secondary-hover);color:var(--element-action-secondary-text-hover)}.menu-nav ::ng-deep .main-menu:first-child si-menu-item{border-start-start-radius:var(--cab-radius);border-end-start-radius:var(--cab-radius)}.menu-nav ::ng-deep si-menu-item{display:flex;background:var(--element-base-1)}.menu-nav ::ng-deep si-menu-item .dropdown-item{color:var(--element-action-secondary-text)}.menu-nav ::ng-deep si-menu-item .dropdown-item.active{background:var(--element-base-1-hover)}.menu-nav ::ng-deep si-menu-item .dropdown-item.disabled{opacity:var(--element-action-disabled-opacity)}.menu-nav ::ng-deep si-menu-item .item-title{max-inline-size:180px}.menu-nav ::ng-deep .dropdown.show>a{background:var(--element-ui-4)}.menu-nav.expanded ::ng-deep .main-menu:last-child si-menu-item{border-start-end-radius:var(--cab-radius);border-end-end-radius:var(--cab-radius)}.element-options-vertical,.element-cancel{font-size:1.5rem}.collapsible .menu-links{display:none}.collapsible .menu-links.active{display:flex}.expanded .menu-nav.toggle{display:none}.expanded .menu-links.active{display:flex}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }, { kind: "component", type: SiContentActionBarMenuComponent, selector: "[si-content-action-bar-menu]", inputs: ["primaryActions", "secondaryActions", "actionParam", "toggleItemLabel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-content-action-bar', template: "<div #main\n  [class.mobile]=\"isMobile\"\n  [class.expanded]=\"isExpanded\"\n  [class.collapsible]=\"isCollapsible\"\n  siListInteraction\n  [listInteractionFocusOnLoad]=\"focusOnLoad\"\n  [listInteractionFocus]=\"isCollapsible ? 'last' : (isMobile ? 'first' : !internalSecondaryActions?.length && primaryActions?.length === 1 ? 'first' : '')\"\n  [listInteractionRefocusAfterEnter]=\"isCollapsible || isMobile\"\n>\n  <div *ngIf=\"isMobile\"\n    class=\"main-container\"\n    siDropdown\n    dropdownAlign=\"end\"\n    dropdownContainer=\"body\"\n    dropdownPlacementReference=\".menu-nav.toggle.mobile\"\n    [dropdownResponsiveDirection]=\"true\"\n    [dropdownFocusOnOpen]=\"false\"\n    (dropdownOnToggle)=\"mobileDropdownToggle($event)\"\n    #mobileDropdown=\"si-dropdown\"\n  >\n    <si-menu\n      *ngIf=\"mobileDropdown.isOpen\"\n      [items]=\"mobileActions\"\n      [dropstart]=\"true\"\n      [actionParam]=\"actionParam\"\n      [isSubList]=\"true\"\n      position=\"adjacent\"\n      [forceTitle]=\"true\"\n      siDropdownMenu\n    ></si-menu>\n    <div\n      *ngIf=\"primaryActions?.length || internalSecondaryActions?.length\"\n      siDropdownToggle\n      class=\"menu-nav toggle mobile menu-icon expand-toggle\"\n      role=\"menu\"\n    >\n      <si-menu-item [item]=\"toggleItem\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n  </div>\n  <div *ngIf=\"actualViewType && !isMobile\" class=\"main-container\">\n    <div class=\"menu-nav menu-links\"\n      [class.expanded]=\"isExpanded\"\n      [class.active]=\"isExpanded || isExpandedCollapsible\"\n      si-content-action-bar-menu\n      #contentActionBarMenu\n      [actionParam]=\"actionParam\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"internalSecondaryActions\"\n      [toggleItemLabel]=\"toggleItemLabel\"\n      role=\"menu\"\n    ></div>\n    <div\n      class=\"menu-nav toggle menu-icon expand-toggle\"\n      [class.active]=\"isExpanded || isExpandedCollapsible\"\n      role=\"menu\"\n    >\n      <si-menu-item [item]=\"toggleItem\" [focusStylingInside]=\"true\"></si-menu-item>\n    </div>\n  </div>\n</div>\n", styles: [":host{--cab-radius: calc(var(--element-input-radius) - var(--element-radius-1))}:host>div{block-size:32px}.main-container{display:flex;position:relative;flex-wrap:nowrap}.main-container>.menu-nav{block-size:32px}.expand-toggle si-menu-item{border-radius:var(--cab-radius)}.expand-toggle ::ng-deep .dropdown-item{padding-block:6px;padding-inline:8px;block-size:32px;border-radius:var(--cab-radius)}.expand-toggle.active:not(.mobile) si-menu-item{border-start-start-radius:0;border-end-start-radius:0}.menu-nav ::ng-deep si-menu-item .dropdown-item:hover:not(.dropdown-item-static),.menu-nav ::ng-deep si-menu-item.show .dropdown-item{background:var(--element-action-secondary-hover);color:var(--element-action-secondary-text-hover)}.menu-nav ::ng-deep .main-menu:first-child si-menu-item{border-start-start-radius:var(--cab-radius);border-end-start-radius:var(--cab-radius)}.menu-nav ::ng-deep si-menu-item{display:flex;background:var(--element-base-1)}.menu-nav ::ng-deep si-menu-item .dropdown-item{color:var(--element-action-secondary-text)}.menu-nav ::ng-deep si-menu-item .dropdown-item.active{background:var(--element-base-1-hover)}.menu-nav ::ng-deep si-menu-item .dropdown-item.disabled{opacity:var(--element-action-disabled-opacity)}.menu-nav ::ng-deep si-menu-item .item-title{max-inline-size:180px}.menu-nav ::ng-deep .dropdown.show>a{background:var(--element-ui-4)}.menu-nav.expanded ::ng-deep .main-menu:last-child si-menu-item{border-start-end-radius:var(--cab-radius);border-end-end-radius:var(--cab-radius)}.element-options-vertical,.element-cancel{font-size:1.5rem}.collapsible .menu-links{display:none}.collapsible .menu-links.active{display:flex}.expanded .menu-nav.toggle{display:none}.expanded .menu-links.active{display:flex}\n"] }]
        }], propDecorators: { primaryActions: [{
                type: Input
            }], secondaryActions: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], viewType: [{
                type: Input
            }], focusOnLoad: [{
                type: Input
            }], toggleItemLabel: [{
                type: Input
            }], preventIconsInDropdownMenus: [{
                type: Input
            }], elementRef: [{
                type: ViewChild,
                args: ['main', { static: true }]
            }], mobileDropdown: [{
                type: ViewChild,
                args: ['mobileDropdown']
            }] } });

class SiListInteractionItemDirective {
    constructor() {
        /**
         * Whether the list item interaction is disabled.
         */
        this.listInteractionItemDisabled = false;
        this.tabindex = '-1';
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.element = inject(ElementRef);
        this.listInteractionService = inject(SiListInteractionService);
    }
    get disabled() {
        return !(this.element.nativeElement.offsetParent
            || (this.isBrowser && window.getComputedStyle(this.element.nativeElement).position === 'fixed'
                && window.getComputedStyle(this.element.nativeElement).display !== 'none'))
            || this.listInteractionItemDisabled;
    }
    get dropdownToggle() {
        return this.listInteractionItemDropdownToggle;
    }
    get parentList() {
        if (this.interactableListItem?.parent) {
            return this.interactableListItem.parent.directive;
        }
        return null;
    }
    get childList() {
        if (this.interactableListItem?.child
            && this.interactableListItem.child.trigger?.element === this.interactableListItem.element) {
            return this.interactableListItem.child.directive;
        }
        return null;
    }
    ngOnInit() {
        this.interactableListItem = this.listInteractionService.addInteractableListItem(this, this.element.nativeElement);
    }
    ngOnDestroy() {
        if (this.interactableListItem) {
            this.listInteractionService.removeInteractableListItem(this.interactableListItem);
        }
    }
    /** Focuses the list item. */
    focus(origin, options) {
        if (this.interactableListItem.parent) {
            this.interactableListItem.parent.directive.setActiveItem(this);
        }
        this.element.nativeElement.focus(options);
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this.element.nativeElement;
    }
    hasChildElement(element) {
        return this.interactableListItem
            ? this.listInteractionService.isChildElementOfItem(this.interactableListItem, element)
            : this.element.nativeElement.contains(element);
    }
    getItemElement() {
        return this.getHostElement();
    }
}
SiListInteractionItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiListInteractionItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: { listInteractionItemDisabled: "listInteractionItemDisabled", listInteractionItemDropdownToggle: "listInteractionItemDropdownToggle" }, host: { properties: { "attr.tabindex": "this.tabindex" } }, exportAs: ["si-list-interaction-item"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siListInteractionItem]',
                    exportAs: 'si-list-interaction-item'
                }]
        }], propDecorators: { listInteractionItemDisabled: [{
                type: Input
            }], listInteractionItemDropdownToggle: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }] } });

class SiListInteractionModule {
}
SiListInteractionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiListInteractionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionModule, declarations: [SiListInteractionDirective,
        SiListInteractionItemDirective], imports: [CommonModule], exports: [SiListInteractionDirective, SiListInteractionItemDirective] });
SiListInteractionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiListInteractionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SiListInteractionDirective,
                        SiListInteractionItemDirective
                    ],
                    exports: [SiListInteractionDirective, SiListInteractionItemDirective]
                }]
        }] });

class SiDropdownModule {
}
SiDropdownModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiDropdownModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, declarations: [SiDropdownContainerComponent,
        SiDropdownDirective,
        SiDropdownMenuDirective,
        SiDropdownToggleDirective], imports: [CommonModule], exports: [SiDropdownDirective, SiDropdownMenuDirective, SiDropdownToggleDirective] });
SiDropdownModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SiDropdownContainerComponent,
                        SiDropdownDirective,
                        SiDropdownMenuDirective,
                        SiDropdownToggleDirective
                    ],
                    exports: [SiDropdownDirective, SiDropdownMenuDirective, SiDropdownToggleDirective]
                }]
        }] });

class SiMenuModule {
}
SiMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiMenuModule, declarations: [SiMenuComponent, SiMenuItemComponent], imports: [CommonModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiMenuComponent, SiMenuItemComponent] });
SiMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuModule, imports: [CommonModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiMenuComponent, SiMenuItemComponent],
                    exports: [SiMenuComponent, SiMenuItemComponent]
                }]
        }] });

class SiContentActionBarModule {
}
SiContentActionBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiContentActionBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarModule, declarations: [SiContentActionBarComponent,
        SiContentActionBarMenuComponent], imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule], exports: [SiContentActionBarComponent] });
SiContentActionBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarModule, imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiContentActionBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiDropdownModule,
                        SiListInteractionModule,
                        SiMenuModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiContentActionBarComponent,
                        SiContentActionBarMenuComponent
                    ],
                    exports: [SiContentActionBarComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

const WEEK_ISO = [
    { id: 'monday', index: 1, offset: 0, isWeekend: false },
    { id: 'tuesday', index: 2, offset: 1, isWeekend: false },
    { id: 'wednesday', index: 3, offset: 2, isWeekend: false },
    { id: 'thursday', index: 4, offset: 3, isWeekend: false },
    { id: 'friday', index: 5, offset: 4, isWeekend: false },
    { id: 'saturday', index: 6, offset: 5, isWeekend: true },
    { id: 'sunday', index: 7, offset: 6, isWeekend: true }
];
const WEEK_START_OFFSET = {
    'monday': 0,
    'saturday': 5,
    'sunday': 6
};
const UNITS = {
    millisecondsPerDay: 86400000,
    daysPerWeek: 7,
    thursday: 4
};
const dayOfWeekMap = {};
const getDaysOfWeek = (weekStart) => {
    weekStart = weekStart ?? 'monday';
    let weekdays = dayOfWeekMap[weekStart];
    if (!weekdays) {
        dayOfWeekMap[weekStart] = weekdays = [];
        const offset = WEEK_START_OFFSET[weekStart];
        for (let i = 0; i < 7; i++) {
            weekdays.push({ ...WEEK_ISO[(i + offset) % 7], offset: i });
        }
    }
    return weekdays;
};
/**
 * Gets the first day in the specified month.
 * Expects the month as a value between 1 and 12.
 * The year is required to handle leap years.
 *
 * @returns The first day of the month as a Date.
 */
const getFirstDayInMonth = (year, month) => new Date(year, month - 1, 1);
/**
 * Gets the week number of the specified date.
 * Week number according to the ISO-8601 standard, weeks starting on Monday.
 * The first week of a year is the week that contains the first Thursday of the year (='First 4-day week').
 * The highest week number in a year is either 52 or 53.
 *
 * @param date The JavaScript date object.
 * @param weekStart Name of the first day of the week
 * @returns The number of the Week
 */
const getWeekOfYear = (date, weekStart) => {
    // Algorithm rewritten from C# example given at http://en.wikipedia.org/wiki/Talk:ISO_week_date
    const dayOfWeek = getWeekDayOffset(date, weekStart) + 1;
    const nearestThu = new Date(date);
    nearestThu.setDate(date.getDate() + (UNITS.thursday - dayOfWeek)); // get nearest Thursday (-3..+3 days)
    const year = nearestThu.getFullYear();
    const janfirst = getFirstDayInMonth(year, 1);
    const days = Math.floor((nearestThu - janfirst) / UNITS.millisecondsPerDay);
    const week = 1 + Math.floor(days / UNITS.daysPerWeek); // Count of Thursdays
    return week;
};
const getWeekDayOffset = (date, weekStart) => {
    const offset = WEEK_START_OFFSET[weekStart ?? 'monday'];
    return (date.getDay() + 6 - offset) % 7;
};
/** returns the date string in format YYYY-MM-DD for given date */
const getStringforDate = (date) => {
    let month = '' + (date.getMonth() + 1);
    let day = '' + date.getDate();
    const year = date.getFullYear();
    if (month.length < 2) {
        month = '0' + month;
    }
    if (day.length < 2) {
        day = '0' + day;
    }
    return `${year}-${month}-${day}`;
};
/**
 * Create a new date without time.
 * @param value date object
 * @param hours numeric value of the hours.
 * @param minutes numeric value of the minutes.
 * @param seconds numeric value of the seconds.
 * @param milliseconds numeric value of the milliseconds.
 * @returns
 */
const createDate = (value, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) => {
    const newDate = new Date(value.getFullYear(), value.getMonth(), value.getDate(), hours, minutes, seconds, milliseconds);
    // Seems redundant, but makes sure that increasing
    // the hours does no change the date when switching
    // from 11pm up.
    newDate.setFullYear(value.getFullYear());
    newDate.setMonth(value.getMonth());
    newDate.setDate(value.getDate());
    return newDate;
};
const NAMED_FORMATS = {};
/**
 * @param input String containing a date or dateTime value (Ex. "05/15/2020"
 * @param format Format of the input value (Ex. "M/d/YYYY")
 * @param locale Locale of the input value
 * @returns A JS Date in accordance of the three parameters
 */
const parseDate = (input, format, locale) => {
    if (!input) {
        return undefined;
    }
    const splitFormat = format.split(/[^MydhHmsSa]+/);
    const splitDate = input.toUpperCase().match(/\d+|\w+/g);
    if (!splitDate) {
        return undefined;
    }
    // in case of some locales where meridian is with special chars
    const dateAndMeridian = input.split(/ +/);
    if (splitDate?.length > 0 && dateAndMeridian.length > 0) {
        const parsedMeridian = dateAndMeridian[dateAndMeridian.length - 1].toUpperCase();
        if (splitDate[splitDate?.length - 1] !== parsedMeridian && getMeridian(parsedMeridian, locale)) {
            splitDate?.push(parsedMeridian);
        }
    }
    let year = 0;
    let month = 0;
    let day = 0;
    let hour = 0;
    let minute = 0;
    let second = 0;
    let milliseconds = 0;
    // check if date is invalid and return if it is invalid
    for (let i = 0; i < splitDate?.length; i++) {
        if (i === splitDate?.length - 1) {
            const parsedMeridian = splitDate[splitDate?.length - 1];
            // skip if its meridian
            if (getMeridian(parsedMeridian, locale)) {
                continue;
            }
        }
        const parsedDateVal = parseInt(splitDate[i], 10);
        if (isNaN(parsedDateVal)) {
            return undefined;
        }
    }
    for (let i = 0; i < splitFormat.length; i++) {
        const f = splitFormat[i];
        let parsedNumber = parseInt(splitDate[i], 10);
        if (f !== 'a' && isNaN(parsedNumber)) {
            // auto fill seconds and milliseconds if not passed by user
            if (f === 'ss' || f === 'SSS') {
                parsedNumber = 0;
            }
            else {
                return undefined;
            }
        }
        if (f === 'M' || f === 'MM') {
            month = parsedNumber;
        }
        else if (f === 'd' || f === 'dd') {
            day = parsedNumber;
        }
        else if (f === 'y' || f === 'yy' || f === 'yyyy') {
            // JS adds 1900 for numbers between 0 and 99. Adjust to be more user-friendly
            year = parsedNumber < 50
                ? parsedNumber + 2000
                : parsedNumber < 100
                    ? parsedNumber + 1900
                    : parsedNumber;
        }
        else if (f === 'h' || f === 'HH') {
            hour = parsedNumber;
        }
        else if (f === 'mm') {
            minute = parsedNumber;
        }
        else if (f === 'ss') {
            second = parsedNumber;
        }
        else if (f === 'SSS') {
            milliseconds = parsedNumber;
        }
        else if (f === 'a') {
            if (!isNaN(parseInt(splitDate[splitDate?.length - 1], 10))) {
                continue;
            }
            const parsedMeridian = splitDate[splitDate?.length - 1];
            const meridian = getMeridian(parsedMeridian, locale);
            if (hour === 12 && meridian === 'AM') {
                hour = 0;
            }
            else if (meridian === 'PM') {
                hour = (hour % 12) + 12;
            }
            else if (!meridian) {
                return;
            }
        }
    }
    if (month > 12 || day > 31 || day > new Date(year, month, 0).getDate()) {
        return undefined;
    }
    return new Date(year, month - 1, day, hour, minute, second, milliseconds);
};
const getMeridian = (parsedMeridian, locale) => {
    const meridian = getLocaleDayPeriods(locale, FormStyle.Format, TranslationWidth.Short);
    const usingAM = parsedMeridian === meridian[0].toUpperCase();
    if (usingAM) {
        return 'AM';
    }
    else {
        const usingPM = parsedMeridian === meridian[1].toUpperCase();
        return usingPM ? 'PM' : undefined;
    }
};
// Adapted from: https://github.com/angular/angular/blob/91954cf20e17a386d71cc8ea25d1d17b9ae1e31c/packages/common/src/i18n/format_date.ts
// unfortunately it is not exported there
const getNamedFormat = (locale, format) => {
    const localeId = getLocaleId(locale);
    NAMED_FORMATS[localeId] = NAMED_FORMATS[localeId] || {};
    if (NAMED_FORMATS[localeId][format]) {
        return NAMED_FORMATS[localeId][format];
    }
    let formatValue = '';
    switch (format) {
        case 'shortDate':
            formatValue = getLocaleDateFormat(locale, FormatWidth.Short);
            break;
        case 'shortTime':
            formatValue = getLocaleTimeFormat(locale, FormatWidth.Short);
            break;
        case 'mediumTime':
            formatValue = getLocaleTimeFormat(locale, FormatWidth.Medium);
            break;
        case 'longTime':
            formatValue = getLocaleTimeFormat(locale, FormatWidth.Long);
            break;
        case 'fullTime':
            formatValue = getLocaleTimeFormat(locale, FormatWidth.Full);
            break;
        case 'short':
        case 'medium':
            const shortTime = getNamedFormat(locale, format === 'short' ? 'shortTime' : 'mediumTime');
            const shortDate = getNamedFormat(locale, 'shortDate');
            formatValue = formatDateTime(getLocaleDateTimeFormat(locale, FormatWidth.Short), [shortTime, shortDate]);
            break;
    }
    if (formatValue) {
        NAMED_FORMATS[localeId][format] = formatValue;
    }
    return formatValue;
};
const formatDateTime = (str, optVals) => {
    if (optVals) {
        str = str.replace(/\{([^}]+)}/g, (match, key) => (optVals != null && key in optVals) ? optVals[key] : match);
    }
    return str;
};
const getDateWithoutTime = (date) => createDate(date, 0, 0, 0, 0);
/**
 * Get today
 * @returns date of today
 */
const today = () => new Date();
/**
 * Calculate a new date based on the offset while considering the min and max date.
 * @param current input date.
 * @param offsetDays numeric offset of days.
 * @returinputns new date if the range is valid or original date
 */
const addDaysInRange = (current, daysOffset, minDate, maxDate) => {
    const newDate = addDays(current, daysOffset);
    // Make sure the new date is within the specified limits
    // MinDate < new data < MaxDate
    if (isSameOrBetween(newDate, minDate, maxDate)) {
        return newDate;
    }
    return current;
};
/**
 * Get date delta based on the offset.
 * @param date source date object.
 * @param days numeric offset of days.
 * @returns new date.
 */
const addDays = (date, days) => {
    const d = createDate(date);
    d.setDate(d.getDate() + days);
    return d;
};
/**
 * Get the first date of the week based on the input date.
 * @param current a date object from where the last date of a week is derived.
 * @param weekStartDay optional when a week shall start.
 * @returns
 */
const getWeekStartDate = (current, weekStartDay = 'monday') => {
    const weekStartDate = createDate(current);
    const diff = current.getDate() - getWeekDayOffset(current, weekStartDay);
    weekStartDate.setDate(diff);
    return weekStartDate;
};
/**
 * Get the last date of the week based on the input date.
 * @param current a date object from where the last date of a week is derived.
 * @param weekStartDay optional when a week shall start.
 * @returns the last date within the week.
 */
const getWeekEndDate = (current, weekStartDay = 'monday') => {
    const weekStartDate = createDate(current);
    weekStartDate.setDate(current.getDate() + 6 - getWeekDayOffset(current, weekStartDay));
    return weekStartDate;
};
/**
 * Get the beginning of the month.
 * @param current a date object from where the first date in a month is derived.
 * @returns a new date object which starts a the first.
 */
const getFirstDateInMonth = (current) => new Date(current.getFullYear(), current.getMonth(), 1);
/**
 * Get the last date of the month.
 * @param current a date object from where we derive the last date in a month.
 * @returns a new date object which is the last day in the current month.
 */
const getLastDateInMonth = (current) => new Date(current.getFullYear(), current.getMonth() + 1, 0);
/**
 * Are the two dates identical without considering time.
 */
const isSameDate = (current, compareTo) => {
    if (!compareTo) {
        return false;
    }
    return current.getFullYear() === compareTo.getFullYear()
        && current.getMonth() === compareTo.getMonth()
        && current.getDate() === compareTo.getDate();
};
/**
 * Compares two dates.
 * @param first The first date to compare.
 * @param second The second date to compare.
 * @returns 0 if the dates are equal, a number less than 0 if the first date is earlier,
 * a number greater than 0 if the first date is later.
 */
const compareDate = (first, second) => (first.getFullYear() - second.getFullYear() ||
    first.getMonth() - second.getMonth() ||
    first.getDate() - second.getDate());
/**
 * Compare the current date is the same date or between start and end date.
 * @param d the date object.
 * @param minDate optional min date, if no value is provided we assume true for the min value.
 * @param maxDate optional max date, if no value is provided we assume true for the max value.
 * @returns true if the date is in the provided range.
 */
const isSameOrBetween = (current, from, to) => {
    // from <= d
    const isInMinRange = from ? compareDate(from, current) <= 0 : true;
    // d <= to
    const isInMaxRange = to ? compareDate(current, to) <= 0 : true;
    return isInMinRange && isInMaxRange;
};
/**
 * Compare the current date is between start and end date.
 * from < current < to
 */
const isBetween = (current, from, to) => {
    const isInMinRange = from ? compareDate(current, from) > 0 : true;
    const isInMaxRange = to ? compareDate(current, to) < 0 : true;
    return isInMinRange && isInMaxRange;
};
/**
 * Is first date after the second date (without considering the time).
 * current > compareTo
 */
const isAfter = (current, compareTo) => compareDate(current, compareTo) > 0;
/**
 * Is first date equal or before the second date (without considering the time).
 * current >= compareTo
 */
const isSameOrBefore = (current, compareTo) => compareDate(current, compareTo) <= 0;
/**
 * Are the two dates in different months.
 */
const isAnotherMonth = (current, compareTo) => current.getMonth() !== compareTo.getMonth();
/**
 * Are the two dates in different years.
 */
const isAnotherYear = (current, compareTo) => current.getFullYear() !== compareTo.getFullYear();
/**
 * Are the two dates either in different months or years.
 */
const isAnotherMonthOrYear = (current, compareTo) => isAnotherMonth(current, compareTo) || isAnotherYear(current, compareTo);

/**
 * Returns date / datetime format to be used for rendering a date object as text
 * to an Html input element, which has the `SiDatepickerDirective`.
 *
 * @see https://angular.io/api/common/DatePipe#pre-defined-format-options
 * @param locale The locale for which the format is returned.
 * @param config The config object of the datepicker.
 * @param timeWhenDisabled If `true`, a format with time (medium or short) is returned, even if the `disabledTime` config is `true`.
 * @returns Either
 *   - a custom format provided by the config,
 *   - the localized `medium` format when time and seconds included
 *   - the localized `short` format when time and no seconds included
 */
const getDatepickerFormat = (locale, config, timeWhenDisabled = false) => {
    // try format from consumer
    let dateFormat = config?.showTime && !config.disabledTime
        ? config.dateTimeFormat
        : config?.dateFormat;
    if (!dateFormat) {
        // no format from consumer - use default depending on configuration
        let named;
        if (config?.showTime && (!config.disabledTime || timeWhenDisabled)) {
            named = config.showSeconds ? 'medium' : 'short';
        }
        else {
            named = 'shortDate';
        }
        dateFormat = getNamedFormat(locale, named);
    }
    // patch 2-digit year to 4-digit year
    if (!dateFormat.includes('yyyy')) {
        dateFormat = dateFormat.replace('yy', 'yyyy');
    }
    return dateFormat;
};

class SiDateInputDirective {
    constructor() {
        /**
         * Output cancel event on clicking backdrop.
         */
        this.siDatepickerClose = new EventEmitter();
        this.siDatepickerDisabledTime = new EventEmitter();
        this.siDatepickerRangeChange = new EventEmitter();
        this.onTouched = () => { };
        this.onModelChange = () => { };
        this.subscriptions = [];
        this._disabled = false;
        this._readonly = false;
        this.cdRef = inject(ChangeDetectorRef);
        this.elementRef = inject(ElementRef);
        this.renderer = inject(Renderer2);
        this.locale = inject(LOCALE_ID);
    }
    updateInstance(input) { }
    get disabled() {
        return this._disabled;
    }
    /**
     * @defaultref {@link _disabled}
     */
    set disabled(value) {
        this.setDisabledState(value !== false);
    }
    get readOnlyState() {
        return this._readonly ? true : null;
    }
    get readonly() {
        return this._readonly;
    }
    /** @defaultref {@link _readonly} */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    ngOnDestroy() {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
    validate(control) {
        const date = this.elementRef.nativeElement.value;
        const invalidFormat = !!(!control?.value && date);
        let minDate = this.siDatepickerConfig?.minDate;
        let maxDate = this.siDatepickerConfig?.maxDate;
        let dateTime = control.value ?? 0;
        if (!this.siDatepickerConfig?.enableTimeValidation || coerceBooleanProperty(this.siDatepickerConfig?.disabledTime)) {
            if (minDate) {
                minDate = getDateWithoutTime(minDate);
            }
            if (maxDate) {
                maxDate = getDateWithoutTime(maxDate);
            }
            if (dateTime) {
                dateTime = getDateWithoutTime(dateTime);
            }
        }
        const invalidMinDate = !invalidFormat && minDate && dateTime < minDate;
        const invalidMaxDate = !invalidFormat && maxDate && dateTime > maxDate;
        const errorObj = invalidFormat || invalidMinDate || invalidMaxDate ? {
            ...invalidFormat && ({ invalidFormat }),
            ...invalidMinDate && ({ invalidMinDate }),
            ...invalidMaxDate && ({ invalidMaxDate })
        } : null;
        return errorObj;
    }
    /**
     * Set the function to be called when the control receives a change event.
     */
    registerOnChange(fn) {
        this.onModelChange = fn;
    }
    /**
     * Set the function to be called when the control receives a touch event.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
        this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
        this.cdRef.markForCheck();
    }
    writeValue(value) {
        // remove date when user input is empty
        let emptyString = false;
        // Flag to define invalid string
        let invalidDate = false;
        if (typeof value === 'string') {
            emptyString = value.trim().length === 0;
            value = parseDate(value, this.getFormat(), this.locale);
            invalidDate = !value;
        }
        this.date = value;
        let dtStr = '';
        if (this.date) {
            dtStr = formatDate(this.date, this.getFormat(), this.locale);
            this.updateInstance(this.date);
        }
        // We should not change the content of the input field when the user typed
        // a wrong input. Otherwise the typed content changes and the user cannot
        // correct the content.
        if (!invalidDate || emptyString) {
            this.renderer.setProperty(this.elementRef.nativeElement, 'value', dtStr);
        }
        this.cdRef.markForCheck();
    }
    onKeyup(event) {
        this.writeValue(event.target.value);
    }
    onChange(event) {
        this.writeValue(event.target.value);
        this.onModelChange(this.date);
        this.onTouched();
    }
    getFormat() {
        if (!this.dateFormat) {
            this.dateFormat = getDatepickerFormat(this.locale, this.siDatepickerConfig);
        }
        return this.dateFormat;
    }
}
SiDateInputDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDateInputDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiDateInputDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDateInputDirective, selector: "[siDateInput]", inputs: { siDatepickerConfig: "siDatepickerConfig", dateRange: "dateRange", rangeType: "rangeType", disabled: "disabled", readonly: "readonly" }, outputs: { siDatepickerClose: "siDatepickerClose", siDatepickerDisabledTime: "siDatepickerDisabledTime", siDatepickerRangeChange: "siDatepickerRangeChange" }, host: { listeners: { "keyups": "onKeyup($event)", "change": "onChange($event)" }, properties: { "class.readonly": "this._readonly", "attr.readonly": "this.readOnlyState" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiDateInputDirective,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: SiDateInputDirective,
            multi: true
        }], exportAs: ["siDateInput"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDateInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDateInput]',
                    exportAs: 'siDateInput',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiDateInputDirective,
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: SiDateInputDirective,
                            multi: true
                        }]
                }]
        }], propDecorators: { siDatepickerConfig: [{
                type: Input
            }], siDatepickerClose: [{
                type: Output
            }], siDatepickerDisabledTime: [{
                type: Output
            }], siDatepickerRangeChange: [{
                type: Output
            }], dateRange: [{
                type: Input
            }], rangeType: [{
                type: Input
            }], disabled: [{
                type: Input
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }], readOnlyState: [{
                type: HostBinding,
                args: ['attr.readonly']
            }], readonly: [{
                type: Input
            }], onKeyup: [{
                type: HostListener,
                args: ['keyups', ['$event']]
            }], onChange: [{
                type: HostListener,
                args: ['change', ['$event']]
            }] } });

const positionStart = {
    overlayX: 'end',
    overlayY: 'center',
    originX: 'start',
    originY: 'center'
};
const positionEnd = {
    overlayX: 'start',
    overlayY: 'center',
    originX: 'end',
    originY: 'center'
};
const positionTopCenter = {
    overlayX: 'center',
    overlayY: 'bottom',
    originX: 'center',
    originY: 'top'
};
const positionTopStart = {
    overlayX: 'start',
    overlayY: 'bottom',
    originX: 'center',
    originY: 'top'
};
const positionTopEnd = {
    overlayX: 'end',
    overlayY: 'bottom',
    originX: 'center',
    originY: 'top'
};
const positionBottomCenter = {
    overlayX: 'center',
    overlayY: 'top',
    originX: 'center',
    originY: 'bottom'
};
const positionBottomStart = {
    overlayX: 'start',
    overlayY: 'top',
    originX: 'center',
    originY: 'bottom'
};
const positionBottomEnd = {
    overlayX: 'end',
    overlayY: 'top',
    originX: 'center',
    originY: 'bottom'
};
const positions = {
    top: [
        positionTopCenter,
        positionTopStart,
        positionTopEnd,
        positionBottomCenter,
        positionBottomStart,
        positionBottomEnd,
        positionStart,
        positionEnd
    ],
    end: [
        positionEnd,
        positionTopCenter,
        positionTopStart,
        positionTopEnd,
        positionBottomCenter,
        positionBottomStart,
        positionBottomEnd,
        positionStart
    ],
    bottom: [
        positionBottomCenter,
        positionBottomStart,
        positionBottomEnd,
        positionTopCenter,
        positionTopStart,
        positionTopEnd,
        positionStart,
        positionEnd
    ],
    start: [
        positionStart,
        positionTopCenter,
        positionTopStart,
        positionTopEnd,
        positionBottomCenter,
        positionBottomStart,
        positionBottomEnd,
        positionEnd
    ],
    auto: [
        positionTopCenter,
        positionTopStart,
        positionTopEnd,
        positionBottomCenter,
        positionBottomStart,
        positionBottomEnd,
        positionStart,
        positionEnd
    ]
};

/* eslint-disable no-console, prefer-arrow/prefer-arrow-functions, @typescript-eslint/naming-convention */
function getOverlay(elementRef, overlay, hasBackdrop, placement, constrain = false, center = true) {
    const popoverPositions = getOverlayPositions(elementRef, placement, center);
    const config = new OverlayConfig();
    let positionStrategy = overlay
        .position()
        .flexibleConnectedTo(elementRef)
        .withPush(false)
        .withGrowAfterOpen(true)
        .withFlexibleDimensions(constrain)
        .withPositions(popoverPositions);
    if (constrain) {
        positionStrategy = positionStrategy.withViewportMargin(8);
    }
    config.positionStrategy = positionStrategy;
    config.scrollStrategy = overlay.scrollStrategies.reposition();
    config.direction = isRTL() ? 'rtl' : 'ltr';
    if (hasBackdrop) {
        config.hasBackdrop = true;
        config.backdropClass = 'cdk-overlay-transparent-backdrop';
    }
    else {
        config.hasBackdrop = false;
    }
    return overlay.create(config);
}
function getPositionStrategy(overlayref) {
    return overlayref.getConfig().positionStrategy;
}
function getOverlayPositions(elementRef, placement, center = true) {
    if (elementRef.nativeElement && center) {
        const halfWidth = Math.round(elementRef.nativeElement.offsetWidth / 2);
        positionTopStart.offsetX = -halfWidth;
        positionTopEnd.offsetX = halfWidth;
        positionBottomStart.offsetX = -halfWidth;
        positionBottomEnd.offsetX = halfWidth;
    }
    else {
        positionTopStart.offsetX = undefined;
        positionTopEnd.offsetX = undefined;
        positionBottomStart.offsetX = undefined;
        positionBottomEnd.offsetX = undefined;
    }
    return typeof placement === 'string' ? positions[placement] : placement;
}
function hasTrigger(trigger, triggers) {
    return (triggers?.split(/\s+/) ?? []).includes(trigger);
}

class SiTimepickerComponent {
    get showMeridian() {
        if (this._showMeridian === undefined) {
            return !this.is24HoursLocale;
        }
        else {
            return this._showMeridian;
        }
    }
    set showMeridian(showMeridian) {
        this._showMeridian = showMeridian;
    }
    get readonly() {
        return this._readonly;
    }
    /**
     * @defaultref {@link _readonly}
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    get periods() {
        return this.meridians?.length ? this.meridians : this.periodDefaults;
    }
    constructor() {
        this.invalidHours = false;
        this.invalidMinutes = false;
        this.invalidSeconds = false;
        this.invalidMilliseconds = false;
        /**
         * All input fields will be disabled if set to true.
         */
        this.disabled = false;
        this.hoursLabel = 'hh';
        this.minutesLabel = 'mm';
        this.secondsLabel = 'ss';
        this.millisecondsLabel = 'ms';
        this.hideLabels = false;
        this.hoursAriaLabel = 'hours';
        this.minutesAriaLabel = 'minutes';
        this.secondsAriaLabel = 'seconds';
        this.millisecondsAriaLabel = 'milliseconds';
        this.hoursPlaceholder = 'hh';
        this.minutesPlaceholder = 'mm';
        this.secondsPlaceholder = 'ss';
        this.millisecondsPlaceholder = 'ms';
        this.meridiansLabel = 'am/pm';
        this.meridiansAriaLabel = 'period';
        this.showMinutes = true;
        this.showSeconds = false;
        this.showMilliseconds = false;
        this._readonly = false;
        this.isValid = new EventEmitter();
        this.meridianChange = new EventEmitter();
        this.onChange = () => { };
        this.onTouched = () => { };
        // The following are the time values for the ui.
        this.hours = '';
        this.minutes = '';
        this.seconds = '';
        this.milliseconds = '';
        this.meridian = '';
        this.locale = inject(LOCALE_ID);
        this.trackByIndex = (index) => index;
        this.toHtmlInputElement = (target) => target;
        const timeFormat = getLocaleTimeFormat(this.locale, FormatWidth.Full);
        this.is24HoursLocale = !timeFormat.includes('a');
        this.periodDefaults = getLocaleDayPeriods(this.locale, FormStyle.Format, TranslationWidth.Short).slice();
    }
    /**
     * Invoked when setting a new
     */
    writeValue(obj) {
        if (this.isValidDate(obj)) {
            this.setTime(this.parseTime(obj));
        }
        else if (obj == null) {
            this.setTime();
        }
        if (obj) {
            this.isInputValid(this.hours, this.minutes, this.seconds, this.milliseconds, this.isPM());
        }
    }
    isPM() {
        return !!this.showMeridian && this.meridian === 'pm';
    }
    /**
     * Set the function to be called when the control receives a change event.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Set the function to be called when the control receives a touch event.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    upDown(event) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            const key = event.target.name;
            this.changeTimeComponent(key, event.key === 'ArrowUp');
        }
    }
    updateHours(value) {
        value = value.toString();
        if (this.hours !== value) {
            this.hours = value;
            const isValid = this.isHourInputValid(this.hours, this.isPM()) && this.isValidLimit();
            if (!isValid) {
                this.invalidHours = true;
                this.isValid.emit(false);
                this.onChange(null);
            }
            else {
                this.invalidHours = false;
                this.updateTime();
            }
        }
    }
    updateMinutes(value) {
        value = value.toString();
        if (this.minutes !== value) {
            this.minutes = value;
            const isValid = this.isMinuteInputValid(this.minutes) && this.isValidLimit();
            if (!isValid) {
                this.invalidMinutes = true;
                this.isValid.emit(false);
                this.onChange(null);
            }
            else {
                this.invalidMinutes = false;
                this.updateTime();
            }
        }
    }
    updateSeconds(value) {
        value = value.toString();
        if (this.seconds !== value) {
            this.seconds = value.toString();
            const isValid = this.isSecondInputValid(this.seconds) && this.isValidLimit();
            if (!isValid) {
                this.invalidSeconds = true;
                this.isValid.emit(false);
                this.onChange(null);
            }
            else {
                this.invalidSeconds = false;
                this.updateTime();
            }
        }
    }
    updateMilliseconds(value) {
        value = value.toString();
        if (this.milliseconds !== value) {
            this.milliseconds = value.toString();
            const isValid = this.isMillisecondInputValid(this.milliseconds) && this.isValidLimit();
            if (!isValid) {
                this.invalidMilliseconds = true;
                this.isValid.emit(false);
                this.onChange(null);
            }
            else {
                this.invalidMilliseconds = false;
                this.updateTime();
            }
        }
    }
    toggleMeridian() {
        if (!this.showMeridian) {
            return;
        }
        const time = this.changeTime(this.time, { hour: 12 });
        this.setTime(time);
    }
    /**
     * Takes the current UI values and updates the time object value
     * accordingly, if they UI input values are valid.
     */
    updateTime() {
        const minutes = this.showMinutes ? this.minutes : undefined;
        const seconds = this.showSeconds ? this.seconds : undefined;
        const milliseconds = this.showMilliseconds ? this.milliseconds : undefined;
        if (!this.isInputValid(this.hours, minutes, seconds, milliseconds, this.isPM())) {
            this.isValid.emit(false);
            this.onChange(null);
            return;
        }
        const time = this.createDateUpdate(this.time, {
            hour: this.hours,
            minute: this.minutes,
            seconds: this.seconds,
            milliseconds: this.milliseconds,
            isPM: this.isPM()
        });
        this.setTime(time);
    }
    /**
     * Sets a new time object as model value, updates the user interface
     * and invokes onChange to let timepicker clients know about the update.
     * @param time The new time to be set.
     */
    setTime(time) {
        if (this.time !== time) {
            this.time = time;
            this.updateUI(this.time);
            this.onChange(this.time);
        }
    }
    /**
     * Updates the user interface by filling the time components
     * into the time input fields. Sets empty values if the date
     * is undefined or invalid.
     *
     * @param value The date object or string from with the time components are taken.
     */
    updateUI(value) {
        if (!value || !this.isValidDate(value)) {
            this.hours = '';
            this.minutes = '';
            this.seconds = '';
            this.milliseconds = '';
            this.meridian = 'am';
            this.meridianChange.emit(this.meridian);
        }
        else {
            const time = this.parseTime(value);
            if (!time) {
                return;
            }
            let hours = time.getHours();
            if (this.showMeridian) {
                this.meridian = hours >= 12 ? 'pm' : 'am';
                this.meridianChange.emit(this.meridian);
                hours = hours % 12;
                if (hours === 0) {
                    hours = 12;
                }
            }
            this.hours = hours.toString().padStart(2, '0');
            this.minutes = time.getMinutes().toString().padStart(2, '0');
            this.seconds = time.getUTCSeconds().toString().padStart(2, '0');
            this.milliseconds = time.getUTCMilliseconds().toString().padStart(3, '0');
        }
    }
    isValidDate(value) {
        if (!value) {
            return false;
        }
        if (typeof value === 'string') {
            return this.isValidDate(new Date(value));
        }
        if (value instanceof Date && isNaN(value.getHours())) {
            return false;
        }
        return true;
    }
    parseTime(value) {
        if (typeof value === 'string') {
            return new Date(value);
        }
        return value;
    }
    parseHours(value, isPM = false) {
        const hour = this.toNumber(value);
        if (isNaN(hour) || hour < 0 || hour > (isPM ? 12 : 24)) {
            return NaN;
        }
        return hour;
    }
    parseMinutes(value) {
        const minute = this.toNumber(value);
        if (isNaN(minute) || minute < 0 || minute > 60) {
            return NaN;
        }
        return minute;
    }
    parseSeconds(value) {
        const seconds = this.toNumber(value);
        if (isNaN(seconds) || seconds < 0 || seconds > 60) {
            return NaN;
        }
        return seconds;
    }
    parseMilliseconds(value) {
        const milliseconds = this.toNumber(value);
        if (isNaN(milliseconds) || milliseconds < 0 || milliseconds > 1000) {
            return NaN;
        }
        return milliseconds;
    }
    createDateUpdate(date, time) {
        let hour = this.parseHours(time.hour);
        const minute = this.parseMinutes(time.minute);
        const seconds = this.parseSeconds(time.seconds) || 0;
        const milliseconds = this.parseMilliseconds(time.milliseconds) || 0;
        if (time.isPM && hour !== 12) {
            hour += 12;
        }
        if (!date) {
            if (!isNaN(hour) && !isNaN(minute)) {
                return createDate(new Date(), hour, minute, seconds, milliseconds);
            }
            else {
                return date;
            }
        }
        else if (isNaN(hour) || isNaN(minute)) {
            return date;
        }
        else {
            return createDate(date, hour, minute, seconds, milliseconds);
        }
    }
    toNumber(value) {
        if (typeof value === 'undefined') {
            return NaN;
        }
        else if (typeof value === 'number') {
            return value;
        }
        return parseInt(value, 10);
    }
    isInputValid(hours, minutes = '0', seconds = '0', milliseconds = '0', isPM) {
        if (!this.isValidLimit()) {
            this.invalidHours = true;
            this.invalidMinutes = true;
            this.invalidSeconds = true;
            this.invalidMilliseconds = true;
        }
        else {
            this.invalidHours = !this.isHourInputValid(hours, isPM);
            this.invalidMinutes = !this.isMinuteInputValid(minutes);
            this.invalidSeconds = !this.isSecondInputValid(seconds);
            this.invalidMilliseconds = !this.isMillisecondInputValid(milliseconds);
        }
        return !this.invalidHours
            && !this.invalidMinutes
            && !this.invalidSeconds
            && !this.invalidMilliseconds;
    }
    isHourInputValid(hours, isPM) {
        return !isNaN(this.parseHours(hours, isPM));
    }
    isMinuteInputValid(minutes) {
        return !isNaN(this.parseMinutes(minutes));
    }
    isSecondInputValid(seconds) {
        return !isNaN(this.parseSeconds(seconds));
    }
    isMillisecondInputValid(milliseconds) {
        return !isNaN(this.parseMilliseconds(milliseconds));
    }
    isValidLimit() {
        const refDate = new Date();
        const newDate = this.createDateUpdate(refDate, {
            hour: this.hours,
            minute: this.minutes,
            seconds: this.seconds,
            milliseconds: this.milliseconds,
            isPM: this.isPM()
        });
        if (!newDate) {
            return false;
        }
        let refMax;
        if (this.max) {
            refMax = new Date(refDate);
            refMax.setHours(this.max.getHours());
            refMax.setMinutes(this.max.getMinutes());
            refMax.setSeconds(this.max.getSeconds());
            refMax.setMilliseconds(this.max.getMilliseconds());
        }
        let refMin;
        if (this.min) {
            refMin = new Date(refDate);
            refMin.setHours(this.min.getHours());
            refMin.setMinutes(this.min.getMinutes());
            refMin.setSeconds(this.min.getSeconds());
            refMin.setMilliseconds(this.min.getMilliseconds());
        }
        if (refMax && newDate > refMax) {
            return false;
        }
        else if (refMin && newDate < refMin) {
            return false;
        }
        return true;
    }
    changeTimeComponent(key, up) {
        const change = up ? 1 : -1;
        const date = this.createDateUpdate(new Date(), {
            hour: this.hours,
            minute: this.minutes,
            seconds: this.seconds,
            milliseconds: this.milliseconds,
            isPM: this.isPM()
        });
        switch (key) {
            case 'hours':
                {
                    const newTime = this.changeTime(date, { hour: change });
                    let hour = newTime.getHours();
                    if (this.showMeridian) {
                        hour = hour % 12;
                        if (hour === 0 && !this.isPM()) {
                            hour = 12;
                        }
                        else if (hour === 0 && this.isPM()) {
                            this.toggleMeridian();
                        }
                    }
                    this.updateHours(hour);
                    break;
                }
            case 'minutes':
                {
                    const newTime = this.changeTime(date, { minute: change });
                    this.updateMinutes(newTime.getMinutes());
                    break;
                }
            case 'seconds':
                {
                    const newTime = this.changeTime(date, { seconds: change });
                    this.updateSeconds(newTime.getSeconds());
                    break;
                }
            case 'milliseconds':
                {
                    const newTime = this.changeTime(date, { milliseconds: change });
                    this.updateMilliseconds(newTime.getMilliseconds());
                    break;
                }
            default:
                break;
        }
    }
    changeTime(value, diff) {
        if (!value) {
            return this.changeTime(createDate(new Date(), 0, 0, 0, 0), diff);
        }
        if (!diff) {
            return value;
        }
        let hour = value.getHours();
        let minutes = value.getMinutes();
        let seconds = value.getSeconds();
        let milliseconds = value.getMilliseconds();
        if (diff.hour) {
            hour = hour + this.toNumber(diff.hour);
        }
        if (diff.minute) {
            minutes = minutes + this.toNumber(diff.minute);
        }
        if (diff.seconds) {
            seconds = seconds + this.toNumber(diff.seconds);
        }
        if (diff.milliseconds) {
            milliseconds = milliseconds + this.toNumber(diff.milliseconds);
        }
        return createDate(value, hour, minutes, seconds, milliseconds);
    }
}
SiTimepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTimepickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTimepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTimepickerComponent, selector: "si-timepicker", inputs: { disabled: "disabled", hoursLabel: "hoursLabel", minutesLabel: "minutesLabel", secondsLabel: "secondsLabel", millisecondsLabel: "millisecondsLabel", hideLabels: "hideLabels", hoursAriaLabel: "hoursAriaLabel", minutesAriaLabel: "minutesAriaLabel", secondsAriaLabel: "secondsAriaLabel", millisecondsAriaLabel: "millisecondsAriaLabel", hoursPlaceholder: "hoursPlaceholder", minutesPlaceholder: "minutesPlaceholder", secondsPlaceholder: "secondsPlaceholder", millisecondsPlaceholder: "millisecondsPlaceholder", meridians: "meridians", meridiansLabel: "meridiansLabel", meridiansAriaLabel: "meridiansAriaLabel", showMinutes: "showMinutes", showSeconds: "showSeconds", showMilliseconds: "showMilliseconds", showMeridian: "showMeridian", min: "min", max: "max", readonly: "readonly" }, outputs: { isValid: "isValid", meridianChange: "meridianChange" }, host: { properties: { "class.readonly": "this._readonly" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiTimepickerComponent,
            multi: true
        }], ngImport: i0, template: "<div class=\"d-flex flex-row flex-wrap\">\n  <label class=\"min-width\">\n    <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (hoursLabel | translate) || '&nbsp;' }}</span>\n    <input\n      type=\"text\"\n      inputmode=\"numeric\"\n      pattern=\"2[0-4]|[01]?[0-9]\"\n      class=\"form-control\"\n      name=\"hours\"\n      maxlength=\"2\"\n      autocomplete=\"off\"\n      [attr.aria-label]=\"hoursAriaLabel | translate\"\n      [class.is-invalid]=\"invalidHours\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      [placeholder]=\"hoursPlaceholder\"\n      [value]=\"hours\"\n      (change)=\"updateHours(toHtmlInputElement($event.target).value)\"\n      (blur)=\"updateHours(toHtmlInputElement($event.target).value)\"\n      (keydown)=\"upDown($event)\"\n    >\n  </label>\n\n  <ng-container *ngIf=\"showMinutes\">\n    <ng-container *ngTemplateOutlet=\"separator\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (minutesLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-5]?[0-9]\"\n        class=\"form-control\"\n        name=\"minutes\"\n        maxlength=\"2\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"minutesAriaLabel | translate\"\n        [class.is-invalid]=\"invalidMinutes\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"minutesPlaceholder\"\n        [value]=\"minutes\"\n        (change)=\"updateMinutes(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateMinutes(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <ng-container *ngIf=\"showSeconds\">\n    <ng-container *ngTemplateOutlet=\"separator\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (secondsLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-5]?[0-9]\"\n        class=\"form-control\"\n        name=\"seconds\"\n        maxlength=\"2\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"secondsAriaLabel | translate\"\n        [class.is-invalid]=\"invalidSeconds\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"secondsPlaceholder\"\n        [value]=\"seconds\"\n        (change)=\"updateSeconds(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateSeconds(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <ng-container *ngIf=\"showMilliseconds\">\n    <ng-container *ngTemplateOutlet=\"separator; context: {separator: '.'}\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (millisecondsLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-9]{1,3}\"\n        class=\"form-control\"\n        name=\"milliseconds\"\n        maxlength=\"3\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"millisecondsAriaLabel | translate\"\n        [class.is-invalid]=\"invalidMilliseconds\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"millisecondsPlaceholder\"\n        [value]=\"milliseconds\"\n        (change)=\"updateMilliseconds(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateMilliseconds(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <label *ngIf=\"showMeridian\" class=\"ms-2\">\n    <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (meridiansLabel | translate) || '&nbsp;' }}</span>\n    <select class=\"form-control\" (change)=\"toggleMeridian()\" [attr.aria-label]=\"meridiansAriaLabel | translate\" [disabled]=\"disabled || readonly\">\n      <option [selected]=\"meridian === 'am'\" value=\"am\">{{ periods[0] }}</option>\n      <option [selected]=\"meridian === 'pm'\" value=\"pm\">{{ periods[1] }}</option>\n    </select>\n  </label>\n</div>\n\n<ng-template #separator let-separator=\"separator\">\n  <div class=\"align-self-end pb-3 px-1\" aria-hidden=\"true\">{{ separator ? separator : ':' }}</div>\n</ng-template>\n", styles: [":host{display:block}.min-width{inline-size:100%;max-inline-size:45px;min-inline-size:35px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTimepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-timepicker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiTimepickerComponent,
                            multi: true
                        }], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"d-flex flex-row flex-wrap\">\n  <label class=\"min-width\">\n    <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (hoursLabel | translate) || '&nbsp;' }}</span>\n    <input\n      type=\"text\"\n      inputmode=\"numeric\"\n      pattern=\"2[0-4]|[01]?[0-9]\"\n      class=\"form-control\"\n      name=\"hours\"\n      maxlength=\"2\"\n      autocomplete=\"off\"\n      [attr.aria-label]=\"hoursAriaLabel | translate\"\n      [class.is-invalid]=\"invalidHours\"\n      [disabled]=\"disabled\"\n      [readonly]=\"readonly\"\n      [placeholder]=\"hoursPlaceholder\"\n      [value]=\"hours\"\n      (change)=\"updateHours(toHtmlInputElement($event.target).value)\"\n      (blur)=\"updateHours(toHtmlInputElement($event.target).value)\"\n      (keydown)=\"upDown($event)\"\n    >\n  </label>\n\n  <ng-container *ngIf=\"showMinutes\">\n    <ng-container *ngTemplateOutlet=\"separator\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (minutesLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-5]?[0-9]\"\n        class=\"form-control\"\n        name=\"minutes\"\n        maxlength=\"2\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"minutesAriaLabel | translate\"\n        [class.is-invalid]=\"invalidMinutes\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"minutesPlaceholder\"\n        [value]=\"minutes\"\n        (change)=\"updateMinutes(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateMinutes(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <ng-container *ngIf=\"showSeconds\">\n    <ng-container *ngTemplateOutlet=\"separator\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (secondsLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-5]?[0-9]\"\n        class=\"form-control\"\n        name=\"seconds\"\n        maxlength=\"2\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"secondsAriaLabel | translate\"\n        [class.is-invalid]=\"invalidSeconds\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"secondsPlaceholder\"\n        [value]=\"seconds\"\n        (change)=\"updateSeconds(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateSeconds(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <ng-container *ngIf=\"showMilliseconds\">\n    <ng-container *ngTemplateOutlet=\"separator; context: {separator: '.'}\"></ng-container>\n    <label class=\"min-width\">\n      <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (millisecondsLabel | translate) || '&nbsp;' }}</span>\n      <input\n        type=\"text\"\n        inputmode=\"numeric\"\n        pattern=\"[0-9]{1,3}\"\n        class=\"form-control\"\n        name=\"milliseconds\"\n        maxlength=\"3\"\n        autocomplete=\"off\"\n        [attr.aria-label]=\"millisecondsAriaLabel | translate\"\n        [class.is-invalid]=\"invalidMilliseconds\"\n        [disabled]=\"disabled\"\n        [readonly]=\"readonly\"\n        [placeholder]=\"millisecondsPlaceholder\"\n        [value]=\"milliseconds\"\n        (change)=\"updateMilliseconds(toHtmlInputElement($event.target).value)\"\n        (blur)=\"updateMilliseconds(toHtmlInputElement($event.target).value)\"\n        (keydown)=\"upDown($event)\"\n      >\n    </label>\n  </ng-container>\n\n  <label *ngIf=\"showMeridian\" class=\"ms-2\">\n    <span *ngIf=\"!hideLabels\" class=\"form-label\">{{ (meridiansLabel | translate) || '&nbsp;' }}</span>\n    <select class=\"form-control\" (change)=\"toggleMeridian()\" [attr.aria-label]=\"meridiansAriaLabel | translate\" [disabled]=\"disabled || readonly\">\n      <option [selected]=\"meridian === 'am'\" value=\"am\">{{ periods[0] }}</option>\n      <option [selected]=\"meridian === 'pm'\" value=\"pm\">{{ periods[1] }}</option>\n    </select>\n  </label>\n</div>\n\n<ng-template #separator let-separator=\"separator\">\n  <div class=\"align-self-end pb-3 px-1\" aria-hidden=\"true\">{{ separator ? separator : ':' }}</div>\n</ng-template>\n", styles: [":host{display:block}.min-width{inline-size:100%;max-inline-size:45px;min-inline-size:35px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { disabled: [{
                type: Input
            }], hoursLabel: [{
                type: Input
            }], minutesLabel: [{
                type: Input
            }], secondsLabel: [{
                type: Input
            }], millisecondsLabel: [{
                type: Input
            }], hideLabels: [{
                type: Input
            }], hoursAriaLabel: [{
                type: Input
            }], minutesAriaLabel: [{
                type: Input
            }], secondsAriaLabel: [{
                type: Input
            }], millisecondsAriaLabel: [{
                type: Input
            }], hoursPlaceholder: [{
                type: Input
            }], minutesPlaceholder: [{
                type: Input
            }], secondsPlaceholder: [{
                type: Input
            }], millisecondsPlaceholder: [{
                type: Input
            }], meridians: [{
                type: Input
            }], meridiansLabel: [{
                type: Input
            }], meridiansAriaLabel: [{
                type: Input
            }], showMinutes: [{
                type: Input
            }], showSeconds: [{
                type: Input
            }], showMilliseconds: [{
                type: Input
            }], showMeridian: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }], readonly: [{
                type: Input
            }], isValid: [{
                type: Output
            }], meridianChange: [{
                type: Output
            }] } });

const NUM_YEARS = 12;
const START_YEAR = 1900;
const END_YEAR = 2154;
let idCounter$2 = 1;
class SiDatepickerComponent {
    get rangeType() {
        return this._rangeType;
    }
    set rangeType(rangeType) {
        this._rangeType = rangeType;
        if (this.config.enableDateRange) {
            setTimeout(() => {
                if (this.dateRange?.start && !this.dateRange?.end || this.rangeType === 'START') {
                    this.calcDays(this.dateRange?.start);
                }
                else if (this.dateRange?.end) {
                    this.calcDays(this.dateRange?.end);
                }
                this.cdRef.markForCheck();
            }, 1);
        }
    }
    get includeTimeLabel() {
        return this.disabledTime ? this.config.disabledTimeText ?? 'Ignore time' : this.config.enabledTimeText ?? 'Consider time';
    }
    get weekStartDay() {
        return this.config.weekStartDay ?? this.localeWeekStart;
    }
    get hideWeekNumbers() {
        return this.config.hideWeekNumbers ?? false;
    }
    get disabledTime() {
        return this.config.disabledTime ?? false;
    }
    constructor() {
        /**
         * Emits the new value of `date` whenever its value changes.
         */
        this.dateChange = new EventEmitter();
        /**
         * Emits the new value of `dateRange` whenever its value changes.
         */
        this.dateRangeChange = new EventEmitter();
        /**
         * Emits an event to notify about disabling the time from the datepicker.
         * When time is disable, we construct a pure date object in UTC 00:00:00 time.
         */
        this.disabledTimeChange = new EventEmitter();
        /**
         * Object to configure the datepicker.
         */
        this.config = {};
        /**
         * Aria label for the previous button. Needed for a11y.
         */
        this.previousLabel = 'previous';
        /**
         * Aria label for the next button. Needed for a11y.
         */
        this.nextLabel = 'next';
        /**
         * Use this to force date range operation to select either start date or end date
         */
        this._rangeType = 'START';
        /**
         * Sets the focus state by tracking focusin and focusout events.
         */
        this.hasFocus = false;
        this.daysOfWeek = [];
        this.view = 'week';
        this.weeks = [];
        this.weekNums = [];
        this.years = [];
        this.showToday = false;
        this.minYear = START_YEAR;
        this.maxYear = END_YEAR;
        this.switchId = `__si-datepicker-switch-id-${idCounter$2++}`;
        this.locale = inject(LOCALE_ID);
        this.cdRef = inject(ChangeDetectorRef);
        this.trackByIndex = (index) => index;
        this.days = Object.assign([], getLocaleDayNames(this.locale, FormStyle.Format, TranslationWidth.Abbreviated));
        this.days.push(this.days.shift());
        this.months = Object.assign([], getLocaleMonthNames(this.locale, FormStyle.Format, TranslationWidth.Wide));
        const weekStart = getLocaleFirstDayOfWeek(this.locale);
        this.localeWeekStart =
            weekStart === WeekDay.Sunday ? 'sunday' :
                weekStart === WeekDay.Saturday ? 'saturday' : 'monday';
    }
    ngOnInit() {
        if (this.config.enableDateRange && this.dateRange) {
            this.dateRange = {
                start: this.dateRange.start ? createDate(this.dateRange.start) : undefined,
                end: this.dateRange.end ? createDate(this.dateRange.end) : undefined
            };
            this.calcDays(this.dateRange.start);
        }
        if (!this.config.enableDateRange) {
            this.calcDays(this.date);
        }
    }
    ngOnChanges(changes) {
        let calc = false;
        if (changes.date && !this.config.enableDateRange) {
            if (this.date) {
                if (changes.date.isFirstChange()) {
                    this.previousTime = new Date(this.date);
                    this.time = this.date;
                }
                if (this.time?.getTime() !== this.date?.getTime()) {
                    this.time = this.date;
                }
            }
            calc = true;
        }
        if (changes.date && this.config.enableDateRange) {
            if (!changes.date.isFirstChange()) {
                this.calcDays(changes.date.currentValue);
            }
        }
        if (changes.config) {
            const disabledChange = !!changes.config.previousValue?.disabledTime !== this.disabledTime;
            if (disabledChange) {
                this.onDisabledTimeChanged();
            }
            calc = true;
        }
        if (calc) {
            this.calcDays(this.date);
        }
        this.cdRef.markForCheck();
    }
    setDate(date) {
        const dateWithoutTime = createDate(date);
        const validForMinDate = !(this.config?.minDate && dateWithoutTime < getDateWithoutTime(this.config.minDate));
        const validForMaxDate = !(this.config?.maxDate && dateWithoutTime > getDateWithoutTime(this.config.maxDate));
        if (this.date !== date && validForMinDate && validForMaxDate) {
            const previousValue = this.date;
            this.date = date;
            // eslint-disable-next-line @angular-eslint/no-lifecycle-call
            this.ngOnChanges({ date: new SimpleChange(previousValue, this.date, previousValue === undefined) });
            this.dateChange.next(this.date);
        }
        else if (!validForMinDate || !validForMaxDate) {
            // eslint-disable-next-line @angular-eslint/no-lifecycle-call
            this.ngOnChanges({ date: new SimpleChange(undefined, this.date, true) });
        }
        if (this.config.enableTimeValidation && this.timePicker && (this.config.minDate || this.config.maxDate)) {
            this.validateTime(date);
        }
    }
    dateSelected(day) {
        const selectedDateWithoutTime = getDateWithoutTime(day);
        const isValidRange = isSameOrBetween(selectedDateWithoutTime, this.config?.minDate, this.config?.maxDate);
        if (!isValidRange) {
            return;
        }
        const date = createDate(day, this.time?.getHours(), this.time?.getMinutes(), this.time?.getSeconds(), this.time?.getMilliseconds());
        if (!this.config.enableDateRange) {
            this.setDate(date);
        }
        else {
            // eslint-disable-next-line max-len
            if ((!this.dateRange?.start && this.rangeType !== 'END') || (this.dateRange?.start && this.dateRange.end && this.rangeType !== 'END')) {
                this.setDateRange({
                    start: date,
                    end: undefined
                });
            }
            else if (this.rangeType === 'END' || (this.dateRange?.start && !this.dateRange.end)) {
                this.setDateRange({
                    start: this.dateRange?.start,
                    end: date
                });
                this.rangeType = undefined;
            }
        }
    }
    setDateRange(dtRange) {
        let calcDate;
        if (this.config.enableDateRange && dtRange) {
            const dateRange = {
                start: undefined,
                end: undefined,
                ...this.dateRange
            };
            if (dtRange.start && dtRange.end && dtRange.end < dtRange.start) {
                dateRange.end = undefined;
                dateRange.start = createDate(dtRange.end);
                calcDate = dateRange.start;
            }
            else {
                if (dtRange.start) {
                    dateRange.start = createDate(dtRange.start);
                    dateRange.end = undefined;
                    calcDate = dateRange.start;
                }
                if (dtRange.end) {
                    dateRange.end = createDate(dtRange.end);
                    calcDate = dateRange.end;
                }
            }
            this.dateRange = dateRange;
            this.dateRangeChange.next(dateRange);
            if (calcDate) {
                this.calcDays(calcDate);
            }
        }
    }
    isRangeHover(day) {
        // eslint-disable-next-line max-len
        return this.dateRange?.start && this.activeHover && (day.date > this.dateRange.start) && !this.dateRange.end && day.date <= this.activeHover;
    }
    isRangeSelected(day) {
        if (!this.config.enableDateRange) {
            return false;
        }
        return isSameDate(day.date, this.dateRange?.start)
            || isSameDate(day.date, this.dateRange?.end);
    }
    inRange(day) {
        if (!this.config.enableDateRange || !this.dateRange?.start || !this.dateRange.end) {
            return false;
        }
        return isBetween(day.date, this.dateRange?.start, this.dateRange.end);
    }
    timeSelected(newTime) {
        if (!newTime) {
            return;
        }
        if (this.time?.getTime() !== newTime.getTime()) {
            this.previousTime = this.time;
            this.time = newTime;
            let newDate;
            if (this.disabledTime) {
                // if time is disabled, ensure that 00:00:00 is displayed in any timezone
                newDate = createDate(this.date);
                this.time = newDate;
            }
            else {
                newDate = createDate(this.date, this.time.getHours(), this.time.getMinutes(), this.time.getSeconds(), this.time.getMilliseconds());
            }
            this.dateSelected(newDate);
        }
        else {
            this.validateTime(newTime);
        }
    }
    toggleDisabledTime() {
        this.config.disabledTime = !this.disabledTime;
        this.onDisabledTimeChanged();
    }
    onDisabledTimeChanged() {
        if (this.disabledTime) {
            const newTime = new Date(Date.UTC(this.date.getFullYear(), this.date.getMonth(), this.date.getDate(), 0, 0, 0, 0));
            this.timeSelected(newTime);
        }
        else if (this.previousTime) {
            this.timeSelected(this.previousTime);
        }
        else {
            this.timeSelected(new Date());
        }
        this.disabledTimeChange.next(this.disabledTime);
    }
    validateTime(date) {
        if (!this.disabledTime &&
            (this.config.minDate && date < this.config.minDate || this.config.maxDate && date > this.config.maxDate)) {
            // wait for a cycle to initialize timepicker
            setTimeout(() => {
                this.timePicker.invalidHours = this.timePicker.invalidMinutes = true;
                this.timePicker.invalidSeconds = this.timePicker.invalidMilliseconds = true;
                this.cdRef.markForCheck();
            });
        }
        else {
            this.timePicker.invalidHours = this.timePicker.invalidMinutes = false;
            this.timePicker.invalidSeconds = this.timePicker.invalidMilliseconds = false;
        }
    }
    onClick(event) {
        this.hasFocus = true;
        event.stopPropagation();
    }
    updateLimits() {
        if (this.config?.minDate) {
            this.minYear = this.config.minDate.getFullYear();
            this.minMonth = this.config.minDate.getMonth() + 1;
            this.minDay = this.config.minDate.getDate();
        }
        if (this.config?.maxDate) {
            this.maxYear = this.config.maxDate.getFullYear();
            this.maxMonth = this.config.maxDate.getMonth() + 1;
            this.maxDay = this.config.maxDate.getDate();
        }
    }
    calcDays(initialDate) {
        this.updateLimits();
        const today$1 = today();
        if (!initialDate) {
            initialDate = today$1;
        }
        const monthStart = getFirstDateInMonth(initialDate);
        const monthEnd = getLastDateInMonth(initialDate);
        /**
         * We start the month with the first day in the week which has the effect that dates are
         * visible which aren't in the active month.
         */
        const startDate = getWeekStartDate(monthStart, this.weekStartDay);
        const lastDate = getWeekEndDate(monthEnd, this.weekStartDay);
        this.daysOfWeek = getDaysOfWeek(this.weekStartDay);
        // Hide Today button, when today's date falls within selected month and selected date
        // equals today's date
        const isTodayWithinLimits = isSameOrBetween(today$1, this.config?.minDate, this.config.maxDate);
        const isTodaySelected = isSameDate(today$1, this.date);
        this.showToday = isTodayWithinLimits && !isTodaySelected;
        this.weeks = [];
        this.weekNums = [];
        let week = [];
        if (this.dateRange) {
            if (this.dateRange.start) {
                this.dateRange.start = createDate(this.dateRange?.start);
            }
            if (this.dateRange.end) {
                this.dateRange.end = createDate(this.dateRange?.end);
            }
        }
        for (let i = 0, date = new Date(startDate); date <= lastDate; i++, date.setDate(date.getDate() + 1)) {
            const firstDayOfWeek = i % 7 === 0;
            if (i && firstDayOfWeek) {
                this.weeks.push(week);
                week = [];
            }
            if (firstDayOfWeek && !this.hideWeekNumbers) {
                this.weekNums.push(getWeekOfYear(date, this.weekStartDay));
            }
            const isToday = isSameDate(date, today$1);
            const isSelected = isSameDate(date, this.date);
            const outOfRange = !isSameOrBetween(date, this.config.minDate, this.config.maxDate);
            week.push({
                date: createDate(date),
                selected: isSelected,
                day: date.getDate(),
                isToday,
                otherMonth: isAnotherMonth(date, monthStart),
                disabled: outOfRange
            });
        }
        this.weeks.push(week);
        this.currentMonth = monthStart;
        this.monthNumber = this.currentMonth.getMonth() + 1;
        this.year = this.currentMonth.getFullYear();
        this.updateYears(this.year - NUM_YEARS / 2);
        this.activeHover = undefined;
    }
    updateYears(start) {
        if (start < this.minYear) {
            start = this.minYear;
        }
        else if (start + NUM_YEARS > this.maxYear) {
            start = this.maxYear - NUM_YEARS + 1;
        }
        this.years = [];
        for (let i = 0; i < NUM_YEARS; i++) {
            this.years.push(start + i);
        }
    }
    setMonthOffset(offset) {
        const month = new Date(this.currentMonth);
        month.setMonth(month.getMonth() + offset);
        this.calcDays(month);
    }
    setMonth(m) {
        if ((this.year <= this.minYear && this.minMonth && m < this.minMonth) ||
            (this.year >= this.maxYear && this.maxMonth && m > this.maxMonth)) {
            return;
        }
        const month = new Date(this.currentMonth);
        month.setMonth(m - 1);
        this.calcDays(month);
        this.view = 'week';
    }
    setYear(year) {
        if (year <= this.maxYear && year >= this.minYear) {
            const month = new Date(this.currentMonth);
            month.setFullYear(year);
            this.calcDays(month);
            this.view = 'month';
        }
    }
    prevYears() {
        this.updateYears(this.years[0] - NUM_YEARS);
    }
    nextYears() {
        this.updateYears(this.years[0] + NUM_YEARS);
    }
    monthSelection() {
        this.view = 'month';
    }
    yearSelection() {
        this.view = 'year';
    }
    goToToday() {
        this.dateSelected(today());
    }
}
SiDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDatepickerComponent, selector: "si-datepicker", inputs: { date: "date", dateRange: "dateRange", config: "config", previousLabel: "previousLabel", nextLabel: "nextLabel", time12h: "time12h", rangeType: "rangeType" }, outputs: { dateChange: "dateChange", dateRangeChange: "dateRangeChange", disabledTimeChange: "disabledTimeChange" }, host: { listeners: { "click": "onClick($event)" } }, viewQueries: [{ propertyName: "timePicker", first: true, predicate: ["timePicker"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"view === 'year'\" class=\"text-center\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"years[0] <= minYear\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"prevYears()\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </button>\n    <span class=\"year flex-fill si-title-1 text-secondary\">\n      {{ years[0] }} - {{ years[years.length - 1] }}\n    </span>\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"years[years.length - 1] >= maxYear\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"nextYears()\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </button>\n  </div>\n  <div class=\"selection d-flex flex-wrap px-4 pb-4\" siListInteraction listInteractionOrientation=\"ste\">\n    <div\n      *ngFor=\"let year of years; trackBy: trackByIndex\"\n      class=\"year si-title-1\"\n      [class.disabled]=\"year > maxYear || year < minYear\"\n      [tabindex]=\"year > maxYear || year < minYear ? null : '0'\"\n      (click)=\"setYear(year)\"\n      (keydown.enter)=\"setYear(year)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"year > maxYear || year < minYear\"\n    >{{ year }}</div>\n  </div>\n</div>\n<div *ngIf=\"view === 'month'\" class=\"text-center\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"year <= minYear\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"setYear(year - 1)\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </button>\n    <a class=\"year flex-fill si-title-1\" (click)=\"yearSelection()\" tabindex=\"0\">\n      {{ year }}\n    </a>\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"year >= maxYear\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"setYear(year + 1)\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </button>\n  </div>\n  <div class=\"selection d-flex flex-wrap px-4 pb-4\" siListInteraction listInteractionOrientation=\"ste\">\n    <div\n      *ngFor=\"let m of [1,2,3,4,5,6,7,8,9,10,11,12]; trackBy: trackByIndex\"\n      class=\"month si-title-1\"\n      [class.disabled]=\"(year >= maxYear && maxMonth && m > maxMonth) || (year <=minYear && minMonth && m < minMonth)\"\n      [tabindex]=\"(year >= maxYear && maxMonth && m > maxMonth) || (year <=minYear && minMonth && m < minMonth) ? null : '0'\"\n      (click)=\"setMonth(m)\"\n      (keydown.enter)=\"setMonth(m)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!(year >= maxYear && maxMonth && m > maxMonth) || !!(year <= minYear && minMonth && m < minMonth)\"\n    >\n      {{ months[m - 1] }}\n    </div>\n  </div>\n</div>\n<div *ngIf=\"view === 'week'\" class=\"text-center pb-6\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <a\n      role=\"button\"\n      [class.disabled]=\"year <= minYear && minMonth && monthNumber <= minMonth ?? null\"\n      [tabindex]=\"year <= minYear && minMonth && monthNumber <= minMonth ? null : '0'\"\n      class=\"btn btn-circle btn-sm btn-tertiary p-2\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"setMonthOffset(-1)\"\n      (keydown.enter)=\"setMonthOffset(-1)\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </a>\n    <div class=\"flex-fill\">\n      <a\n        class=\"month flex-fill text-end pe-1 si-title-1\"\n        tabindex=\"0\"\n        (click)=\"monthSelection()\"\n        (keydown.enter)=\"monthSelection()\"\n      >\n        {{ months[monthNumber - 1] }}\n      </a>\n      <a\n        class=\"year flex-fill text-start ps-1 si-title-1\"\n        tabindex=\"0\"\n        (click)=\"yearSelection()\"\n        (keydown.enter)=\"yearSelection()\"\n      >\n        {{ year }}\n      </a>\n    </div>\n    <a\n      role=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary p-2\"\n      [class.disabled]=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ?? null\"\n      [tabindex]=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? null : '0'\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? false : setMonthOffset(1)\"\n      (keydown.enter)=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? false : setMonthOffset(1)\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </a>\n  </div>\n  <table class=\"ps-8 pe-8\" siListInteraction listInteractionOrientation=\"ste\">\n    <thead>\n      <tr>\n        <td *ngIf=\"!hideWeekNumbers\" class=\"week-num\"></td>\n        <th *ngFor=\"let day of daysOfWeek; trackBy: trackByIndex\">\n          <span class=\"si-hidden-xs si-hidden-sm\">{{ days[day.index - 1] }}</span>\n        </th>\n      </tr>\n    </thead>\n    <tbody>\n      <tr *ngFor=\"let week of weeks; let index = index; trackBy: trackByIndex\" [class.selected]=\"week[0].selected\">\n        <td *ngIf=\"!hideWeekNumbers\" class=\"week-num\">{{ weekNums[index] }}</td>\n        <td *ngFor=\"let day of week; trackBy: trackByIndex\"\n            [class.range-hover]=\"config.enableDateRange && isRangeHover(day)\"\n            [class.range]=\"inRange(day)\"\n            [class.range-start-end]=\"isRangeSelected(day)\"\n            (mouseover)=\"activeHover = day.date;\"\n            >\n          <div\n            [class.text-secondary]=\"day.otherMonth && !day.disabled && !isRangeSelected(day)\"\n            [class.si-body-1]=\"day.otherMonth\"\n            [class.si-title-1]=\"!day.otherMonth\"\n            [attr.aria-disabled]=\"day.disabled\"\n            [class.today]=\"day.isToday\"\n            [class.selected]=\"day.selected && !config.enableDateRange\"\n            [class.disabled]=\"day.disabled\"\n            (click)=\"dateSelected(day.date)\"\n            (keydown.enter)=\"dateSelected(day.date)\"\n            siListInteractionItem\n            [listInteractionItemDisabled]=\"day.disabled\"\n          >\n            {{ day.day }}\n          </div>\n        </td>\n      </tr>\n    </tbody>\n  </table>\n  <div *ngIf=\"showToday\" class=\"footer pt-2 ps-8 pe-8\">\n    <button type=\"button\" class=\"btn btn-tertiary flex-fill\" (click)=\"goToToday()\">\n      {{ config.todayText ?? 'Today' | translate }}\n    </button>\n  </div>\n</div>\n<div *ngIf=\"this.config.showTime && !this.config.enableDateRange ?? false\" class=\"border-top pt-6\">\n  <div class=\"ps-8 pe-8 pb-8\">\n    <div *ngIf=\"!config.mandatoryTime ?? false\" class=\"ms-3 mb-5 form-switch\">\n      <input\n        type=\"checkbox\"\n        class=\"form-check-input\"\n        role=\"switch\"\n        [id]=\"switchId\"\n        [checked]=\"!disabledTime\"\n        (change)=\"toggleDisabledTime()\"\n      >\n      <label [for]=\"switchId\">{{ includeTimeLabel | translate }}</label>\n    </div>\n    <si-timepicker\n      [ngModel]=\"time\"\n      (ngModelChange)=\"timeSelected($event)\"\n      [disabled]=\"disabledTime\"\n      [showMinutes]=\"config.showMinutes ?? true\"\n      [showSeconds]=\"config.showSeconds ?? false\"\n      [showMilliseconds]=\"config.showMilliseconds ?? false\"\n      [showMeridian]=\"time12h\"\n      #timePicker\n      ></si-timepicker>\n  </div>\n</div>\n", styles: [":host{background:var(--element-base-1);border:1px solid var(--element-ui-4);border-radius:var(--element-radius-2);display:block;inline-size:348px}.header,.footer{display:flex;align-items:center}.header{block-size:60px}.header a{text-decoration:none;cursor:pointer}.footer{margin-block-start:4px;text-align:center}table{inline-size:100%;border-collapse:separate;border-spacing:0 3px;table-layout:fixed;margin-block-end:-3px}.selection .month:not(.disabled):hover,.selection .year:not(.disabled):hover,td div:not(.disabled):not(.selected):hover{background:var(--element-base-1-hover);color:var(--element-ui-0)}td{block-size:32px;text-align:center}td div{inline-size:32px;block-size:32px;line-height:32px;margin-inline:auto;position:relative;cursor:pointer}td div.selected{background:var(--element-ui-0);color:var(--element-base-1)}th{font-weight:400;text-align:center;block-size:32px}.week-num,th{font-size:.75rem;color:var(--element-text-secondary);inline-size:32px;cursor:default}.today.selected:before{border-color:var(--element-ui-5)}.today:before{position:absolute;content:\"\";inset:1px;border:1px solid var(--element-ui-1);border-radius:var(--element-radius-2);display:inline-block}.selection .month,.selection .year{block-size:30px;line-height:30px;cursor:pointer}.selection .month{inline-size:50%}.selection .year{inline-size:33.3%}.disabled{color:var(--element-text-disabled);font-weight:400!important;cursor:default!important}.range{background-color:var(--element-ui-4)}.range:hover{background:var(--element-base-1-hover)}.range:hover div.si-title-1:hover{background:transparent}.range-hover{background-color:var(--element-base-1-hover)}td.range-start-end{background:var(--element-ui-0);color:var(--element-base-1)}td.range-start-end div.si-title-1:hover,td.range-start-end div.si-body-1:hover{background:transparent;color:var(--element-base-1)}td.range-start-end .today:before{border-color:var(--element-ui-5)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiTimepickerComponent, selector: "si-timepicker", inputs: ["disabled", "hoursLabel", "minutesLabel", "secondsLabel", "millisecondsLabel", "hideLabels", "hoursAriaLabel", "minutesAriaLabel", "secondsAriaLabel", "millisecondsAriaLabel", "hoursPlaceholder", "minutesPlaceholder", "secondsPlaceholder", "millisecondsPlaceholder", "meridians", "meridiansLabel", "meridiansAriaLabel", "showMinutes", "showSeconds", "showMilliseconds", "showMeridian", "min", "max", "readonly"], outputs: ["isValid", "meridianChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-datepicker', template: "<div *ngIf=\"view === 'year'\" class=\"text-center\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"years[0] <= minYear\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"prevYears()\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </button>\n    <span class=\"year flex-fill si-title-1 text-secondary\">\n      {{ years[0] }} - {{ years[years.length - 1] }}\n    </span>\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"years[years.length - 1] >= maxYear\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"nextYears()\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </button>\n  </div>\n  <div class=\"selection d-flex flex-wrap px-4 pb-4\" siListInteraction listInteractionOrientation=\"ste\">\n    <div\n      *ngFor=\"let year of years; trackBy: trackByIndex\"\n      class=\"year si-title-1\"\n      [class.disabled]=\"year > maxYear || year < minYear\"\n      [tabindex]=\"year > maxYear || year < minYear ? null : '0'\"\n      (click)=\"setYear(year)\"\n      (keydown.enter)=\"setYear(year)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"year > maxYear || year < minYear\"\n    >{{ year }}</div>\n  </div>\n</div>\n<div *ngIf=\"view === 'month'\" class=\"text-center\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"year <= minYear\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"setYear(year - 1)\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </button>\n    <a class=\"year flex-fill si-title-1\" (click)=\"yearSelection()\" tabindex=\"0\">\n      {{ year }}\n    </a>\n    <button\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary\"\n      [disabled]=\"year >= maxYear\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"setYear(year + 1)\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </button>\n  </div>\n  <div class=\"selection d-flex flex-wrap px-4 pb-4\" siListInteraction listInteractionOrientation=\"ste\">\n    <div\n      *ngFor=\"let m of [1,2,3,4,5,6,7,8,9,10,11,12]; trackBy: trackByIndex\"\n      class=\"month si-title-1\"\n      [class.disabled]=\"(year >= maxYear && maxMonth && m > maxMonth) || (year <=minYear && minMonth && m < minMonth)\"\n      [tabindex]=\"(year >= maxYear && maxMonth && m > maxMonth) || (year <=minYear && minMonth && m < minMonth) ? null : '0'\"\n      (click)=\"setMonth(m)\"\n      (keydown.enter)=\"setMonth(m)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!(year >= maxYear && maxMonth && m > maxMonth) || !!(year <= minYear && minMonth && m < minMonth)\"\n    >\n      {{ months[m - 1] }}\n    </div>\n  </div>\n</div>\n<div *ngIf=\"view === 'week'\" class=\"text-center pb-6\">\n  <div class=\"header border-bottom mb-6 px-8 py-5\">\n    <a\n      role=\"button\"\n      [class.disabled]=\"year <= minYear && minMonth && monthNumber <= minMonth ?? null\"\n      [tabindex]=\"year <= minYear && minMonth && monthNumber <= minMonth ? null : '0'\"\n      class=\"btn btn-circle btn-sm btn-tertiary p-2\"\n      [attr.aria-label]=\"previousLabel | translate\"\n      (click)=\"setMonthOffset(-1)\"\n      (keydown.enter)=\"setMonthOffset(-1)\"\n    >\n      <i class=\"icon element-left-2 flip-rtl\"></i>\n    </a>\n    <div class=\"flex-fill\">\n      <a\n        class=\"month flex-fill text-end pe-1 si-title-1\"\n        tabindex=\"0\"\n        (click)=\"monthSelection()\"\n        (keydown.enter)=\"monthSelection()\"\n      >\n        {{ months[monthNumber - 1] }}\n      </a>\n      <a\n        class=\"year flex-fill text-start ps-1 si-title-1\"\n        tabindex=\"0\"\n        (click)=\"yearSelection()\"\n        (keydown.enter)=\"yearSelection()\"\n      >\n        {{ year }}\n      </a>\n    </div>\n    <a\n      role=\"button\"\n      class=\"btn btn-circle btn-sm btn-tertiary p-2\"\n      [class.disabled]=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ?? null\"\n      [tabindex]=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? null : '0'\"\n      [attr.aria-label]=\"nextLabel | translate\"\n      (click)=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? false : setMonthOffset(1)\"\n      (keydown.enter)=\"year >= maxYear && maxMonth && monthNumber >= maxMonth ? false : setMonthOffset(1)\"\n    >\n      <i class=\"icon element-right-2 flip-rtl\"></i>\n    </a>\n  </div>\n  <table class=\"ps-8 pe-8\" siListInteraction listInteractionOrientation=\"ste\">\n    <thead>\n      <tr>\n        <td *ngIf=\"!hideWeekNumbers\" class=\"week-num\"></td>\n        <th *ngFor=\"let day of daysOfWeek; trackBy: trackByIndex\">\n          <span class=\"si-hidden-xs si-hidden-sm\">{{ days[day.index - 1] }}</span>\n        </th>\n      </tr>\n    </thead>\n    <tbody>\n      <tr *ngFor=\"let week of weeks; let index = index; trackBy: trackByIndex\" [class.selected]=\"week[0].selected\">\n        <td *ngIf=\"!hideWeekNumbers\" class=\"week-num\">{{ weekNums[index] }}</td>\n        <td *ngFor=\"let day of week; trackBy: trackByIndex\"\n            [class.range-hover]=\"config.enableDateRange && isRangeHover(day)\"\n            [class.range]=\"inRange(day)\"\n            [class.range-start-end]=\"isRangeSelected(day)\"\n            (mouseover)=\"activeHover = day.date;\"\n            >\n          <div\n            [class.text-secondary]=\"day.otherMonth && !day.disabled && !isRangeSelected(day)\"\n            [class.si-body-1]=\"day.otherMonth\"\n            [class.si-title-1]=\"!day.otherMonth\"\n            [attr.aria-disabled]=\"day.disabled\"\n            [class.today]=\"day.isToday\"\n            [class.selected]=\"day.selected && !config.enableDateRange\"\n            [class.disabled]=\"day.disabled\"\n            (click)=\"dateSelected(day.date)\"\n            (keydown.enter)=\"dateSelected(day.date)\"\n            siListInteractionItem\n            [listInteractionItemDisabled]=\"day.disabled\"\n          >\n            {{ day.day }}\n          </div>\n        </td>\n      </tr>\n    </tbody>\n  </table>\n  <div *ngIf=\"showToday\" class=\"footer pt-2 ps-8 pe-8\">\n    <button type=\"button\" class=\"btn btn-tertiary flex-fill\" (click)=\"goToToday()\">\n      {{ config.todayText ?? 'Today' | translate }}\n    </button>\n  </div>\n</div>\n<div *ngIf=\"this.config.showTime && !this.config.enableDateRange ?? false\" class=\"border-top pt-6\">\n  <div class=\"ps-8 pe-8 pb-8\">\n    <div *ngIf=\"!config.mandatoryTime ?? false\" class=\"ms-3 mb-5 form-switch\">\n      <input\n        type=\"checkbox\"\n        class=\"form-check-input\"\n        role=\"switch\"\n        [id]=\"switchId\"\n        [checked]=\"!disabledTime\"\n        (change)=\"toggleDisabledTime()\"\n      >\n      <label [for]=\"switchId\">{{ includeTimeLabel | translate }}</label>\n    </div>\n    <si-timepicker\n      [ngModel]=\"time\"\n      (ngModelChange)=\"timeSelected($event)\"\n      [disabled]=\"disabledTime\"\n      [showMinutes]=\"config.showMinutes ?? true\"\n      [showSeconds]=\"config.showSeconds ?? false\"\n      [showMilliseconds]=\"config.showMilliseconds ?? false\"\n      [showMeridian]=\"time12h\"\n      #timePicker\n      ></si-timepicker>\n  </div>\n</div>\n", styles: [":host{background:var(--element-base-1);border:1px solid var(--element-ui-4);border-radius:var(--element-radius-2);display:block;inline-size:348px}.header,.footer{display:flex;align-items:center}.header{block-size:60px}.header a{text-decoration:none;cursor:pointer}.footer{margin-block-start:4px;text-align:center}table{inline-size:100%;border-collapse:separate;border-spacing:0 3px;table-layout:fixed;margin-block-end:-3px}.selection .month:not(.disabled):hover,.selection .year:not(.disabled):hover,td div:not(.disabled):not(.selected):hover{background:var(--element-base-1-hover);color:var(--element-ui-0)}td{block-size:32px;text-align:center}td div{inline-size:32px;block-size:32px;line-height:32px;margin-inline:auto;position:relative;cursor:pointer}td div.selected{background:var(--element-ui-0);color:var(--element-base-1)}th{font-weight:400;text-align:center;block-size:32px}.week-num,th{font-size:.75rem;color:var(--element-text-secondary);inline-size:32px;cursor:default}.today.selected:before{border-color:var(--element-ui-5)}.today:before{position:absolute;content:\"\";inset:1px;border:1px solid var(--element-ui-1);border-radius:var(--element-radius-2);display:inline-block}.selection .month,.selection .year{block-size:30px;line-height:30px;cursor:pointer}.selection .month{inline-size:50%}.selection .year{inline-size:33.3%}.disabled{color:var(--element-text-disabled);font-weight:400!important;cursor:default!important}.range{background-color:var(--element-ui-4)}.range:hover{background:var(--element-base-1-hover)}.range:hover div.si-title-1:hover{background:transparent}.range-hover{background-color:var(--element-base-1-hover)}td.range-start-end{background:var(--element-ui-0);color:var(--element-base-1)}td.range-start-end div.si-title-1:hover,td.range-start-end div.si-body-1:hover{background:transparent;color:var(--element-base-1)}td.range-start-end .today:before{border-color:var(--element-ui-5)}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { date: [{
                type: Input
            }], dateRange: [{
                type: Input
            }], dateChange: [{
                type: Output
            }], dateRangeChange: [{
                type: Output
            }], disabledTimeChange: [{
                type: Output
            }], config: [{
                type: Input
            }], previousLabel: [{
                type: Input
            }], nextLabel: [{
                type: Input
            }], time12h: [{
                type: Input
            }], rangeType: [{
                type: Input
            }], timePicker: [{
                type: ViewChild,
                args: ['timePicker']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class SiDatepickerOverlayComponent {
    constructor() {
        this.dateChange = new EventEmitter();
        this.disabledTimeChange = new EventEmitter();
        this.dateRangeChange = new EventEmitter();
        this.elementRef = inject(ElementRef);
        this.subscriptions = [];
        this.cdRef = inject(ChangeDetectorRef);
    }
    ngAfterViewInit() {
        setTimeout(() => {
            if (!this.datepicker) {
                return;
            }
            if (this.config) {
                this.datepicker.config = this.config;
            }
            this.datepicker.time12h = this.time12h;
            this.datepicker.setDate(this.date ? this.date : new Date());
            this.datepicker.rangeType = this.rangeType ?? 'START';
            this.datepicker.dateRange = this.dateRange;
            this.subscriptions.push(this.datepicker.dateChange.subscribe(dt => {
                this.dateChange.next(dt);
            }));
            this.subscriptions.push(this.datepicker.dateRangeChange.subscribe(dtRange => {
                this.dateRangeChange.next(dtRange);
            }));
            this.subscriptions.push(this.datepicker.disabledTimeChange.subscribe(disabled => {
                this.disabledTimeChange.next(disabled);
            }));
            this.cdRef.markForCheck();
        }, 1);
    }
    ngOnDestroy() {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
SiDatepickerOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiDatepickerOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDatepickerOverlayComponent, selector: "si-datepicker-overlay", inputs: { date: "date", dateRange: "dateRange", rangeType: "rangeType" }, outputs: { dateChange: "dateChange", disabledTimeChange: "disabledTimeChange", dateRangeChange: "dateRangeChange" }, viewQueries: [{ propertyName: "datepicker", first: true, predicate: ["datepicker"], descendants: true }], ngImport: i0, template: `
  <si-datepicker #datepicker class="mt-1 elevation-2 mh-100 overflow-auto" tabindex="-1"></si-datepicker>
  `, isInline: true, dependencies: [{ kind: "component", type: SiDatepickerComponent, selector: "si-datepicker", inputs: ["date", "dateRange", "config", "previousLabel", "nextLabel", "time12h", "rangeType"], outputs: ["dateChange", "dateRangeChange", "disabledTimeChange"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerOverlayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'si-datepicker-overlay',
                    template: `
  <si-datepicker #datepicker class="mt-1 elevation-2 mh-100 overflow-auto" tabindex="-1"></si-datepicker>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { datepicker: [{
                type: ViewChild,
                args: ['datepicker', { static: false }]
            }], date: [{
                type: Input
            }], dateRange: [{
                type: Input
            }], rangeType: [{
                type: Input
            }], dateChange: [{
                type: Output
            }], disabledTimeChange: [{
                type: Output
            }], dateRangeChange: [{
                type: Output
            }] } });

class SiDatepickerDirective extends SiDateInputDirective {
    constructor() {
        super(...arguments);
        this.overlay = inject(Overlay);
    }
    ngOnDestroy() {
        this.overlayref?.dispose();
    }
    updateInstance(date) {
        if (this.datepickerOverlay) {
            this.datepickerOverlay.instance.date = date;
            this.datepickerOverlay.instance.datepicker?.setDate(date);
        }
    }
    onFocus() {
        this.showDatepicker();
    }
    onKeydown(event) {
        if (event.key === 'Enter') {
            if (this.overlayref?.hasAttached()) {
                this.hideDatepicker();
            }
            else {
                this.showDatepicker();
            }
        }
    }
    onBlur(event) {
        this.onTouched();
        setTimeout(() => {
            // Close the datepicker, when the user tabs from the input element to the next element, unless the datepicker got the tab.
            // Occurs when tabbing through a form with multiple input elements of within the filtered search.
            if (!this.datepickerOverlay?.instance.elementRef.nativeElement.contains(document.activeElement)
                && !this.datepickerOverlay?.instance.datepicker?.hasFocus) {
                this.hideDatepicker();
            }
            this.cdRef.markForCheck();
        });
    }
    onDatepickerChange(date) {
        // update input element
        this.writeValue(date);
        // update the Forms ngModel
        this.onModelChange(this.date);
    }
    showDatepicker() {
        if (this.readonly) {
            return;
        }
        if (!this.overlayref?.hasAttached()) {
            this.overlayref = getOverlay(this.elementRef, this.overlay, true, [
                positionBottomCenter,
                positionBottomStart,
                positionBottomEnd,
                positionTopCenter,
                positionTopStart,
                positionTopEnd
            ], true);
        }
        if (this.overlayref.hasAttached()) {
            if (this.datepickerOverlay?.instance.config?.enableDateRange) {
                this.datepickerOverlay.instance.dateRange = this.dateRange;
                if (this.datepickerOverlay.instance.datepicker) {
                    this.datepickerOverlay.instance.datepicker.dateRange = this.dateRange;
                }
                else {
                    // component is constructing
                    setTimeout(() => {
                        if (this.datepickerOverlay?.instance.datepicker) {
                            this.datepickerOverlay.instance.datepicker.dateRange = this.dateRange;
                        }
                        this.cdRef.markForCheck();
                    });
                }
            }
            return;
        }
        this.overlayref.backdropClick().subscribe(() => {
            this.hideDatepicker();
        });
        const portal = new ComponentPortal(SiDatepickerOverlayComponent);
        const datepickerRef = this.overlayref.attach(portal);
        datepickerRef.instance.date = this.date;
        datepickerRef.instance.dateRange = this.dateRange;
        datepickerRef.instance.rangeType = this.rangeType;
        datepickerRef.instance.config = this.siDatepickerConfig;
        datepickerRef.instance.time12h = this.getTime12h();
        this.datepickerOverlay = datepickerRef;
        this.subscriptions.push(datepickerRef.instance.dateChange.subscribe(dt => this.onDatepickerChange(dt)));
        this.subscriptions.push(datepickerRef.instance.dateRangeChange.subscribe(dtRange => this.siDatepickerRangeChange.next(dtRange)));
        this.subscriptions.push(datepickerRef.instance.disabledTimeChange.subscribe(disabledTime => {
            if (!this.siDatepickerConfig) {
                this.siDatepickerConfig = {};
            }
            this.siDatepickerConfig.disabledTime = disabledTime;
            // clear the date formatter cache
            this.dateFormat = undefined;
            // Write updated format to input element
            this.writeValue(this.date);
            this.siDatepickerDisabledTime.next(disabledTime);
        }));
    }
    hideDatepicker() {
        this.overlayref?.detach();
        this.datepickerOverlay = undefined;
        this.siDatepickerClose.next();
    }
    getTime12h() {
        const dateFormat = getDatepickerFormat(this.locale, this.siDatepickerConfig, true);
        return dateFormat?.includes('a');
    }
}
SiDatepickerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiDatepickerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDatepickerDirective, selector: "[siDatepicker]", host: { listeners: { "focus": "onFocus()", "keydown": "onKeydown($event)", "blur": "onBlur($event)" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiDatepickerDirective,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: SiDatepickerDirective,
            multi: true
        }], exportAs: ["siDatepicker"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDatepicker]',
                    exportAs: 'siDatepicker',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiDatepickerDirective,
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: SiDatepickerDirective,
                            multi: true
                        }]
                }]
        }], propDecorators: { onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }] } });

class SiDateRangeComponent {
    constructor() {
        this._siDatepickerConfig = {
            enableDateRange: true,
            showTime: false
        };
        this.startDatePlaceholder = 'Start date';
        this.endDatePlaceholder = 'End date';
        this._disabled = false;
        this._readonly = false;
        this.focusOn = false;
        this.subscriptions = [];
        this.cdRef = inject(ChangeDetectorRef);
        this.onChange = (_val) => { };
        this.onTouch = () => { };
    }
    get siDatepickerConfig() {
        return this._siDatepickerConfig;
    }
    set siDatepickerConfig(val) {
        this._siDatepickerConfig = {
            ...val,
            ...this.siDatepickerConfig
        };
    }
    get disabled() {
        return this._disabled;
    }
    /**
     * @defaultref {@link _disabled}
     */
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    get readonly() {
        return this._readonly;
    }
    /**
     * @defaultref {@link _readonly}
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    ngOnInit() {
        this.subscriptions.push(this.siDatepicker.siDatepickerRangeChange.subscribe(dtRange => {
            this.writeValue({
                ...dtRange
            });
            if (this.siDatepicker.rangeType === 'START') {
                if (dtRange) {
                    this.endDateInput.nativeElement.focus();
                }
            }
        }));
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
    showDatePicker(rangeType) {
        this.focusOn = true;
        if (this.siDatepicker) {
            //this.siDatepicker.hideDatepicker();
            this.siDatepicker.rangeType = rangeType;
            this.siDatepicker.dateRange = this.value;
            this.siDatepicker.showDatepicker();
        }
    }
    get value() {
        return this._value;
    }
    /**
     * @defaultref {@link _value}
     */
    set value(value) {
        if (value !== this._value) {
            this._value = value;
            // this allows angular's built in required validator to work correctly
            if (!value?.start && !value?.end) {
                this._value = undefined;
            }
            this.onChange(this._value);
            this.onTouch();
            this.validate(this.dateRangeControl);
            this.cdRef.markForCheck();
        }
    }
    writeValue(value) {
        this.value = value ?? this.value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    validate(control) {
        if (control !== this.dateRangeControl) {
            this.dateRangeControl = control;
        }
        let error = this.dateRangeControl?.invalid ? this.dateRangeControl.errors : null;
        if (this.startInput.invalid && !this.value?.start) {
            error = {
                ...error,
                invalidStartDateFormat: true
            };
        }
        if (this.endInput.invalid && !this.value?.end) {
            error = {
                ...error,
                invalidEndDateFormat: true
            };
        }
        else if (this.endInput.value && this.endInput.value < this.startInput.value) {
            error = {
                ...error,
                endBeforeStart: {
                    start: this.startInput.value,
                    end: this.endInput.value
                }
            };
        }
        return error ?? null;
    }
    onFocusOut(event) {
        setTimeout(() => {
            if (!this.focusOn) {
                this.siDatepicker.onBlur(event);
                this.onTouch();
                this.validate(this.dateRangeControl);
            }
        }, 1);
    }
}
SiDateRangeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDateRangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiDateRangeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDateRangeComponent, selector: "si-date-range", inputs: { siDatepickerConfig: "siDatepickerConfig", startDatePlaceholder: "startDatePlaceholder", endDatePlaceholder: "endDatePlaceholder", disabled: "disabled", readonly: "readonly", value: "value" }, host: { listeners: { "focusout": "onFocusOut()" }, classAttribute: "form-control p-0 border-0" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiDateRangeComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: SiDateRangeComponent,
            multi: true
        }
    ], viewQueries: [{ propertyName: "siDatepicker", first: true, predicate: ["range"], descendants: true, read: SiDatepickerDirective, static: true }, { propertyName: "startInput", first: true, predicate: ["startInput"], descendants: true, static: true }, { propertyName: "endInput", first: true, predicate: ["endInput"], descendants: true, static: true }, { propertyName: "endDateInput", first: true, predicate: ["end"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"form-control d-flex\"\n  [class.disabled]=\"disabled\"\n  [class.focus]=\"focusOn\"\n  [class.ng-invalid]=\"!dateRangeControl?.valid\"\n  [class.ng-touched]=\"dateRangeControl?.touched\"\n  [class.ng-dirty]=\"dateRangeControl?.dirty\"\n  #range\n  siDatepicker\n  [siDatepickerConfig]=\"siDatepickerConfig\"\n  [readonly]=\"readonly\"\n  >\n  <input\n    #startInput=\"ngModel\"\n    type=\"text\"\n    class=\"border-0 p-0 focus-none form-control\"\n    siDateInput\n    (focus)=\"showDatePicker('START')\"\n    (blur)=\"focusOn = false\"\n    (keyup.enter)=\"siDatepicker.dateRange = value\"\n    [ngModel]=\"dateRangeControl && dateRangeControl.value && dateRangeControl.value.start\"\n    (ngModelChange)=\"writeValue({start: $event, end: value?.end})\"\n    [siDatepickerConfig]=\"siDatepickerConfig\"\n    [placeholder]=\"startDatePlaceholder | translate\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n  />\n  <span class=\"mx-3\">-</span>\n  <input\n    #end\n    #endInput=\"ngModel\"\n    type=\"text\"\n    class=\"border-0 p-0 focus-none form-control text-end\"\n    siDateInput\n    (focus)=\"showDatePicker('END')\"\n    (blur)=\"focusOn = false\"\n    (keyup.enter)=\"siDatepicker.dateRange = value\"\n    [ngModel]=\"dateRangeControl && dateRangeControl.value && dateRangeControl.value.end\"\n    (ngModelChange)=\"writeValue({start: value?.start, end: $event})\"\n    [siDatepickerConfig]=\"siDatepickerConfig\"\n    [placeholder]=\"endDatePlaceholder | translate\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n  />\n</div>\n", styles: [":host{display:block;min-inline-size:198px}.focus-none{outline:0}.focus{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{flex-grow:1;background-color:var(--element-base-1);min-inline-size:80px}input:disabled,input[readonly]{background-color:var(--element-base-1);opacity:1}input,input:focus{box-shadow:none}input:not([readonly]):focus::placeholder,input:focus:not([readonly]):focus::placeholder{color:transparent}.disabled,.disabled:hover,.disabled:focus{--border-color: var(--element-ui-3);color:var(--element-text-disabled)}\n"], dependencies: [{ kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: SiDateInputDirective, selector: "[siDateInput]", inputs: ["siDatepickerConfig", "dateRange", "rangeType", "disabled", "readonly"], outputs: ["siDatepickerClose", "siDatepickerDisabledTime", "siDatepickerRangeChange"], exportAs: ["siDateInput"] }, { kind: "directive", type: SiDatepickerDirective, selector: "[siDatepicker]", exportAs: ["siDatepicker"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDateRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-date-range', host: {
                        class: 'form-control p-0 border-0'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiDateRangeComponent,
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: SiDateRangeComponent,
                            multi: true
                        }
                    ], template: "<div\n  class=\"form-control d-flex\"\n  [class.disabled]=\"disabled\"\n  [class.focus]=\"focusOn\"\n  [class.ng-invalid]=\"!dateRangeControl?.valid\"\n  [class.ng-touched]=\"dateRangeControl?.touched\"\n  [class.ng-dirty]=\"dateRangeControl?.dirty\"\n  #range\n  siDatepicker\n  [siDatepickerConfig]=\"siDatepickerConfig\"\n  [readonly]=\"readonly\"\n  >\n  <input\n    #startInput=\"ngModel\"\n    type=\"text\"\n    class=\"border-0 p-0 focus-none form-control\"\n    siDateInput\n    (focus)=\"showDatePicker('START')\"\n    (blur)=\"focusOn = false\"\n    (keyup.enter)=\"siDatepicker.dateRange = value\"\n    [ngModel]=\"dateRangeControl && dateRangeControl.value && dateRangeControl.value.start\"\n    (ngModelChange)=\"writeValue({start: $event, end: value?.end})\"\n    [siDatepickerConfig]=\"siDatepickerConfig\"\n    [placeholder]=\"startDatePlaceholder | translate\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n  />\n  <span class=\"mx-3\">-</span>\n  <input\n    #end\n    #endInput=\"ngModel\"\n    type=\"text\"\n    class=\"border-0 p-0 focus-none form-control text-end\"\n    siDateInput\n    (focus)=\"showDatePicker('END')\"\n    (blur)=\"focusOn = false\"\n    (keyup.enter)=\"siDatepicker.dateRange = value\"\n    [ngModel]=\"dateRangeControl && dateRangeControl.value && dateRangeControl.value.end\"\n    (ngModelChange)=\"writeValue({start: value?.start, end: $event})\"\n    [siDatepickerConfig]=\"siDatepickerConfig\"\n    [placeholder]=\"endDatePlaceholder | translate\"\n    [disabled]=\"disabled\"\n    [readonly]=\"readonly\"\n  />\n</div>\n", styles: [":host{display:block;min-inline-size:198px}.focus-none{outline:0}.focus{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{flex-grow:1;background-color:var(--element-base-1);min-inline-size:80px}input:disabled,input[readonly]{background-color:var(--element-base-1);opacity:1}input,input:focus{box-shadow:none}input:not([readonly]):focus::placeholder,input:focus:not([readonly]):focus::placeholder{color:transparent}.disabled,.disabled:hover,.disabled:focus{--border-color: var(--element-ui-3);color:var(--element-text-disabled)}\n"] }]
        }], propDecorators: { siDatepicker: [{
                type: ViewChild,
                args: ['range', {
                        read: SiDatepickerDirective,
                        static: true
                    }]
            }], startInput: [{
                type: ViewChild,
                args: ['startInput', { static: true }]
            }], endInput: [{
                type: ViewChild,
                args: ['endInput', { static: true }]
            }], endDateInput: [{
                type: ViewChild,
                args: ['end', { static: true }]
            }], siDatepickerConfig: [{
                type: Input
            }], startDatePlaceholder: [{
                type: Input
            }], endDatePlaceholder: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], value: [{
                type: Input
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout']
            }] } });

/**
 * @deprecated This component should no longer be used as a native HTML solution is availalbe.
 * `<si-switch></si-switch>` can be rewritten as
 * ```html
 * <div class="from-check form-switch">
 *   <input class="form-check-input" type="checkbox" id="switch-input" role="switch">
 *   <label class="form-check-label" for="switch-input">Switch label></label>
 * </div>
 * ```
 * Expected removal in version 44.
 */
class SiSwitchComponent {
    constructor() {
        /**
         * State of switch.
         */
        this.on = false;
        /**
         * Input for disabling toggle action.
         */
        this.disabled = false;
        /**
         * Aria label needed for a11y
         */
        this.ariaLabel = 'switch';
        /**
         * Checkbox id required for accessibility
         */
        this.switchId = 'switch';
        /**
         * Callback function triggered for state changes. The emitted value shows the new state.
         */
        this.toggle = new EventEmitter();
    }
    onToggle() {
        this.toggle.emit(this.on);
    }
    onKeydownToggle() {
        this.on = !this.on;
        this.onToggle();
    }
}
SiSwitchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSwitchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSwitchComponent, selector: "si-switch", inputs: { on: "on", disabled: "disabled", ariaLabel: "ariaLabel", switchId: "switchId" }, outputs: { toggle: "toggle" }, host: { classAttribute: "form-check form-switch" }, ngImport: i0, template: "<input\n  type=\"checkbox\"\n  [id]=\"switchId\"\n  class=\"form-check-input\"\n  role=\"switch\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel | translate\"\n  [(ngModel)]=\"on\"\n  (change)=\"onToggle()\">\n", dependencies: [{ kind: "directive", type: i2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-switch', host: {
                        class: 'form-check form-switch'
                    }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<input\n  type=\"checkbox\"\n  [id]=\"switchId\"\n  class=\"form-check-input\"\n  role=\"switch\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel | translate\"\n  [(ngModel)]=\"on\"\n  (change)=\"onToggle()\">\n" }]
        }], propDecorators: { on: [{
                type: Input
            }], disabled: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], switchId: [{
                type: Input
            }], toggle: [{
                type: Output
            }] } });

class SiSwitchModule {
}
SiSwitchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSwitchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchModule, declarations: [SiSwitchComponent], imports: [FormsModule, SiTranslateModule], exports: [SiSwitchComponent] });
SiSwitchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchModule, imports: [FormsModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSwitchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FormsModule, SiTranslateModule],
                    declarations: [SiSwitchComponent],
                    exports: [SiSwitchComponent]
                }]
        }] });

class SiDatepickerModule {
}
SiDatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiDatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerModule, declarations: [SiDateInputDirective,
        SiDatepickerComponent,
        SiDatepickerDirective,
        SiDatepickerOverlayComponent,
        SiDateRangeComponent,
        SiTimepickerComponent], imports: [CommonModule,
        FormsModule,
        OverlayModule,
        SiListInteractionModule,
        SiSwitchModule,
        SiTranslateModule], exports: [SiDateInputDirective,
        SiDatepickerComponent,
        SiDatepickerDirective,
        SiDateRangeComponent,
        SiTimepickerComponent] });
SiDatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerModule, imports: [CommonModule,
        FormsModule,
        OverlayModule,
        SiListInteractionModule,
        SiSwitchModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        OverlayModule,
                        SiListInteractionModule,
                        SiSwitchModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiDateInputDirective,
                        SiDatepickerComponent,
                        SiDatepickerDirective,
                        SiDatepickerOverlayComponent,
                        SiDateRangeComponent,
                        SiTimepickerComponent
                    ],
                    exports: [
                        SiDateInputDirective,
                        SiDatepickerComponent,
                        SiDatepickerDirective,
                        SiDateRangeComponent,
                        SiTimepickerComponent
                    ]
                }]
        }] });

class SiElectrontitlebarComponent {
    constructor() {
        /**
         * Defines if the application can go back or not
         */
        this.canGoBack = false;
        /**
         * Defines if the application can go forward or not
         */
        this.canGoForward = false;
        /**
         * Defines if the application is focused or not
         */
        this.hasFocus = true;
        /**
         * List of menu items for the dropdown
         * In this dropdown should the zoom functionality as well as the refresh function be provided
         */
        this.menuItems = [];
        /**
         * Browsing forward function
         */
        this.forward = new EventEmitter();
        /**
         * Browsing back function
         */
        this.back = new EventEmitter();
        /**
         * Aria Label for Forward Button
         */
        this.ariaLabelForward = 'forward';
        /**
         * Aria Label for Back Button
         */
        this.ariaLabelBack = 'back';
        /**
         * Aria Label for Menu Button
         */
        this.ariaLabelMenu = 'menu';
    }
}
SiElectrontitlebarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiElectrontitlebarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiElectrontitlebarComponent, selector: "si-electron-titlebar", inputs: { appTitle: "appTitle", canGoBack: "canGoBack", canGoForward: "canGoForward", hasFocus: "hasFocus", menuItems: "menuItems", ariaLabelForward: "ariaLabelForward", ariaLabelBack: "ariaLabelBack", ariaLabelMenu: "ariaLabelMenu" }, outputs: { forward: "forward", back: "back" }, ngImport: i0, template: "<!-- eslint-disable @angular-eslint/template/accessibility-elements-content -->\n<div class=\"electron-title-bar-container w-100 bg-base-1 border-bottom fixed-top\">\n  <div class=\"electron-title-bar align-items-center d-flex h-100\">\n    <span [class.unfocused]=\"!hasFocus\" class=\"ms-6 me-9 si-body-2 electron-title-bar-app-name text-truncate\">{{ appTitle }}</span>\n    <button type=\"button\" [class.unfocused]=\"!hasFocus\"\n      class=\"btn btn-circle btn-xs btn-inline element-left-4 flip-rtl me-6\" [attr.aria-label]=\"ariaLabelBack\"\n      (click)=\"back.emit()\" [disabled]=\"!canGoBack\"></button>\n    <button type=\"button\" [class.unfocused]=\"!hasFocus\"\n      class=\"btn btn-circle btn-xs btn-inline element-right-4 flip-rtl me-6\" [attr.aria-label]=\"ariaLabelForward\"\n      (click)=\"forward.emit()\" [disabled]=\"!canGoForward\"></button>\n    <div *ngIf=\"menuItems.length > 0\" siDropdown #dropdown=\"si-dropdown\" class=\"btn-group me-5\" dropdownDirection=\"down\" dropdownContainer=\"body\">\n      <button siDropdownToggle type=\"button\" [class.unfocused]=\"!hasFocus\" class=\"btn btn-circle electron-title-bar-btn btn-inline btn-xs element-options-vertical me-6\" [attr.aria-label]=\"ariaLabelMenu\"></button>\n      <si-menu *ngIf=\"dropdown.isOpen\"\n        class=\"dropdown-menu m-5\"\n        siDropdownMenu\n        [items]=\"menuItems\"\n      >\n      </si-menu>\n    </div>\n    <div class=\"electron-title-bar-draggable flex-grow-1 w-100 h-100\"></div>\n  </div>\n</div>\n", styles: [".electron-title-bar-container{block-size:36px}.electron-title-bar{position:absolute;inset-inline-start:env(titlebar-area-x,0);inline-size:env(titlebar-area-width,100%)}.electron-title-bar-app-name{color:var(--element-text-secondary);-webkit-app-region:drag;-webkit-user-select:none;user-select:none}.electron-title-bar-draggable{flex-basis:content;-webkit-app-region:drag}.unfocused{color:var(--element-ui-3)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }] });
__decorate([
    Required()
], SiElectrontitlebarComponent.prototype, "appTitle", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-electron-titlebar', template: "<!-- eslint-disable @angular-eslint/template/accessibility-elements-content -->\n<div class=\"electron-title-bar-container w-100 bg-base-1 border-bottom fixed-top\">\n  <div class=\"electron-title-bar align-items-center d-flex h-100\">\n    <span [class.unfocused]=\"!hasFocus\" class=\"ms-6 me-9 si-body-2 electron-title-bar-app-name text-truncate\">{{ appTitle }}</span>\n    <button type=\"button\" [class.unfocused]=\"!hasFocus\"\n      class=\"btn btn-circle btn-xs btn-inline element-left-4 flip-rtl me-6\" [attr.aria-label]=\"ariaLabelBack\"\n      (click)=\"back.emit()\" [disabled]=\"!canGoBack\"></button>\n    <button type=\"button\" [class.unfocused]=\"!hasFocus\"\n      class=\"btn btn-circle btn-xs btn-inline element-right-4 flip-rtl me-6\" [attr.aria-label]=\"ariaLabelForward\"\n      (click)=\"forward.emit()\" [disabled]=\"!canGoForward\"></button>\n    <div *ngIf=\"menuItems.length > 0\" siDropdown #dropdown=\"si-dropdown\" class=\"btn-group me-5\" dropdownDirection=\"down\" dropdownContainer=\"body\">\n      <button siDropdownToggle type=\"button\" [class.unfocused]=\"!hasFocus\" class=\"btn btn-circle electron-title-bar-btn btn-inline btn-xs element-options-vertical me-6\" [attr.aria-label]=\"ariaLabelMenu\"></button>\n      <si-menu *ngIf=\"dropdown.isOpen\"\n        class=\"dropdown-menu m-5\"\n        siDropdownMenu\n        [items]=\"menuItems\"\n      >\n      </si-menu>\n    </div>\n    <div class=\"electron-title-bar-draggable flex-grow-1 w-100 h-100\"></div>\n  </div>\n</div>\n", styles: [".electron-title-bar-container{block-size:36px}.electron-title-bar{position:absolute;inset-inline-start:env(titlebar-area-x,0);inline-size:env(titlebar-area-width,100%)}.electron-title-bar-app-name{color:var(--element-text-secondary);-webkit-app-region:drag;-webkit-user-select:none;user-select:none}.electron-title-bar-draggable{flex-basis:content;-webkit-app-region:drag}.unfocused{color:var(--element-ui-3)}\n"] }]
        }], propDecorators: { appTitle: [{
                type: Input
            }], canGoBack: [{
                type: Input
            }], canGoForward: [{
                type: Input
            }], hasFocus: [{
                type: Input
            }], menuItems: [{
                type: Input
            }], forward: [{
                type: Output
            }], back: [{
                type: Output
            }], ariaLabelForward: [{
                type: Input
            }], ariaLabelBack: [{
                type: Input
            }], ariaLabelMenu: [{
                type: Input
            }] } });

class SiElectrontitlebarModule {
}
SiElectrontitlebarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiElectrontitlebarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarModule, declarations: [SiElectrontitlebarComponent], imports: [CommonModule,
        SiDropdownModule,
        SiMenuModule], exports: [SiElectrontitlebarComponent] });
SiElectrontitlebarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarModule, imports: [CommonModule,
        SiDropdownModule,
        SiMenuModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiElectrontitlebarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiElectrontitlebarComponent],
                    imports: [
                        CommonModule,
                        SiDropdownModule,
                        SiMenuModule
                    ],
                    exports: [SiElectrontitlebarComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

class SiPaginationComponent {
    constructor() {
        /**
         * The current active page, counting starts from 1
         */
        this.currentPage = 1;
        /**
         * The text of the back button for pagination. Required for a11y.
         */
        this.backButtonText = 'back';
        /**
         * The text of the forward button for pagination. Required for a11y.
         */
        this.forwardButtonText = 'forward';
        /**
         * When multiple paginations are used on the same page each pagination needs a distinct aria label.
         * Required for a11y.
         */
        this.navAriaLabel = 'Pagination';
        /**
         * Fired when the user changes the page
         */
        this.currentPageChange = new EventEmitter();
        this.pageButtons = [];
        this.prevDisabled = false;
        this.nextDisabled = false;
        this.trackByIndex = (index) => index;
    }
    ngOnChanges() {
        this.createPages();
    }
    createPages() {
        this.pageButtons = [];
        const totalPages = this.totalPages ?? Math.ceil((this.totalRowCount ?? 0) / (this.pageSize ?? 1));
        if (totalPages <= SiPaginationComponent.maxItems) {
            for (let i = 0; i < totalPages; i++) {
                this.pageButtons.push({ page: i + 1, sep: false });
            }
        }
        else if (this.currentPage < SiPaginationComponent.maxItems - 2) {
            // separator only on the right (in LTR)
            for (let i = 0; i < SiPaginationComponent.maxItems - 2; i++) {
                this.pageButtons.push({ page: i + 1, sep: false });
            }
            this.pageButtons.push({ page: 0, sep: true });
            this.pageButtons.push({ page: totalPages, sep: false });
        }
        else if (this.currentPage >= totalPages - 3) {
            // separator only on the left (in LTR)
            this.pageButtons.push({ page: 1, sep: false });
            this.pageButtons.push({ page: 0, sep: true });
            for (let i = totalPages - SiPaginationComponent.maxItems + 2; i < totalPages; i++) {
                this.pageButtons.push({ page: i + 1, sep: false });
            }
        }
        else {
            // separator on both sides
            this.pageButtons.push({ page: 1, sep: false });
            this.pageButtons.push({ page: 0, sep: true });
            for (let i = -1; i <= 1; i++) {
                this.pageButtons.push({ page: this.currentPage + i, sep: false });
            }
            this.pageButtons.push({ page: 0, sep: true });
            this.pageButtons.push({ page: totalPages, sep: false });
        }
        this.prevDisabled = this.currentPage === 1;
        this.nextDisabled = this.currentPage === totalPages;
    }
    direction(event, delta) {
        this.setPage(event, this.currentPage + delta);
    }
    setPage(event, page) {
        event.currentTarget.blur();
        this.currentPage = page;
        this.createPages();
        this.currentPageChange.emit(page);
    }
}
SiPaginationComponent.maxItems = 7;
SiPaginationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiPaginationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiPaginationComponent, selector: "si-pagination", inputs: { totalPages: "totalPages", currentPage: "currentPage", pageSize: "pageSize", totalRowCount: "totalRowCount", backButtonText: "backButtonText", forwardButtonText: "forwardButtonText", navAriaLabel: "navAriaLabel" }, outputs: { currentPageChange: "currentPageChange" }, usesOnChanges: true, ngImport: i0, template: "<nav role=\"navigation\" [attr.aria-label]=\"navAriaLabel | translate\">\n  <ul class=\"pagination\">\n    <li class=\"me-5\">\n      <button type=\"button\"\n        class=\"btn nav-button border-0 mx-4\"\n        [disabled]=\"prevDisabled\"\n        (click)=\"direction($event, -1)\"\n        [attr.aria-label]=\"backButtonText | translate\"\n      >\n        <i class=\"element-left-3 flip-rtl\"></i>\n      </button>\n    </li>\n    <li *ngFor=\"let page of pageButtons; trackBy: trackByIndex\">\n      <button *ngIf=\"!page.sep\"\n        type=\"button\"\n        class=\"btn page-button border-0 mx-1 si-title-2 px-2\"\n        [attr.aria-current]=\"page.page === currentPage ? 'page' : ''\"\n        [disabled]=\"page.page === currentPage\"\n        (click)=\"setPage($event, page.page)\"\n      >\n        {{ page.page }}\n      </button>\n      <span *ngIf=\"page.sep\" class=\"separator mx-2\">&hellip;</span>\n    </li>\n    <li class=\"ms-5\">\n      <button type=\"button\"\n        class=\"btn nav-button border-0 mx-4\"\n        [disabled]=\"nextDisabled\"\n        (click)=\"direction($event, 1)\"\n        [attr.aria-label]=\"forwardButtonText | translate\"\n      >\n        <i class=\"element-right-3 flip-rtl\"></i>\n      </button>\n    </li>\n  </ul>\n</nav>\n", styles: [".pagination{display:flex;align-items:center;block-size:40px}.page-button,.nav-button,.separator{display:flex;align-items:center;justify-content:center;border-radius:12px;min-inline-size:24px!important;block-size:24px;padding-block:0;padding-inline:2px;background:transparent;font-size:.875rem}.nav-button{font-size:1.5rem;color:var(--element-text-primary);inline-size:24px}.nav-button:hover{background:var(--element-base-1-selected)}.nav-button:disabled{opacity:var(--element-action-disabled-opacity)}.page-button:hover{background:var(--element-base-1-selected)}.page-button:disabled{background:var(--element-base-1-selected);opacity:1}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-pagination', changeDetection: ChangeDetectionStrategy.OnPush, template: "<nav role=\"navigation\" [attr.aria-label]=\"navAriaLabel | translate\">\n  <ul class=\"pagination\">\n    <li class=\"me-5\">\n      <button type=\"button\"\n        class=\"btn nav-button border-0 mx-4\"\n        [disabled]=\"prevDisabled\"\n        (click)=\"direction($event, -1)\"\n        [attr.aria-label]=\"backButtonText | translate\"\n      >\n        <i class=\"element-left-3 flip-rtl\"></i>\n      </button>\n    </li>\n    <li *ngFor=\"let page of pageButtons; trackBy: trackByIndex\">\n      <button *ngIf=\"!page.sep\"\n        type=\"button\"\n        class=\"btn page-button border-0 mx-1 si-title-2 px-2\"\n        [attr.aria-current]=\"page.page === currentPage ? 'page' : ''\"\n        [disabled]=\"page.page === currentPage\"\n        (click)=\"setPage($event, page.page)\"\n      >\n        {{ page.page }}\n      </button>\n      <span *ngIf=\"page.sep\" class=\"separator mx-2\">&hellip;</span>\n    </li>\n    <li class=\"ms-5\">\n      <button type=\"button\"\n        class=\"btn nav-button border-0 mx-4\"\n        [disabled]=\"nextDisabled\"\n        (click)=\"direction($event, 1)\"\n        [attr.aria-label]=\"forwardButtonText | translate\"\n      >\n        <i class=\"element-right-3 flip-rtl\"></i>\n      </button>\n    </li>\n  </ul>\n</nav>\n", styles: [".pagination{display:flex;align-items:center;block-size:40px}.page-button,.nav-button,.separator{display:flex;align-items:center;justify-content:center;border-radius:12px;min-inline-size:24px!important;block-size:24px;padding-block:0;padding-inline:2px;background:transparent;font-size:.875rem}.nav-button{font-size:1.5rem;color:var(--element-text-primary);inline-size:24px}.nav-button:hover{background:var(--element-base-1-selected)}.nav-button:disabled{opacity:var(--element-action-disabled-opacity)}.page-button:hover{background:var(--element-base-1-selected)}.page-button:disabled{background:var(--element-base-1-selected);opacity:1}\n"] }]
        }], propDecorators: { totalPages: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], totalRowCount: [{
                type: Input
            }], backButtonText: [{
                type: Input
            }], forwardButtonText: [{
                type: Input
            }], navAriaLabel: [{
                type: Input
            }], currentPageChange: [{
                type: Output
            }] } });

class SiPaginationModule {
}
SiPaginationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiPaginationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationModule, declarations: [SiPaginationComponent], imports: [CommonModule, SiTranslateModule], exports: [SiPaginationComponent] });
SiPaginationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiPaginationComponent],
                    exports: [SiPaginationComponent]
                }]
        }] });

class SiProgressbarComponent {
    constructor() {
        /**
         * Needed for a11y
         */
        this.ariaLabel = 'progress';
        /**
         * Max value for progressbar
         */
        this.max = 100;
        /**
         * Current value
         */
        this.value = 0;
        /**
         * Height for progress bar.
         */
        this.height = 'normal';
        this.percent = 0;
    }
    ngOnChanges(changes) {
        if (changes.value || changes.max) {
            this.percent = 100 * Number(this.value ?? 0) / Number(this.max ?? 100);
        }
    }
}
SiProgressbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiProgressbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiProgressbarComponent, selector: "si-progressbar", inputs: { ariaLabel: "ariaLabel", max: "max", value: "value", heading: "heading", progress: "progress", height: "height" }, usesOnChanges: true, ngImport: i0, template: "<div\n  [class.justify-content-end]=\"!heading\"\n  [class.justify-content-between]=\"heading\"\n  class=\"d-flex align-items-baseline\"\n  *ngIf=\"heading || progress\"\n>\n  <span class=\"si-title-2 mb-2\" *ngIf=\"heading\">{{ heading | translate }}</span>\n  <small *ngIf=\"progress\" class=\"text-nowrap\">{{ progress }}</small>\n</div>\n<div [class.si-progress-small]=\"height === 'small'\" class=\"progress\">\n  <div\n    class=\"progress-bar\"\n    role=\"progressbar\"\n    [attr.aria-valuemin]=\"0\"\n    [attr.aria-valuemax]=\"max\"\n    [attr.aria-valuenow]=\"value\"\n    [attr.aria-valuetext]=\"value !== undefined ? percent.toFixed(0) + '%' : null\"\n    [attr.aria-label]=\"ariaLabel | translate\"\n    [style.width.%]=\"percent\"\n  >\n  </div>\n</div>\n", styles: [".si-progress-small{block-size:.125rem;border-radius:.0625rem}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-progressbar', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  [class.justify-content-end]=\"!heading\"\n  [class.justify-content-between]=\"heading\"\n  class=\"d-flex align-items-baseline\"\n  *ngIf=\"heading || progress\"\n>\n  <span class=\"si-title-2 mb-2\" *ngIf=\"heading\">{{ heading | translate }}</span>\n  <small *ngIf=\"progress\" class=\"text-nowrap\">{{ progress }}</small>\n</div>\n<div [class.si-progress-small]=\"height === 'small'\" class=\"progress\">\n  <div\n    class=\"progress-bar\"\n    role=\"progressbar\"\n    [attr.aria-valuemin]=\"0\"\n    [attr.aria-valuemax]=\"max\"\n    [attr.aria-valuenow]=\"value\"\n    [attr.aria-valuetext]=\"value !== undefined ? percent.toFixed(0) + '%' : null\"\n    [attr.aria-label]=\"ariaLabel | translate\"\n    [style.width.%]=\"percent\"\n  >\n  </div>\n</div>\n", styles: [".si-progress-small{block-size:.125rem;border-radius:.0625rem}\n"] }]
        }], propDecorators: { ariaLabel: [{
                type: Input
            }], max: [{
                type: Input
            }], value: [{
                type: Input
            }], heading: [{
                type: Input
            }], progress: [{
                type: Input
            }], height: [{
                type: Input
            }] } });

class TooltipComponent {
    constructor() {
        this.tooltipText = '';
        this.tooltipPositionClass = '';
        this.zone = inject(NgZone);
    }
    updateTooltipPosition(change) {
        const arrowClassTooltip = `tooltip-${change.connectionPair.overlayX}-${change.connectionPair.overlayY}`;
        const offsetX = change.connectionPair.offsetX;
        if (offsetX) {
            if (offsetX < 0) {
                // Need to subtract half of the width of arrow to get the center
                this.left = -1 * (offsetX + 6);
            }
            else {
                // Need to subtract half of the width of arrow to get the center
                this.right = (offsetX - 6);
            }
        }
        if (arrowClassTooltip !== this.tooltipPositionClass) {
            this.zone.run(() => this.tooltipPositionClass = arrowClassTooltip);
        }
    }
}
TooltipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: TooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TooltipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: TooltipComponent, selector: "si-tooltip", inputs: { tooltipText: "tooltipText", anchor: "anchor", positions: "positions" }, ngImport: i0, template: "<div class=\"tooltip show position-relative\" [ngClass]=\"tooltipPositionClass\">\n  <div class=\"tooltip-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div class=\"tooltip-inner si-body-2\">{{ tooltipText }}</div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-tooltip', template: "<div class=\"tooltip show position-relative\" [ngClass]=\"tooltipPositionClass\">\n  <div class=\"tooltip-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div class=\"tooltip-inner si-body-2\">{{ tooltipText }}</div>\n</div>\n" }]
        }], propDecorators: { tooltipText: [{
                type: Input
            }], anchor: [{
                type: Input
            }], positions: [{
                type: Input
            }] } });

class SiTooltipDirective {
    constructor() {
        /**
         * The tooltip text to be displayed
         */
        this.siTooltip = '';
        this.overlay = inject(Overlay);
        this.elementRef = inject(ElementRef);
    }
    /**
     * The placement of the tooltip. One of 'top', 'start', end', 'bottom'
     */
    set placement(value) {
        const deprecatedMessage = '@simpl/element-ng - SiTooltipDirective: \
Setting placement to "left" or "right" is deprecated, use "start" or "end" instead';
        if (value === 'left') {
            console.warn(deprecatedMessage);
            this._placement = 'start';
        }
        else if (value === 'right') {
            console.warn(deprecatedMessage);
            this._placement = 'end';
        }
        else {
            this._placement = value;
        }
    }
    get placement() {
        return this._placement;
    }
    ngOnInit() {
        if (!this.placement || !positions[this.placement]) {
            this.placement = 'auto';
        }
    }
    ngOnDestroy() {
        this.overlayref?.dispose();
    }
    showTooltip() {
        if (!this.overlayref?.hasAttached()) {
            this.overlayref = getOverlay(this.elementRef, this.overlay, false, this.placement);
        }
        if (this.overlayref.hasAttached()) {
            return;
        }
        const toolTipPortal = new ComponentPortal(TooltipComponent);
        const tooltipRef = this.overlayref.attach(toolTipPortal);
        tooltipRef.instance.tooltipText = this.siTooltip;
        tooltipRef.instance.anchor = new CdkOverlayOrigin(this.elementRef);
        tooltipRef.instance.positions = positions[this.placement];
        const positionStrategy = getPositionStrategy(this.overlayref);
        positionStrategy?.positionChanges.subscribe(change => tooltipRef.instance.updateTooltipPosition(change));
        tooltipRef.changeDetectorRef.detectChanges();
    }
    focusIn() {
        this.showTooltip();
    }
    show() {
        if (this.triggers === 'focus') {
            return;
        }
        this.showTooltip();
    }
    hide() {
        this.overlayref?.detach();
    }
    mouseOut() {
        if (this.triggers === 'focus') {
            return;
        }
        this.hide();
    }
}
SiTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiTooltipDirective, selector: "[siTooltip]", inputs: { siTooltip: "siTooltip", placement: "placement", triggers: "triggers" }, host: { listeners: { "focus": "focusIn()", "mouseenter": "show()", "touchstart": "hide()", "focusout": "hide()", "mouseleave": "mouseOut()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siTooltip]'
                }]
        }], propDecorators: { siTooltip: [{
                type: Input
            }], placement: [{
                type: Input
            }], triggers: [{
                type: Input
            }], focusIn: [{
                type: HostListener,
                args: ['focus']
            }], show: [{
                type: HostListener,
                args: ['mouseenter']
            }], hide: [{
                type: HostListener,
                args: ['touchstart']
            }, {
                type: HostListener,
                args: ['focusout']
            }], mouseOut: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class SiTooltipModule {
}
SiTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipModule, declarations: [SiTooltipDirective, TooltipComponent], imports: [CommonModule, OverlayModule], exports: [SiTooltipDirective, TooltipComponent] });
SiTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipModule, imports: [CommonModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [SiTooltipDirective, TooltipComponent],
                    exports: [SiTooltipDirective, TooltipComponent]
                }]
        }] });

/**
 * @deprecated use the Angular built-in feature instead:`<a routerLinkActive ariaCurrentWhenActive="page">`
 *
 * @see https://github.com/angular/angular/pull/45167
 */
class SiAriaCurrentDirective {
    constructor() {
        this.routerLink = inject(RouterLinkActive, { host: true });
    }
    get isActive() {
        return this.routerLink.isActive;
    }
    get ariaCurrent() {
        return this.isActive ? this.siAriaCurrent ? this.siAriaCurrent : 'true' : 'false';
    }
}
SiAriaCurrentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiAriaCurrentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiAriaCurrentDirective, selector: "[siAriaCurrent]", inputs: { siAriaCurrent: "siAriaCurrent" }, host: { properties: { "attr.aria-current": "this.ariaCurrent" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAriaCurrent]'
                }]
        }], propDecorators: { siAriaCurrent: [{
                type: Input
            }], ariaCurrent: [{
                type: HostBinding,
                args: ['attr.aria-current']
            }] } });

/**
 * @deprecated use the Angular built-in feature instead:`<a routerLinkActive ariaCurrentWhenActive="page">`
 *
 * @see https://github.com/angular/angular/pull/45167
 */
class SiAriaCurrentModule {
}
SiAriaCurrentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiAriaCurrentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentModule, declarations: [SiAriaCurrentDirective], imports: [CommonModule], exports: [SiAriaCurrentDirective] });
SiAriaCurrentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAriaCurrentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SiAriaCurrentDirective
                    ],
                    exports: [SiAriaCurrentDirective]
                }]
        }] });

class PopoverComponent {
    constructor() {
        this.popoverTitle = '';
        this.containerClass = '';
        this.popoverPositionClass = '';
        this.isTemplate = false;
        this.zone = inject(NgZone);
    }
    ngOnInit() {
        if (this.popover instanceof TemplateRef) {
            this.isTemplate = true;
            this.popoverTemplate = this.popover;
        }
    }
    updateArrow(change) {
        let popoverClassTooltip = `popover-${change.connectionPair.overlayX}-${change.connectionPair.overlayY}`;
        if (this.containerClass) {
            popoverClassTooltip += ' ' + this.containerClass;
        }
        const offsetX = change.connectionPair.offsetX;
        if (offsetX) {
            if (offsetX < 0) {
                // Need to subtract half of the width of arrow to get the center
                this.left = -1 * (offsetX + 6);
            }
            else {
                // Need to subtract half of the width of arrow to get the center
                this.right = (offsetX - 6);
            }
        }
        if (popoverClassTooltip !== this.popoverPositionClass) {
            this.zone.run(() => this.popoverPositionClass = popoverClassTooltip);
        }
    }
}
PopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: PopoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: PopoverComponent, selector: "si-popover", inputs: { popover: "popover", popoverTitle: "popoverTitle", anchor: "anchor", positions: "positions", containerClass: "containerClass", icon: "icon" }, ngImport: i0, template: "<div class=\"popover position-relative\" [ngClass]=\"popoverPositionClass\">\n  <div class=\"popover-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div *ngIf=\"popoverTitle\" class=\"popover-header\">\n    <span *ngIf=\"icon\" class=\"icon me-2\" [ngClass]=\"icon\"></span>\n    <span class=\"si-title-2\">{{ popoverTitle }}</span>\n  </div>\n  <div class=\"popover-body\">\n    <div *ngIf=\"!isTemplate; else templateRef\" class=\"si-body-2\" [innerHTML]=\"popover\"></div>\n    <ng-template #templateRef>\n      <ng-template *ngTemplateOutlet=\"popoverTemplate\"></ng-template>\n    </ng-template>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-popover', template: "<div class=\"popover position-relative\" [ngClass]=\"popoverPositionClass\">\n  <div class=\"popover-arrow flip-rtl\" [style.left.px]=\"left\" [style.right.px]=\"right\"></div>\n  <div *ngIf=\"popoverTitle\" class=\"popover-header\">\n    <span *ngIf=\"icon\" class=\"icon me-2\" [ngClass]=\"icon\"></span>\n    <span class=\"si-title-2\">{{ popoverTitle }}</span>\n  </div>\n  <div class=\"popover-body\">\n    <div *ngIf=\"!isTemplate; else templateRef\" class=\"si-body-2\" [innerHTML]=\"popover\"></div>\n    <ng-template #templateRef>\n      <ng-template *ngTemplateOutlet=\"popoverTemplate\"></ng-template>\n    </ng-template>\n  </div>\n</div>\n" }]
        }], propDecorators: { popover: [{
                type: Input
            }], popoverTitle: [{
                type: Input
            }], anchor: [{
                type: Input
            }], positions: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], icon: [{
                type: Input
            }] } });

class SiPopoverDirective {
    constructor() {
        /**
         * The trigger event on which the popover shall be displayed
         */
        this.triggers = 'click';
        /**
         * The title to be displayed on top for the popover
         */
        this.popoverTitle = '';
        /**
         * The class that will be applied to container of the popover
         */
        this.containerClass = '';
        /**
         * The flag determines whether to close popover on clicking outside
         */
        this.outsideClick = true;
        /**
         * Specify whether or not the popover is currently shown
         */
        this.isOpen = false;
        /**
         * Emits an event when the popover is shown
         */
        this.shown = new EventEmitter();
        /**
         * Emits an event when the popover is hidden
         */
        this.hidden = new EventEmitter();
        this.overlay = inject(Overlay);
        this.elementRef = inject(ElementRef);
    }
    /**
     * The placement of the popover. One of 'top', 'start', end', 'bottom'
     */
    set placement(value) {
        const deprecatedMessage = '@simpl/element-ng - SiPopoverDirective: \
Setting placement to "left" or "right" is deprecated, use "start" or "end" instead';
        if (value === 'left') {
            console.warn(deprecatedMessage);
            this._placement = 'start';
        }
        else if (value === 'right') {
            console.warn(deprecatedMessage);
            this._placement = 'end';
        }
        else {
            this._placement = value;
        }
    }
    get placement() {
        return this._placement;
    }
    ngOnInit() {
        if (this.placement !== 'top' && this.placement !== 'bottom' &&
            this.placement !== 'start' && this.placement !== 'end') {
            this.placement = 'auto';
        }
        if (this.isOpen) {
            this.show();
        }
    }
    ngOnDestroy() {
        this.overlayref?.dispose();
    }
    show() {
        if (!this.overlayref?.hasAttached()) {
            const backdrop = this.outsideClick && !hasTrigger('focus', this.triggers) && !hasTrigger('hover', this.triggers);
            this.overlayref = getOverlay(this.elementRef, this.overlay, backdrop, this.placement);
            if (backdrop) {
                this.overlayref.backdropClick().subscribe(() => this.hide());
            }
        }
        if (this.overlayref.hasAttached()) {
            return;
        }
        const popoverPortal = new ComponentPortal(PopoverComponent);
        const popoverRef = this.overlayref.attach(popoverPortal);
        popoverRef.instance.popover = this.siPopover;
        popoverRef.instance.popoverTitle = this.popoverTitle;
        popoverRef.instance.anchor = new CdkOverlayOrigin(this.elementRef);
        popoverRef.instance.positions = positions[this.placement];
        popoverRef.instance.icon = this.icon;
        popoverRef.instance.containerClass = this.containerClass;
        const positionStrategy = getPositionStrategy(this.overlayref);
        positionStrategy?.positionChanges.subscribe(change => popoverRef.instance.updateArrow(change));
        popoverRef.changeDetectorRef.detectChanges();
        this.shown.emit();
    }
    hide() {
        this.overlayref?.detach();
        this.hidden.emit();
    }
    updatePosition() {
        this.overlayref?.updatePosition();
    }
    onTrigger(trigger) {
        if (hasTrigger(trigger, this.triggers)) {
            if (this.overlayref?.hasAttached()) {
                this.hide();
            }
            else {
                this.show();
            }
        }
    }
    focusOut() {
        if (hasTrigger('focus', this.triggers)) {
            if (this.outsideClick) {
                this.hide();
            }
        }
    }
}
SiPopoverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiPopoverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiPopoverDirective, selector: "[siPopover]", inputs: { siPopover: "siPopover", placement: "placement", triggers: "triggers", popoverTitle: "popoverTitle", containerClass: "containerClass", outsideClick: "outsideClick", icon: "icon", isOpen: "isOpen" }, outputs: { shown: "shown", hidden: "hidden" }, host: { listeners: { "mouseenter": "onTrigger(\"hover\")", "mouseleave": "onTrigger(\"hover\")", "focus": "onTrigger(\"focus\")", "click": "onTrigger(\"click\")", "touchstart": "focusOut()", "focusout": "focusOut()" } }, exportAs: ["si-popover"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siPopover]',
                    exportAs: 'si-popover'
                }]
        }], propDecorators: { siPopover: [{
                type: Input
            }], placement: [{
                type: Input
            }], triggers: [{
                type: Input
            }], popoverTitle: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], outsideClick: [{
                type: Input
            }], icon: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], onTrigger: [{
                type: HostListener,
                args: ['mouseenter', ['"hover"']]
            }, {
                type: HostListener,
                args: ['mouseleave', ['"hover"']]
            }, {
                type: HostListener,
                args: ['focus', ['"focus"']]
            }, {
                type: HostListener,
                args: ['click', ['"click"']]
            }], focusOut: [{
                type: HostListener,
                args: ['touchstart']
            }, {
                type: HostListener,
                args: ['focusout']
            }] } });

class SiPopoverModule {
}
SiPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverModule, declarations: [PopoverComponent, SiPopoverDirective], imports: [CommonModule, OverlayModule], exports: [PopoverComponent, SiPopoverDirective] });
SiPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverModule, imports: [CommonModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [PopoverComponent, SiPopoverDirective],
                    exports: [PopoverComponent, SiPopoverDirective]
                }]
        }] });

class SiProgressbarModule {
}
SiProgressbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiProgressbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarModule, declarations: [SiProgressbarComponent], imports: [CommonModule, SiTranslateModule], exports: [SiProgressbarComponent] });
SiProgressbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiProgressbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiProgressbarComponent],
                    exports: [SiProgressbarComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiPromptDialogButtonsComponent {
    constructor() {
        /**
         * Input for disabling cancel button.
         */
        this.cancelDisabled = false;
        /**
         * Input for disabling confirm button.
         */
        this.confirmDisabled = false;
        /**
         * Input for disabling save button.
         */
        this.saveDisabled = false;
        /**
         * Text for aria-label of cancel button. Needed for a11y.
         */
        this.cancelLabel = 'cancel';
        /**
         * Text for aria-label of confirm button. Needed for a11y.
         */
        this.confirmLabel = 'confirm';
        /**
         * Text for aria-label of save button. Needed for a11y.
         */
        this.saveLabel = 'save';
        /**
         * Output callback event notifying you when the cancel button is pushed.
         */
        this.cancel = new EventEmitter();
        /**
         * Output callback event that is trigger on confirm.
         */
        this.confirm = new EventEmitter();
        /**
         * Output callback event this triggers when the save button was pressed.
         */
        this.save = new EventEmitter();
        this.showSave = false;
    }
    ngOnInit() {
        this.showSave = this.save.observers.length > 0;
    }
    clickCancel() {
        this.cancel.emit();
    }
    clickConfirm() {
        this.confirm.emit();
    }
    clickSave() {
        this.save.emit();
    }
}
SiPromptDialogButtonsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiPromptDialogButtonsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiPromptDialogButtonsComponent, selector: "si-prompt-dialog-buttons", inputs: { cancelDisabled: "cancelDisabled", confirmDisabled: "confirmDisabled", saveDisabled: "saveDisabled", cancelLabel: "cancelLabel", confirmLabel: "confirmLabel", saveLabel: "saveLabel" }, outputs: { cancel: "cancel", confirm: "confirm", save: "save" }, ngImport: i0, template: "<div class=\"prompt-dialog\">\n  <button type=\"button\"\n    *ngIf=\"showSave\"\n    class=\"element-save btn btn-circle btn-sm btn-secondary\"\n    [disabled]=\"saveDisabled\"\n    (click)=\"clickSave()\"\n    [attr.aria-label]=\"saveLabel | translate\"\n  ></button>\n\n  <div>\n    <button type=\"button\"\n      class=\"element-cancel btn btn-circle btn-sm btn-secondary\"\n      [disabled]=\"cancelDisabled\"\n      (click)=\"clickCancel()\"\n      [attr.aria-label]=\"cancelLabel | translate\"\n    ></button>\n    <button type=\"button\"\n      class=\"element-ok btn btn-circle btn-sm btn-primary ms-4\"\n      [disabled]=\"confirmDisabled\"\n      (click)=\"clickConfirm()\"\n      [attr.aria-label]=\"confirmLabel | translate\"\n    ></button>\n  </div>\n</div>\n", styles: [".prompt-dialog{display:flex;flex-direction:row;justify-content:space-between}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-prompt-dialog-buttons', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"prompt-dialog\">\n  <button type=\"button\"\n    *ngIf=\"showSave\"\n    class=\"element-save btn btn-circle btn-sm btn-secondary\"\n    [disabled]=\"saveDisabled\"\n    (click)=\"clickSave()\"\n    [attr.aria-label]=\"saveLabel | translate\"\n  ></button>\n\n  <div>\n    <button type=\"button\"\n      class=\"element-cancel btn btn-circle btn-sm btn-secondary\"\n      [disabled]=\"cancelDisabled\"\n      (click)=\"clickCancel()\"\n      [attr.aria-label]=\"cancelLabel | translate\"\n    ></button>\n    <button type=\"button\"\n      class=\"element-ok btn btn-circle btn-sm btn-primary ms-4\"\n      [disabled]=\"confirmDisabled\"\n      (click)=\"clickConfirm()\"\n      [attr.aria-label]=\"confirmLabel | translate\"\n    ></button>\n  </div>\n</div>\n", styles: [".prompt-dialog{display:flex;flex-direction:row;justify-content:space-between}\n"] }]
        }], propDecorators: { cancelDisabled: [{
                type: Input
            }], confirmDisabled: [{
                type: Input
            }], saveDisabled: [{
                type: Input
            }], cancelLabel: [{
                type: Input
            }], confirmLabel: [{
                type: Input
            }], saveLabel: [{
                type: Input
            }], cancel: [{
                type: Output
            }], confirm: [{
                type: Output
            }], save: [{
                type: Output
            }] } });

class SiPromptDialogButtonsModule {
}
SiPromptDialogButtonsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiPromptDialogButtonsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsModule, declarations: [SiPromptDialogButtonsComponent], imports: [CommonModule, SiTranslateModule], exports: [SiPromptDialogButtonsComponent] });
SiPromptDialogButtonsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsModule, imports: [CommonModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiPromptDialogButtonsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiPromptDialogButtonsComponent],
                    exports: [SiPromptDialogButtonsComponent]
                }]
        }] });

class SiEmptyStateComponent {
}
SiEmptyStateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiEmptyStateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiEmptyStateComponent, selector: "si-empty-state", inputs: { icon: "icon", heading: "heading", content: "content" }, ngImport: i0, template: "<div class=\"list-group-item-empty\">\n  <span class=\"state-icon\" [ngClass]=\"icon\"></span>\n  <h3 class=\"my-6\">{{ heading | translate }}</h3>\n  <p\n    *ngIf=\"content\"\n    class=\"text-pre-wrap text-center mb-6\"\n  >{{ content | translate }}</p>\n  <ng-content></ng-content>\n</div>\n", styles: [".list-group-item-empty{block-size:100%;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:16px}.state-icon{color:var(--element-ui-2);font-size:80px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    Required()
], SiEmptyStateComponent.prototype, "icon", void 0);
__decorate([
    Required()
], SiEmptyStateComponent.prototype, "heading", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-empty-state', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"list-group-item-empty\">\n  <span class=\"state-icon\" [ngClass]=\"icon\"></span>\n  <h3 class=\"my-6\">{{ heading | translate }}</h3>\n  <p\n    *ngIf=\"content\"\n    class=\"text-pre-wrap text-center mb-6\"\n  >{{ content | translate }}</p>\n  <ng-content></ng-content>\n</div>\n", styles: [".list-group-item-empty{block-size:100%;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:16px}.state-icon{color:var(--element-ui-2);font-size:80px}\n"] }]
        }], propDecorators: { icon: [{
                type: Input
            }], heading: [{
                type: Input
            }], content: [{
                type: Input
            }] } });

class SiEmptyStateModule {
}
SiEmptyStateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiEmptyStateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateModule, declarations: [SiEmptyStateComponent], imports: [CommonModule,
        SiTranslateModule], exports: [SiEmptyStateComponent] });
SiEmptyStateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateModule, imports: [CommonModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEmptyStateModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiTranslateModule
                    ],
                    declarations: [SiEmptyStateComponent],
                    exports: [SiEmptyStateComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiFilterPillComponent {
    constructor() {
        /**
         * True if variant is responsive
         */
        this.responsive = false;
        this.disabled = false;
        this.totalPills = 0;
        /**
         * Output callback event which will provide you the name of the deleted filter
         * pill if a filter was deleted.
         */
        this.deleteFilters = new EventEmitter();
    }
    deleteClicked() {
        this.deleteFilters.emit(this.filter);
    }
}
SiFilterPillComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterPillComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFilterPillComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFilterPillComponent, selector: "si-filter-pill", inputs: { filter: "filter", responsive: "responsive", disabled: "disabled", totalPills: "totalPills" }, outputs: { deleteFilters: "deleteFilters" }, ngImport: i0, template: "<div\n  class=\"filter-pill\"\n  [class.disabled]=\"disabled\"\n  [class.responsive]=\"responsive\"\n  [ngClass]=\"filter.status ? 'filter-pill-' + filter.status : ''\"\n  [attr.aria-disabled]=\"disabled\"\n>\n  <div class=\"wrapper\">\n    <div\n      *ngIf=\"filter.title\"\n      class=\"name\"\n      [class.empty-desc]=\"!filter.description && !responsive\"\n    >{{ filter.title | translate }}</div>\n    <span\n      class=\"name pe-4\"\n      *ngIf=\"responsive && filter.title && filter.description\"\n    >:</span>\n    <div class=\"value\">{{ filter.description || '' | translate }}</div>\n    <div *ngIf=\"!filter.title && !responsive\">&#160;</div>\n  </div>\n  <ng-container *ngIf=\"!responsive || !disabled\">\n    <ng-container *ngTemplateOutlet=\"remove\"></ng-container>\n  </ng-container>\n</div>\n<ng-template #remove>\n  <button\n    type=\"button\"\n    class=\"btn-remove element-cancel btn btn-circle btn-ghost focus-inside\"\n    [class.ms-0]=\"responsive\"\n    [class.ms-2]=\"!responsive\"\n    [class.btn-xs]=\"responsive\"\n    [class.btn-sm]=\"!responsive\"\n    role=\"button\"\n    aria-label=\"Remove\"\n    (keydown.enter)=\"deleteClicked()\"\n    (click)=\"deleteClicked()\">\n  </button>\n</ng-template>\n", styles: [":host{--filter-pill-background-color: var(--element-base-1)}:host.dark-background{--filter-pill-background-color: var(--element-base-0)}.filter-pill{margin-inline-start:8px;font-weight:500;block-size:32px;border-radius:16px;background:var(--filter-pill-background-color);display:flex;padding-block:0 4px;padding-inline:16px 0;line-height:12px}.filter-pill:not(.responsive) .name{padding-block-start:4px}.wrapper{display:flex;flex-direction:column}.filter-pill.responsive{display:flex;align-items:center;padding-block:0;padding-inline:8px 0;block-size:24px}.filter-pill.responsive .wrapper{flex-direction:row}.filter-pill.responsive .name{font-weight:700;white-space:nowrap}.filter-pill.responsive .value{color:var(--element-text-primary);white-space:nowrap;margin-inline-end:4px}.filter-pill.responsive.disabled,.filter-pill.responsive.disabled .value{padding-inline-end:2px;color:var(--element-text-disabled)}.name{margin:0}.name.empty-desc{margin:0;position:relative;inset-block-start:50%;transform:translateY(-50%)}.filter-pill .value,.filter-pill-default .value{color:var(--element-text-active)}.filter-pill-success .value{color:var(--element-text-success)}.filter-pill-info .value{color:var(--element-text-information)}.filter-pill-warning .value{color:var(--element-text-warning)}.filter-pill-danger .value{color:var(--element-text-danger)}.filter-pill-inactive .value{color:var(--element-text-secondary)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiFilterPillComponent.prototype, "filter", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterPillComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-filter-pill', template: "<div\n  class=\"filter-pill\"\n  [class.disabled]=\"disabled\"\n  [class.responsive]=\"responsive\"\n  [ngClass]=\"filter.status ? 'filter-pill-' + filter.status : ''\"\n  [attr.aria-disabled]=\"disabled\"\n>\n  <div class=\"wrapper\">\n    <div\n      *ngIf=\"filter.title\"\n      class=\"name\"\n      [class.empty-desc]=\"!filter.description && !responsive\"\n    >{{ filter.title | translate }}</div>\n    <span\n      class=\"name pe-4\"\n      *ngIf=\"responsive && filter.title && filter.description\"\n    >:</span>\n    <div class=\"value\">{{ filter.description || '' | translate }}</div>\n    <div *ngIf=\"!filter.title && !responsive\">&#160;</div>\n  </div>\n  <ng-container *ngIf=\"!responsive || !disabled\">\n    <ng-container *ngTemplateOutlet=\"remove\"></ng-container>\n  </ng-container>\n</div>\n<ng-template #remove>\n  <button\n    type=\"button\"\n    class=\"btn-remove element-cancel btn btn-circle btn-ghost focus-inside\"\n    [class.ms-0]=\"responsive\"\n    [class.ms-2]=\"!responsive\"\n    [class.btn-xs]=\"responsive\"\n    [class.btn-sm]=\"!responsive\"\n    role=\"button\"\n    aria-label=\"Remove\"\n    (keydown.enter)=\"deleteClicked()\"\n    (click)=\"deleteClicked()\">\n  </button>\n</ng-template>\n", styles: [":host{--filter-pill-background-color: var(--element-base-1)}:host.dark-background{--filter-pill-background-color: var(--element-base-0)}.filter-pill{margin-inline-start:8px;font-weight:500;block-size:32px;border-radius:16px;background:var(--filter-pill-background-color);display:flex;padding-block:0 4px;padding-inline:16px 0;line-height:12px}.filter-pill:not(.responsive) .name{padding-block-start:4px}.wrapper{display:flex;flex-direction:column}.filter-pill.responsive{display:flex;align-items:center;padding-block:0;padding-inline:8px 0;block-size:24px}.filter-pill.responsive .wrapper{flex-direction:row}.filter-pill.responsive .name{font-weight:700;white-space:nowrap}.filter-pill.responsive .value{color:var(--element-text-primary);white-space:nowrap;margin-inline-end:4px}.filter-pill.responsive.disabled,.filter-pill.responsive.disabled .value{padding-inline-end:2px;color:var(--element-text-disabled)}.name{margin:0}.name.empty-desc{margin:0;position:relative;inset-block-start:50%;transform:translateY(-50%)}.filter-pill .value,.filter-pill-default .value{color:var(--element-text-active)}.filter-pill-success .value{color:var(--element-text-success)}.filter-pill-info .value{color:var(--element-text-information)}.filter-pill-warning .value{color:var(--element-text-warning)}.filter-pill-danger .value{color:var(--element-text-danger)}.filter-pill-inactive .value{color:var(--element-text-secondary)}\n"] }]
        }], propDecorators: { filter: [{
                type: Input
            }], responsive: [{
                type: Input
            }], disabled: [{
                type: Input
            }], totalPills: [{
                type: Input
            }], deleteFilters: [{
                type: Output
            }] } });

class SiFilterBarComponent {
    constructor() {
        /**
         * Title of filter bar.
         */
        this.filterTitle = 'Filters';
        /**
         * Custom text if no filters are selected.
         */
        this.filterDefaultText = 'No filters applied.';
        /**
         * Array of filter items to show
         */
        this.filters = [];
        /**
         * Enable to render responsive variant
         */
        this.responsive = false;
        /**
         * Text for reset button
         */
        this.resetText = 'Reset filters';
        /**
         * Set false to hide reset filters button
         */
        this.allowReset = true;
        /**
         * Color to use for component background
         */
        this.colorVariant = 'base-1';
        this._disabled = false;
        /**
         * Output callback to be executed when the filter changes
         */
        this.filtersChange = new EventEmitter();
        /**
         * Output callback to be executed when the reset filter is clicked
         */
        this.resetFilters = new EventEmitter();
        this.trackByIndex = (index) => index;
    }
    /**
     * Set to true to disable component
     * @defaultref {@link _disabled}
     */
    set disabled(val) {
        this._disabled = coerceBooleanProperty(val);
    }
    get disabled() {
        return this._disabled;
    }
    deleteFilters(deletedPill) {
        this.filters.splice(this.filters.indexOf(deletedPill), 1);
        this.filtersChange.emit(this.filters);
    }
    get isEmpty() {
        return !this.filters.length;
    }
    onResetFilters() {
        this.filters = [];
        this.resetFilters.emit();
    }
    deleteOverflowFilter() {
        this.filters.splice(this.collapsableListDirective.items.toArray().findIndex(item => !item.visible));
        this.filtersChange.emit(this.filters);
    }
}
SiFilterBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFilterBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFilterBarComponent, selector: "si-filter-bar", inputs: { filterTitle: "filterTitle", filterDefaultText: "filterDefaultText", filters: "filters", responsive: "responsive", resetText: "resetText", allowReset: "allowReset", colorVariant: "colorVariant", disabled: "disabled" }, outputs: { filtersChange: "filtersChange", resetFilters: "resetFilters" }, viewQueries: [{ propertyName: "collapsableListDirective", first: true, predicate: SiAutoCollapsableListDirective, descendants: true, static: true }], ngImport: i0, template: "<div\n  [class.rounded]=\"!responsive\"\n  [class.list-header]=\"!responsive\"\n  [class.responsive]=\"responsive\"\n  [class.py-2]=\"responsive\"\n  [siAutoCollapsableList]=\"responsive\"\n>\n  <span *ngIf=\"!responsive\" class=\"list-title\">{{ filterTitle }}:</span>\n  <span class=\"list-text\" *ngIf=\"isEmpty\">{{ filterDefaultText }}</span>\n  <div\n    class=\"pill-wrapper\"\n    *ngFor=\"let filter of filters;let i= index; trackBy: trackByIndex\"\n    siAutoCollapsableListItem\n  >\n    <si-filter-pill\n      [class.dark-background]=\"responsive && colorVariant === 'base-0'\"\n      [totalPills]=\"filters.length\"\n      [filter]=\"filter\"\n      [responsive]=\"responsive\"\n      [disabled]=\"disabled\"\n      (deleteFilters)=\"deleteFilters($event)\"\n    >\n    </si-filter-pill>\n  </div>\n  <si-filter-pill\n    siAutoCollapsableListOverflowItem #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    [class.dark-background]=\"responsive && colorVariant === 'base-0'\"\n    [responsive]=\"responsive\"\n    [disabled]=\"disabled\"\n    [filter]=\"{description: '+ ' + overflowItem.hiddenItemCount + ' filters', title: '', status: 'default', filterName: ''}\"\n    (deleteFilters)=\"deleteOverflowFilter()\"\n  >\n  </si-filter-pill>\n  <button\n    siAutoCollapsableListAdditionalContent\n    *ngIf=\"allowReset && responsive && !isEmpty\"\n    type=\"button\"\n    class=\"text-nowrap btn btn-tertiary ms-4\"\n    (click)=\"onResetFilters()\"\n    [disabled]=\"disabled\"\n  >\n    {{resetText}}\n  </button>\n</div>\n", styles: [".list-header{margin-block-start:12px;background:var(--element-base-0);padding-block:6px;padding-inline:12px;white-space:nowrap;overflow-y:hidden;overflow-x:auto;display:flex;align-items:center}.list-text{line-height:32px}.pill-wrapper{display:inline-block;vertical-align:middle}.responsive{overflow:hidden;display:flex;align-items:center}.responsive .list-text{color:var(--element-text-disabled)}.responsive .invisible{position:absolute}.list-title{margin-inline-end:6px;font-weight:500}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiAutoCollapsableListAdditionalContentDirective, selector: "[siAutoCollapsableListAdditionalContent]" }, { kind: "directive", type: SiAutoCollapsableListDirective, selector: "[siAutoCollapsableList]", inputs: ["siAutoCollapsableList"], exportAs: ["siAutoCollapsableList"] }, { kind: "directive", type: SiAutoCollapsableListItemDirective, selector: "[siAutoCollapsableListItem]", exportAs: ["siAutoCollapsableListItem"] }, { kind: "directive", type: SiAutoCollapsableListOverflowItemDirective, selector: "[siAutoCollapsableListOverflowItem]", exportAs: ["siAutoCollapsableListOverflowItem"] }, { kind: "component", type: SiFilterPillComponent, selector: "si-filter-pill", inputs: ["filter", "responsive", "disabled", "totalPills"], outputs: ["deleteFilters"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-filter-bar', template: "<div\n  [class.rounded]=\"!responsive\"\n  [class.list-header]=\"!responsive\"\n  [class.responsive]=\"responsive\"\n  [class.py-2]=\"responsive\"\n  [siAutoCollapsableList]=\"responsive\"\n>\n  <span *ngIf=\"!responsive\" class=\"list-title\">{{ filterTitle }}:</span>\n  <span class=\"list-text\" *ngIf=\"isEmpty\">{{ filterDefaultText }}</span>\n  <div\n    class=\"pill-wrapper\"\n    *ngFor=\"let filter of filters;let i= index; trackBy: trackByIndex\"\n    siAutoCollapsableListItem\n  >\n    <si-filter-pill\n      [class.dark-background]=\"responsive && colorVariant === 'base-0'\"\n      [totalPills]=\"filters.length\"\n      [filter]=\"filter\"\n      [responsive]=\"responsive\"\n      [disabled]=\"disabled\"\n      (deleteFilters)=\"deleteFilters($event)\"\n    >\n    </si-filter-pill>\n  </div>\n  <si-filter-pill\n    siAutoCollapsableListOverflowItem #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    [class.dark-background]=\"responsive && colorVariant === 'base-0'\"\n    [responsive]=\"responsive\"\n    [disabled]=\"disabled\"\n    [filter]=\"{description: '+ ' + overflowItem.hiddenItemCount + ' filters', title: '', status: 'default', filterName: ''}\"\n    (deleteFilters)=\"deleteOverflowFilter()\"\n  >\n  </si-filter-pill>\n  <button\n    siAutoCollapsableListAdditionalContent\n    *ngIf=\"allowReset && responsive && !isEmpty\"\n    type=\"button\"\n    class=\"text-nowrap btn btn-tertiary ms-4\"\n    (click)=\"onResetFilters()\"\n    [disabled]=\"disabled\"\n  >\n    {{resetText}}\n  </button>\n</div>\n", styles: [".list-header{margin-block-start:12px;background:var(--element-base-0);padding-block:6px;padding-inline:12px;white-space:nowrap;overflow-y:hidden;overflow-x:auto;display:flex;align-items:center}.list-text{line-height:32px}.pill-wrapper{display:inline-block;vertical-align:middle}.responsive{overflow:hidden;display:flex;align-items:center}.responsive .list-text{color:var(--element-text-disabled)}.responsive .invisible{position:absolute}.list-title{margin-inline-end:6px;font-weight:500}\n"] }]
        }], propDecorators: { filterTitle: [{
                type: Input
            }], filterDefaultText: [{
                type: Input
            }], filters: [{
                type: Input
            }], responsive: [{
                type: Input
            }], resetText: [{
                type: Input
            }], allowReset: [{
                type: Input
            }], colorVariant: [{
                type: Input
            }], collapsableListDirective: [{
                type: ViewChild,
                args: [SiAutoCollapsableListDirective, { static: true }]
            }], disabled: [{
                type: Input
            }], filtersChange: [{
                type: Output
            }], resetFilters: [{
                type: Output
            }] } });

class SiFilterBarModule {
}
SiFilterBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFilterBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarModule, declarations: [SiFilterBarComponent, SiFilterPillComponent], imports: [CommonModule,
        SiAutoCollapsableListModule,
        SiTranslateModule], exports: [SiFilterBarComponent] });
SiFilterBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarModule, imports: [CommonModule,
        SiAutoCollapsableListModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilterBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiAutoCollapsableListModule,
                        SiTranslateModule
                    ],
                    declarations: [SiFilterBarComponent, SiFilterPillComponent],
                    exports: [SiFilterBarComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiFooterComponent {
    constructor() {
        this.trackByIndex = (index) => index;
    }
}
SiFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFooterComponent, selector: "si-footer", inputs: { copyright: "copyright", links: "links" }, ngImport: i0, template: "<footer>\n  <div class=\"container-fluid\">\n    <div class=\"row\">\n      <div class=\"col-sm-4 col-md-6\">\n        \u00A9 {{ copyright }}\n      </div>\n      <div class=\"col-sm-8 col-md-6 text-sm-end\">\n          <span *ngFor=\"let link of links; trackBy: trackByIndex\">\n            <a [siLink]=\"link\">\n              {{ link.title! | translate }}\n            </a>\n          </span>\n      </div>\n    </div>\n  </div>\n</footer>\n", styles: ["footer{margin-block-end:0;background-color:var(--element-base-0);color:var(--element-text-secondary);font-size:.75rem;border-block-start:1px solid var(--element-ui-4);overflow:auto}a,span{display:inline-flex}a+a:before,a+span:before,span+a:before,span+span:before{content:\"|\";padding-inline:8px}.container-fluid{padding-block:12px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiFooterComponent.prototype, "copyright", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-footer', template: "<footer>\n  <div class=\"container-fluid\">\n    <div class=\"row\">\n      <div class=\"col-sm-4 col-md-6\">\n        \u00A9 {{ copyright }}\n      </div>\n      <div class=\"col-sm-8 col-md-6 text-sm-end\">\n          <span *ngFor=\"let link of links; trackBy: trackByIndex\">\n            <a [siLink]=\"link\">\n              {{ link.title! | translate }}\n            </a>\n          </span>\n      </div>\n    </div>\n  </div>\n</footer>\n", styles: ["footer{margin-block-end:0;background-color:var(--element-base-0);color:var(--element-text-secondary);font-size:.75rem;border-block-start:1px solid var(--element-ui-4);overflow:auto}a,span{display:inline-flex}a+a:before,a+span:before,span+a:before,span+span:before{content:\"|\";padding-inline:8px}.container-fluid{padding-block:12px}\n"] }]
        }], propDecorators: { copyright: [{
                type: Input
            }], links: [{
                type: Input
            }] } });

class SiFooterModule {
}
SiFooterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFooterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFooterModule, declarations: [SiFooterComponent], imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiFooterComponent] });
SiFooterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFooterModule, imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiFooterComponent],
                    exports: [SiFooterComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

// -------------------------------------------------------------------------------

class SiInlineNotificationComponent {
    constructor() {
        /**
         * Params passed to the translation pipe
         */
        this.translationParams = {};
        /**
         * Displays in embedded style, w/o radius and indicator bar
         */
        this.embedded = false;
        this.icon = STATUS_ICON.info;
    }
    ngOnChanges(changes) {
        if (changes.severity && this.severity) {
            this.icon = STATUS_ICON[this.severity];
        }
    }
}
SiInlineNotificationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiInlineNotificationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiInlineNotificationComponent, selector: "si-inline-notification", inputs: { severity: "severity", heading: "heading", message: "message", action: "action", translationParams: "translationParams", embedded: "embedded" }, usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"alert d-flex align-items-start\"\n  [ngClass]=\"'alert-' + severity\"\n  [hidden]=\"!severity\"\n>\n  <si-icon\n    class=\"me-4 flex-shrink-0\"\n    [icon]=\"icon.icon\"\n    [color]=\"icon.color\"\n    [stackedIcon]=\"icon.stacked\"\n    [stackedColor]=\"icon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"notification-body d-flex flex-grow-1 flex-wrap mt-1\">\n    <strong *ngIf=\"heading\" class=\"pe-2\">{{ heading | translate:translationParams }}: </strong>\n    <span>{{ message | translate:translationParams }}</span>\n  </div>\n  <a\n    *ngIf=\"action && action.title\"\n    class=\"btn btn-link ms-4 my-n3 me-n2 px-2 flex-shrink-0\"\n    [siLink]=\"action\"\n  >{{ action.title | translate:translationParams }}</a>\n</div>\n", styles: [":host{display:block;overflow:hidden;flex-shrink:0}.btn-link{color:currentColor!important;background:transparent!important;font-size:1rem;line-height:1.25rem}.notification-body{padding-block-start:1px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiInlineNotificationComponent.prototype, "severity", void 0);
__decorate([
    Required()
], SiInlineNotificationComponent.prototype, "message", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-inline-notification', template: "<div\n  class=\"alert d-flex align-items-start\"\n  [ngClass]=\"'alert-' + severity\"\n  [hidden]=\"!severity\"\n>\n  <si-icon\n    class=\"me-4 flex-shrink-0\"\n    [icon]=\"icon.icon\"\n    [color]=\"icon.color\"\n    [stackedIcon]=\"icon.stacked\"\n    [stackedColor]=\"icon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"notification-body d-flex flex-grow-1 flex-wrap mt-1\">\n    <strong *ngIf=\"heading\" class=\"pe-2\">{{ heading | translate:translationParams }}: </strong>\n    <span>{{ message | translate:translationParams }}</span>\n  </div>\n  <a\n    *ngIf=\"action && action.title\"\n    class=\"btn btn-link ms-4 my-n3 me-n2 px-2 flex-shrink-0\"\n    [siLink]=\"action\"\n  >{{ action.title | translate:translationParams }}</a>\n</div>\n", styles: [":host{display:block;overflow:hidden;flex-shrink:0}.btn-link{color:currentColor!important;background:transparent!important;font-size:1rem;line-height:1.25rem}.notification-body{padding-block-start:1px}\n"] }]
        }], propDecorators: { severity: [{
                type: Input
            }], heading: [{
                type: Input
            }], message: [{
                type: Input
            }], action: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], embedded: [{
                type: Input
            }] } });

class SiInlineNotificationModule {
}
SiInlineNotificationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiInlineNotificationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationModule, declarations: [SiInlineNotificationComponent], imports: [CommonModule,
        SiIconModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiInlineNotificationComponent] });
SiInlineNotificationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationModule, imports: [CommonModule,
        SiIconModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiInlineNotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiInlineNotificationComponent],
                    exports: [SiInlineNotificationComponent]
                }]
        }] });

class SiLanguageSwitcherComponent {
    constructor() {
        /**
         * Key for translation.
         * If the key is set to an empty string, the language of the underlaying tanslation framework will not be switched.
         */
        this.translationKey = 'LANGUAGE';
        /**
         * Text for aria label for the language selector. Needed for a11y
         */
        this.languageSwitcherLabel = 'language switcher';
        this.translate = inject(SiTranslateService);
        this.trackByValue = (index, value) => value.value;
    }
    /**
     * List of all available languages in this application.
     */
    set availableLanguages(languages) {
        if (typeof languages[0] !== 'object') {
            languages = languages.map(languageValue => ({
                value: languageValue,
                name: this.translationKey
                    ? `${this.translationKey}.${languageValue.toUpperCase()}`
                    : languageValue
            }));
        }
        this.availableIsoLanguages = languages;
    }
    get availableLanguages() {
        return this.availableIsoLanguages;
    }
    ngOnInit() {
        this.availableLanguages ?? (this.availableLanguages = this.translate.availableLanguages);
    }
    switchLanguage(target) {
        const language = target?.value;
        if (!language || !this.translationKey || this.translationKey.length === 0) {
            return;
        }
        this.translate.setCurrentLanguage(language);
    }
}
SiLanguageSwitcherComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiLanguageSwitcherComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiLanguageSwitcherComponent, selector: "si-language-switcher", inputs: { translationKey: "translationKey", languageSwitcherLabel: "languageSwitcherLabel", availableLanguages: "availableLanguages" }, ngImport: i0, template: "<select\n  class=\"si-title-2 language-switcher-select\"\n  [attr.aria-label]=\"languageSwitcherLabel | translate\"\n  (change)=\"switchLanguage($event.target)\"\n>\n  <option *ngFor=\"let language of availableIsoLanguages; trackBy: trackByValue\"\n    [value]=\"language.value\"\n    [selected]=\"language.value === translate.currentLanguage\"\n  >\n    {{ language.name | translate }}\n  </option>\n</select>\n", styles: [":host{--language-switcher-color: var(--element-text-active)}.language-switcher-select{inline-size:100%;background-color:transparent;border:0;border-block-end:1px solid var(--input-border-color);padding:6px;outline:none;cursor:pointer;color:var(--language-switcher-color)}@media (min-width: 768px){.language-switcher-select{max-inline-size:200px}}.language-switcher-select option{background-color:var(--element-base-1)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-language-switcher', template: "<select\n  class=\"si-title-2 language-switcher-select\"\n  [attr.aria-label]=\"languageSwitcherLabel | translate\"\n  (change)=\"switchLanguage($event.target)\"\n>\n  <option *ngFor=\"let language of availableIsoLanguages; trackBy: trackByValue\"\n    [value]=\"language.value\"\n    [selected]=\"language.value === translate.currentLanguage\"\n  >\n    {{ language.name | translate }}\n  </option>\n</select>\n", styles: [":host{--language-switcher-color: var(--element-text-active)}.language-switcher-select{inline-size:100%;background-color:transparent;border:0;border-block-end:1px solid var(--input-border-color);padding:6px;outline:none;cursor:pointer;color:var(--language-switcher-color)}@media (min-width: 768px){.language-switcher-select{max-inline-size:200px}}.language-switcher-select option{background-color:var(--element-base-1)}\n"] }]
        }], propDecorators: { translationKey: [{
                type: Input
            }], languageSwitcherLabel: [{
                type: Input
            }], availableLanguages: [{
                type: Input
            }] } });

class SiLanguageSwitcherModule {
}
SiLanguageSwitcherModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLanguageSwitcherModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherModule, declarations: [SiLanguageSwitcherComponent], imports: [CommonModule,
        SiTranslateModule], exports: [SiLanguageSwitcherComponent] });
SiLanguageSwitcherModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherModule, imports: [CommonModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLanguageSwitcherModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiTranslateModule
                    ],
                    declarations: [SiLanguageSwitcherComponent],
                    exports: [SiLanguageSwitcherComponent]
                }]
        }] });

class SiLandingPageComponent {
    constructor() {
        /**
         * List of links (e.g. Corporate information)
         */
        this.links = [];
        /**
         * URL to custom background image
         */
        this.backgroundImageUrl = './assets/images/landing-page-digitalcity.jpg';
        /**
         * Input of si-language-switcher: Key for translation.
         * If your languages are already translated, you may display them without any
         * manipulation by passing in an empty string.
         */
        this.translationKey = 'LANGUAGE';
        this.trackByIndex = (index) => index;
    }
}
SiLandingPageComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiLandingPageComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiLandingPageComponent, selector: "si-landing-page", inputs: { heading: "heading", subheading: "subheading", subtitle: "subtitle", copyright: "copyright", links: "links", backgroundImageUrl: "backgroundImageUrl", logoUrl: "logoUrl", translationKey: "translationKey", availableLanguages: "availableLanguages", announcement: "announcement", loginAlert: "loginAlert" }, ngImport: i0, template: "<si-inline-notification\n  *ngIf=\"announcement\"\n  [severity]=\"announcement.severity\"\n  [heading]=\"announcement.heading\"\n  [message]=\"announcement.message\"\n></si-inline-notification>\n<div class=\"landing-page\">\n  <div style.background-image=\"url({{ backgroundImageUrl }})\" class=\"landing-page-background d-none d-md-block\">\n  </div>\n  <div class=\"landing-page-content\">\n    <div class=\"d-flex justify-content-between align-items-center pb-9\">\n      <div class=\"landing-page-brand\">\n        <svg *ngIf=\"!logoUrl\" xmlns=\"http://www.w3.org/2000/svg\" width=\"103\" height=\"20\" viewBox=\"0 0 142.315 22.5\">\n          <path\n            d=\"M125.878 21.704v-4.378c2.53.806 4.718 1.209 6.566 1.209 2.53 0 3.795-.674 3.795-2.02 0-.49-.183-.898-.549-1.225-.43-.388-1.419-.903-2.971-1.545-2.735-1.113-4.5-2.006-5.296-2.68-1.325-1.121-1.99-2.581-1.99-4.376 0-2.235.873-3.94 2.617-5.113C129.623.525 131.744 0 134.418 0c2.143 0 4.21.27 6.198.811v4.194c-2.172-.838-4.116-1.255-5.83-1.255-2.276 0-3.415.647-3.415 1.942 0 .522.247.94.737 1.257.417.265 1.53.786 3.335 1.561 2.725 1.174 4.522 2.194 5.388 3.061.989.99 1.484 2.301 1.484 3.934 0 2.5-1.03 4.356-3.09 5.571-1.612.949-3.755 1.424-6.43 1.424-2.489 0-4.796-.266-6.917-.796m-22.913.352V.367h7.101l7.469 14.097V.367h4.349v21.69h-6.859L107.31 7.744v14.311h-4.345zm-19.73 0V.367H99v3.964h-9.705V9.2h8.45v3.581h-8.45v5.096h9.935v4.18H83.235zm-31.837 0V.367h7.913l5.434 13.607L70.378.367h7.546v21.69h-5.939V7.238L65.679 22.27h-3.781L55.76 7.239v14.817h-4.362zm-19.775 0V.367h15.78v3.964H37.7V9.2h8.45v3.581H37.7v5.096h9.919v4.18H31.623zM20.05.367h6.29v21.69h-6.29V.366zM.443 21.704v-4.378c2.52.806 4.71 1.209 6.567 1.209 2.531 0 3.796-.674 3.796-2.02 0-.49-.185-.898-.551-1.225-.419-.388-1.414-.903-2.985-1.545-2.704-1.103-4.464-1.995-5.28-2.68C.663 9.944 0 8.484 0 6.689c0-2.235.873-3.94 2.618-5.113C4.178.525 6.3 0 8.984 0c2.142 0 4.211.27 6.2.811v4.194C12.989 4.167 11.046 3.75 9.35 3.75c-2.275 0-3.412.647-3.412 1.942 0 .512.249.93.749 1.257.408.265 1.516.786 3.323 1.561 2.724 1.174 4.519 2.194 5.387 3.061.99.99 1.486 2.301 1.486 3.934 0 2.5-1.036 4.356-3.109 5.571-1.601.949-3.74 1.424-6.414 1.424-2.49 0-4.795-.266-6.918-.796\"\n            fill=\"currentColor\"></path>\n        </svg>\n        <img *ngIf=\"logoUrl\" [src]=\"logoUrl\" [alt]=\"heading + '\\'s Background'\" class=\"landing-page-logo\">\n      </div>\n      <div class=\"landing-page-language-switcher\" *ngIf=\"availableLanguages && availableLanguages.length > 1\">\n        <si-language-switcher [translationKey]=\"translationKey\" [availableLanguages]=\"availableLanguages\">\n        </si-language-switcher>\n      </div>\n    </div>\n    <div class=\"landing-page-main\">\n      <div *ngIf=\"subheading\" class=\"si-h2\">{{ subheading }}</div>\n      <h1 class=\"si-h1-black my-2\">{{ heading }}</h1>\n      <h2 *ngIf=\"!loginAlert\" class=\"mt-10 si-h2\">{{ subtitle }}</h2>\n      <si-inline-notification\n        *ngIf=\"loginAlert\"\n        class=\"my-6\"\n        [severity]=\"loginAlert.severity || 'danger'\"\n        [heading]=\"loginAlert.heading\"\n        [message]=\"loginAlert.message\"\n      ></si-inline-notification>\n      <div class=\"landing-page-custom-content\">\n        <ng-content></ng-content>\n      </div>\n    </div>\n    <footer>\n      <div>\n        <div class=\"d-flex justify-content-start flex-row flex-wrap mt-6\">\n          <span *ngFor=\"let link of links; trackBy: trackByIndex\">\n            <a [siLink]=\"link\" siLinkDefaultTarget=\"_blank\" class=\"si-body-2 d-flex-inline pe-6\">\n              {{ link.title ?? '' | translate }}\n            </a>\n          </span>\n        </div>\n        <div *ngIf=\"copyright\" class=\"si-caption mt-4 mb-6 text-body\">\n          {{ copyright }}\n        </div>\n      </div>\n    </footer>\n  </div>\n</div>\n", styles: [":host{--landing-page-logo-width: 180px;display:flex;flex-direction:column;block-size:100%}.landing-page{display:flex;flex-direction:row-reverse;flex-grow:1}.landing-page-background{background-size:cover;background-position:center;inline-size:60%}.landing-page-logo{inline-size:var(--landing-page-logo-width);block-size:var(--landing-page-logo-height)}.landing-page-content{display:flex;flex-direction:column;justify-content:space-between;position:relative;inline-size:40%;background-color:var(--element-base-0);padding-block:32px 32px;padding-inline:96px}.landing-page-brand{color:var(--element-logo-color)}.landing-page-custom-content{margin-block:16px;margin-inline:0}.landing-page-links a{font-size:12px}footer{color:var(--element-text-secondary);overflow:auto}@media (max-width: 1199.98px){.landing-page-background{padding:64px}.landing-page-content{padding-block:32px 32px;padding-inline:64px}}@media (max-width: 991.98px){.landing-page-background{padding:32px}.landing-page-content{padding-block:32px 32px;padding-inline:32px}}@media (max-width: 767.98px){.landing-page{flex-direction:column}.landing-page-logo{transform:scale(.6);transform-origin:top left}.landing-page-content{inline-size:100%;flex-grow:1;padding:32px}}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiInlineNotificationComponent, selector: "si-inline-notification", inputs: ["severity", "heading", "message", "action", "translationParams", "embedded"] }, { kind: "component", type: SiLanguageSwitcherComponent, selector: "si-language-switcher", inputs: ["translationKey", "languageSwitcherLabel", "availableLanguages"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    Required()
], SiLandingPageComponent.prototype, "heading", void 0);
__decorate([
    Required()
], SiLandingPageComponent.prototype, "subtitle", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-landing-page', changeDetection: ChangeDetectionStrategy.OnPush, template: "<si-inline-notification\n  *ngIf=\"announcement\"\n  [severity]=\"announcement.severity\"\n  [heading]=\"announcement.heading\"\n  [message]=\"announcement.message\"\n></si-inline-notification>\n<div class=\"landing-page\">\n  <div style.background-image=\"url({{ backgroundImageUrl }})\" class=\"landing-page-background d-none d-md-block\">\n  </div>\n  <div class=\"landing-page-content\">\n    <div class=\"d-flex justify-content-between align-items-center pb-9\">\n      <div class=\"landing-page-brand\">\n        <svg *ngIf=\"!logoUrl\" xmlns=\"http://www.w3.org/2000/svg\" width=\"103\" height=\"20\" viewBox=\"0 0 142.315 22.5\">\n          <path\n            d=\"M125.878 21.704v-4.378c2.53.806 4.718 1.209 6.566 1.209 2.53 0 3.795-.674 3.795-2.02 0-.49-.183-.898-.549-1.225-.43-.388-1.419-.903-2.971-1.545-2.735-1.113-4.5-2.006-5.296-2.68-1.325-1.121-1.99-2.581-1.99-4.376 0-2.235.873-3.94 2.617-5.113C129.623.525 131.744 0 134.418 0c2.143 0 4.21.27 6.198.811v4.194c-2.172-.838-4.116-1.255-5.83-1.255-2.276 0-3.415.647-3.415 1.942 0 .522.247.94.737 1.257.417.265 1.53.786 3.335 1.561 2.725 1.174 4.522 2.194 5.388 3.061.989.99 1.484 2.301 1.484 3.934 0 2.5-1.03 4.356-3.09 5.571-1.612.949-3.755 1.424-6.43 1.424-2.489 0-4.796-.266-6.917-.796m-22.913.352V.367h7.101l7.469 14.097V.367h4.349v21.69h-6.859L107.31 7.744v14.311h-4.345zm-19.73 0V.367H99v3.964h-9.705V9.2h8.45v3.581h-8.45v5.096h9.935v4.18H83.235zm-31.837 0V.367h7.913l5.434 13.607L70.378.367h7.546v21.69h-5.939V7.238L65.679 22.27h-3.781L55.76 7.239v14.817h-4.362zm-19.775 0V.367h15.78v3.964H37.7V9.2h8.45v3.581H37.7v5.096h9.919v4.18H31.623zM20.05.367h6.29v21.69h-6.29V.366zM.443 21.704v-4.378c2.52.806 4.71 1.209 6.567 1.209 2.531 0 3.796-.674 3.796-2.02 0-.49-.185-.898-.551-1.225-.419-.388-1.414-.903-2.985-1.545-2.704-1.103-4.464-1.995-5.28-2.68C.663 9.944 0 8.484 0 6.689c0-2.235.873-3.94 2.618-5.113C4.178.525 6.3 0 8.984 0c2.142 0 4.211.27 6.2.811v4.194C12.989 4.167 11.046 3.75 9.35 3.75c-2.275 0-3.412.647-3.412 1.942 0 .512.249.93.749 1.257.408.265 1.516.786 3.323 1.561 2.724 1.174 4.519 2.194 5.387 3.061.99.99 1.486 2.301 1.486 3.934 0 2.5-1.036 4.356-3.109 5.571-1.601.949-3.74 1.424-6.414 1.424-2.49 0-4.795-.266-6.918-.796\"\n            fill=\"currentColor\"></path>\n        </svg>\n        <img *ngIf=\"logoUrl\" [src]=\"logoUrl\" [alt]=\"heading + '\\'s Background'\" class=\"landing-page-logo\">\n      </div>\n      <div class=\"landing-page-language-switcher\" *ngIf=\"availableLanguages && availableLanguages.length > 1\">\n        <si-language-switcher [translationKey]=\"translationKey\" [availableLanguages]=\"availableLanguages\">\n        </si-language-switcher>\n      </div>\n    </div>\n    <div class=\"landing-page-main\">\n      <div *ngIf=\"subheading\" class=\"si-h2\">{{ subheading }}</div>\n      <h1 class=\"si-h1-black my-2\">{{ heading }}</h1>\n      <h2 *ngIf=\"!loginAlert\" class=\"mt-10 si-h2\">{{ subtitle }}</h2>\n      <si-inline-notification\n        *ngIf=\"loginAlert\"\n        class=\"my-6\"\n        [severity]=\"loginAlert.severity || 'danger'\"\n        [heading]=\"loginAlert.heading\"\n        [message]=\"loginAlert.message\"\n      ></si-inline-notification>\n      <div class=\"landing-page-custom-content\">\n        <ng-content></ng-content>\n      </div>\n    </div>\n    <footer>\n      <div>\n        <div class=\"d-flex justify-content-start flex-row flex-wrap mt-6\">\n          <span *ngFor=\"let link of links; trackBy: trackByIndex\">\n            <a [siLink]=\"link\" siLinkDefaultTarget=\"_blank\" class=\"si-body-2 d-flex-inline pe-6\">\n              {{ link.title ?? '' | translate }}\n            </a>\n          </span>\n        </div>\n        <div *ngIf=\"copyright\" class=\"si-caption mt-4 mb-6 text-body\">\n          {{ copyright }}\n        </div>\n      </div>\n    </footer>\n  </div>\n</div>\n", styles: [":host{--landing-page-logo-width: 180px;display:flex;flex-direction:column;block-size:100%}.landing-page{display:flex;flex-direction:row-reverse;flex-grow:1}.landing-page-background{background-size:cover;background-position:center;inline-size:60%}.landing-page-logo{inline-size:var(--landing-page-logo-width);block-size:var(--landing-page-logo-height)}.landing-page-content{display:flex;flex-direction:column;justify-content:space-between;position:relative;inline-size:40%;background-color:var(--element-base-0);padding-block:32px 32px;padding-inline:96px}.landing-page-brand{color:var(--element-logo-color)}.landing-page-custom-content{margin-block:16px;margin-inline:0}.landing-page-links a{font-size:12px}footer{color:var(--element-text-secondary);overflow:auto}@media (max-width: 1199.98px){.landing-page-background{padding:64px}.landing-page-content{padding-block:32px 32px;padding-inline:64px}}@media (max-width: 991.98px){.landing-page-background{padding:32px}.landing-page-content{padding-block:32px 32px;padding-inline:32px}}@media (max-width: 767.98px){.landing-page{flex-direction:column}.landing-page-logo{transform:scale(.6);transform-origin:top left}.landing-page-content{inline-size:100%;flex-grow:1;padding:32px}}\n"] }]
        }], propDecorators: { heading: [{
                type: Input
            }], subheading: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], copyright: [{
                type: Input
            }], links: [{
                type: Input
            }], backgroundImageUrl: [{
                type: Input
            }], logoUrl: [{
                type: Input
            }], translationKey: [{
                type: Input
            }], availableLanguages: [{
                type: Input
            }], announcement: [{
                type: Input
            }], loginAlert: [{
                type: Input
            }] } });

class SiLandingPageModule {
}
SiLandingPageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLandingPageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageModule, declarations: [SiLandingPageComponent], imports: [CommonModule,
        SiInlineNotificationModule,
        SiLanguageSwitcherModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiLandingPageComponent] });
SiLandingPageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageModule, imports: [CommonModule,
        SiInlineNotificationModule,
        SiLanguageSwitcherModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLandingPageModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiInlineNotificationModule,
                        SiLanguageSwitcherModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    declarations: [SiLandingPageComponent],
                    exports: [SiLandingPageComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

class SiCommissioningStatusComponent {
    constructor() {
        /**
         * Number of "overwritten" items. Hidden when `null` is passed.
         */
        this.overwritten = null;
        /**
         * Number of "checked" items. Hidden when `null` is passed.
         */
        this.checked = 0;
        /**
         * Number of "failed" items. Hidden when `null` is passed.
         */
        this.failed = 0;
        /**
         * Number of "unchecked" items. Hidden when `null` is passed.
         */
        this.unchecked = null;
    }
}
SiCommissioningStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiCommissioningStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiCommissioningStatusComponent, selector: "si-commissioning-status", inputs: { disabled: "disabled", overwritten: "overwritten", checked: "checked", failed: "failed", unchecked: "unchecked" }, ngImport: i0, template: "<div *ngIf=\"overwritten || overwritten === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"overwritten\"\n    icon=\"element-manual-filled\"\n    [class.status-warning]=\"overwritten > 0 && !disabled\"\n    [class.inactive]=\"overwritten === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ overwritten }}</span>\n</div>\n<div *ngIf=\"checked || checked === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"checked\"\n    icon=\"element-circle-filled\"\n    [color]=\"(checked === 0 || disabled) ? '' : 'status-success'\"\n    stackedIcon=\"element-state-tick\"\n    [stackedColor]=\"(checked === 0 || disabled) ? 'stacked-color-inactive' : 'status-success-contrast'\"\n    [class.inactive]=\"checked === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ checked }}</span>\n</div>\n<div *ngIf=\"failed || failed === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"failed\"\n    icon=\"element-circle-filled\"\n    [color]=\"(failed === 0 || disabled) ? '' : 'status-danger'\"\n    stackedIcon=\"element-state-exclamation-mark\"\n    [stackedColor]=\"(failed === 0 || disabled) ? 'stacked-color-inactive' : 'status-danger-contrast'\"\n    [class.inactive]=\"failed === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ failed }}</span>\n</div>\n<div *ngIf=\"unchecked || unchecked === 0\" class=\"si-body-2 si-commissioning-status-item\">\n  <si-icon\n    class=\"unchecked\"\n    icon=\"element-not-checked\"\n    [class.stable]=\"unchecked > 0 && !disabled\"\n    [class.inactive]=\"unchecked === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ unchecked }}</span>\n</div>\n", styles: [":host{display:flex;align-items:center}:host.vertical{flex-direction:column;align-items:start}.si-commissioning-status-item{display:flex;align-items:center;flex-direction:column;text-align:center;inline-size:24px}.si-commissioning-status-item:last-of-type{margin-inline-end:0!important}si-icon{font-size:1.5rem;margin-block:0 8px!important}::ng-deep si-icon .stacked-color-inactive{color:var(--element-ui-5)}.stable{color:var(--element-ui-1)}.inactive{color:var(--element-ui-3)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-commissioning-status', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div *ngIf=\"overwritten || overwritten === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"overwritten\"\n    icon=\"element-manual-filled\"\n    [class.status-warning]=\"overwritten > 0 && !disabled\"\n    [class.inactive]=\"overwritten === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ overwritten }}</span>\n</div>\n<div *ngIf=\"checked || checked === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"checked\"\n    icon=\"element-circle-filled\"\n    [color]=\"(checked === 0 || disabled) ? '' : 'status-success'\"\n    stackedIcon=\"element-state-tick\"\n    [stackedColor]=\"(checked === 0 || disabled) ? 'stacked-color-inactive' : 'status-success-contrast'\"\n    [class.inactive]=\"checked === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ checked }}</span>\n</div>\n<div *ngIf=\"failed || failed === 0\" class=\"si-body-2 si-commissioning-status-item me-2\">\n  <si-icon\n    class=\"failed\"\n    icon=\"element-circle-filled\"\n    [color]=\"(failed === 0 || disabled) ? '' : 'status-danger'\"\n    stackedIcon=\"element-state-exclamation-mark\"\n    [stackedColor]=\"(failed === 0 || disabled) ? 'stacked-color-inactive' : 'status-danger-contrast'\"\n    [class.inactive]=\"failed === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ failed }}</span>\n</div>\n<div *ngIf=\"unchecked || unchecked === 0\" class=\"si-body-2 si-commissioning-status-item\">\n  <si-icon\n    class=\"unchecked\"\n    icon=\"element-not-checked\"\n    [class.stable]=\"unchecked > 0 && !disabled\"\n    [class.inactive]=\"unchecked === 0 || disabled\"\n  ></si-icon>\n  <span *ngIf=\"!disabled\">{{ unchecked }}</span>\n</div>\n", styles: [":host{display:flex;align-items:center}:host.vertical{flex-direction:column;align-items:start}.si-commissioning-status-item{display:flex;align-items:center;flex-direction:column;text-align:center;inline-size:24px}.si-commissioning-status-item:last-of-type{margin-inline-end:0!important}si-icon{font-size:1.5rem;margin-block:0 8px!important}::ng-deep si-icon .stacked-color-inactive{color:var(--element-ui-5)}.stable{color:var(--element-ui-1)}.inactive{color:var(--element-ui-3)}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], overwritten: [{
                type: Input
            }], checked: [{
                type: Input
            }], failed: [{
                type: Input
            }], unchecked: [{
                type: Input
            }] } });

class SiCommissioningStatusModule {
}
SiCommissioningStatusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiCommissioningStatusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusModule, declarations: [SiCommissioningStatusComponent], imports: [CommonModule, SiIconModule], exports: [SiCommissioningStatusComponent] });
SiCommissioningStatusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusModule, imports: [CommonModule, SiIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCommissioningStatusModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiIconModule],
                    declarations: [SiCommissioningStatusComponent],
                    exports: [SiCommissioningStatusComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

// ------------------------------------------------------------------------------

class SiLoadingSpinnerComponent {
}
SiLoadingSpinnerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiLoadingSpinnerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiLoadingSpinnerComponent, selector: "si-loading-spinner", ngImport: i0, template: "<div class=\"loading\">\n  <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\">\n    <g>\n      <path d=\"M256,156a16,16,0,0,1-16-16V80a16,16,0,0,1,32,0v60A16,16,0,0,1,256,156Z\"/>\n      <path d=\"M314.78,175.1a16,16,0,0,1-3.54-22.35l35.27-48.54A16,16,0,1,1,372.39,123l-35.26,48.54A16,16,0,0,1,314.78,175.1Z\"/>\n      <path d=\"M351.11,225.1a16,16,0,0,1,10.27-20.16l57.06-18.54a16,16,0,1,1,9.89,30.43l-57.06,18.54A16,16,0,0,1,351.11,225.1Z\"/>\n      <path d=\"M351.11,286.9a16,16,0,0,1,20.16-10.27l57.06,18.54a16,16,0,1,1-9.89,30.43l-57.06-18.54A16,16,0,0,1,351.11,286.9Z\"/>\n      <path d=\"M314.78,336.9a16,16,0,0,1,22.35,3.54L372.39,389a16,16,0,1,1-25.88,18.81l-35.27-48.54A16,16,0,0,1,314.78,336.9Z\"/>\n      <path d=\"M256,356a16,16,0,0,1,16,16v60a16,16,0,0,1-32,0V372A16,16,0,0,1,256,356Z\"/>\n      <path d=\"M197.22,336.9a16,16,0,0,1,3.54,22.35l-35.27,48.54A16,16,0,1,1,139.61,389l35.26-48.54A16,16,0,0,1,197.22,336.9Z\"/>\n      <path d=\"M160.89,286.9a16,16,0,0,1-10.27,20.16L93.56,325.6a16,16,0,0,1-9.89-30.43l57.06-18.54A16,16,0,0,1,160.89,286.9Z\"/>\n      <path d=\"M160.89,225.1a16,16,0,0,1-20.16,10.27L83.67,216.83a16,16,0,1,1,9.89-30.43l57.06,18.54A16,16,0,0,1,160.89,225.1Z\"/>\n      <path d=\"M197.22,175.1a16,16,0,0,1-22.35-3.54L139.61,123a16,16,0,1,1,25.88-18.81l35.27,48.54A16,16,0,0,1,197.22,175.1Z\"/>\n    </g>\n  </svg>\n</div>\n", styles: ["@keyframes spinner-fade{0%{opacity:1}to{opacity:.05}}.loading{display:block;text-align:center}svg{inline-size:var(--loading-spinner-size, 64px);block-size:var(--loading-spinner-size, 64px)}path{fill:var(--loading-spinner-color, var(--element-ui-2));animation:spinner-fade calc(1s * var(--element-animations-enabled, 1)) infinite linear}path:nth-child(1){animation-delay:0s;opacity:1}path:nth-child(2){animation-delay:.1s;opacity:.9}path:nth-child(3){animation-delay:.2s;opacity:.8}path:nth-child(4){animation-delay:.3s;opacity:.7}path:nth-child(5){animation-delay:.4s;opacity:.6}path:nth-child(6){animation-delay:.5s;opacity:.5}path:nth-child(7){animation-delay:.6s;opacity:.4}path:nth-child(8){animation-delay:.7s;opacity:.3}path:nth-child(9){animation-delay:.8s;opacity:.2}path:nth-child(10){animation-delay:.9s;opacity:.1}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-loading-spinner', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"loading\">\n  <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\">\n    <g>\n      <path d=\"M256,156a16,16,0,0,1-16-16V80a16,16,0,0,1,32,0v60A16,16,0,0,1,256,156Z\"/>\n      <path d=\"M314.78,175.1a16,16,0,0,1-3.54-22.35l35.27-48.54A16,16,0,1,1,372.39,123l-35.26,48.54A16,16,0,0,1,314.78,175.1Z\"/>\n      <path d=\"M351.11,225.1a16,16,0,0,1,10.27-20.16l57.06-18.54a16,16,0,1,1,9.89,30.43l-57.06,18.54A16,16,0,0,1,351.11,225.1Z\"/>\n      <path d=\"M351.11,286.9a16,16,0,0,1,20.16-10.27l57.06,18.54a16,16,0,1,1-9.89,30.43l-57.06-18.54A16,16,0,0,1,351.11,286.9Z\"/>\n      <path d=\"M314.78,336.9a16,16,0,0,1,22.35,3.54L372.39,389a16,16,0,1,1-25.88,18.81l-35.27-48.54A16,16,0,0,1,314.78,336.9Z\"/>\n      <path d=\"M256,356a16,16,0,0,1,16,16v60a16,16,0,0,1-32,0V372A16,16,0,0,1,256,356Z\"/>\n      <path d=\"M197.22,336.9a16,16,0,0,1,3.54,22.35l-35.27,48.54A16,16,0,1,1,139.61,389l35.26-48.54A16,16,0,0,1,197.22,336.9Z\"/>\n      <path d=\"M160.89,286.9a16,16,0,0,1-10.27,20.16L93.56,325.6a16,16,0,0,1-9.89-30.43l57.06-18.54A16,16,0,0,1,160.89,286.9Z\"/>\n      <path d=\"M160.89,225.1a16,16,0,0,1-20.16,10.27L83.67,216.83a16,16,0,1,1,9.89-30.43l57.06,18.54A16,16,0,0,1,160.89,225.1Z\"/>\n      <path d=\"M197.22,175.1a16,16,0,0,1-22.35-3.54L139.61,123a16,16,0,1,1,25.88-18.81l35.27,48.54A16,16,0,0,1,197.22,175.1Z\"/>\n    </g>\n  </svg>\n</div>\n", styles: ["@keyframes spinner-fade{0%{opacity:1}to{opacity:.05}}.loading{display:block;text-align:center}svg{inline-size:var(--loading-spinner-size, 64px);block-size:var(--loading-spinner-size, 64px)}path{fill:var(--loading-spinner-color, var(--element-ui-2));animation:spinner-fade calc(1s * var(--element-animations-enabled, 1)) infinite linear}path:nth-child(1){animation-delay:0s;opacity:1}path:nth-child(2){animation-delay:.1s;opacity:.9}path:nth-child(3){animation-delay:.2s;opacity:.8}path:nth-child(4){animation-delay:.3s;opacity:.7}path:nth-child(5){animation-delay:.4s;opacity:.6}path:nth-child(6){animation-delay:.5s;opacity:.5}path:nth-child(7){animation-delay:.6s;opacity:.4}path:nth-child(8){animation-delay:.7s;opacity:.3}path:nth-child(9){animation-delay:.8s;opacity:.2}path:nth-child(10){animation-delay:.9s;opacity:.1}\n"] }]
        }] });

class SiLoadingSpinnerModule {
}
SiLoadingSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLoadingSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerModule, declarations: [SiLoadingSpinnerComponent], exports: [SiLoadingSpinnerComponent] });
SiLoadingSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLoadingSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiLoadingSpinnerComponent],
                    exports: [SiLoadingSpinnerComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

class SiMasterDetailContainerComponent {
    constructor() {
        this.animationDuration = 500;
        this.elementRef = inject(ElementRef);
        this.resizeObserver = inject(ResizeObserverService);
        this.changeDetectorRef = inject(ChangeDetectorRef);
        /**
         * A numeric value defining the minimum width in px, which the container needs
         * to be displayed in its large layout. Whenever smaller than
         * this threshold, the small layout will be used. Default is
         * value is BOOTSTRAP_BREAKPOINTS.mdMinimum.
         */
        this.largeLayoutBreakpoint = BOOTSTRAP_BREAKPOINTS.mdMinimum;
        /**
         * Emits whether the components size is large enough to display
         * master and details views next to each other or not.
         */
        this.hasLargeSizeChange = new EventEmitter();
        /**
         * Whether the details are currently active or not, mostly relevant in the
         * responsive scenario when the viewport only shows either the master or the detail.
         */
        this.detailsActive = false;
        /**
         * Emits the new value of `detailsActive` whenever its value changes.
         */
        this.detailsActiveChange = new EventEmitter();
        /**
         * The heading of the master-detail layout component, usually a page heading.
         */
        this.heading = '';
        /**
         * Whether the heading should be truncated (single line) or not.
         * Default value is `false`.
         */
        this.truncateHeading = false;
        /**
         * The heading of the detail area.
         */
        this.detailsHeading = '';
        /**
         * Whether the master and detail parts should be resizable by a splitter or not.
         * This is only supported in the 'large' scenario (when `hasLargeSize` is `true`).
         * Default value is `false`.
         */
        this.resizableParts = false;
        /**
         * You can hide the back button in the mobile view by setting true. Required
         * in add, edit workflows on mobile sizes. During add or edit, the back button
         * should be hidden. Default value is `false`.
         */
        this.hideBackButton = false;
        /**
         * Details back button text. Required for a11y.
         */
        this.detailsBackButtonText = 'Back';
        /**
         * CSS class(es) applied to the outermost container. Per default, Bootstrap classes
         * to handle responsive paddings are applied: `px-6 pt-6 px-md-9`.
         */
        this.containerClass = 'px-6 pt-6 px-md-9';
        /**
         * CSS class(es) applied to the master container. In combination with `containerClass`,
         * this allows for settings individual padding and margin values on the individual containers.
         */
        this.masterContainerClass = '';
        /**
         * CSS class(es) applied to the detail container. In combination with `containerClass`,
         * this allows for settings individual padding and margin values on the individual containers.
         * Default value: `pb-7 pb-md-9`
         */
        this.detailContainerClass = 'pb-6 pb-md-9';
        /**
         * The percentage width of the master container from the overall component width.
         * Can be a number or `'default'`, which is 32% when {@link resizableParts} is active, otherwise 50%.
         */
        this.masterContainerWidth = 'default';
        /**
         * Emits on split changes the new percentage width of the master container from
         * the overall container width.
         */
        this.masterContainerWidthChange = new EventEmitter();
        /**
         * Sets the minimal width of the master container in pixel.
         */
        this.minMasterSize = 300;
        /**
         * Sets the minimal width of the detail container in pixel.
         */
        this.minDetailSize = 300;
        this.splitSizes = [this.actualMasterContainerWidth, 100 - this.actualMasterContainerWidth];
        // The max size to limit the master container in the static flex layout (if less than 50%), otherwise not set.
        this.maxMasterSize = this.getMaxSize(0);
        // The max size to limit the detail container in the static flex layout (if less than 50%), otherwise not set.
        this.maxDetailSize = this.getMaxSize(1);
        this.animate = false;
        this.opacity = '0';
    }
    get actualMasterContainerWidth() {
        return this.masterContainerWidth === 'default' ? (this.resizableParts ? 32 : 50) : this.masterContainerWidth;
    }
    get masterStateId() {
        return this.stateId ? `${this.stateId}-master` : undefined;
    }
    get detailStateId() {
        return this.stateId ? `${this.stateId}-detail` : undefined;
    }
    ngOnInit() {
        this.resizeSubs = this.resizeObserver
            .observe(this.elementRef.nativeElement, 100, true)
            .subscribe(dimensions => this.determineLayout(dimensions));
    }
    ngOnChanges(changes) {
        if (changes.detailsActive) {
            this.doAnimation(changes.detailsActive.currentValue);
        }
        if (changes.masterContainerWidth || changes.resizableParts) {
            this.splitSizes = [this.actualMasterContainerWidth, 100 - this.actualMasterContainerWidth];
            this.maxMasterSize = this.getMaxSize(0);
            this.maxDetailSize = this.getMaxSize(1);
        }
    }
    ngOnDestroy() {
        this.resizeSubs?.unsubscribe();
    }
    onSplitSizesChange(sizes) {
        this.masterContainerWidth = sizes[0];
        this.masterContainerWidthChange.emit(this.actualMasterContainerWidth);
    }
    detailsBackClicked() {
        this.detailsActive = false;
        this.doAnimation(false);
    }
    /**
     * Get the max size to limit in the static flex layout (if less than 50%), otherwise not set
     */
    getMaxSize(part) {
        return (this.resizableParts || this.masterContainerWidth === 'default' || !this.hasLargeSize || this.splitSizes[part] > 50)
            ? ''
            : this.splitSizes[part] + '%';
    }
    determineLayout(dimensions) {
        const newHasLargeSize = dimensions.width >= this.largeLayoutBreakpoint;
        if (this.hasLargeSize !== newHasLargeSize) {
            this.hasLargeSize = newHasLargeSize;
            this.maxMasterSize = this.getMaxSize(0);
            this.maxDetailSize = this.getMaxSize(0);
            this.hasLargeSizeChange.emit(this.hasLargeSize);
            this.changeDetectorRef.markForCheck();
        }
        if (this.opacity === '0') {
            this.opacity = '';
            this.changeDetectorRef.markForCheck();
        }
    }
    doAnimation(detailsActive) {
        this.animate = true;
        setTimeout(() => {
            this.animate = false;
            this.changeDetectorRef.markForCheck();
        }, this.animationDuration);
        this.detailsActiveChange.emit(detailsActive);
    }
}
SiMasterDetailContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiMasterDetailContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiMasterDetailContainerComponent, selector: "si-master-detail-container", inputs: { largeLayoutBreakpoint: "largeLayoutBreakpoint", detailsActive: "detailsActive", heading: "heading", truncateHeading: "truncateHeading", detailsHeading: "detailsHeading", resizableParts: "resizableParts", hideBackButton: "hideBackButton", detailsBackButtonText: "detailsBackButtonText", containerClass: "containerClass", masterContainerClass: "masterContainerClass", detailContainerClass: "detailContainerClass", masterContainerWidth: "masterContainerWidth", minMasterSize: "minMasterSize", minDetailSize: "minDetailSize", stateId: "stateId" }, outputs: { hasLargeSizeChange: "hasLargeSizeChange", detailsActiveChange: "detailsActiveChange", masterContainerWidthChange: "masterContainerWidthChange" }, host: { properties: { "class.animate": "this.animate", "style.opacity": "this.opacity" }, classAttribute: "si-layout-inner" }, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"resizableParts && hasLargeSize; else staticTemplate\">\n  <div\n    class=\"master-detail-layout d-flex flex-column\"\n    [ngClass]=\"containerClass\">\n    <ng-container *ngTemplateOutlet=\"headingTemplate\"></ng-container>\n    <si-split\n      class=\"w-100 flex-grow-1\"\n      orientation=\"horizontal\"\n      [stateId]=\"stateId\"\n      [sizes]=\"splitSizes\"\n      (sizesChange)=\"onSplitSizesChange($event)\">\n      <si-split-part\n        [showCollapseButton]=\"false\"\n        [showHeader]=\"false\"\n        [minSize]=\"minMasterSize\"\n        scale=\"none\"\n        [stateId]=\"masterStateId\"\n      >\n        <ng-container *ngTemplateOutlet=\"masterTemplate\"></ng-container>\n      </si-split-part>\n      <si-split-part\n        [showCollapseButton]=\"false\"\n        [showHeader]=\"false\"\n        [minSize]=\"minDetailSize\"\n        scale=\"auto\"\n        [stateId]=\"detailStateId\"\n      >\n        <ng-container *ngTemplateOutlet=\"detailTemplate\"></ng-container>\n      </si-split-part>\n    </si-split>\n  </div>\n</ng-container>\n\n<ng-template #staticTemplate>\n  <div\n    class=\"master-detail-layout d-flex flex-column\"\n    [ngClass]=\"containerClass\"\n    [class.large-size]=\"hasLargeSize\">\n    <ng-container *ngTemplateOutlet=\"headingTemplate\"></ng-container>\n    <div\n      class=\"master-detail-container d-flex flex-grow-1\"\n      [class.details-active]=\"detailsActive && !hasLargeSize\">\n      <ng-container *ngTemplateOutlet=\"masterTemplate; context: { customClass: hasLargeSize ? 'col-3 ps-0 pe-4' : 'col-6 px-0' }\"></ng-container>\n      <ng-container *ngTemplateOutlet=\"detailTemplate; context: { customClass: hasLargeSize ? 'col-9 ps-4 pe-0' : 'col-6 px-0' }\"></ng-container>\n    </div>\n  </div>\n</ng-template>\n\n<!-- Templates -->\n<ng-template #headingTemplate>\n  <h2\n    *ngIf=\"heading\"\n    class=\"master-detail-heading page-top-title flex-grow-0 flex-shrink-0 text-secondary\"\n    [class.text-truncate]=\"truncateHeading\">{{ heading | translate }}</h2>\n</ng-template>\n\n<ng-template #masterTemplate let-customClass=\"customClass\">\n  <div\n    class=\"master-container d-flex flex-column\"\n    [ngClass]=\"[\n      masterContainerClass,\n      (customClass ? customClass : '')\n    ]\"\n    [style.max-inline-size]=\"maxMasterSize\"\n  >\n    <div class=\"master-search-actions d-flex align-items-center\">\n      <ng-content select=\"[slot=masterSearch]\"></ng-content>\n      <ng-content select=\"[slot=masterActions]\"></ng-content>\n    </div>\n    <div class=\"master-data-container d-flex\">\n      <ng-content select=\"[slot=masterData]\"></ng-content>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #detailTemplate let-customClass=\"customClass\">\n  <div\n    class=\"detail-container d-flex flex-column\"\n    [ngClass]=\"[\n      detailContainerClass,\n      (customClass ? customClass : '')\n    ]\"\n    [style.max-inline-size]=\"maxDetailSize\"\n   >\n    <div class=\"detail-heading-actions d-flex align-items-center\">\n      <button\n        *ngIf=\"!this.hasLargeSize && !this.hideBackButton\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-secondary element-back\"\n        (click)=\"detailsBackClicked()\"\n      ><span class=\"visually-hidden\">{{ detailsBackButtonText | translate }}</span></button>\n      <span\n        *ngIf=\"detailsHeading\"\n        class=\"detail-heading si-title-1 me-auto ms-6 my-auto text-truncate\">{{ detailsHeading | translate }}</span>\n      <ng-content select=\"[slot=detailActions]\"></ng-content>\n    </div>\n    <ng-content select=\"[slot=details]\"></ng-content>\n  </div>\n</ng-template>\n", styles: [":host{display:flex;flex-direction:column;overflow-x:hidden;overflow-y:auto}:host ::ng-deep [slot=masterSearch]{flex:1 1 0}:host ::ng-deep [slot=masterSearch]+[slot=masterActions]{margin-inline-start:12px}:host ::ng-deep [slot=masterActions]{margin-inline-start:auto}:host ::ng-deep [slot=masterData],:host ::ng-deep [slot=details]{flex:1 1 0;min-block-size:0}:host ::ng-deep [slot=detailActions]{margin-inline-start:auto}:host ::ng-deep .detail-heading+[slot=detailActions]{margin-inline-start:12px}:host ::ng-deep si-split{--si-split-gutter-background: transparent;min-block-size:0}:host ::ng-deep si-split si-split-part,:host ::ng-deep si-split .si-split-container,:host ::ng-deep si-split .si-split-part-content{overflow:visible}:host.animate{--si-master-detail-animation-duration: calc(.5s * var(--element-animations-enabled, 1))}.master-detail-layout{overflow-x:hidden;position:relative}.master-detail-layout.large-size{--si-master-detail-container-width: 100%;--si-master-detail-container-transition: none;--si-master-detail-container-transform: none;--si-master-detail-transition: none;--si-master-detail-opacity: 1;--si-master-detail-pointer-events: auto}.master-detail-heading{margin-block-end:20px}.master-detail-container{--si-master-detail-transition: opacity var(--si-master-detail-animation-duration, 0) ease-in-out;--si-master-detail-detail-opacity: 0;--si-master-detail-detail-pointer-events: none;inline-size:var(--si-master-detail-container-width, 200%);transform:var(--si-master-detail-container-transform, none);transform-origin:top left;transition:var(--si-master-detail-container-transition, transform var(--si-master-detail-animation-duration, 0) ease-in-out)}.master-detail-container.details-active{--si-master-detail-container-transform: translateX(-50%);--si-master-detail-master-opacity: 0;--si-master-detail-detail-opacity: 1;--si-master-detail-master-pointer-events: none;--si-master-detail-detail-pointer-events: auto}.master-detail-layout,.master-detail-container,.master-container,.detail-container,.master-data-container{flex:1 1 0;min-block-size:0}.master-container,.detail-container{transition:var(--si-master-detail-transition, none)}.master-container{opacity:var(--si-master-detail-opacity, var(--si-master-detail-master-opacity, 1));pointer-events:var(--si-master-detail-pointer-events, var(--si-master-detail-master-pointer-events, auto))}.detail-container{opacity:var(--si-master-detail-opacity, var(--si-master-detail-detail-opacity, 1));pointer-events:var(--si-master-detail-pointer-events, var(--si-master-detail-detail-pointer-events, auto))}.master-search-actions,.detail-heading-actions{min-block-size:32px;margin-block-end:12px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: SiSplitComponent, selector: "si-split", inputs: ["orientation", "sizes", "gutterSize", "stateId"], outputs: ["sizesChange"] }, { kind: "component", type: SiSplitPartComponent, selector: "si-split-part", inputs: ["actions", "collapseDirection", "collapseIconClass", "collapseToMinSize", "headerStatusColor", "headerStatusIconClass", "headerTemplate", "heading", "minSize", "removeContentOnCollapse", "scale", "showCollapseButton", "showHeader", "collapseLabel", "stateId", "expanded", "size"], outputs: ["collapseChanged", "stateChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-master-detail-container', host: { class: 'si-layout-inner' }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngIf=\"resizableParts && hasLargeSize; else staticTemplate\">\n  <div\n    class=\"master-detail-layout d-flex flex-column\"\n    [ngClass]=\"containerClass\">\n    <ng-container *ngTemplateOutlet=\"headingTemplate\"></ng-container>\n    <si-split\n      class=\"w-100 flex-grow-1\"\n      orientation=\"horizontal\"\n      [stateId]=\"stateId\"\n      [sizes]=\"splitSizes\"\n      (sizesChange)=\"onSplitSizesChange($event)\">\n      <si-split-part\n        [showCollapseButton]=\"false\"\n        [showHeader]=\"false\"\n        [minSize]=\"minMasterSize\"\n        scale=\"none\"\n        [stateId]=\"masterStateId\"\n      >\n        <ng-container *ngTemplateOutlet=\"masterTemplate\"></ng-container>\n      </si-split-part>\n      <si-split-part\n        [showCollapseButton]=\"false\"\n        [showHeader]=\"false\"\n        [minSize]=\"minDetailSize\"\n        scale=\"auto\"\n        [stateId]=\"detailStateId\"\n      >\n        <ng-container *ngTemplateOutlet=\"detailTemplate\"></ng-container>\n      </si-split-part>\n    </si-split>\n  </div>\n</ng-container>\n\n<ng-template #staticTemplate>\n  <div\n    class=\"master-detail-layout d-flex flex-column\"\n    [ngClass]=\"containerClass\"\n    [class.large-size]=\"hasLargeSize\">\n    <ng-container *ngTemplateOutlet=\"headingTemplate\"></ng-container>\n    <div\n      class=\"master-detail-container d-flex flex-grow-1\"\n      [class.details-active]=\"detailsActive && !hasLargeSize\">\n      <ng-container *ngTemplateOutlet=\"masterTemplate; context: { customClass: hasLargeSize ? 'col-3 ps-0 pe-4' : 'col-6 px-0' }\"></ng-container>\n      <ng-container *ngTemplateOutlet=\"detailTemplate; context: { customClass: hasLargeSize ? 'col-9 ps-4 pe-0' : 'col-6 px-0' }\"></ng-container>\n    </div>\n  </div>\n</ng-template>\n\n<!-- Templates -->\n<ng-template #headingTemplate>\n  <h2\n    *ngIf=\"heading\"\n    class=\"master-detail-heading page-top-title flex-grow-0 flex-shrink-0 text-secondary\"\n    [class.text-truncate]=\"truncateHeading\">{{ heading | translate }}</h2>\n</ng-template>\n\n<ng-template #masterTemplate let-customClass=\"customClass\">\n  <div\n    class=\"master-container d-flex flex-column\"\n    [ngClass]=\"[\n      masterContainerClass,\n      (customClass ? customClass : '')\n    ]\"\n    [style.max-inline-size]=\"maxMasterSize\"\n  >\n    <div class=\"master-search-actions d-flex align-items-center\">\n      <ng-content select=\"[slot=masterSearch]\"></ng-content>\n      <ng-content select=\"[slot=masterActions]\"></ng-content>\n    </div>\n    <div class=\"master-data-container d-flex\">\n      <ng-content select=\"[slot=masterData]\"></ng-content>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #detailTemplate let-customClass=\"customClass\">\n  <div\n    class=\"detail-container d-flex flex-column\"\n    [ngClass]=\"[\n      detailContainerClass,\n      (customClass ? customClass : '')\n    ]\"\n    [style.max-inline-size]=\"maxDetailSize\"\n   >\n    <div class=\"detail-heading-actions d-flex align-items-center\">\n      <button\n        *ngIf=\"!this.hasLargeSize && !this.hideBackButton\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-secondary element-back\"\n        (click)=\"detailsBackClicked()\"\n      ><span class=\"visually-hidden\">{{ detailsBackButtonText | translate }}</span></button>\n      <span\n        *ngIf=\"detailsHeading\"\n        class=\"detail-heading si-title-1 me-auto ms-6 my-auto text-truncate\">{{ detailsHeading | translate }}</span>\n      <ng-content select=\"[slot=detailActions]\"></ng-content>\n    </div>\n    <ng-content select=\"[slot=details]\"></ng-content>\n  </div>\n</ng-template>\n", styles: [":host{display:flex;flex-direction:column;overflow-x:hidden;overflow-y:auto}:host ::ng-deep [slot=masterSearch]{flex:1 1 0}:host ::ng-deep [slot=masterSearch]+[slot=masterActions]{margin-inline-start:12px}:host ::ng-deep [slot=masterActions]{margin-inline-start:auto}:host ::ng-deep [slot=masterData],:host ::ng-deep [slot=details]{flex:1 1 0;min-block-size:0}:host ::ng-deep [slot=detailActions]{margin-inline-start:auto}:host ::ng-deep .detail-heading+[slot=detailActions]{margin-inline-start:12px}:host ::ng-deep si-split{--si-split-gutter-background: transparent;min-block-size:0}:host ::ng-deep si-split si-split-part,:host ::ng-deep si-split .si-split-container,:host ::ng-deep si-split .si-split-part-content{overflow:visible}:host.animate{--si-master-detail-animation-duration: calc(.5s * var(--element-animations-enabled, 1))}.master-detail-layout{overflow-x:hidden;position:relative}.master-detail-layout.large-size{--si-master-detail-container-width: 100%;--si-master-detail-container-transition: none;--si-master-detail-container-transform: none;--si-master-detail-transition: none;--si-master-detail-opacity: 1;--si-master-detail-pointer-events: auto}.master-detail-heading{margin-block-end:20px}.master-detail-container{--si-master-detail-transition: opacity var(--si-master-detail-animation-duration, 0) ease-in-out;--si-master-detail-detail-opacity: 0;--si-master-detail-detail-pointer-events: none;inline-size:var(--si-master-detail-container-width, 200%);transform:var(--si-master-detail-container-transform, none);transform-origin:top left;transition:var(--si-master-detail-container-transition, transform var(--si-master-detail-animation-duration, 0) ease-in-out)}.master-detail-container.details-active{--si-master-detail-container-transform: translateX(-50%);--si-master-detail-master-opacity: 0;--si-master-detail-detail-opacity: 1;--si-master-detail-master-pointer-events: none;--si-master-detail-detail-pointer-events: auto}.master-detail-layout,.master-detail-container,.master-container,.detail-container,.master-data-container{flex:1 1 0;min-block-size:0}.master-container,.detail-container{transition:var(--si-master-detail-transition, none)}.master-container{opacity:var(--si-master-detail-opacity, var(--si-master-detail-master-opacity, 1));pointer-events:var(--si-master-detail-pointer-events, var(--si-master-detail-master-pointer-events, auto))}.detail-container{opacity:var(--si-master-detail-opacity, var(--si-master-detail-detail-opacity, 1));pointer-events:var(--si-master-detail-pointer-events, var(--si-master-detail-detail-pointer-events, auto))}.master-search-actions,.detail-heading-actions{min-block-size:32px;margin-block-end:12px}\n"] }]
        }], propDecorators: { largeLayoutBreakpoint: [{
                type: Input
            }], hasLargeSizeChange: [{
                type: Output
            }], detailsActive: [{
                type: Input
            }], detailsActiveChange: [{
                type: Output
            }], heading: [{
                type: Input
            }], truncateHeading: [{
                type: Input
            }], detailsHeading: [{
                type: Input
            }], resizableParts: [{
                type: Input
            }], hideBackButton: [{
                type: Input
            }], detailsBackButtonText: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], masterContainerClass: [{
                type: Input
            }], detailContainerClass: [{
                type: Input
            }], masterContainerWidth: [{
                type: Input
            }], masterContainerWidthChange: [{
                type: Output
            }], minMasterSize: [{
                type: Input
            }], minDetailSize: [{
                type: Input
            }], stateId: [{
                type: Input
            }], animate: [{
                type: HostBinding,
                args: ['class.animate']
            }], opacity: [{
                type: HostBinding,
                args: ['style.opacity']
            }] } });

class SiMasterDetailContainerModule {
}
SiMasterDetailContainerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiMasterDetailContainerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerModule, declarations: [SiMasterDetailContainerComponent], imports: [CommonModule,
        SiSplitModule,
        SiTranslateModule], exports: [SiMasterDetailContainerComponent] });
SiMasterDetailContainerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerModule, imports: [CommonModule,
        SiSplitModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiMasterDetailContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiMasterDetailContainerComponent],
                    imports: [
                        CommonModule,
                        SiSplitModule,
                        SiTranslateModule
                    ],
                    exports: [SiMasterDetailContainerComponent]
                }]
        }] });

var AlertDialogResult;
(function (AlertDialogResult) {
    AlertDialogResult[AlertDialogResult["Confirm"] = 0] = "Confirm";
})(AlertDialogResult || (AlertDialogResult = {}));
var EditAbortDialogResult;
(function (EditAbortDialogResult) {
    EditAbortDialogResult[EditAbortDialogResult["Save"] = 0] = "Save";
    EditAbortDialogResult[EditAbortDialogResult["Abort"] = 1] = "Abort";
    EditAbortDialogResult[EditAbortDialogResult["Cancel"] = 2] = "Cancel";
})(EditAbortDialogResult || (EditAbortDialogResult = {}));
var ConfirmationDialogResult;
(function (ConfirmationDialogResult) {
    ConfirmationDialogResult[ConfirmationDialogResult["Confirm"] = 0] = "Confirm";
    ConfirmationDialogResult[ConfirmationDialogResult["Decline"] = 1] = "Decline";
})(ConfirmationDialogResult || (ConfirmationDialogResult = {}));
var DeleteConfirmationDialogResult;
(function (DeleteConfirmationDialogResult) {
    DeleteConfirmationDialogResult[DeleteConfirmationDialogResult["Delete"] = 0] = "Delete";
    DeleteConfirmationDialogResult[DeleteConfirmationDialogResult["Cancel"] = 1] = "Cancel";
})(DeleteConfirmationDialogResult || (DeleteConfirmationDialogResult = {}));

class SiAlertDialogComponent {
    constructor() {
        this.heading = '';
        this.message = '';
        this.confirmBtnName = 'OK';
        this.translationParams = {};
        this.closed = new EventEmitter();
    }
    onConfirm() {
        this.closed.emit(AlertDialogResult.Confirm);
    }
}
SiAlertDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAlertDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiAlertDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiAlertDialogComponent, selector: "si-alert-dialog", inputs: { titleId: "titleId", heading: "heading", message: "message", confirmBtnName: "confirmBtnName", translationParams: "translationParams" }, outputs: { closed: "closed" }, ngImport: i0, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"confirmBtnName | translate\"\n    (click)=\"onConfirm()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"onConfirm()\">{{ confirmBtnName | translate }}</button>\n</div>\n", dependencies: [{ kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiAlertDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-alert-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"confirmBtnName | translate\"\n    (click)=\"onConfirm()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"onConfirm()\">{{ confirmBtnName | translate }}</button>\n</div>\n" }]
        }], propDecorators: { titleId: [{
                type: Input
            }], heading: [{
                type: Input
            }], message: [{
                type: Input
            }], confirmBtnName: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

class SiConfirmationDialogComponent {
    constructor() {
        this.heading = '';
        this.message = '';
        this.confirmBtnName = 'Yes';
        this.declineBtnName = 'No';
        this.translationParams = {};
        this.closed = new EventEmitter();
    }
    onConfirm() {
        this.closed.emit(ConfirmationDialogResult.Confirm);
    }
    onDecline() {
        this.closed.emit(ConfirmationDialogResult.Decline);
    }
}
SiConfirmationDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConfirmationDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiConfirmationDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiConfirmationDialogComponent, selector: "si-confirmation-dialog", inputs: { titleId: "titleId", heading: "heading", message: "message", confirmBtnName: "confirmBtnName", declineBtnName: "declineBtnName", translationParams: "translationParams" }, outputs: { closed: "closed" }, ngImport: i0, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"declineBtnName | translate\"\n    (click)=\"onDecline()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onDecline()\">{{ declineBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"onConfirm()\">{{ confirmBtnName | translate }}</button>\n</div>\n", dependencies: [{ kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiConfirmationDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-confirmation-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"declineBtnName | translate\"\n    (click)=\"onDecline()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onDecline()\">{{ declineBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"onConfirm()\">{{ confirmBtnName | translate }}</button>\n</div>\n" }]
        }], propDecorators: { titleId: [{
                type: Input
            }], heading: [{
                type: Input
            }], message: [{
                type: Input
            }], confirmBtnName: [{
                type: Input
            }], declineBtnName: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

class SiDeleteConfirmationDialogComponent {
    constructor() {
        this.heading = '';
        this.message = 'Do you really want to delete the selected elements?';
        this.deleteBtnName = 'Delete';
        this.cancelBtnName = 'Cancel';
        this.translationParams = {};
        this.closed = new EventEmitter();
    }
    onDelete() {
        this.closed.emit(DeleteConfirmationDialogResult.Delete);
    }
    onCancel() {
        this.closed.emit(DeleteConfirmationDialogResult.Cancel);
    }
}
SiDeleteConfirmationDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDeleteConfirmationDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiDeleteConfirmationDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDeleteConfirmationDialogComponent, selector: "si-delete-confirmation-dialog", inputs: { titleId: "titleId", heading: "heading", message: "message", deleteBtnName: "deleteBtnName", cancelBtnName: "cancelBtnName", translationParams: "translationParams" }, outputs: { closed: "closed" }, ngImport: i0, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"onCancel()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onCancel()\">{{ cancelBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-danger\" (click)=\"onDelete()\">{{ deleteBtnName | translate }}</button>\n</div>\n", dependencies: [{ kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDeleteConfirmationDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-delete-confirmation-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"onCancel()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ message | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onCancel()\">{{ cancelBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-danger\" (click)=\"onDelete()\">{{ deleteBtnName | translate }}</button>\n</div>\n" }]
        }], propDecorators: { titleId: [{
                type: Input
            }], heading: [{
                type: Input
            }], message: [{
                type: Input
            }], deleteBtnName: [{
                type: Input
            }], cancelBtnName: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

class SiEditAbortDialogComponent {
    constructor() {
        this.heading = '';
        this.message = 'Do you want to save changes to the modified element?';
        this.saveBtnName = 'Save';
        this.abortBtnName = `Don't save`;
        this.cancelBtnName = 'Cancel';
        this.disableSave = false;
        this.disableSaveMessage = 'Do you want to discard the changes?';
        this.disableSaveAbortBtnName = `Discard`;
        this.translationParams = {};
        this.closed = new EventEmitter();
    }
    onSave() {
        this.closed.emit(EditAbortDialogResult.Save);
    }
    onAbort() {
        this.closed.emit(EditAbortDialogResult.Abort);
    }
    onCancel() {
        this.closed.emit(EditAbortDialogResult.Cancel);
    }
}
SiEditAbortDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEditAbortDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiEditAbortDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiEditAbortDialogComponent, selector: "si-edit-abort-dialog", inputs: { titleId: "titleId", heading: "heading", message: "message", saveBtnName: "saveBtnName", abortBtnName: "abortBtnName", cancelBtnName: "cancelBtnName", disableSave: "disableSave", disableSaveMessage: "disableSaveMessage", disableSaveAbortBtnName: "disableSaveAbortBtnName", translationParams: "translationParams" }, outputs: { closed: "closed" }, ngImport: i0, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"onCancel()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ (!disableSave ? message : disableSaveMessage) | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onCancel()\">{{ cancelBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-danger\" (click)=\"onAbort()\">{{ (!disableSave ? abortBtnName : disableSaveAbortBtnName) | translate }}</button>\n  <button type=\"button\" class=\"btn btn-primary\" *ngIf=\"!disableSave\" (click)=\"onSave()\">{{ saveBtnName | translate }}</button>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiEditAbortDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-edit-abort-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate:translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"onCancel()\"\n  > </button>\n</div>\n<div class=\"modal-body text-pre-wrap text-break\">{{ (!disableSave ? message : disableSaveMessage) | translate:translationParams }}</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"onCancel()\">{{ cancelBtnName | translate }}</button>\n  <button type=\"button\" class=\"btn btn-danger\" (click)=\"onAbort()\">{{ (!disableSave ? abortBtnName : disableSaveAbortBtnName) | translate }}</button>\n  <button type=\"button\" class=\"btn btn-primary\" *ngIf=\"!disableSave\" (click)=\"onSave()\">{{ saveBtnName | translate }}</button>\n</div>\n" }]
        }], propDecorators: { titleId: [{
                type: Input
            }], heading: [{
                type: Input
            }], message: [{
                type: Input
            }], saveBtnName: [{
                type: Input
            }], abortBtnName: [{
                type: Input
            }], cancelBtnName: [{
                type: Input
            }], disableSave: [{
                type: Input
            }], disableSaveMessage: [{
                type: Input
            }], disableSaveAbortBtnName: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

let idCounter$1 = 1;
const createModalConfig = (inputs) => {
    if (inputs) {
        Object.keys(inputs).forEach(k => inputs[k] === undefined && delete inputs[k]);
    }
    else {
        inputs = {};
    }
    const config = {
        initialState: inputs,
        ignoreBackdropClick: true,
        keyboard: false,
        animated: true,
        class: 'modal-dialog-centered'
    };
    const id = `__si-modal-id-${idCounter$1++}`;
    config.initialState.titleId = id;
    config.ariaLabelledBy = id;
    return config;
};

class ModalRef {
    constructor() {
        this.hidden = new Subject();
        this.shown = new ReplaySubject(1);
        this.data = {};
        this.ignoreBackdropClick = true;
        this.dialogClass = '';
        this.layer = 0;
        this.isCurrent = () => false;
        this.detach = () => { };
        this.hide = () => { };
    }
    get content() {
        return undefined;
    }
}

class SiModalComponent {
    constructor() {
        this.modalRef = inject((ModalRef));
        this.changeDetectorRef = inject(ChangeDetectorRef);
        this.dialogClass = this.modalRef.dialogClass ?? '';
        this.titleId = this.modalRef.data?.ariaLabelledBy ?? '';
        this.init = false;
        this.show = false;
        this.clickStartInDialog = false;
    }
    ngOnInit() {
        this.init = true;
        this.showTimer = setTimeout(() => {
            this.show = true;
            this.changeDetectorRef.markForCheck();
        }, this.animationTime(150));
    }
    ngAfterViewInit() {
        this.modalContainerRef.nativeElement.querySelector('.modal-dialog').focus();
        setTimeout(() => this.modalRef?.shown.next(this.modalContainerRef));
    }
    ngOnDestroy() {
        this.hideBackdrop();
    }
    hideDialog() {
        if (!this.show) {
            return;
        }
        clearTimeout(this.showTimer);
        this.show = false;
        setTimeout(() => {
            this.hideBackdrop();
            this.changeDetectorRef.markForCheck();
            setTimeout(() => this.modalRef?.detach(), this.animationTime(150));
        }, this.animationTime(300));
        this.modalRef?.hidden.next();
        this.modalRef?.hidden.complete();
        this.changeDetectorRef.markForCheck();
    }
    showBackdrop() {
        if (this.modalRef?.data.animated !== false) {
            this.showBackdropClass = false;
            this.backdropTimer = setTimeout(() => {
                this.showBackdropClass = true;
                this.changeDetectorRef.markForCheck();
            }, 16);
        }
        else {
            this.showBackdropClass = true;
        }
        this.origBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.changeDetectorRef.markForCheck();
    }
    hideBackdrop() {
        clearTimeout(this.backdropTimer);
        if (this.showBackdropClass !== undefined) {
            this.showBackdropClass = false;
        }
        if (this.origBodyOverflow !== undefined) {
            document.body.style.overflow = this.origBodyOverflow;
            this.origBodyOverflow = undefined;
        }
    }
    clickStarted(event) {
        this.clickStartInDialog = event.target !== this.modalContainerRef.nativeElement;
    }
    onClickStop(event) {
        const clickedInBackdrop = event.target === this.modalContainerRef.nativeElement && !this.clickStartInDialog;
        if (this.modalRef?.ignoreBackdropClick || !clickedInBackdrop) {
            this.clickStartInDialog = false;
            return;
        }
        // Called when backdrop close is allowed and user clicks on the backdrop
        this.hideDialog();
    }
    onEsc(event) {
        if (this.modalRef?.data.keyboard && this.modalRef?.isCurrent()) {
            event.preventDefault();
            this.hideDialog();
        }
    }
    animationTime(millis) {
        return this.modalRef?.data.animated !== false ? millis : 0;
    }
}
SiModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiModalComponent, selector: "si-modal", host: { listeners: { "mousedown": "clickStarted($event)", "mouseup": "onClickStop($event)", "window:keydown.esc": "onEsc($event)" } }, viewQueries: [{ propertyName: "modalContainerRef", first: true, predicate: ["modalContainer"], descendants: true }], ngImport: i0, template: "<div\n  *ngIf=\"showBackdropClass !== undefined\"\n  class=\"modal-backdrop\"\n  [class.fade]=\"modalRef.data?.animated !== false\"\n  [class.show]=\"showBackdropClass\"\n></div>\n<div\n  *ngIf=\"init\"\n  #modalContainer\n  role=\"dialog\"\n  class=\"modal d-block\"\n  [class.fade]=\"modalRef.data?.animated !== false\"\n  [class.show]=\"show\"\n  aria-modal=\"true\"\n  [attr.aria-labelledby]=\"titleId\"\n>\n  <div class=\"modal-dialog\" [ngClass]=\"dialogClass\" cdkTrapFocus tabindex=\"-1\">\n    <div class=\"modal-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-modal', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  *ngIf=\"showBackdropClass !== undefined\"\n  class=\"modal-backdrop\"\n  [class.fade]=\"modalRef.data?.animated !== false\"\n  [class.show]=\"showBackdropClass\"\n></div>\n<div\n  *ngIf=\"init\"\n  #modalContainer\n  role=\"dialog\"\n  class=\"modal d-block\"\n  [class.fade]=\"modalRef.data?.animated !== false\"\n  [class.show]=\"show\"\n  aria-modal=\"true\"\n  [attr.aria-labelledby]=\"titleId\"\n>\n  <div class=\"modal-dialog\" [ngClass]=\"dialogClass\" cdkTrapFocus tabindex=\"-1\">\n    <div class=\"modal-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { modalContainerRef: [{
                type: ViewChild,
                args: ['modalContainer']
            }], clickStarted: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onClickStop: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onEsc: [{
                type: HostListener,
                args: ['window:keydown.esc', ['$event']]
            }] } });

class ModalRefImpl extends ModalRef {
    get content() {
        return this.componentRef?.instance;
    }
    constructor(contentRef, layer, data, previousFocusElement) {
        super();
        this.contentRef = contentRef;
        this.layer = layer;
        this.data = data;
        this.ignoreBackdropClick = data.ignoreBackdropClick !== false;
        this.dialogClass = data?.class ?? '';
        this.previousFocusElement = previousFocusElement;
    }
}
class SiModalService {
    constructor() {
        this.modalsCount = 0;
        this.appRef = inject(ApplicationRef);
        this.injector = inject(Injector);
        this.environmentInjector = inject(EnvironmentInjector);
    }
    show(content, config) {
        const modalRef = new ModalRefImpl(content, ++this.modalsCount, config, document.activeElement);
        const siModalRef = this.attachComponent(modalRef);
        modalRef.modalCompRef = siModalRef;
        modalRef.detach = () => this.detach(modalRef);
        modalRef.hide = () => siModalRef.instance.hideDialog();
        modalRef.isCurrent = () => modalRef.layer === this.modalsCount;
        if (this.modalsCount === 1) {
            siModalRef.instance.showBackdrop();
        }
        return modalRef;
    }
    attachComponent(modalRef) {
        const providers = [{ provide: ModalRef, useValue: modalRef }];
        modalRef.data.providers?.forEach(p => providers.push(p));
        const injector = Injector.create({ providers, parent: modalRef.data.injector ?? this.buildInjector() });
        const componentRef = createComponent(SiModalComponent, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector,
            projectableNodes: this.getContentProjectableNodes(injector, modalRef)
        });
        document.body.appendChild(componentRef.location.nativeElement);
        this.appRef.attachView(componentRef.hostView);
        return componentRef;
    }
    getContentProjectableNodes(injector, modalRef) {
        if (modalRef.contentRef instanceof TemplateRef) {
            modalRef.viewRef = modalRef.contentRef.createEmbeddedView({ modalRef, ...modalRef.data.initialState });
            this.appRef.attachView(modalRef.viewRef);
            return [modalRef.viewRef.rootNodes];
        }
        modalRef.componentRef = createComponent(modalRef.contentRef, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector
        });
        Object.assign(modalRef.componentRef.instance, modalRef.data?.initialState); // set initial @Input()s
        modalRef.componentRef.changeDetectorRef.detectChanges();
        this.appRef.attachView(modalRef.componentRef.hostView);
        return [[modalRef.componentRef.location.nativeElement]];
    }
    detach(modalRef) {
        const ref = modalRef;
        if (ref.modalCompRef) {
            ref.modalCompRef?.destroy();
            ref.modalCompRef = undefined;
            ref.componentRef?.destroy();
            ref.componentRef = undefined;
            ref.viewRef?.destroy();
            ref.viewRef = undefined;
            ref.shown.complete();
            this.modalsCount--;
        }
        if (ref.previousFocusElement?.focus) {
            ref.previousFocusElement.focus();
        }
    }
    // TODO remove once translation must be defined at application start
    // Notification service is provided in 'root'. If no translation is defined, SiNoTranslateService is not provided
    buildInjector() {
        let injector = this.injector;
        if (!injector.get(SiTranslateService, null)) {
            injector = Injector.create({
                providers: [{ provide: SiTranslateService, useClass: SiNoTranslateService, deps: [] }],
                parent: this.injector
            });
        }
        return injector;
    }
}
SiModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * The service provides convenient methods to show common modal dialogs.
 * All return an observable, which emit the dialog results. After emitting,
 * the observables complete. Therefore, clients do not need to unsubscribe.
 */
class SiActionDialogService {
    constructor() {
        this.modalService = inject(SiModalService);
    }
    showAlertDialog(message, heading, confirmBtnName, translationParams) {
        return new Observable(subscriber => {
            const config = createModalConfig({
                message,
                heading,
                confirmBtnName,
                translationParams
            });
            const modalRef = this.modalService.show(SiAlertDialogComponent, config);
            subscriber.__modalRef = modalRef;
            const subscription = modalRef.content.closed
                .subscribe((alertResult) => {
                subscription.unsubscribe();
                modalRef.hide();
                subscriber.next(alertResult);
                subscriber.complete();
            });
            return () => this.close(subscription, modalRef);
        });
    }
    showConfirmationDialog(message, heading, confirmBtnName, declineBtnName, translationParams) {
        return new Observable(subscriber => {
            const config = createModalConfig({
                message,
                heading,
                confirmBtnName,
                declineBtnName,
                translationParams
            });
            const modalRef = this.modalService.show(SiConfirmationDialogComponent, config);
            subscriber.__modalRef = modalRef;
            const subscription = modalRef.content.closed
                .subscribe((confirmationResult) => {
                subscription.unsubscribe();
                modalRef.hide();
                subscriber.next(confirmationResult);
                subscriber.complete();
            });
            return () => this.close(subscription, modalRef);
        });
    }
    showEditAbortDialog(disableSave, message, heading, saveBtnName, abortBtnName, cancelBtnName, disableSaveMessage, disableSaveAbortBtnName, translationParams) {
        return new Observable(subscriber => {
            const config = createModalConfig({
                message,
                heading,
                saveBtnName,
                abortBtnName,
                cancelBtnName,
                disableSave,
                disableSaveMessage,
                disableSaveAbortBtnName,
                translationParams
            });
            const modalRef = this.modalService.show(SiEditAbortDialogComponent, config);
            subscriber.__modalRef = modalRef;
            const subscription = modalRef.content.closed
                .subscribe((confirmationResult) => {
                subscription.unsubscribe();
                modalRef.hide();
                subscriber.next(confirmationResult);
                subscriber.complete();
            });
            return () => this.close(subscription, modalRef);
        });
    }
    showDeleteConfirmationDialog(message, heading, deleteBtnName, cancelBtnName, translationParams) {
        return new Observable(subscriber => {
            const config = createModalConfig({
                message,
                heading,
                deleteBtnName,
                cancelBtnName,
                translationParams
            });
            const modalRef = this.modalService.show(SiDeleteConfirmationDialogComponent, config);
            subscriber.__modalRef = modalRef;
            const subscription = modalRef.content.closed
                .subscribe((confirmationResult) => {
                subscription.unsubscribe();
                modalRef.hide();
                subscriber.next(confirmationResult);
                subscriber.complete();
            });
            return () => this.close(subscription, modalRef);
        });
    }
    close(subscription, modalRef) {
        if (!subscription.closed) {
            subscription.unsubscribe();
            modalRef.detach();
        }
    }
}
SiActionDialogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiActionDialogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogService, decorators: [{
            type: Injectable
        }] });

class SiModalModule {
}
SiModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiModalModule, declarations: [SiModalComponent], imports: [A11yModule, CommonModule] });
SiModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalModule, imports: [A11yModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11yModule, CommonModule],
                    declarations: [SiModalComponent]
                }]
        }] });

class SiActionDialogModule {
}
SiActionDialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiActionDialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogModule, declarations: [SiAlertDialogComponent,
        SiConfirmationDialogComponent,
        SiDeleteConfirmationDialogComponent,
        SiEditAbortDialogComponent], imports: [CommonModule,
        RouterModule,
        SiModalModule,
        SiSwitchModule,
        SiTranslateModule] });
SiActionDialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogModule, providers: [SiActionDialogService], imports: [CommonModule,
        RouterModule,
        SiModalModule,
        SiSwitchModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiActionDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiAlertDialogComponent,
                        SiConfirmationDialogComponent,
                        SiDeleteConfirmationDialogComponent,
                        SiEditAbortDialogComponent
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiModalModule,
                        SiSwitchModule,
                        SiTranslateModule
                    ],
                    providers: [SiActionDialogService]
                }]
        }] });

class SiColumnSelectionDialogComponent {
    constructor() {
        this.hiddenText = 'Hidden';
        this.visibleText = 'Visible';
        this.restoreEnabled = false;
        this.translationParams = {};
        this.closed = new EventEmitter();
        this.tempHeaderData = [];
        this.visibleIds = [];
        this.trackByItem = (_index, item) => item;
        this.liveAnnouncer = inject(LiveAnnouncer);
        this.translateService = inject(SiTranslateService);
    }
    ngOnInit() {
        this.createBackupData();
        this.visibleIds = this.columns.filter(column => column.visible).map(column => column.id);
    }
    submitColumnSelection() {
        this.closed.emit({ type: 'ok', columns: this.columns });
    }
    cancelColumnSelection() {
        this.columns.length = 0;
        this.tempHeaderData.forEach(element => this.columns.push(element));
        this.closed.emit({ type: 'cancel', columns: this.columns });
    }
    drop(event) {
        if (this.columns[event.currentIndex].draggable) {
            moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
            this.closed.emit({ type: 'instant', columns: this.columns });
        }
    }
    restoreToDefault() {
        this.closed.emit({
            type: 'restoreDefault',
            columns: this.columns,
            updateColumns: columns => {
                this.columns = columns;
                this.createBackupData();
            }
        });
    }
    changeColumnVisibilityFromListbox({ value }) {
        for (const column of this.columns) {
            column.visible = value.includes(column.id);
        }
        this.closed.emit({ type: 'instant', columns: this.columns });
    }
    moveDown(index, event) {
        if (this.columns[index].draggable) {
            let targetIndex = index + 1;
            while (this.columns[targetIndex] && !this.columns[targetIndex].draggable) {
                targetIndex++;
            }
            if (targetIndex !== index && this.columns[targetIndex]?.draggable) {
                event.preventDefault();
                moveItemInArray(this.columns, index, targetIndex);
                // When moving the first partially visible item down,
                // the browser tries to keep its position stable within the viewport by automatically scrolling down.
                // This behavior is not wanted here, so we restore the previous scroll after moving the item
                // TODO: check if this could be solved easier
                if (this.listOptions.get(index).element.getBoundingClientRect().top
                    <= this.modalBodyElement.nativeElement.getBoundingClientRect().top) {
                    const previousScrollTop = this.modalBodyElement.nativeElement.scrollTop;
                    setTimeout(() => this.modalBodyElement.nativeElement.scrollTop = previousScrollTop);
                }
                // When moving the last visible element down, the scroll position is not adopted. So its scroll out of view.
                // We correct this manually by scrolling it back into view
                const targetElement = this.listOptions.get(targetIndex).element;
                if (targetElement.getBoundingClientRect().bottom > this.modalBodyElement.nativeElement.getBoundingClientRect().bottom) {
                    targetElement.scrollIntoView({ block: 'end' });
                }
                this.announceSuccessfulMove(targetIndex);
            }
            else {
                this.announceNotSuccessfulMove();
            }
        }
    }
    moveUp(index, event) {
        if (this.columns[index].draggable) {
            let targetIndex = index - 1;
            while (this.columns[targetIndex] && !this.columns[targetIndex].draggable) {
                targetIndex--;
            }
            if (targetIndex !== index && this.columns[targetIndex]?.draggable) {
                event.preventDefault();
                moveItemInArray(this.columns, index, targetIndex);
                // it seems like this is only necessary for move up. Don't know why
                setTimeout(() => this.listOptions.get(targetIndex).focus());
                this.announceSuccessfulMove(targetIndex);
            }
            else {
                this.announceNotSuccessfulMove();
            }
        }
    }
    createBackupData() {
        this.tempHeaderData = this.columns.map(x => Object.assign({}, x));
    }
    announceSuccessfulMove(index) {
        this.announceMove(this.a11yItemMovedMessage, { ...this.translationParams, targetPosition: index + 1 });
    }
    announceNotSuccessfulMove() {
        this.announceMove(this.a11yItemNotMovedMessage, this.translationParams);
    }
    announceMove(message, translationParams) {
        if (message) {
            this.translateService.translateAsync(message, translationParams)
                .pipe(first())
                .subscribe(translatedMessage => this.liveAnnouncer.announce(translatedMessage));
        }
    }
}
SiColumnSelectionDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiColumnSelectionDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiColumnSelectionDialogComponent, selector: "si-column-selection-dialog", inputs: { titleId: "titleId", heading: "heading", bodyTitle: "bodyTitle", submitBtnName: "submitBtnName", cancelBtnName: "cancelBtnName", restoreToDefaultBtnName: "restoreToDefaultBtnName", hiddenText: "hiddenText", visibleText: "visibleText", restoreEnabled: "restoreEnabled", columns: "columns", translationParams: "translationParams", listAriaLabel: "listAriaLabel", a11yItemMovedMessage: "a11yItemMovedMessage", a11yItemNotMovedMessage: "a11yItemNotMovedMessage" }, outputs: { closed: "closed" }, viewQueries: [{ propertyName: "modalBodyElement", first: true, predicate: ["modalBody"], descendants: true, static: true }, { propertyName: "listOptions", predicate: CdkOption, descendants: true }], ngImport: i0, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate: translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"cancelColumnSelection()\"\n  ></button>\n</div>\n<p class=\"text-secondary px-8 pb-5 mb-0\">{{ bodyTitle | translate: translationParams }}</p>\n<div #modalBody class=\"modal-body py-0\">\n  <div\n    cdkListbox\n    cdkListboxMultiple\n    [cdkListboxValue]=\"visibleIds\"\n    cdkDropList\n    aria-keyshortcuts=\"Alt+ArrowUp Alt+ArrowDown\"\n    [attr.aria-label]=\"listAriaLabel | translate:translationParams\"\n    (cdkListboxValueChange)=\"changeColumnVisibilityFromListbox($event)\"\n    (cdkDropListDropped)=\"drop($event)\">\n    <div\n      *ngFor=\"let headerContent of columns;let index = index; trackBy: trackByItem;\"\n      [cdkDragDisabled]=\"!headerContent.draggable\"\n      cdkDrag\n      [cdkOption]=\"headerContent.id\"\n      [cdkOptionDisabled]=\"headerContent.disabled\"\n      class=\"d-flex justify-content-between user-select-none my-4 mx-1 p-5 rounded-2 elevation-1 bg-base-1\"\n      (keydown.alt.arrowUp)=\"moveUp(index, $event)\"\n      (keydown.alt.arrowDown)=\"moveDown(index, $event)\"\n    >\n      <div class=\"d-flex align-items-center\">\n        <div\n          cdkDragHandle\n          aria-hidden=\"true\"\n          class=\"drag-handle element-menu me-5 rounded-1\"\n          [class.disabled]=\"headerContent.disabled\"\n          [class.draggable]=\"headerContent.draggable\"\n        ></div>\n        <span\n          class=\"si-body-2\"\n          [class.disabled]=\"headerContent.disabled\"\n        >{{ headerContent.title | translate: translationParams }}</span>\n      </div>\n      <div class=\"d-flex align-items-center form-check form-switch\" aria-hidden=\"true\">\n        <span\n          class=\"si-body-2 me-4 form-check-label\"\n          [class.disabled]=\"headerContent.disabled\"\n        >{{ (headerContent.visible ? visibleText : hiddenText) | translate }}</span>\n        <span\n          type=\"checkbox\"\n          class=\"form-check-input\"\n          [class.disabled]=\"headerContent.disabled\"\n          [class.checked]=\"headerContent.visible\"\n        ></span>\n      </div>\n    </div>\n  </div>\n</div>\n<div class=\"modal-footer\">\n  <button *ngIf=\"restoreEnabled\" type=\"button\" class=\"btn btn-secondary me-auto\" (click)=\"restoreToDefault()\">\n    {{ restoreToDefaultBtnName | translate }}\n  </button>\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"cancelColumnSelection()\">\n    {{ cancelBtnName | translate }}\n  </button>\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"submitColumnSelection()\">\n    {{ submitBtnName | translate }}\n  </button>\n</div>\n", styles: [":host{-webkit-user-select:none;user-select:none;max-block-size:100%;overflow:hidden;display:flex;flex-direction:column}.disabled{color:var(--element-text-disabled)}.drag-handle{font-size:24px;color:var(--element-ui-2)}.drag-handle.disabled{color:var(--element-ui-3)}.drag-handle.draggable{cursor:pointer}.drag-handle.draggable:hover,.drag-handle.draggable.hover{background:var(--element-base-0)}.cdk-drag-placeholder{opacity:0}.cdk-drag-preview .drag-handle{color:var(--element-ui-5);background-color:var(--element-ui-3)}.switch{margin:0}\n"], dependencies: [{ kind: "directive", type: i1$2.CdkListbox, selector: "[cdkListbox]", inputs: ["id", "tabindex", "cdkListboxValue", "cdkListboxMultiple", "cdkListboxDisabled", "cdkListboxUseActiveDescendant", "cdkListboxOrientation", "cdkListboxCompareWith", "cdkListboxNavigationWrapDisabled", "cdkListboxNavigatesDisabledOptions"], outputs: ["cdkListboxValueChange"], exportAs: ["cdkListbox"] }, { kind: "directive", type: i1$2.CdkOption, selector: "[cdkOption]", inputs: ["id", "cdkOption", "cdkOptionTypeaheadLabel", "cdkOptionDisabled", "tabindex"], exportAs: ["cdkOption"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.CdkDropList, selector: "[cdkDropList], cdk-drop-list", inputs: ["cdkDropListConnectedTo", "cdkDropListData", "cdkDropListOrientation", "id", "cdkDropListLockAxis", "cdkDropListDisabled", "cdkDropListSortingDisabled", "cdkDropListEnterPredicate", "cdkDropListSortPredicate", "cdkDropListAutoScrollDisabled", "cdkDropListAutoScrollStep"], outputs: ["cdkDropListDropped", "cdkDropListEntered", "cdkDropListExited", "cdkDropListSorted"], exportAs: ["cdkDropList"] }, { kind: "directive", type: i3.CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "directive", type: i3.CdkDragHandle, selector: "[cdkDragHandle]", inputs: ["cdkDragHandleDisabled"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-column-selection-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"modal-header\">\n  <span class=\"modal-title\" [id]=\"titleId\">{{ heading | translate: translationParams }}</span>\n  <button\n    type=\"button\"\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel\"\n    [attr.aria-label]=\"cancelBtnName | translate\"\n    (click)=\"cancelColumnSelection()\"\n  ></button>\n</div>\n<p class=\"text-secondary px-8 pb-5 mb-0\">{{ bodyTitle | translate: translationParams }}</p>\n<div #modalBody class=\"modal-body py-0\">\n  <div\n    cdkListbox\n    cdkListboxMultiple\n    [cdkListboxValue]=\"visibleIds\"\n    cdkDropList\n    aria-keyshortcuts=\"Alt+ArrowUp Alt+ArrowDown\"\n    [attr.aria-label]=\"listAriaLabel | translate:translationParams\"\n    (cdkListboxValueChange)=\"changeColumnVisibilityFromListbox($event)\"\n    (cdkDropListDropped)=\"drop($event)\">\n    <div\n      *ngFor=\"let headerContent of columns;let index = index; trackBy: trackByItem;\"\n      [cdkDragDisabled]=\"!headerContent.draggable\"\n      cdkDrag\n      [cdkOption]=\"headerContent.id\"\n      [cdkOptionDisabled]=\"headerContent.disabled\"\n      class=\"d-flex justify-content-between user-select-none my-4 mx-1 p-5 rounded-2 elevation-1 bg-base-1\"\n      (keydown.alt.arrowUp)=\"moveUp(index, $event)\"\n      (keydown.alt.arrowDown)=\"moveDown(index, $event)\"\n    >\n      <div class=\"d-flex align-items-center\">\n        <div\n          cdkDragHandle\n          aria-hidden=\"true\"\n          class=\"drag-handle element-menu me-5 rounded-1\"\n          [class.disabled]=\"headerContent.disabled\"\n          [class.draggable]=\"headerContent.draggable\"\n        ></div>\n        <span\n          class=\"si-body-2\"\n          [class.disabled]=\"headerContent.disabled\"\n        >{{ headerContent.title | translate: translationParams }}</span>\n      </div>\n      <div class=\"d-flex align-items-center form-check form-switch\" aria-hidden=\"true\">\n        <span\n          class=\"si-body-2 me-4 form-check-label\"\n          [class.disabled]=\"headerContent.disabled\"\n        >{{ (headerContent.visible ? visibleText : hiddenText) | translate }}</span>\n        <span\n          type=\"checkbox\"\n          class=\"form-check-input\"\n          [class.disabled]=\"headerContent.disabled\"\n          [class.checked]=\"headerContent.visible\"\n        ></span>\n      </div>\n    </div>\n  </div>\n</div>\n<div class=\"modal-footer\">\n  <button *ngIf=\"restoreEnabled\" type=\"button\" class=\"btn btn-secondary me-auto\" (click)=\"restoreToDefault()\">\n    {{ restoreToDefaultBtnName | translate }}\n  </button>\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"cancelColumnSelection()\">\n    {{ cancelBtnName | translate }}\n  </button>\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"submitColumnSelection()\">\n    {{ submitBtnName | translate }}\n  </button>\n</div>\n", styles: [":host{-webkit-user-select:none;user-select:none;max-block-size:100%;overflow:hidden;display:flex;flex-direction:column}.disabled{color:var(--element-text-disabled)}.drag-handle{font-size:24px;color:var(--element-ui-2)}.drag-handle.disabled{color:var(--element-ui-3)}.drag-handle.draggable{cursor:pointer}.drag-handle.draggable:hover,.drag-handle.draggable.hover{background:var(--element-base-0)}.cdk-drag-placeholder{opacity:0}.cdk-drag-preview .drag-handle{color:var(--element-ui-5);background-color:var(--element-ui-3)}.switch{margin:0}\n"] }]
        }], propDecorators: { titleId: [{
                type: Input
            }], heading: [{
                type: Input
            }], bodyTitle: [{
                type: Input
            }], submitBtnName: [{
                type: Input
            }], cancelBtnName: [{
                type: Input
            }], restoreToDefaultBtnName: [{
                type: Input
            }], hiddenText: [{
                type: Input
            }], visibleText: [{
                type: Input
            }], restoreEnabled: [{
                type: Input
            }], columns: [{
                type: Input
            }], translationParams: [{
                type: Input
            }], listAriaLabel: [{
                type: Input
            }], a11yItemMovedMessage: [{
                type: Input
            }], a11yItemNotMovedMessage: [{
                type: Input
            }], closed: [{
                type: Output
            }], listOptions: [{
                type: ViewChildren,
                args: [CdkOption]
            }], modalBodyElement: [{
                type: ViewChild,
                args: ['modalBody', { static: true }]
            }] } });

class SiColumnSelectionDialogService {
    constructor() {
        this.modalService = inject(SiModalService);
    }
    /**
     * @deprecated use {showColumnSelectionDialog(config: SiColumnSelectionDialogueConfig)} instead
     */
    showColumnSelectionDialog(headingOrConfig, bodyTitle, submitBtnName, cancelBtnName, columns, translationParams, restoreToDefaultBtnName, restoreEnabled, visibleText, hiddenText) {
        return new Observable(subscriber => {
            let config;
            if (typeof headingOrConfig === 'object') {
                config = createModalConfig(headingOrConfig);
            }
            else {
                config = createModalConfig({
                    heading: headingOrConfig,
                    bodyTitle,
                    submitBtnName,
                    cancelBtnName,
                    visibleText,
                    hiddenText, columns,
                    translationParams,
                    restoreToDefaultBtnName,
                    restoreEnabled
                });
            }
            config.class += ' modal-dialog-scrollable';
            const modalRef = this.modalService.show(SiColumnSelectionDialogComponent, config);
            subscriber.__modalRef = modalRef;
            const subscription = modalRef.content.closed.subscribe((confirmationResult) => {
                const keepModalOpen = confirmationResult?.type === 'instant' || confirmationResult?.type === 'restoreDefault';
                if (!keepModalOpen) {
                    modalRef.hide();
                }
                subscriber.next(confirmationResult);
                if (!keepModalOpen) {
                    subscription.unsubscribe();
                    subscriber.complete();
                }
            });
            return () => {
                if (!subscription.closed) {
                    subscription.unsubscribe();
                    modalRef.detach();
                }
            };
        });
    }
}
SiColumnSelectionDialogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionDialogService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiColumnSelectionDialogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionDialogService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionDialogService, decorators: [{
            type: Injectable
        }] });

const dragConfig = {
    dragStartThreshold: 0,
    pointerDirectionChangeThreshold: 5,
    zIndex: 10000
};
class SiColumnSelectionModalModule {
}
SiColumnSelectionModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiColumnSelectionModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionModalModule, declarations: [SiColumnSelectionDialogComponent], imports: [CdkListboxModule,
        CommonModule,
        DragDropModule,
        RouterModule,
        SiModalModule,
        SiSwitchModule,
        SiTranslateModule] });
SiColumnSelectionModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionModalModule, providers: [
        SiColumnSelectionDialogService,
        { provide: CDK_DRAG_CONFIG, useValue: dragConfig }
    ], imports: [CdkListboxModule,
        CommonModule,
        DragDropModule,
        RouterModule,
        SiModalModule,
        SiSwitchModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiColumnSelectionModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiColumnSelectionDialogComponent],
                    imports: [
                        CdkListboxModule,
                        CommonModule,
                        DragDropModule,
                        RouterModule,
                        SiModalModule,
                        SiSwitchModule,
                        SiTranslateModule
                    ],
                    providers: [
                        SiColumnSelectionDialogService,
                        { provide: CDK_DRAG_CONFIG, useValue: dragConfig }
                    ]
                }]
        }] });

/**
 * Method to help you toggle the selection state of `MenuItems` depending on their
 * id value in a mutually exclusive fashion. It marks a specific item
 * as selected and unselects the others.
 * This can be used for example to mark the currently selected theme
 * while de-selecting the ones that are not active.
 *
 * @param menuItems Array of items that should be (de)-selected
 * @param id Id of the item that should get selected
 * @param type If it should display a check or a radio select
 * @returns Modified menuItems Array with one item selected
 **/
const selectMenuItemById = (menuItems, id, type = 'check') => menuItems.map(item => {
    item.selectionState = item.id === id ? type : '';
    return item;
});

// ------------------------------------------------------------------------------

/* eslint-disable */
// TODO: upstream bug, re-enable when fixed
/**
  * Siemens Logo and link to main page.
  * Should be used in a `.navbar-header`.
  *
  * @example
  *   <nav class="navbar">
  *     <div class="navbar-header">
  *       <si-navbar-brand></si-navbar-brand>
  *     </div>
  *   </nav>
  *
  * <example-url>../dist-playground/?scenario=SiNavbarBrandComponent/Navbar%20Brand</example-url>
  */
/* eslint-enable */
class SiNavbarBrandComponent {
    constructor() {
        /**
         * like that is invoked on click, defaults to "/"
         */
        this.link = { link: '/' };
        /**
         * Text which describes the logo or the name. Needed for a11y.
         */
        this.logoText = 'Siemens logo';
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    }
    ngOnInit() {
        this.updateThemeLogoUrl();
    }
    /**
     * Returns value of css variable `--element-brand-logo-url` if configured,
     * otherwise the value of `logoUrl`.
     */
    get resolvedLogoUrl() {
        return this.themeLogoUrl ?? this.logoUrl;
    }
    /**
     * Returns value of css variable `--element-brand-logo-text` if configured,
     * otherwise the value of `logoText`.
     */
    get resolvedLogoText() {
        return this.themeLogoText ?? this.logoText;
    }
    onThemeSwitch() {
        this.updateThemeLogoUrl();
    }
    updateThemeLogoUrl() {
        if (!this.isBrowser) {
            return;
        }
        const computed = window.getComputedStyle(document.documentElement);
        const themeLogoUrl = computed.getPropertyValue('--element-brand-logo-url');
        this.themeLogoUrl = themeLogoUrl.trim().length > 0 ? themeLogoUrl : undefined;
        const themeLogoText = computed.getPropertyValue('--element-brand-logo-text');
        this.themeLogoText = themeLogoText.trim().length > 0 ? themeLogoText : undefined;
    }
}
SiNavbarBrandComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarBrandComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarBrandComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarBrandComponent, selector: "si-navbar-brand", inputs: { logoUrl: "logoUrl", link: "link", logoText: "logoText" }, host: { listeners: { "window:theme-switch": "onThemeSwitch()" } }, ngImport: i0, template: "<a class=\"navbar-brand px-6 focus-inside\" [siLink]=\"link\" [attr.aria-label]=\"resolvedLogoText | translate\" siListInteractionItem>\n  <svg *ngIf=\"!resolvedLogoUrl; else alternativeLogo\" viewBox=\"0 0 142.315 22.5\" width=\"103\" height=\"16\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path d=\"M125.878 21.704v-4.378c2.53.806 4.718 1.209 6.566 1.209 2.53 0 3.795-.674 3.795-2.02 0-.49-.183-.898-.549-1.225-.43-.388-1.419-.903-2.971-1.545-2.735-1.113-4.5-2.006-5.296-2.68-1.325-1.121-1.99-2.581-1.99-4.376 0-2.235.873-3.94 2.617-5.113C129.623.525 131.744 0 134.418 0c2.143 0 4.21.27 6.198.811v4.194c-2.172-.838-4.116-1.255-5.83-1.255-2.276 0-3.415.647-3.415 1.942 0 .522.247.94.737 1.257.417.265 1.53.786 3.335 1.561 2.725 1.174 4.522 2.194 5.388 3.061.989.99 1.484 2.301 1.484 3.934 0 2.5-1.03 4.356-3.09 5.571-1.612.949-3.755 1.424-6.43 1.424-2.489 0-4.796-.266-6.917-.796m-22.913.352V.367h7.101l7.469 14.097V.367h4.349v21.69h-6.859L107.31 7.744v14.311h-4.345zm-19.73 0V.367H99v3.964h-9.705V9.2h8.45v3.581h-8.45v5.096h9.935v4.18H83.235zm-31.837 0V.367h7.913l5.434 13.607L70.378.367h7.546v21.69h-5.939V7.238L65.679 22.27h-3.781L55.76 7.239v14.817h-4.362zm-19.775 0V.367h15.78v3.964H37.7V9.2h8.45v3.581H37.7v5.096h9.919v4.18H31.623zM20.05.367h6.29v21.69h-6.29V.366zM.443 21.704v-4.378c2.52.806 4.71 1.209 6.567 1.209 2.531 0 3.796-.674 3.796-2.02 0-.49-.185-.898-.551-1.225-.419-.388-1.414-.903-2.985-1.545-2.704-1.103-4.464-1.995-5.28-2.68C.663 9.944 0 8.484 0 6.689c0-2.235.873-3.94 2.618-5.113C4.178.525 6.3 0 8.984 0c2.142 0 4.211.27 6.2.811v4.194C12.989 4.167 11.046 3.75 9.35 3.75c-2.275 0-3.412.647-3.412 1.942 0 .512.249.93.749 1.257.408.265 1.516.786 3.323 1.561 2.724 1.174 4.519 2.194 5.387 3.061.99.99 1.486 2.301 1.486 3.934 0 2.5-1.036 4.356-3.109 5.571-1.601.949-3.74 1.424-6.414 1.424-2.49 0-4.795-.266-6.918-.796\"\n      fill=\"currentColor\"></path>\n  </svg>\n  <ng-template #alternativeLogo>\n    <div class=\"custom-logo\">\n      <img [src]=\"resolvedLogoUrl\" alt=\"Brand Logo\">\n    </div>\n  </ng-template>\n</a>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarBrandComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-brand', template: "<a class=\"navbar-brand px-6 focus-inside\" [siLink]=\"link\" [attr.aria-label]=\"resolvedLogoText | translate\" siListInteractionItem>\n  <svg *ngIf=\"!resolvedLogoUrl; else alternativeLogo\" viewBox=\"0 0 142.315 22.5\" width=\"103\" height=\"16\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path d=\"M125.878 21.704v-4.378c2.53.806 4.718 1.209 6.566 1.209 2.53 0 3.795-.674 3.795-2.02 0-.49-.183-.898-.549-1.225-.43-.388-1.419-.903-2.971-1.545-2.735-1.113-4.5-2.006-5.296-2.68-1.325-1.121-1.99-2.581-1.99-4.376 0-2.235.873-3.94 2.617-5.113C129.623.525 131.744 0 134.418 0c2.143 0 4.21.27 6.198.811v4.194c-2.172-.838-4.116-1.255-5.83-1.255-2.276 0-3.415.647-3.415 1.942 0 .522.247.94.737 1.257.417.265 1.53.786 3.335 1.561 2.725 1.174 4.522 2.194 5.388 3.061.989.99 1.484 2.301 1.484 3.934 0 2.5-1.03 4.356-3.09 5.571-1.612.949-3.755 1.424-6.43 1.424-2.489 0-4.796-.266-6.917-.796m-22.913.352V.367h7.101l7.469 14.097V.367h4.349v21.69h-6.859L107.31 7.744v14.311h-4.345zm-19.73 0V.367H99v3.964h-9.705V9.2h8.45v3.581h-8.45v5.096h9.935v4.18H83.235zm-31.837 0V.367h7.913l5.434 13.607L70.378.367h7.546v21.69h-5.939V7.238L65.679 22.27h-3.781L55.76 7.239v14.817h-4.362zm-19.775 0V.367h15.78v3.964H37.7V9.2h8.45v3.581H37.7v5.096h9.919v4.18H31.623zM20.05.367h6.29v21.69h-6.29V.366zM.443 21.704v-4.378c2.52.806 4.71 1.209 6.567 1.209 2.531 0 3.796-.674 3.796-2.02 0-.49-.185-.898-.551-1.225-.419-.388-1.414-.903-2.985-1.545-2.704-1.103-4.464-1.995-5.28-2.68C.663 9.944 0 8.484 0 6.689c0-2.235.873-3.94 2.618-5.113C4.178.525 6.3 0 8.984 0c2.142 0 4.211.27 6.2.811v4.194C12.989 4.167 11.046 3.75 9.35 3.75c-2.275 0-3.412.647-3.412 1.942 0 .512.249.93.749 1.257.408.265 1.516.786 3.323 1.561 2.724 1.174 4.519 2.194 5.387 3.061.99.99 1.486 2.301 1.486 3.934 0 2.5-1.036 4.356-3.109 5.571-1.601.949-3.74 1.424-6.414 1.424-2.49 0-4.795-.266-6.918-.796\"\n      fill=\"currentColor\"></path>\n  </svg>\n  <ng-template #alternativeLogo>\n    <div class=\"custom-logo\">\n      <img [src]=\"resolvedLogoUrl\" alt=\"Brand Logo\">\n    </div>\n  </ng-template>\n</a>\n" }]
        }], propDecorators: { logoUrl: [{
                type: Input
            }], link: [{
                type: Input
            }], logoText: [{
                type: Input
            }], onThemeSwitch: [{
                type: HostListener,
                args: ['window:theme-switch']
            }] } });

// ------------------------------------------------------------------------------

class SiNavbarItemComponent {
    constructor() {
        this.active = false;
    }
    submenuActiveChange(active) {
        this.active = active;
    }
}
SiNavbarItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarItemComponent, selector: "si-navbar-item", inputs: { item: "item", quickAction: "quickAction" }, ngImport: i0, template: "<ul class=\"nav navbar-nav\" role=\"menu\">\n  <li *ngIf=\"item.items || item.customContent\"\n    class=\"dropdown\"\n    siDropdown\n    role=\"presentation\"\n    [dropdownFocusOnOpen]=\"false\"\n  >\n    <si-menu-item\n      siDropdownToggle\n      #dropdownToggle=\"si-dropdown-toggle\"\n      class=\"nav-item\"\n      [class.quick-action]=\"quickAction !== undefined\"\n      [item]=\"item\"\n      [badgeStyle]=\"quickAction !== undefined ? 'dot' : ''\"\n      [disableParentClick]=\"false\"\n      [forceActive]=\"active\"\n      [focusStylingInside]=\"true\"\n      [dropdownToggle]=\"dropdownToggle\"\n    ></si-menu-item>\n    <ng-container *ngIf=\"item.items\">\n      <si-menu\n        siDropdownMenu\n        class=\"dropdown-menu\"\n        [items]=\"item.items\"\n        [isSubList]=\"true\"\n        position=\"adjacent\"\n        (activeChange)=\"submenuActiveChange($event)\"\n      ></si-menu>\n    </ng-container>\n    <ng-container *ngIf=\"item.customContent\">\n      <div class=\"dropdown-menu custom-content\" siDropdownMenu>\n        <ng-content></ng-content>\n      </div>\n    </ng-container>\n  </li>\n  <li role=\"presentation\">\n    <si-menu-item *ngIf=\"!item.items && !item.customContent\"\n      class=\"nav-item\"\n      [class.quick-action]=\"quickAction !== undefined\"\n      [badgeStyle]=\"quickAction !== undefined ? 'dot' : ''\"\n      [item]=\"item\"\n      [focusStylingInside]=\"true\">\n    </si-menu-item>\n  </li>\n</ul>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }] });
__decorate([
    Required()
], SiNavbarItemComponent.prototype, "item", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-item', template: "<ul class=\"nav navbar-nav\" role=\"menu\">\n  <li *ngIf=\"item.items || item.customContent\"\n    class=\"dropdown\"\n    siDropdown\n    role=\"presentation\"\n    [dropdownFocusOnOpen]=\"false\"\n  >\n    <si-menu-item\n      siDropdownToggle\n      #dropdownToggle=\"si-dropdown-toggle\"\n      class=\"nav-item\"\n      [class.quick-action]=\"quickAction !== undefined\"\n      [item]=\"item\"\n      [badgeStyle]=\"quickAction !== undefined ? 'dot' : ''\"\n      [disableParentClick]=\"false\"\n      [forceActive]=\"active\"\n      [focusStylingInside]=\"true\"\n      [dropdownToggle]=\"dropdownToggle\"\n    ></si-menu-item>\n    <ng-container *ngIf=\"item.items\">\n      <si-menu\n        siDropdownMenu\n        class=\"dropdown-menu\"\n        [items]=\"item.items\"\n        [isSubList]=\"true\"\n        position=\"adjacent\"\n        (activeChange)=\"submenuActiveChange($event)\"\n      ></si-menu>\n    </ng-container>\n    <ng-container *ngIf=\"item.customContent\">\n      <div class=\"dropdown-menu custom-content\" siDropdownMenu>\n        <ng-content></ng-content>\n      </div>\n    </ng-container>\n  </li>\n  <li role=\"presentation\">\n    <si-menu-item *ngIf=\"!item.items && !item.customContent\"\n      class=\"nav-item\"\n      [class.quick-action]=\"quickAction !== undefined\"\n      [badgeStyle]=\"quickAction !== undefined ? 'dot' : ''\"\n      [item]=\"item\"\n      [focusStylingInside]=\"true\">\n    </si-menu-item>\n  </li>\n</ul>\n" }]
        }], propDecorators: { item: [{
                type: Input
            }], quickAction: [{
                type: Input
            }] } });

// ------------------------------------------------------------------------------

const trackByIdentity = ((index, item) => item);
/**
 * Creates a trackByFunction for ngFor, that tracks an Item by its identity.
 * It should be used with a generic parameter, to generate a proper type:
 *
 * @example  @Input() trackByFunction = buildTrackByIdentity<ItemType>()
 */
const buildTrackByIdentity = () => trackByIdentity;
const trackByIndex = (index) => index;
/**
 * Creates a trackByFunction for ngFor, that tracks an Item by its index.
 * It should be used with a generic parameter, to generate a proper type:
 *
 * @example  @Input() trackByFunction = buildTrackByIdentity<ItemType>()
 */
const buildTrackByIndex = () => trackByIndex;

class SiLaunchpadComponent {
    constructor() {
        /**
         * Text to close the app switcher. Needed for a11y.
         */
        this.closeAppSwitcherText = 'Close App Switcher';
        /**
         * title for the app switcher
         */
        this.appSwitcherTitle = 'Launchpad';
        /**
         * sub-title for the app switcher
         */
        this.appSwitcherSubTitle = 'Access all your applications or add more';
        /**
         * Allow the user to favorite apps which will then be displayed at the top.
         */
        this.appItemsFavorites = false;
        /**
         * Title or translate key for the favorite apps section.
         */
        this.favoriteAppsTitle = 'Favorite apps';
        /**
         * Title or translate key for the default apps section.
         */
        this.defaultAppsTitle = 'Applications';
        /**
         * Title or translate key for the show more apps button.
         */
        this.showMoreAppsTitle = 'Show more';
        /**
         * Title or translate key for the show less apps button.
         */
        this.showLessAppsTitle = 'Show less';
        this.appItemFavoriteChanged = new EventEmitter();
        this.hide = new EventEmitter();
        this.isCategorized = false;
        this.showAllApps = false;
        this.showFavorites = false;
        this.trackByIndex = buildTrackByIndex();
    }
    ngOnInit() {
        this.calcAppItems();
    }
    hideAppSwitcherOnClick(element) {
        const actualElement = element;
        if (!actualElement.classList.contains('app-favorite')) {
            this.hideAppSwitcher();
        }
    }
    hideAppSwitcher() {
        this.hide.emit();
    }
    calcAppItems() {
        this.appsCategorized = this.appCategoryItems ??
            (this.appItems ? [{ category: '', items: this.appItems }] : undefined);
        this.isCategorized = this.appItemsFavorites
            || (!!this.appsCategorized && (this.appsCategorized.length > 1 || this.appsCategorized.some(item => item.category)));
        this.calcAppFavorites();
    }
    calcAppFavorites() {
        this.showFavorites = this.appItemsFavorites &&
            !!this.appsCategorized && this.appsCategorized.some(item => item.items.some(appItem => appItem.isFavorite));
        this.appsFavorites = this.appItemsFavorites
            ? this.appsCategorized?.reduce((acc, item) => acc.concat(item.items.filter(appItem => appItem.isFavorite)), [])
            : undefined;
    }
    toggleFavorite(item) {
        item.isFavorite = !item.isFavorite;
        this.showAllApps = true;
        this.calcAppFavorites();
        this.appItemFavoriteChanged.emit([item, item.isFavorite]);
    }
}
SiLaunchpadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLaunchpadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiLaunchpadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiLaunchpadComponent, isStandalone: true, selector: "si-launchpad", inputs: { closeAppSwitcherText: "closeAppSwitcherText", appSwitcherTitle: "appSwitcherTitle", appSwitcherSubTitle: "appSwitcherSubTitle", appCategoryItems: "appCategoryItems", appItems: "appItems", appItemsFavorites: "appItemsFavorites", favoriteAppsTitle: "favoriteAppsTitle", defaultAppsTitle: "defaultAppsTitle", showMoreAppsTitle: "showMoreAppsTitle", showLessAppsTitle: "showLessAppsTitle", allAppsLink: "allAppsLink" }, outputs: { appItemFavoriteChanged: "appItemFavoriteChanged", hide: "hide" }, ngImport: i0, template: "<div class=\"app-switcher ps-0 ps-sm-6 pt-8 text-center text-sm-start\">\n  <button\n    type=\"button\"\n    siListInteractionItem\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel position-absolute top-0 end-0 mt-6 me-4\"\n    [attr.aria-label]=\"closeAppSwitcherText | translate\"\n    (click)=\"hideAppSwitcher()\"\n  > </button>\n  <div class=\"mx-9\">\n    <p\n      *ngIf=\"appSwitcherTitle\"\n      class=\"mb-4 si-title-1 lh-1\"\n    >{{ appSwitcherTitle | translate }}</p>\n    <p\n      *ngIf=\"appSwitcherSubTitle\"\n      class=\"si-body-2 text-secondary\"\n      [class.mb-9]=\"isCategorized\"\n      [class.mb-4]=\"!isCategorized\"\n    >{{ appSwitcherSubTitle | translate }}</p>\n  </div>\n  <div class=\"apps-scroll px-9 pb-8\">\n    <div *ngIf=\"showFavorites\" class=\"apps-category focus-none\" siListInteractionItem>\n      <span class=\"si-title-1 apps-category-title\">{{ favoriteAppsTitle | translate }}</span>\n      <div\n        class=\"apps apps-favorite mt-4 justify-content-center justify-content-sm-start focus-direct-sub-inside\"\n        siListInteraction\n        [listInteractionIsSubList]=\"true\"\n        listInteractionPosition=\"adjacent\"\n        listInteractionOrientation=\"ste\"\n        #appSwitcherKeyboardInteraction=\"si-list-interaction\"\n      >\n        <a\n          *ngFor=\"let app of appsFavorites; trackBy: trackByIndex\"\n          siListInteractionItem\n          [siLink]=\"app\"\n          activeClass=\"active\"\n          class=\"app-container-wrapper app app-favorite\"\n          (click)=\"hideAppSwitcher()\"\n        >\n          <div class=\"app-container app-icon\" [ngClass]=\"app.icon ?? ''\"></div>\n          <span class=\"title si-title-2\">{{ app.title! | translate }}</span>\n        </a>\n      </div>\n    </div>\n    <a\n      *ngIf=\"showFavorites\"\n      siListInteractionItem\n      class=\"dropdown-toggle apps-show-more py-4 mb-6 mt-9 si-title-2\"\n      [class.show]=\"showAllApps\"\n      (click)=\"showAllApps = !showAllApps\"\n      (keydown.enter)=\"showAllApps = !showAllApps\"\n    >\n      {{ (showAllApps ? showLessAppsTitle : showMoreAppsTitle) | translate }}<i class=\"dropdown-caret icon element-down-2\"></i>\n    </a>\n    <ng-container *ngIf=\"!showFavorites || showAllApps\">\n      <div\n        *ngFor=\"let appsCategory of appsCategorized; let i = index; trackBy: trackByIndex\"\n        class=\"apps-category focus-none\"\n        [class.mt-4]=\"isCategorized\"\n        siListInteractionItem\n      >\n        <span *ngIf=\"appsCategory.category\" class=\"si-title-1 apps-category-title\">{{ appsCategory.category | translate }}</span>\n        <span *ngIf=\"!appsCategory.category && isCategorized\" class=\"si-title-1 apps-category-title\">{{ defaultAppsTitle | translate }}</span>\n        <div\n          class=\"apps mt-4 ms-0 ms-sm-8 justify-content-center justify-content-sm-start focus-direct-sub-inside\"\n          [class.apps-categorized]=\"isCategorized\"\n          siListInteraction\n          [listInteractionIsSubList]=\"true\"\n          listInteractionPosition=\"adjacent\"\n          listInteractionOrientation=\"ste\"\n          #appSwitcherKeyboardInteraction=\"si-list-interaction\"\n        >\n          <div *ngFor=\"let app of appsCategory.items; trackBy: trackByIndex\" class=\"app-wrapper\">\n            <div *ngIf=\"appItemsFavorites\" class=\"app-container-wrapper position-absolute\">\n              <div class=\"app-container\">\n                <i\n                  class=\"icon app-favorite-icon\"\n                  [class.favorite]=\"app.isFavorite\"\n                  [class.element-favorites-filled]=\"app.isFavorite\"\n                  [class.element-favorites]=\"!app.isFavorite\"\n                  (click)=\"toggleFavorite(app)\"\n                ></i>\n              </div>\n            </div>\n            <a\n              siListInteractionItem\n              [siLink]=\"app\"\n              activeClass=\"active\"\n              class=\"app-container-wrapper app\"\n              (keydown.space)=\"toggleFavorite(app)\"\n              (click)=\"hideAppSwitcherOnClick($event.target)\"\n            >\n              <div class=\"app-container app-icon\" [ngClass]=\"app.icon ?? ''\"></div>\n              <span class=\"title si-title-2\">\n                {{ app.title! | translate }}<i *ngIf=\"app.isExternal\" class=\"icon element-export my-n3\" role=\"presentation\"></i>\n              </span>\n            </a>\n          </div>\n          <div class=\"app-wrapper\">\n            <a\n              *ngIf=\"allAppsLink && appsCategorized!.length - 1 === i\"\n              siListInteractionItem\n              [siLink]=\"allAppsLink\"\n              class=\"app-container-wrapper app app-more\"\n              (click)=\"hideAppSwitcher()\"\n            >\n              <div class=\"app-container icon-add btn btn-circle btn-secondary {{ allAppsLink?.icon || 'element-plus' }}\"></div>\n              <span class=\"title si-title-2\">{{ (allAppsLink?.title || 'Add more')! | translate }}</span>\n            </a>\n          </div>\n        </div>\n      </div>\n    </ng-container>\n  </div>\n</div>\n<div class=\"app-switcher-backdrop\" (click)=\"hideAppSwitcher()\"></div>\n", styles: [".app-switcher-backdrop{z-index:1031;position:fixed;inset:0;background:var(--element-base-translucent-1);opacity:1}.app-switcher{position:fixed;inset-block-start:48px;inset-inline:0;min-block-size:200px;max-block-size:calc(100vh - 48px);display:flex;flex-direction:column;z-index:1032;background-color:var(--element-base-1);box-shadow:inset 0 0 4px var(--element-box-shadow-color-1),inset 0 4px 4px var(--element-box-shadow-color-2)}.app-switcher a:hover,.app-switcher a:focus,.app-switcher a:active{text-decoration:none;outline:none}.apps-show-more{display:inline-block;cursor:pointer}.apps-show-more:hover{text-decoration:underline}.apps-scroll{overflow-y:auto}.apps{display:flex;flex-wrap:wrap}.apps.apps-categorized,.apps.apps-favorite{margin-block-start:0px}.app-wrapper{position:relative;margin-block-end:8px}.app-wrapper:last-child{margin-inline-end:0}.app-container-wrapper{display:flex;flex-direction:column;align-items:center;text-align:center;margin-block:16px;margin-inline:4px;padding-block:8px;padding-inline:0;inline-size:9rem;max-inline-size:9rem}.app-container-wrapper.app-favorite,.app-container-wrapper.app-more{margin-inline-end:8px}.app-container-wrapper.app-favorite:last-child,.app-container-wrapper.app-more:last-child{margin-inline-end:4px}.app-container{position:relative;display:flex;align-items:center;justify-content:center;block-size:64px;inline-size:64px;margin-block-end:8px}.app-favorite-icon{position:absolute;inset-block-start:-8px;inset-inline-end:-1.5rem;cursor:pointer}.app-favorite-icon.favorite{color:var(--element-status-caution)}.app{color:var(--element-text-primary)}.app.active .app-icon{color:var(--element-ui-0);border-color:var(--element-ui-0)}.app:hover .app-icon{color:var(--element-action-secondary-text-hover);background-color:var(--element-action-secondary-hover);border-color:var(--element-action-secondary-border-hover)}.app:focus{box-shadow:none}.app:focus .app-icon,.app:focus .icon-add{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}.app .app-icon,.app .icon-add{border-radius:50%;font-size:48px}.app .app-icon{color:var(--element-text-primary);border:1px solid var(--element-ui-4)}.app .title{inline-size:100%;overflow:hidden;text-overflow:ellipsis}.app .title .element-export{display:inline;line-height:1rem}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: SiTranslateModule }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }, { kind: "ngmodule", type: SiListInteractionModule }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "ngmodule", type: SiLinkModule }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLaunchpadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-launchpad', standalone: true, imports: [CommonModule, SiTranslateModule, SiListInteractionModule, SiLinkModule], template: "<div class=\"app-switcher ps-0 ps-sm-6 pt-8 text-center text-sm-start\">\n  <button\n    type=\"button\"\n    siListInteractionItem\n    class=\"btn btn-circle btn-sm btn-ghost element-cancel position-absolute top-0 end-0 mt-6 me-4\"\n    [attr.aria-label]=\"closeAppSwitcherText | translate\"\n    (click)=\"hideAppSwitcher()\"\n  > </button>\n  <div class=\"mx-9\">\n    <p\n      *ngIf=\"appSwitcherTitle\"\n      class=\"mb-4 si-title-1 lh-1\"\n    >{{ appSwitcherTitle | translate }}</p>\n    <p\n      *ngIf=\"appSwitcherSubTitle\"\n      class=\"si-body-2 text-secondary\"\n      [class.mb-9]=\"isCategorized\"\n      [class.mb-4]=\"!isCategorized\"\n    >{{ appSwitcherSubTitle | translate }}</p>\n  </div>\n  <div class=\"apps-scroll px-9 pb-8\">\n    <div *ngIf=\"showFavorites\" class=\"apps-category focus-none\" siListInteractionItem>\n      <span class=\"si-title-1 apps-category-title\">{{ favoriteAppsTitle | translate }}</span>\n      <div\n        class=\"apps apps-favorite mt-4 justify-content-center justify-content-sm-start focus-direct-sub-inside\"\n        siListInteraction\n        [listInteractionIsSubList]=\"true\"\n        listInteractionPosition=\"adjacent\"\n        listInteractionOrientation=\"ste\"\n        #appSwitcherKeyboardInteraction=\"si-list-interaction\"\n      >\n        <a\n          *ngFor=\"let app of appsFavorites; trackBy: trackByIndex\"\n          siListInteractionItem\n          [siLink]=\"app\"\n          activeClass=\"active\"\n          class=\"app-container-wrapper app app-favorite\"\n          (click)=\"hideAppSwitcher()\"\n        >\n          <div class=\"app-container app-icon\" [ngClass]=\"app.icon ?? ''\"></div>\n          <span class=\"title si-title-2\">{{ app.title! | translate }}</span>\n        </a>\n      </div>\n    </div>\n    <a\n      *ngIf=\"showFavorites\"\n      siListInteractionItem\n      class=\"dropdown-toggle apps-show-more py-4 mb-6 mt-9 si-title-2\"\n      [class.show]=\"showAllApps\"\n      (click)=\"showAllApps = !showAllApps\"\n      (keydown.enter)=\"showAllApps = !showAllApps\"\n    >\n      {{ (showAllApps ? showLessAppsTitle : showMoreAppsTitle) | translate }}<i class=\"dropdown-caret icon element-down-2\"></i>\n    </a>\n    <ng-container *ngIf=\"!showFavorites || showAllApps\">\n      <div\n        *ngFor=\"let appsCategory of appsCategorized; let i = index; trackBy: trackByIndex\"\n        class=\"apps-category focus-none\"\n        [class.mt-4]=\"isCategorized\"\n        siListInteractionItem\n      >\n        <span *ngIf=\"appsCategory.category\" class=\"si-title-1 apps-category-title\">{{ appsCategory.category | translate }}</span>\n        <span *ngIf=\"!appsCategory.category && isCategorized\" class=\"si-title-1 apps-category-title\">{{ defaultAppsTitle | translate }}</span>\n        <div\n          class=\"apps mt-4 ms-0 ms-sm-8 justify-content-center justify-content-sm-start focus-direct-sub-inside\"\n          [class.apps-categorized]=\"isCategorized\"\n          siListInteraction\n          [listInteractionIsSubList]=\"true\"\n          listInteractionPosition=\"adjacent\"\n          listInteractionOrientation=\"ste\"\n          #appSwitcherKeyboardInteraction=\"si-list-interaction\"\n        >\n          <div *ngFor=\"let app of appsCategory.items; trackBy: trackByIndex\" class=\"app-wrapper\">\n            <div *ngIf=\"appItemsFavorites\" class=\"app-container-wrapper position-absolute\">\n              <div class=\"app-container\">\n                <i\n                  class=\"icon app-favorite-icon\"\n                  [class.favorite]=\"app.isFavorite\"\n                  [class.element-favorites-filled]=\"app.isFavorite\"\n                  [class.element-favorites]=\"!app.isFavorite\"\n                  (click)=\"toggleFavorite(app)\"\n                ></i>\n              </div>\n            </div>\n            <a\n              siListInteractionItem\n              [siLink]=\"app\"\n              activeClass=\"active\"\n              class=\"app-container-wrapper app\"\n              (keydown.space)=\"toggleFavorite(app)\"\n              (click)=\"hideAppSwitcherOnClick($event.target)\"\n            >\n              <div class=\"app-container app-icon\" [ngClass]=\"app.icon ?? ''\"></div>\n              <span class=\"title si-title-2\">\n                {{ app.title! | translate }}<i *ngIf=\"app.isExternal\" class=\"icon element-export my-n3\" role=\"presentation\"></i>\n              </span>\n            </a>\n          </div>\n          <div class=\"app-wrapper\">\n            <a\n              *ngIf=\"allAppsLink && appsCategorized!.length - 1 === i\"\n              siListInteractionItem\n              [siLink]=\"allAppsLink\"\n              class=\"app-container-wrapper app app-more\"\n              (click)=\"hideAppSwitcher()\"\n            >\n              <div class=\"app-container icon-add btn btn-circle btn-secondary {{ allAppsLink?.icon || 'element-plus' }}\"></div>\n              <span class=\"title si-title-2\">{{ (allAppsLink?.title || 'Add more')! | translate }}</span>\n            </a>\n          </div>\n        </div>\n      </div>\n    </ng-container>\n  </div>\n</div>\n<div class=\"app-switcher-backdrop\" (click)=\"hideAppSwitcher()\"></div>\n", styles: [".app-switcher-backdrop{z-index:1031;position:fixed;inset:0;background:var(--element-base-translucent-1);opacity:1}.app-switcher{position:fixed;inset-block-start:48px;inset-inline:0;min-block-size:200px;max-block-size:calc(100vh - 48px);display:flex;flex-direction:column;z-index:1032;background-color:var(--element-base-1);box-shadow:inset 0 0 4px var(--element-box-shadow-color-1),inset 0 4px 4px var(--element-box-shadow-color-2)}.app-switcher a:hover,.app-switcher a:focus,.app-switcher a:active{text-decoration:none;outline:none}.apps-show-more{display:inline-block;cursor:pointer}.apps-show-more:hover{text-decoration:underline}.apps-scroll{overflow-y:auto}.apps{display:flex;flex-wrap:wrap}.apps.apps-categorized,.apps.apps-favorite{margin-block-start:0px}.app-wrapper{position:relative;margin-block-end:8px}.app-wrapper:last-child{margin-inline-end:0}.app-container-wrapper{display:flex;flex-direction:column;align-items:center;text-align:center;margin-block:16px;margin-inline:4px;padding-block:8px;padding-inline:0;inline-size:9rem;max-inline-size:9rem}.app-container-wrapper.app-favorite,.app-container-wrapper.app-more{margin-inline-end:8px}.app-container-wrapper.app-favorite:last-child,.app-container-wrapper.app-more:last-child{margin-inline-end:4px}.app-container{position:relative;display:flex;align-items:center;justify-content:center;block-size:64px;inline-size:64px;margin-block-end:8px}.app-favorite-icon{position:absolute;inset-block-start:-8px;inset-inline-end:-1.5rem;cursor:pointer}.app-favorite-icon.favorite{color:var(--element-status-caution)}.app{color:var(--element-text-primary)}.app.active .app-icon{color:var(--element-ui-0);border-color:var(--element-ui-0)}.app:hover .app-icon{color:var(--element-action-secondary-text-hover);background-color:var(--element-action-secondary-hover);border-color:var(--element-action-secondary-border-hover)}.app:focus{box-shadow:none}.app:focus .app-icon,.app:focus .icon-add{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}.app .app-icon,.app .icon-add{border-radius:50%;font-size:48px}.app .app-icon{color:var(--element-text-primary);border:1px solid var(--element-ui-4)}.app .title{inline-size:100%;overflow:hidden;text-overflow:ellipsis}.app .title .element-export{display:inline;line-height:1rem}\n"] }]
        }], propDecorators: { closeAppSwitcherText: [{
                type: Input
            }], appSwitcherTitle: [{
                type: Input
            }], appSwitcherSubTitle: [{
                type: Input
            }], appCategoryItems: [{
                type: Input
            }], appItems: [{
                type: Input
            }], appItemsFavorites: [{
                type: Input
            }], favoriteAppsTitle: [{
                type: Input
            }], defaultAppsTitle: [{
                type: Input
            }], showMoreAppsTitle: [{
                type: Input
            }], showLessAppsTitle: [{
                type: Input
            }], allAppsLink: [{
                type: Input
            }], appItemFavoriteChanged: [{
                type: Output
            }], hide: [{
                type: Output
            }] } });

class SiNavbarProfileMenuComponent {
    constructor() {
        this.trackByIndex = (index) => index;
    }
    ngOnChanges(changes) {
        if (changes.account && this.account && this.account.title) {
            const parts = this.account.title.split(' ');
            let first = parts.shift() ?? '';
            if (first) {
                first = first[0].toLocaleUpperCase();
            }
            let last = parts.pop() ?? '';
            if (last) {
                last = last[0].toLocaleUpperCase();
            }
            this.accountShortName = first + last;
        }
    }
}
SiNavbarProfileMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarProfileMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarProfileMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarProfileMenuComponent, selector: "si-navbar-profile-menu", inputs: { account: "account", items: "items" }, usesOnChanges: true, ngImport: i0, template: "<ul *ngIf=\"account\" class=\"nav navbar-nav profile-menu\" role=\"menu\">\n  <li\n    class=\"dropdown\"\n    siDropdown\n    #dropdown=\"si-dropdown\"\n    [dropdownFocusOnOpen]=\"false\"\n    [dropdownFocusOnClose]=\"false\"\n    role=\"presentation\"\n  >\n    <div\n      class=\"nav-item quick-action\"\n      [class.show]=\"dropdown.isOpen\"\n    >\n      <a class=\"dropdown-item focus-inside px-4\" siListInteractionItem siDropdownToggle role=\"menuitem\">\n        <img *ngIf=\"account.image\" [src]=\"account.image\" [alt]=\"account.title + '\\'s Avatar'\" class=\"navbar-avatar\">\n        <span *ngIf=\"!account.image\" class=\"navbar-avatar\">{{ accountShortName }}</span>\n        <span class=\"title\">{{ account.title }}</span>\n        <span class=\"dropdown-caret menu-end-icon element-down-2 ms-2\"></span>\n      </a>\n    </div>\n    <ul\n      class=\"dropdown-menu\"\n      role=\"menu\"\n      siListInteraction\n      [listInteractionIsSubList]=\"true\"\n      listInteractionOrientation=\"vert\"\n      listInteractionPosition=\"adjacent\"\n      siDropdownMenu\n    >\n      <li class=\"d-block userinfo mx-5 my-4\" role=\"presentation\">\n        <div class=\"fw-bold text-truncate\">{{ account.title }}</div>\n        <div *ngIf=\"account.email\" class=\"email\">{{ account.email }}</div>\n        <div class=\"d-flex align-items-center text-secondary mt-2\">\n          <span *ngIf=\"account.company\" class=\"label me-4\">{{ account.company | translate }}</span>\n          <span *ngIf=\"account.role\" class=\"ms-0 badge bg-default\">\n            {{ account.role | translate }}\n          </span>\n        </div>\n      </li>\n      <ng-container *ngIf=\"items?.length\">\n        <li class=\"d-block dropdown-divider\" role=\"separator\"></li>\n        <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n          <li *ngIf=\"!item.items && item.title === '-'\" class=\"dropdown-divider\" role=\"separator\"></li>\n          <li *ngIf=\"!item.items && item.title !== '-'\" role=\"presentation\">\n            <si-menu-item [item]=\"item\"></si-menu-item>\n          </li>\n          <li *ngIf=\"item.items\"\n            class=\"sub-menu-toggle\"\n            siDropdown\n            role=\"presentation\"\n            [dropdownFocusOnOpen]=\"false\"\n          >\n            <si-menu-item\n              siDropdownToggle\n              [item]=\"item\"\n              [disableParentClick]=\"false\"\n            ></si-menu-item>\n            <si-menu\n              class=\"dropdown-menu sub-menu\"\n              [items]=\"item.items\"\n              [isSubList]=\"true\"\n              siDropdownMenu\n            ></si-menu>\n          </li>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"account.customContent\">\n        <li><ng-content></ng-content></li>\n      </ng-container>\n    </ul>\n  </li>\n</ul>\n", styles: [".navbar-avatar{block-size:2rem;inline-size:2rem;min-inline-size:2rem;color:var(--element-ui-2);background-color:var(--element-base-0);border-radius:50%;margin-block:-.5rem;margin-inline:0;display:inline-flex;justify-content:center;align-items:center;overflow:hidden}@media (max-width: 575.98px){.navbar-avatar{filter:grayscale(80%)}.navbar-avatar .title{display:block}}.dropdown-item{cursor:pointer}.dropdown-item:hover .navbar-avatar,.dropdown-item.show .navbar-avatar{background:var(--element-base-1)}.nav-link:focus .navbar-avatar{color:var(--element-ui-0)}@media (max-width: 575.98px){.profile-menu .nav-link{padding-inline:16px}.profile-menu .title{display:inline!important;padding-inline-start:8px}.profile-menu .sub-menu::ng-deep .dropdown-item{padding-inline-start:28px}}.profile-menu .nav-link{outline:none}.profile-menu .title{display:none}.sub-menu-toggle{cursor:pointer;position:relative}@media (min-width: 576px){.sub-menu-toggle ::ng-deep .dropdown-caret{transform:rotate(-90deg)!important}}@media (min-width: 576px){.dropdown-menu{min-inline-size:240px;max-inline-size:480px}.dropdown-menu.sub-menu{min-inline-size:200px;margin-inline-end:-6px}}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarProfileMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-profile-menu', template: "<ul *ngIf=\"account\" class=\"nav navbar-nav profile-menu\" role=\"menu\">\n  <li\n    class=\"dropdown\"\n    siDropdown\n    #dropdown=\"si-dropdown\"\n    [dropdownFocusOnOpen]=\"false\"\n    [dropdownFocusOnClose]=\"false\"\n    role=\"presentation\"\n  >\n    <div\n      class=\"nav-item quick-action\"\n      [class.show]=\"dropdown.isOpen\"\n    >\n      <a class=\"dropdown-item focus-inside px-4\" siListInteractionItem siDropdownToggle role=\"menuitem\">\n        <img *ngIf=\"account.image\" [src]=\"account.image\" [alt]=\"account.title + '\\'s Avatar'\" class=\"navbar-avatar\">\n        <span *ngIf=\"!account.image\" class=\"navbar-avatar\">{{ accountShortName }}</span>\n        <span class=\"title\">{{ account.title }}</span>\n        <span class=\"dropdown-caret menu-end-icon element-down-2 ms-2\"></span>\n      </a>\n    </div>\n    <ul\n      class=\"dropdown-menu\"\n      role=\"menu\"\n      siListInteraction\n      [listInteractionIsSubList]=\"true\"\n      listInteractionOrientation=\"vert\"\n      listInteractionPosition=\"adjacent\"\n      siDropdownMenu\n    >\n      <li class=\"d-block userinfo mx-5 my-4\" role=\"presentation\">\n        <div class=\"fw-bold text-truncate\">{{ account.title }}</div>\n        <div *ngIf=\"account.email\" class=\"email\">{{ account.email }}</div>\n        <div class=\"d-flex align-items-center text-secondary mt-2\">\n          <span *ngIf=\"account.company\" class=\"label me-4\">{{ account.company | translate }}</span>\n          <span *ngIf=\"account.role\" class=\"ms-0 badge bg-default\">\n            {{ account.role | translate }}\n          </span>\n        </div>\n      </li>\n      <ng-container *ngIf=\"items?.length\">\n        <li class=\"d-block dropdown-divider\" role=\"separator\"></li>\n        <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n          <li *ngIf=\"!item.items && item.title === '-'\" class=\"dropdown-divider\" role=\"separator\"></li>\n          <li *ngIf=\"!item.items && item.title !== '-'\" role=\"presentation\">\n            <si-menu-item [item]=\"item\"></si-menu-item>\n          </li>\n          <li *ngIf=\"item.items\"\n            class=\"sub-menu-toggle\"\n            siDropdown\n            role=\"presentation\"\n            [dropdownFocusOnOpen]=\"false\"\n          >\n            <si-menu-item\n              siDropdownToggle\n              [item]=\"item\"\n              [disableParentClick]=\"false\"\n            ></si-menu-item>\n            <si-menu\n              class=\"dropdown-menu sub-menu\"\n              [items]=\"item.items\"\n              [isSubList]=\"true\"\n              siDropdownMenu\n            ></si-menu>\n          </li>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"account.customContent\">\n        <li><ng-content></ng-content></li>\n      </ng-container>\n    </ul>\n  </li>\n</ul>\n", styles: [".navbar-avatar{block-size:2rem;inline-size:2rem;min-inline-size:2rem;color:var(--element-ui-2);background-color:var(--element-base-0);border-radius:50%;margin-block:-.5rem;margin-inline:0;display:inline-flex;justify-content:center;align-items:center;overflow:hidden}@media (max-width: 575.98px){.navbar-avatar{filter:grayscale(80%)}.navbar-avatar .title{display:block}}.dropdown-item{cursor:pointer}.dropdown-item:hover .navbar-avatar,.dropdown-item.show .navbar-avatar{background:var(--element-base-1)}.nav-link:focus .navbar-avatar{color:var(--element-ui-0)}@media (max-width: 575.98px){.profile-menu .nav-link{padding-inline:16px}.profile-menu .title{display:inline!important;padding-inline-start:8px}.profile-menu .sub-menu::ng-deep .dropdown-item{padding-inline-start:28px}}.profile-menu .nav-link{outline:none}.profile-menu .title{display:none}.sub-menu-toggle{cursor:pointer;position:relative}@media (min-width: 576px){.sub-menu-toggle ::ng-deep .dropdown-caret{transform:rotate(-90deg)!important}}@media (min-width: 576px){.dropdown-menu{min-inline-size:240px;max-inline-size:480px}.dropdown-menu.sub-menu{min-inline-size:200px;margin-inline-end:-6px}}\n"] }]
        }], propDecorators: { account: [{
                type: Input
            }], items: [{
                type: Input
            }] } });

class SiNavbarPrimaryComponent {
    /**
     * Router link for the "home", i.e. logo or app title
     * @deprecated Use `home` instead of `homeLink`.
     * @defaultref {@link home}
     */
    set homeLink(link) {
        this.home = { link };
    }
    /**
     * @deprecated Use `home` instead of `homeLink`.
     */
    get homeLink() {
        return this.home?.link?.toString() ?? '/';
    }
    constructor() {
        /**
         * List of navbar items which should be displayed at the left (in LTR) side next to the
         * banner.
         */
        this.primaryItems = [];
        /**
         * Configurable home link that is used at the logo and app title.
         * Use `undefined` to disable the link.
         */
        this.home = { link: '/' };
        /**
         * title for the app switcher
         */
        this.appSwitcherTitle = 'Launchpad';
        /**
         * sub-title for the app switcher
         */
        this.appSwitcherSubTitle = 'Access all your applications or add more';
        /**
         * Title or translate key for the favorite apps section.
         */
        this.favoriteAppsTitle = 'Favorite apps';
        /**
         * Title or translate key for the default apps section.
         */
        this.defaultAppsTitle = 'Applications';
        /**
         * Title or translate key for the show more apps button.
         */
        this.showMoreAppsTitle = 'Show more';
        /**
         * Title or translate key for the show less apps button.
         */
        this.showLessAppsTitle = 'Show less';
        /**
         * Allow the user to favorite apps which will then be displayed at the top.
         */
        this.appItemsFavorites = false;
        /**
         * Specifies whether the component should automatically be focused as soon as it is loaded.
         */
        this.focusOnLoad = false;
        /**
         * Marks the navbar as primary navigation element. Needed for a11y (screen reader).
         * Only one element should be primary. If multiple navbars are used, it's up to the
         * user of the components to label them in the correct order.
         */
        this.navAriaLabel = 'Primary';
        /**
         * Text to close the app switcher. Needed for a11y.
         */
        this.closeAppSwitcherText = 'Close App Switcher';
        /**
         * Text for the app switcher icon. Needed for a11y.
         */
        this.openAppSwitcherText = 'Open App Switcher';
        /**
         * Text or translate key for the toggle navigation icon. Needed for a11y.
         */
        this.toggleNavigationText = 'Toggle navigation';
        this.appItemFavoriteChanged = new EventEmitter();
        this.showMobileNav = false;
        this.appsVisible = false;
        this.router = inject(Router);
        this.route = inject(ActivatedRoute);
        this.trackByIndex = (index) => index;
        this.setSelectedItemTitle(this.router.url);
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(event => {
            this.setSelectedItemTitle(event.url);
            this.appsVisible = false;
        });
    }
    setSelectedItemTitle(url) {
        setTimeout(() => {
            const compareSelected = (item) => {
                const commands = Array.isArray(item.link) ? item.link : [item.link];
                const urlTree = this.router.createUrlTree(commands, { relativeTo: this.route });
                const serialized = this.router.serializeUrl(urlTree);
                return serialized === url;
            };
            const selected = this.primaryItems.find(compareSelected)
                ?? this.accountItems?.find(compareSelected)
                ?? this.primaryItems.find(item => !!item.items?.find(compareSelected));
            this.selectedItemTitle = selected?.title ?? '';
        });
    }
    ngOnChanges() {
        if (this.account) {
            this.accountItem = this.ref?.nativeElement.innerHTML
                ? Object.assign({ customContent: true }, this.account)
                : Object.assign({ items: this.accountItems }, this.account);
        }
        else {
            this.accountItem = undefined;
        }
    }
    closeMobileNav(event) {
        let element = event.target;
        while (element && element.tagName !== 'A') {
            if (element.classList?.contains('userinfo')) {
                return;
            }
            element = element.parentElement;
        }
        if (element && (element.hasAttribute('sidropdowntoggle')
            || element.parentElement && element.parentElement.tagName === 'SI-MENU-ITEM'
                && element.parentElement.hasAttribute('sidropdowntoggle'))) {
            return;
        }
        this.showMobileNav = false;
    }
    focusLaunchpad() {
        if (this.appsVisible) {
            setTimeout(() => this.appSwitcherKeyboardInteraction.focusFirstItem(), 12);
        }
    }
    toggleAppSwitcher() {
        this.appsVisible = !this.appsVisible;
    }
    hideAppSwitcher() {
        this.appsVisible = false;
    }
    toggleMobileNav(event) {
        this.showMobileNav = !this.showMobileNav;
    }
    onClick(targetElement) {
        const clickedInside = this.navbar?.nativeElement.contains(targetElement);
        if (!clickedInside) {
            this.showMobileNav = false;
        }
    }
}
SiNavbarPrimaryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarPrimaryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarPrimaryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarPrimaryComponent, selector: "si-navbar-primary", inputs: { primaryItems: "primaryItems", accountItems: "accountItems", account: "account", logoUrl: "logoUrl", appTitle: "appTitle", homeLink: "homeLink", home: "home", appSwitcherTitle: "appSwitcherTitle", appSwitcherSubTitle: "appSwitcherSubTitle", favoriteAppsTitle: "favoriteAppsTitle", defaultAppsTitle: "defaultAppsTitle", showMoreAppsTitle: "showMoreAppsTitle", showLessAppsTitle: "showLessAppsTitle", appItems: "appItems", appCategoryItems: "appCategoryItems", appItemsFavorites: "appItemsFavorites", allAppsLink: "allAppsLink", focusOnLoad: "focusOnLoad", navAriaLabel: "navAriaLabel", closeAppSwitcherText: "closeAppSwitcherText", openAppSwitcherText: "openAppSwitcherText", toggleNavigationText: "toggleNavigationText" }, outputs: { appItemFavoriteChanged: "appItemFavoriteChanged" }, host: { listeners: { "document:click": "onClick($event.target)" } }, viewQueries: [{ propertyName: "ref", first: true, predicate: ["ref"], descendants: true, static: true }, { propertyName: "navbar", first: true, predicate: ["navbar"], descendants: true }, { propertyName: "appSwitcherToggleComponent", first: true, predicate: ["appSwitcherToggle"], descendants: true }, { propertyName: "appSwitcherKeyboardInteraction", first: true, predicate: ["appSwitcherKeyboardInteraction"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  [cdkTrapFocus]=\"appsVisible && !!appItems && !!appCategoryItems\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  listInteractionFocus=\"first\"\n  [listInteractionFocusOnLoad]=\"focusOnLoad\"\n  class=\"focus-none\"\n>\n  <div siListInteractionItem class=\"focus-none\">\n    <nav\n      #navbar\n      class=\"navbar navbar-primary navbar-expand-sm navbar-light fixed-top elevation-1 focus-inside focus-direct-sub-inside\"\n      [attr.aria-label]=\"navAriaLabel | translate\"\n      siListInteraction\n      listInteractionPosition=\"adjacent\"\n      [listInteractionIsSubList]=\"true\"\n      [listInteractionFocus]=\"navbarToggler.offsetParent && !showMobileNav ? 'first' : ''\"\n      [listInteractionOrientation]=\"navbarToggler.offsetParent && showMobileNav ? 'vert' : 'ste'\"\n      [listInteractionAutofocusFromParent]=\"true\"\n    >\n      <button\n        #navbarToggler\n        type=\"button\"\n        class=\"navbar-toggler focus-inside\"\n        aria-expanded=\"false\"\n        (click)=\"hideAppSwitcher(); toggleMobileNav($event);\"\n        siListInteractionItem\n      >\n        <span class=\"visually-hidden\">{{ toggleNavigationText | translate }}</span>\n        <span class=\"element-menu\"></span>\n      </button>\n      <div *ngIf=\"appTitle\" class=\"title-outside\">\n        <a\n          siListInteractionItem\n          class=\"navbar-app-title focus-inside\"\n          [class.has-selected-item]=\"selectedItemTitle\"\n          [siLink]=\"home\"\n        >\n          <div class=\"navbar-app-title-inner text-truncate px-6 si-body-1\">{{ appTitle }}</div>\n        </a>\n      </div>\n      <div class=\"d-sm-none selected-item\">\n        <span *ngIf=\"selectedItemTitle\">{{ selectedItemTitle! | translate }}</span>\n      </div>\n      <div class=\"collapse navbar-collapse\" [class.show]=\"showMobileNav\">\n        <div class=\"start\">\n          <ng-container *ngIf=\"appItems || appCategoryItems\">\n            <a\n              #appSwitcherToggle=\"si-list-interaction-item\"\n              href=\"#\"\n              siListInteractionItem\n              class=\"app-switcher-trigger navbar-collapse collapse focus-inside\"\n              [attr.aria-label]=\"openAppSwitcherText | translate\"\n              (click)=\"toggleAppSwitcher(); $event.preventDefault()\"\n            >\n              <i class=\"icon element-thumbnails m-5\"></i>\n            </a>\n          </ng-container>\n          <si-navbar-brand class=\"d-none d-lg-flex\" [logoUrl]=\"logoUrl\" [link]=\"home\"></si-navbar-brand>\n          <div *ngIf=\"appTitle\" class=\"title-collapse\">\n            <a\n              siListInteractionItem\n              class=\"navbar-app-title focus-inside\"\n              [class.has-selected-item]=\"selectedItemTitle\"\n              [siLink]=\"home\"\n            >\n              <div class=\"navbar-app-title-inner text-truncate px-6 si-body-1\">{{ appTitle }}</div>\n            </a>\n          </div>\n        </div>\n        <ul *ngIf=\"appItems || appCategoryItems\" class=\"nav navbar-nav app-switcher-menu\">\n          <li class=\"nav-item\">\n            <a\n              href=\"#\"\n              class=\"dropdown-item\"\n              siListInteractionItem\n              (click)=\"toggleAppSwitcher(); focusLaunchpad(); closeMobileNav($event); $event.preventDefault()\"\n            >\n              <i class=\"icon element-thumbnails\"></i>\n              <span class=\"item-title\">{{ appSwitcherTitle | translate}}</span>\n            </a>\n          </li>\n        </ul>\n        <div class=\"navbar-start ms-sm-6\" (click)=\"closeMobileNav($event)\">\n          <si-navbar-item *ngFor=\"let item of primaryItems; trackBy: trackByIndex\" [item]=\"item\"></si-navbar-item>\n        </div>\n        <div class=\"navbar-end end\" (click)=\"closeMobileNav($event)\">\n          <ng-content select=\"si-navbar-item [quickAction]\"></ng-content>\n\n          <si-navbar-profile-menu *ngIf=\"!!accountItem\" [items]=\"accountItems\" [account]=\"accountItem\">\n            <div #ref>\n              <ng-content></ng-content>\n            </div>\n          </si-navbar-profile-menu>\n        </div>\n      </div>\n    </nav>\n  </div>\n  <ng-container *ngIf=\"appsVisible\">\n    <si-launchpad\n      [closeAppSwitcherText]=\"closeAppSwitcherText\"\n      [appSwitcherTitle]=\"appSwitcherTitle\"\n      [appSwitcherSubTitle]=\"appSwitcherSubTitle\"\n      [appCategoryItems]=\"appCategoryItems\"\n      [appItems]=\"appItems\"\n      [appItemsFavorites]=\"appItemsFavorites\"\n      [favoriteAppsTitle]=\"favoriteAppsTitle\"\n      [defaultAppsTitle]=\"defaultAppsTitle\"\n      [showMoreAppsTitle]=\"showMoreAppsTitle\"\n      [showLessAppsTitle]=\"showLessAppsTitle\"\n      [allAppsLink]=\"allAppsLink\"\n      (appItemFavoriteChanged)=\"appItemFavoriteChanged.emit($event)\"\n      (hide)=\"hideAppSwitcher()\"\n    ></si-launchpad>\n  </ng-container>\n</div>\n", styles: ["@media (max-width: 575.98px){.navbar-primary .navbar-collapse>*:nth-child(n+3){margin-block-start:16px}.navbar-primary .title-collapse{display:none}}@media (min-width: 576px){.navbar-primary .app-switcher-menu{display:none}.navbar-primary .navbar-end::ng-deep si-navbar-item:last-child{margin-inline-end:8px}}.navbar-primary ::ng-deep .navbar-start .navbar-nav>li>a .icon{display:none!important}.navbar-primary ::ng-deep .navbar-start .navbar-nav>li>a .item-title{padding-inline-start:0!important}.navbar-primary .selected-item{flex:1;display:flex;align-items:center;justify-content:center;font-weight:700;color:var(--element-ui-2)}.app-switcher-trigger{color:var(--element-action-primary-text);background:var(--element-action-primary);flex:0;min-inline-size:48px}.app-switcher-trigger:hover,.app-switcher-trigger:focus,.app-switcher-trigger:active{text-decoration:none;outline:none;background:var(--element-action-primary-hover)}.start{display:flex;justify-content:flex-start;inline-size:100%;padding-inline-end:16px}.end{justify-content:flex-end;inline-size:100%}\n"], dependencies: [{ kind: "directive", type: i1$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiLaunchpadComponent, selector: "si-launchpad", inputs: ["closeAppSwitcherText", "appSwitcherTitle", "appSwitcherSubTitle", "appCategoryItems", "appItems", "appItemsFavorites", "favoriteAppsTitle", "defaultAppsTitle", "showMoreAppsTitle", "showLessAppsTitle", "allAppsLink"], outputs: ["appItemFavoriteChanged", "hide"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiNavbarBrandComponent, selector: "si-navbar-brand", inputs: ["logoUrl", "link", "logoText"] }, { kind: "component", type: SiNavbarItemComponent, selector: "si-navbar-item", inputs: ["item", "quickAction"] }, { kind: "component", type: SiNavbarProfileMenuComponent, selector: "si-navbar-profile-menu", inputs: ["account", "items"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarPrimaryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-primary', template: "<div\n  [cdkTrapFocus]=\"appsVisible && !!appItems && !!appCategoryItems\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  listInteractionFocus=\"first\"\n  [listInteractionFocusOnLoad]=\"focusOnLoad\"\n  class=\"focus-none\"\n>\n  <div siListInteractionItem class=\"focus-none\">\n    <nav\n      #navbar\n      class=\"navbar navbar-primary navbar-expand-sm navbar-light fixed-top elevation-1 focus-inside focus-direct-sub-inside\"\n      [attr.aria-label]=\"navAriaLabel | translate\"\n      siListInteraction\n      listInteractionPosition=\"adjacent\"\n      [listInteractionIsSubList]=\"true\"\n      [listInteractionFocus]=\"navbarToggler.offsetParent && !showMobileNav ? 'first' : ''\"\n      [listInteractionOrientation]=\"navbarToggler.offsetParent && showMobileNav ? 'vert' : 'ste'\"\n      [listInteractionAutofocusFromParent]=\"true\"\n    >\n      <button\n        #navbarToggler\n        type=\"button\"\n        class=\"navbar-toggler focus-inside\"\n        aria-expanded=\"false\"\n        (click)=\"hideAppSwitcher(); toggleMobileNav($event);\"\n        siListInteractionItem\n      >\n        <span class=\"visually-hidden\">{{ toggleNavigationText | translate }}</span>\n        <span class=\"element-menu\"></span>\n      </button>\n      <div *ngIf=\"appTitle\" class=\"title-outside\">\n        <a\n          siListInteractionItem\n          class=\"navbar-app-title focus-inside\"\n          [class.has-selected-item]=\"selectedItemTitle\"\n          [siLink]=\"home\"\n        >\n          <div class=\"navbar-app-title-inner text-truncate px-6 si-body-1\">{{ appTitle }}</div>\n        </a>\n      </div>\n      <div class=\"d-sm-none selected-item\">\n        <span *ngIf=\"selectedItemTitle\">{{ selectedItemTitle! | translate }}</span>\n      </div>\n      <div class=\"collapse navbar-collapse\" [class.show]=\"showMobileNav\">\n        <div class=\"start\">\n          <ng-container *ngIf=\"appItems || appCategoryItems\">\n            <a\n              #appSwitcherToggle=\"si-list-interaction-item\"\n              href=\"#\"\n              siListInteractionItem\n              class=\"app-switcher-trigger navbar-collapse collapse focus-inside\"\n              [attr.aria-label]=\"openAppSwitcherText | translate\"\n              (click)=\"toggleAppSwitcher(); $event.preventDefault()\"\n            >\n              <i class=\"icon element-thumbnails m-5\"></i>\n            </a>\n          </ng-container>\n          <si-navbar-brand class=\"d-none d-lg-flex\" [logoUrl]=\"logoUrl\" [link]=\"home\"></si-navbar-brand>\n          <div *ngIf=\"appTitle\" class=\"title-collapse\">\n            <a\n              siListInteractionItem\n              class=\"navbar-app-title focus-inside\"\n              [class.has-selected-item]=\"selectedItemTitle\"\n              [siLink]=\"home\"\n            >\n              <div class=\"navbar-app-title-inner text-truncate px-6 si-body-1\">{{ appTitle }}</div>\n            </a>\n          </div>\n        </div>\n        <ul *ngIf=\"appItems || appCategoryItems\" class=\"nav navbar-nav app-switcher-menu\">\n          <li class=\"nav-item\">\n            <a\n              href=\"#\"\n              class=\"dropdown-item\"\n              siListInteractionItem\n              (click)=\"toggleAppSwitcher(); focusLaunchpad(); closeMobileNav($event); $event.preventDefault()\"\n            >\n              <i class=\"icon element-thumbnails\"></i>\n              <span class=\"item-title\">{{ appSwitcherTitle | translate}}</span>\n            </a>\n          </li>\n        </ul>\n        <div class=\"navbar-start ms-sm-6\" (click)=\"closeMobileNav($event)\">\n          <si-navbar-item *ngFor=\"let item of primaryItems; trackBy: trackByIndex\" [item]=\"item\"></si-navbar-item>\n        </div>\n        <div class=\"navbar-end end\" (click)=\"closeMobileNav($event)\">\n          <ng-content select=\"si-navbar-item [quickAction]\"></ng-content>\n\n          <si-navbar-profile-menu *ngIf=\"!!accountItem\" [items]=\"accountItems\" [account]=\"accountItem\">\n            <div #ref>\n              <ng-content></ng-content>\n            </div>\n          </si-navbar-profile-menu>\n        </div>\n      </div>\n    </nav>\n  </div>\n  <ng-container *ngIf=\"appsVisible\">\n    <si-launchpad\n      [closeAppSwitcherText]=\"closeAppSwitcherText\"\n      [appSwitcherTitle]=\"appSwitcherTitle\"\n      [appSwitcherSubTitle]=\"appSwitcherSubTitle\"\n      [appCategoryItems]=\"appCategoryItems\"\n      [appItems]=\"appItems\"\n      [appItemsFavorites]=\"appItemsFavorites\"\n      [favoriteAppsTitle]=\"favoriteAppsTitle\"\n      [defaultAppsTitle]=\"defaultAppsTitle\"\n      [showMoreAppsTitle]=\"showMoreAppsTitle\"\n      [showLessAppsTitle]=\"showLessAppsTitle\"\n      [allAppsLink]=\"allAppsLink\"\n      (appItemFavoriteChanged)=\"appItemFavoriteChanged.emit($event)\"\n      (hide)=\"hideAppSwitcher()\"\n    ></si-launchpad>\n  </ng-container>\n</div>\n", styles: ["@media (max-width: 575.98px){.navbar-primary .navbar-collapse>*:nth-child(n+3){margin-block-start:16px}.navbar-primary .title-collapse{display:none}}@media (min-width: 576px){.navbar-primary .app-switcher-menu{display:none}.navbar-primary .navbar-end::ng-deep si-navbar-item:last-child{margin-inline-end:8px}}.navbar-primary ::ng-deep .navbar-start .navbar-nav>li>a .icon{display:none!important}.navbar-primary ::ng-deep .navbar-start .navbar-nav>li>a .item-title{padding-inline-start:0!important}.navbar-primary .selected-item{flex:1;display:flex;align-items:center;justify-content:center;font-weight:700;color:var(--element-ui-2)}.app-switcher-trigger{color:var(--element-action-primary-text);background:var(--element-action-primary);flex:0;min-inline-size:48px}.app-switcher-trigger:hover,.app-switcher-trigger:focus,.app-switcher-trigger:active{text-decoration:none;outline:none;background:var(--element-action-primary-hover)}.start{display:flex;justify-content:flex-start;inline-size:100%;padding-inline-end:16px}.end{justify-content:flex-end;inline-size:100%}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { primaryItems: [{
                type: Input
            }], accountItems: [{
                type: Input
            }], account: [{
                type: Input
            }], logoUrl: [{
                type: Input
            }], appTitle: [{
                type: Input
            }], homeLink: [{
                type: Input
            }], home: [{
                type: Input
            }], appSwitcherTitle: [{
                type: Input
            }], appSwitcherSubTitle: [{
                type: Input
            }], favoriteAppsTitle: [{
                type: Input
            }], defaultAppsTitle: [{
                type: Input
            }], showMoreAppsTitle: [{
                type: Input
            }], showLessAppsTitle: [{
                type: Input
            }], appItems: [{
                type: Input
            }], appCategoryItems: [{
                type: Input
            }], appItemsFavorites: [{
                type: Input
            }], allAppsLink: [{
                type: Input
            }], focusOnLoad: [{
                type: Input
            }], navAriaLabel: [{
                type: Input
            }], closeAppSwitcherText: [{
                type: Input
            }], openAppSwitcherText: [{
                type: Input
            }], toggleNavigationText: [{
                type: Input
            }], appItemFavoriteChanged: [{
                type: Output
            }], ref: [{
                type: ViewChild,
                args: ['ref', { static: true }]
            }], navbar: [{
                type: ViewChild,
                args: ['navbar', { static: false }]
            }], appSwitcherToggleComponent: [{
                type: ViewChild,
                args: ['appSwitcherToggle']
            }], appSwitcherKeyboardInteraction: [{
                type: ViewChild,
                args: ['appSwitcherKeyboardInteraction']
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

// ------------------------------------------------------------------------------

// ------------------------------------------------------------------------------

const components$1 = [
    SiNavbarBrandComponent,
    SiNavbarItemComponent,
    SiNavbarProfileMenuComponent,
    SiNavbarPrimaryComponent
];
class SiNavbarModule {
}
SiNavbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiNavbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarModule, declarations: [SiNavbarBrandComponent,
        SiNavbarItemComponent,
        SiNavbarProfileMenuComponent,
        SiNavbarPrimaryComponent], imports: [A11yModule,
        CommonModule,
        RouterModule,
        SiDropdownModule,
        SiLaunchpadComponent,
        SiLinkModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule], exports: [SiNavbarBrandComponent,
        SiNavbarItemComponent,
        SiNavbarProfileMenuComponent,
        SiNavbarPrimaryComponent] });
SiNavbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarModule, imports: [A11yModule,
        CommonModule,
        RouterModule,
        SiDropdownModule,
        SiLaunchpadComponent,
        SiLinkModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        A11yModule,
                        CommonModule,
                        RouterModule,
                        SiDropdownModule,
                        SiLaunchpadComponent,
                        SiLinkModule,
                        SiListInteractionModule,
                        SiMenuModule,
                        SiTranslateModule
                    ],
                    declarations: components$1,
                    exports: components$1
                }]
        }] });

// ------------------------------------------------------------------------------

class SiNavbarSecondaryComponent {
    constructor() {
        /**
         * Array of navbar items.
         */
        this.items = [];
        /**
         * If multiple secondary navbars are used each one needs a distinct aria label.
         * Required for a11y
         */
        this.navAriaLabel = 'Secondary';
        this.trackByIndex = (index) => index;
        this.active = false;
    }
    submenuActiveChange(active) {
        this.active = active;
    }
}
SiNavbarSecondaryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarSecondaryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarSecondaryComponent, selector: "si-navbar-secondary", inputs: { items: "items", navAriaLabel: "navAriaLabel" }, ngImport: i0, template: "<nav class=\"navbar navbar-secondary\" [attr.aria-label]=\"navAriaLabel | translate\">\n  <div class=\"navbar-collapse navbar-expand\">\n    <ul class=\"nav navbar-nav nav-tabs focus-inside\" role=\"menu\" siListInteraction>\n      <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n        <li\n          *ngIf=\"item.items || item.customContent\"\n          class=\"dropdown nav-item\"\n          siDropdown\n          dropdownContainer=\"body\"\n          [dropdownFocusOnOpen]=\"false\"\n          role=\"presentation\"\n        >\n          <si-menu-item\n            siDropdownToggle\n            #dropdownToggle=\"si-dropdown-toggle\"\n            [item]=\"item\"\n            [disableParentClick]=\"false\"\n            [forceActive]=\"active\"\n            [focusStylingInside]=\"true\"\n            [dropdownToggle]=\"dropdownToggle\"\n            itemClass=\"nav-link\"\n          ></si-menu-item>\n          <ng-container *ngIf=\"item.items\">\n            <si-menu\n              siDropdownMenu\n              class=\"dropdown-menu\"\n              [items]=\"item.items\"\n              [isSubList]=\"true\"\n              position=\"adjacent\"\n              (activeChange)=\"submenuActiveChange($event)\"\n            ></si-menu>\n          </ng-container>\n          <ng-container *ngIf=\"item.customContent\">\n            <div class=\"dropdown-menu custom-content\" siDropdownMenu>\n              <ng-content></ng-content>\n            </div>\n          </ng-container>\n        </li>\n        <li *ngIf=\"!item.items && !item.customContent\" role=\"presentation\" class=\"nav-item\">\n          <si-menu-item\n            itemClass=\"nav-link\"\n            [item]=\"item\"\n            [focusStylingInside]=\"true\"\n          ></si-menu-item>\n        </li>\n      </ng-container>\n    </ul>\n  </div>\n</nav>\n", styles: [".navbar-expand{overflow-y:hidden;overflow-x:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-secondary', template: "<nav class=\"navbar navbar-secondary\" [attr.aria-label]=\"navAriaLabel | translate\">\n  <div class=\"navbar-collapse navbar-expand\">\n    <ul class=\"nav navbar-nav nav-tabs focus-inside\" role=\"menu\" siListInteraction>\n      <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n        <li\n          *ngIf=\"item.items || item.customContent\"\n          class=\"dropdown nav-item\"\n          siDropdown\n          dropdownContainer=\"body\"\n          [dropdownFocusOnOpen]=\"false\"\n          role=\"presentation\"\n        >\n          <si-menu-item\n            siDropdownToggle\n            #dropdownToggle=\"si-dropdown-toggle\"\n            [item]=\"item\"\n            [disableParentClick]=\"false\"\n            [forceActive]=\"active\"\n            [focusStylingInside]=\"true\"\n            [dropdownToggle]=\"dropdownToggle\"\n            itemClass=\"nav-link\"\n          ></si-menu-item>\n          <ng-container *ngIf=\"item.items\">\n            <si-menu\n              siDropdownMenu\n              class=\"dropdown-menu\"\n              [items]=\"item.items\"\n              [isSubList]=\"true\"\n              position=\"adjacent\"\n              (activeChange)=\"submenuActiveChange($event)\"\n            ></si-menu>\n          </ng-container>\n          <ng-container *ngIf=\"item.customContent\">\n            <div class=\"dropdown-menu custom-content\" siDropdownMenu>\n              <ng-content></ng-content>\n            </div>\n          </ng-container>\n        </li>\n        <li *ngIf=\"!item.items && !item.customContent\" role=\"presentation\" class=\"nav-item\">\n          <si-menu-item\n            itemClass=\"nav-link\"\n            [item]=\"item\"\n            [focusStylingInside]=\"true\"\n          ></si-menu-item>\n        </li>\n      </ng-container>\n    </ul>\n  </div>\n</nav>\n", styles: [".navbar-expand{overflow-y:hidden;overflow-x:auto}\n"] }]
        }], propDecorators: { items: [{
                type: Input
            }], navAriaLabel: [{
                type: Input
            }] } });

class SiNavbarSecondaryModule {
}
SiNavbarSecondaryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiNavbarSecondaryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryModule, declarations: [SiNavbarSecondaryComponent], imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule], exports: [SiNavbarSecondaryComponent] });
SiNavbarSecondaryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryModule, imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiMenuModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarSecondaryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiDropdownModule,
                        SiListInteractionModule,
                        SiMenuModule,
                        SiTranslateModule
                    ],
                    declarations: [SiNavbarSecondaryComponent],
                    exports: [SiNavbarSecondaryComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiSearchBarComponent {
    constructor() {
        this._value = '';
        this.debouncer = new Subject();
        /**
         * Time unit change of search input takes effect.
         */
        this.debounceTime = 400;
        /**
         * Define search input placeholder.
         */
        this.placeholder = '';
        /**
         * Define search input content.
         */
        this.tabbable = true;
        this._readonly = false;
        /**
         * Color to use for component background
         */
        this.colorVariant = 'base-1';
        /**
         * Output callback event will provide you with search term if search input changes.
         */
        this.searchChange = new EventEmitter();
        this.isInvalid = false;
        this.inFocus = false;
        this.onChange = (_val) => { };
        this.onTouch = () => { };
    }
    /**
     * Whether the search is tabbable or not.
     */
    get value() {
        return this._value;
    }
    /**
     * @defaultref {@link _value}
     */
    set value(value) {
        if (value !== this._value) {
            this.writeValue(value);
            this.onChange(this._value);
            this.onTouch();
            this.searchChange.emit(this._value);
        }
    }
    /**
     * Disable component
     */
    get disabled() {
        return this.inputRef.nativeElement.disabled;
    }
    set disabled(value) {
        this.setDisabledState(coerceBooleanProperty(value));
    }
    get readonly() {
        return this._readonly;
    }
    /**
     * @defaultref {@link _readonly}
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    writeValue(value) {
        this._value = value ?? '';
        this.inputRef.nativeElement.value = this._value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this.inputRef.nativeElement.disabled = isDisabled;
    }
    ngOnInit() {
        this.debouncer.pipe(debounceTime(this.debounceTime)).subscribe(value => this.value = value);
    }
    ngOnDestroy() {
        this.debouncer.complete();
    }
    resetForm() {
        this.value = '';
    }
    isProhibitedCharactersUsed(searchString) {
        if (!this.prohibitedCharacters || !searchString) {
            return false;
        }
        for (const prohibitedCharacter of this.prohibitedCharacters) {
            if (searchString.includes(prohibitedCharacter)) {
                return true;
            }
        }
        return false;
    }
    focus() {
        this.inputRef.nativeElement.focus();
    }
    onCancelFocus(event) {
        event.stopPropagation();
    }
    input(event) {
        const value = event.target.value;
        if (!this.isProhibitedCharactersUsed(value)) {
            this.debouncer.next(value);
        }
    }
}
SiSearchBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSearchBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSearchBarComponent, selector: "si-search-bar", inputs: { debounceTime: "debounceTime", prohibitedCharacters: "prohibitedCharacters", placeholder: "placeholder", showIcon: "showIcon", tabbable: "tabbable", value: "value", disabled: "disabled", readonly: "readonly", colorVariant: "colorVariant" }, outputs: { searchChange: "searchChange" }, host: { listeners: { "focus": "focus()" }, properties: { "class.readonly": "this._readonly" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiSearchBarComponent,
            multi: true
        }
    ], viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["inputRef"], descendants: true, static: true }], ngImport: i0, template: "<div [class.disabled]=\"disabled\" [class.focus]=\"inFocus\" class=\"position-relative\">\n  <i *ngIf=\"showIcon\" class=\"search-bar-icon icon element-search text-secondary\"></i>\n  <input\n    #inputRef\n    type=\"text\"\n    class=\"form-control\"\n    [placeholder]=\"placeholder\"\n    [class.dark-background]=\"colorVariant === 'base-0'\"\n    [class.ps-9]=\"showIcon\"\n    [class.is-invalid]=\"isInvalid\"\n    [class.icon-end]=\"value\"\n    (focus)=\"inFocus = true\"\n    (blur)=\"inFocus = false\"\n    [attr.tabindex]=\"tabbable ? '' : '-1'\"\n    [readonly]=\"readonly\"\n    (input)=\"input($event)\"\n  />\n  <button\n    *ngIf=\"value\"\n    [disabled]=\"disabled || readonly\"\n    type=\"button\"\n    aria-label=\"clear\"\n    class=\"btn btn-circle btn-xs btn-ghost cancel-button element-cancel\"\n    (click)=\"resetForm()\"\n    (focusin)=\"onCancelFocus($event)\"\n  ></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-secondary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1)}:host div:hover .search-bar-icon,:host div:hover ::placeholder,:host div.focus .search-bar-icon,:host div.focus ::placeholder{color:var(--element-text-primary)!important}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4)}input{box-shadow:none;border-color:var(--element-base-1)!important;transition:none;background-color:var(--input-background-color);border-radius:4px}input.form-control:focus-visible{background-color:var(--input-background-color);border-color:var(--input-background-color)!important}input.form-control:hover:not([disabled]){border-color:var(--element-ui-4)}.cancel-button{position:absolute;inset-block-start:4px;inset-inline-end:4px}.is-invalid~.cancel-button{color:var(--element-status-danger)}.search-bar-icon{position:absolute;inset-block-start:4px;inset-inline-start:4px;color:var(--search-bar-icon-color);line-height:1}.icon-end{padding-inline-end:1.5rem}::placeholder{color:var(--element-text-secondary);padding-inline-start:2px}.disabled .search-bar-icon,.disabled input,.disabled .cancel-button,.disabled ::placeholder{color:var(--element-text-disabled)}.disabled input{background-color:var(--input-background-color);border-color:var(--input-background-color)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-search-bar', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSearchBarComponent,
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div [class.disabled]=\"disabled\" [class.focus]=\"inFocus\" class=\"position-relative\">\n  <i *ngIf=\"showIcon\" class=\"search-bar-icon icon element-search text-secondary\"></i>\n  <input\n    #inputRef\n    type=\"text\"\n    class=\"form-control\"\n    [placeholder]=\"placeholder\"\n    [class.dark-background]=\"colorVariant === 'base-0'\"\n    [class.ps-9]=\"showIcon\"\n    [class.is-invalid]=\"isInvalid\"\n    [class.icon-end]=\"value\"\n    (focus)=\"inFocus = true\"\n    (blur)=\"inFocus = false\"\n    [attr.tabindex]=\"tabbable ? '' : '-1'\"\n    [readonly]=\"readonly\"\n    (input)=\"input($event)\"\n  />\n  <button\n    *ngIf=\"value\"\n    [disabled]=\"disabled || readonly\"\n    type=\"button\"\n    aria-label=\"clear\"\n    class=\"btn btn-circle btn-xs btn-ghost cancel-button element-cancel\"\n    (click)=\"resetForm()\"\n    (focusin)=\"onCancelFocus($event)\"\n  ></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-secondary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1)}:host div:hover .search-bar-icon,:host div:hover ::placeholder,:host div.focus .search-bar-icon,:host div.focus ::placeholder{color:var(--element-text-primary)!important}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4)}input{box-shadow:none;border-color:var(--element-base-1)!important;transition:none;background-color:var(--input-background-color);border-radius:4px}input.form-control:focus-visible{background-color:var(--input-background-color);border-color:var(--input-background-color)!important}input.form-control:hover:not([disabled]){border-color:var(--element-ui-4)}.cancel-button{position:absolute;inset-block-start:4px;inset-inline-end:4px}.is-invalid~.cancel-button{color:var(--element-status-danger)}.search-bar-icon{position:absolute;inset-block-start:4px;inset-inline-start:4px;color:var(--search-bar-icon-color);line-height:1}.icon-end{padding-inline-end:1.5rem}::placeholder{color:var(--element-text-secondary);padding-inline-start:2px}.disabled .search-bar-icon,.disabled input,.disabled .cancel-button,.disabled ::placeholder{color:var(--element-text-disabled)}.disabled input{background-color:var(--input-background-color);border-color:var(--input-background-color)}\n"] }]
        }], propDecorators: { inputRef: [{
                type: ViewChild,
                args: ['inputRef', { static: true }]
            }], debounceTime: [{
                type: Input
            }], prohibitedCharacters: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], showIcon: [{
                type: Input
            }], tabbable: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }], readonly: [{
                type: Input
            }], colorVariant: [{
                type: Input
            }], searchChange: [{
                type: Output
            }], focus: [{
                type: HostListener,
                args: ['focus']
            }] } });

class SiNavbarVerticalFlyoutComponent {
    constructor() {
        this.closeMenu = new EventEmitter(true);
        this.flyoutItems = [];
        this.visible = 'hidden';
        this.calculatedTop = 0;
        this.cdRef = inject(ChangeDetectorRef);
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.clickHandler = (event) => this.handleWindowClick(event);
        this.keydownHandler = (event) => {
            if (event.key === 'Enter') {
                this.handleWindowClick(event);
            }
        };
    }
    ngOnInit() {
        this.flyoutItems = [{ ...this.item, items: undefined }, ...(this.item.items ? this.item.items : [])];
        setTimeout(() => this.setPosition());
        // CAPTURE click and keydown enter event to ensure the menu is closed automatically
        if (this.isBrowser) {
            window.addEventListener('click', this.clickHandler, true);
            window.addEventListener('keydown', this.keydownHandler, true);
        }
    }
    ngOnDestroy() {
        if (this.isBrowser) {
            window.removeEventListener('click', this.clickHandler, true);
            window.removeEventListener('keydown', this.keydownHandler, true);
        }
    }
    setPosition() {
        this.calculatedTop = this.top;
        // check if there's enough room below, adjust if not
        const height = this.menuRef.nativeElement.offsetHeight;
        const navbarTop = this.navbar.offsetTop;
        const navbarHeight = this.navbar.offsetHeight;
        const avail = navbarHeight - this.top;
        if (height > avail) {
            this.calculatedTop -= height - avail;
            if (this.calculatedTop < 0) {
                this.calculatedTop = 0;
            }
        }
        this.calculatedTop += navbarTop;
        this.visible = 'visible';
        this.cdRef.markForCheck();
    }
    handleWindowClick(_event) {
        this.closeMenu.emit();
    }
}
SiNavbarVerticalFlyoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalFlyoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarVerticalFlyoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarVerticalFlyoutComponent, selector: "si-navbar-vertical-flyout", inputs: { item: "item", top: "top", navbar: "navbar", menuParentItem: "menuParentItem" }, outputs: { closeMenu: "closeMenu" }, viewQueries: [{ propertyName: "menuRef", first: true, predicate: ["menu"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<si-menu\n  #menu\n  [items]=\"flyoutItems\"\n  [style.visibility]=\"visible\"\n  [style.top.px]=\"calculatedTop\"\n  [isSubList]=\"true\"\n  [parentItem]=\"menuParentItem\"\n></si-menu>\n", styles: [":host{display:block}:host si-menu{display:block;position:fixed;inset-inline-start:48px;margin-inline-start:2px;margin-block-start:0}:host si-menu ::ng-deep li:first-child .dropdown-item{font-weight:700}:host si-menu.dropdown-menu::ng-deep si-menu-item>a.dropdown-item>span.item-title{display:inline}\n"], dependencies: [{ kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalFlyoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-vertical-flyout', template: "<si-menu\n  #menu\n  [items]=\"flyoutItems\"\n  [style.visibility]=\"visible\"\n  [style.top.px]=\"calculatedTop\"\n  [isSubList]=\"true\"\n  [parentItem]=\"menuParentItem\"\n></si-menu>\n", styles: [":host{display:block}:host si-menu{display:block;position:fixed;inset-inline-start:48px;margin-inline-start:2px;margin-block-start:0}:host si-menu ::ng-deep li:first-child .dropdown-item{font-weight:700}:host si-menu.dropdown-menu::ng-deep si-menu-item>a.dropdown-item>span.item-title{display:inline}\n"] }]
        }], propDecorators: { menuRef: [{
                type: ViewChild,
                args: ['menu', { static: true, read: ElementRef }]
            }], item: [{
                type: Input
            }], top: [{
                type: Input
            }], navbar: [{
                type: Input
            }], menuParentItem: [{
                type: Input
            }], closeMenu: [{
                type: Output
            }] } });

class SiNavbarVerticalItemComponent {
    constructor() {
        this.nav = true;
        this.textOnly = false;
        this.collapsed = '';
        this.menuTrigger = new EventEmitter();
        this.open = false;
        this.expanded = false;
        this.subItemActive = false;
        this.flyOut = false;
        this.flyOutTop = 0;
        this.autoExpanded = false;
        this.elementRef = inject(ElementRef);
        this.cdRef = inject(ChangeDetectorRef);
        this.trackByIndex = (index) => index;
    }
    get isLink() {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return (this.item.action || this.item.link || this.item.href);
    }
    ngOnChanges(changes) {
        if (changes.item?.currentValue.expanded !== undefined && this.expanded !== changes.item.currentValue.expanded) {
            this.triggerMenu(undefined, false);
        }
    }
    checkActive() {
        this.subItemActive = this.itemLink?.active || !!this.subItems?.some(item => item.active);
        if (this.subItemActive && !this.autoExpanded) {
            this.autoExpanded = true;
            this.open = true;
            this.expanded = true;
            this.item.expanded = true;
            this.menuMaxHeight = undefined; // prevent animation
            this.setExpandedHeight();
            this.menuTrigger.emit();
        }
        this.cdRef.markForCheck();
    }
    triggerMenu(event, animate = true) {
        event?.preventDefault();
        if (this.collapsed) {
            this.triggerFlyout();
            return;
        }
        this.flyOut = false;
        this.open = !this.open;
        // set to undefined to disable animation
        this.menuMaxHeight = animate ? 0 : undefined;
        this.item.expanded = !this.expanded;
        if (this.expanded) {
            setTimeout(() => {
                this.expanded = false;
                this.cdRef.markForCheck();
            }, 500);
        }
        else {
            this.expanded = true;
            this.setExpandedHeight();
        }
        this.menuTrigger.emit();
        this.cdRef.markForCheck();
    }
    triggerItemOrMenu(event) {
        if (!this.collapsed && this.isLink) {
            this.itemLink.onClick(event);
        }
        else {
            this.triggerMenu(event);
        }
    }
    setExpandedHeight() {
        setTimeout(() => {
            this.menuMaxHeight = this.menuBody?.nativeElement?.scrollHeight;
            this.cdRef.markForCheck();
        });
    }
    triggerFlyout() {
        this.flyOut = !this.flyOut;
        this.flyOutTop = this.elementRef.nativeElement.offsetTop - this.scroller.scrollTop;
        this.cdRef.markForCheck();
    }
}
SiNavbarVerticalItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarVerticalItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarVerticalItemComponent, selector: "si-navbar-vertical-item", inputs: { item: "item", textOnly: "textOnly", navbar: "navbar", scroller: "scroller", collapsed: "collapsed", collapseButtonText: "collapseButtonText", expandButtonText: "expandButtonText" }, outputs: { menuTrigger: "menuTrigger" }, host: { properties: { "class.navbar-nav": "this.nav", "class.text-only": "this.textOnly" } }, viewQueries: [{ propertyName: "menuBody", first: true, predicate: ["menuBody"], descendants: true }, { propertyName: "itemLink", first: true, predicate: ["itemLink"], descendants: true, read: SiLinkDirective }, { propertyName: "subItems", predicate: ["siLink"], descendants: true, read: SiLinkDirective }], usesOnChanges: true, ngImport: i0, template: "<!-- section header -->\n<div *ngIf=\"!isLink && !item.items\" class=\"section-item fw-bold text-secondary px-5\">\n  {{ item.title! | translate }}\n</div>\n\n<!-- normal item -->\n<a *ngIf=\"isLink && !item.items\"\n  class=\"nav-item pe-5 nav-main-item focus-inside\"\n  [siLink]=\"item\"\n  activeClass=\"active\"\n  #itemLink=\"siLink\"\n  [attr.aria-current]=\"itemLink.active\"\n  role=\"menuitem\"\n  (activeChange)=\"checkActive()\"\n  siListInteractionItem\n  [listInteractionItemDisabled]=\"collapsed === 'hidden'\"\n>\n  <i *ngIf=\"item.icon\"\n    class=\"icon ms-5 me-n2\"\n    [ngClass]=\"[item.icon, itemLink.active ? item.icon + '-filled' : '']\"\n    role=\"presentation\"\n  ></i>\n  <span\n    *ngIf=\"item.icon && item.badge !== undefined && item.badge !== ''\"\n    class=\"badge-text\"\n  >{{ item.badge }}</span>\n  <span\n    class=\"item-title text-truncate fw-bold\"\n    [class.ps-4]=\"item.icon\"\n    [class.ps-5]=\"!item.icon\"\n  >{{ item.title! | translate }}</span>\n  <span *ngIf=\"item.badge !== undefined && item.badge !== ''\"\n    class=\"badge\"\n    [ngClass]=\"'bg-' + item.badgeColor\"\n  >\n    {{ item.badge }}\n  </span>\n</a>\n\n<!-- sub menu -->\n<div *ngIf=\"item.items\"\n  role=\"menu\"\n  class=\"sub-menu navbar-nav\"\n  [class.open]=\"open\"\n  [class.expanded]=\"expanded\"\n  [attr.aria-expanded]=\"expanded\"\n  [class.sub-active]=\"subItemActive || flyOut\"\n>\n  <div\n    class=\"d-flex\"\n    [class.open]=\"open\"\n    [class.hover-full]=\"!item.link && !item.action && !item.href\"\n  >\n    <a\n      class=\"flex-fill nav-item pe-5 nav-main-item focus-inside\"\n      [class.navbar-vertical-no-collapse]=\"!isLink\"\n      [class.active]=\"subItemActive\"\n      [attr.title]=\"item.tooltip ? (item.tooltip | translate) : null\"\n      [attr.aria-current]=\"subItemActive\"\n      (click)=\"triggerItemOrMenu($event)\"\n      (keydown.enter)=\"triggerItemOrMenu($event)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"(!isLink && !collapsed) || collapsed === 'hidden'\"\n      #menuParentItem=\"si-list-interaction-item\"\n    >\n      <a\n        [siLink]=\"item\"\n        activeClass=\"active\"\n        #itemLink=\"siLink\"\n        (keydown.enter)=\"$event.preventDefault()\"\n        (activeChange)=\"checkActive()\"\n        tabindex=\"-1\"\n        class=\"d-none\"\n      > </a>\n      <i *ngIf=\"item.icon\"\n        class=\"icon ps-5 me-n2\"\n        [ngClass]=\"[item.icon, subItemActive ? item.icon + '-filled' : '']\"\n        role=\"presentation\"\n      ></i>\n      <span class=\"item-title text-truncate fw-bold ps-5\">{{ item.title! | translate }}</span>\n      <span *ngIf=\"item.badge !== undefined && item.badge !== ''\"\n        class=\"badge\"\n        [ngClass]=\"'bg-' + item.badgeColor\"\n      >\n        {{ item.badge }}\n      </span>\n    </a>\n    <a\n      class=\"nav-item px-5 nav-main-item expander navbar-vertical-no-collapse focus-inside\"\n      (click)=\"triggerMenu($event)\"\n      (keydown.enter)=\"triggerMenu($event)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!collapsed\"\n      role=\"button\"\n      [attr.aria-label]=\"(expanded ? collapseButtonText : expandButtonText) | translate\"\n    >\n      <span role=\"presentation\" class=\"dropdown-caret element-down-2\"></span>\n    </a>\n  </div>\n  <div class=\"menu-body\" #menuBody [style.max-height.px]=\"menuMaxHeight\">\n    <a *ngFor=\"let subitem of item.items; trackBy: trackByIndex\"\n      class=\"nav-item nav-sub-item focus-inside ps-9 pe-5\"\n      [siLink]=\"subitem\"\n      activeClass=\"active\"\n      #siLink=\"siLink\"\n      [attr.aria-current]=\"siLink.active\"\n      role=\"menuitem\"\n      (activeChange)=\"checkActive()\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!collapsed || !open\"\n    >\n      <i *ngIf=\"subitem.icon\"\n        class=\"icon ps-5 me-n2\"\n        [ngClass]=\"[subitem.icon, siLink.active ? subitem.icon + '-filled' : '']\"\n        role=\"presentation\"\n      ></i>\n      <span\n        class=\"item-title text-truncate\"\n        [class.ps-5]=\"!textOnly\"\n      >{{ subitem.title! | translate }}</span>\n      <span *ngIf=\"subitem.badge !== undefined && subitem.badge !== ''\"\n        class=\"badge\"\n        [ngClass]=\"'bg-' + subitem.badgeColor\"\n      >\n        {{ subitem.badge }}\n      </span>\n    </a>\n  </div>\n  <si-navbar-vertical-flyout\n    *ngIf=\"flyOut\"\n    [item]=\"item\"\n    [top]=\"flyOutTop\"\n    [navbar]=\"navbar\"\n    (closeMenu)=\"flyOut = false\"\n    [menuParentItem]=\"menuParentItem\"\n  ></si-navbar-vertical-flyout>\n</div>\n", styles: [":host{display:flex;overflow:hidden}:host>*{flex:1;white-space:nowrap}.nav-item,.section-item{display:flex;align-items:center;min-block-size:40px}.nav-item{background:transparent;color:var(--element-text-primary);position:relative;text-decoration:none;cursor:pointer}.nav-item:hover{background:var(--element-base-1-hover)}.nav-item.active{color:var(--element-ui-0)}.hover-full:hover .nav-item{background:var(--element-base-1-hover)}.sub-active .hover-full:hover .nav-item{background:var(--element-base-1)}.nav-main-item{min-inline-size:0}.nav-main-item.active{background:var(--element-base-0)}.nav-main-item.active:after{content:\"\";position:absolute;inset-block:0;inset-inline-start:0;inline-size:4px;background:var(--element-ui-0)}.menu-body{display:none;flex-direction:column}.expanded .menu-body{display:flex;transition:max-block-size calc(.5s * var(--element-animations-enabled, 1))}.open .dropdown-caret{transform:rotate(180deg)}.expander{flex:0 0 auto;align-self:stretch;display:flex;align-items:center}.sub-active{background:var(--element-base-0)}.sub-active .nav-item:hover{background:var(--element-base-1)}.badge{margin-inline-end:0}.badge-text{display:none;inset-block-start:-8px;inset-inline-start:-6px}.item-title{flex:1 0 0;min-inline-size:0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiNavbarVerticalFlyoutComponent, selector: "si-navbar-vertical-flyout", inputs: ["item", "top", "navbar", "menuParentItem"], outputs: ["closeMenu"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-vertical-item', template: "<!-- section header -->\n<div *ngIf=\"!isLink && !item.items\" class=\"section-item fw-bold text-secondary px-5\">\n  {{ item.title! | translate }}\n</div>\n\n<!-- normal item -->\n<a *ngIf=\"isLink && !item.items\"\n  class=\"nav-item pe-5 nav-main-item focus-inside\"\n  [siLink]=\"item\"\n  activeClass=\"active\"\n  #itemLink=\"siLink\"\n  [attr.aria-current]=\"itemLink.active\"\n  role=\"menuitem\"\n  (activeChange)=\"checkActive()\"\n  siListInteractionItem\n  [listInteractionItemDisabled]=\"collapsed === 'hidden'\"\n>\n  <i *ngIf=\"item.icon\"\n    class=\"icon ms-5 me-n2\"\n    [ngClass]=\"[item.icon, itemLink.active ? item.icon + '-filled' : '']\"\n    role=\"presentation\"\n  ></i>\n  <span\n    *ngIf=\"item.icon && item.badge !== undefined && item.badge !== ''\"\n    class=\"badge-text\"\n  >{{ item.badge }}</span>\n  <span\n    class=\"item-title text-truncate fw-bold\"\n    [class.ps-4]=\"item.icon\"\n    [class.ps-5]=\"!item.icon\"\n  >{{ item.title! | translate }}</span>\n  <span *ngIf=\"item.badge !== undefined && item.badge !== ''\"\n    class=\"badge\"\n    [ngClass]=\"'bg-' + item.badgeColor\"\n  >\n    {{ item.badge }}\n  </span>\n</a>\n\n<!-- sub menu -->\n<div *ngIf=\"item.items\"\n  role=\"menu\"\n  class=\"sub-menu navbar-nav\"\n  [class.open]=\"open\"\n  [class.expanded]=\"expanded\"\n  [attr.aria-expanded]=\"expanded\"\n  [class.sub-active]=\"subItemActive || flyOut\"\n>\n  <div\n    class=\"d-flex\"\n    [class.open]=\"open\"\n    [class.hover-full]=\"!item.link && !item.action && !item.href\"\n  >\n    <a\n      class=\"flex-fill nav-item pe-5 nav-main-item focus-inside\"\n      [class.navbar-vertical-no-collapse]=\"!isLink\"\n      [class.active]=\"subItemActive\"\n      [attr.title]=\"item.tooltip ? (item.tooltip | translate) : null\"\n      [attr.aria-current]=\"subItemActive\"\n      (click)=\"triggerItemOrMenu($event)\"\n      (keydown.enter)=\"triggerItemOrMenu($event)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"(!isLink && !collapsed) || collapsed === 'hidden'\"\n      #menuParentItem=\"si-list-interaction-item\"\n    >\n      <a\n        [siLink]=\"item\"\n        activeClass=\"active\"\n        #itemLink=\"siLink\"\n        (keydown.enter)=\"$event.preventDefault()\"\n        (activeChange)=\"checkActive()\"\n        tabindex=\"-1\"\n        class=\"d-none\"\n      > </a>\n      <i *ngIf=\"item.icon\"\n        class=\"icon ps-5 me-n2\"\n        [ngClass]=\"[item.icon, subItemActive ? item.icon + '-filled' : '']\"\n        role=\"presentation\"\n      ></i>\n      <span class=\"item-title text-truncate fw-bold ps-5\">{{ item.title! | translate }}</span>\n      <span *ngIf=\"item.badge !== undefined && item.badge !== ''\"\n        class=\"badge\"\n        [ngClass]=\"'bg-' + item.badgeColor\"\n      >\n        {{ item.badge }}\n      </span>\n    </a>\n    <a\n      class=\"nav-item px-5 nav-main-item expander navbar-vertical-no-collapse focus-inside\"\n      (click)=\"triggerMenu($event)\"\n      (keydown.enter)=\"triggerMenu($event)\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!collapsed\"\n      role=\"button\"\n      [attr.aria-label]=\"(expanded ? collapseButtonText : expandButtonText) | translate\"\n    >\n      <span role=\"presentation\" class=\"dropdown-caret element-down-2\"></span>\n    </a>\n  </div>\n  <div class=\"menu-body\" #menuBody [style.max-height.px]=\"menuMaxHeight\">\n    <a *ngFor=\"let subitem of item.items; trackBy: trackByIndex\"\n      class=\"nav-item nav-sub-item focus-inside ps-9 pe-5\"\n      [siLink]=\"subitem\"\n      activeClass=\"active\"\n      #siLink=\"siLink\"\n      [attr.aria-current]=\"siLink.active\"\n      role=\"menuitem\"\n      (activeChange)=\"checkActive()\"\n      siListInteractionItem\n      [listInteractionItemDisabled]=\"!!collapsed || !open\"\n    >\n      <i *ngIf=\"subitem.icon\"\n        class=\"icon ps-5 me-n2\"\n        [ngClass]=\"[subitem.icon, siLink.active ? subitem.icon + '-filled' : '']\"\n        role=\"presentation\"\n      ></i>\n      <span\n        class=\"item-title text-truncate\"\n        [class.ps-5]=\"!textOnly\"\n      >{{ subitem.title! | translate }}</span>\n      <span *ngIf=\"subitem.badge !== undefined && subitem.badge !== ''\"\n        class=\"badge\"\n        [ngClass]=\"'bg-' + subitem.badgeColor\"\n      >\n        {{ subitem.badge }}\n      </span>\n    </a>\n  </div>\n  <si-navbar-vertical-flyout\n    *ngIf=\"flyOut\"\n    [item]=\"item\"\n    [top]=\"flyOutTop\"\n    [navbar]=\"navbar\"\n    (closeMenu)=\"flyOut = false\"\n    [menuParentItem]=\"menuParentItem\"\n  ></si-navbar-vertical-flyout>\n</div>\n", styles: [":host{display:flex;overflow:hidden}:host>*{flex:1;white-space:nowrap}.nav-item,.section-item{display:flex;align-items:center;min-block-size:40px}.nav-item{background:transparent;color:var(--element-text-primary);position:relative;text-decoration:none;cursor:pointer}.nav-item:hover{background:var(--element-base-1-hover)}.nav-item.active{color:var(--element-ui-0)}.hover-full:hover .nav-item{background:var(--element-base-1-hover)}.sub-active .hover-full:hover .nav-item{background:var(--element-base-1)}.nav-main-item{min-inline-size:0}.nav-main-item.active{background:var(--element-base-0)}.nav-main-item.active:after{content:\"\";position:absolute;inset-block:0;inset-inline-start:0;inline-size:4px;background:var(--element-ui-0)}.menu-body{display:none;flex-direction:column}.expanded .menu-body{display:flex;transition:max-block-size calc(.5s * var(--element-animations-enabled, 1))}.open .dropdown-caret{transform:rotate(180deg)}.expander{flex:0 0 auto;align-self:stretch;display:flex;align-items:center}.sub-active{background:var(--element-base-0)}.sub-active .nav-item:hover{background:var(--element-base-1)}.badge{margin-inline-end:0}.badge-text{display:none;inset-block-start:-8px;inset-inline-start:-6px}.item-title{flex:1 0 0;min-inline-size:0}\n"] }]
        }], propDecorators: { nav: [{
                type: HostBinding,
                args: ['class.navbar-nav']
            }], item: [{
                type: Input
            }], textOnly: [{
                type: HostBinding,
                args: ['class.text-only']
            }, {
                type: Input
            }], navbar: [{
                type: Input
            }], scroller: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], collapseButtonText: [{
                type: Input
            }], expandButtonText: [{
                type: Input
            }], menuTrigger: [{
                type: Output
            }], menuBody: [{
                type: ViewChild,
                args: ['menuBody', { static: false }]
            }], itemLink: [{
                type: ViewChild,
                args: ['itemLink', { static: false, read: SiLinkDirective }]
            }], subItems: [{
                type: ViewChildren,
                args: ['siLink', { read: SiLinkDirective }]
            }] } });

class SiNavbarVerticalComponent {
    constructor() {
        /**
         * Default state of navigation
         */
        this.collapsed = false;
        /**
         * Toggles search bar
         */
        this.searchable = false;
        /**
         * Placeholder text for search
         */
        this.searchPlaceholder = 'Search...';
        /**
         * List of vertical navigation items
         */
        this.items = [];
        this.itemsChange = new EventEmitter();
        /**
         * Set to `true` if there are no icons
         */
        this.textOnly = false;
        /**
         * Set to false to hide the vertical navbar
         */
        this.visible = true;
        /**
         * Set a delay in milliseconds to automatically collapse the navigation bar in mobile view.
         * When used, the main content view is visible after the configured delay. This ensures
         * that important status bar views are not hidden by the vertical navigation bar longer
         * than e.g. 3000 milliseconds. Default is -1 to disable the auto collapse function.
         */
        this.autoCollapseDelay = -1;
        /**
         * Text for the navbar expand button. Required for a11y
         */
        this.navbarExpandButtonText = 'expand';
        /**
         * Text for the navbar collapse button. Required for a11y
         */
        this.navbarCollapseButtonText = 'collapse';
        /**
         * Output for search bar input
         */
        this.searchEvent = new EventEmitter();
        this.mobileExpanded = false;
        this.ready = false;
        /**
         * The $vertical-nav-transition-duration in the style is 0.5 seconds.
         * Sending the resize event after resize need to wait until resize is done.
         */
        this.resizeEventDelay = 500;
        this.resizeEvent = new Subject();
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.element = inject(ElementRef);
        this.cdRef = inject(ChangeDetectorRef);
        this.resizeObserver = inject(ResizeObserverService);
        this.uiStateService = inject(SiUIStateService, { optional: true });
        this.isMobile = false;
        this.isMobileSmall = false;
        this.trackByIndex = (index) => index;
    }
    ngOnInit() {
        if (this.isBrowser) {
            this.resizeSubs = this.resizeObserver.observe(this.element.nativeElement, 100, true)
                .subscribe(() => this.checkSize());
        }
        setTimeout(() => this.ready = true);
    }
    ngOnChanges(changes) {
        if (changes.collapsed) {
            this.updateCollapsedState();
        }
        if (changes.items && this.uiStateService && this.stateId) {
            this.uiStateService.load(this.stateId).pipe(take(1))
                .subscribe(store => this.loadUIState(store));
        }
    }
    ngOnDestroy() {
        this.resizeSubs?.unsubscribe();
    }
    checkSize() {
        const paddingStart = parseInt(window.getComputedStyle(this.element.nativeElement).paddingLeft, 10);
        this.isMobile = paddingStart < 100;
        this.isMobileSmall = paddingStart === 0;
        this.cdRef.markForCheck();
    }
    updateCollapsedState() {
        setTimeout(() => {
            this.checkSize();
        }, this.resizeEventDelay);
    }
    onEnterKeydown(target) {
        if (target.classList.contains('collapse-toggle')) {
            setTimeout(() => {
                this.listInteraction.focusFirstItem();
                setTimeout(() => {
                    this.listInteraction.focusFirstItem();
                }, this.resizeEventDelay);
            });
        }
    }
    toggleCollapse(event) {
        event.currentTarget.blur();
        this.collapsed = !this.collapsed;
        this.updateCollapsedState();
        this.resizeEvent.next();
        this.cdRef.markForCheck();
    }
    toggleMobileExpand(event) {
        event.currentTarget.blur();
        this.mobileExpanded = !this.mobileExpanded;
        this.updateCollapsedState();
        this.mobileCollapseTimer();
    }
    mobileCollapseTimer() {
        this.saveUIState();
        this.itemsChange.emit(this.items);
        if (this.autoCollapseDelay <= 0) {
            return;
        }
        if (this.autoCollapseTimer) {
            clearTimeout(this.autoCollapseTimer);
            this.autoCollapseTimer = undefined;
        }
        if (this.mobileExpanded) {
            this.autoCollapseTimer = setTimeout(() => {
                this.mobileExpanded = false;
                this.updateCollapsedState();
                this.cdRef.markForCheck();
            }, this.autoCollapseDelay);
        }
    }
    handleClick(event) {
        if (this.mobileExpanded) {
            const target = event.target;
            if (!target.classList.contains('navbar-vertical-no-collapse') && !this.searchBarElement?.nativeElement.contains(target)
                && (target.tagName !== 'SPAN' && target.tagName !== 'I'
                    || !target.parentElement?.classList.contains('navbar-vertical-no-collapse'))) {
                this.mobileExpanded = false;
                this.updateCollapsedState();
                this.mobileCollapseTimer();
                this.cdRef.markForCheck();
            }
        }
    }
    toggleSearch(event, mobile) {
        if (mobile) {
            this.toggleMobileExpand(event);
        }
        else {
            this.toggleCollapse(event);
        }
        setTimeout(() => this.searchBar.focus());
    }
    doSearch(event) {
        this.searchEvent.emit(event);
        this.mobileCollapseTimer();
    }
    get isCollapsed() {
        const hidden = this.isMobileSmall && !this.mobileExpanded ||
            this.textOnly && (!this.isMobile && this.collapsed || this.isMobile && !this.mobileExpanded);
        return hidden ? 'hidden' :
            (this.isMobile && !this.mobileExpanded || !this.isMobile && this.collapsed) ? 'shown' : '';
    }
    saveUIState() {
        if (!this.uiStateService || !this.stateId) {
            return;
        }
        const state = { contextId: this.stateId, expandables: {} };
        this.items.forEach(item => {
            if (item.id) {
                state.expandables[item.id] = !!item.expanded;
            }
        });
        this.uiStateService.save(state);
    }
    loadUIState(state) {
        if (!state?.expandables) {
            return;
        }
        this.items.forEach(item => {
            // Only update
            //   - items with an id
            //   - and if the item has not explicitly already set an expanded state
            if (item.id && item.expanded === undefined) {
                item.expanded = state.expandables[item.id];
            }
        });
    }
}
SiNavbarVerticalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNavbarVerticalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNavbarVerticalComponent, selector: "si-navbar-vertical", inputs: { collapsed: "collapsed", searchable: "searchable", searchPlaceholder: "searchPlaceholder", items: "items", textOnly: "textOnly", visible: "visible", autoCollapseDelay: "autoCollapseDelay", navbarExpandButtonText: "navbarExpandButtonText", navbarCollapseButtonText: "navbarCollapseButtonText", stateId: "stateId" }, outputs: { itemsChange: "itemsChange", searchEvent: "searchEvent" }, host: { listeners: { "keydown.enter": "onEnterKeydown($event.target)" }, properties: { "class.nav-collapsed": "this.collapsed", "class.nav-text-only": "this.textOnly", "class.visible": "this.visible", "class.nav-mobile-expanded": "this.mobileExpanded", "class.ready": "this.ready" }, classAttribute: "si-layout-inner" }, viewQueries: [{ propertyName: "searchBar", first: true, predicate: ["searchBar"], descendants: true }, { propertyName: "searchBarElement", first: true, predicate: ["searchBar"], descendants: true, read: ElementRef }, { propertyName: "listInteraction", first: true, predicate: ["listInteraction"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"focus-none\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  [listInteractionShowSubListOnParentFocus]=\"false\"\n  listInteractionFocus=\"first\"\n  #listInteraction=\"si-list-interaction\"\n>\n  <div\n    *ngIf=\"visible && !mobileExpanded\"\n    class=\"collapse-toggle mobile-drawer mobile navbar-vertical-no-collapse focus-inside\"\n  >\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl element-double-right navbar-vertical-no-collapse\"\n      (click)=\"toggleMobileExpand($event)\"\n      siListInteractionItem\n      [attr.aria-label]=\"navbarExpandButtonText | translate\"\n    >\n    </button>\n  </div>\n  <div\n    *ngIf=\"visible && textOnly && collapsed\"\n    class=\"collapse-toggle mobile-drawer desktop focus-inside\"\n  >\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl element-double-right\"\n      (click)=\"toggleCollapse($event)\"\n      siListInteractionItem\n      [attr.aria-label]=\"navbarExpandButtonText | translate\"\n    >\n    </button>\n  </div>\n\n  <nav\n    *ngIf=\"visible\"\n    #nav\n    class=\"elevation-1 bg-base-1 focus-sub-inside\"\n    [class.text-only]=\"textOnly\"\n    (click)=\"handleClick($event)\"\n  >\n    <div\n      class=\"collapse-toggle desktop focus-inside\"\n    >\n      <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl\"\n        (click)=\"toggleCollapse($event)\"\n        siListInteractionItem\n        [listInteractionItemDisabled]=\"(isMobileSmall && !mobileExpanded) || (((isMobile && !mobileExpanded) || (!isMobile && collapsed)) && textOnly)\"\n        [class.element-double-left]=\"!collapsed\"\n        [class.element-double-right]=\"collapsed\"\n        [attr.aria-label]=\"(collapsed ? navbarExpandButtonText : navbarCollapseButtonText) | translate\"\n        [attr.aria-expanded]=\"!collapsed\">\n      </button>\n    </div>\n    <div class=\"collapse-toggle mobile navbar-vertical-no-collapse focus-inside\"\n    >\n      <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl navbar-vertical-no-collapse\"\n        [class.element-double-left]=\"mobileExpanded\"\n        [class.element-double-right]=\"!mobileExpanded\"\n        (click)=\"toggleMobileExpand($event)\"\n        siListInteractionItem\n        [listInteractionItemDisabled]=\"(isMobileSmall && !mobileExpanded) || (((isMobile && !mobileExpanded) || (!isMobile && collapsed)) && textOnly)\"\n        [attr.aria-label]=\"(mobileExpanded ? navbarCollapseButtonText : navbarExpandButtonText) | translate\"\n        [attr.aria-expanded]=\"mobileExpanded\"\n      >\n      </button>\n    </div>\n    <div *ngIf=\"searchable && (!isMobileSmall || mobileExpanded)\" class=\"nav-search py-4 mt-2\">\n      <si-search-bar\n        class=\"d-block mx-5\"\n        #searchBar\n        [placeholder]=\"searchPlaceholder | translate\"\n        [colorVariant]=\"'base-0'\"\n        (searchChange)=\"doSearch($event)\"\n        [debounceTime]=\"400\"\n        [showIcon]=\"true\"\n        [prohibitedCharacters]=\"'*?'\"\n        [tabbable]=\"false\"\n        siListInteractionItem\n      ></si-search-bar>\n      <button type=\"button\"\n        class=\"btn-search bg-base-0 p-2 mx-4 desktop navbar-vertical-no-collapse\"\n        [attr.aria-label]=\"searchPlaceholder | translate\"\n        (click)=\"toggleSearch($event, false)\"\n        siListInteractionItem\n      >\n        <i class=\"icon element-search\"></i>\n      </button>\n      <button type=\"button\"\n        class=\"btn-search bg-base-0 p-2 mx-4 mobile navbar-vertical-no-collapse\"\n        [attr.aria-label]=\"searchPlaceholder | translate\"\n        (click)=\"toggleSearch($event, true)\"\n        siListInteractionItem\n      >\n        <i class=\"icon element-search\"></i>\n      </button>\n    </div>\n    <div class=\"nav-scroll mt-2\" #scroller role=\"menu\">\n      <si-navbar-vertical-item\n        *ngFor=\"let item of items; trackBy: trackByIndex\"\n        class=\"mb-4\"\n        [item]=\"item\"\n        [textOnly]=\"textOnly\"\n        [scroller]=\"scroller\"\n        [collapsed]=\"isCollapsed\"\n        [collapseButtonText]=\"navbarCollapseButtonText\"\n        [expandButtonText]=\"navbarExpandButtonText\"\n        [navbar]=\"nav\"\n        (menuTrigger)=\"mobileCollapseTimer()\"\n      ></si-navbar-vertical-item>\n    </div>\n  </nav>\n</div>\n<main class=\"si-layout-inner\">\n  <ng-content></ng-content>\n</main>\n", styles: [":host{display:block}:host.ready{transition:padding-inline-start calc(.5s * var(--element-animations-enabled, 1))}@media (min-width: 992px){:host.visible{padding-inline-start:240px}:host.visible:not(.nav-text-only).nav-collapsed{padding-inline-start:48px}:host.visible:not(.nav-text-only).nav-collapsed nav{inline-size:48px}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item.expander,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .item-title,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .dropdown-caret,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .badge{display:none}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .badge-text{display:inline-block}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .section-item{display:none}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .menu-body{max-block-size:0!important}:host.visible:not(.nav-text-only).nav-collapsed si-search-bar{display:none!important}:host.visible:not(.nav-text-only).nav-collapsed .btn-search.desktop{display:block}:host.visible.nav-text-only.nav-collapsed{padding-inline-start:0!important}:host.visible.nav-text-only.nav-collapsed:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible.nav-text-only.nav-collapsed:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible.nav-text-only.nav-collapsed ::ng-deep .page-top-title{margin-inline-start:40px}:host.visible.nav-text-only.nav-collapsed .mobile-drawer.mobile{display:block}:host.visible .collapse-toggle.mobile{display:none!important}}@media (min-width: 576px) and (max-width: 991.98px){:host.visible:not(.nav-text-only){padding-inline-start:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded){padding-inline-start:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) nav{inline-size:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item.expander,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .item-title,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .dropdown-caret,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .badge{display:none}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .badge-text{display:inline-block}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .section-item{display:none}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .menu-body{max-block-size:0!important}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) si-search-bar{display:none!important}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .btn-search.mobile{display:block}:host.visible.nav-text-only{padding-inline-start:0!important}:host.visible.nav-text-only:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible.nav-text-only:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible.nav-text-only ::ng-deep .page-top-title{margin-inline-start:40px}.collapse-toggle.desktop{display:none!important}}@media (max-width: 575.98px){:host.visible{padding-inline-start:0!important}:host.visible:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible ::ng-deep .page-top-title{margin-inline-start:40px}:host.visible:after{content:\"\";position:fixed;inset-block:48px 0;inset-inline:0;background:transparent;pointer-events:none;transition:background calc(.5s * var(--element-animations-enabled, 1));z-index:2}:host.visible.nav-mobile-expanded:after{background:var(--element-base-translucent-1);pointer-events:auto}:host.visible .collapse-toggle.desktop{display:none}}nav{display:flex;flex-direction:column;position:fixed;z-index:1029;inset-block:48px 0;inset-inline-start:0;inline-size:240px;overflow-x:hidden;transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}.nav-scroll{overflow-x:hidden;min-block-size:0;flex:1 0 0;overflow-y:auto}.collapse-toggle{display:block;border:0;text-align:end;background:transparent;color:var(--element-text-primary);padding-block:4px;padding-inline:8px;margin-block-start:8px}.collapse-toggle.mobile-drawer{display:none;position:fixed;inset-inline-start:0;inset-block-start:48px;border-start-end-radius:var(--element-radius-2);border-end-end-radius:var(--element-radius-2);background:var(--element-base-1);box-shadow:0 0 4px var(--element-box-shadow-color-1),0 4px 4px var(--element-box-shadow-color-2);z-index:2}.collapse-toggle.mobile-drawer.desktop{display:none}.btn-search{display:none;border:0;border-radius:var(--element-radius-2)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiSearchBarComponent, selector: "si-search-bar", inputs: ["debounceTime", "prohibitedCharacters", "placeholder", "showIcon", "tabbable", "value", "disabled", "readonly", "colorVariant"], outputs: ["searchChange"] }, { kind: "component", type: SiNavbarVerticalItemComponent, selector: "si-navbar-vertical-item", inputs: ["item", "textOnly", "navbar", "scroller", "collapsed", "collapseButtonText", "expandButtonText"], outputs: ["menuTrigger"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-navbar-vertical', host: { class: 'si-layout-inner' }, template: "<div\n  class=\"focus-none\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  [listInteractionShowSubListOnParentFocus]=\"false\"\n  listInteractionFocus=\"first\"\n  #listInteraction=\"si-list-interaction\"\n>\n  <div\n    *ngIf=\"visible && !mobileExpanded\"\n    class=\"collapse-toggle mobile-drawer mobile navbar-vertical-no-collapse focus-inside\"\n  >\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl element-double-right navbar-vertical-no-collapse\"\n      (click)=\"toggleMobileExpand($event)\"\n      siListInteractionItem\n      [attr.aria-label]=\"navbarExpandButtonText | translate\"\n    >\n    </button>\n  </div>\n  <div\n    *ngIf=\"visible && textOnly && collapsed\"\n    class=\"collapse-toggle mobile-drawer desktop focus-inside\"\n  >\n    <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl element-double-right\"\n      (click)=\"toggleCollapse($event)\"\n      siListInteractionItem\n      [attr.aria-label]=\"navbarExpandButtonText | translate\"\n    >\n    </button>\n  </div>\n\n  <nav\n    *ngIf=\"visible\"\n    #nav\n    class=\"elevation-1 bg-base-1 focus-sub-inside\"\n    [class.text-only]=\"textOnly\"\n    (click)=\"handleClick($event)\"\n  >\n    <div\n      class=\"collapse-toggle desktop focus-inside\"\n    >\n      <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl\"\n        (click)=\"toggleCollapse($event)\"\n        siListInteractionItem\n        [listInteractionItemDisabled]=\"(isMobileSmall && !mobileExpanded) || (((isMobile && !mobileExpanded) || (!isMobile && collapsed)) && textOnly)\"\n        [class.element-double-left]=\"!collapsed\"\n        [class.element-double-right]=\"collapsed\"\n        [attr.aria-label]=\"(collapsed ? navbarExpandButtonText : navbarCollapseButtonText) | translate\"\n        [attr.aria-expanded]=\"!collapsed\">\n      </button>\n    </div>\n    <div class=\"collapse-toggle mobile navbar-vertical-no-collapse focus-inside\"\n    >\n      <button type=\"button\" class=\"btn btn-circle btn-sm btn-ghost flip-rtl navbar-vertical-no-collapse\"\n        [class.element-double-left]=\"mobileExpanded\"\n        [class.element-double-right]=\"!mobileExpanded\"\n        (click)=\"toggleMobileExpand($event)\"\n        siListInteractionItem\n        [listInteractionItemDisabled]=\"(isMobileSmall && !mobileExpanded) || (((isMobile && !mobileExpanded) || (!isMobile && collapsed)) && textOnly)\"\n        [attr.aria-label]=\"(mobileExpanded ? navbarCollapseButtonText : navbarExpandButtonText) | translate\"\n        [attr.aria-expanded]=\"mobileExpanded\"\n      >\n      </button>\n    </div>\n    <div *ngIf=\"searchable && (!isMobileSmall || mobileExpanded)\" class=\"nav-search py-4 mt-2\">\n      <si-search-bar\n        class=\"d-block mx-5\"\n        #searchBar\n        [placeholder]=\"searchPlaceholder | translate\"\n        [colorVariant]=\"'base-0'\"\n        (searchChange)=\"doSearch($event)\"\n        [debounceTime]=\"400\"\n        [showIcon]=\"true\"\n        [prohibitedCharacters]=\"'*?'\"\n        [tabbable]=\"false\"\n        siListInteractionItem\n      ></si-search-bar>\n      <button type=\"button\"\n        class=\"btn-search bg-base-0 p-2 mx-4 desktop navbar-vertical-no-collapse\"\n        [attr.aria-label]=\"searchPlaceholder | translate\"\n        (click)=\"toggleSearch($event, false)\"\n        siListInteractionItem\n      >\n        <i class=\"icon element-search\"></i>\n      </button>\n      <button type=\"button\"\n        class=\"btn-search bg-base-0 p-2 mx-4 mobile navbar-vertical-no-collapse\"\n        [attr.aria-label]=\"searchPlaceholder | translate\"\n        (click)=\"toggleSearch($event, true)\"\n        siListInteractionItem\n      >\n        <i class=\"icon element-search\"></i>\n      </button>\n    </div>\n    <div class=\"nav-scroll mt-2\" #scroller role=\"menu\">\n      <si-navbar-vertical-item\n        *ngFor=\"let item of items; trackBy: trackByIndex\"\n        class=\"mb-4\"\n        [item]=\"item\"\n        [textOnly]=\"textOnly\"\n        [scroller]=\"scroller\"\n        [collapsed]=\"isCollapsed\"\n        [collapseButtonText]=\"navbarCollapseButtonText\"\n        [expandButtonText]=\"navbarExpandButtonText\"\n        [navbar]=\"nav\"\n        (menuTrigger)=\"mobileCollapseTimer()\"\n      ></si-navbar-vertical-item>\n    </div>\n  </nav>\n</div>\n<main class=\"si-layout-inner\">\n  <ng-content></ng-content>\n</main>\n", styles: [":host{display:block}:host.ready{transition:padding-inline-start calc(.5s * var(--element-animations-enabled, 1))}@media (min-width: 992px){:host.visible{padding-inline-start:240px}:host.visible:not(.nav-text-only).nav-collapsed{padding-inline-start:48px}:host.visible:not(.nav-text-only).nav-collapsed nav{inline-size:48px}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item.expander,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .item-title,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .dropdown-caret,:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .badge{display:none}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .nav-item .badge-text{display:inline-block}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .section-item{display:none}:host.visible:not(.nav-text-only).nav-collapsed .nav-scroll ::ng-deep .menu-body{max-block-size:0!important}:host.visible:not(.nav-text-only).nav-collapsed si-search-bar{display:none!important}:host.visible:not(.nav-text-only).nav-collapsed .btn-search.desktop{display:block}:host.visible.nav-text-only.nav-collapsed{padding-inline-start:0!important}:host.visible.nav-text-only.nav-collapsed:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible.nav-text-only.nav-collapsed:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible.nav-text-only.nav-collapsed ::ng-deep .page-top-title{margin-inline-start:40px}:host.visible.nav-text-only.nav-collapsed .mobile-drawer.mobile{display:block}:host.visible .collapse-toggle.mobile{display:none!important}}@media (min-width: 576px) and (max-width: 991.98px){:host.visible:not(.nav-text-only){padding-inline-start:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded){padding-inline-start:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) nav{inline-size:48px}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item.expander,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .item-title,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .dropdown-caret,:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .badge{display:none}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .nav-item .badge-text{display:inline-block}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .section-item{display:none}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .nav-scroll ::ng-deep .menu-body{max-block-size:0!important}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) si-search-bar{display:none!important}:host.visible:not(.nav-text-only):not(.nav-mobile-expanded) .btn-search.mobile{display:block}:host.visible.nav-text-only{padding-inline-start:0!important}:host.visible.nav-text-only:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible.nav-text-only:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible.nav-text-only ::ng-deep .page-top-title{margin-inline-start:40px}.collapse-toggle.desktop{display:none!important}}@media (max-width: 575.98px){:host.visible{padding-inline-start:0!important}:host.visible:not(.nav-mobile-expanded) nav{inline-size:0}:host.visible:not(.nav-mobile-expanded) .collapse-toggle.mobile-drawer{display:block}:host.visible ::ng-deep .page-top-title{margin-inline-start:40px}:host.visible:after{content:\"\";position:fixed;inset-block:48px 0;inset-inline:0;background:transparent;pointer-events:none;transition:background calc(.5s * var(--element-animations-enabled, 1));z-index:2}:host.visible.nav-mobile-expanded:after{background:var(--element-base-translucent-1);pointer-events:auto}:host.visible .collapse-toggle.desktop{display:none}}nav{display:flex;flex-direction:column;position:fixed;z-index:1029;inset-block:48px 0;inset-inline-start:0;inline-size:240px;overflow-x:hidden;transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}.nav-scroll{overflow-x:hidden;min-block-size:0;flex:1 0 0;overflow-y:auto}.collapse-toggle{display:block;border:0;text-align:end;background:transparent;color:var(--element-text-primary);padding-block:4px;padding-inline:8px;margin-block-start:8px}.collapse-toggle.mobile-drawer{display:none;position:fixed;inset-inline-start:0;inset-block-start:48px;border-start-end-radius:var(--element-radius-2);border-end-end-radius:var(--element-radius-2);background:var(--element-base-1);box-shadow:0 0 4px var(--element-box-shadow-color-1),0 4px 4px var(--element-box-shadow-color-2);z-index:2}.collapse-toggle.mobile-drawer.desktop{display:none}.btn-search{display:none;border:0;border-radius:var(--element-radius-2)}\n"] }]
        }], propDecorators: { collapsed: [{
                type: HostBinding,
                args: ['class.nav-collapsed']
            }, {
                type: Input
            }], searchable: [{
                type: Input
            }], searchPlaceholder: [{
                type: Input
            }], items: [{
                type: Input
            }], itemsChange: [{
                type: Output
            }], textOnly: [{
                type: HostBinding,
                args: ['class.nav-text-only']
            }, {
                type: Input
            }], visible: [{
                type: HostBinding,
                args: ['class.visible']
            }, {
                type: Input
            }], autoCollapseDelay: [{
                type: Input
            }], navbarExpandButtonText: [{
                type: Input
            }], navbarCollapseButtonText: [{
                type: Input
            }], stateId: [{
                type: Input
            }], searchEvent: [{
                type: Output
            }], mobileExpanded: [{
                type: HostBinding,
                args: ['class.nav-mobile-expanded']
            }], ready: [{
                type: HostBinding,
                args: ['class.ready']
            }], searchBar: [{
                type: ViewChild,
                args: ['searchBar']
            }], searchBarElement: [{
                type: ViewChild,
                args: ['searchBar', { read: ElementRef }]
            }], listInteraction: [{
                type: ViewChild,
                args: ['listInteraction']
            }], onEnterKeydown: [{
                type: HostListener,
                args: ['keydown.enter', ['$event.target']]
            }] } });

class SiSearchBarModule {
}
SiSearchBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSearchBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarModule, declarations: [SiSearchBarComponent], imports: [CommonModule,
        ReactiveFormsModule], exports: [SiSearchBarComponent] });
SiSearchBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarModule, imports: [CommonModule,
        ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSearchBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ReactiveFormsModule
                    ],
                    declarations: [SiSearchBarComponent],
                    exports: [SiSearchBarComponent]
                }]
        }] });

class SiNavbarVerticalModule {
}
SiNavbarVerticalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiNavbarVerticalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalModule, declarations: [SiNavbarVerticalComponent,
        SiNavbarVerticalFlyoutComponent,
        SiNavbarVerticalItemComponent], imports: [CommonModule,
        SiLinkModule,
        SiListInteractionModule,
        SiMenuModule,
        SiSearchBarModule,
        SiTranslateModule], exports: [SiNavbarVerticalComponent] });
SiNavbarVerticalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalModule, imports: [CommonModule,
        SiLinkModule,
        SiListInteractionModule,
        SiMenuModule,
        SiSearchBarModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNavbarVerticalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiLinkModule,
                        SiListInteractionModule,
                        SiMenuModule,
                        SiSearchBarModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiNavbarVerticalComponent,
                        SiNavbarVerticalFlyoutComponent,
                        SiNavbarVerticalItemComponent
                    ],
                    exports: [
                        SiNavbarVerticalComponent
                    ]
                }]
        }] });

class SiNumberInputComponent {
    constructor() {
        /** The step size for HTML input */
        this.step = 1;
        /** Show increment/decrement buttons? */
        this.showButtons = true;
        this.valueChange = new EventEmitter();
        this.canInc = true;
        this.canDec = true;
        this._disabled = false;
        this._readonly = false;
        this._required = false;
        this.autoUpdate$ = timer(400, 80);
        this.onTouched = () => { };
        this.onChange = () => { };
    }
    get disabled() {
        return this._disabled;
    }
    /** @defaultref {@link _disabled} */
    set disabled(value) {
        this.setDisabledState(value !== false);
    }
    get readonly() {
        return this._readonly;
    }
    /** @defaultref {@link _readonly} */
    set readonly(value) {
        this._readonly = value !== false;
    }
    get required() {
        return this._required;
    }
    /** @defaultref {@link _required} */
    set required(value) {
        this._required = value !== false;
    }
    ngOnChanges(changes) {
        if (changes.value) {
            this.internalValue = this.value;
        }
        this.modelChanged(this.internalValue, false);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    writeValue(value) {
        this.internalValue = value;
    }
    validate(control) {
        const subject = new Subject();
        setTimeout(() => {
            subject.next(this.ngModel.control.valid ? null : this.ngModel.control.errors);
            subject.complete();
        });
        return subject;
    }
    modelChanged(value, emit = true) {
        this.onChange(value);
        if (emit) {
            this.valueChange.emit(value);
        }
        this.canInc = this.max == null || this.internalValue == null || this.internalValue + this.step <= this.max;
        this.canDec = this.min == null || this.internalValue == null || this.internalValue - this.step >= this.min;
    }
    autoUpdateStart(event, isIncrement) {
        const mouseButton = event.button;
        if (mouseButton) {
            return;
        }
        this.onTouched();
        event.preventDefault();
        const trigger = isIncrement
            ? () => this.increment()
            : () => this.decrement();
        this.autoUpdateSubs?.unsubscribe();
        this.autoUpdateSubs = this.autoUpdate$.subscribe(trigger);
        trigger();
    }
    autoUpdateStop() {
        this.autoUpdateSubs?.unsubscribe();
        this.autoUpdateSubs = undefined;
    }
    roundToStepPrecision(value) {
        const factor = 1 / this.step;
        if (factor > 1) {
            return Math.round(value * factor) / factor;
        }
        return Math.round(value / this.step) * this.step;
    }
    decrement() {
        this.internalValue = this.internalValue == null
            ? (this.max ?? this.min ?? 0)
            : this.roundToStepPrecision(this.internalValue - this.step);
        this.modelChanged(this.internalValue);
        if (!this.canDec) {
            this.autoUpdateStop();
        }
    }
    increment() {
        this.internalValue = this.internalValue == null
            ? (this.min ?? this.max ?? 0)
            : this.roundToStepPrecision(this.internalValue + this.step);
        this.modelChanged(this.internalValue);
        if (!this.canInc) {
            this.autoUpdateStop();
        }
    }
}
SiNumberInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiNumberInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiNumberInputComponent, selector: "si-number-input", inputs: { min: "min", max: "max", step: "step", value: "value", unit: "unit", showButtons: "showButtons", ariaLabel: ["aria-label", "ariaLabel"], inputId: "inputId", disabled: "disabled", readonly: "readonly", required: "required" }, outputs: { valueChange: "valueChange" }, host: { properties: { "class.disabled": "this._disabled", "class.readonly": "this._readonly" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiNumberInputComponent,
            multi: true
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: SiNumberInputComponent,
            multi: true
        }], viewQueries: [{ propertyName: "ngModel", first: true, predicate: ["ctrl"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<input\n  type=\"number\"\n  class=\"focus-none si-body-2\"\n  [attr.id]=\"inputId ?? null\"\n  [(ngModel)]=\"internalValue\"\n  [ngModelOptions]=\"{ standalone: true }\"\n  [min]=\"min ?? null\"\n  [max]=\"max ?? null\"\n  [step]=\"step ?? null\"\n  [required]=\"required\"\n  [readonly]=\"readonly\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel\"\n  #ctrl=\"ngModel\"\n  (blur)=\"onTouched()\"\n  (ngModelChange)=\"modelChanged($event)\"\n>\n<span\n  *ngIf=\"unit\"\n  class=\"unit ms-2\"\n  [class.text-secondary]=\"!disabled\"\n  [class.text-disabled]=\"disabled\"\n>{{ unit }}</span>\n<ng-container *ngIf=\"showButtons\">\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"dec btn btn-circle btn-tertiary btn-xs element-minus my-n3 ms-2\"\n    [disabled]=\"disabled || readonly || !canDec\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  > </button>\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"inc btn btn-circle btn-tertiary btn-xs element-plus my-n3 me-n2\"\n    [disabled]=\"disabled || readonly || !canInc\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  > </button>\n</ng-container>\n", styles: [":host{display:flex;align-items:center}:host:focus-within{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{-moz-appearance:textfield;border:0;margin:0;outline:none;background:transparent;color:inherit;text-align:inherit;flex:1 0 0;min-inline-size:0}input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.MinValidator, selector: "input[type=number][min][formControlName],input[type=number][min][formControl],input[type=number][min][ngModel]", inputs: ["min"] }, { kind: "directive", type: i2.MaxValidator, selector: "input[type=number][max][formControlName],input[type=number][max][formControl],input[type=number][max][ngModel]", inputs: ["max"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-number-input', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiNumberInputComponent,
                            multi: true
                        },
                        {
                            provide: NG_ASYNC_VALIDATORS,
                            useExisting: SiNumberInputComponent,
                            multi: true
                        }], changeDetection: ChangeDetectionStrategy.OnPush, template: "<input\n  type=\"number\"\n  class=\"focus-none si-body-2\"\n  [attr.id]=\"inputId ?? null\"\n  [(ngModel)]=\"internalValue\"\n  [ngModelOptions]=\"{ standalone: true }\"\n  [min]=\"min ?? null\"\n  [max]=\"max ?? null\"\n  [step]=\"step ?? null\"\n  [required]=\"required\"\n  [readonly]=\"readonly\"\n  [disabled]=\"disabled\"\n  [attr.aria-label]=\"ariaLabel\"\n  #ctrl=\"ngModel\"\n  (blur)=\"onTouched()\"\n  (ngModelChange)=\"modelChanged($event)\"\n>\n<span\n  *ngIf=\"unit\"\n  class=\"unit ms-2\"\n  [class.text-secondary]=\"!disabled\"\n  [class.text-disabled]=\"disabled\"\n>{{ unit }}</span>\n<ng-container *ngIf=\"showButtons\">\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"dec btn btn-circle btn-tertiary btn-xs element-minus my-n3 ms-2\"\n    [disabled]=\"disabled || readonly || !canDec\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  > </button>\n  <button\n    type=\"button\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    class=\"inc btn btn-circle btn-tertiary btn-xs element-plus my-n3 me-n2\"\n    [disabled]=\"disabled || readonly || !canInc\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n  > </button>\n</ng-container>\n", styles: [":host{display:flex;align-items:center}:host:focus-within{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}input{-moz-appearance:textfield;border:0;margin:0;outline:none;background:transparent;color:inherit;text-align:inherit;flex:1 0 0;min-inline-size:0}input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}\n"] }]
        }], propDecorators: { min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], value: [{
                type: Input
            }], unit: [{
                type: Input
            }], showButtons: [{
                type: Input
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], inputId: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], ngModel: [{
                type: ViewChild,
                args: ['ctrl', { static: true }]
            }], _disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }] } });

class SiNumberInputModule {
}
SiNumberInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiNumberInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputModule, declarations: [SiNumberInputComponent], imports: [CommonModule,
        FormsModule], exports: [SiNumberInputComponent] });
SiNumberInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputModule, imports: [CommonModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiNumberInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    declarations: [SiNumberInputComponent],
                    exports: [SiNumberInputComponent]
                }]
        }] });

/**
 * This enum defines the state that a detailed result step can have.
 */
var ResultDetailStepState;
(function (ResultDetailStepState) {
    ResultDetailStepState["Passed"] = "Passed";
    ResultDetailStepState["Failed"] = "Failed";
    ResultDetailStepState["Running"] = "Running";
    ResultDetailStepState["NotSupported"] = "Not supported";
    ResultDetailStepState["NotStarted"] = "Not started";
})(ResultDetailStepState || (ResultDetailStepState = {}));

class SiResultDetailsListComponent {
    constructor() {
        /**
         * Array of steps to show in the list.
         */
        this.steps = [];
        this.trackByIndex = (index) => index;
        this.resultDetailStepState = ResultDetailStepState;
    }
}
SiResultDetailsListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiResultDetailsListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiResultDetailsListComponent, selector: "si-result-details-list", inputs: { steps: "steps" }, ngImport: i0, template: "<ul class=\"list-unstyled d-flex flex-column mb-0\">\n  <li\n    *ngFor=\"let step of steps; trackBy: trackByIndex\"\n    class=\"position-relative d-flex align-items-center timeline px-6 py-5\"\n    [class.text-primary]=\"step.state === 'Running'\"\n  >\n    <si-loading-spinner *ngIf=\"step.state === 'Running'\" class=\"me-4\"></si-loading-spinner>\n    <si-icon\n      *ngIf=\"step.icon && step.state !== 'Running'\"\n      class=\"me-4\"\n      [icon]=\"step.icon\"\n    ></si-icon>\n    <ng-container *ngIf=\"!step.icon && step.state !== 'Running'\" [ngSwitch]=\"step.state\">\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.Passed\"\n        class=\"me-4\"\n        icon=\"element-circle-filled\"\n        color=\"status-success\"\n        stackedIcon=\"element-state-tick\"\n        stackedColor=\"status-critical-contrast\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.Failed\"\n        class=\"me-4\"\n        icon=\"element-circle-filled\"\n        color=\"status-danger\"\n        stackedIcon=\"element-state-exclamation-mark\"\n        stackedColor=\"status-critical-contrast\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.NotStarted\"\n        class=\"me-4\"\n        icon=\"element-not-checked\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchDefault\n        class=\"me-4\"\n        icon=\"element-out-of-service\"\n      ></si-icon>\n    </ng-container>\n    <div class=\"d-flex flex-column justify-content-center w-100\">\n      <div class=\"d-flex\">\n        <span class=\"result-description si-title-2\">{{ step.description | translate }}</span>\n        <span *ngIf=\"step.value\" class=\"result-value\">{{ step.value }}</span>\n      </div>\n      <span\n        *ngIf=\"step.errorMessage\"\n        class=\"si-title-2 text-danger\"\n      >{{ step.errorMessage | translate }}</span>\n    </div>\n  </li>\n</ul>\n", styles: ["si-loading-spinner{--loading-spinner-size: 1.5rem;--loading-spinner-color: var(--element-text-active)}.result-description{flex:2 1;align-self:center}.result-value{flex:1 1;white-space:nowrap;align-self:center}.timeline:not(:last-child):after,.timeline:not(:first-child):before{content:\"\";position:absolute;inline-size:1px;background-color:var(--element-ui-2);block-size:calc(50% - .75rem);margin-inline-start:calc(.75rem - .5px)}si-icon{color:var(--element-ui-2)}.timeline:not(:first-child):before{inset-block-start:0}.timeline:not(:last-child):after{inset-block-end:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i1.NgSwitchDefault, selector: "[ngSwitchDefault]" }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "component", type: SiLoadingSpinnerComponent, selector: "si-loading-spinner" }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-result-details-list', template: "<ul class=\"list-unstyled d-flex flex-column mb-0\">\n  <li\n    *ngFor=\"let step of steps; trackBy: trackByIndex\"\n    class=\"position-relative d-flex align-items-center timeline px-6 py-5\"\n    [class.text-primary]=\"step.state === 'Running'\"\n  >\n    <si-loading-spinner *ngIf=\"step.state === 'Running'\" class=\"me-4\"></si-loading-spinner>\n    <si-icon\n      *ngIf=\"step.icon && step.state !== 'Running'\"\n      class=\"me-4\"\n      [icon]=\"step.icon\"\n    ></si-icon>\n    <ng-container *ngIf=\"!step.icon && step.state !== 'Running'\" [ngSwitch]=\"step.state\">\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.Passed\"\n        class=\"me-4\"\n        icon=\"element-circle-filled\"\n        color=\"status-success\"\n        stackedIcon=\"element-state-tick\"\n        stackedColor=\"status-critical-contrast\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.Failed\"\n        class=\"me-4\"\n        icon=\"element-circle-filled\"\n        color=\"status-danger\"\n        stackedIcon=\"element-state-exclamation-mark\"\n        stackedColor=\"status-critical-contrast\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchCase=\"resultDetailStepState.NotStarted\"\n        class=\"me-4\"\n        icon=\"element-not-checked\"\n      ></si-icon>\n      <si-icon\n        *ngSwitchDefault\n        class=\"me-4\"\n        icon=\"element-out-of-service\"\n      ></si-icon>\n    </ng-container>\n    <div class=\"d-flex flex-column justify-content-center w-100\">\n      <div class=\"d-flex\">\n        <span class=\"result-description si-title-2\">{{ step.description | translate }}</span>\n        <span *ngIf=\"step.value\" class=\"result-value\">{{ step.value }}</span>\n      </div>\n      <span\n        *ngIf=\"step.errorMessage\"\n        class=\"si-title-2 text-danger\"\n      >{{ step.errorMessage | translate }}</span>\n    </div>\n  </li>\n</ul>\n", styles: ["si-loading-spinner{--loading-spinner-size: 1.5rem;--loading-spinner-color: var(--element-text-active)}.result-description{flex:2 1;align-self:center}.result-value{flex:1 1;white-space:nowrap;align-self:center}.timeline:not(:last-child):after,.timeline:not(:first-child):before{content:\"\";position:absolute;inline-size:1px;background-color:var(--element-ui-2);block-size:calc(50% - .75rem);margin-inline-start:calc(.75rem - .5px)}si-icon{color:var(--element-ui-2)}.timeline:not(:first-child):before{inset-block-start:0}.timeline:not(:last-child):after{inset-block-end:0}\n"] }]
        }], propDecorators: { steps: [{
                type: Input
            }] } });

class SiResultDetailsListModule {
}
SiResultDetailsListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiResultDetailsListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListModule, declarations: [SiResultDetailsListComponent], imports: [CommonModule,
        SiIconModule,
        SiLoadingSpinnerModule,
        SiTranslateModule], exports: [SiResultDetailsListComponent] });
SiResultDetailsListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListModule, imports: [CommonModule,
        SiIconModule,
        SiLoadingSpinnerModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiResultDetailsListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiLoadingSpinnerModule,
                        SiTranslateModule
                    ],
                    declarations: [SiResultDetailsListComponent],
                    exports: [SiResultDetailsListComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

/** Convert options to option criterions */
const toOptionCriteria = (values) => values.map(v => typeof v === 'string'
    ? { label: v, value: v, selected: false }
    : { label: v.label ?? v.value, value: v.value, selected: false });
/*
 * Update selected state the matching is based on value since plain
 * string options will automatically fill the value attribute with the
 * actual string value.
 */
const selectOptions = (options, toSelect) => options.forEach(val => val.selected = toSelect.includes(val.value));
/** Filter option criterions by label (case-insensitive) */
const filterByLabel = (options, searchLabel) => searchLabel ? options.filter(e => e.label.toLowerCase().includes(searchLabel.toLowerCase())) : options;
/** Whether the criteria's contain the label (case-insensitive) */
const hasCriteriaLabel = (criterias, searchLabel) => searchLabel ? criterias.some(c => c.label.toLowerCase().includes(searchLabel.toLowerCase())) : false;
/** Find criterion by name */
const findByName = (source, name) => source.find(item => name === item.name);
/**
 * Difference by name, create an array that contains those elements of criteria's a that are not in criteria's b.
 * This operation is also sometimes called minus (-).
 */
const differenceByName = (a, b) => a.filter(x => b.filter(y => y.name === x.name).length === 0); // filter has not
/** Whether Criteria is date or date-time */
const isDateTimeCriteria = (crit) => crit?.validationType === 'date' || crit?.validationType === 'date-time';
/** Convert criteria to internal model criteria */
const toCriteriaIntern = (crit) => {
    const result = {
        ...crit,
        label: crit.label ?? crit.name,
        options: crit.options ?? [],
        value: crit.value ?? ''
    };
    if (isDateTimeCriteria(result)) {
        result.dateValue = result.value ? new Date(result.value.toString()) : new Date();
    }
    return result;
};
/** Convert search criteria to internal model search criteria */
const toSearchCriteriaIntern = (crit, critConfig) => {
    const defaultValue = critConfig?.multiSelect ? [] : '';
    const result = {
        ...crit,
        label: crit.label ?? critConfig?.label ?? crit.name,
        options: crit.options ?? [],
        value: crit.value ?? defaultValue
    };
    // Fix input, in case the user provided the value as string for the multi-select use case.
    if (critConfig?.multiSelect && typeof result.value === 'string') {
        result.value = result.value !== '' ? [result.value] : [];
    }
    if (isDateTimeCriteria(critConfig)) {
        result.dateValue = crit.value ? new Date(crit.value.toString()) : new Date();
    }
    return result;
};

class ToLabelPipe {
    /**
     * It gets the label from options for the token (value) passed
     * @param options The options
     * @param token The token
     */
    static getLabel(options, token) {
        return ToLabelPipe.getFor(options, 'value', 'label', token);
    }
    /**
     * It gets the value from options for the token (label) passed
     * @param options The options
     * @param token The token
     */
    static getValue(options, token) {
        return ToLabelPipe.getFor(options, 'label', 'value', token);
    }
    static getFor(options, propertyToCompare, propertyToRetrieve, token) {
        if (!options || options.length === 0) {
            return token;
        }
        if (typeof token === 'string') {
            const option = options.find(op => typeof op === 'string' ? op === token : op[propertyToCompare] === token);
            if (option) {
                return typeof option === 'string' ? option : option[propertyToRetrieve];
            }
            return token;
        }
        else {
            const tokenArr = [];
            token?.forEach(tokenVal => {
                const option = options.find(op => typeof op === 'string' ? op === tokenVal : op[propertyToCompare] === tokenVal);
                if (option) {
                    tokenArr.push(typeof option === 'string' ? option : option[propertyToRetrieve]);
                }
                else {
                    tokenArr.push(tokenVal);
                }
            });
            return tokenArr;
        }
    }
    transform(token, options) {
        return ToLabelPipe.getLabel(options, token);
    }
}
ToLabelPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ToLabelPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ToLabelPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: ToLabelPipe, name: "toLabel" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ToLabelPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'toLabel'
                }]
        }] });

class SiTypeaheadComponent {
    constructor() {
        this.cdRef = inject(ChangeDetectorRef);
        this.matches = new BehaviorSubject([]);
        this.activeIndex = -1;
        this.matchesArray = [];
        this.query = '';
        this.multiselect = false;
        this.dropup = false;
        // The offset on the side based on the icon of the input field.
        this.iconOffset = 0;
        // Store the number of rendered matches to know if the height needs to be recalculated.
        this.renderedMatchesLength = 0;
        this.trackByIndex = (index) => index;
    }
    set typeaheadElement(element) {
        this._typeaheadElement = element;
        this.setHeight(element);
        this.renderedMatchesLength = this.matchesArray.length;
    }
    get typeaheadElement() {
        return this._typeaheadElement;
    }
    ngOnInit() {
        this.multiselect = !!this.parent.typeaheadMultiSelect;
        this.dropup = !!this.parent.typeaheadDropup;
        this.iconOffset = this.parent.iconOffset;
        // Subscribe to the matches and store them in an array, also reset the active index to prevent conflicts.
        this.subscription = this.matches.subscribe(matches => {
            if (this.activeIndex === -1 || this.activeIndex >= matches.length) {
                // If the first match should not be automatically selected, set the active index to -1, otherwise to 0.
                this.activeIndex = this.parent.typeaheadAutoSelectIndex ? this.parent.typeaheadAutoSelectIndex : 0;
            }
            this.matchesArray = this.parent.typeaheadOptionsLimit ? matches.slice(0, this.parent.typeaheadOptionsLimit) : matches.slice();
            if (this.dropup) {
                this.matchesArray.reverse();
            }
            this.cdRef.markForCheck();
        });
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    onMouseDown(event) {
        event.preventDefault();
    }
    /*
     * Set the height of the element passed to it (typeahead) if there are items displayed,
     * the number of displayed items changed and it is scrollable.
     */
    setHeight(element) {
        if (this.matchesArray.length !== 0 && this.matchesArray.length !== this.renderedMatchesLength) {
            if (this.parent.typeaheadScrollable && this.parent.typeaheadOptionsInScrollableView < this.matchesArray.length) {
                const computedStyle = getComputedStyle(element.nativeElement);
                const matchComputedStyle = getComputedStyle(element.nativeElement.firstElementChild);
                const matchHeight = parseFloat(matchComputedStyle.height || '0');
                const paddingTop = parseFloat(computedStyle.paddingTop || '0');
                const paddingBottom = parseFloat(computedStyle.paddingBottom || '');
                const height = this.parent.typeaheadOptionsInScrollableView * matchHeight;
                element.nativeElement.style.height = `${height + paddingTop + paddingBottom + this.parent.typeaheadScrollableAdditionalHeight}px`;
            }
            else {
                element.nativeElement.style.height = 'auto';
            }
        }
    }
    // Gets called when a match is selected by clicking on it.
    selectMatch(event, matchIndex) {
        this.parent.selectMatch(matchIndex);
        event.stopPropagation();
    }
    // Gets called by the parent when the typeahead is scrollable and the user is going through the matches using the arrow keys.
    scrollToMatch(index) {
        if (this.typeaheadMatchElements) {
            // Calculate if the active match is visible, if not scroll to it.
            const typeaheadElement = this.typeaheadElement.nativeElement;
            const matchElement = Array.from(this.typeaheadMatchElements)[index].nativeElement;
            const top = typeaheadElement.scrollTop;
            const bottom = top + typeaheadElement.offsetHeight;
            const matchTop = matchElement.offsetTop;
            const matchBottom = matchTop + matchElement.offsetHeight;
            if (matchBottom >= bottom) {
                const typeaheadMargin = parseFloat(getComputedStyle(typeaheadElement).marginTop);
                typeaheadElement.scrollTop =
                    matchElement.offsetTop - typeaheadElement.offsetHeight + typeaheadMargin + matchElement.offsetHeight;
            }
            else if (matchTop <= top) {
                typeaheadElement.scrollTop = matchElement.offsetTop;
            }
        }
    }
}
SiTypeaheadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTypeaheadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTypeaheadComponent, selector: "si-typeahead", host: { listeners: { "mousedown": "onMouseDown($event)" } }, viewQueries: [{ propertyName: "typeaheadElement", first: true, predicate: ["typeahead"], descendants: true }, { propertyName: "typeaheadMatchElements", predicate: ["typeaheadMatch"], descendants: true }], ngImport: i0, template: "<!-- Template to be used for every match, can be replaced using an input. -->\n<ng-template #defaultItemTemplate let-match=\"match\">\n  <input\n    *ngIf=\"multiselect\"\n    type=\"checkbox\"\n    [checked]=\"match.itemSelected\"\n    class=\"form-check-input me-5 mb-3\"\n    tabindex=\"-1\"\n  >\n  <span class=\"icon me-2\" [ngClass]=\"match.iconClass\" *ngIf=\"match.iconClass\"></span>\n  <span\n    *ngFor=\"let segment of match.result; trackBy: trackByIndex\"\n    [class.typeahead-match-segment-matching]=\"segment.isMatching\"\n  >{{ segment.text }}</span>\n</ng-template>\n\n<!-- Only display the component if there are any matches and set the CSS transform to properly position the typeahead -->\n<ul *ngIf=\"matchesArray.length !== 0\" class=\"typeahead dropdown-menu\" #typeahead [class.up]=\"dropup\" [style.margin-inline-start.px]=\"iconOffset\">\n  <!-- Loop through every match and bind events, the mousedown prevent default is to prevent the host from losing focus on click -->\n  <li\n    *ngFor=\"let match of matchesArray; let index = index; trackBy: trackByIndex\"\n    #typeaheadMatch\n    class=\"dropdown-item pe-4\"\n    [class.active]=\"activeIndex === (dropup ? matchesArray.length - index - 1 : index)\"\n    (click)=\"selectMatch($event, (dropup ? matchesArray.length - index - 1 : index))\"\n    (mousedown)=\"$event.preventDefault()\"\n    (mouseover)=\"activeIndex = (dropup ? matchesArray.length - index - 1 : index)\"\n  >\n    <!-- Display either a template set as the input or the template above -->\n    <ng-template [ngTemplateOutlet]=\"parent.typeaheadItemTemplate || defaultItemTemplate\"\n      [ngTemplateOutletContext]=\"{item: match.option, index: (dropup ? matchesArray.length - index - 1 : index), match: match, query: query}\">\n    </ng-template>\n  </li>\n</ul>\n", styles: [".dropdown-menu{display:block;position:relative;inset-block-start:0;inset-inline-start:0;margin-block-start:1px;overflow-y:auto;overflow-x:hidden;max-block-size:100%}.dropdown-menu.up{margin-block:0 2px}.typeahead-match-segment-matching{font-weight:700}.dropdown-item *{flex-shrink:0}.dropdown-item span{white-space:pre-wrap}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-typeahead', changeDetection: ChangeDetectionStrategy.OnPush, template: "<!-- Template to be used for every match, can be replaced using an input. -->\n<ng-template #defaultItemTemplate let-match=\"match\">\n  <input\n    *ngIf=\"multiselect\"\n    type=\"checkbox\"\n    [checked]=\"match.itemSelected\"\n    class=\"form-check-input me-5 mb-3\"\n    tabindex=\"-1\"\n  >\n  <span class=\"icon me-2\" [ngClass]=\"match.iconClass\" *ngIf=\"match.iconClass\"></span>\n  <span\n    *ngFor=\"let segment of match.result; trackBy: trackByIndex\"\n    [class.typeahead-match-segment-matching]=\"segment.isMatching\"\n  >{{ segment.text }}</span>\n</ng-template>\n\n<!-- Only display the component if there are any matches and set the CSS transform to properly position the typeahead -->\n<ul *ngIf=\"matchesArray.length !== 0\" class=\"typeahead dropdown-menu\" #typeahead [class.up]=\"dropup\" [style.margin-inline-start.px]=\"iconOffset\">\n  <!-- Loop through every match and bind events, the mousedown prevent default is to prevent the host from losing focus on click -->\n  <li\n    *ngFor=\"let match of matchesArray; let index = index; trackBy: trackByIndex\"\n    #typeaheadMatch\n    class=\"dropdown-item pe-4\"\n    [class.active]=\"activeIndex === (dropup ? matchesArray.length - index - 1 : index)\"\n    (click)=\"selectMatch($event, (dropup ? matchesArray.length - index - 1 : index))\"\n    (mousedown)=\"$event.preventDefault()\"\n    (mouseover)=\"activeIndex = (dropup ? matchesArray.length - index - 1 : index)\"\n  >\n    <!-- Display either a template set as the input or the template above -->\n    <ng-template [ngTemplateOutlet]=\"parent.typeaheadItemTemplate || defaultItemTemplate\"\n      [ngTemplateOutletContext]=\"{item: match.option, index: (dropup ? matchesArray.length - index - 1 : index), match: match, query: query}\">\n    </ng-template>\n  </li>\n</ul>\n", styles: [".dropdown-menu{display:block;position:relative;inset-block-start:0;inset-inline-start:0;margin-block-start:1px;overflow-y:auto;overflow-x:hidden;max-block-size:100%}.dropdown-menu.up{margin-block:0 2px}.typeahead-match-segment-matching{font-weight:700}.dropdown-item *{flex-shrink:0}.dropdown-item span{white-space:pre-wrap}\n"] }]
        }], propDecorators: { typeaheadElement: [{
                type: ViewChild,
                args: ['typeahead', { static: false }]
            }], typeaheadMatchElements: [{
                type: ViewChildren,
                args: ['typeaheadMatch']
            }], onMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

class SiTypeaheadSorting {
    sortMatches(matches) {
        // Sort the matches,
        // first is the option and query an exact match.
        // then according to whether it is matching in the beginning,
        // then whether it matches the entire untokenized query.
        // then according to how many unique separate matches it contains.
        // then according to how many unique matches it contains.
        // then according to how many matches it contains.
        return matches.sort((matchA, matchB) => {
            if (matchA.stringMatch || matchB.stringMatch) {
                return matchA.stringMatch ? -1 : 1;
            }
            if (matchA.atBeginning) {
                return !matchB.atBeginning
                    ? -1
                    : this.compareMatches(matchA, matchB);
            }
            else {
                return matchB.atBeginning
                    ? 1
                    : this.compareMatches(matchA, matchB);
            }
        });
    }
    compareMatchesNumbers(matchA, matchB) {
        return (matchB.uniqueSeparateMatches - matchA.uniqueSeparateMatches)
            || (matchB.uniqueMatches - matchA.uniqueMatches) || matchB.matches - matchA.matches;
    }
    compareMatches(matchA, matchB) {
        if (matchA.matchesEntireQuery) {
            return !matchB.matchesEntireQuery
                ? -1
                : this.compareMatchesNumbers(matchA, matchB);
        }
        else {
            return matchB.matchesEntireQuery
                ? 1
                : this.compareMatchesNumbers(matchA, matchB);
        }
    }
}

class SiTypeaheadDirective {
    constructor() {
        /**
         * Turns on/off the processing (searching and sorting) of the typeahead options.
         * Is used when searching and sorting is done externally.
         */
        this.typeaheadProcess = true;
        /**
         * Makes the typeahead scrollable and sets its height.
         * Uses {@link typeaheadOptionsInScrollableView} and {@link typeaheadScrollableAdditionalHeight}.
         */
        this.typeaheadScrollable = false;
        /**
         * If {@link typeaheadScrollable} is `true`, defines the number of items visible at once.
         */
        this.typeaheadOptionsInScrollableView = 10;
        /**
         * Defines the maximum number of items added into the DOM. Default is 20 and 0 means unlimited.
         */
        this.typeaheadOptionsLimit = 20;
        /**
         * If {@link typeaheadScrollable} is `true`, defines the number of additional pixels
         * to be added the the bottom of the typeahead to show users that it is scrollable.
         */
        this.typeaheadScrollableAdditionalHeight = 13;
        /**
         * Defines the index of the item which should automatically be selected.
         */
        this.typeaheadAutoSelectIndex = 0;
        /**
         * Defines whether the typeahead can be closed using escape.
         */
        this.typeaheadCloseOnEsc = true;
        /**
         * Defines whether the host value should be cleared when a value is selected.
         */
        this.typeaheadClearValueOnSelect = false;
        /**
         * Defines the number of milliseconds to wait before displaying a typeahead after the host was
         * focused or a value inputted.
         */
        this.typeaheadWaitMs = 0;
        /**
         * Defines the number of characters the value of the host needs to be before a typeahead is displayed.
         * Use `0` to have it display when focussing the host (clicking or tabbing into it).
         */
        this.typeaheadMinLength = 1;
        /**
         * Defines the name of the field/property the option string is in when the typeahead options are objects.
         */
        this.typeaheadOptionField = 'name';
        /**
         * Defines whether multiselection of typeahead is possible with checkboxes.
         */
        this.typeaheadMultiSelect = false;
        /**
         * Defines whether to tokenize the search or match the whole search.
         */
        this.typeaheadTokenize = true;
        /**
         * Defines whether and how to require to match with all the tokens if {@link typeaheadTokenize} is enabled.
         * - `no` does not require all of the tokens to match.
         * - `once` requires all of the parts to be found in the search.
         * - `separately` requires all of the parts to be found in the search where there is not an overlapping different result.
         * - `independently` requires all of the parts to be found in the search where there is not an overlapping or adjacent different result.
         *   ('independently' also slightly changes sorting behavior in the same way.)
         */
        this.typeaheadMatchAllTokens = 'separately';
        /**
         * Whether to display the typeahead upwards instead of downwards.
         */
        this.typeaheadDropup = false;
        /**
         * Skip the sorting of matches.
         * If the value is `true`, the matches are sorted according to {@link SiTypeaheadSorting}.
         */
        this.typeaheadSkipSortingMatches = false;
        /**
         * Emits an Event when the input field is changed.
         */
        this.typeaheadOnInput = new EventEmitter();
        /**
         * Emits an Event when a typeahead match is selected.
         * The event is a {@link TypeaheadMatch}
         */
        this.typeaheadOnSelect = new EventEmitter(true);
        /**
         * Emits an Event when a multiselect is enabled and closed using enter.
         */
        this.typeaheadOnMultiselectClose = new EventEmitter();
        /**
         * Emits an Event when a typeahead full match exists. A full match occurs when the entered text
         * is equal to one of the typeahead options.
         * The event is a {@link TypeaheadMatch}
         */
        this.typeaheadOnFullMatch = new EventEmitter(true);
        /**
         * Emits when the typeahead closed
         */
        this.typeaheadClosed = new EventEmitter(true);
        // The offset on the side based on the icon of the input field.
        this.iconOffset = 0;
        this.overlay = inject(Overlay);
        this.elementRef = inject(ElementRef);
        this.ngControl = inject(NgControl);
        this.cdRef = inject(ChangeDetectorRef);
        this.$typeahead = new ReplaySubject(1);
        this.isCurrentlyDropup = false;
        this.matchSorter = new SiTypeaheadSorting();
    }
    // Every time the main input changes, detect whether it is async and if it is not make an observable out of the array.
    ngOnChanges(changes) {
        if (changes.siTypeahead) {
            this.sourceSubscription?.unsubscribe();
            if (isObservable(this.siTypeahead)) {
                this.sourceSubscription = this.siTypeahead.subscribe(this.$typeahead);
            }
            else {
                this.$typeahead.next(this.siTypeahead);
            }
        }
    }
    // Clear the current input timeout (if set) and remove the component when the focus of the host is lost.
    onBlur() {
        this.clearTimer();
        if (this.component) {
            this.removeComponent();
        }
    }
    // Start the input timeout to display the typeahead when the host is focussed or a value is inputted into it.
    onInput(event) {
        const target = event.target;
        if (!target) {
            return;
        }
        // Get the value or otherwise textContent of the host element now, because later it could be reset.
        const firstValue = target.value || target.textContent;
        if (!this.inputTimer) {
            this.inputTimer = setTimeout(() => {
                this.inputTimer = undefined;
                const value = (target.value || target.textContent) ?? firstValue;
                // The value needs to fullfil the minimum length requirement set.
                if (value !== null && value.length >= this.typeaheadMinLength) {
                    if (this.component) {
                        this.setMatchesAndQuery(value, this.$typeahead, this.component);
                    }
                    else {
                        this.loadComponent(value);
                    }
                }
                else if (this.component) {
                    // If the value is too short, check if a typeahead still exists and remove it if it does.
                    this.removeComponent();
                }
                this.typeaheadOnInput.emit(value ?? '');
            }, this.typeaheadWaitMs);
        }
    }
    // Listen on the keydown event to navigate the typeahead using the keyboard.
    onKeydown(event) {
        // Only check the key pressed if the component exists and it displays any matches.
        if (!this.component || this.component.matchesArray.length === 0) {
            return;
        }
        // On escape clear the current input timeout (if set) and remove the typeahead if closing with escape is enabled.
        if (event.key === 'Escape' && this.typeaheadCloseOnEsc) {
            this.clearTimer();
            if (this.component) {
                event.stopPropagation();
                this.removeComponent();
            }
            return;
        }
        // If it's multiselect and open on enter prevent default (to prevent a form submit), emit a relevant event and close the dropdown.
        if (event.key === 'Enter' && this.typeaheadMultiSelect && this.component) {
            event.preventDefault();
            // Clear the current input timeout (if set) and remove the typeahead.
            this.clearTimer();
            this.typeaheadOnMultiselectClose.emit();
            this.removeComponent();
            return;
        }
        // If it's not multiselect on enter prevent default (to prevent a form submit) and select the active match.
        // Or if it's multiselect and space prevent default (to prevent a character input) and check the active match.
        if ((event.key === 'Enter' && !this.typeaheadMultiSelect)
            || (this.typeaheadMultiSelect && (event.key === ' ' || event.key === 'Space'))) {
            // Only intercept and select match if an actual match is active.
            if (this.component.activeIndex !== -1) {
                event.preventDefault();
                this.selectMatch(this.isCurrentlyDropup
                    ? this.component.matchesArray.length - this.component.activeIndex - 1
                    : this.component.activeIndex);
            }
            return;
        }
        // On arrow down go down one match or wrap around to the top, also if scrollable scroll to the now active match.
        if ((event.key === 'ArrowDown' && !this.isCurrentlyDropup) || (event.key === 'ArrowUp' && this.isCurrentlyDropup)) {
            if (this.component.activeIndex === this.component.matchesArray.length - 1) {
                this.component.activeIndex = 0;
            }
            else {
                this.component.activeIndex++;
            }
            if (this.typeaheadScrollable) {
                this.component.scrollToMatch(this.component.activeIndex);
            }
            event.preventDefault();
            this.component.cdRef.markForCheck();
            return;
        }
        // On arrow up go up one match or wrap around to the bottom, also if scrollable scroll to the now active match.
        if ((event.key === 'ArrowUp' && !this.isCurrentlyDropup) || (event.key === 'ArrowDown' && this.isCurrentlyDropup)) {
            // Don't go up when the active index is -1 and no match is selected.
            if (this.component.activeIndex === 0) {
                this.component.activeIndex = this.component.matchesArray.length - 1;
            }
            else if (this.component.activeIndex !== -1) {
                this.component.activeIndex--;
            }
            if (this.typeaheadScrollable) {
                this.component.scrollToMatch(this.component.activeIndex);
            }
            event.preventDefault();
            this.component.cdRef.markForCheck();
            return;
        }
    }
    ngOnDestroy() {
        this.clearTimer();
        this.sourceSubscription?.unsubscribe();
        this.subscription?.unsubscribe();
        this.overlayRef?.dispose();
    }
    // Dynamically create the typeahead component and then set the matches and the query.
    loadComponent(value) {
        if (!this.overlayRef?.hasAttached() || this.typeaheadDropup !== this.isCurrentlyDropup) {
            this.overlayRef?.dispose();
            const position = {
                overlayX: 'start',
                overlayY: this.typeaheadDropup ? 'bottom' : 'top',
                originX: 'start',
                originY: this.typeaheadDropup ? 'top' : 'bottom'
            };
            this.overlayRef = getOverlay(this.elementRef, this.overlay, false, [position], true, false);
        }
        this.isCurrentlyDropup = this.typeaheadDropup;
        if (this.overlayRef.hasAttached()) {
            return;
        }
        const typeaheadPortal = new ComponentPortal(SiTypeaheadComponent);
        this.componentRef = this.overlayRef.attach(typeaheadPortal);
        this.component = this.componentRef.instance;
        const input = this.elementRef.nativeElement.querySelector('input');
        this.iconOffset = input ? parseFloat(getComputedStyle(input).paddingLeft) : 0;
        // Set this as a variable for easy access from the component.
        this.component.parent = this;
        // Wait for change detection to prevent setting the matches before the component has subscribed to it.
        this.cdRef.detectChanges();
        this.setMatchesAndQuery(value, this.$typeahead, this.component);
    }
    // Get the matches and push them to the subject of the component, then set the query of the component.
    setMatchesAndQuery(query, items, component) {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = (this.getMatches(items, query).subscribe(matches => {
            component.matches.next(matches);
            const escapedQuery = this.escapeRegex(query);
            const equalsExp = new RegExp(`\^${escapedQuery}\$`, 'i');
            const fullMatches = matches.filter(match => match.result.length === 1 && equalsExp.test(match.text));
            if (fullMatches.length > 0) {
                this.typeaheadOnFullMatch.emit(fullMatches[0]);
            }
        }));
        component.query = query;
        this.component?.cdRef.markForCheck();
    }
    // If the typeahead options are objects, pick the specified field/property.
    getOptionValue(option, field) {
        return typeof option !== 'object' ? option.toString() : (option[field] || '');
    }
    // If enabled, process the matches and sort through them.
    getMatches(observableList, query) {
        try {
            const entireQueryRegex = new RegExp(this.escapeRegex(query), 'gi');
            const queryParts = this.typeaheadTokenize ? query.split(/\s+/g).filter(queryPart => queryPart) : query ? [query] : [];
            const queryRegexes = queryParts.map(queryPart => new RegExp(this.escapeRegex(queryPart), 'gi'));
            return observableList.pipe(map(options => {
                // Check if the options need to be processed, if not just return an unprocessed object.
                if (!this.typeaheadProcess) {
                    return options.map(option => {
                        const optionValue = this.getOptionValue(option, this.typeaheadOptionField);
                        const itemSelected = this.typeaheadMultiSelect ? this.getOptionValue(option, 'selected') : false;
                        const iconClass = this.getOptionValue(option, 'iconClass');
                        return {
                            option,
                            itemSelected,
                            text: optionValue,
                            iconClass,
                            result: optionValue ? [{ text: optionValue, isMatching: false, matches: 0, uniqueMatches: 0 }] : [],
                            stringMatch: false,
                            atBeginning: false,
                            matches: 0,
                            uniqueMatches: 0,
                            uniqueSeparateMatches: 0,
                            matchesEntireQuery: false,
                            matchesAllParts: false,
                            matchesAllPartsSeparately: false,
                            active: false
                        };
                    });
                }
                else {
                    // Process the options.
                    const matches = [];
                    options.forEach(option => {
                        const optionValue = this.getOptionValue(option, this.typeaheadOptionField);
                        const stringMatch = optionValue.toLocaleLowerCase().trim() === query.toLocaleLowerCase().trim();
                        const itemSelected = this.typeaheadMultiSelect ? option['selected'] : false;
                        const iconClass = this.getOptionValue(option, 'iconClass');
                        const candidate = {
                            option,
                            itemSelected,
                            text: optionValue,
                            iconClass,
                            result: [],
                            stringMatch,
                            atBeginning: false,
                            matches: 0,
                            uniqueMatches: 0,
                            uniqueSeparateMatches: 0,
                            matchesEntireQuery: false,
                            matchesAllParts: false,
                            matchesAllPartsSeparately: false
                        };
                        // Only search the options if a part of the query is at least one character long to prevent an endless loop.
                        if (queryParts.length === 0) {
                            if (optionValue) {
                                candidate.result.push({ text: optionValue, isMatching: false, matches: 0, uniqueMatches: 0 });
                            }
                            matches.push(candidate);
                        }
                        else {
                            const allResults = [];
                            const allIndexes = [];
                            candidate.matchesEntireQuery = !!optionValue.match(entireQueryRegex);
                            // Loop through the option value to find multiple matches, then store every segment (matching or non-matching) in the results.
                            queryRegexes.forEach((queryRegex, index) => {
                                let regexMatch = queryRegex.exec(optionValue);
                                while (regexMatch) {
                                    allResults.push({
                                        index,
                                        start: regexMatch.index,
                                        end: regexMatch.index + regexMatch[0].length,
                                        result: regexMatch[0]
                                    });
                                    if (!regexMatch.index) {
                                        candidate.atBeginning = true;
                                    }
                                    if (!allIndexes.includes(index)) {
                                        allIndexes.push(index);
                                    }
                                    regexMatch = queryRegex.exec(optionValue);
                                }
                            });
                            candidate.matchesAllParts = allIndexes.length === queryParts.length;
                            // Check if all parts of the query match at least once (if required).
                            if (this.typeaheadMatchAllTokens === 'no' || candidate.matchesAllParts) {
                                const combinedResults = [];
                                // First combine intersecting (or if set to independently adjacent) results to combined results.
                                // We achieve this by first sorting them by the starting index, then by the ending index and then looking for overlaps.
                                allResults.sort((a, b) => (a.start - b.start) || a.end - b.end)
                                    .forEach(result => {
                                    if (combinedResults.length) {
                                        const foundPreviousResult = combinedResults.find(previousResult => this.typeaheadMatchAllTokens === 'independently'
                                            ? result.start <= previousResult.end
                                            : result.start < previousResult.end);
                                        if (foundPreviousResult) {
                                            foundPreviousResult.result +=
                                                result.result.slice(foundPreviousResult.end - result.start, result.result.length);
                                            if (result.end > foundPreviousResult.end) {
                                                foundPreviousResult.end = result.end;
                                            }
                                            foundPreviousResult.indexes.push(result.index);
                                            if (!foundPreviousResult.uniqueIndexes.includes(result.index)) {
                                                foundPreviousResult.uniqueIndexes.push(result.index);
                                            }
                                            return;
                                        }
                                    }
                                    combinedResults.push({ ...result, indexes: [result.index], uniqueIndexes: [result.index] });
                                });
                                // Recursively go through all unique combinations of the unique indexes to get the option which has the most indexes.
                                const countUniqueSubindexes = (indexIndex = 0, previousIndexes = []) => indexIndex === combinedResults.length
                                    ? previousIndexes.length
                                    : Math.max(previousIndexes.length, ...combinedResults[indexIndex].uniqueIndexes
                                        .filter(index => !previousIndexes.includes(index))
                                        .map(index => countUniqueSubindexes(indexIndex + 1, [index, ...previousIndexes])));
                                candidate.uniqueSeparateMatches = countUniqueSubindexes();
                                candidate.matchesAllPartsSeparately = candidate.uniqueSeparateMatches === queryParts.length;
                                let currentPreviousEnd = 0;
                                // Add the combined results to the candidate including the non-matching parts in between.
                                combinedResults.forEach(result => {
                                    const textBefore = optionValue.slice(currentPreviousEnd, result.start);
                                    if (textBefore) {
                                        candidate.result.push({ text: textBefore, isMatching: false,
                                            matches: 0, uniqueMatches: 0 });
                                    }
                                    candidate.result.push({ text: result.result, isMatching: true,
                                        matches: result.indexes.length, uniqueMatches: result.uniqueIndexes.length });
                                    currentPreviousEnd = result.end;
                                    candidate.matches += result.indexes.length;
                                    candidate.uniqueMatches += result.uniqueIndexes.length;
                                });
                                // Check if there are result segments and all parts are matched independently (if required).
                                if (candidate.result.length !== 0
                                    && ((this.typeaheadMatchAllTokens !== 'separately' && this.typeaheadMatchAllTokens !== 'independently')
                                        || candidate.matchesAllPartsSeparately)) {
                                    const textAtEnd = optionValue.slice(currentPreviousEnd);
                                    if (textAtEnd) {
                                        candidate.result.push({ text: textAtEnd, isMatching: false,
                                            matches: 0, uniqueMatches: 0 });
                                    }
                                    matches.push(candidate);
                                }
                            }
                        }
                    });
                    if (this.typeaheadSkipSortingMatches) {
                        return matches;
                    }
                    else {
                        return this.matchSorter.sortMatches(matches);
                    }
                }
            }));
        }
        catch {
            // Could not create regex (only in extremely rare cases, maybe even impossible), so return an empty array.
            return of([]);
        }
    }
    // Select a match, either gets called due to a enter keypress or from the component due to a click.
    selectMatch(matchIndex) {
        const match = this.component.matchesArray[matchIndex];
        const value = this.typeaheadClearValueOnSelect ? '' : match.text;
        match.itemSelected = !match.itemSelected;
        if (!this.typeaheadMultiSelect) {
            this.ngControl.control.setValue(value);
        }
        // Clear the current input timeout (if set) and remove the typeahead.
        this.clearTimer();
        if (this.component) {
            this.typeaheadOnSelect.emit(match);
            if (!this.typeaheadMultiSelect) {
                this.removeComponent();
            }
        }
    }
    // Remove the component by clearing the viewContainerRef
    removeComponent() {
        this.typeaheadClosed.emit();
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef?.detach();
        }
        this.componentRef?.destroy();
        this.componentRef = undefined;
        this.component = undefined;
    }
    clearTimer() {
        if (this.inputTimer) {
            clearTimeout(this.inputTimer);
            this.inputTimer = undefined;
        }
    }
    escapeRegex(query) {
        return query.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    }
}
SiTypeaheadDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiTypeaheadDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiTypeaheadDirective, selector: "[siTypeahead]", inputs: { siTypeahead: "siTypeahead", typeaheadProcess: "typeaheadProcess", typeaheadScrollable: "typeaheadScrollable", typeaheadOptionsInScrollableView: "typeaheadOptionsInScrollableView", typeaheadOptionsLimit: "typeaheadOptionsLimit", typeaheadScrollableAdditionalHeight: "typeaheadScrollableAdditionalHeight", typeaheadAutoSelectIndex: "typeaheadAutoSelectIndex", typeaheadCloseOnEsc: "typeaheadCloseOnEsc", typeaheadClearValueOnSelect: "typeaheadClearValueOnSelect", typeaheadWaitMs: "typeaheadWaitMs", typeaheadMinLength: "typeaheadMinLength", typeaheadOptionField: "typeaheadOptionField", typeaheadMultiSelect: "typeaheadMultiSelect", typeaheadTokenize: "typeaheadTokenize", typeaheadMatchAllTokens: "typeaheadMatchAllTokens", typeaheadItemTemplate: "typeaheadItemTemplate", typeaheadDropup: "typeaheadDropup", typeaheadSkipSortingMatches: "typeaheadSkipSortingMatches" }, outputs: { typeaheadOnInput: "typeaheadOnInput", typeaheadOnSelect: "typeaheadOnSelect", typeaheadOnMultiselectClose: "typeaheadOnMultiselectClose", typeaheadOnFullMatch: "typeaheadOnFullMatch", typeaheadClosed: "typeaheadClosed" }, host: { listeners: { "focusout": "onBlur()", "focusin": "onInput($event)", "input": "onInput($event)", "paste": "onInput($event)", "keydown": "onKeydown($event)" } }, exportAs: ["si-typeahead"], usesOnChanges: true, ngImport: i0 });
__decorate([
    Required()
], SiTypeaheadDirective.prototype, "siTypeahead", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siTypeahead]',
                    exportAs: 'si-typeahead'
                }]
        }], propDecorators: { siTypeahead: [{
                type: Input
            }], typeaheadProcess: [{
                type: Input
            }], typeaheadScrollable: [{
                type: Input
            }], typeaheadOptionsInScrollableView: [{
                type: Input
            }], typeaheadOptionsLimit: [{
                type: Input
            }], typeaheadScrollableAdditionalHeight: [{
                type: Input
            }], typeaheadAutoSelectIndex: [{
                type: Input
            }], typeaheadCloseOnEsc: [{
                type: Input
            }], typeaheadClearValueOnSelect: [{
                type: Input
            }], typeaheadWaitMs: [{
                type: Input
            }], typeaheadMinLength: [{
                type: Input
            }], typeaheadOptionField: [{
                type: Input
            }], typeaheadMultiSelect: [{
                type: Input
            }], typeaheadTokenize: [{
                type: Input
            }], typeaheadMatchAllTokens: [{
                type: Input
            }], typeaheadItemTemplate: [{
                type: Input
            }], typeaheadDropup: [{
                type: Input
            }], typeaheadSkipSortingMatches: [{
                type: Input
            }], typeaheadOnInput: [{
                type: Output
            }], typeaheadOnSelect: [{
                type: Output
            }], typeaheadOnMultiselectClose: [{
                type: Output
            }], typeaheadOnFullMatch: [{
                type: Output
            }], typeaheadClosed: [{
                type: Output
            }], onBlur: [{
                type: HostListener,
                args: ['focusout']
            }], onInput: [{
                type: HostListener,
                args: ['focusin', ['$event']]
            }, {
                type: HostListener,
                args: ['input', ['$event']]
            }, {
                type: HostListener,
                args: ['paste', ['$event']]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const FOCUS_INDEX_FREE_TEXT_INPUT_FIELD = -1;
const FOCUS_INDEX_OUT_OF_COMPONENT = -2;
class SiFilteredSearchComponent {
    constructor() {
        /**
         * Output callback event that provides an object describing the
         * selected criteria and additional filter text.
         */
        this.doSearch = new EventEmitter();
        /**
         * If this is set to `true`, the search triggers for each input.
         * By default this is only done on 'click' and 'enter'.
         * This disables the search on 'click' and 'enter'.
         */
        this.doSearchOnInputChange = false;
        /**
         * Disable any interactivity.
         */
        this.disabled = false;
        /**
         * Do not allow changes. Search can still be triggered.
         */
        this.readonly = false;
        /**
         * Limit criteria to the predefined ones.
         */
        this.strictCriterion = false;
        /**
         * Limit criterion options to the predefined ones. `[strictValue]`
         * enforces `[strictCriterion]` to true automatically.
         */
        this.strictValue = false;
        /**
         * Limit criterion options to the predefined ones and prevent typing. `[onlySelectValue]`
         * enforces `[strictValue]` and `[strictCriterion]` to true automatically.
         */
        this.onlySelectValue = false;
        /**
         * Custom debounce time for lazy loading of criteria data.
         */
        this.lazyLoadingDebounceTime = 500;
        /**
         * Custom debounce time (in mills) to delay the search emission.
         * (Default is 0 as in most cases a users manually triggers a search.
         * Recommended to increase a bit when using doSearchOnInputChange=true)
         */
        this.searchDebounceTime = 0;
        /**
         * The placeholder for input field.
         */
        this.placeholder = '';
        /**
         * Show a search icon in input field.
         */
        this.showIcon = false;
        /**
         * Custom 'submit' text.
         */
        this.submitText = 'Press Enter or Click to Search';
        /**
         * Index of the criteria in dropdown which should be selected initially.
         */
        this.selectedCriteriaIndex = 0;
        /**
         * Defines the number of criterions or criteria values or operators visible at once.
         */
        this.optionsInScrollableView = 10;
        /**
         * Current criteria to be searched.
         * @defaultref {@link _searchCriteria}
         */
        this.searchCriteria = { criteria: [], value: '' };
        /**
         * Predefine criteria options.
         */
        this.criteria = [];
        /**
         * Opt-in to search for each criterion only once.
         */
        this.exclusiveCriteria = false;
        /**
         * Limit the number of possible criteria. The default is undefined so that any number of criteria can be used.
         * For example, setting the value to 1 let you only select one criterion that you need to remove before being
         * able to set another one.
         */
        this.maxCriteria = undefined;
        /**
         * Defines the maximum options within one criteria. The default is 20 and 0 means unlimited.
         */
        this.maxCriteriaOptions = 20;
        /**
         * Search input aria label, Needed by a11y
         */
        this.searchLabel = 'search';
        /**
         * Clear button aria label. Needed for a11y
         */
        this.clearButtonLabel = 'clear';
        /**
         * Items count text appended to the count in case of multiselection of values.
         * Translation key, `{{itemCount}}` in the translation will be replaced with the actual value
         */
        this.itemCountText = '';
        /**
         * Color variant to determine component background
         */
        this.colorVariant = 'base-1';
        /**
         * Text or translate key for multi selection pills text.
         */
        this.items = 'items';
        /**
         * Disables the free text search to only use the criterion for filtering.
         */
        this.disableFreeTextSearch = false;
        /**
         * Text for first entry in dropdown in case that no criteria
         * match the text input and the property `disableFreeTextSearch`
         * is set to `true`.
         */
        this.noMatchingCriteriaText = 'No matching criteria';
        this.editCriterionIndex = FOCUS_INDEX_FREE_TEXT_INPUT_FIELD;
        /** Internal representation of the input search criteria */
        this.internSearchCriteria = {
            criteria: [],
            value: ''
        };
        /** Internal criteria model */
        this.internCriteria = [];
        this.integerInvalidChar = [',', '.'];
        this.debouncedCriterionQueueSubject = new Subject();
        /** Used to debounce while fetching for criteria data */
        this.debouncedValueSubject = new Subject();
        /** Used to trigger a renewed search */
        this.typeaheadInputChange = new Subject();
        this.lastTypeaheadInput = '';
        /** Used to debounce the Search emissions */
        this.searchEmitQueue = new Subject();
        this.destroySubscriptions = new Subject();
        this.lazyLoadedValue = new Subject();
        this.elementRef = inject(ElementRef);
        this.locale = inject(LOCALE_ID);
        this.cdRef = inject(ChangeDetectorRef);
        this.invalidDateTime = false;
        this.focusState = false;
        this.trackByIndex = (index) => index;
        this.isReadOnly = () => this.readonly || this.disabled;
        this.shortDateFormat = getNamedFormat(this.locale, 'shortDate');
        if (!this.shortDateFormat.includes('yyyy')) {
            this.shortDateFormat = this.shortDateFormat.replace('yy', 'yyyy');
        }
        this.dataSource = new Observable((observer) => {
            if (this.lazyCriterionProvider) {
                this.debouncedCriterionQueueSubject.next(observer);
            }
            else {
                observer.next(this.getCriteriaToDisplay(''));
            }
            this.typeaheadInputChange.subscribe(value => {
                if (this.lazyCriterionProvider) {
                    this.debouncedCriterionQueueSubject.next(observer);
                }
                else {
                    observer.next(this.getCriteriaToDisplay(value));
                }
            });
        });
    }
    ngOnChanges(changes) {
        if (changes.searchCriteria || changes.criteria) {
            this.initCriteria();
            this.initSearchCriteria();
        }
    }
    ngOnInit() {
        if (this.onlySelectValue) {
            this.strictValue = true;
        }
        if (this.strictValue) {
            this.strictCriterion = true;
        }
        if (this.strictCriterion && this.internCriteria.length === 0) {
            throw new Error('strict criterion mode activated without predefined criteria!');
        }
        this.setupCriteriaFetchQueue();
        this.searchEmitQueue.pipe(debounceTime(this.searchDebounceTime), takeUntil(this.destroySubscriptions)).subscribe(searchCriteria => this.doSearch.emit(searchCriteria));
        if (this.elementRef.nativeElement.attributes.disabled) {
            this.disabled = true;
        }
    }
    ngOnDestroy() {
        this.destroySubscriptions.next(true);
        this.destroySubscriptions.unsubscribe();
    }
    /** Initialize criteria */
    initCriteria() {
        this.criteria = this.criteria ?? [];
        this.internCriteria = this.criteria.map(c => toCriteriaIntern(c));
    }
    /** Initialize internal search criteria */
    initSearchCriteria() {
        this.searchCriteria = this.searchCriteria ?? [];
        this.internSearchCriteria = {
            criteria: this.searchCriteria?.criteria.map(c => toSearchCriteriaIntern(c, this.getCriterionByName(c.name))),
            value: this.searchCriteria?.value ?? ''
        };
    }
    /**
     * It sets the value for searchCriteria for free text and patch the form control accordingly
     * @param newValue the new value
     */
    setSearchCriteriaValue(newValue) {
        this.internSearchCriteria.value = newValue;
    }
    getCriteriaToDisplay(value) {
        const filtered = this.getFilteredTypeaheadCriteria(value);
        const topItems = (this.disableFreeTextSearch && filtered.length === 0) || !this.doSearchOnInputChange ? [{
                name: '',
                value: '',
                label: this.disableFreeTextSearch && filtered.length === 0 ? this.noMatchingCriteriaText : this.submitText
            }] : [];
        return [...topItems, ...filtered];
    }
    criterionInputKeyUp(event) {
        let newValue = event.target.value;
        if (event.key === SiFilteredSearchComponent.keys.enter) {
            newValue = this.previousEditingValue;
        }
        else if (event.key === SiFilteredSearchComponent.keys.backspace) {
            this.handleInputBackspace();
        }
        else if (event.key === SiFilteredSearchComponent.keys.colon && !this.onlySelectValue) {
            this.handleInputColon();
            // emit clear, when doSearchOnInputChange is wished or value is empty
            if (this.doSearchOnInputChange || !this.internSearchCriteria.value) {
                this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
            }
        }
        else if (this.doSearchOnInputChange) {
            // trigger search if value doesn't include criterion.label
            const labelIncluded = hasCriteriaLabel(this.internCriteria, newValue);
            if (labelIncluded) {
                this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
            }
        }
        this.previousEditingValue = newValue;
    }
    handleContextMenuPaste(event) {
        if (!this.disableFreeTextSearch) {
            const clipboardData = event.clipboardData;
            this.previousEditingValue = clipboardData?.getData('Text') ?? '';
        }
    }
    deleteAllCriteria(event) {
        if (this.isReadOnly()) {
            return;
        }
        event?.stopPropagation();
        // Reset search criteria
        this.internSearchCriteria = { criteria: [], value: '' };
        this.searchCriteria = { criteria: [], value: '' };
        this.doSearch.emit(this.internSearchCriteria);
    }
    deleteCriterion(event, index) {
        if (this.isReadOnly()) {
            return;
        }
        if (event) {
            event.stopPropagation();
            this.editCriterionByIndex(FOCUS_INDEX_FREE_TEXT_INPUT_FIELD);
        }
        this.searchCriteria.criteria.splice(index, 1);
        this.internSearchCriteria.criteria.splice(index, 1);
        this.doSearch.emit(this.convertToExternalModel(this.internSearchCriteria));
    }
    editCriterion(criterion) {
        if (this.isReadOnly()) {
            return;
        }
        const index = this.internSearchCriteria.criteria.indexOf(criterion);
        const criterionOptions = this.getCriterionByName(criterion.name);
        this.editCriterionByIndex(index, (criterionOptions?.operators?.length ?? 0) > 0);
    }
    editOperator(criterion, event) {
        if (this.isReadOnly()) {
            return;
        }
        event.stopPropagation();
        const index = this.internSearchCriteria.criteria.indexOf(criterion);
        this.editCriterionByIndex(index, true);
    }
    editValue(criterion, event) {
        if (this.isReadOnly()) {
            return;
        }
        event.stopPropagation();
        const index = this.internSearchCriteria.criteria.indexOf(criterion);
        this.editCriterionByIndex(index);
    }
    getCriterionOptions(name, index, typed) {
        return new Observable((observer) => {
            if (!typed) {
                typed = '';
            }
            const existingSearchCriteria = this.internSearchCriteria.criteria[index];
            if (this.lazyValueProvider) {
                const lazyValRequest = {
                    criterionName: name,
                    typed
                };
                // Initiate call to app with typed string and selected criteria
                this.debouncedValueSubject.next(lazyValRequest);
                // Listen to subscription when app sends back server data
                this.lazyLoadedValue.pipe(takeUntil(this.destroySubscriptions)).subscribe(result => {
                    const options = this.filterOptionCriterias(name, existingSearchCriteria.value, typed?.toString(), result);
                    observer.next(options);
                });
            }
            else {
                const criterionOptions = this.getCriterionByName(name);
                const selected = existingSearchCriteria.value;
                const options = this.filterOptionCriterias(name, selected, typed?.toString(), criterionOptions?.options);
                observer.next(options);
            }
        });
    }
    /** Create a filtered criterions option list based on the search label */
    filterOptionCriterias(optionName, selected, searchLabel, options) {
        if (!options) {
            return [];
        }
        else {
            const optionCriterias = toOptionCriteria(options);
            if (this.isMultiSelect(optionName)) {
                selectOptions(optionCriterias, selected);
            }
            return filterByLabel(optionCriterias, searchLabel?.toString().toLowerCase());
        }
    }
    getCriterionOperators(name, typed) {
        return new Observable((observer) => {
            const criterionOptions = this.getCriterionByName(name);
            if (!criterionOptions?.operators) {
                observer.next([]);
            }
            else if (typed && typed !== this.previousEditingValue) {
                observer.next(criterionOptions.operators.filter(operator => operator.includes(typed)));
            }
            else {
                observer.next(criterionOptions.operators);
            }
        });
    }
    getCriterionOnlySelectValue(name) {
        const criterion = this.getCriterionByName(name);
        return criterion ? criterion.onlySelectValue && !criterion.multiSelect : false;
    }
    getSelectedOperatorIndex(name, operator) {
        const criterionOptions = this.getCriterionByName(name);
        if (!operator || !criterionOptions || !criterionOptions.operators) {
            return -1;
        }
        else if (operator && operator !== this.previousEditingValue) {
            return criterionOptions.operators.filter(op => op.includes(operator)).indexOf(operator);
        }
        return criterionOptions.operators.indexOf(operator);
    }
    getLongestOperatorLength(name) {
        const criterionOptions = this.getCriterionByName(name);
        if (!criterionOptions?.operators) {
            return 0;
        }
        return Math.max(...(criterionOptions.operators.map(a => a.length)));
    }
    getValueInputForCriterion(index, editOperator = false) {
        if (index === FOCUS_INDEX_FREE_TEXT_INPUT_FIELD) {
            return this.elementRef.nativeElement.getElementsByClassName('value-input')[0];
        }
        else if (editOperator) {
            return this.elementRef.nativeElement.getElementsByClassName('operator-input-' + index)[0];
        }
        else {
            return this.elementRef.nativeElement.getElementsByClassName('value-input-' + index)[0];
        }
    }
    highlight(criterion) {
        let label = criterion.label;
        const lcValue = this.internSearchCriteria.value ? this.internSearchCriteria.value.toLowerCase() : '';
        if (criterion.name === '' || !lcValue || lcValue.length === 0) {
            return label;
        }
        let lcLabel = label.toLowerCase();
        let idx;
        // eslint-disable-next-line no-cond-assign
        while ((idx = lcLabel.lastIndexOf(lcValue)) >= 0) {
            // eslint-disable-next-line max-len
            label = `${label.substring(0, idx)}<strong>${label.substring(idx, idx + lcValue.length)}</strong>${label.substring(idx + lcValue.length)}`;
            lcLabel = lcLabel.substring(0, idx);
        }
        return label;
    }
    inputFocused(event, index = FOCUS_INDEX_FREE_TEXT_INPUT_FIELD) {
        this.editCriterionIndex = index;
        this.previousEditingValue = event.target.value;
        this.focusState = true;
    }
    isOperatorSpecified(criterionName) {
        const criterionOptions = this.getCriterionByName(criterionName);
        return ((criterionOptions?.operators?.length ?? 0) > 0);
    }
    /** selectValue is invoked by select boxes in strict value mode. If the user selects an entry, the criterion is deselected. */
    selectValue() {
        this.editCriterionByIndex(FOCUS_INDEX_FREE_TEXT_INPUT_FIELD);
    }
    typeaheadOnSelectCriterion(event) {
        const criterion = event;
        if (criterion.label === this.noMatchingCriteriaText) {
            return;
        }
        else if (!criterion.name || criterion.name.trim().length === 0) {
            this.internSearchCriteria.value = this.previousEditingValue;
            this.setSearchCriteriaValue(this.previousEditingValue);
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
            return;
        }
        const editor = this.getValueInputForCriterion(this.editCriterionIndex);
        const endIndex = editor.selectionStart;
        const value = criterion.name;
        this.addCriterion({
            name: value,
            label: criterion.label,
            value: ''
        });
        if (!endIndex) {
            // selected from typeahead dropdown
            this.setSearchCriteriaValue('');
        }
        else {
            // not selected from typeahead dropdown
            this.setSearchCriteriaValue(value.substring(0, endIndex - value.length - 1) + value.substring(endIndex, value.length));
        }
    }
    typeaheadOnSelectValue(criterionName, index, match) {
        const existingCriteria = this.getCriterionByName(criterionName);
        if (!existingCriteria?.multiSelect) {
            this.handleInputSeparator(false);
        }
        else {
            // In multi-select scenarios the internal model value is always of type array (see toSearchCriteriaIntern)
            const existingSearchCriteria = this.internSearchCriteria.criteria[index];
            const option = match.option;
            if (existingSearchCriteria) {
                if (match.itemSelected) {
                    existingSearchCriteria.value.push(option.value);
                }
                else {
                    if (typeof existingSearchCriteria.value !== 'string') {
                        existingSearchCriteria.value = existingSearchCriteria.value?.filter(elem => elem !== option.value);
                    }
                }
            }
            // Since in case of multiselect the model will not change, we need to explicitly call the output event
            if (this.doSearchOnInputChange) {
                this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
            }
        }
    }
    typeaheadOnSelectOperator() {
        // When an operator is selected from typeahead using the keyboard enter, it selects the operator and edits
        // the value input field. The action is so fast that the user keyup event is later called for the value
        // input which then exists the current criterion To delay this focus, below timeout is provided.
        this.editCriterionByIndex(this.editCriterionIndex, false, 200);
    }
    validateCriterionValue(criterion) {
        const originalCriterion = this.getCriterionByName(criterion.name);
        const validMinMax = this.validateDateMinMaxCriteria(criterion);
        if (!originalCriterion
            || !this.strictValue && !originalCriterion.strictValue && !originalCriterion.onlySelectValue) {
            return validMinMax;
        }
        const values = this.getOptionsForCriterion(criterion.name, null);
        return values && criterion.value
            ? validMinMax && this.matchesValues(values, criterion)
            : !values && !!criterion.value;
    }
    matchesValues(values, criterion) {
        return Array.isArray(criterion.value)
            ? criterion.value.every(val => values.some(v => v.label ? v.label === val : v.value === val))
            : values.some(v => v.label ? v.label === criterion.value : v.value === criterion.value);
    }
    validateCriterionLabel(criterion) {
        if (!this.strictCriterion) {
            return true;
        }
        return !!this.getCriterionByName(criterion.name);
    }
    trimCharBasedOnType(criterionName) {
        let trimLastCharacter = true;
        const existingCriteria = this.getCriterionByName(criterionName);
        if (existingCriteria && (existingCriteria.validationType === 'float' || existingCriteria.validationType === 'integer')) {
            trimLastCharacter = false;
        }
        return trimLastCharacter;
    }
    operatorKeyUp(event) {
        const newValue = event.target.value;
        if (event.key === SiFilteredSearchComponent.keys.backspace) {
            this.handleInputBackspace(true);
        }
        else if (event.key === SiFilteredSearchComponent.keys.enter) {
            this.editCriterionByIndex(this.editCriterionIndex);
        }
        this.previousEditingValue = newValue;
    }
    inputFocusOut(event, criterionName, index) {
        const newValue = event.target.value;
        const criterionOptions = this.getCriterionByName(criterionName);
        if (criterionOptions?.operators && !criterionOptions.operators.includes(newValue)) {
            const existingSearchCriteria = this.internSearchCriteria.criteria[index];
            if (existingSearchCriteria) {
                existingSearchCriteria.operator = criterionOptions.operators.includes('=') ? '=' : criterionOptions.operators[0];
            }
        }
    }
    /**
     * It gets the array of OptionType for the criteria passed
     * @param name
     */
    getOptionsFromCriteria(name) {
        return this.getCriterionByName(name)?.options ?? [];
    }
    /**
     * Converts the internally used data model to the external model.
     * In case options for Criterion is Option[] map to the value from the label.
     * @param searchCriteria searchCriteria to be converted.
     */
    convertToExternalModel(searchCriteria) {
        const correctedCriteria = {
            ...searchCriteria,
            criteria: searchCriteria.criteria.map(sc => {
                if (this.isDate(sc.name)) {
                    return { ...sc, value: sc.dateValue ? this.getISODateString(sc.dateValue) : '' };
                }
                else if (this.isDateTime(sc.name)) {
                    if (sc.datepickerConfig?.disabledTime) {
                        return { ...sc, value: sc.dateValue ? this.getISODateString(sc.dateValue) : '' };
                    }
                    else {
                        return { ...sc, value: sc.dateValue ? sc.dateValue.toISOString() : '' };
                    }
                }
                else {
                    return { ...sc, value: this.getValue(sc) };
                }
            })
        };
        if (this.disableFreeTextSearch) {
            correctedCriteria.value = '';
        }
        return correctedCriteria;
    }
    getValue(sc) {
        const val = ToLabelPipe.getValue(this.getOptionsFromCriteria(sc.name), sc.value ?? '');
        return val ? this.getValueBasedOnType(val, sc.value) : sc.value;
    }
    getValueBasedOnType(val, searchCritVal) {
        if (Array.isArray(searchCritVal) && Array.isArray(val)) {
            // this is the case for multi-select
            return val.map((value, index) => value ?? searchCritVal[index]);
        }
        return val;
    }
    getISODateString(date) {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
    addCriterion(criterion) {
        if (!criterion.label || criterion.label.trim().length === 0) {
            criterion.label = criterion.name;
        }
        const criterionOptions = this.getCriterionByName(criterion.name);
        this.internSearchCriteria.criteria.push(toSearchCriteriaIntern(criterion, criterionOptions));
        // on initialization we do not want to set any criterion to editing
        if ((criterionOptions?.operators?.length ?? 0) > 0) {
            // when a new criterion is selected from typeahead using the keyboard enter, it adds the editable operator
            // input field and focuses on the operator field. The action is so fast that the user keyup event is later
            // called for the edited operator which then shifts focus and edit the value field. to delay this focus,
            // below timeout is provided.
            this.editCriterionByIndex(this.internSearchCriteria.criteria.length - 1, true, 200);
        }
        else {
            this.editCriterionByIndex(this.internSearchCriteria.criteria.length - 1);
        }
        if (this.doSearchOnInputChange) {
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
        }
    }
    editCriterionByIndex(index, editOperator = false, timeout = 0) {
        this.editCriterionIndex = index;
        setTimeout(() => {
            const editor = this.getValueInputForCriterion(index, editOperator);
            if (editor) {
                editor.focus();
            }
            this.cdRef.markForCheck();
        }, timeout);
    }
    getCriterionByName(criterionName) {
        if (!this.criteria) {
            return undefined;
        }
        // TODO: This does not work for lazy load criteria
        return findByName(this.internCriteria, criterionName);
    }
    hasMultiSelections(criterionName, criterionValue) {
        return this.isMultiSelect(criterionName)
            && Array.isArray(criterionValue)
            && criterionValue.length > 1;
    }
    getFilteredTypeaheadCriteria(token) {
        if (this.maxCriteria === undefined || this.internSearchCriteria.criteria.length < this.maxCriteria) {
            if (!token || token.length === 0) {
                // TODO: Which use-case is covered here???
                if (!this.exclusiveCriteria || !this.internCriteria) {
                    return this.internCriteria;
                }
                else {
                    return differenceByName(this.internCriteria, this.internSearchCriteria.criteria);
                }
            }
            token = token.toLowerCase();
            const allowedCriterias = !this.exclusiveCriteria
                ? this.internCriteria
                : differenceByName(this.internCriteria, this.internSearchCriteria.criteria);
            return filterByLabel(allowedCriterias, token);
        }
        else {
            return [];
        }
    }
    getOptionsForCriterion(criterionName, emptyResult) {
        const criterion = this.getCriterionByName(criterionName);
        if (!criterion) {
            return emptyResult;
        }
        if (criterion.options && criterion.options.length > 0) {
            // return criterion options if not empty
            return toOptionCriteria(criterion.options);
        }
        else {
            if (this.lazyValueProvider) {
                const request = { criterionName, typed: '' };
                this.debouncedValueSubject.next(request);
            }
            return emptyResult;
        }
    }
    /**
     * It gets the array of operators for the criteria passed
     * @param name
     */
    getOperatorOptionsFromCriteria(name) {
        return this.getCriterionByName(name)?.operators ?? [];
    }
    handleInputBackspace(actionFromOperator = false) {
        // True if the backspace is pressed in operator input field.
        if (actionFromOperator) {
            const editor = this.getValueInputForCriterion(this.editCriterionIndex, true);
            this.deleteCrtiterionAndEditLastCriterion(editor);
        }
        else if (this.editCriterionIndex === FOCUS_INDEX_FREE_TEXT_INPUT_FIELD) {
            if (this.internSearchCriteria.value.length === 0 && this.previousEditingValue.length === 0) {
                // edit last criterion if user presses backspace in empty search input
                this.editCriterionByIndex(this.internSearchCriteria.criteria.length - 1);
            }
        }
        else {
            // The condition executes when user enters backspace in the value input field
            const editor = this.getValueInputForCriterion(this.editCriterionIndex);
            const operatorEditor = this.getValueInputForCriterion(this.editCriterionIndex, true);
            // Check if operator is specified for the criteria. If yes edit the operator when value field is empty.
            if (operatorEditor && editor && editor.value.length === 0 && this.previousEditingValue.length === 0) {
                this.editCriterionByIndex(this.editCriterionIndex, true);
            }
            else {
                this.deleteCrtiterionAndEditLastCriterion(editor);
            }
        }
        if (this.internSearchCriteria.value.length === 0 && this.lazyCriterionProvider) {
            this.criteria = [];
        }
        if (this.doSearchOnInputChange && this.previousEditingValue.length > 0) {
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
        }
    }
    deleteCrtiterionAndEditLastCriterion(editor) {
        if (editor && editor.value.length === 0 && this.previousEditingValue.length === 0) {
            this.deleteCriterion(null, this.editCriterionIndex);
            if (this.editCriterionIndex >= 0) {
                this.editCriterionByIndex(this.editCriterionIndex - 1);
            }
        }
    }
    handleInputColon() {
        if (this.editCriterionIndex !== FOCUS_INDEX_FREE_TEXT_INPUT_FIELD) {
            return;
        }
        const editor = this.getValueInputForCriterion(this.editCriterionIndex);
        if (!editor || editor.value.length <= 1) {
            return;
        }
        let match;
        const endIndex = editor.selectionStart;
        const value = editor.value;
        const re = new RegExp(SiFilteredSearchComponent.criterionRegex, 'g');
        // eslint-disable-next-line no-cond-assign
        while ((match = re.exec(value)) != null) {
            if (endIndex === re.lastIndex) {
                const criterionName = value.substring(match.index, re.lastIndex - 1);
                this.addCriterion({ name: criterionName, label: criterionName, value: '' });
                this.setSearchCriteriaValue(value.substring(0, match.index - 1) + value.substring(re.lastIndex, value.length));
            }
        }
    }
    handleInputSeparator(trimLastCharacter) {
        const editedCriterion = this.internSearchCriteria.criteria[this.editCriterionIndex];
        if (!editedCriterion) {
            return;
        }
        if (trimLastCharacter) {
            editedCriterion.value = editedCriterion.value?.toString().substring(0, editedCriterion.value.length - 1) ?? '';
        }
        if (editedCriterion.value) {
            this.editCriterionByIndex(FOCUS_INDEX_FREE_TEXT_INPUT_FIELD);
        }
    }
    setupCriteriaFetchQueue() {
        this.debouncedCriterionQueueSubject.pipe(debounceTime(this.lazyLoadingDebounceTime)).subscribe((observer) => {
            let subscription;
            if (this.lazyCriterionProvider) {
                subscription = this.getCriteriaDisplaySubscription(this.lastTypeaheadInput, observer);
            }
            this.typeaheadInputChange.subscribe(() => {
                if (this.lazyCriterionProvider) {
                    subscription?.unsubscribe();
                    subscription = this.getCriteriaDisplaySubscription(this.lastTypeaheadInput, observer);
                }
            });
        });
        this.debouncedValueSubject.pipe(debounceTime(this.lazyLoadingDebounceTime)).subscribe((request) => {
            if (this.lazyValueProvider) {
                this.lazyValueProvider(request.criterionName, request.typed).pipe(takeUntil(this.destroySubscriptions)).subscribe(result => {
                    this.lazyLoadedValue.next(result);
                });
            }
        });
    }
    getCriteriaDisplaySubscription(value, observer) {
        return this.lazyCriterionProvider ? this.lazyCriterionProvider(value).pipe(takeUntil(this.destroySubscriptions)).subscribe(result => {
            observer.next(this.getCriteriaToDisplayFromSubscription(result));
        }) : undefined;
    }
    getCriteriaToDisplayFromSubscription(result) {
        this.criteria = result;
        let displayCriteria = [];
        if (this.maxCriteria === undefined || this.internSearchCriteria.criteria.length < this.maxCriteria) {
            displayCriteria = !this.exclusiveCriteria
                ? result
                : differenceByName(result, this.internSearchCriteria.criteria);
        }
        const topItems = (this.disableFreeTextSearch && this.internSearchCriteria.value.length > 0 && displayCriteria.length === 0)
            || !this.doSearchOnInputChange ? [{
                name: '',
                label: this.disableFreeTextSearch && this.internSearchCriteria.value.length > 0 && displayCriteria.length === 0 ?
                    this.noMatchingCriteriaText : this.submitText
            }] : [];
        return [...topItems, ...displayCriteria];
    }
    getInputType(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        if (existingCriteria && (existingCriteria.validationType === 'integer' || existingCriteria.validationType === 'float')) {
            return 'number';
        }
        return 'text';
    }
    isDateOrDateTime(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        return existingCriteria?.validationType === 'date-time' || existingCriteria?.validationType === 'date';
    }
    isDate(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        return existingCriteria?.validationType === 'date';
    }
    isDateTime(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        return existingCriteria?.validationType === 'date-time';
    }
    getDateTimeFormat(searchCriterion) {
        const existingCriteria = this.getCriterionByName(searchCriterion.name);
        // The information if the time is currently disabled is only present in the
        // current search criterion instance and not in the generic configuration.
        // So we need to merge the initial config with the current instance config.
        const config = { ...existingCriteria?.datepickerConfig, ...searchCriterion.datepickerConfig };
        return getDatepickerFormat(this.locale, config);
    }
    disableTime(criterion, disabledTime) {
        const dateConfig = criterion.datepickerConfig ? criterion.datepickerConfig : {};
        dateConfig.disabledTime = disabledTime;
        criterion.datepickerConfig = dateConfig;
        if (this.doSearchOnInputChange) {
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
        }
    }
    getDatepickerConfig(searchCriterion) {
        const existingCriteria = this.getCriterionByName(searchCriterion.name);
        // a clone of the general criterion configuration
        const config = { ...existingCriteria?.datepickerConfig };
        // the current value could have an updated version, so we update
        if (searchCriterion.datepickerConfig?.disabledTime) {
            config.disabledTime = searchCriterion.datepickerConfig?.disabledTime;
        }
        return config;
    }
    isMultiSelect(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        return !!existingCriteria?.multiSelect;
    }
    getStepAttribute(criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        if (existingCriteria && existingCriteria.validationType === 'integer') {
            return '1';
        }
        return 'any';
    }
    filterKeyInputEvents(event, criterionName) {
        const existingCriteria = this.getCriterionByName(criterionName);
        if (existingCriteria && existingCriteria.validationType === 'integer' && this.integerInvalidChar.includes(event.key)) {
            event.preventDefault();
        }
    }
    onValueInputKeyEvent(event, criterionName) {
        const newValue = event.target.value;
        if (event.key === SiFilteredSearchComponent.keys.backspace) {
            this.handleInputBackspace();
        }
        else if (event.key === SiFilteredSearchComponent.keys.separator && !this.isMultiSelect(criterionName)) {
            const trimLastCharacter = this.trimCharBasedOnType(criterionName);
            this.handleInputSeparator(trimLastCharacter);
        }
        else if (event.key === SiFilteredSearchComponent.keys.enter && !this.isMultiSelect(criterionName)) {
            this.handleInputSeparator(false);
        }
        this.previousEditingValue = newValue;
    }
    onValueInputClosed() {
        if (this.clearInputButton?.nativeElement === document.activeElement) {
            return;
        }
        // editCriterionIndex = -1 represents that the free text input is currently edited
        // all other numbers will represent the filtered pill, so we use -2 here.
        // Only apply when the focus does not shift on free text (i.e. "-1")
        if (this.editCriterionIndex !== FOCUS_INDEX_FREE_TEXT_INPUT_FIELD) {
            this.editCriterionIndex = FOCUS_INDEX_OUT_OF_COMPONENT;
        }
        this.cdRef.markForCheck();
    }
    onDateTimeKeyDown(event) {
        if (event.key === SiFilteredSearchComponent.keys.tab) {
            setTimeout(() => {
                this.onValueInputClosed();
            });
        }
    }
    onDateTimeKeyEvent(event) {
        const newValue = event.target.value;
        if (event.key === SiFilteredSearchComponent.keys.backspace) {
            this.handleInputBackspace();
        }
        else if (event.key === SiFilteredSearchComponent.keys.enter) {
            this.handleInputSeparator(false);
        }
        this.previousEditingValue = newValue;
    }
    onDatePickerClose() {
        if (!this.elementRef.nativeElement.contains(document.activeElement)) {
            this.editCriterionIndex = FOCUS_INDEX_OUT_OF_COMPONENT;
        }
    }
    selectDate(searchCriterion, date) {
        // In case the user type an illegal date into the date input,
        // our directive emits a new undefined value and keeps
        if (!date && searchCriterion.dateValue) {
            date = new Date(searchCriterion.dateValue);
        }
        searchCriterion.dateValue = date;
        if (this.doSearchOnInputChange) {
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
        }
    }
    validateDateMinMaxCriteria(criterion) {
        const dateConfig = this.getDatepickerConfig(criterion);
        const minDate = dateConfig?.minDate ?? false;
        const maxDate = dateConfig?.maxDate ?? false;
        const dateValue = criterion?.dateValue ?? false;
        return (!minDate ||
            (minDate && dateValue && dateValue >= minDate))
            && (!maxDate ||
                (maxDate && dateValue && dateValue <= maxDate));
    }
    focusFreeInputField() {
        this.editCriterionByIndex(FOCUS_INDEX_FREE_TEXT_INPUT_FIELD);
    }
    editFreeInputField() {
        this.editCriterionIndex = FOCUS_INDEX_FREE_TEXT_INPUT_FIELD;
    }
    modelChanged(event, index = FOCUS_INDEX_FREE_TEXT_INPUT_FIELD, criterionName) {
        if (criterionName && !this.isMultiSelect(criterionName)) {
            const existingSearchCriteria = this.internSearchCriteria.criteria[index];
            existingSearchCriteria.value = event;
        }
        // Also make sure event is not empty to prevent extra emissions. For example
        // when the user presses "Enter" or clicks to manually trigger the search
        if (this.doSearchOnInputChange && event) {
            this.searchEmitQueue.next(this.convertToExternalModel(this.internSearchCriteria));
        }
    }
    onTypeaheadInputChange(event) {
        this.typeaheadInputChange.next(event);
        this.lastTypeaheadInput = event;
    }
    clearValueInput(index, criterion) {
        if (this.isDateOrDateTime(criterion.name)) {
            criterion.dateValue = undefined;
            criterion.value = '';
        }
        else {
            if (this.isMultiSelect(criterion.name)) {
                this.valueInput.nativeElement.value = '';
            }
            else {
                criterion.value = '';
            }
        }
        this.editCriterionByIndex(index);
    }
}
SiFilteredSearchComponent.criterionRegex = '(\\S+:)';
// See  https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
SiFilteredSearchComponent.keys = {
    backspace: 'Backspace',
    colon: ':',
    enter: 'Enter',
    semicolon: ';',
    separator: ';',
    tab: 'Tab'
};
SiFilteredSearchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFilteredSearchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFilteredSearchComponent, selector: "si-filtered-search", inputs: { doSearchOnInputChange: "doSearchOnInputChange", lazyCriterionProvider: "lazyCriterionProvider", lazyValueProvider: "lazyValueProvider", disabled: "disabled", readonly: "readonly", strictCriterion: "strictCriterion", strictValue: "strictValue", onlySelectValue: "onlySelectValue", lazyLoadingDebounceTime: "lazyLoadingDebounceTime", searchDebounceTime: "searchDebounceTime", placeholder: "placeholder", showIcon: "showIcon", submitText: "submitText", selectedCriteriaIndex: "selectedCriteriaIndex", optionsInScrollableView: "optionsInScrollableView", searchCriteria: "searchCriteria", criteria: "criteria", exclusiveCriteria: "exclusiveCriteria", maxCriteria: "maxCriteria", maxCriteriaOptions: "maxCriteriaOptions", searchLabel: "searchLabel", clearButtonLabel: "clearButtonLabel", itemCountText: "itemCountText", colorVariant: "colorVariant", items: "items", disableFreeTextSearch: "disableFreeTextSearch", noMatchingCriteriaText: "noMatchingCriteriaText" }, outputs: { doSearch: "doSearch" }, viewQueries: [{ propertyName: "clearInputButton", first: true, predicate: ["clearInputButton"], descendants: true, read: ElementRef }, { propertyName: "valueInput", first: true, predicate: ["valueInput"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: "<ng-template #criterionItemTemplate let-model=\"item\">\n  <span [innerHTML]=\"highlight(model)\"></span>\n</ng-template>\n<div\n  class=\"filtered-search py-1 ps-2 pe-3\"\n  [class.dark-background]=\"colorVariant === 'base-0'\"\n  [class.focus-within]=\"editCriterionIndex === -1 && internSearchCriteria.criteria.length === 0\"\n  [class.disabled]=\"disabled\"\n>\n  <!-- placeholder icon-->\n  <span *ngIf=\"showIcon\"\n    [class.empty]=\"!internSearchCriteria.criteria.length && !internSearchCriteria.value\"\n    class=\"placeholder-icon icon element-search mx-0\">\n  </span>\n\n  <div\n    *ngFor=\"let criterion of internSearchCriteria.criteria; let i = index; trackBy: trackByIndex\"\n    class=\"criteria\"\n    [class.disabled]=\"disabled\"\n    [attr.aria-disabled]=\"disabled\"\n    [class.invalid-criterion]=\"!validateCriterionLabel(criterion)\"\n    >\n    <div class=\"criterion-label me-1 si-title-2\" (click)=\"editCriterion(criterion)\">\n      {{ criterion.label }}\n    </div>\n    <!-- criterion operator input -->\n    <input\n      *ngIf=\"isOperatorSpecified(criterion.name) && editCriterionIndex === i\"\n      type=\"text\"\n      class=\"operator-input-{{ i }} operator-input\"\n      [(ngModel)]=\"criterion.operator\"\n      (keyup)=\"operatorKeyUp($event)\"\n      (click)=\"editOperator(criterion, $event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (focusout)=\"inputFocusOut($event, criterion.name, i)\"\n      (ngModelChange)=\"modelChanged($event, i)\"\n      [siTypeahead]=\"getCriterionOperators(criterion.name, criterion.operator)\"\n      [typeaheadProcess]=\"false\"\n      (typeaheadOnSelect)=\"typeaheadOnSelectOperator()\"\n      [typeaheadAutoSelectIndex]=\"getSelectedOperatorIndex(criterion.name, criterion.operator)\"\n      [typeaheadMinLength]=\"0\"\n      [typeaheadOptionsLimit]=\"0\"\n      [readOnly]=\"false\"\n      [typeaheadScrollable]=\"true\"\n      [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      [attr.size]=\"getLongestOperatorLength(criterion.name)\"\n      />\n    <!-- criterion operator field -->\n    <div\n      *ngIf=\"criterion.operator && editCriterionIndex !== i\"\n      class=\"criterion-operator me-1 focus-inside\"\n      (click)=\"editOperator(criterion, $event)\"\n      >\n      <span\n        *ngIf=\"!!criterion.operator\"\n        class=\"criterion-operator-text focus-inside\"\n        [tabindex]=\"!disabled ? 0 : -1\"\n        >\n        {{ criterion.operator }}\n      </span>\n    </div>\n    <!-- criterion value field -->\n    <div\n      *ngIf=\"editCriterionIndex !== i\"\n      class=\"criterion-value px-0 focus-inside\"\n      [class.invalid-criterion]=\"!validateCriterionLabel(criterion) || !validateCriterionValue(criterion)\"\n      (keyup.enter)=\"editValue(criterion, $event)\"\n      (click)=\"editValue(criterion, $event)\"\n      >\n      <ng-container [ngTemplateOutlet]=\"!!criterion.dateValue && isDateTime(criterion.name) ? dateTimeTemplate :\n      !!criterion.dateValue && isDate(criterion.name) ? dateTemplate :\n      !!criterion.value && hasMultiSelections(criterion.name, criterion.value) ? itemCount : valueTemplate\"></ng-container>\n      <ng-template #dateTemplate>\n        <span\n          class=\"criterion-value-text focus-inside\"\n          [tabindex]=\"disabled ? -1 : 0\"\n          >\n          {{ criterion.dateValue | date: shortDateFormat }}\n        </span>\n      </ng-template>\n      <ng-template #dateTimeTemplate>\n        <span class=\"criterion-value-text p-0\">\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.dateValue | date: getDateTimeFormat(criterion) }}\n          </span>\n        </span>\n      </ng-template>\n      <ng-template #itemCount>\n        <span\n          *ngIf=\"!!criterion.value && itemCountText\"\n          class=\"criterion-value-text p-0\"\n          >\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ itemCountText | translate:{ itemCount: criterion.value.length } }}\n          </span>\n        </span>\n        <span\n          *ngIf=\"!!criterion.value && !itemCountText\"\n          class=\"criterion-value-text p-0\"\n          >\n          <span class=\"ps-4 pe-2 d-block focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.value.length }} {{ items | translate }}\n          </span>\n        </span>\n      </ng-template>\n      <ng-template #valueTemplate>\n        <span class=\"criterion-value-text p-0\">\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.value! | toLabel: getOptionsFromCriteria(criterion.name) }}\n          </span>\n        </span>\n      </ng-template>\n      <button\n        *ngIf=\"!readonly\"\n        type=\"button\"\n        class=\"criterion-value-button ps-0 ms-0 py-0 btn btn-circle btn-xs btn-ghost element-cancel focus-inside\"\n        [attr.aria-label]=\"clearButtonLabel | translate\"\n        (click)=\"deleteCriterion($event, i)\"\n        [disabled]=\"disabled\">\n      </button>\n    </div>\n    <!-- criterion value input -->\n    <input\n      *ngIf=\"editCriterionIndex === i && !isDateOrDateTime(criterion.name)\"\n      class=\"value-input-{{ i }} criterion-value pe-4\"\n      [class.me-n8]=\"criterion.value?.length\"\n      [class.pe-8]=\"criterion.value?.length\"\n      [type]=\"getInputType(criterion.name)\"\n      (keydown)=\"filterKeyInputEvents($event, criterion.name)\"\n      [step]=\"getStepAttribute(criterion.name)\"\n      [ngModel]=\"!isMultiSelect(criterion.name) ? criterion.value : ''\"\n      (keyup)=\"onValueInputKeyEvent($event, criterion.name)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      (ngModelChange)=\"modelChanged($event, i, criterion.name)\"\n      [siTypeahead]=\"getCriterionOptions(criterion.name, i, isMultiSelect(criterion.name) ? '' : criterion.value)\"\n      [typeaheadProcess]=\"isMultiSelect(criterion.name)\"\n      (typeaheadOnSelect)=\"typeaheadOnSelectValue(criterion.name, i, $event)\"\n      (typeaheadOnMultiselectClose)=\"focusFreeInputField()\"\n      (typeaheadClosed)=\"onValueInputClosed()\"\n      [typeaheadMultiSelect]='isMultiSelect(criterion.name)'\n      [typeaheadMinLength]=\"0\"\n      typeaheadOptionField=\"label\"\n      [typeaheadOptionsLimit]=\"maxCriteriaOptions\"\n      [readOnly]=\"readonly || onlySelectValue || this.getCriterionOnlySelectValue(criterion.name)\"\n      [typeaheadScrollable]=\"true\"\n      [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      #valueInput\n      />\n\n    <input\n      *ngIf=\"editCriterionIndex === i && isDate(criterion.name)\"\n      type=\"text\"\n      class=\"value-input-{{ i }} criterion-value datepicker form-control\"\n      [class.me-n8]=\"criterion.dateValue\"\n      [class.pe-8]=\"criterion.dateValue\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      (keydown)=\"onDateTimeKeyDown($event)\"\n      (keyup)=\"onDateTimeKeyEvent($event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      siDatepicker\n      [siDatepickerConfig]=\"getDatepickerConfig(criterion)\"\n      (siDatepickerClose)=\"onDatePickerClose()\"\n      [ngModel]=\"criterion.dateValue!\"\n      (ngModelChange)=\"selectDate(criterion, $event)\"\n      />\n    <input\n      *ngIf=\"editCriterionIndex === i && isDateTime(criterion.name)\"\n      type=\"text\"\n      class=\"value-input-{{ i }} criterion-value datepicker form-control\"\n      [class.me-n8]=\"criterion.dateValue\"\n      [class.pe-8]=\"criterion.dateValue\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      (keydown)=\"onDateTimeKeyDown($event)\"\n      (keyup)=\"onDateTimeKeyEvent($event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      siDatepicker\n      [siDatepickerConfig]=\"getDatepickerConfig(criterion)\"\n      (siDatepickerClose)=\"onDatePickerClose()\"\n      (siDatepickerDisabledTime)=\"disableTime(criterion, $event)\"\n      [ngModel]=\"criterion.dateValue!\"\n      (ngModelChange)=\"selectDate(criterion, $event)\"\n      />\n      <button\n        *ngIf=\"editCriterionIndex === i && (criterion.value?.length || criterion.dateValue) && !readonly\"\n        type=\"button\"\n        class=\"clear-input btn btn-circle btn-xs btn-ghost element-cancel\"\n        [attr.aria-label]=\"clearButtonLabel | translate\"\n        (click)=\"clearValueInput(i, criterion)\"\n        #clearInputButton></button>\n  </div>\n  <!-- criterion input -->\n  <input\n    type=\"text\"\n    class=\"value-input ps-2 me-3\"\n    [class.ms-1]=\"internSearchCriteria.criteria.length\"\n    [(ngModel)]=\"internSearchCriteria.value\"\n    (keyup)=\"criterionInputKeyUp($event)\"\n    (paste)=\"handleContextMenuPaste($event)\"\n    (click)=\"editFreeInputField()\"\n    (blur)=\"editCriterionIndex = -2; focusState = false;\"\n    (focus)=\"inputFocused($event)\"\n    (ngModelChange)=\"modelChanged($event)\"\n    [siTypeahead]=\"dataSource\"\n    [typeaheadProcess]=\"false\"\n    (typeaheadOnInput)=\"onTypeaheadInputChange($event)\"\n    (typeaheadOnSelect)=\"typeaheadOnSelectCriterion($event.option)\"\n    [typeaheadClearValueOnSelect]=\"true\"\n    [typeaheadMinLength]=\"readonly ? 1 : 0\"\n    typeaheadOptionField=\"name\"\n    [typeaheadOptionsLimit]=\"maxCriteriaOptions\"\n    [typeaheadAutoSelectIndex]=\"selectedCriteriaIndex\"\n    [typeaheadItemTemplate]=\"criterionItemTemplate\"\n    [typeaheadScrollable]=\"true\"\n    [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n    [readonly]=\"readonly\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    [attr.aria-label]=\"searchLabel | translate\"\n    />\n\n  <!-- clear all searchCriteria -->\n  <button\n    *ngIf=\"(internSearchCriteria.criteria.length || internSearchCriteria.value.length) && !readonly\"\n    type=\"button\"\n    class=\"clear-button btn btn-circle btn-xs btn-ghost element-cancel\"\n    [disabled]=\"disabled\"\n    [attr.aria-label]=\"clearButtonLabel | translate\"\n    (click)=\"deleteAllCriteria($event)\"></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-primary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1);--filter-search-background-color: var(--element-base-1);--filter-pill-background-color: var(--element-base-0)}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4);--filter-search-background-color: var(--element-base-0);--filter-pill-background-color: var(--element-base-1)}.filtered-search{border-radius:4px;display:flex;flex-direction:row;line-height:24px;background-color:var(--filter-search-background-color);overflow-x:auto;overflow-y:hidden;min-block-size:32px;align-items:center}.filtered-search:hover:not(.disabled) .placeholder-icon,.filtered-search:hover:not(.disabled) input:not(:focus)::placeholder{color:var(--element-text-primary)}.filtered-search.focus-within:not(.disabled):focus-within{box-shadow:0 0 0 2px var(--element-focus-default)}.filtered-search.focus-within:not(.disabled):focus-within .value-input{box-shadow:none;border:0}.filtered-search.focus-within:not(.disabled):focus-within .placeholder-icon{color:var(--element-text-primary)}.filtered-search .placeholder-icon.empty{color:var(--element-text-secondary)}.filtered-search .criterion-value:not(input) .criterion-value-text{padding-block:0;padding-inline:8px;margin-inline-end:2px}.filtered-search:not(.disabled) .criterion-label:hover,.filtered-search:not(.disabled) .criterion-value-text:hover,.filtered-search:not(.disabled) :not(.invalid-criterion) .element-cancel:hover,.filtered-search:not(.disabled) .criterion-operator:hover{background:var(--element-action-secondary-hover)}.filtered-search .criterion-label{border-start-start-radius:24px;border-end-start-radius:24px}.filtered-search.disabled{color:var(--element-text-disabled)}.filtered-search.disabled input,.filtered-search.disabled button,.filtered-search.disabled .badge,.filtered-search.disabled .placeholder-icon,.filtered-search.disabled ::placeholder{color:var(--element-text-disabled)}.filtered-search.disabled .element-cancel.criterion-value-button{display:none}.criteria{display:flex;cursor:pointer;white-space:nowrap;margin-block:2px;margin-inline:4px;border-radius:24px}.criteria.invalid-criterion{box-shadow:0 0 0 1px var(--element-status-danger)}.criteria:not(.invalid-criterion) .invalid-criterion>span{box-shadow:inset 0 0 0 1px var(--element-status-danger);padding:1px!important}.criteria:not(.invalid-criterion) .invalid-criterion>span>span{block-size:22px;line-height:22px}.criteria.disabled{cursor:inherit}.criterion-label,.criterion-value{background:var(--filter-pill-background-color);display:inline-flex;align-items:center;padding:0 8px;padding-inline-start:12px}.criterion-value{border:0;border-start-start-radius:0;border-end-start-radius:0;border-start-end-radius:24px;border-end-end-radius:24px;outline:none}.criterion-value.datepicker{inline-size:auto}.criterion-operator{display:inline-flex;align-items:center}.criterion-operator .criterion-operator-text{min-inline-size:20px;text-align:center;padding-inline:4px}.criterion-value,.operator-input,.criterion-operator{background:var(--filter-pill-background-color)}.criterion-value-button{background-color:transparent;font-size:1.5rem;border:0}.criterion-value-text+.criterion-value-button{margin-inline-start:0}.value-input{border:0;flex-grow:1;line-height:24px;background-color:var(--input-background-color);margin:0;outline:none}.operator-input{display:inline-flex;align-items:center;margin-inline-end:4px;padding-inline-start:12px;padding-block:0;border:0;outline:none}input::placeholder{color:var(--element-text-secondary);padding-inline:0;margin-block:0;margin-inline:4px}input:focus::placeholder,.criteria+input::placeholder{color:transparent!important}.clear-button{margin-inline-end:-2px;margin-block:-2px}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: SiDatepickerDirective, selector: "[siDatepicker]", exportAs: ["siDatepicker"] }, { kind: "directive", type: SiTypeaheadDirective, selector: "[siTypeahead]", inputs: ["siTypeahead", "typeaheadProcess", "typeaheadScrollable", "typeaheadOptionsInScrollableView", "typeaheadOptionsLimit", "typeaheadScrollableAdditionalHeight", "typeaheadAutoSelectIndex", "typeaheadCloseOnEsc", "typeaheadClearValueOnSelect", "typeaheadWaitMs", "typeaheadMinLength", "typeaheadOptionField", "typeaheadMultiSelect", "typeaheadTokenize", "typeaheadMatchAllTokens", "typeaheadItemTemplate", "typeaheadDropup", "typeaheadSkipSortingMatches"], outputs: ["typeaheadOnInput", "typeaheadOnSelect", "typeaheadOnMultiselectClose", "typeaheadOnFullMatch", "typeaheadClosed"], exportAs: ["si-typeahead"] }, { kind: "pipe", type: i1.DatePipe, name: "date" }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }, { kind: "pipe", type: ToLabelPipe, name: "toLabel" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-filtered-search', template: "<ng-template #criterionItemTemplate let-model=\"item\">\n  <span [innerHTML]=\"highlight(model)\"></span>\n</ng-template>\n<div\n  class=\"filtered-search py-1 ps-2 pe-3\"\n  [class.dark-background]=\"colorVariant === 'base-0'\"\n  [class.focus-within]=\"editCriterionIndex === -1 && internSearchCriteria.criteria.length === 0\"\n  [class.disabled]=\"disabled\"\n>\n  <!-- placeholder icon-->\n  <span *ngIf=\"showIcon\"\n    [class.empty]=\"!internSearchCriteria.criteria.length && !internSearchCriteria.value\"\n    class=\"placeholder-icon icon element-search mx-0\">\n  </span>\n\n  <div\n    *ngFor=\"let criterion of internSearchCriteria.criteria; let i = index; trackBy: trackByIndex\"\n    class=\"criteria\"\n    [class.disabled]=\"disabled\"\n    [attr.aria-disabled]=\"disabled\"\n    [class.invalid-criterion]=\"!validateCriterionLabel(criterion)\"\n    >\n    <div class=\"criterion-label me-1 si-title-2\" (click)=\"editCriterion(criterion)\">\n      {{ criterion.label }}\n    </div>\n    <!-- criterion operator input -->\n    <input\n      *ngIf=\"isOperatorSpecified(criterion.name) && editCriterionIndex === i\"\n      type=\"text\"\n      class=\"operator-input-{{ i }} operator-input\"\n      [(ngModel)]=\"criterion.operator\"\n      (keyup)=\"operatorKeyUp($event)\"\n      (click)=\"editOperator(criterion, $event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (focusout)=\"inputFocusOut($event, criterion.name, i)\"\n      (ngModelChange)=\"modelChanged($event, i)\"\n      [siTypeahead]=\"getCriterionOperators(criterion.name, criterion.operator)\"\n      [typeaheadProcess]=\"false\"\n      (typeaheadOnSelect)=\"typeaheadOnSelectOperator()\"\n      [typeaheadAutoSelectIndex]=\"getSelectedOperatorIndex(criterion.name, criterion.operator)\"\n      [typeaheadMinLength]=\"0\"\n      [typeaheadOptionsLimit]=\"0\"\n      [readOnly]=\"false\"\n      [typeaheadScrollable]=\"true\"\n      [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      [attr.size]=\"getLongestOperatorLength(criterion.name)\"\n      />\n    <!-- criterion operator field -->\n    <div\n      *ngIf=\"criterion.operator && editCriterionIndex !== i\"\n      class=\"criterion-operator me-1 focus-inside\"\n      (click)=\"editOperator(criterion, $event)\"\n      >\n      <span\n        *ngIf=\"!!criterion.operator\"\n        class=\"criterion-operator-text focus-inside\"\n        [tabindex]=\"!disabled ? 0 : -1\"\n        >\n        {{ criterion.operator }}\n      </span>\n    </div>\n    <!-- criterion value field -->\n    <div\n      *ngIf=\"editCriterionIndex !== i\"\n      class=\"criterion-value px-0 focus-inside\"\n      [class.invalid-criterion]=\"!validateCriterionLabel(criterion) || !validateCriterionValue(criterion)\"\n      (keyup.enter)=\"editValue(criterion, $event)\"\n      (click)=\"editValue(criterion, $event)\"\n      >\n      <ng-container [ngTemplateOutlet]=\"!!criterion.dateValue && isDateTime(criterion.name) ? dateTimeTemplate :\n      !!criterion.dateValue && isDate(criterion.name) ? dateTemplate :\n      !!criterion.value && hasMultiSelections(criterion.name, criterion.value) ? itemCount : valueTemplate\"></ng-container>\n      <ng-template #dateTemplate>\n        <span\n          class=\"criterion-value-text focus-inside\"\n          [tabindex]=\"disabled ? -1 : 0\"\n          >\n          {{ criterion.dateValue | date: shortDateFormat }}\n        </span>\n      </ng-template>\n      <ng-template #dateTimeTemplate>\n        <span class=\"criterion-value-text p-0\">\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.dateValue | date: getDateTimeFormat(criterion) }}\n          </span>\n        </span>\n      </ng-template>\n      <ng-template #itemCount>\n        <span\n          *ngIf=\"!!criterion.value && itemCountText\"\n          class=\"criterion-value-text p-0\"\n          >\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ itemCountText | translate:{ itemCount: criterion.value.length } }}\n          </span>\n        </span>\n        <span\n          *ngIf=\"!!criterion.value && !itemCountText\"\n          class=\"criterion-value-text p-0\"\n          >\n          <span class=\"ps-4 pe-2 d-block focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.value.length }} {{ items | translate }}\n          </span>\n        </span>\n      </ng-template>\n      <ng-template #valueTemplate>\n        <span class=\"criterion-value-text p-0\">\n          <span class=\"d-block px-4 focus-inside\" [tabindex]=\"disabled ? -1 : 0\">\n            {{ criterion.value! | toLabel: getOptionsFromCriteria(criterion.name) }}\n          </span>\n        </span>\n      </ng-template>\n      <button\n        *ngIf=\"!readonly\"\n        type=\"button\"\n        class=\"criterion-value-button ps-0 ms-0 py-0 btn btn-circle btn-xs btn-ghost element-cancel focus-inside\"\n        [attr.aria-label]=\"clearButtonLabel | translate\"\n        (click)=\"deleteCriterion($event, i)\"\n        [disabled]=\"disabled\">\n      </button>\n    </div>\n    <!-- criterion value input -->\n    <input\n      *ngIf=\"editCriterionIndex === i && !isDateOrDateTime(criterion.name)\"\n      class=\"value-input-{{ i }} criterion-value pe-4\"\n      [class.me-n8]=\"criterion.value?.length\"\n      [class.pe-8]=\"criterion.value?.length\"\n      [type]=\"getInputType(criterion.name)\"\n      (keydown)=\"filterKeyInputEvents($event, criterion.name)\"\n      [step]=\"getStepAttribute(criterion.name)\"\n      [ngModel]=\"!isMultiSelect(criterion.name) ? criterion.value : ''\"\n      (keyup)=\"onValueInputKeyEvent($event, criterion.name)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      (ngModelChange)=\"modelChanged($event, i, criterion.name)\"\n      [siTypeahead]=\"getCriterionOptions(criterion.name, i, isMultiSelect(criterion.name) ? '' : criterion.value)\"\n      [typeaheadProcess]=\"isMultiSelect(criterion.name)\"\n      (typeaheadOnSelect)=\"typeaheadOnSelectValue(criterion.name, i, $event)\"\n      (typeaheadOnMultiselectClose)=\"focusFreeInputField()\"\n      (typeaheadClosed)=\"onValueInputClosed()\"\n      [typeaheadMultiSelect]='isMultiSelect(criterion.name)'\n      [typeaheadMinLength]=\"0\"\n      typeaheadOptionField=\"label\"\n      [typeaheadOptionsLimit]=\"maxCriteriaOptions\"\n      [readOnly]=\"readonly || onlySelectValue || this.getCriterionOnlySelectValue(criterion.name)\"\n      [typeaheadScrollable]=\"true\"\n      [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      #valueInput\n      />\n\n    <input\n      *ngIf=\"editCriterionIndex === i && isDate(criterion.name)\"\n      type=\"text\"\n      class=\"value-input-{{ i }} criterion-value datepicker form-control\"\n      [class.me-n8]=\"criterion.dateValue\"\n      [class.pe-8]=\"criterion.dateValue\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      (keydown)=\"onDateTimeKeyDown($event)\"\n      (keyup)=\"onDateTimeKeyEvent($event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      siDatepicker\n      [siDatepickerConfig]=\"getDatepickerConfig(criterion)\"\n      (siDatepickerClose)=\"onDatePickerClose()\"\n      [ngModel]=\"criterion.dateValue!\"\n      (ngModelChange)=\"selectDate(criterion, $event)\"\n      />\n    <input\n      *ngIf=\"editCriterionIndex === i && isDateTime(criterion.name)\"\n      type=\"text\"\n      class=\"value-input-{{ i }} criterion-value datepicker form-control\"\n      [class.me-n8]=\"criterion.dateValue\"\n      [class.pe-8]=\"criterion.dateValue\"\n      [attr.aria-label]=\"searchLabel | translate\"\n      (keydown)=\"onDateTimeKeyDown($event)\"\n      (keyup)=\"onDateTimeKeyEvent($event)\"\n      (focus)=\"inputFocused($event, i)\"\n      (blur)=\"focusState = false;\"\n      siDatepicker\n      [siDatepickerConfig]=\"getDatepickerConfig(criterion)\"\n      (siDatepickerClose)=\"onDatePickerClose()\"\n      (siDatepickerDisabledTime)=\"disableTime(criterion, $event)\"\n      [ngModel]=\"criterion.dateValue!\"\n      (ngModelChange)=\"selectDate(criterion, $event)\"\n      />\n      <button\n        *ngIf=\"editCriterionIndex === i && (criterion.value?.length || criterion.dateValue) && !readonly\"\n        type=\"button\"\n        class=\"clear-input btn btn-circle btn-xs btn-ghost element-cancel\"\n        [attr.aria-label]=\"clearButtonLabel | translate\"\n        (click)=\"clearValueInput(i, criterion)\"\n        #clearInputButton></button>\n  </div>\n  <!-- criterion input -->\n  <input\n    type=\"text\"\n    class=\"value-input ps-2 me-3\"\n    [class.ms-1]=\"internSearchCriteria.criteria.length\"\n    [(ngModel)]=\"internSearchCriteria.value\"\n    (keyup)=\"criterionInputKeyUp($event)\"\n    (paste)=\"handleContextMenuPaste($event)\"\n    (click)=\"editFreeInputField()\"\n    (blur)=\"editCriterionIndex = -2; focusState = false;\"\n    (focus)=\"inputFocused($event)\"\n    (ngModelChange)=\"modelChanged($event)\"\n    [siTypeahead]=\"dataSource\"\n    [typeaheadProcess]=\"false\"\n    (typeaheadOnInput)=\"onTypeaheadInputChange($event)\"\n    (typeaheadOnSelect)=\"typeaheadOnSelectCriterion($event.option)\"\n    [typeaheadClearValueOnSelect]=\"true\"\n    [typeaheadMinLength]=\"readonly ? 1 : 0\"\n    typeaheadOptionField=\"name\"\n    [typeaheadOptionsLimit]=\"maxCriteriaOptions\"\n    [typeaheadAutoSelectIndex]=\"selectedCriteriaIndex\"\n    [typeaheadItemTemplate]=\"criterionItemTemplate\"\n    [typeaheadScrollable]=\"true\"\n    [typeaheadOptionsInScrollableView]=\"optionsInScrollableView\"\n    [readonly]=\"readonly\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    [attr.aria-label]=\"searchLabel | translate\"\n    />\n\n  <!-- clear all searchCriteria -->\n  <button\n    *ngIf=\"(internSearchCriteria.criteria.length || internSearchCriteria.value.length) && !readonly\"\n    type=\"button\"\n    class=\"clear-button btn btn-circle btn-xs btn-ghost element-cancel\"\n    [disabled]=\"disabled\"\n    [attr.aria-label]=\"clearButtonLabel | translate\"\n    (click)=\"deleteAllCriteria($event)\"></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-primary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1);--filter-search-background-color: var(--element-base-1);--filter-pill-background-color: var(--element-base-0)}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4);--filter-search-background-color: var(--element-base-0);--filter-pill-background-color: var(--element-base-1)}.filtered-search{border-radius:4px;display:flex;flex-direction:row;line-height:24px;background-color:var(--filter-search-background-color);overflow-x:auto;overflow-y:hidden;min-block-size:32px;align-items:center}.filtered-search:hover:not(.disabled) .placeholder-icon,.filtered-search:hover:not(.disabled) input:not(:focus)::placeholder{color:var(--element-text-primary)}.filtered-search.focus-within:not(.disabled):focus-within{box-shadow:0 0 0 2px var(--element-focus-default)}.filtered-search.focus-within:not(.disabled):focus-within .value-input{box-shadow:none;border:0}.filtered-search.focus-within:not(.disabled):focus-within .placeholder-icon{color:var(--element-text-primary)}.filtered-search .placeholder-icon.empty{color:var(--element-text-secondary)}.filtered-search .criterion-value:not(input) .criterion-value-text{padding-block:0;padding-inline:8px;margin-inline-end:2px}.filtered-search:not(.disabled) .criterion-label:hover,.filtered-search:not(.disabled) .criterion-value-text:hover,.filtered-search:not(.disabled) :not(.invalid-criterion) .element-cancel:hover,.filtered-search:not(.disabled) .criterion-operator:hover{background:var(--element-action-secondary-hover)}.filtered-search .criterion-label{border-start-start-radius:24px;border-end-start-radius:24px}.filtered-search.disabled{color:var(--element-text-disabled)}.filtered-search.disabled input,.filtered-search.disabled button,.filtered-search.disabled .badge,.filtered-search.disabled .placeholder-icon,.filtered-search.disabled ::placeholder{color:var(--element-text-disabled)}.filtered-search.disabled .element-cancel.criterion-value-button{display:none}.criteria{display:flex;cursor:pointer;white-space:nowrap;margin-block:2px;margin-inline:4px;border-radius:24px}.criteria.invalid-criterion{box-shadow:0 0 0 1px var(--element-status-danger)}.criteria:not(.invalid-criterion) .invalid-criterion>span{box-shadow:inset 0 0 0 1px var(--element-status-danger);padding:1px!important}.criteria:not(.invalid-criterion) .invalid-criterion>span>span{block-size:22px;line-height:22px}.criteria.disabled{cursor:inherit}.criterion-label,.criterion-value{background:var(--filter-pill-background-color);display:inline-flex;align-items:center;padding:0 8px;padding-inline-start:12px}.criterion-value{border:0;border-start-start-radius:0;border-end-start-radius:0;border-start-end-radius:24px;border-end-end-radius:24px;outline:none}.criterion-value.datepicker{inline-size:auto}.criterion-operator{display:inline-flex;align-items:center}.criterion-operator .criterion-operator-text{min-inline-size:20px;text-align:center;padding-inline:4px}.criterion-value,.operator-input,.criterion-operator{background:var(--filter-pill-background-color)}.criterion-value-button{background-color:transparent;font-size:1.5rem;border:0}.criterion-value-text+.criterion-value-button{margin-inline-start:0}.value-input{border:0;flex-grow:1;line-height:24px;background-color:var(--input-background-color);margin:0;outline:none}.operator-input{display:inline-flex;align-items:center;margin-inline-end:4px;padding-inline-start:12px;padding-block:0;border:0;outline:none}input::placeholder{color:var(--element-text-secondary);padding-inline:0;margin-block:0;margin-inline:4px}input:focus::placeholder,.criteria+input::placeholder{color:transparent!important}.clear-button{margin-inline-end:-2px;margin-block:-2px}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { doSearch: [{
                type: Output
            }], doSearchOnInputChange: [{
                type: Input
            }], lazyCriterionProvider: [{
                type: Input
            }], lazyValueProvider: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], strictCriterion: [{
                type: Input
            }], strictValue: [{
                type: Input
            }], onlySelectValue: [{
                type: Input
            }], lazyLoadingDebounceTime: [{
                type: Input
            }], searchDebounceTime: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], showIcon: [{
                type: Input
            }], submitText: [{
                type: Input
            }], selectedCriteriaIndex: [{
                type: Input
            }], optionsInScrollableView: [{
                type: Input
            }], searchCriteria: [{
                type: Input
            }], criteria: [{
                type: Input
            }], exclusiveCriteria: [{
                type: Input
            }], maxCriteria: [{
                type: Input
            }], maxCriteriaOptions: [{
                type: Input
            }], searchLabel: [{
                type: Input
            }], clearButtonLabel: [{
                type: Input
            }], itemCountText: [{
                type: Input
            }], colorVariant: [{
                type: Input
            }], items: [{
                type: Input
            }], disableFreeTextSearch: [{
                type: Input
            }], noMatchingCriteriaText: [{
                type: Input
            }], clearInputButton: [{
                type: ViewChild,
                args: ['clearInputButton', { read: ElementRef }]
            }], valueInput: [{
                type: ViewChild,
                args: ['valueInput', { read: ElementRef }]
            }] } });

class SiTypeaheadModule {
}
SiTypeaheadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiTypeaheadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadModule, declarations: [SiTypeaheadComponent, SiTypeaheadDirective], imports: [CommonModule, OverlayModule], exports: [SiTypeaheadDirective] });
SiTypeaheadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadModule, imports: [CommonModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTypeaheadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [SiTypeaheadComponent, SiTypeaheadDirective],
                    exports: [SiTypeaheadDirective]
                }]
        }] });

class SiFilteredSearchModule {
}
SiFilteredSearchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFilteredSearchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchModule, declarations: [SiFilteredSearchComponent,
        ToLabelPipe], imports: [CommonModule,
        FormsModule,
        SiDatepickerModule,
        SiTranslateModule,
        SiTypeaheadModule], exports: [SiFilteredSearchComponent] });
SiFilteredSearchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchModule, imports: [CommonModule,
        FormsModule,
        SiDatepickerModule,
        SiTranslateModule,
        SiTypeaheadModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFilteredSearchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SiDatepickerModule,
                        SiTranslateModule,
                        SiTypeaheadModule
                    ],
                    declarations: [
                        SiFilteredSearchComponent,
                        ToLabelPipe
                    ],
                    exports: [SiFilteredSearchComponent]
                }]
        }] });

class SiFileDropzoneComponent {
    constructor() {
        /**
         * Text or translation key of the input file selector (is combined with the `uploadTextRest`).
         */
        this.uploadTextFileSelect = 'click to upload';
        /**
         * Text or translation key of the drag&drop field (is combined with the `uploadTextFileSelect`).
         */
        this.uploadDropText = 'Drop files here or';
        /**
         * Text or translation key for max file size.
         */
        this.maxFileSizeText = 'Maximum upload size';
        /**
         * Text or translation key for accepted types.
         */
        this.acceptText = 'Accepted file types';
        /**
         * Text or translation key of message title if incorrect file type is dragged / dropped.
         */
        this.errorTextFileType = 'Incorrect file type selected';
        /**
         * Message or translation key if file exceeds the maximum file size limit.
         */
        this.errorTextFileMaxSize = 'File exceeds allowed maximum size';
        /**
         * Defines whether the file input allows selecting multiple files.
         */
        this.multiple = false;
        /**
         * Event emitted when files are added.
         */
        this.filesAdded = new EventEmitter();
        this.maxFileSizeString = '';
        this.dragOver = false;
        this.locale = inject(LOCALE_ID);
        this.numberFormat = new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 });
    }
    ngOnChanges(changes) {
        if (changes.maxFileSize) {
            this.maxFileSizeString = this.maxFileSize ? this.fileSizeToString(this.maxFileSize) : '';
        }
    }
    dropHandler(event) {
        event.preventDefault();
        this.handleFiles(event.dataTransfer.files);
        this.dragOver = false;
    }
    dragOverHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = true;
    }
    inputEnterHandler() {
        this.fileInput?.nativeElement.click();
    }
    inputHandler(event) {
        this.handleFiles(event.target.files);
    }
    handleFiles(files) {
        if (!files?.length) {
            return;
        }
        const newFiles = [];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < files.length; i++) {
            newFiles.push(this.makeUploadFile(files[i]));
        }
        newFiles.sort((a, b) => a.fileName.localeCompare(b.fileName));
        this.filesAdded.emit(newFiles);
    }
    reset() {
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }
    makeUploadFile(file) {
        const uploadFile = {
            fileName: file.name,
            file,
            size: this.fileSizeToString(file.size),
            progress: 0,
            status: 'added'
        };
        // use MIME type of file if set. Otherwise fall back to file name ending
        const ext = '.' + uploadFile.file.name.split('.').pop();
        if (!this.verifyFileType(uploadFile.file.type, ext)) {
            uploadFile.status = 'invalid';
            uploadFile.errorText = this.errorTextFileType;
        }
        else if (!this.verifyFileSize(uploadFile.file.size)) {
            uploadFile.status = 'invalid';
            uploadFile.errorText = this.errorTextFileMaxSize;
        }
        return uploadFile;
    }
    verifyFileSize(size) {
        return !this.maxFileSize || size <= this.maxFileSize;
    }
    verifyFileType(fileType, ext) {
        if (!this.accept) {
            return true;
        }
        if (fileType === undefined && ext === undefined) {
            return false;
        }
        // Spec says that comma is the delimiter for filetypes. Also allow pipe for compatibility
        return this.accept.split(/,|\|/).some(acceptedType => {
            // convert accept glob into regex (example: images/* --> images/.*)
            const acceptedRegex = acceptedType.replace('.', '\.').replace('*', '.*').trim();
            // if fileType is set and accepted type looks like a MIME type, match that otherwise extension
            if (fileType && acceptedType.includes('/')) {
                return !!fileType.match(acceptedRegex);
            }
            return !!ext?.match(acceptedRegex);
        });
    }
    fileSizeToString(num) {
        let suffix = 'B';
        if (num >= 1073741824) {
            num /= 1073741824;
            suffix = 'GB';
        }
        if (num >= 1048576) {
            num /= 1048576;
            suffix = 'MB';
        }
        else if (num >= 1024) {
            num /= 1024;
            suffix = 'KB';
        }
        return this.numberFormat.format(num) + suffix;
    }
}
SiFileDropzoneComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileDropzoneComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFileDropzoneComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFileDropzoneComponent, selector: "si-file-dropzone", inputs: { uploadTextFileSelect: "uploadTextFileSelect", uploadDropText: "uploadDropText", maxFileSizeText: "maxFileSizeText", acceptText: "acceptText", errorTextFileType: "errorTextFileType", errorTextFileMaxSize: "errorTextFileMaxSize", accept: "accept", maxFileSize: "maxFileSize", multiple: "multiple" }, outputs: { filesAdded: "filesAdded" }, viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"drag-and-drop rounded p-6\"\n  [class.drag-over]=\"dragOver\"\n  (drop)=\"dropHandler($event)\"\n  (dragover)=\"dragOverHandler($event)\"\n  (dragleave)=\"dragOver = false\"\n>\n  <i class=\"display-6 element-upload mb-6\" role=\"presentation\"></i>\n  <span class=\"drag-and-drop-description si-title-2 d-flex\">\n    <span>{{ uploadDropText | translate }}</span>\n    &nbsp;\n    <label class=\"select-file mb-0\">\n      <span\n        class=\"select-file\"\n        tabindex=\"0\"\n        (keydown.enter)=\"inputEnterHandler()\"\n      >{{ uploadTextFileSelect | translate }}</span>\n      <input\n        #fileInput\n        type=\"file\"\n        tabindex=\"-1\"\n        class=\"d-none\"\n        [accept]=\"accept\"\n        [multiple]=\"multiple\"\n        (change)=\"inputHandler($event)\"\n      >\n    </label>\n  </span>\n  <div *ngIf=\"maxFileSize || accept\" class=\"allowed si-caption mt-5\">\n    <ng-container *ngIf=\"maxFileSize\">\n      {{ maxFileSizeText | translate }}: {{ maxFileSizeString }}.\n    </ng-container>\n    <ng-container *ngIf=\"accept\">\n      {{ acceptText | translate }}: {{ accept }}.\n    </ng-container>\n  </div>\n</div>\n", styles: [":host{display:block}.select-file{margin-block-end:0;cursor:pointer;font-weight:700;color:var(--element-ui-0)}.select-file:hover,.select-file:active{text-decoration:underline}.drag-and-drop{background-color:var(--element-base-1);border:1px dashed var(--element-ui-2);display:flex;flex-direction:column;justify-content:center;align-items:center}.drag-and-drop.drag-over{border:1px solid var(--element-focus-default);box-shadow:0 0 0 1px var(--element-focus-default)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileDropzoneComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-file-dropzone', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"drag-and-drop rounded p-6\"\n  [class.drag-over]=\"dragOver\"\n  (drop)=\"dropHandler($event)\"\n  (dragover)=\"dragOverHandler($event)\"\n  (dragleave)=\"dragOver = false\"\n>\n  <i class=\"display-6 element-upload mb-6\" role=\"presentation\"></i>\n  <span class=\"drag-and-drop-description si-title-2 d-flex\">\n    <span>{{ uploadDropText | translate }}</span>\n    &nbsp;\n    <label class=\"select-file mb-0\">\n      <span\n        class=\"select-file\"\n        tabindex=\"0\"\n        (keydown.enter)=\"inputEnterHandler()\"\n      >{{ uploadTextFileSelect | translate }}</span>\n      <input\n        #fileInput\n        type=\"file\"\n        tabindex=\"-1\"\n        class=\"d-none\"\n        [accept]=\"accept\"\n        [multiple]=\"multiple\"\n        (change)=\"inputHandler($event)\"\n      >\n    </label>\n  </span>\n  <div *ngIf=\"maxFileSize || accept\" class=\"allowed si-caption mt-5\">\n    <ng-container *ngIf=\"maxFileSize\">\n      {{ maxFileSizeText | translate }}: {{ maxFileSizeString }}.\n    </ng-container>\n    <ng-container *ngIf=\"accept\">\n      {{ acceptText | translate }}: {{ accept }}.\n    </ng-container>\n  </div>\n</div>\n", styles: [":host{display:block}.select-file{margin-block-end:0;cursor:pointer;font-weight:700;color:var(--element-ui-0)}.select-file:hover,.select-file:active{text-decoration:underline}.drag-and-drop{background-color:var(--element-base-1);border:1px dashed var(--element-ui-2);display:flex;flex-direction:column;justify-content:center;align-items:center}.drag-and-drop.drag-over{border:1px solid var(--element-focus-default);box-shadow:0 0 0 1px var(--element-focus-default)}\n"] }]
        }], propDecorators: { uploadTextFileSelect: [{
                type: Input
            }], uploadDropText: [{
                type: Input
            }], maxFileSizeText: [{
                type: Input
            }], acceptText: [{
                type: Input
            }], errorTextFileType: [{
                type: Input
            }], errorTextFileMaxSize: [{
                type: Input
            }], accept: [{
                type: Input
            }], maxFileSize: [{
                type: Input
            }], multiple: [{
                type: Input
            }], filesAdded: [{
                type: Output
            }], fileInput: [{
                type: ViewChild,
                args: ['fileInput']
            }] } });

class SiFileUploaderComponent {
    constructor() {
        /**
         * Text of the link to open the file select dialog (follows `uploadDropText`).
         */
        this.uploadTextFileSelect = 'click to upload';
        /**
         * Text instructing a user to drop the files inside the dropzone.
         */
        this.uploadDropText = 'Drop files here or';
        /**
         * Text to describe the maximum file size.
         */
        this.maxFileSizeText = 'Maximum upload size';
        /**
         * Error message shown when the maximum number of files are reached.
         */
        this.maxFilesReachedText = 'Maximum number of files reached';
        /**
         * Text for the accepted file types.
         */
        this.acceptText = 'Accepted file types';
        /**
         * Text used inside the upload button.
         */
        this.uploadButtonText = 'Upload';
        /**
         * Text used inside the clear button.
         */
        this.clearButtonText = 'Clear';
        /**
         * Text shown during the file upload.
         */
        this.uploadingText = 'Uploading';
        /**
         * Text shown to remove a file from the file list. Required for a11y.
         */
        this.removeButtonText = 'Remove';
        /**
         * Text of cancel button. Shown during upload. Required for a11y.
         */
        this.cancelButtonText = 'Cancel';
        /**
         * Text shown if the upload was successful.
         */
        this.successTextTitle = 'Upload completed';
        /**
         * Text shown if the upload failed.
         */
        this.errorUploadFailed = 'Upload failed';
        /**
         * On failed upload, show the error received from the server.
         */
        this.showHttpError = false;
        /**
         * Text shown to indicate that an incorrect file type was added to file list.
         */
        this.errorTextFileType = 'Incorrect file type selected';
        /**
         * Message or translation key if file exceeds the maximum file size limit.
         */
        this.errorTextFileMaxSize = 'File exceeds allowed maximum size';
        /**
         * Config for HTTP request to upload file.
         */
        this.uploadConfig = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: new HttpHeaders({ 'Accept': 'application/json' }),
            method: 'POST',
            url: '',
            fieldName: 'upload_file',
            responseType: 'json'
        };
        /**
         * Define maximal allowed number of files.
         */
        this.maxFiles = 10;
        /**
         * Maximum number of concurrent uploads.
         */
        this.maxConcurrentUploads = 3;
        /**
         * Numbers of retries for failed uploads.
         */
        this.retries = 0;
        /**
         * Auto-upload mode - automatically start upload once files are added.
         */
        this.autoUpload = false;
        /**
         * Disable the upload button.
         */
        this.disableUpload = false;
        /**
         * Output callback event will provide you if upload is finished. If an error
         * occurred it will be emitted.
         */
        this.uploadCompleted = new EventEmitter(true);
        /**
         * Output which fires whenever new files are added to or removed from the uploader.
         */
        this.filesChanges = new EventEmitter(true);
        this.files = [];
        this.pending = 0;
        this.uploading = 0;
        this.uploadEnabled = false;
        this.maxFilesReached = false;
        this.trackByItem = (_index, item) => item;
        this.cdRef = inject(ChangeDetectorRef);
        this.http = inject(HttpClient, { optional: true });
    }
    ngOnChanges(changes) {
        if (changes.maxFiles || changes.disableUpload) {
            this.updateStates();
        }
    }
    handleFiles(files) {
        if (!files?.length) {
            return;
        }
        // for single-file case, replace exiting file if any
        if (this.maxFiles === 1 && this.files.length) {
            this.reset(false);
        }
        let numValid = this.countValid();
        for (const file of files) {
            if (this.isDuplicate(file)) {
                continue;
            }
            const canAdd = numValid + 1 <= this.maxFiles;
            const valid = file.status === 'added';
            if (valid && !canAdd) {
                this.maxFilesReached = true;
                break;
            }
            else if (valid) {
                numValid++;
            }
            this.files.push(file);
        }
        this.files.sort((a, b) => a.fileName.localeCompare(b.fileName));
        this.filesChanges.emit(this.files.slice());
        this.updateStates();
        if (this.autoUpload) {
            this.fileUpload(false);
        }
    }
    removeFile(index) {
        if (index >= 0) {
            this.files.splice(index, 1);
            this.filesChanges.emit(this.files.slice());
            this.dropZone?.reset();
            this.updateStates();
        }
    }
    cancelUpload(file) {
        if (file.subscription) {
            file.subscription.unsubscribe();
            this.pending--;
        }
        file.status = 'added';
        file.progress = 0;
        this.updateStates();
    }
    retryUpload(file) {
        file.status = 'added';
        this.doUpload([file], true);
    }
    reset(emit = true) {
        this.files.forEach(f => f.subscription?.unsubscribe());
        this.files = [];
        this.dropZone?.reset();
        this.updateStates();
        if (emit) {
            this.filesChanges.emit([]);
        }
    }
    fileUpload(doRetry = true) {
        if (!this.uploadEnabled) {
            return;
        }
        this.uploadEnabled = false;
        this.doUpload(this.files, doRetry);
    }
    doUpload(files, doRetry) {
        for (const file of files) {
            if (file.status !== 'added' && (!doRetry || file.status !== 'error')) {
                continue;
            }
            this.pending++;
            file.status = 'queued';
        }
        this.processQueue();
    }
    processQueue() {
        for (let i = 0; i < this.files.length && this.uploading < this.maxConcurrentUploads; i++) {
            const file = this.files[i];
            if (file.status === 'queued') {
                this.uploading++;
                this.uploadOneFile(file);
            }
        }
    }
    uploadOneFile(file) {
        let formData;
        if (!this.uploadConfig.sendBinary) {
            formData = new FormData();
            formData.append(this.uploadConfig.fieldName, file.file, file.fileName);
            if (this.uploadConfig.additionalFields) {
                Object.keys(this.uploadConfig.additionalFields).forEach(key => {
                    formData.append(key, this.uploadConfig.additionalFields[key]);
                });
            }
        }
        const req = new HttpRequest(this.uploadConfig.method, this.uploadConfig.url, formData ?? file.file, {
            headers: this.uploadConfig.headers,
            responseType: this.uploadConfig.responseType,
            reportProgress: true
        });
        file.status = 'uploading';
        file.errorText = undefined;
        file.httpErrorText = undefined;
        const requestHandler = this.uploadConfig.handler ??
            (this.http ? ((r) => this.http.request(r)) : undefined);
        if (!requestHandler) {
            return;
        }
        file.subscription = requestHandler(req).pipe(retry(this.retries)).subscribe(event => this.handleUploadEvent(file, event), (error) => this.handleUploadError(file, error), () => this.handleUploadComplete(file));
    }
    // this is a light check for duplicate file - name and size only, not content!
    isDuplicate(file) {
        for (const uploadFile of this.files) {
            if (uploadFile.file.name === file.file.name && uploadFile.file.size === file.file.size) {
                return true;
            }
        }
        return false;
    }
    handleUploadEvent(file, httpEvent) {
        if (httpEvent instanceof HttpResponse) {
            file.successResponse = httpEvent;
        }
        else if (httpEvent.type === HttpEventType.UploadProgress && httpEvent.total) {
            file.progress = Math.floor(100 * httpEvent.loaded / httpEvent.total);
            this.cdRef.markForCheck();
        }
    }
    handleUploadError(file, error) {
        this.uploadCompleted.emit({ file: file.fileName, error });
        file.status = 'error';
        file.errorText = this.errorUploadFailed;
        if (this.showHttpError && error.status && error.statusText) {
            file.httpErrorText = `${error.status}: ${error.statusText}`;
        }
        this.oneUploadDone(file);
    }
    handleUploadComplete(file) {
        this.uploadCompleted.emit({ file: file.fileName, response: file.successResponse });
        file.status = 'success';
        file.progress = 100;
        file.successResponse = undefined;
        if (this.autoUpload) {
            this.fadeOut(file);
        }
        this.oneUploadDone(file);
    }
    oneUploadDone(file) {
        file.subscription = undefined;
        this.pending--;
        this.uploading--;
        this.updateStates();
        this.processQueue();
    }
    fadeOut(file) {
        setTimeout(() => {
            file.fadeOut = true;
            this.cdRef.markForCheck();
            setTimeout(() => {
                this.removeFile(this.files.indexOf(file));
                this.cdRef.markForCheck();
            }, 500);
            this.cdRef.markForCheck();
        }, 3500);
    }
    updateStates() {
        this.uploadEnabled = !this.disableUpload && !this.pending &&
            this.files.some(f => f.status === 'added' || f.status === 'error');
        if (this.maxFilesReached && this.countValid() < this.maxFiles) {
            this.maxFilesReached = false;
        }
    }
    countValid() {
        return this.files.reduce((acc, f) => acc + (f.status !== 'invalid' ? 1 : 0), 0);
    }
}
SiFileUploaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFileUploaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFileUploaderComponent, selector: "si-file-uploader", inputs: { uploadTextFileSelect: "uploadTextFileSelect", uploadDropText: "uploadDropText", maxFileSizeText: "maxFileSizeText", maxFilesReachedText: "maxFilesReachedText", acceptText: "acceptText", uploadButtonText: "uploadButtonText", clearButtonText: "clearButtonText", uploadingText: "uploadingText", removeButtonText: "removeButtonText", cancelButtonText: "cancelButtonText", successTextTitle: "successTextTitle", errorUploadFailed: "errorUploadFailed", showHttpError: "showHttpError", errorTextFileType: "errorTextFileType", errorTextFileMaxSize: "errorTextFileMaxSize", uploadConfig: "uploadConfig", accept: "accept", maxFileSize: "maxFileSize", maxFiles: "maxFiles", maxConcurrentUploads: "maxConcurrentUploads", retries: "retries", autoUpload: "autoUpload", disableUpload: "disableUpload" }, outputs: { uploadCompleted: "uploadCompleted", filesChanges: "filesChanges" }, viewQueries: [{ propertyName: "dropZone", first: true, predicate: ["dropZone"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<si-file-dropzone\n  #dropZone\n  class=\"mb-4\"\n  [uploadTextFileSelect]=\"uploadTextFileSelect\"\n  [uploadDropText]=\"uploadDropText\"\n  [multiple]=\"maxFiles > 1\"\n  [maxFileSizeText]=\"maxFileSizeText\"\n  [acceptText]=\"acceptText\"\n  [errorTextFileType]=\"errorTextFileType\"\n  [errorTextFileMaxSize]=\"errorTextFileMaxSize\"\n  [accept]=\"accept\"\n  [maxFileSize]=\"maxFileSize\"\n  (filesAdded)=\"handleFiles($event)\"\n></si-file-dropzone>\n\n<si-inline-notification\n  *ngIf=\"maxFilesReached\"\n  class=\"mb-4\"\n  severity=\"info\"\n  [message]=\"maxFilesReachedText\"\n></si-inline-notification>\n\n<div class=\"file-list\">\n  <div\n    *ngFor=\"let file of files; index as i; trackBy: trackByItem\"\n    class=\"file d-flex\"\n    [class.fade-out]=\"file.fadeOut\"\n  >\n    <i class=\"icon align-self-center ms-6 me-4 element-document\" role=\"presentation\"></i>\n    <div class=\"flex-fill overflow-hidden py-3\">\n      <div class=\"file-name text-truncate fw-bold\">{{ file.fileName }}</div>\n      <div class=\"file-size text-secondary\">{{ file.size }}</div>\n      <div\n        *ngIf=\"file.errorText\"\n        class=\"error text-truncate d-flex align-items-center\"\n        [class.text-warning]=\"file.status === 'invalid'\"\n        [class.text-danger]=\"file.status === 'error'\"\n      >\n        <si-icon\n          class=\"d-inline-block my-n2 me-2\"\n          size=\"icon-small\"\n          [icon]=\"file.status === 'invalid' ? 'element-triangle-filled' : 'element-circle-filled'\"\n          [color]=\"file.status === 'invalid' ? 'status-warning' : 'status-danger'\"\n          stackedIcon=\"element-state-exclamation-mark smooth-auto\"\n          [stackedColor]=\"file.status === 'invalid' ? 'status-warning-contrast' : 'status-danger-contrast'\"\n          alt=\"\"\n        ></si-icon>\n        {{ file.errorText | translate }}<ng-container *ngIf=\"file.httpErrorText\">: {{ file.httpErrorText }}</ng-container>\n      </div>\n      <div *ngIf=\"!file.errorText\" class=\"pt-3 pb-5 position-relative\">\n        <div class=\"position-absolute si-caption end-0 mt-n8\">{{ file.progress }} %</div>\n        <si-progressbar height=\"small\" [value]=\"file.progress\" [ariaLabel]=\"uploadingText\"></si-progressbar>\n      </div>\n    </div>\n    <div class=\"status align-self-center text-nowrap px-5\">\n      <button\n        *ngIf=\"file.status === 'error'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-tertiary element-redo me-2\"\n        [disabled]=\"!uploadEnabled\"\n        [title]=\"uploadButtonText | translate\"\n        [attr.aria-label]=\"uploadButtonText | translate\"\n        (click)=\"retryUpload(file)\"\n      ></button>\n      <button\n        *ngIf=\"file.status === 'added' || file.status === 'error' || file.status === 'invalid'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-ghost element-delete\"\n        [title]=\"removeButtonText | translate\"\n        [attr.aria-label]=\"removeButtonText | translate\"\n        (click)=\"removeFile(i)\"\n      ></button>\n      <button\n        *ngIf=\"file.status === 'uploading' || file.status === 'queued'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-tertiary element-cancel\"\n        [title]=\"cancelButtonText | translate\"\n        [attr.aria-label]=\"cancelButtonText | translate\"\n        (click)=\"cancelUpload(file)\"\n      ></button>\n      <si-icon\n        *ngIf=\"file.status === 'success'\"\n        class=\"mx-2\"\n        icon=\"element-circle-filled\"\n        color=\"status-success\"\n        stackedIcon=\"element-state-tick smooth-auto\"\n        stackedColor=\"status-success-contrast\"\n        [alt]=\"successTextTitle\"\n      ></si-icon>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!autoUpload\" class=\"buttons text-end mt-4\">\n  <button\n    type=\"button\"\n    class=\"btn btn-secondary me-4\"\n    [disabled]=\"pending || !files.length\"\n    (click)=\"reset()\"\n  >{{ clearButtonText | translate }}</button>\n  <button\n    type=\"button\"\n    class=\"btn btn-primary\"\n    [disabled]=\"!uploadEnabled\"\n    (click)=\"fileUpload()\"\n  >{{ uploadButtonText | translate }}</button>\n</div>\n", styles: [":host{display:flex;flex-direction:column;justify-content:center;align-items:stretch}:host>*{min-inline-size:320px;max-inline-size:680px;inline-size:100%;align-self:center}.file-list{overflow-y:auto}.file{transition:opacity calc(.5s * var(--element-animations-enabled, 1))}.fade-out{opacity:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "component", type: SiInlineNotificationComponent, selector: "si-inline-notification", inputs: ["severity", "heading", "message", "action", "translationParams", "embedded"] }, { kind: "component", type: SiProgressbarComponent, selector: "si-progressbar", inputs: ["ariaLabel", "max", "value", "heading", "progress", "height"] }, { kind: "component", type: SiFileDropzoneComponent, selector: "si-file-dropzone", inputs: ["uploadTextFileSelect", "uploadDropText", "maxFileSizeText", "acceptText", "errorTextFileType", "errorTextFileMaxSize", "accept", "maxFileSize", "multiple"], outputs: ["filesAdded"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-file-uploader', template: "<si-file-dropzone\n  #dropZone\n  class=\"mb-4\"\n  [uploadTextFileSelect]=\"uploadTextFileSelect\"\n  [uploadDropText]=\"uploadDropText\"\n  [multiple]=\"maxFiles > 1\"\n  [maxFileSizeText]=\"maxFileSizeText\"\n  [acceptText]=\"acceptText\"\n  [errorTextFileType]=\"errorTextFileType\"\n  [errorTextFileMaxSize]=\"errorTextFileMaxSize\"\n  [accept]=\"accept\"\n  [maxFileSize]=\"maxFileSize\"\n  (filesAdded)=\"handleFiles($event)\"\n></si-file-dropzone>\n\n<si-inline-notification\n  *ngIf=\"maxFilesReached\"\n  class=\"mb-4\"\n  severity=\"info\"\n  [message]=\"maxFilesReachedText\"\n></si-inline-notification>\n\n<div class=\"file-list\">\n  <div\n    *ngFor=\"let file of files; index as i; trackBy: trackByItem\"\n    class=\"file d-flex\"\n    [class.fade-out]=\"file.fadeOut\"\n  >\n    <i class=\"icon align-self-center ms-6 me-4 element-document\" role=\"presentation\"></i>\n    <div class=\"flex-fill overflow-hidden py-3\">\n      <div class=\"file-name text-truncate fw-bold\">{{ file.fileName }}</div>\n      <div class=\"file-size text-secondary\">{{ file.size }}</div>\n      <div\n        *ngIf=\"file.errorText\"\n        class=\"error text-truncate d-flex align-items-center\"\n        [class.text-warning]=\"file.status === 'invalid'\"\n        [class.text-danger]=\"file.status === 'error'\"\n      >\n        <si-icon\n          class=\"d-inline-block my-n2 me-2\"\n          size=\"icon-small\"\n          [icon]=\"file.status === 'invalid' ? 'element-triangle-filled' : 'element-circle-filled'\"\n          [color]=\"file.status === 'invalid' ? 'status-warning' : 'status-danger'\"\n          stackedIcon=\"element-state-exclamation-mark smooth-auto\"\n          [stackedColor]=\"file.status === 'invalid' ? 'status-warning-contrast' : 'status-danger-contrast'\"\n          alt=\"\"\n        ></si-icon>\n        {{ file.errorText | translate }}<ng-container *ngIf=\"file.httpErrorText\">: {{ file.httpErrorText }}</ng-container>\n      </div>\n      <div *ngIf=\"!file.errorText\" class=\"pt-3 pb-5 position-relative\">\n        <div class=\"position-absolute si-caption end-0 mt-n8\">{{ file.progress }} %</div>\n        <si-progressbar height=\"small\" [value]=\"file.progress\" [ariaLabel]=\"uploadingText\"></si-progressbar>\n      </div>\n    </div>\n    <div class=\"status align-self-center text-nowrap px-5\">\n      <button\n        *ngIf=\"file.status === 'error'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-tertiary element-redo me-2\"\n        [disabled]=\"!uploadEnabled\"\n        [title]=\"uploadButtonText | translate\"\n        [attr.aria-label]=\"uploadButtonText | translate\"\n        (click)=\"retryUpload(file)\"\n      ></button>\n      <button\n        *ngIf=\"file.status === 'added' || file.status === 'error' || file.status === 'invalid'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-ghost element-delete\"\n        [title]=\"removeButtonText | translate\"\n        [attr.aria-label]=\"removeButtonText | translate\"\n        (click)=\"removeFile(i)\"\n      ></button>\n      <button\n        *ngIf=\"file.status === 'uploading' || file.status === 'queued'\"\n        type=\"button\"\n        class=\"btn btn-circle btn-sm btn-tertiary element-cancel\"\n        [title]=\"cancelButtonText | translate\"\n        [attr.aria-label]=\"cancelButtonText | translate\"\n        (click)=\"cancelUpload(file)\"\n      ></button>\n      <si-icon\n        *ngIf=\"file.status === 'success'\"\n        class=\"mx-2\"\n        icon=\"element-circle-filled\"\n        color=\"status-success\"\n        stackedIcon=\"element-state-tick smooth-auto\"\n        stackedColor=\"status-success-contrast\"\n        [alt]=\"successTextTitle\"\n      ></si-icon>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!autoUpload\" class=\"buttons text-end mt-4\">\n  <button\n    type=\"button\"\n    class=\"btn btn-secondary me-4\"\n    [disabled]=\"pending || !files.length\"\n    (click)=\"reset()\"\n  >{{ clearButtonText | translate }}</button>\n  <button\n    type=\"button\"\n    class=\"btn btn-primary\"\n    [disabled]=\"!uploadEnabled\"\n    (click)=\"fileUpload()\"\n  >{{ uploadButtonText | translate }}</button>\n</div>\n", styles: [":host{display:flex;flex-direction:column;justify-content:center;align-items:stretch}:host>*{min-inline-size:320px;max-inline-size:680px;inline-size:100%;align-self:center}.file-list{overflow-y:auto}.file{transition:opacity calc(.5s * var(--element-animations-enabled, 1))}.fade-out{opacity:0}\n"] }]
        }], propDecorators: { uploadTextFileSelect: [{
                type: Input
            }], uploadDropText: [{
                type: Input
            }], maxFileSizeText: [{
                type: Input
            }], maxFilesReachedText: [{
                type: Input
            }], acceptText: [{
                type: Input
            }], uploadButtonText: [{
                type: Input
            }], clearButtonText: [{
                type: Input
            }], uploadingText: [{
                type: Input
            }], removeButtonText: [{
                type: Input
            }], cancelButtonText: [{
                type: Input
            }], successTextTitle: [{
                type: Input
            }], errorUploadFailed: [{
                type: Input
            }], showHttpError: [{
                type: Input
            }], errorTextFileType: [{
                type: Input
            }], errorTextFileMaxSize: [{
                type: Input
            }], uploadConfig: [{
                type: Input
            }], accept: [{
                type: Input
            }], maxFileSize: [{
                type: Input
            }], maxFiles: [{
                type: Input
            }], maxConcurrentUploads: [{
                type: Input
            }], retries: [{
                type: Input
            }], autoUpload: [{
                type: Input
            }], disableUpload: [{
                type: Input
            }], uploadCompleted: [{
                type: Output
            }], filesChanges: [{
                type: Output
            }], dropZone: [{
                type: ViewChild,
                args: ['dropZone']
            }] } });

class SiFileUploaderModule {
}
SiFileUploaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFileUploaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderModule, declarations: [SiFileDropzoneComponent, SiFileUploaderComponent], imports: [CommonModule,
        SiIconModule,
        SiInlineNotificationModule,
        SiProgressbarModule,
        SiTranslateModule], exports: [SiFileDropzoneComponent, SiFileUploaderComponent] });
SiFileUploaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderModule, imports: [CommonModule,
        SiIconModule,
        SiInlineNotificationModule,
        SiProgressbarModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFileUploaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiInlineNotificationModule,
                        SiProgressbarModule,
                        SiTranslateModule
                    ],
                    declarations: [SiFileDropzoneComponent, SiFileUploaderComponent],
                    exports: [SiFileDropzoneComponent, SiFileUploaderComponent]
                }]
        }] });

// -------------------------------------------------------------------------------

class SiFormContainerComponent {
    constructor() {
        /**
         * A form container in readonly mode is only displaying the form content without ability
         * to change it. The edit panel with typically save and cancel buttons is hidden. Set
         * to true to display the edit panel.
         */
        this.readonly = false;
        /**
         * The title of the help link that is used to display the form validation problems within a popover.
         */
        this.helpTitle = 'Help';
        this.enableValidationHelp = false;
        /**
         * In some scenarios, one may not want the form container to be responsible for the layout relevant
         * `si-container-[xs|...]` classes, but let this be done by a different, nested component, e.g. by a
         * group box. In these cases, the property should be set to true.
         */
        this.disableContainerBreakpoints = false;
        /**
         * Every validation error has an errorCode. This map holds translate keys for error codes. The keys can
         * be used to display a translated message for each validation error. The defaults old english readable
         * key defaults for the Angular standard validators of the `Validators` class like `min`, `max` or `required`.
         *
         * Use the input to set your own translate keys for the form validators you need.
         */
        this.errorCodeTranslateKeyMap = new Map([
            ['min', 'The value is too small.'],
            ['max', 'The value is too large.'],
            ['required', 'A value is required.'],
            ['requiredTrue', 'The value should be true.'],
            ['email', 'The email is not valid.'],
            ['minlength', 'The minimum number of characters is not met.'],
            ['maxlength', 'A maximum number of characters is exceeded.'],
            ['pattern', 'The value does not match the predefined pattern.']
        ]);
        /**
         * A map the maps from control names of the form to their translate keys.
         * The initial map is empty and the user is responsible to add the required
         * translate keys.
         */
        this.controlNameTranslateKeyMap = new Map();
        this.trackByIndex = (index) => index;
    }
    /**
     * Indicates whether the user interacted with the form.
     * @returns `true`, if the user selected at least one form element and
     * [blurred](https://developer.mozilla.org/en-US/docs/Web/API/Element/blur_event) by losing focus again
     * (e.g. by setting focus on another element), or by editing the content of a form element.
     * Otherwise `false`.
     */
    get userInteractedWithForm() {
        return !!this.form && (this.form.dirty || this.form.touched);
    }
    /**
     * Indicates whether content editor message shall be styled as success.
     * @returns `true`, if {@link SiFormContainerComponent.userInteractedWithForm} is true and
     * the form is valid.
     */
    get validFormContainerMessage() {
        return this.userInteractedWithForm && this.form.status === 'VALID';
    }
    /**
     * Indicates whether the content editor message shall be styled as warning.
     * @returns `true`, if {@link SiFormContainerComponent.userInteractedWithForm} is true and
     * the form is invalid.
     */
    get invalidFormContainerMessage() {
        return this.userInteractedWithForm && this.form.status === 'INVALID';
    }
    /**
     * Returns the validation errors of the form's control when the control name is provided. Otherwise,
     * returns all validation errors of the form. Returns an empty arry when no form is available or if
     * the name does not match to a control.
     * @param controlName An optional name of a control that is part of the form.
     */
    getValidationErrors(controlName) {
        if (!this.form) {
            return [];
        }
        else if (!controlName) {
            return this.getFormValidationErrorsPrivate(this.form);
        }
        else {
            const control = this.form.get(controlName);
            if (control) {
                return this.getFormValidationErrorsPrivate(control, controlName);
            }
            else {
                return [];
            }
        }
    }
    getControlNameTranslateKey(controlName) {
        if (!this.controlNameTranslateKeyMap) {
            return undefined;
        }
        return this.controlNameTranslateKeyMap.get(controlName) ?? controlName;
    }
    getErrorCodeTranslateKey(errorCode, controlName) {
        if (!this.errorCodeTranslateKeyMap) {
            return undefined;
        }
        else if (!controlName) {
            return this.errorCodeTranslateKeyMap.get(errorCode);
        }
        else {
            return this.errorCodeTranslateKeyMap.get(this.getControlErrorCodeTranslationKey(controlName, errorCode)) ??
                this.errorCodeTranslateKeyMap.get(errorCode);
        }
    }
    getControlErrorCodeTranslationKey(controlName, errorCode) {
        return `${controlName}.${errorCode}`;
    }
    getFormValidationErrorsPrivate(control, controlName) {
        let errors = [];
        // a form must either consist of
        // a) a formgroup (with nested form controls or groups) or
        // b) a single form control
        if (control instanceof FormGroup) {
            const formGroupErrors = this.getFormGroupErrors(control);
            errors = errors.concat(formGroupErrors);
            if (control.controls) {
                Object.keys(control.controls).forEach(key => {
                    const formGroupControl = control.controls[key];
                    const formGroupControlErrors = this.getFormValidationErrorsPrivate(formGroupControl, key);
                    errors = errors.concat(formGroupControlErrors);
                });
            }
        }
        else if (control instanceof FormControl) {
            const errorCodes = control.errors;
            if (errorCodes) {
                Object.keys(errorCodes).forEach(errorCode => {
                    errors.push({
                        controlName,
                        controlNameTranslationKey: controlName ? this.getControlNameTranslateKey(controlName) : undefined,
                        errorCode,
                        errorCodeTranslationKey: this.getErrorCodeTranslateKey(errorCode, controlName),
                        errorParams: errorCodes[errorCode]
                    });
                });
            }
        }
        return errors;
    }
    getFormGroupErrors(formGroup) {
        const errors = [];
        const errorCodes = formGroup.errors;
        if (errorCodes) {
            Object.keys(errorCodes).forEach(errorCode => {
                errors.push({
                    errorCode,
                    errorCodeTranslationKey: this.getErrorCodeTranslateKey(errorCode),
                    errorParams: errorCodes[errorCode]
                });
            });
        }
        return errors;
    }
}
SiFormContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFormContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFormContainerComponent, selector: "si-form-container", inputs: { form: "form", readonly: "readonly", contentContainerBreakpoints: "contentContainerBreakpoints", helpTitle: "helpTitle", enableValidationHelp: "enableValidationHelp", disableContainerBreakpoints: "disableContainerBreakpoints", errorCodeTranslateKeyMap: "errorCodeTranslateKeyMap", controlNameTranslateKeyMap: "controlNameTranslateKeyMap" }, ngImport: i0, template: "<div class=\"d-flex flex-column align-content-stretch flex-grow-1 flex-shrink-1\">\n\n  <ng-container [ngSwitch]=\"disableContainerBreakpoints\">\n    <div *ngSwitchCase=\"true\" class=\"flex-grow-1 flex-shrink-1 overflow-auto\">\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n    <div *ngSwitchCase=\"false\"\n      class=\"flex-grow-1 flex-shrink-1 overflow-auto\"\n      siResponsiveContainer\n      [breakpoints]=\"contentContainerBreakpoints\"\n    >\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div *ngIf=\"!readonly\" class=\"d-flex flex-row flex-nowrap flex-grow-0 flex-shrink-0 p-4\">\n    <div class=\"d-flex flex-grow-1 flex-shrink-1 justify-content-end text-end overflow-hidden mx-6 my-auto\"\n      [class.text-success]=\"validFormContainerMessage\"\n      [class.text-danger]=\"invalidFormContainerMessage\">\n      <ng-content select=\"[si-form-container-message]\"></ng-content>\n    </div>\n\n    <ng-container *ngIf=\"enableValidationHelp && getValidationErrors() && invalidFormContainerMessage\">\n      <ng-template #popoverContent>\n        <ul class=\"p-0 list-unstyled\">\n          <li\n            *ngFor=\"let error of getValidationErrors(); trackBy: trackByIndex\"\n            class=\"form-container-help-popover-error\"\n          >\n            <b *ngIf=\"error.controlNameTranslationKey\">{{ error.controlNameTranslationKey | translate }}: </b>\n            <span *ngIf=\"error.errorCode\">\n              {{ (error.errorCodeTranslationKey || error.errorCode) | translate:error.errorParams }}\n            </span>\n          </li>\n        </ul>\n      </ng-template>\n      <span class=\"form-container-edit-help ms-0 me-6 my-auto\"\n        [innerHTML]=\"helpTitle | translate\"\n        [siPopover]=\"popoverContent\"\n        triggers=\"click\"\n        #popoverTrigger\n        (keydown.enter)=\"popoverTrigger.click()\"\n        containerClass=\"form-container-help-popover\"\n        tabindex=\"0\"></span>\n    </ng-container>\n\n    <div class=\"m-auto flex-grow-0 flex-shrink-0\">\n      <ng-content select=\"[si-form-container-buttons]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #contentTemplate>\n  <ng-content select=\"[si-form-container-content]\"></ng-content>\n</ng-template>\n", styles: [":host{display:flex;flex:1 1 auto;flex-direction:column}:host ::slotted([si-form-container-message]),:host ::ng-deep [si-form-container-message]{max-inline-size:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.form-container-edit-help{text-decoration:underline;cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: SiPopoverDirective, selector: "[siPopover]", inputs: ["siPopover", "placement", "triggers", "popoverTitle", "containerClass", "outsideClick", "icon", "isOpen"], outputs: ["shown", "hidden"], exportAs: ["si-popover"] }, { kind: "directive", type: SiResponsiveContainerDirective, selector: "[siResponsiveContainer]", inputs: ["resizeThrottle", "breakpoints"], exportAs: ["siResponsiveContainer"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-container', template: "<div class=\"d-flex flex-column align-content-stretch flex-grow-1 flex-shrink-1\">\n\n  <ng-container [ngSwitch]=\"disableContainerBreakpoints\">\n    <div *ngSwitchCase=\"true\" class=\"flex-grow-1 flex-shrink-1 overflow-auto\">\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n    <div *ngSwitchCase=\"false\"\n      class=\"flex-grow-1 flex-shrink-1 overflow-auto\"\n      siResponsiveContainer\n      [breakpoints]=\"contentContainerBreakpoints\"\n    >\n      <ng-container *ngTemplateOutlet=\"contentTemplate\"></ng-container>\n    </div>\n  </ng-container>\n\n  <div *ngIf=\"!readonly\" class=\"d-flex flex-row flex-nowrap flex-grow-0 flex-shrink-0 p-4\">\n    <div class=\"d-flex flex-grow-1 flex-shrink-1 justify-content-end text-end overflow-hidden mx-6 my-auto\"\n      [class.text-success]=\"validFormContainerMessage\"\n      [class.text-danger]=\"invalidFormContainerMessage\">\n      <ng-content select=\"[si-form-container-message]\"></ng-content>\n    </div>\n\n    <ng-container *ngIf=\"enableValidationHelp && getValidationErrors() && invalidFormContainerMessage\">\n      <ng-template #popoverContent>\n        <ul class=\"p-0 list-unstyled\">\n          <li\n            *ngFor=\"let error of getValidationErrors(); trackBy: trackByIndex\"\n            class=\"form-container-help-popover-error\"\n          >\n            <b *ngIf=\"error.controlNameTranslationKey\">{{ error.controlNameTranslationKey | translate }}: </b>\n            <span *ngIf=\"error.errorCode\">\n              {{ (error.errorCodeTranslationKey || error.errorCode) | translate:error.errorParams }}\n            </span>\n          </li>\n        </ul>\n      </ng-template>\n      <span class=\"form-container-edit-help ms-0 me-6 my-auto\"\n        [innerHTML]=\"helpTitle | translate\"\n        [siPopover]=\"popoverContent\"\n        triggers=\"click\"\n        #popoverTrigger\n        (keydown.enter)=\"popoverTrigger.click()\"\n        containerClass=\"form-container-help-popover\"\n        tabindex=\"0\"></span>\n    </ng-container>\n\n    <div class=\"m-auto flex-grow-0 flex-shrink-0\">\n      <ng-content select=\"[si-form-container-buttons]\"></ng-content>\n    </div>\n  </div>\n</div>\n\n<ng-template #contentTemplate>\n  <ng-content select=\"[si-form-container-content]\"></ng-content>\n</ng-template>\n", styles: [":host{display:flex;flex:1 1 auto;flex-direction:column}:host ::slotted([si-form-container-message]),:host ::ng-deep [si-form-container-message]{max-inline-size:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.form-container-edit-help{text-decoration:underline;cursor:pointer}\n"] }]
        }], propDecorators: { form: [{
                type: Input
            }], readonly: [{
                type: Input
            }], contentContainerBreakpoints: [{
                type: Input
            }], helpTitle: [{
                type: Input
            }], enableValidationHelp: [{
                type: Input
            }], disableContainerBreakpoints: [{
                type: Input
            }], errorCodeTranslateKeyMap: [{
                type: Input
            }], controlNameTranslateKeyMap: [{
                type: Input
            }] } });

class SiFormItemComponent {
    constructor() {
        /**
         * Per default, the label is suffixed with a colon. To turn this off,
         * set the value to `false`.
         */
        this.labelColon = false;
        /**
         * Per default, the label wraps into multiple lines if the text is too
         * long for its container. If you want the label to be inline (and
         * possibly clipped), set the value to `false`.
         */
        this.labelWrap = true;
        /**
         * Enables a user to hide the border around the form item.
         */
        this.hideBorder = true;
        /**
         * Use readonly to specify that the content cannot be modified.
         */
        this.readonly = false;
        /**
         * Defines that this form item is required for the overall form to be valid.
         */
        this.required = false;
        this.cdRef = inject(ChangeDetectorRef);
    }
    ngAfterViewInit() {
        this.contentChildren = this.content.nativeElement.getElementsByTagName('*');
        this.afterViewInitRespectingChangeDetection();
    }
    get displayLabel() {
        return this.label && this.label.trim().length > 0;
    }
    get displayLabelColon() {
        return this.labelColon && this.displayLabel;
    }
    afterViewInitRespectingChangeDetection() {
        setTimeout(() => {
            this.relevantInputElement = this.getRelevantInputElement();
            this.mapLabelToInput();
            this.cdRef.markForCheck();
        });
    }
    getRelevantInputElement() {
        const contentChildren = this.contentChildren ?? this.content.nativeElement.getElementsByTagName('*');
        const relevantInputTypes = ['input', 'meter', 'progress', 'select', 'textarea'];
        let currentChildElement;
        if (contentChildren) {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < contentChildren.length; i++) {
                currentChildElement = contentChildren[i];
                if (relevantInputTypes.includes(currentChildElement.nodeName.toLowerCase())) {
                    return currentChildElement;
                }
            }
        }
        return undefined;
    }
    mapLabelToInput() {
        // only necessary if we do not know the inputId
        if (!this.inputId && this.relevantInputElement && this.relevantInputElement.id) {
            this.inputId = this.relevantInputElement.id;
        }
    }
}
SiFormItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiFormItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFormItemComponent, selector: "si-form-item", inputs: { inputId: "inputId", label: "label", labelClass: "labelClass", labelColon: "labelColon", labelWidth: "labelWidth", labelWrap: "labelWrap", hideBorder: "hideBorder", contentClass: "contentClass", readonly: "readonly", required: "required" }, host: { properties: { "class.hide-border": "this.hideBorder", "class.readonly": "this.readonly", "class.required": "this.required" } }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<label\n  class=\"col-form-label\"\n  [class.required]=\"required && !readonly\"\n  [ngClass]=\"labelClass ?? ''\"\n  [attr.for]=\"inputId\"\n  [attr.title]=\"!labelWrap ? label : ''\"\n  [style.width.px]=\"(labelWidth || labelWidth === 0) ? labelWidth : ''\">\n  <span\n    *ngIf=\"displayLabel\"\n    class=\"form-item-label-text\"\n    [class.text-break]=\"labelWrap\"\n    [class.text-truncate]=\"!labelWrap\"\n    >{{ label! | translate }}<ng-container *ngIf=\"displayLabelColon\">:</ng-container></span>\n</label>\n<div\n  #content\n  class=\"form-item-content\"\n  [class.hide-border]=\"hideBorder\"\n  [ngClass]=\"contentClass ?? ''\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{flex-direction:column;border:0;padding-inline-start:2px;margin-block-end:16px;display:flex;padding-inline-end:2px}:host label,:host .form-item-content{max-inline-size:none;flex-basis:100%}:host label[style*=\"width:\"]{min-inline-size:100%}:host.readonly .col-form-label{pointer-events:none}.form-item-content{display:flex;align-items:flex-end;flex-wrap:wrap;position:relative}:host-context(.si-container-sm),:host-context(.si-container-md),:host-context(.si-container-lg),:host-context(.si-container-xl),:host-context(.si-container-xxl){flex-direction:row;box-shadow:inset 1px -1px 0 0 var(--element-ui-4),inset -1px 0 0 0 var(--element-ui-4)}:host-context(.si-container-sm) label,:host-context(.si-container-sm) .form-item-content,:host-context(.si-container-md) label,:host-context(.si-container-md) .form-item-content,:host-context(.si-container-lg) label,:host-context(.si-container-lg) .form-item-content,:host-context(.si-container-xl) label,:host-context(.si-container-xl) .form-item-content,:host-context(.si-container-xxl) label,:host-context(.si-container-xxl) .form-item-content{flex-grow:0;flex-shrink:0}:host-context(.si-container-sm) label,:host-context(.si-container-md) label,:host-context(.si-container-lg) label,:host-context(.si-container-xl) label,:host-context(.si-container-xxl) label{padding-inline:16px;margin-block-start:1px}:host-context(.si-container-sm) label[style*=\"width:\"],:host-context(.si-container-md) label[style*=\"width:\"],:host-context(.si-container-lg) label[style*=\"width:\"],:host-context(.si-container-xl) label[style*=\"width:\"],:host-context(.si-container-xxl) label[style*=\"width:\"]{flex:none;min-inline-size:0}:host-context(.si-container-sm) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-md) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-lg) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-xl) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-xxl) label[style*=\"width:\"]+.form-item-content{flex:auto}:host-context(.si-container-sm) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-md) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-lg) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-xl) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-xxl) label[style*=\"width: 0\"]>.form-item-label-text{display:none}:host-context(.si-container-sm) label:not([style*=\"width:\"]),:host-context(.si-container-md) label:not([style*=\"width:\"]),:host-context(.si-container-lg) label:not([style*=\"width:\"]),:host-context(.si-container-xl) label:not([style*=\"width:\"]),:host-context(.si-container-xxl) label:not([style*=\"width:\"]){flex-basis:16.6666666667%;max-inline-size:16.6666666667%}:host-context(.si-container-sm) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-md) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-lg) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-xl) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-xxl) label:not([style*=\"width:\"])+.form-item-content{flex-basis:83.3333333333%;max-inline-size:83.3333333333%}:host-context(.si-container-sm) .form-item-content,:host-context(.si-container-md) .form-item-content,:host-context(.si-container-lg) .form-item-content,:host-context(.si-container-xl) .form-item-content,:host-context(.si-container-xxl) .form-item-content{border:0}:host-context(.si-container-sm).hide-border,:host-context(.si-container-md).hide-border,:host-context(.si-container-lg).hide-border,:host-context(.si-container-xl).hide-border,:host-context(.si-container-xxl).hide-border{box-shadow:none}:host-context(.si-container-xs){flex-direction:column;border:0;padding-inline-start:2px}:host-context(.si-container-xs) label,:host-context(.si-container-xs) .form-item-content{max-inline-size:none;flex-basis:100%}:host-context(.si-container-xs) label[style*=\"width:\"]{min-inline-size:100%}:host-context(.si-container-xs) label{padding-block-start:0;padding-inline-start:.5rem;margin-block-end:4px}:host-context(.si-container-xs) .form-item-content{box-shadow:inset 1px -1px 0 0 var(--element-ui-4),inset -1px 0 0 0 var(--element-ui-4)}:host-context(.si-container-xs) .form-item-content.hide-border{box-shadow:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-item', template: "<label\n  class=\"col-form-label\"\n  [class.required]=\"required && !readonly\"\n  [ngClass]=\"labelClass ?? ''\"\n  [attr.for]=\"inputId\"\n  [attr.title]=\"!labelWrap ? label : ''\"\n  [style.width.px]=\"(labelWidth || labelWidth === 0) ? labelWidth : ''\">\n  <span\n    *ngIf=\"displayLabel\"\n    class=\"form-item-label-text\"\n    [class.text-break]=\"labelWrap\"\n    [class.text-truncate]=\"!labelWrap\"\n    >{{ label! | translate }}<ng-container *ngIf=\"displayLabelColon\">:</ng-container></span>\n</label>\n<div\n  #content\n  class=\"form-item-content\"\n  [class.hide-border]=\"hideBorder\"\n  [ngClass]=\"contentClass ?? ''\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{flex-direction:column;border:0;padding-inline-start:2px;margin-block-end:16px;display:flex;padding-inline-end:2px}:host label,:host .form-item-content{max-inline-size:none;flex-basis:100%}:host label[style*=\"width:\"]{min-inline-size:100%}:host.readonly .col-form-label{pointer-events:none}.form-item-content{display:flex;align-items:flex-end;flex-wrap:wrap;position:relative}:host-context(.si-container-sm),:host-context(.si-container-md),:host-context(.si-container-lg),:host-context(.si-container-xl),:host-context(.si-container-xxl){flex-direction:row;box-shadow:inset 1px -1px 0 0 var(--element-ui-4),inset -1px 0 0 0 var(--element-ui-4)}:host-context(.si-container-sm) label,:host-context(.si-container-sm) .form-item-content,:host-context(.si-container-md) label,:host-context(.si-container-md) .form-item-content,:host-context(.si-container-lg) label,:host-context(.si-container-lg) .form-item-content,:host-context(.si-container-xl) label,:host-context(.si-container-xl) .form-item-content,:host-context(.si-container-xxl) label,:host-context(.si-container-xxl) .form-item-content{flex-grow:0;flex-shrink:0}:host-context(.si-container-sm) label,:host-context(.si-container-md) label,:host-context(.si-container-lg) label,:host-context(.si-container-xl) label,:host-context(.si-container-xxl) label{padding-inline:16px;margin-block-start:1px}:host-context(.si-container-sm) label[style*=\"width:\"],:host-context(.si-container-md) label[style*=\"width:\"],:host-context(.si-container-lg) label[style*=\"width:\"],:host-context(.si-container-xl) label[style*=\"width:\"],:host-context(.si-container-xxl) label[style*=\"width:\"]{flex:none;min-inline-size:0}:host-context(.si-container-sm) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-md) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-lg) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-xl) label[style*=\"width:\"]+.form-item-content,:host-context(.si-container-xxl) label[style*=\"width:\"]+.form-item-content{flex:auto}:host-context(.si-container-sm) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-md) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-lg) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-xl) label[style*=\"width: 0\"]>.form-item-label-text,:host-context(.si-container-xxl) label[style*=\"width: 0\"]>.form-item-label-text{display:none}:host-context(.si-container-sm) label:not([style*=\"width:\"]),:host-context(.si-container-md) label:not([style*=\"width:\"]),:host-context(.si-container-lg) label:not([style*=\"width:\"]),:host-context(.si-container-xl) label:not([style*=\"width:\"]),:host-context(.si-container-xxl) label:not([style*=\"width:\"]){flex-basis:16.6666666667%;max-inline-size:16.6666666667%}:host-context(.si-container-sm) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-md) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-lg) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-xl) label:not([style*=\"width:\"])+.form-item-content,:host-context(.si-container-xxl) label:not([style*=\"width:\"])+.form-item-content{flex-basis:83.3333333333%;max-inline-size:83.3333333333%}:host-context(.si-container-sm) .form-item-content,:host-context(.si-container-md) .form-item-content,:host-context(.si-container-lg) .form-item-content,:host-context(.si-container-xl) .form-item-content,:host-context(.si-container-xxl) .form-item-content{border:0}:host-context(.si-container-sm).hide-border,:host-context(.si-container-md).hide-border,:host-context(.si-container-lg).hide-border,:host-context(.si-container-xl).hide-border,:host-context(.si-container-xxl).hide-border{box-shadow:none}:host-context(.si-container-xs){flex-direction:column;border:0;padding-inline-start:2px}:host-context(.si-container-xs) label,:host-context(.si-container-xs) .form-item-content{max-inline-size:none;flex-basis:100%}:host-context(.si-container-xs) label[style*=\"width:\"]{min-inline-size:100%}:host-context(.si-container-xs) label{padding-block-start:0;padding-inline-start:.5rem;margin-block-end:4px}:host-context(.si-container-xs) .form-item-content{box-shadow:inset 1px -1px 0 0 var(--element-ui-4),inset -1px 0 0 0 var(--element-ui-4)}:host-context(.si-container-xs) .form-item-content.hide-border{box-shadow:none}\n"] }]
        }], propDecorators: { inputId: [{
                type: Input
            }], label: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], labelColon: [{
                type: Input
            }], labelWidth: [{
                type: Input
            }], labelWrap: [{
                type: Input
            }], hideBorder: [{
                type: HostBinding,
                args: ['class.hide-border']
            }, {
                type: Input
            }], contentClass: [{
                type: Input
            }], readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }, {
                type: Input
            }], required: [{
                type: HostBinding,
                args: ['class.required']
            }, {
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }] } });

class SiFormModule {
}
SiFormModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFormModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFormModule, declarations: [SiFormContainerComponent,
        SiFormItemComponent], imports: [CommonModule,
        SiPopoverModule,
        SiResizeObserverModule,
        SiTranslateModule], exports: [SiFormContainerComponent,
        SiFormItemComponent] });
SiFormModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormModule, imports: [CommonModule,
        SiPopoverModule,
        SiResizeObserverModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiFormContainerComponent,
                        SiFormItemComponent
                    ],
                    imports: [
                        CommonModule,
                        SiPopoverModule,
                        SiResizeObserverModule,
                        SiTranslateModule
                    ],
                    exports: [
                        SiFormContainerComponent,
                        SiFormItemComponent
                    ]
                }]
        }] });

// Adds a window listener which can optionally be active, returns the unregister function.
// For some events (e.g. "touchmove") an explicit "passive: false" is required in order to
// be able to do $event.preventDefault(). Unfortunately there is no way in Angular to pass
// options to Rendrer2/EventManager event handling.
const listenGlobal = (eventName, handler, active) => {
    let opts = false;
    if (active !== undefined) {
        opts = { passive: !active };
    }
    window.addEventListener(eventName, handler, opts);
    return () => { window.removeEventListener(eventName, handler, opts); };
};

class SiSliderComponent {
    constructor() {
        /**
         * Minimum of slider range.
         */
        this.min = 0;
        /**
         * Maximum of slider range.
         */
        this.max = 100;
        /**
         * Label to describe minimum of slider range.
         */
        this.minLabel = '';
        /**
         * Label to describe maximum of slider range.
         */
        this.maxLabel = '';
        /**
         * Interval to step through the slider.
         */
        this.step = 1;
        /**
         * Disables option to change slider value.
         */
        this.disabled = false;
        /**
         * Text for aria-label of increment. Needed for a11y.
         */
        this.incrementLabel = 'increment';
        /**
         * Text for aria-label of decrement. Needed for a11y.
         */
        this.decrementLabel = 'decrement';
        /**
         * Text for aria-label of slider. Needed for a11y
         */
        this.sliderLabel = 'value';
        /**
         * Output callback that triggers when the slider value changes.
         */
        this.valueChange = new EventEmitter();
        this.indicatorPos = 50;
        this.isDragging = false;
        this.autoUpdate$ = timer(400, 80); // 250
        this.rtl = false;
        this.unlistenDragEvents = [];
        this.onTouchedCallback = () => { };
        this.onChangeCallback = () => { };
        this.zone = inject(NgZone);
        this.changeDetectorRef = inject(ChangeDetectorRef);
    }
    get isDisabled() { return this.disabled || this.min === this.max; }
    incrementValue() {
        this.value = this.normalizeValue(this.value + this.step);
        this.valueChanged();
    }
    decrementValue() {
        this.value = this.normalizeValue(this.value - this.step);
        this.valueChanged();
    }
    roundToStepPrecision(value) {
        const factor = 1 / this.step;
        if (factor > 1) {
            return Math.round(value * factor) / factor;
        }
        return Math.round(value / this.step) * this.step;
    }
    normalizeValue(value) {
        return Math.min(Math.max(this.roundToStepPrecision(value), this.min), this.max);
    }
    updateIndicatorPosition() {
        const range = this.max - this.min;
        if (range === 0) {
            this.indicatorPos = 50;
            return;
        }
        const indicatorPos = (this.value - this.min) * 100 / range;
        this.indicatorPos = Math.max(Math.min(indicatorPos, 100), 0);
    }
    handleTouchMove(event) {
        if (event.cancelable) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.handleDragMove(event.touches[0]);
    }
    handleMouseMove(event) {
        event.preventDefault();
        event.stopPropagation();
        this.handleDragMove(event);
    }
    handleDragMove(event) {
        const pointerPosX = event.clientX;
        const handleRect = this.handleRef.nativeElement.getBoundingClientRect();
        const handleWrapperWidth = this.handleRef.nativeElement.parentElement.clientWidth;
        const direction = this.rtl ? -1 : 1;
        const pointerPosDelta = Math.round(pointerPosX - (handleRect.x + handleRect.width / 2)) * direction;
        const valueDelta = pointerPosDelta / handleWrapperWidth * (this.max - this.min);
        const newValue = this.normalizeValue(this.value + valueDelta);
        if (pointerPosDelta > 0 && newValue > this.value || pointerPosDelta < 0 && newValue < this.value) {
            // the zone is required to work around a problem on native device where CD doesn't trigger
            this.zone.run(() => {
                this.changeDetectorRef.markForCheck();
                this.value = newValue;
                this.valueChanged();
            });
        }
        window.getSelection()?.removeAllRanges();
    }
    handleDragEnd() {
        this.isDragging = false;
        this.unlistenDragEvents.forEach(handler => handler());
        this.unlistenDragEvents.length = 0;
    }
    handleUndefinedValue() {
        this.value ?? (this.value = this.roundToStepPrecision((this.min + this.max) / 2));
    }
    ngOnChanges() {
        this.handleUndefinedValue();
        this.updateIndicatorPosition();
    }
    handlePointerDown(event) {
        event.stopPropagation();
    }
    autoUpdateKeydown(event) {
        const rtlCorrectedKey = correctKeyRTL(event.key);
        if (rtlCorrectedKey === 'ArrowLeft') {
            this.autoUpdateStart(event, false);
        }
        else if (rtlCorrectedKey === 'ArrowRight') {
            this.autoUpdateStart(event, true);
        }
    }
    autoUpdateStart(event, isIncrement) {
        event.preventDefault();
        const trigger = isIncrement
            ? () => this.incrementValue()
            : () => this.decrementValue();
        this.autoUpdateSubs?.unsubscribe();
        this.autoUpdateSubs = this.autoUpdate$.subscribe(trigger);
        trigger();
    }
    autoUpdateStop() {
        if (this.autoUpdateSubs) {
            this.autoUpdateSubs.unsubscribe();
            this.autoUpdateSubs = undefined;
        }
    }
    handleMouseDown(event) {
        this.unlistenDragEvents.push(listenGlobal('mousemove', moveEvent => this.handleMouseMove(moveEvent)));
        this.unlistenDragEvents.push(listenGlobal('mouseup', () => this.handleDragEnd()));
        this.isDragging = true;
        this.rtl = isRTL();
        this.handleMouseMove(event);
    }
    handleTouchStart(event) {
        if (event.touches.length !== 1) {
            return;
        }
        this.unlistenDragEvents.push(listenGlobal('touchmove', e => this.handleTouchMove(e), true));
        this.unlistenDragEvents.push(listenGlobal('touchend', () => this.handleDragEnd()));
        this.isDragging = true;
        this.rtl = isRTL();
        this.handleTouchMove(event);
    }
    valueChanged() {
        this.updateIndicatorPosition();
        this.onTouchedCallback();
        this.onChangeCallback(this.value);
        this.valueChange.emit(this.value);
    }
    writeValue(val) {
        this.value = val;
        this.handleUndefinedValue();
        this.updateIndicatorPosition();
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
}
SiSliderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSliderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSliderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSliderComponent, selector: "si-slider", inputs: { value: "value", min: "min", max: "max", minLabel: "minLabel", maxLabel: "maxLabel", step: "step", disabled: "disabled", thumbIcon: "thumbIcon", incrementLabel: "incrementLabel", decrementLabel: "decrementLabel", sliderLabel: "sliderLabel" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "pointerdown": "handlePointerDown($event)", "mousedown": "handlePointerDown($event)", "touchstart": "handlePointerDown($event)" }, properties: { "class.disabled": "this.isDisabled" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => SiSliderComponent),
            multi: true
        }
    ], viewQueries: [{ propertyName: "handleRef", first: true, predicate: ["handle"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"slider-container\">\n  <button\n    [disabled]=\"isDisabled\"\n    type=\"button\"\n    class=\"btn btn-sm btn-circle btn-secondary element-minus decrement-button\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (keydown.enter)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (keyup.enter)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n    [attr.aria-label]=\"decrementLabel | translate\"\n  ></button>\n\n  <div class=\"slider-wrapper\">\n    <div class=\"value-indicator-wrapper\">\n      <span class=\"value-indicator\" [style.inset-inline-start.%]=\"indicatorPos\">\n        <span>\n          <ng-content></ng-content>\n        </span>\n      </span>\n    </div>\n\n    <div class=\"slider\" [class.dragging]=\"isDragging\">\n      <div class=\"track\"></div>\n      <div\n        class=\"thumb-handle-wrapper\"\n        [class.dragging]=\"isDragging\"\n        (mousedown)=\"handleMouseDown($event)\"\n        (touchstart)=\"handleTouchStart($event)\"\n      >\n        <div\n          #handle\n          class=\"thumb-handle\"\n          [style.inset-inline-start.%]=\"indicatorPos\"\n        >\n          <div\n            class=\"thumb\"\n            [class.dragging]=\"isDragging\"\n            tabindex=\"0\"\n            (keydown)=\"autoUpdateKeydown($event)\"\n            (keyup)=\"autoUpdateStop()\"\n          >\n            <i *ngIf=\"thumbIcon\" [ngClass]=\"thumbIcon\"></i>\n            <div *ngIf=\"!thumbIcon\" class=\"hover-circle\"></div>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <button\n    [disabled]=\"isDisabled\"\n    type=\"button\"\n    class=\"btn btn-sm btn-circle btn-secondary element-plus increment-button\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (keydown.enter)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (keyup.enter)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n    [attr.aria-label]=\"incrementLabel | translate\"\n  ></button>\n</div>\n\n<div *ngIf=\"minLabel && maxLabel\" class=\"range-indicator-wrapper\">\n  <div class=\"range-indicator\">\n    <span>{{ minLabel }}</span>\n    <span class=\"ms-auto\">{{ maxLabel }}</span>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column}.slider-container{display:flex;align-items:flex-end}.decrement-button{margin-inline-end:4px}.increment-button{margin-inline-start:4px}.slider-wrapper{position:relative;flex:1 1 0;min-inline-size:0;display:flex;flex-direction:column}.value-indicator-wrapper{position:relative;margin-inline:12px;text-align:start}.value-indicator{position:relative;display:inline-block;white-space:nowrap;color:var(--element-text-secondary)}.value-indicator>span{display:inline-block;margin-inline-start:-50%}.range-indicator-wrapper{margin-block:0;margin-inline:36px}.range-indicator-wrapper .range-indicator{display:inline-flex;white-space:nowrap;inline-size:100%;color:var(--element-text-secondary)}.range-indicator-wrapper .range-indicator>span{display:inline-block}.slider{position:relative;inline-size:100%;block-size:32px;cursor:pointer}.slider.dragging{cursor:grabbing}:host.disabled .slider{cursor:default}.slider .track{position:absolute;inset-block-start:50%;border-radius:var(--element-radius-1);margin-block-start:-2px;block-size:4px;inline-size:100%;background:var(--element-ui-4);pointer-events:none}.slider .thumb-handle-wrapper{position:absolute;inset-block:0;inset-inline:12px}:host.disabled .slider .thumb-handle-wrapper{pointer-events:none}.slider .thumb-handle{display:flex;align-items:center;justify-content:center;position:absolute;inset-block-start:50%;margin-block-start:-20px;margin-inline:-20px;inline-size:40px;block-size:40px}:host.disabled .slider .thumb-handle{pointer-events:none}.slider .thumb{--thumb-state-color: var(--element-ui-0);display:flex;align-items:center;justify-content:center;block-size:24px;min-block-size:24px;inline-size:24px;min-inline-size:24px;border-radius:50%;border:1px solid var(--thumb-state-color);box-shadow:0 0 8px var(--element-box-shadow-color-1),0 8px 8px var(--element-box-shadow-color-2);background:var(--element-base-1)}.slider .thumb:hover:not(.dragging){--thumb-state-color: var(--element-ui-0-hover);background:var(--element-action-secondary-hover)}.slider .thumb:focus{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.disabled .slider .thumb{--thumb-state-color: var(--element-ui-3);box-shadow:none}.slider .thumb i{font-size:1.5rem;color:var(--thumb-state-color)}.slider .thumb:hover .hover-circle,.slider .thumb:focus .hover-circle,.slider .thumb.dragging .hover-circle{display:block}.slider .hover-circle{display:none;block-size:12px;inline-size:12px;border-radius:50%;background:var(--thumb-state-color)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-slider', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            // eslint-disable-next-line @angular-eslint/no-forward-ref
                            useExisting: forwardRef(() => SiSliderComponent),
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"slider-container\">\n  <button\n    [disabled]=\"isDisabled\"\n    type=\"button\"\n    class=\"btn btn-sm btn-circle btn-secondary element-minus decrement-button\"\n    (mousedown)=\"autoUpdateStart($event, false)\"\n    (touchstart)=\"autoUpdateStart($event, false)\"\n    (keydown.enter)=\"autoUpdateStart($event, false)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (keyup.enter)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n    [attr.aria-label]=\"decrementLabel | translate\"\n  ></button>\n\n  <div class=\"slider-wrapper\">\n    <div class=\"value-indicator-wrapper\">\n      <span class=\"value-indicator\" [style.inset-inline-start.%]=\"indicatorPos\">\n        <span>\n          <ng-content></ng-content>\n        </span>\n      </span>\n    </div>\n\n    <div class=\"slider\" [class.dragging]=\"isDragging\">\n      <div class=\"track\"></div>\n      <div\n        class=\"thumb-handle-wrapper\"\n        [class.dragging]=\"isDragging\"\n        (mousedown)=\"handleMouseDown($event)\"\n        (touchstart)=\"handleTouchStart($event)\"\n      >\n        <div\n          #handle\n          class=\"thumb-handle\"\n          [style.inset-inline-start.%]=\"indicatorPos\"\n        >\n          <div\n            class=\"thumb\"\n            [class.dragging]=\"isDragging\"\n            tabindex=\"0\"\n            (keydown)=\"autoUpdateKeydown($event)\"\n            (keyup)=\"autoUpdateStop()\"\n          >\n            <i *ngIf=\"thumbIcon\" [ngClass]=\"thumbIcon\"></i>\n            <div *ngIf=\"!thumbIcon\" class=\"hover-circle\"></div>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <button\n    [disabled]=\"isDisabled\"\n    type=\"button\"\n    class=\"btn btn-sm btn-circle btn-secondary element-plus increment-button\"\n    (mousedown)=\"autoUpdateStart($event, true)\"\n    (touchstart)=\"autoUpdateStart($event, true)\"\n    (keydown.enter)=\"autoUpdateStart($event, true)\"\n    (touchend)=\"autoUpdateStop()\"\n    (mouseup)=\"autoUpdateStop()\"\n    (keyup.enter)=\"autoUpdateStop()\"\n    (mouseleave)=\"autoUpdateStop()\"\n    [attr.aria-label]=\"incrementLabel | translate\"\n  ></button>\n</div>\n\n<div *ngIf=\"minLabel && maxLabel\" class=\"range-indicator-wrapper\">\n  <div class=\"range-indicator\">\n    <span>{{ minLabel }}</span>\n    <span class=\"ms-auto\">{{ maxLabel }}</span>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column}.slider-container{display:flex;align-items:flex-end}.decrement-button{margin-inline-end:4px}.increment-button{margin-inline-start:4px}.slider-wrapper{position:relative;flex:1 1 0;min-inline-size:0;display:flex;flex-direction:column}.value-indicator-wrapper{position:relative;margin-inline:12px;text-align:start}.value-indicator{position:relative;display:inline-block;white-space:nowrap;color:var(--element-text-secondary)}.value-indicator>span{display:inline-block;margin-inline-start:-50%}.range-indicator-wrapper{margin-block:0;margin-inline:36px}.range-indicator-wrapper .range-indicator{display:inline-flex;white-space:nowrap;inline-size:100%;color:var(--element-text-secondary)}.range-indicator-wrapper .range-indicator>span{display:inline-block}.slider{position:relative;inline-size:100%;block-size:32px;cursor:pointer}.slider.dragging{cursor:grabbing}:host.disabled .slider{cursor:default}.slider .track{position:absolute;inset-block-start:50%;border-radius:var(--element-radius-1);margin-block-start:-2px;block-size:4px;inline-size:100%;background:var(--element-ui-4);pointer-events:none}.slider .thumb-handle-wrapper{position:absolute;inset-block:0;inset-inline:12px}:host.disabled .slider .thumb-handle-wrapper{pointer-events:none}.slider .thumb-handle{display:flex;align-items:center;justify-content:center;position:absolute;inset-block-start:50%;margin-block-start:-20px;margin-inline:-20px;inline-size:40px;block-size:40px}:host.disabled .slider .thumb-handle{pointer-events:none}.slider .thumb{--thumb-state-color: var(--element-ui-0);display:flex;align-items:center;justify-content:center;block-size:24px;min-block-size:24px;inline-size:24px;min-inline-size:24px;border-radius:50%;border:1px solid var(--thumb-state-color);box-shadow:0 0 8px var(--element-box-shadow-color-1),0 8px 8px var(--element-box-shadow-color-2);background:var(--element-base-1)}.slider .thumb:hover:not(.dragging){--thumb-state-color: var(--element-ui-0-hover);background:var(--element-action-secondary-hover)}.slider .thumb:focus{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.disabled .slider .thumb{--thumb-state-color: var(--element-ui-3);box-shadow:none}.slider .thumb i{font-size:1.5rem;color:var(--thumb-state-color)}.slider .thumb:hover .hover-circle,.slider .thumb:focus .hover-circle,.slider .thumb.dragging .hover-circle{display:block}.slider .hover-circle{display:none;block-size:12px;inline-size:12px;border-radius:50%;background:var(--thumb-state-color)}\n"] }]
        }], propDecorators: { handleRef: [{
                type: ViewChild,
                args: ['handle', { static: true }]
            }], value: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], minLabel: [{
                type: Input
            }], maxLabel: [{
                type: Input
            }], step: [{
                type: Input
            }], disabled: [{
                type: Input
            }], thumbIcon: [{
                type: Input
            }], incrementLabel: [{
                type: Input
            }], decrementLabel: [{
                type: Input
            }], sliderLabel: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], isDisabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }], handlePointerDown: [{
                type: HostListener,
                args: ['pointerdown', ['$event']]
            }, {
                type: HostListener,
                args: ['mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['touchstart', ['$event']]
            }] } });

class SiSliderModule {
}
SiSliderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSliderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSliderModule, declarations: [SiSliderComponent], imports: [CommonModule,
        FormsModule,
        SiTranslateModule], exports: [SiSliderComponent] });
SiSliderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSliderModule, imports: [CommonModule,
        FormsModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SiTranslateModule
                    ],
                    declarations: [SiSliderComponent],
                    exports: [SiSliderComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiSortBarComponent {
    constructor() {
        /**
         * Custom sort title.
         */
        this.sortTitle = 'Sort by';
        /**
         * Output callback event will provide you with a HttpParams object if active
         * sortCriteria change.
         */
        this.sortChange = new EventEmitter();
        this.activeSortCriteria = '';
        this.sortIsDescending = false;
        this.trackByIndex = (index) => index;
    }
    ngOnInit() {
        if (this.defaultSortCriteria) {
            this.setActive(this.defaultSortCriteria);
        }
    }
    setActive(key) {
        if (this.activeSortCriteria === key) {
            this.sortIsDescending = !this.sortIsDescending;
        }
        this.activeSortCriteria = key;
        const searchParams = new HttpParams()
            .set('sort', this.activeSortCriteria)
            .set('order', this.sortIsDescending ? 'desc' : 'asc');
        this.sortChange.emit(searchParams);
    }
}
SiSortBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSortBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSortBarComponent, selector: "si-sort-bar", inputs: { sortTitle: "sortTitle", sortCriteria: "sortCriteria", defaultSortCriteria: "defaultSortCriteria" }, outputs: { sortChange: "sortChange" }, ngImport: i0, template: "<div class=\"list-header list-header-sorting\">\n  <div class=\"list-title\">{{ sortTitle | translate }}:</div>\n  <div\n    class=\"sort-item\"\n    *ngFor=\"let item of sortCriteria; trackBy: trackByIndex\"\n    (click)=\"setActive(item.key)\"\n    (keydown.enter)=\"setActive(item.key)\"\n    tabindex=\"0\"\n  >\n    <span>{{ item.name | translate }}</span>\n    <span\n      class=\"icon\"\n      [class.element-sort-up]=\"item.key === activeSortCriteria && !sortIsDescending\"\n      [class.element-sort-down]=\"item.key === activeSortCriteria && sortIsDescending\"\n    ></span>\n  </div>\n</div>\n", styles: [".list-title{font-weight:500;margin-inline-end:5px}.list-header-sorting{background:var(--element-ui-4);padding:6px 12px;display:flex;align-items:center}.sort-item{padding-block:0;padding-inline:5px;cursor:pointer;display:flex;align-items:center}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiSortBarComponent.prototype, "sortCriteria", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-sort-bar', template: "<div class=\"list-header list-header-sorting\">\n  <div class=\"list-title\">{{ sortTitle | translate }}:</div>\n  <div\n    class=\"sort-item\"\n    *ngFor=\"let item of sortCriteria; trackBy: trackByIndex\"\n    (click)=\"setActive(item.key)\"\n    (keydown.enter)=\"setActive(item.key)\"\n    tabindex=\"0\"\n  >\n    <span>{{ item.name | translate }}</span>\n    <span\n      class=\"icon\"\n      [class.element-sort-up]=\"item.key === activeSortCriteria && !sortIsDescending\"\n      [class.element-sort-down]=\"item.key === activeSortCriteria && sortIsDescending\"\n    ></span>\n  </div>\n</div>\n", styles: [".list-title{font-weight:500;margin-inline-end:5px}.list-header-sorting{background:var(--element-ui-4);padding:6px 12px;display:flex;align-items:center}.sort-item{padding-block:0;padding-inline:5px;cursor:pointer;display:flex;align-items:center}\n"] }]
        }], propDecorators: { sortTitle: [{
                type: Input
            }], sortCriteria: [{
                type: Input
            }], defaultSortCriteria: [{
                type: Input
            }], sortChange: [{
                type: Output
            }] } });

class SiSortBarModule {
}
SiSortBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSortBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarModule, declarations: [SiSortBarComponent], imports: [CommonModule,
        SiTranslateModule], exports: [SiSortBarComponent] });
SiSortBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarModule, imports: [CommonModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSortBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiTranslateModule
                    ],
                    declarations: [SiSortBarComponent],
                    exports: [SiSortBarComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

/**
 * A global blink pulse generator for synchronized blinking patterns across an entire application.
 * Use to trigger any blinking by subscribing to `pulse$`.
 */
class BlinkService {
    constructor() {
        this.pause$ = new BehaviorSubject(false);
        this.off$ = this.pause$.pipe(filter(v => v));
        this.on$ = this.pause$.pipe(filter(v => !v));
        /**
         * Blink pulse. Subscribe to it to toggle `on` CSS class when true, `off` CSS class when `false`.
         * Do animations using CSS transitions
         */
        this.pulse$ = timer(0, 1400).pipe(map(count => count % 2 === 0), distinctUntilChanged(), takeUntil(this.off$), endWith(false), repeatWhen(() => this.on$), share());
    }
    pause() {
        this.pause$.next(true);
    }
    resume() {
        this.pause$.next(false);
    }
    isPaused() {
        return this.pause$.value;
    }
}
BlinkService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: BlinkService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BlinkService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: BlinkService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: BlinkService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiStatusBarItemComponent {
    constructor() {
        this.blink = false;
        this.valueOnly = false;
        this.clickable = false;
        this.contrastFix = false;
        this.background = '';
    }
    ngOnChanges(changes) {
        if (changes.color || changes.blink) {
            setTimeout(() => this.contrastFix = !!this.color && this.blink && this.calculateContrastFix());
        }
        if (changes.status) {
            this.statusIcon = this.status ? STATUS_ICON[this.status] : undefined;
        }
        if (changes.status || changes.blink) {
            this.background = this.blink && this.status !== 'success'
                ? this.statusIcon?.background ?? ''
                : '';
        }
    }
    calculateContrastFix() {
        // see https://www.w3.org/TR/AERT/#color-contrast
        const rgb = getComputedStyle(this.bg.nativeElement).backgroundColor?.match(/\d+/g)?.map(v => +v);
        return !!rgb && Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000) <= 128;
    }
}
SiStatusBarItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiStatusBarItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiStatusBarItemComponent, selector: "si-status-bar-item", inputs: { status: "status", statusAriaLabel: "statusAriaLabel", value: "value", heading: "heading", color: "color", blink: "blink", valueOnly: "valueOnly", clickable: "clickable" }, host: { properties: { "class.clickable": "this.clickable" } }, viewQueries: [{ propertyName: "bg", first: true, predicate: ["bg"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"status-item rounded-2 focus-inside d-flex align-items-center\"\n  [class.text-muted]=\"!value\"\n  [class.blink]=\"blink && value\"\n  [attr.tabindex]=\"clickable ? '0' : ''\"\n  [attr.aria-disabled]=\"!value\"\n>\n  <div #bg\n    role=\"none\"\n    class=\"bg focus-sub-inside\"\n    [class.custom-color]=\"color\"\n    [class.contrast-fix]=\"contrastFix\"\n    [ngClass]=\"background\"\n    [class.d-none]=\"!value\"\n    [style.background-color]=\"blink && color ? color : null\"\n  ></div>\n  <div\n    *ngIf=\"!statusIcon\"\n    class=\"color-bar me-3 mt-1 mb-1\"\n    [style.background-color]=\"value && color ? color : null\"\n  ></div>\n  <si-icon\n    *ngIf=\"statusIcon\"\n    class=\"indicator me-4\"\n    [icon]=\"statusIcon.icon\"\n    [color]=\"value ? statusIcon.color : 'indicator-disabled'\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"value ? statusIcon.stackedColor : 'text-inverse'\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"overflow-hidden\">\n    <div *ngIf=\"value !== undefined\" class=\"item-value fw-bold\">{{ value.toString() | translate }}</div>\n    <div *ngIf=\"!valueOnly\" class=\"item-title text-truncate\">{{ heading | translate }}</div>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{position:relative;padding-inline-end:4px;block-size:48px}:host.compact{block-size:36px}:host.compact .status-item{padding-block:4px}:host.compact .item-title{margin-block-start:-2px}:host.clickable:hover .status-item{background-color:var(--element-base-0)!important}:host.clickable:hover .bg{display:none!important}:host ::ng-deep .indicator-disabled{color:var(--element-ui-3)}.status-item{position:relative;padding-block:8px;padding-inline:4px 8px;min-inline-size:100px;color:var(--element-text-primary);white-space:nowrap}.status-item>*{pointer-events:none}.bg{position:absolute;inset:0;border-radius:var(--element-radius-2);pointer-events:none}.bg.custom-color{opacity:.5}.bg.custom-color.contrast-fix{opacity:.25}.item-title,.item-value{position:relative}.item-title:before,.item-value:before{content:\"\\200b\"}.color-bar{align-self:stretch;min-inline-size:4px;border-radius:2px;background:var(--element-ui-4)}.item-title{margin-block-start:2px;line-height:1rem}.item-value{line-height:1}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-status-bar-item', template: "<div\n  class=\"status-item rounded-2 focus-inside d-flex align-items-center\"\n  [class.text-muted]=\"!value\"\n  [class.blink]=\"blink && value\"\n  [attr.tabindex]=\"clickable ? '0' : ''\"\n  [attr.aria-disabled]=\"!value\"\n>\n  <div #bg\n    role=\"none\"\n    class=\"bg focus-sub-inside\"\n    [class.custom-color]=\"color\"\n    [class.contrast-fix]=\"contrastFix\"\n    [ngClass]=\"background\"\n    [class.d-none]=\"!value\"\n    [style.background-color]=\"blink && color ? color : null\"\n  ></div>\n  <div\n    *ngIf=\"!statusIcon\"\n    class=\"color-bar me-3 mt-1 mb-1\"\n    [style.background-color]=\"value && color ? color : null\"\n  ></div>\n  <si-icon\n    *ngIf=\"statusIcon\"\n    class=\"indicator me-4\"\n    [icon]=\"statusIcon.icon\"\n    [color]=\"value ? statusIcon.color : 'indicator-disabled'\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"value ? statusIcon.stackedColor : 'text-inverse'\"\n    alt=\"\"\n  ></si-icon>\n  <div class=\"overflow-hidden\">\n    <div *ngIf=\"value !== undefined\" class=\"item-value fw-bold\">{{ value.toString() | translate }}</div>\n    <div *ngIf=\"!valueOnly\" class=\"item-title text-truncate\">{{ heading | translate }}</div>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";:host{position:relative;padding-inline-end:4px;block-size:48px}:host.compact{block-size:36px}:host.compact .status-item{padding-block:4px}:host.compact .item-title{margin-block-start:-2px}:host.clickable:hover .status-item{background-color:var(--element-base-0)!important}:host.clickable:hover .bg{display:none!important}:host ::ng-deep .indicator-disabled{color:var(--element-ui-3)}.status-item{position:relative;padding-block:8px;padding-inline:4px 8px;min-inline-size:100px;color:var(--element-text-primary);white-space:nowrap}.status-item>*{pointer-events:none}.bg{position:absolute;inset:0;border-radius:var(--element-radius-2);pointer-events:none}.bg.custom-color{opacity:.5}.bg.custom-color.contrast-fix{opacity:.25}.item-title,.item-value{position:relative}.item-title:before,.item-value:before{content:\"\\200b\"}.color-bar{align-self:stretch;min-inline-size:4px;border-radius:2px;background:var(--element-ui-4)}.item-title{margin-block-start:2px;line-height:1rem}.item-value{line-height:1}\n"] }]
        }], propDecorators: { status: [{
                type: Input
            }], statusAriaLabel: [{
                type: Input
            }], value: [{
                type: Input
            }], heading: [{
                type: Input
            }], color: [{
                type: Input
            }], blink: [{
                type: Input
            }], valueOnly: [{
                type: Input
            }], clickable: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.clickable']
            }], bg: [{
                type: ViewChild,
                args: ['bg']
            }] } });

// this is a function because Angular compiler exports arrows for no good reason
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function itemSortFunction(a, b) {
    return (a.status && b.status) ? STATUS_ICON[a.status].severity - STATUS_ICON[b.status].severity : 0;
}
let idCounter = 1;
/**
 * The status bar is the main component within an application to inform users at all times
 * about important status information.
 */
class SiStatusBarComponent {
    constructor() {
        /**
         * When true, items with a value have a blinking background
         */
        this.blink = false;
        /**
         * Text/translation key on mute button for screen reader
         */
        this.muteButtonText = 'Mute/unmute';
        /**
         * Text/translation key for "All OK" status in mobile
         */
        this.allOkText = 'All OK';
        /**
         * compact mode
         */
        this.compact = false;
        /**
         * Text for the navbar expand button. Required for a11y
         */
        this.expandButtonText = 'expand';
        /**
         * Text for the navbar collapse button. Required for a11y
         */
        this.collapseButtonText = 'collapse';
        /**
         * Emitted when the mute toggle button is clicked
         */
        this.muteToggle = new EventEmitter();
        this.responsiveItems = [];
        this.responsiveMode = 0;
        this.expanded = 0;
        this.placeholderHeight = 0;
        this.statusId = `__si-status-bar-${idCounter++}`;
        this.element = inject(ElementRef);
        this.blinkService = inject(BlinkService);
        this.translateService = inject(SiTranslateService);
        this.resizeObserver = inject(ResizeObserverService);
        this.cdRef = inject(ChangeDetectorRef);
        this.measureService = inject(TextMeasureService);
        this.resizeSubs = this.resizeObserver.observe(this.element.nativeElement, 100, true)
            .subscribe(() => this.resizeHandler());
        this.resizeSubs.add(this.translateService.translationChange.subscribe(() => this.resizeHandler()));
    }
    ngOnChanges(changes) {
        if (this.blinkService && changes.blink) {
            this.blinkSubs?.unsubscribe();
            if (this.blink) {
                const pulse = this.blinkPulse ?? this.blinkService.pulse$;
                this.blinkSubs = pulse.subscribe(onOff => {
                    this.blinkOnOff = onOff;
                    this.cdRef.markForCheck();
                });
            }
        }
        this.resizeHandler();
    }
    ngOnDestroy() {
        this.resizeSubs.unsubscribe();
        this.blinkSubs?.unsubscribe();
    }
    ngDoCheck() {
        if (this.responsiveMode) {
            this.calcResponsiveItems();
        }
    }
    onItemClicked(item) {
        if (item.action) {
            item.action(item);
        }
    }
    toggleExpand() {
        clearTimeout(this.timer);
        if (!this.expanded) {
            this.expanded = 2;
            this.placeholderHeight = this.theBar.nativeElement.offsetHeight;
            this.contentHeight = 0;
            this.cdRef.markForCheck();
            this.timer = window.setTimeout(() => {
                this.contentHeight = this.content.nativeElement.scrollHeight;
                this.cdRef.markForCheck();
                window.setTimeout(() => {
                    this.contentHeight = undefined;
                    this.cdRef.markForCheck();
                }, 500);
            }, 10);
        }
        else {
            this.contentHeight = this.content.nativeElement.scrollHeight;
            this.expanded = 1;
            this.cdRef.markForCheck();
            window.setTimeout(() => {
                this.contentHeight = 0;
                this.cdRef.markForCheck();
                window.setTimeout(() => {
                    this.placeholderHeight = 0;
                    this.expanded = 0;
                    this.cdRef.markForCheck();
                }, 500);
            }, 10);
        }
    }
    trackByIndex(index) {
        return index;
    }
    resizeHandler() {
        const size = this.element.nativeElement.clientWidth;
        const muteWidth = this.muteButton !== undefined ? SiStatusBarComponent.muteButtonWidth : 0;
        const customWidth = this.custom.nativeElement.scrollWidth ?? 0;
        const minWidth = this.items.length * (SiStatusBarComponent.itemMinWidth + SiStatusBarComponent.itemSpacing) +
            SiStatusBarComponent.itemSpacing + muteWidth + customWidth;
        if (size < minWidth) {
            this.setResponsiveMode(true);
        }
        else if (this.items.length) {
            this.calculateRequiredWidth(muteWidth, customWidth);
        }
    }
    setResponsiveMode(responsive) {
        if (responsive) {
            const size = this.element.nativeElement.clientWidth;
            this.responsiveMode = Math.max(Math.floor(size / SiStatusBarComponent.itemMaxWidth) - 1, 2);
        }
        else {
            this.responsiveMode = 0;
        }
        if (this.responsiveMode) {
            this.contentHeight = this.expanded ? this.content.nativeElement.scrollHeight : 0;
        }
        else {
            this.expanded = 0;
            this.placeholderHeight = 0;
            this.contentHeight = undefined;
        }
        this.cdRef.markForCheck();
    }
    calcResponsiveItems() {
        const activeItems = this.items
            .filter(item => item.value).sort(itemSortFunction);
        if (activeItems.length > this.responsiveMode) {
            activeItems[this.responsiveMode - 1] = {
                status: activeItems[this.responsiveMode - 1].status,
                color: activeItems[this.responsiveMode - 1].color,
                value: (activeItems.length - this.responsiveMode + 1) + '+',
                title: '',
                action: () => this.toggleExpand(),
                isSpecial: true
            };
            activeItems.length = this.responsiveMode;
        }
        else if (!activeItems.length) {
            activeItems.push({
                status: 'success',
                title: '',
                value: this.allOkText,
                isSpecial: true
            });
        }
        if (activeItems.length === this.responsiveMode) {
            activeItems[activeItems.length - 1].mutePadding = true;
        }
        this.responsiveItems = activeItems;
        this.cdRef.markForCheck();
    }
    calculateRequiredWidth(muteWidth, customWidth) {
        const keys = this.items.map(item => item.title);
        this.translateService.translateAsync(keys).pipe(first()).subscribe(translations => {
            const size = this.element.nativeElement.clientWidth;
            const requiredWidth = this.items.reduce((acc, item) => {
                const textWidth = this.measureService.measureText(translations[item.title]);
                const itemWidth = Math.max(SiStatusBarComponent.itemMinWidth, textWidth + (item.color ? SiStatusBarComponent.itemPaddingXdeprecated : SiStatusBarComponent.itemPaddingX)) + SiStatusBarComponent.itemSpacing;
                return acc + itemWidth;
            }, muteWidth + customWidth + SiStatusBarComponent.itemSpacing);
            this.setResponsiveMode(size < requiredWidth);
        });
    }
}
SiStatusBarComponent.itemMinWidth = 100;
SiStatusBarComponent.itemMaxWidth = 152;
SiStatusBarComponent.itemSpacing = 4;
SiStatusBarComponent.itemPaddingX = 44; // padding + icon size + icon margin
SiStatusBarComponent.itemPaddingXdeprecated = 20; // padding + color bar
SiStatusBarComponent.muteButtonWidth = 48;
SiStatusBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiStatusBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiStatusBarComponent, selector: "si-status-bar", inputs: { items: "items", blink: "blink", muteButton: "muteButton", muteButtonText: "muteButtonText", allOkText: "allOkText", compact: "compact", blinkPulse: "blinkPulse", expandButtonText: "expandButtonText", collapseButtonText: "collapseButtonText" }, outputs: { muteToggle: "muteToggle" }, viewQueries: [{ propertyName: "theBar", first: true, predicate: ["thebar"], descendants: true, static: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "custom", first: true, predicate: ["custom"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"expand-placeholder\"\n  [class.expanded]=\"expanded\"\n  [style.height.px]=\"placeholderHeight\"\n></div>\n<div #thebar\n  aria-busy=\"true\"\n  [class.responsive]=\"responsiveMode\"\n  [class.expanded]=\"expanded\"\n  [class.compact]=\"responsiveMode || compact\"\n  [class.pulse-off]=\"blinkOnOff === false\"\n  [class.pulse-on]=\"blinkOnOff\"\n  [ngClass]=\"responsiveMode ? 'responsive-' + responsiveMode : ''\"\n>\n  <div class=\"status-bar-wrapper elevation-2 rounded-2\" [class.pe-2]=\"muteButton !== undefined\">\n    <div *ngIf=\"responsiveMode\" class=\"align-items-center overflow-hidden\">\n      <div class=\"d-flex flex-fill position-relative\">\n        <si-status-bar-item\n          *ngFor=\"let item of responsiveItems; trackBy: trackByIndex\"\n          class=\"bar-item compact\"\n          [value]=\"item.value\"\n          [heading]=\"item.title\"\n          [status]=\"item.status\"\n          [color]=\"item.color\"\n          [blink]=\"item.blink !== false && blink && !expanded\"\n          [clickable]=\"!!item.action\"\n          [class.mute-padding]=\"item.mutePadding && muteButton !== undefined\"\n          [style.flex-basis.%]=\"responsiveMode ? 100 / responsiveMode : null\"\n          [valueOnly]=\"item.isSpecial\"\n          (click)=\"onItemClicked(item)\"\n          (keydown.enter)=\"onItemClicked(item)\"\n        ></si-status-bar-item>\n      </div>\n      <div *ngIf=\"muteButton !== undefined\" class=\"mobile-mute\">\n        <ng-container *ngTemplateOutlet=\"muteButtonTemplate\"></ng-container>\n      </div>\n    </div>\n    <div #content\n      class=\"main-content align-items-center overflow-hidden\"\n      [style.max-height.px]=\"contentHeight\"\n    >\n      <div *ngIf=\"responsiveMode\" class=\"mobile-spacer\"></div>\n      <div\n        class=\"d-flex flex-fill position-relative\"\n        [id]=\"statusId\"\n        [class.d-none]=\"responsiveMode && !expanded\"\n        [class.flex-wrap]=\"responsiveMode\"\n      >\n        <si-status-bar-item\n          *ngFor=\"let item of items; trackBy: trackByIndex\"\n          class=\"bar-item\"\n          [class.compact]=\"responsiveMode || compact\"\n          [value]=\"item.value\"\n          [heading]=\"item.title\"\n          [status]=\"item.status\"\n          [color]=\"item.color\"\n          [blink]=\"item.blink !== false && blink\"\n          [clickable]=\"!!item.action\"\n          [style.flex-basis.%]=\"responsiveMode ? 100 / responsiveMode : null\"\n          (click)=\"onItemClicked(item)\"\n          (keydown.enter)=\"onItemClicked(item)\"\n        ></si-status-bar-item>\n      </div>\n      <div class=\"custom-content bar-item d-flex\">\n        <div #custom [class.pb-2]=\"responsiveMode && expanded\" (siResizeObserver)=\"resizeHandler()\">\n          <ng-content></ng-content>\n        </div>\n      </div>\n      <ng-container *ngIf=\"muteButton !== undefined && !responsiveMode\">\n        <ng-container *ngTemplateOutlet=\"muteButtonTemplate\"></ng-container>\n      </ng-container>\n    </div>\n  </div>\n  <div *ngIf=\"responsiveMode\" class=\"d-flex align-items-center justify-content-center\">\n    <a\n      class=\"collapse-expand text-center p-0 focus-force\"\n      tabindex=\"0\"\n      role=\"button\"\n      [attr.aria-label]=\"(expanded ? collapseButtonText : expandButtonText) | translate\"\n      [attr.aria-expanded]=\"!!expanded\"\n      [attr.aria-controls]=\"statusId\"\n      (keydown.enter)=\"toggleExpand()\"\n      (click)=\"toggleExpand()\"\n    >\n      <i class=\"icon element-down-2\" [class.expanded]=\"expanded === 2\"></i>\n    </a>\n  </div>\n</div>\n\n<ng-template #muteButtonTemplate>\n  <button\n    type=\"button\"\n    class=\"bar-item mute-button btn btn-circle btn-sm btn-tertiary ms-5\"\n    [class.element-sound-mute]=\"muteButton === false\"\n    [class.element-sound-on]=\"muteButton === true\"\n    [attr.aria-label]=\"muteButtonText | translate\"\n    (click)=\"muteToggle.emit()\"\n  ></button>\n</ng-template>\n", styles: [":host{display:block;position:relative;margin-block-end:16px}:host ::ng-deep .blink{transition:color calc(.5s * var(--element-animations-enabled, 1))}:host ::ng-deep .bg{transition:opacity calc(.5s * var(--element-animations-enabled, 1))}.pulse-off ::ng-deep .bg{opacity:.1!important}.status-bar-wrapper{padding-inline:8px;background-color:var(--element-base-1)}.status-bar-wrapper>*{display:flex}.mobile-spacer{margin-block:4px;margin-inline:0;background:var(--element-base-0);block-size:1px}.bar-item{margin-block:8px}.custom-content{margin-inline-start:auto}.clickable{cursor:pointer}.mute-padding{padding-inline-end:44px}.mobile-mute{position:absolute;inset-inline-end:8px}.main-content .mute-button{margin-inline-end:4px}.collapse-expand{background-color:var(--element-base-1);border-radius:0 0 20px 20px;box-shadow:0 8px 8px #00000014;inline-size:40px;cursor:pointer}.collapse-expand i{margin-block-start:-4px;transition:transform calc(.4s * var(--element-animations-enabled, 1))}.collapse-expand i.expanded{transform:rotate(180deg)}.compact .bar-item{margin-block:2px}.responsive si-status-bar-item ::ng-deep .status-item{max-inline-size:unset;position:absolute;inset-block:0;inset-inline:0 4px}.responsive si-status-bar-item.mute-padding ::ng-deep .status-item{inset-inline-end:44px}.responsive.expanded{position:absolute;inset-block-start:0;inset-inline:0;z-index:1028}.responsive .status-bar-wrapper>.main-content{display:block;transition:max-block-size calc(.5s * var(--element-animations-enabled, 1))}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: ["resizeThrottle"], outputs: ["siResizeObserver"] }, { kind: "component", type: SiStatusBarItemComponent, selector: "si-status-bar-item", inputs: ["status", "statusAriaLabel", "value", "heading", "color", "blink", "valueOnly", "clickable"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    Required()
], SiStatusBarComponent.prototype, "items", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-status-bar', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"expand-placeholder\"\n  [class.expanded]=\"expanded\"\n  [style.height.px]=\"placeholderHeight\"\n></div>\n<div #thebar\n  aria-busy=\"true\"\n  [class.responsive]=\"responsiveMode\"\n  [class.expanded]=\"expanded\"\n  [class.compact]=\"responsiveMode || compact\"\n  [class.pulse-off]=\"blinkOnOff === false\"\n  [class.pulse-on]=\"blinkOnOff\"\n  [ngClass]=\"responsiveMode ? 'responsive-' + responsiveMode : ''\"\n>\n  <div class=\"status-bar-wrapper elevation-2 rounded-2\" [class.pe-2]=\"muteButton !== undefined\">\n    <div *ngIf=\"responsiveMode\" class=\"align-items-center overflow-hidden\">\n      <div class=\"d-flex flex-fill position-relative\">\n        <si-status-bar-item\n          *ngFor=\"let item of responsiveItems; trackBy: trackByIndex\"\n          class=\"bar-item compact\"\n          [value]=\"item.value\"\n          [heading]=\"item.title\"\n          [status]=\"item.status\"\n          [color]=\"item.color\"\n          [blink]=\"item.blink !== false && blink && !expanded\"\n          [clickable]=\"!!item.action\"\n          [class.mute-padding]=\"item.mutePadding && muteButton !== undefined\"\n          [style.flex-basis.%]=\"responsiveMode ? 100 / responsiveMode : null\"\n          [valueOnly]=\"item.isSpecial\"\n          (click)=\"onItemClicked(item)\"\n          (keydown.enter)=\"onItemClicked(item)\"\n        ></si-status-bar-item>\n      </div>\n      <div *ngIf=\"muteButton !== undefined\" class=\"mobile-mute\">\n        <ng-container *ngTemplateOutlet=\"muteButtonTemplate\"></ng-container>\n      </div>\n    </div>\n    <div #content\n      class=\"main-content align-items-center overflow-hidden\"\n      [style.max-height.px]=\"contentHeight\"\n    >\n      <div *ngIf=\"responsiveMode\" class=\"mobile-spacer\"></div>\n      <div\n        class=\"d-flex flex-fill position-relative\"\n        [id]=\"statusId\"\n        [class.d-none]=\"responsiveMode && !expanded\"\n        [class.flex-wrap]=\"responsiveMode\"\n      >\n        <si-status-bar-item\n          *ngFor=\"let item of items; trackBy: trackByIndex\"\n          class=\"bar-item\"\n          [class.compact]=\"responsiveMode || compact\"\n          [value]=\"item.value\"\n          [heading]=\"item.title\"\n          [status]=\"item.status\"\n          [color]=\"item.color\"\n          [blink]=\"item.blink !== false && blink\"\n          [clickable]=\"!!item.action\"\n          [style.flex-basis.%]=\"responsiveMode ? 100 / responsiveMode : null\"\n          (click)=\"onItemClicked(item)\"\n          (keydown.enter)=\"onItemClicked(item)\"\n        ></si-status-bar-item>\n      </div>\n      <div class=\"custom-content bar-item d-flex\">\n        <div #custom [class.pb-2]=\"responsiveMode && expanded\" (siResizeObserver)=\"resizeHandler()\">\n          <ng-content></ng-content>\n        </div>\n      </div>\n      <ng-container *ngIf=\"muteButton !== undefined && !responsiveMode\">\n        <ng-container *ngTemplateOutlet=\"muteButtonTemplate\"></ng-container>\n      </ng-container>\n    </div>\n  </div>\n  <div *ngIf=\"responsiveMode\" class=\"d-flex align-items-center justify-content-center\">\n    <a\n      class=\"collapse-expand text-center p-0 focus-force\"\n      tabindex=\"0\"\n      role=\"button\"\n      [attr.aria-label]=\"(expanded ? collapseButtonText : expandButtonText) | translate\"\n      [attr.aria-expanded]=\"!!expanded\"\n      [attr.aria-controls]=\"statusId\"\n      (keydown.enter)=\"toggleExpand()\"\n      (click)=\"toggleExpand()\"\n    >\n      <i class=\"icon element-down-2\" [class.expanded]=\"expanded === 2\"></i>\n    </a>\n  </div>\n</div>\n\n<ng-template #muteButtonTemplate>\n  <button\n    type=\"button\"\n    class=\"bar-item mute-button btn btn-circle btn-sm btn-tertiary ms-5\"\n    [class.element-sound-mute]=\"muteButton === false\"\n    [class.element-sound-on]=\"muteButton === true\"\n    [attr.aria-label]=\"muteButtonText | translate\"\n    (click)=\"muteToggle.emit()\"\n  ></button>\n</ng-template>\n", styles: [":host{display:block;position:relative;margin-block-end:16px}:host ::ng-deep .blink{transition:color calc(.5s * var(--element-animations-enabled, 1))}:host ::ng-deep .bg{transition:opacity calc(.5s * var(--element-animations-enabled, 1))}.pulse-off ::ng-deep .bg{opacity:.1!important}.status-bar-wrapper{padding-inline:8px;background-color:var(--element-base-1)}.status-bar-wrapper>*{display:flex}.mobile-spacer{margin-block:4px;margin-inline:0;background:var(--element-base-0);block-size:1px}.bar-item{margin-block:8px}.custom-content{margin-inline-start:auto}.clickable{cursor:pointer}.mute-padding{padding-inline-end:44px}.mobile-mute{position:absolute;inset-inline-end:8px}.main-content .mute-button{margin-inline-end:4px}.collapse-expand{background-color:var(--element-base-1);border-radius:0 0 20px 20px;box-shadow:0 8px 8px #00000014;inline-size:40px;cursor:pointer}.collapse-expand i{margin-block-start:-4px;transition:transform calc(.4s * var(--element-animations-enabled, 1))}.collapse-expand i.expanded{transform:rotate(180deg)}.compact .bar-item{margin-block:2px}.responsive si-status-bar-item ::ng-deep .status-item{max-inline-size:unset;position:absolute;inset-block:0;inset-inline:0 4px}.responsive si-status-bar-item.mute-padding ::ng-deep .status-item{inset-inline-end:44px}.responsive.expanded{position:absolute;inset-block-start:0;inset-inline:0;z-index:1028}.responsive .status-bar-wrapper>.main-content{display:block;transition:max-block-size calc(.5s * var(--element-animations-enabled, 1))}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { items: [{
                type: Input
            }], blink: [{
                type: Input
            }], muteButton: [{
                type: Input
            }], muteButtonText: [{
                type: Input
            }], allOkText: [{
                type: Input
            }], compact: [{
                type: Input
            }], blinkPulse: [{
                type: Input
            }], expandButtonText: [{
                type: Input
            }], collapseButtonText: [{
                type: Input
            }], muteToggle: [{
                type: Output
            }], theBar: [{
                type: ViewChild,
                args: ['thebar', { static: true }]
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], custom: [{
                type: ViewChild,
                args: ['custom', { static: true }]
            }] } });

class SiStatusBarModule {
}
SiStatusBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiStatusBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarModule, declarations: [SiStatusBarComponent,
        SiStatusBarItemComponent], imports: [CommonModule,
        SiIconModule,
        SiResizeObserverModule,
        SiTranslateModule], exports: [SiStatusBarComponent] });
SiStatusBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarModule, imports: [CommonModule,
        SiIconModule,
        SiResizeObserverModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiStatusBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiResizeObserverModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiStatusBarComponent,
                        SiStatusBarItemComponent
                    ],
                    exports: [
                        SiStatusBarComponent
                    ]
                }]
        }] });

// ------------------------------------------------------------------------------

// ------------------------------------------------------------------------------

class SiCircleStatusComponent {
    constructor() {
        /**
         * event direction is out
         */
        this.eventOut = false;
        /**
         * Whether the status should appear with a pulsing circle around the badge.
         */
        this.blink = false;
        this.backgroundClass = '';
        this.theAriaLabel = '';
        this.blinkOn = false;
        this.contrastFix = false;
        this.blinkService = inject(BlinkService);
        this.cdRef = inject(ChangeDetectorRef);
    }
    ngOnChanges(changes) {
        if (this.blinkService && changes.blink) {
            this.blinkSubs?.unsubscribe();
            if (this.blink) {
                const pulse = this.blinkPulse ?? this.blinkService.pulse$;
                this.blinkSubs = pulse.subscribe(onOff => {
                    this.blinkOn = onOff;
                    this.cdRef.markForCheck();
                });
            }
            else {
                this.blinkOn = false;
            }
        }
        if (changes.status) {
            this.statusIcon = this.status && this.status !== 'success' ? STATUS_ICON[this.status] : undefined;
            this.backgroundClass = this.statusIcon?.background ?? '';
        }
        if (changes.ariaLabel || changes.status || changes.icon) {
            this.calcAriaLabel();
        }
        if (changes.color || changes.blink) {
            setTimeout(() => {
                this.cdRef.markForCheck();
                this.contrastFix = !!this.color && this.blink && this.calculateContrastFix();
            });
        }
    }
    ngOnDestroy() {
        this.blinkSubs?.unsubscribe();
    }
    calcAriaLabel() {
        if (this.ariaLabel) {
            this.theAriaLabel = this.ariaLabel;
        }
        else {
            const statusName = this.status && STATUS_ICON[this.status] ? this.status : 'none';
            const direction = this.eventOut ? ' out' : '';
            const iconName = this.icon?.replace('element-', '') ?? '';
            this.theAriaLabel = `${iconName}${this.status && this.icon ? ' in ' : ''}status ${statusName}${direction}`;
        }
    }
    calculateContrastFix() {
        // see https://www.w3.org/TR/AERT/#color-contrast
        const rgb = getComputedStyle(this.bg.nativeElement).backgroundColor?.match(/\d+/g)?.map(v => +v);
        return !!rgb && Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000) <= 128;
    }
}
SiCircleStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiCircleStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiCircleStatusComponent, selector: "si-circle-status", inputs: { status: "status", color: "color", icon: "icon", eventOut: "eventOut", blink: "blink", blinkPulse: "blinkPulse", ariaLabel: "ariaLabel" }, viewQueries: [{ propertyName: "bg", first: true, predicate: ["bg"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"status-indication d-flex align-items-center justify-content-center\"\n  [class.has-icon]=\"icon\"\n  role=\"status\"\n  [attr.aria-label]=\"theAriaLabel | translate\"\n>\n  <div\n    #bg\n    class=\"bg\"\n    [class.pulse]=\"blinkOn\"\n    [class.deprecated-color]=\"!status\"\n    [class.contrast-fix]=\"contrastFix\"\n    [ngClass]=\"backgroundClass\"\n    [style.background-color]=\"color\"\n  ></div>\n  <div *ngIf=\"!status\" class=\"deprecated-dot\" [style.background-color]=\"color\"></div>\n  <si-icon\n    *ngIf=\"statusIcon\"\n    size=\"status-icon\"\n    class=\"indicator\"\n    [icon]=\"statusIcon.icon\"\n    [color]=\"statusIcon.color\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"statusIcon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <i\n    *ngIf=\"eventOut\"\n    class=\"icon text-body event-out element-right-4 flip-rtl\"\n    role=\"presentation\"\n  ></i>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon position-relative text-body\"\n    [ngClass]=\"icon\"\n    role=\"presentation\"\n  ></i>\n</div>\n", styles: [":host{display:inline-block;vertical-align:middle}:host ::ng-deep .status-icon{font-size:24px}.status-indication{position:relative;inline-size:40px;block-size:40px;border-radius:50%;border:1px solid transparent}.status-indication.has-icon{border-color:var(--element-ui-4);background-color:var(--element-base-1);margin-block:6px;margin-inline:0 12px}.status-indication.has-icon .deprecated-dot,.status-indication.has-icon .indicator{position:absolute}.status-indication.has-icon .deprecated-dot{inset-block-start:3px;inset-inline-end:-1px}.status-indication.has-icon .indicator{inset-block-start:-9px;inset-inline-end:-13px}.status-indication>*{pointer-events:none;line-height:0}.event-out{position:absolute;inset-block-end:11px;inset-inline-end:-13px}.bg{position:absolute;inset:0;border-radius:50%;transition:opacity calc(.5s * var(--element-animations-enabled, 1));opacity:0}.pulse.deprecated-color{opacity:.5}.pulse.deprecated-color.contrast-fix{opacity:.25}.pulse:not(.deprecated-color){opacity:1}.deprecated-dot{inline-size:8px;block-size:8px;border-radius:50%}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-circle-status', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"status-indication d-flex align-items-center justify-content-center\"\n  [class.has-icon]=\"icon\"\n  role=\"status\"\n  [attr.aria-label]=\"theAriaLabel | translate\"\n>\n  <div\n    #bg\n    class=\"bg\"\n    [class.pulse]=\"blinkOn\"\n    [class.deprecated-color]=\"!status\"\n    [class.contrast-fix]=\"contrastFix\"\n    [ngClass]=\"backgroundClass\"\n    [style.background-color]=\"color\"\n  ></div>\n  <div *ngIf=\"!status\" class=\"deprecated-dot\" [style.background-color]=\"color\"></div>\n  <si-icon\n    *ngIf=\"statusIcon\"\n    size=\"status-icon\"\n    class=\"indicator\"\n    [icon]=\"statusIcon.icon\"\n    [color]=\"statusIcon.color\"\n    [stackedIcon]=\"statusIcon.stacked\"\n    [stackedColor]=\"statusIcon.stackedColor\"\n    alt=\"\"\n  ></si-icon>\n  <i\n    *ngIf=\"eventOut\"\n    class=\"icon text-body event-out element-right-4 flip-rtl\"\n    role=\"presentation\"\n  ></i>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon position-relative text-body\"\n    [ngClass]=\"icon\"\n    role=\"presentation\"\n  ></i>\n</div>\n", styles: [":host{display:inline-block;vertical-align:middle}:host ::ng-deep .status-icon{font-size:24px}.status-indication{position:relative;inline-size:40px;block-size:40px;border-radius:50%;border:1px solid transparent}.status-indication.has-icon{border-color:var(--element-ui-4);background-color:var(--element-base-1);margin-block:6px;margin-inline:0 12px}.status-indication.has-icon .deprecated-dot,.status-indication.has-icon .indicator{position:absolute}.status-indication.has-icon .deprecated-dot{inset-block-start:3px;inset-inline-end:-1px}.status-indication.has-icon .indicator{inset-block-start:-9px;inset-inline-end:-13px}.status-indication>*{pointer-events:none;line-height:0}.event-out{position:absolute;inset-block-end:11px;inset-inline-end:-13px}.bg{position:absolute;inset:0;border-radius:50%;transition:opacity calc(.5s * var(--element-animations-enabled, 1));opacity:0}.pulse.deprecated-color{opacity:.5}.pulse.deprecated-color.contrast-fix{opacity:.25}.pulse:not(.deprecated-color){opacity:1}.deprecated-dot{inline-size:8px;block-size:8px;border-radius:50%}\n"] }]
        }], propDecorators: { status: [{
                type: Input
            }], color: [{
                type: Input
            }], icon: [{
                type: Input
            }], eventOut: [{
                type: Input
            }], blink: [{
                type: Input
            }], blinkPulse: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], bg: [{
                type: ViewChild,
                args: ['bg']
            }] } });

class SiCircleStatusModule {
}
SiCircleStatusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiCircleStatusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusModule, declarations: [SiCircleStatusComponent], imports: [CommonModule, SiIconModule, SiTranslateModule], exports: [SiCircleStatusComponent] });
SiCircleStatusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusModule, imports: [CommonModule, SiIconModule, SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCircleStatusModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiIconModule, SiTranslateModule],
                    declarations: [SiCircleStatusComponent],
                    exports: [SiCircleStatusComponent]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiTreeViewItemTemplateDirective {
    constructor() {
        this.template = inject((TemplateRef));
    }
}
SiTreeViewItemTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiTreeViewItemTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiTreeViewItemTemplateDirective, selector: "[siTreeViewItemTemplate]", inputs: { name: ["siTreeViewItemTemplate", "name"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siTreeViewItemTemplate]'
                }]
        }], propDecorators: { name: [{
                type: Input,
                args: ['siTreeViewItemTemplate']
            }] } });

class FolderStateEventArgs {
    constructor(treeItem, oldState, newState) {
        this.treeItem = treeItem;
        this.oldState = oldState;
        this.newState = newState;
    }
}
class CheckboxClickEventArgs {
    constructor(target, oldState, newState) {
        this.target = target;
        this.oldState = oldState;
        this.newState = newState;
    }
}
class ClickEventArgs {
    constructor(target, mouseEvent) {
        this.target = target;
        this.mouseEvent = mouseEvent;
    }
}
class ClickEventArgs2 {
    constructor(target, itemTop, itemHeight) {
        this.target = target;
        this.itemTop = itemTop;
        this.itemHeight = itemHeight;
    }
}
class LoadChildrenEventArgs {
    constructor(treeItem, callback) {
        this.treeItem = treeItem;
        this.callback = callback;
    }
}
class ItemsVirtualizedArgs {
    constructor(treeItems, virtualized) {
        this.treeItems = treeItems;
        this.virtualized = virtualized;
    }
}
const DEFAULT_TREE_ICON_SET = {
    headerHome: 'element-home',
    headerArrow: 'element-left-2 flip-rtl',
    itemMenu: 'element-options-vertical',
    itemCollapsed: 'element-down-2',
    itemCollapsedFlat: 'element-right-2 flip-rtl',
    itemCollapsedLeft: 'element-right-2 flip-rtl',
    itemExpanded: 'element-up-2',
    itemExpandedFlat: 'element-down-3',
    itemExpandedLeft: 'element-down-2'
};
const DEFAULT_CHILDREN_INDENTATION = 14;

const ROOT_LEVEL = -1;
const childrenLoaded = (item) => (item.children?.length ?? 0) > 0;
const hasChildren = (item) => item.state !== 'leaf';
const removeUndefinedState = (state) => state ?? 'collapsed';
/**
 * Collapses the tree item except for leaf items.
 */
const collapse = (item) => {
    if (item?.state !== 'leaf') {
        item.state = 'collapsed';
    }
};
/**
 * Expands the tree item except for leaf items.
 */
const expand = (item) => {
    if (item?.state !== 'leaf') {
        item.state = 'expanded';
    }
};
const expandRecursive = (item) => {
    if (item.parent) {
        expand(item.parent);
        expandRecursive(item.parent);
    }
};
const boxClicked = (treeItem, inheritChecked) => {
    if (treeItem.showCheckbox) {
        treeItem.checked = treeItem.checked === 'checked' ? 'unchecked' : 'checked';
        if (inheritChecked) {
            checkAllChildren(treeItem, treeItem.checked);
            updateCheckStateOfParents(treeItem);
        }
    }
    if (treeItem.showOptionbox && treeItem.checked === 'unchecked') {
        treeItem.parent?.children?.forEach(item => item.checked = 'unchecked');
        treeItem.checked = 'checked';
    }
};
/**
 * Changes the folder state to a new state based on the old state.
 * If a client calls this method, the same is responsible to load the children in case the new state is 'expanding'!!!
 *
 * @param deleteChildrenOnCollapse If set to true, the children are deleted upon collapsing the item.
 */
const doFolderStateChange = (item, deleteChildrenOnCollapse, newState) => {
    if (item?.state === 'collapsed' && (!newState || newState === 'expanded')) {
        item.state = childrenLoaded(item) ? 'expanded' : 'expanding';
    }
    else if (item?.state === 'expanding' && (!newState || newState === 'collapsed')) {
        item.state = 'collapsed';
    }
    else if (item?.state === 'expanded' && (!newState || newState === 'collapsed')) {
        if (deleteChildrenOnCollapse) {
            item.children = [];
        }
        item.state = 'collapsed';
    }
};
const enableCheckboxRecursive = (item, enable) => {
    item.showCheckbox = enable;
    if (childrenLoaded(item)) {
        item.children?.forEach(child => enableCheckboxRecursive(child, enable));
    }
};
const enableOptionboxRecursive = (item, enable) => {
    item.showOptionbox = enable;
    if (childrenLoaded(item)) {
        item.children?.forEach(child => enableOptionboxRecursive(child, enable));
    }
};
const parentCountRecursive = (item, stackLevel) => {
    if (item?.parent && item.parent.level !== ROOT_LEVEL) {
        return parentCountRecursive(item.parent, stackLevel + 1);
    }
    return stackLevel;
};
const selectItemsBetween = (roots, start, end) => {
    if (start === end) {
        return;
    }
    const foundItems = [];
    const inBetweenState = { startIsFirst: false, inBetween: false, done: false };
    for (const root of roots) {
        getItemsBetweenRecursive(foundItems, root, start, end, inBetweenState);
        if (inBetweenState.done) {
            break;
        }
    }
    foundItems.forEach(item => {
        if (item.selectable) {
            item.selected = true;
        }
    });
    return foundItems;
};
const selectRecursive = (item, select) => {
    if (item.selectable) {
        item.selected = select;
    }
    if (childrenLoaded(item)) {
        item.children?.forEach(child => selectRecursive(child, select));
    }
};
const setBoxStateRecursive = (item, state) => {
    item.checked = state;
    if (childrenLoaded(item)) {
        item.children?.forEach(child => setBoxStateRecursive(child, state));
    }
};
/**
 * Sets the item to be active or not; all other items of the tree are reset.
 */
const setActive = (item, active) => {
    const getRoot = (_item) => _item.parent ? getRoot(_item.parent) : _item;
    resetActive(getRoot(item));
    item.active = active;
};
const resetActive = (item) => {
    item.active = false;
    if (childrenLoaded(item)) {
        item.children?.forEach(childItem => resetActive(childItem));
    }
};
/**
 * Sets the treeItem as selectable or not
 */
const setSelectable = (item, selectable) => {
    item.selectable = selectable;
    if (item.selected && !item.selectable) {
        item.selected = selectable;
    }
};
const deleteItem = (item) => {
    if (item.parent) {
        const idx = item.parent.children?.indexOf(item) ?? -1;
        if (idx < 0) {
            return;
        }
        item.parent.children?.splice(idx, 1);
    }
};
const addChildItem = (item, child, position) => {
    if (position < 0) {
        return;
    }
    child.parent = item;
    if (!item.children) {
        item.children = [];
        item.children.push(child);
        return;
    }
    if (position >= item.children.length) {
        item.children.push(child);
        return;
    }
    item.children.splice(position, 0, child);
};
const checkAllChildren = (treeItem, check) => {
    if (childrenLoaded(treeItem)) {
        treeItem.children?.forEach(item => {
            item.checked = check;
            checkAllChildren(item, check);
        });
    }
};
const getItemsBetweenRecursive = (found, current, start, end, inBetweenState) => {
    if (inBetweenState.done) {
        return;
    }
    if (current === start && !inBetweenState.inBetween) {
        // This is the first found tree item within the selection, the start tree item (the item which is clicked first) is ordered
        // before the end tree item (the item which is clicked second).
        // The recursive function is from now on handling tree node which are in between the two selections.
        // Note, we don't need to select it, as it is already selected with previous click
        inBetweenState.startIsFirst = true;
        inBetweenState.inBetween = true;
    }
    else if (current === end && !inBetweenState.inBetween) {
        // This is the first found tree item within the selection, the end tree item (the item which is clicked second) is ordered
        // before the start tree item (the item which is clicked first).
        // The recursive function is from now on handling tree node which are in between the two selections.
        inBetweenState.startIsFirst = false;
        inBetweenState.inBetween = true;
        found.push(current);
    }
    else if (current === end && inBetweenState.inBetween) {
        found.push(current);
        inBetweenState.inBetween = false;
        inBetweenState.done = true;
        return; // the end of the search
    }
    else if (current === start && inBetweenState.inBetween) {
        inBetweenState.inBetween = false;
        inBetweenState.done = true;
        return; // the end of the search
    }
    else if (inBetweenState.inBetween) {
        found.push(current);
    }
    if (current.state === 'expanded') {
        current.children?.forEach(item => getItemsBetweenRecursive(found, item, start, end, inBetweenState));
    }
};
const updateCheckStateOfParents = (treeItem) => {
    if (treeItem.parent) {
        let allChecked = true;
        let allUnchecked = true;
        for (const item of treeItem.parent?.children ?? []) {
            if (item.checked === 'indeterminate') {
                treeItem.parent.checked = 'indeterminate';
                updateCheckStateOfParents(treeItem.parent);
                return;
            }
            if (item.checked === 'checked') {
                allUnchecked = false;
            }
            if (item.checked === 'unchecked') {
                allChecked = false;
            }
            if (!allChecked && !allUnchecked) {
                treeItem.parent.checked = 'indeterminate';
                updateCheckStateOfParents(treeItem.parent);
                return;
            }
        }
        if (allChecked) {
            treeItem.parent.checked = 'checked';
        }
        else {
            treeItem.parent.checked = 'unchecked';
        }
        updateCheckStateOfParents(treeItem.parent);
    }
};

/**
 * Supports communication between the TreeViewComponent and the TreeViewItemComponent and the services.
 * Stores tree specific settings.
 * One instance per TreeViewComponent exists.
 */
class SiTreeViewService {
    constructor() {
        this.clickEvent = new Subject();
        this.contextMenuOpenEvent = new Subject();
        this.folderClickEvent = new Subject();
        this.checkboxClickEvent = new Subject();
        this.loadChildrenEvent = new Subject();
        this.scrollIntoViewEvent = new Subject();
        this.inheritChecked = true;
        this.enableContextMenuButton = true;
        this.deleteChildrenOnCollapse = false;
        this.enableSelection = false;
        this.enableDataField1 = false;
        this.enableDataField2 = false;
        this.enableStateIndicator = true;
        this.enableIcon = true;
        this.enableCheckbox = false;
        this.enableOptionbox = false;
        this.flatTree = false;
        this.singleSelectMode = false;
        this.childrenIndentation = 14;
        this.groupedList = false;
        this.isVirtualized = true;
        this.folderStateStart = true;
        this.contextMenuDropUp = false;
        this.noActionsString = 'No actions available';
        this.triggerMarkForCheck = new Subject();
    }
    isGroupedItem(item) {
        return (!item.parent || item.parent.level === ROOT_LEVEL) && this.groupedList;
    }
}
SiTreeViewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTreeViewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewService, decorators: [{
            type: Injectable
        }] });

class SiTreeViewItemComponent {
    constructor() {
        this.element = inject(ElementRef);
        this.siTreeViewService = inject(SiTreeViewService);
        this.cdRef = inject(ChangeDetectorRef);
        this.scrollIntoView = new Subject();
        this.childrenLoaded = new Subject();
        this.contextMenuAriaLabel = 'Open context menu';
        this._contextMenuItems = [];
        this._noActions = [{ title: this.siTreeViewService.noActionsString, isHeading: true }];
        this._hasNoActions = false;
        this.stickyEndItems = false;
        this.displayFolderState = false;
        this.subscriptions = [];
        this._indentLevel = 0;
    }
    /**
     * @defaultref {@link _indentLevel}
     */
    set indentLevel(value) {
        this._indentLevel = value;
    }
    /**
     * @defaultref {@link _contextMenuItems}
     */
    set contextMenuItemsProvider(items) {
        this._contextMenuItems = items;
    }
    get contextMenuItems() {
        if (this._contextMenuItemsCurrent) {
            return this._contextMenuItemsCurrent;
        }
        if (this._contextMenuItems) {
            if (Array.isArray(this._contextMenuItems)) {
                return this._contextMenuItems;
            }
            else {
                this._contextMenuItemsCurrent = this._contextMenuItems(this.treeItem);
                if (!this._contextMenuItemsCurrent?.length) {
                    this._contextMenuItemsCurrent = this._noActions;
                    this._hasNoActions = true;
                }
                return this._contextMenuItemsCurrent;
            }
        }
        else {
            return null;
        }
    }
    get isContextMenuButtonVisible() {
        return this.enableContextMenuButton && !!this._contextMenuItems && (!Array.isArray(this._contextMenuItems)
            || !!this._contextMenuItems.length);
    }
    /**
     * @defaultref {@link stickyEndItems}
     * @deprecated Use `stickyEndItems` instead.
     */
    set stickyRightItems(value) {
        this.stickyEndItems = value;
    }
    get stickyRightItems() {
        return this.stickyEndItems;
    }
    ngAfterViewInit() {
        if (this.savedElement) {
            this.siTreeViewService.scrollIntoViewEvent.next(this.savedElement);
            this.savedElement = undefined;
        }
        this.nextSiblingElement = this.element.nativeElement?.nextElementSibling;
    }
    ngOnInit() {
        this.subscriptions.push(this.scrollIntoView.subscribe(event => this.onScrollIntoViewByConsumer(event)));
        this.subscriptions.push(this.childrenLoaded.subscribe(event => this.childrenLoadingDone(event)));
        this.subscriptions.push(this.siTreeViewService.triggerMarkForCheck.subscribe(() => this.cdRef.markForCheck()));
    }
    ngOnDestroy() {
        this.subscriptions.filter(subscription => !!subscription).forEach(subscription => subscription.unsubscribe());
    }
    get enableSelection() {
        return this.siTreeViewService.enableSelection;
    }
    get enableContextMenuButton() {
        return this.siTreeViewService.enableContextMenuButton;
    }
    get enableDataField1() {
        return this.siTreeViewService.enableDataField1;
    }
    get enableDataField2() {
        return this.siTreeViewService.enableDataField2;
    }
    get deleteChildrenOnCollapse() {
        return this.siTreeViewService.deleteChildrenOnCollapse;
    }
    get paddingStart() {
        return this.siTreeViewService.groupedList ? '0' : (this._indentLevel * this.siTreeViewService.childrenIndentation) + 'px';
    }
    get biggerPaddingStart() {
        const basePadding = this.showFolderStateStart ? 24 : 0;
        return (this.siTreeViewService.groupedList
            ? basePadding
            : this._indentLevel * this.siTreeViewService.childrenIndentation + basePadding) + 'px';
    }
    get isGroupedItem() {
        return this.siTreeViewService.isGroupedItem(this.treeItem);
    }
    get showFolderState() {
        return !this.isFlatTree() && (!!this.isGroupedItem || !this.siTreeViewService.groupedList);
    }
    get showFolderStateStart() {
        return this.displayFolderState && this.siTreeViewService.folderStateStart && !this.isFlatTree()
            && (!!this.isGroupedItem || !this.siTreeViewService.groupedList);
    }
    get showFolderStateEnd() {
        return this.displayFolderState && !this.showFolderStateStart;
    }
    get isExpanding() {
        return this.treeItem.state === 'expanding';
    }
    get showStateIndicator() {
        return this.siTreeViewService.enableStateIndicator;
    }
    get showIcon() {
        return this.siTreeViewService.enableIcon && !!this.treeItem.icon;
    }
    get showCheckOrOptionBox() {
        return !!this.treeItem.showCheckbox || !!this.treeItem.showOptionbox;
    }
    getItemFolderStateClass() {
        if (this.treeItem.state === 'collapsed') {
            if (this.siTreeViewService.flatTree) {
                // flat tree mode
                return this.icons.itemCollapsedFlat;
            }
            else if (this.siTreeViewService.folderStateStart) {
                // normal tree mode; folder state icon shown on the left (in LTR) side
                return this.icons.itemCollapsedLeft; // si-tree-view-item-collapsed
            }
            else {
                // normal tree mode; folder state icon shown on the right (in LTR) side
                return this.icons.itemCollapsed; // si-tree-view-item-collapsed
            }
        }
        else if (this.treeItem.state === 'expanding') {
            return 'si-tree-view-item-expanding'; // because empty is treated as disabled
        }
        else if (this.treeItem.state === 'expanded') {
            if (this.siTreeViewService.flatTree) {
                // flat tree mode
                return this.icons.itemExpandedFlat;
            }
            else if (this.siTreeViewService.folderStateStart) {
                // normal tree mode; folder state icon shown on the left (in LTR) side
                return this.icons.itemExpandedLeft; // si-tree-view-item-expanded
            }
            else {
                // normal tree mode; folder state icon shown on the right (in LTR) side
                return this.icons.itemExpanded; // si-tree-view-item-expanded
            }
        }
        return '';
    }
    /**
     * Show filled icon if the item is selected.
     **/
    getIconClass() {
        let iconClass = this.treeItem.icon;
        if (this.treeItem.selected && this.treeItem.selectable && this.enableSelection) {
            iconClass = iconClass + ' ' + iconClass + '-filled';
        }
        return iconClass ?? '';
    }
    getStateIndicatorColor() {
        return this.treeItem ? this.treeItem.stateIndicatorColor : '';
    }
    onItemFolderClicked(newState) {
        this.nextSiblingElement = this.element.nativeElement?.nextElementSibling;
        const oldState = removeUndefinedState(this.treeItem.state);
        doFolderStateChange(this.treeItem, this.deleteChildrenOnCollapse, newState);
        this.siTreeViewService.folderClickEvent.next(new FolderStateEventArgs(this.treeItem, oldState, removeUndefinedState(this.treeItem.state)));
        if (this.treeItem.state === 'expanding') {
            this.siTreeViewService.loadChildrenEvent.next(this.treeItem);
        }
        if (this.treeItem.state === 'expanded') {
            this.scrollChildNodeIntoViewPort();
        }
    }
    childrenLoadingDone(item) {
        if (item === this.treeItem) {
            asyncScheduler.schedule(() => this.scrollChildNodeIntoViewPort(), 0);
        }
    }
    scrollChildNodeIntoViewPort() {
        const task = () => {
            const container = this.element.nativeElement.closest('.si-tree-view');
            const first = this.element.nativeElement;
            this.nextSiblingElement = this.nextSiblingElement?.previousElementSibling ?? this.nextSiblingElement;
            const last = container?.contains(this.nextSiblingElement) ? this.nextSiblingElement :
                this.element.nativeElement.parentElement?.lastElementChild;
            const fits = (container?.offsetHeight - (last?.offsetTop + last?.getBoundingClientRect().height - first?.offsetTop)) >= 0;
            const targetElement = fits ? last : first;
            if (targetElement) {
                const observer = new window.IntersectionObserver(([entry]) => {
                    requestAnimationFrame(() => {
                        const scrollElement = targetElement.firstChild ?? targetElement;
                        if (fits) {
                            scrollElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        }
                        else {
                            // scrollIntoView messes up with page scroll when body also has a scroll for { block : 'start' }
                            const totalScrollTop = container?.scrollTop + first?.getBoundingClientRect().top - container?.getBoundingClientRect().top;
                            container?.scrollTo({ top: totalScrollTop, behavior: 'smooth' });
                        }
                    });
                    observer.disconnect();
                }, {
                    root: null,
                    threshold: 1
                });
                observer.observe(targetElement);
            }
        };
        asyncScheduler.schedule(task, 0);
    }
    onItemClicked(event) {
        this.siTreeViewService.clickEvent.next(new ClickEventArgs(this.treeItem, event));
        if (!this.siTreeViewService.enableSelection && (this.siTreeViewService.enableCheckbox || this.siTreeViewService.enableOptionbox)) {
            this.onBoxClicked();
        }
    }
    onMouseDownTreeItem(event) {
        if (event.shiftKey) {
            // let selection: Selection = window.getSelection();
            // selection.removeAllRanges();
            // we do not want the tree item text selected: thus we prevent the default behavior in this situation
            event.preventDefault();
        }
    }
    onToggleContextMenu(state) {
        if (state) {
            const elRect = this.element.nativeElement.getBoundingClientRect();
            this.siTreeViewService.contextMenuOpenEvent.next(new ClickEventArgs2(this.treeItem, elRect.top, elRect.height));
            this.siTreeViewService.activeContextDropdown = this.contextDropdown;
        }
        else {
            if (this._contextMenuItemsCurrent) {
                this._contextMenuItemsCurrent = undefined;
                this._hasNoActions = false;
            }
            this.siTreeViewService.activeContextDropdown = undefined;
            this.element.nativeElement.focus();
        }
    }
    getInputType() {
        // This method will be called only when either of the showCheckbox or showOptionBox is true, thus
        // following condition can be shortened
        return this.treeItem.showCheckbox ? 'checkbox' : 'radio';
    }
    onBoxClicked() {
        if (!this.treeItem.selectable) {
            return;
        }
        const oldState = this.treeItem.checked ?? 'unchecked';
        boxClicked(this.treeItem, this.siTreeViewService.inheritChecked);
        this.siTreeViewService.checkboxClickEvent.next(new CheckboxClickEventArgs(this.treeItem, oldState, this.treeItem.checked ?? 'unchecked'));
    }
    renderMatchingTemplate(treeItem) {
        const templateDirective = this.templates.find(td => td.name === treeItem.templateName);
        return templateDirective ? templateDirective.template : this.templates.toArray()[0].template;
    }
    onContextMenu(event) {
        this.handleContextMenuEvent(event);
    }
    onKeydown(event) {
        const rtlCorrectedKey = correctKeyRTL(event.key);
        if (rtlCorrectedKey === 'Enter') {
            event.preventDefault();
            if (this.contextDropdown?.isOpen) {
                if (this._hasNoActions) {
                    this.contextDropdown?.close();
                }
            }
            else {
                this.onItemClicked(event);
            }
        }
        else if (rtlCorrectedKey === 'ContextMenu' || (rtlCorrectedKey === 'F10' && event.shiftKey)) {
            this.handleContextMenuEvent(event);
        }
        else if (rtlCorrectedKey === 'ArrowLeft' && !this.contextDropdown?.isOpen) {
            if ((!this.isFlatTree() && this.showFolderStateStart &&
                (this.treeItem.state !== 'leaf' || this.isGroupedItem)) || this.showFolderStateEnd) {
                event.preventDefault();
                this.onItemFolderClicked('collapsed');
            }
        }
        else if (rtlCorrectedKey === 'ArrowRight' && !this.contextDropdown?.isOpen) {
            if ((this.showFolderStateStart && (this.treeItem.state !== 'leaf' || this.isGroupedItem)) || this.showFolderStateEnd) {
                event.preventDefault();
                this.onItemFolderClicked('expanded');
            }
        }
        else if (this.showCheckOrOptionBox && (rtlCorrectedKey === 'Space' || rtlCorrectedKey === ' ')) {
            event.preventDefault();
            this.onBoxClicked();
        }
    }
    handleContextMenuEvent(event) {
        if (this.isContextMenuButtonVisible) {
            event.preventDefault();
            this.contextDropdown.toggle();
            this.siTreeViewService.activeContextDropdown?.close();
            this.siTreeViewService.activeContextDropdown = this.contextDropdown;
            const windowContextMenuHandler = (contextMenuEvent) => {
                contextMenuEvent.preventDefault();
                window.removeEventListener('contextmenu', windowContextMenuHandler);
            };
            window.addEventListener('contextmenu', windowContextMenuHandler);
        }
    }
    isFlatTree() {
        return this.siTreeViewService.flatTree;
    }
    /**
     * Called by the consumer when they want a node to be scrolled into view.
     */
    onScrollIntoViewByConsumer(item) {
        if (item === this.treeItem) {
            this.savedElement = this.element;
            this.siTreeViewService.scrollIntoViewEvent.next(this.element);
        }
    }
}
SiTreeViewItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTreeViewItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTreeViewItemComponent, selector: "si-tree-view-item", inputs: { indentLevel: "indentLevel", treeItem: "treeItem", scrollIntoView: "scrollIntoView", childrenLoaded: "childrenLoaded", contextMenuAriaLabel: "contextMenuAriaLabel", templates: "templates", contextMenuItemsProvider: "contextMenuItemsProvider", stickyRightItems: "stickyRightItems", stickyEndItems: "stickyEndItems", displayFolderState: "displayFolderState", icons: "icons" }, host: { listeners: { "contextmenu": "onContextMenu($event)", "keydown": "onKeydown($event)" } }, viewQueries: [{ propertyName: "contextDropdown", first: true, predicate: ["contextDropdown"], descendants: true }], ngImport: i0, template: "<div\n  *ngIf=\"!isGroupedItem\"\n  class=\"si-tree-view-li si-tree-view-li-item focus-direct-sub-inside\"\n  [class.si-tree-view-item-context-menu-visible]=\"contextDropdown?.isOpen ?? false\"\n  [class.si-tree-view-item-active]=\"treeItem.active\"\n  [class.si-tree-view-item-selected]=\"enableSelection && treeItem.selected\"\n  [class.si-tree-view-item-not-selectable]=\"!treeItem.selectable\"\n  [attr.aria-disabled]=\"!treeItem.selectable\"\n  [style.padding-inline-start]=\"biggerPaddingStart\"\n>\n  <div class=\"si-tree-view-item\" (mousedown)=\"onMouseDownTreeItem($event)\">\n    <a\n      *ngIf=\"showFolderStateStart && treeItem.state !== 'leaf'\"\n      [style.margin-inline-start]=\"'-' + biggerPaddingStart\"\n      [style.padding-inline-start]=\"paddingStart\"\n      class=\"si-tree-stretch-center si-tree-view-item-toggle\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-dropdown-caret\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n\n    <a *ngIf=\"showCheckOrOptionBox\" class=\"form-check input-box\" tabindex=\"-1\">\n      <input\n        [type]=\"getInputType()\"\n        class=\"form-check-input\"\n        (click)=\"onBoxClicked()\"\n        [indeterminate]=\"this.treeItem.checked === 'indeterminate' ? true: null\"\n        [checked]=\"this.treeItem.checked === 'checked'\"\n        [disabled]=\"!treeItem?.selectable\"\n        [attr.aria-label]=\"(treeItem.label ?? '') | translate\"\n        tabindex=\"-1\"\n      >\n    </a>\n\n    <div\n      class=\"si-tree-view-item-main d-flex flex-grow-1 align-self-stretch\"\n      (click)=\"onItemClicked($event)\"\n    >\n      <div *ngIf=\"showIcon\" class=\"si-tree-stretch-center\">\n        <span class=\"si-tree-view-item-icon\" [ngClass]=\"getIconClass()\"></span>\n      </div>\n\n      <div class=\"si-tree-view-item-object-data d-flex flex-column justify-content-center\">\n        <h5 *ngIf=\"!templates?.length\" >{{ (treeItem.label ?? '') | translate }}</h5>\n        <div *ngIf=\"templates?.length\">\n          <ng-container\n            *ngTemplateOutlet=\"renderMatchingTemplate(treeItem); context: { $implicit: treeItem }\"\n          ></ng-container>\n        </div>\n        <p\n          *ngIf=\"enableDataField1 && treeItem.dataField1\"\n          class=\"si-tree-view-item-object-data-field-1\"\n        >{{ treeItem.dataField1 | translate }}</p>\n        <p\n          *ngIf=\"enableDataField2 && treeItem.dataField2\"\n          class=\"si-tree-view-item-object-data-field-2\"\n        >{{ treeItem.dataField2 | translate }}</p>\n      </div>\n\n      <span\n        *ngIf=\"treeItem.badge\"\n        class=\"badge flex-shrink-0\"\n        [ngClass]=\"'bg-' + (treeItem.badgeColor || 'default')\"\n      >{{ treeItem.badge }}</span>\n    </div>\n\n    <div\n      class=\"si-tree-view-item-end-icons d-flex ms-2 align-items-center\"\n      [class.si-tree-view-item-end-icons-sticky]=\"stickyEndItems\"\n    >\n      <div\n        *ngIf=\"showStateIndicator\"\n        class=\"si-tree-view-state-indicator\"\n        [style.background-color]=\"getStateIndicatorColor()\"\n        [class.si-tree-view-state-indicator-endmost]=\"!enableContextMenuButton && !showFolderStateEnd\"\n      ></div>\n      <div\n        siDropdown\n        #contextDropdown=\"si-dropdown\"\n        dropdownContainer=\"body\"\n        dropdownDirection=\"down\"\n        dropdownAlign=\"end\"\n        [dropdownResponsiveDirection]=\"true\"\n        [dropdownResponsiveDirectionToPlacement]=\"false\"\n        [dropdownIsDisabled]=\"!treeItem?.selectable\"\n        [dropdownFocusOnClose]=\"false\"\n        (dropdownOnToggle)=\"onToggleContextMenu($event)\"\n        *ngIf=\"isContextMenuButtonVisible\"\n        class=\"si-tree-stretch-center si-tree-context-menu-btn\"\n      >\n        <div siDropdownToggle [attr.aria-label]=\"contextMenuAriaLabel | translate\" role=\"button\">\n          <i class=\"si-tree-view-item-icon si-tree-view-menu-btn\" [ngClass]=\"icons?.itemMenu ?? ''\"></i>\n        </div>\n        <si-menu\n          *ngIf=\"contextDropdown?.isOpen ?? false\"\n          [items]=\"contextMenuItems!\"\n          [dropstart]=\"true\"\n          [actionParam]=\"treeItem\"\n          [focusOnLoad]=\"true\"\n          siDropdownMenu\n          class=\"me-5\"\n        ></si-menu>\n      </div>\n      <a\n        *ngIf=\"showFolderStateEnd && treeItem.state !== 'leaf'\"\n        class=\"si-tree-stretch-center si-tree-view-item-toggle si-tree-view-item-toggle-end\"\n        [class.disabled]=\"!getItemFolderStateClass()\"\n        (click)=\"onItemFolderClicked()\"\n        tabindex=\"-1\"\n      >\n        <si-loading-spinner *ngIf=\"isExpanding\" class=\"si-tree-view-item-icon\"></si-loading-spinner>\n        <i\n          *ngIf=\"!isExpanding\"\n          class=\"si-tree-view-item-icon\"\n          [ngClass]=\"getItemFolderStateClass()\"\n        ></i>\n      </a>\n      <div\n        *ngIf=\"showFolderStateEnd && treeItem.state === 'leaf'\"\n        class=\"si-tree-stretch-center si-tree-view-item-toggle si-tree-view-item-toggle-end\"\n      >\n        <i\n          class=\"si-tree-view-item-icon si-tree-view-item-icon-spacer\"\n        ></i>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"isGroupedItem\"\n  class=\"si-tree-view-li focus-direct-sub-inside\"\n>\n  <div class=\"si-tree-view-item-group\">\n    <a\n      *ngIf=\"showFolderStateStart\"\n      class=\"si-tree-stretch-center si-tree-view-item-toggle\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-icon\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n    <a *ngIf=\"showCheckOrOptionBox\" class=\"form-check input-box\" tabindex=\"-1\">\n      <input\n        [type]=\"getInputType()\"\n        class=\"form-check-input\"\n        [indeterminate]=\"this.treeItem.checked === 'indeterminate' ? true: null\"\n        [checked]=\"this.treeItem.checked === 'checked'\"\n        (click)=\"onBoxClicked()\"\n        [attr.aria-label]=\"(treeItem.label ?? '') | translate\"\n        tabindex=\"-1\"\n      >\n    </a>\n    <p class=\"mb-0\">{{ (treeItem.label ?? '') | translate }}</p>\n    <a\n      *ngIf=\"showFolderStateEnd\"\n      class=\"si-tree-stretch-center\"\n      [class.disabled]=\"!getItemFolderStateClass()\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-icon\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n  </div>\n</div>\n", styles: [":host.si-tree-view-top-level-item>.si-tree-view-li{margin-block:var(--si-tree-view-padding-base-vertical)}:host.si-tree-view-top-level-item+::ng-deep .si-tree-view-top-level-item>.si-tree-view-li{margin-block-start:0}.si-tree-view-li.si-tree-view-li-item{position:relative}.si-tree-view-li.si-tree-view-li-item:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-context-menu-visible,.si-tree-view-li.si-tree-view-li-item:hover .si-tree-view-item-end-icons,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-context-menu-visible .si-tree-view-item-end-icons{background-color:var(--si-tree-view-item-hover-color)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected .si-tree-view-item-end-icons{background-color:var(--si-tree-view-item-select-color)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected>.si-tree-view-item>.si-tree-view-item-main>.si-tree-view-item-object-data>h5{font-weight:700}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable h5,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-icon,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-object-data-field-1{color:var(--element-text-disabled)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-end-icons{background-color:unset}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-context-menu-btn>div[role=button]:hover{background-color:unset;cursor:default}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-dropdown-caret,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-toggle-end{cursor:pointer}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-toggle-end .si-tree-view-item-icon{color:var(--element-text-primary)}.si-tree-view-li.si-tree-view-li-item .si-tree-view-item-end-icons{background-color:var(--si-tree-view-background)}.si-tree-view-item-group,.si-tree-view-item{display:flex;align-items:center}.si-tree-stretch-center{align-self:stretch;display:flex;align-items:center;justify-content:center;margin:0;text-decoration:none}.si-tree-view-item-toggle.disabled .si-tree-view-item-icon{inline-size:24px}.si-tree-stretch-center+.si-tree-view-item-toggle-end .si-tree-view-item-icon{margin-inline-start:-4px}.si-tree-view-item-icon{padding-inline-end:8px}.si-tree-view-item-icon.si-tree-view-item-icon-spacer{box-sizing:content-box;inline-size:var(--si-tree-view-icon-size)}.si-tree-view-item-object-data,.si-tree-view-item-icon{margin-block:0;margin-inline:calc(var(--si-tree-view-padding-base-horizontal) / 4)}.si-tree-view-item{justify-content:space-between;cursor:pointer;min-block-size:var(--si-tree-view-item-min-height);padding-inline:var(--si-tree-view-padding-base-horizontal) calc(var(--si-tree-view-padding-base-horizontal) / 4)}:host-context(.si-tree-view-flat) .si-tree-view-item{padding-inline-start:calc(var(--si-tree-view-padding-base-horizontal) / 2)}.si-tree-view-state-indicator{margin-block:0;margin-inline:calc(var(--si-tree-view-padding-base-horizontal) * .75);min-inline-size:6px;block-size:6px;border-radius:50%}.si-tree-view-state-indicator.si-tree-view-state-indicator-endmost{margin-inline-end:calc(var(--si-tree-view-padding-base-horizontal) * 1.75)}.si-tree-view-item-group{padding-block:calc(2 * var(--si-tree-view-padding-base-vertical));padding-inline:calc(var(--si-tree-view-padding-base-horizontal) / 2);cursor:default}.si-tree-view-item-group .si-tree-view-item-dropdown-caret:hover,.si-tree-view-item-group .si-tree-view-item-icon:hover{cursor:pointer}.si-tree-view-item-dropdown-caret,.si-tree-view-item-dropdown-caret:hover,.si-tree-view-item-icon,.si-tree-view-item-icon:hover{color:var(--element-text-primary);text-decoration:none;font-size:var(--si-tree-view-icon-size)}.si-tree-view-item-main{overflow-x:hidden}.si-tree-view-item-main>.badge{margin-block:auto}.si-tree-view-item-object-data>.si-tree-view-item-object-data-field-1{color:var(--si-tree-view-item-object-data-field-1-color)}:host.si-tree-ellipsis .si-tree-view-item-object-data{overflow-x:hidden}.si-tree-view-item-object-data>*{white-space:nowrap;margin-block:0;line-height:var(--si-tree-view-item-line-height)}:host.si-tree-ellipsis .si-tree-view-item-object-data>*{overflow:hidden;text-overflow:ellipsis}.si-tree-view-item-object-data>h5{font-size:var(--si-tree-view-item-object-data-h5-font-size);font-weight:var(--si-tree-view-item-object-data-h5-font-weight)}.si-tree-view-menu-btn{pointer-events:none}.si-tree-view-item-end-icons{block-size:calc(var(--si-tree-view-item-min-height) - 2 * (var(--element-button-focus-width) + var(--element-button-focus-overlay-width)))}.si-tree-view-item-end-icons.si-tree-view-item-end-icons-sticky{position:sticky;inset-inline-end:0}.input-box{margin-inline:2px 12px}.form-check-input{inset-block-start:2px}si-loading-spinner{--loading-spinner-size: var(--si-tree-view-icon-size)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SiDropdownDirective, selector: "[siDropdown]", inputs: ["dropdownIsDisabled", "dropdownIsOpen", "dropdownOpenOnLoad", "dropdownCloseOnSelect", "dropdownCloseOnClickOut", "dropdownCloseOnEsc", "dropdownDirection", "dropdownAlign", "dropdownContainer", "dropdownResponsiveDirection", "dropdownResponsiveDirectionToPlacement", "dropdownPlacement", "dropdownPlacementReference", "dropdownItemSelector", "dropdownItemSelectorException", "dropdownFocusOnOpen", "dropdownFocusOnClose", "closeOnMenuScrollOut", "closeOnPlacementReferenceScrollOut"], outputs: ["dropdownOnToggle", "dropdownOnSelect", "dropdownResponsiveDirectionChange"], exportAs: ["si-dropdown"] }, { kind: "directive", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: ["tabindex"], exportAs: ["si-dropdown-menu"] }, { kind: "directive", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", exportAs: ["si-dropdown-toggle"] }, { kind: "component", type: SiLoadingSpinnerComponent, selector: "si-loading-spinner" }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-tree-view-item', template: "<div\n  *ngIf=\"!isGroupedItem\"\n  class=\"si-tree-view-li si-tree-view-li-item focus-direct-sub-inside\"\n  [class.si-tree-view-item-context-menu-visible]=\"contextDropdown?.isOpen ?? false\"\n  [class.si-tree-view-item-active]=\"treeItem.active\"\n  [class.si-tree-view-item-selected]=\"enableSelection && treeItem.selected\"\n  [class.si-tree-view-item-not-selectable]=\"!treeItem.selectable\"\n  [attr.aria-disabled]=\"!treeItem.selectable\"\n  [style.padding-inline-start]=\"biggerPaddingStart\"\n>\n  <div class=\"si-tree-view-item\" (mousedown)=\"onMouseDownTreeItem($event)\">\n    <a\n      *ngIf=\"showFolderStateStart && treeItem.state !== 'leaf'\"\n      [style.margin-inline-start]=\"'-' + biggerPaddingStart\"\n      [style.padding-inline-start]=\"paddingStart\"\n      class=\"si-tree-stretch-center si-tree-view-item-toggle\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-dropdown-caret\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n\n    <a *ngIf=\"showCheckOrOptionBox\" class=\"form-check input-box\" tabindex=\"-1\">\n      <input\n        [type]=\"getInputType()\"\n        class=\"form-check-input\"\n        (click)=\"onBoxClicked()\"\n        [indeterminate]=\"this.treeItem.checked === 'indeterminate' ? true: null\"\n        [checked]=\"this.treeItem.checked === 'checked'\"\n        [disabled]=\"!treeItem?.selectable\"\n        [attr.aria-label]=\"(treeItem.label ?? '') | translate\"\n        tabindex=\"-1\"\n      >\n    </a>\n\n    <div\n      class=\"si-tree-view-item-main d-flex flex-grow-1 align-self-stretch\"\n      (click)=\"onItemClicked($event)\"\n    >\n      <div *ngIf=\"showIcon\" class=\"si-tree-stretch-center\">\n        <span class=\"si-tree-view-item-icon\" [ngClass]=\"getIconClass()\"></span>\n      </div>\n\n      <div class=\"si-tree-view-item-object-data d-flex flex-column justify-content-center\">\n        <h5 *ngIf=\"!templates?.length\" >{{ (treeItem.label ?? '') | translate }}</h5>\n        <div *ngIf=\"templates?.length\">\n          <ng-container\n            *ngTemplateOutlet=\"renderMatchingTemplate(treeItem); context: { $implicit: treeItem }\"\n          ></ng-container>\n        </div>\n        <p\n          *ngIf=\"enableDataField1 && treeItem.dataField1\"\n          class=\"si-tree-view-item-object-data-field-1\"\n        >{{ treeItem.dataField1 | translate }}</p>\n        <p\n          *ngIf=\"enableDataField2 && treeItem.dataField2\"\n          class=\"si-tree-view-item-object-data-field-2\"\n        >{{ treeItem.dataField2 | translate }}</p>\n      </div>\n\n      <span\n        *ngIf=\"treeItem.badge\"\n        class=\"badge flex-shrink-0\"\n        [ngClass]=\"'bg-' + (treeItem.badgeColor || 'default')\"\n      >{{ treeItem.badge }}</span>\n    </div>\n\n    <div\n      class=\"si-tree-view-item-end-icons d-flex ms-2 align-items-center\"\n      [class.si-tree-view-item-end-icons-sticky]=\"stickyEndItems\"\n    >\n      <div\n        *ngIf=\"showStateIndicator\"\n        class=\"si-tree-view-state-indicator\"\n        [style.background-color]=\"getStateIndicatorColor()\"\n        [class.si-tree-view-state-indicator-endmost]=\"!enableContextMenuButton && !showFolderStateEnd\"\n      ></div>\n      <div\n        siDropdown\n        #contextDropdown=\"si-dropdown\"\n        dropdownContainer=\"body\"\n        dropdownDirection=\"down\"\n        dropdownAlign=\"end\"\n        [dropdownResponsiveDirection]=\"true\"\n        [dropdownResponsiveDirectionToPlacement]=\"false\"\n        [dropdownIsDisabled]=\"!treeItem?.selectable\"\n        [dropdownFocusOnClose]=\"false\"\n        (dropdownOnToggle)=\"onToggleContextMenu($event)\"\n        *ngIf=\"isContextMenuButtonVisible\"\n        class=\"si-tree-stretch-center si-tree-context-menu-btn\"\n      >\n        <div siDropdownToggle [attr.aria-label]=\"contextMenuAriaLabel | translate\" role=\"button\">\n          <i class=\"si-tree-view-item-icon si-tree-view-menu-btn\" [ngClass]=\"icons?.itemMenu ?? ''\"></i>\n        </div>\n        <si-menu\n          *ngIf=\"contextDropdown?.isOpen ?? false\"\n          [items]=\"contextMenuItems!\"\n          [dropstart]=\"true\"\n          [actionParam]=\"treeItem\"\n          [focusOnLoad]=\"true\"\n          siDropdownMenu\n          class=\"me-5\"\n        ></si-menu>\n      </div>\n      <a\n        *ngIf=\"showFolderStateEnd && treeItem.state !== 'leaf'\"\n        class=\"si-tree-stretch-center si-tree-view-item-toggle si-tree-view-item-toggle-end\"\n        [class.disabled]=\"!getItemFolderStateClass()\"\n        (click)=\"onItemFolderClicked()\"\n        tabindex=\"-1\"\n      >\n        <si-loading-spinner *ngIf=\"isExpanding\" class=\"si-tree-view-item-icon\"></si-loading-spinner>\n        <i\n          *ngIf=\"!isExpanding\"\n          class=\"si-tree-view-item-icon\"\n          [ngClass]=\"getItemFolderStateClass()\"\n        ></i>\n      </a>\n      <div\n        *ngIf=\"showFolderStateEnd && treeItem.state === 'leaf'\"\n        class=\"si-tree-stretch-center si-tree-view-item-toggle si-tree-view-item-toggle-end\"\n      >\n        <i\n          class=\"si-tree-view-item-icon si-tree-view-item-icon-spacer\"\n        ></i>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"isGroupedItem\"\n  class=\"si-tree-view-li focus-direct-sub-inside\"\n>\n  <div class=\"si-tree-view-item-group\">\n    <a\n      *ngIf=\"showFolderStateStart\"\n      class=\"si-tree-stretch-center si-tree-view-item-toggle\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-icon\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n    <a *ngIf=\"showCheckOrOptionBox\" class=\"form-check input-box\" tabindex=\"-1\">\n      <input\n        [type]=\"getInputType()\"\n        class=\"form-check-input\"\n        [indeterminate]=\"this.treeItem.checked === 'indeterminate' ? true: null\"\n        [checked]=\"this.treeItem.checked === 'checked'\"\n        (click)=\"onBoxClicked()\"\n        [attr.aria-label]=\"(treeItem.label ?? '') | translate\"\n        tabindex=\"-1\"\n      >\n    </a>\n    <p class=\"mb-0\">{{ (treeItem.label ?? '') | translate }}</p>\n    <a\n      *ngIf=\"showFolderStateEnd\"\n      class=\"si-tree-stretch-center\"\n      [class.disabled]=\"!getItemFolderStateClass()\"\n      (click)=\"onItemFolderClicked()\"\n      tabindex=\"-1\"\n    >\n      <si-loading-spinner *ngIf=\"isExpanding\"></si-loading-spinner>\n      <i\n        *ngIf=\"!isExpanding\"\n        class=\"si-tree-view-item-icon\"\n        [ngClass]=\"getItemFolderStateClass()\"\n      ></i>\n    </a>\n  </div>\n</div>\n", styles: [":host.si-tree-view-top-level-item>.si-tree-view-li{margin-block:var(--si-tree-view-padding-base-vertical)}:host.si-tree-view-top-level-item+::ng-deep .si-tree-view-top-level-item>.si-tree-view-li{margin-block-start:0}.si-tree-view-li.si-tree-view-li-item{position:relative}.si-tree-view-li.si-tree-view-li-item:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-context-menu-visible,.si-tree-view-li.si-tree-view-li-item:hover .si-tree-view-item-end-icons,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-context-menu-visible .si-tree-view-item-end-icons{background-color:var(--si-tree-view-item-hover-color)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected .si-tree-view-item-end-icons{background-color:var(--si-tree-view-item-select-color)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-selected>.si-tree-view-item>.si-tree-view-item-main>.si-tree-view-item-object-data>h5{font-weight:700}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable h5,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-icon,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-object-data-field-1{color:var(--element-text-disabled)}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-end-icons{background-color:unset}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item:hover,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-context-menu-btn>div[role=button]:hover{background-color:unset;cursor:default}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-dropdown-caret,.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-toggle-end{cursor:pointer}.si-tree-view-li.si-tree-view-li-item.si-tree-view-item-not-selectable .si-tree-view-item-toggle-end .si-tree-view-item-icon{color:var(--element-text-primary)}.si-tree-view-li.si-tree-view-li-item .si-tree-view-item-end-icons{background-color:var(--si-tree-view-background)}.si-tree-view-item-group,.si-tree-view-item{display:flex;align-items:center}.si-tree-stretch-center{align-self:stretch;display:flex;align-items:center;justify-content:center;margin:0;text-decoration:none}.si-tree-view-item-toggle.disabled .si-tree-view-item-icon{inline-size:24px}.si-tree-stretch-center+.si-tree-view-item-toggle-end .si-tree-view-item-icon{margin-inline-start:-4px}.si-tree-view-item-icon{padding-inline-end:8px}.si-tree-view-item-icon.si-tree-view-item-icon-spacer{box-sizing:content-box;inline-size:var(--si-tree-view-icon-size)}.si-tree-view-item-object-data,.si-tree-view-item-icon{margin-block:0;margin-inline:calc(var(--si-tree-view-padding-base-horizontal) / 4)}.si-tree-view-item{justify-content:space-between;cursor:pointer;min-block-size:var(--si-tree-view-item-min-height);padding-inline:var(--si-tree-view-padding-base-horizontal) calc(var(--si-tree-view-padding-base-horizontal) / 4)}:host-context(.si-tree-view-flat) .si-tree-view-item{padding-inline-start:calc(var(--si-tree-view-padding-base-horizontal) / 2)}.si-tree-view-state-indicator{margin-block:0;margin-inline:calc(var(--si-tree-view-padding-base-horizontal) * .75);min-inline-size:6px;block-size:6px;border-radius:50%}.si-tree-view-state-indicator.si-tree-view-state-indicator-endmost{margin-inline-end:calc(var(--si-tree-view-padding-base-horizontal) * 1.75)}.si-tree-view-item-group{padding-block:calc(2 * var(--si-tree-view-padding-base-vertical));padding-inline:calc(var(--si-tree-view-padding-base-horizontal) / 2);cursor:default}.si-tree-view-item-group .si-tree-view-item-dropdown-caret:hover,.si-tree-view-item-group .si-tree-view-item-icon:hover{cursor:pointer}.si-tree-view-item-dropdown-caret,.si-tree-view-item-dropdown-caret:hover,.si-tree-view-item-icon,.si-tree-view-item-icon:hover{color:var(--element-text-primary);text-decoration:none;font-size:var(--si-tree-view-icon-size)}.si-tree-view-item-main{overflow-x:hidden}.si-tree-view-item-main>.badge{margin-block:auto}.si-tree-view-item-object-data>.si-tree-view-item-object-data-field-1{color:var(--si-tree-view-item-object-data-field-1-color)}:host.si-tree-ellipsis .si-tree-view-item-object-data{overflow-x:hidden}.si-tree-view-item-object-data>*{white-space:nowrap;margin-block:0;line-height:var(--si-tree-view-item-line-height)}:host.si-tree-ellipsis .si-tree-view-item-object-data>*{overflow:hidden;text-overflow:ellipsis}.si-tree-view-item-object-data>h5{font-size:var(--si-tree-view-item-object-data-h5-font-size);font-weight:var(--si-tree-view-item-object-data-h5-font-weight)}.si-tree-view-menu-btn{pointer-events:none}.si-tree-view-item-end-icons{block-size:calc(var(--si-tree-view-item-min-height) - 2 * (var(--element-button-focus-width) + var(--element-button-focus-overlay-width)))}.si-tree-view-item-end-icons.si-tree-view-item-end-icons-sticky{position:sticky;inset-inline-end:0}.input-box{margin-inline:2px 12px}.form-check-input{inset-block-start:2px}si-loading-spinner{--loading-spinner-size: var(--si-tree-view-icon-size)}\n"] }]
        }], propDecorators: { indentLevel: [{
                type: Input
            }], treeItem: [{
                type: Input
            }], scrollIntoView: [{
                type: Input
            }], childrenLoaded: [{
                type: Input
            }], contextMenuAriaLabel: [{
                type: Input
            }], templates: [{
                type: Input
            }], contextMenuItemsProvider: [{
                type: Input
            }], stickyRightItems: [{
                type: Input
            }], stickyEndItems: [{
                type: Input
            }], displayFolderState: [{
                type: Input
            }], icons: [{
                type: Input
            }], contextDropdown: [{
                type: ViewChild,
                args: ['contextDropdown']
            }], onContextMenu: [{
                type: HostListener,
                args: ['contextmenu', ['$event']]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/**
 * Provides the functionality to flatten the hierarchical tree structure into a flat one dimensional array; called flattenedTrees.
 * The TreeViewComponent uses this flattened trees (the array) to bind the contained tree/list items to the GUI into a list.
 * In addition the service provides the number of 'normal' list items and the number of group items.
 * Important:
 * The coupling to the TreeViewComponent is high, thus the service is provided only by the TreeViewComponent.
 */
class SiTreeViewConverterService {
    constructor() {
        /**
         * The flattened tree array; filled upon calling the method 'fillFlattenedTree'
         */
        this.flattenedTrees = [];
        /**
         * The number of group items of the flattened tree.
         */
        this.groupItemsCount = 0;
        /**
         * The total items count of the flattened tree.
         */
        this.itemsTotal = 0;
        this.siTreeViewService = inject(SiTreeViewService);
    }
    /**
     * Flattens the tree handed over as parameter.
     * Takes the mode of the tree into consideration:
     * In case of a flat tree: only the first level of the items parameter is copied into the flattenedTrees array.
     * In case of a grouped tree: only the first two levels are copied into the flattenedTrees array
     * In the other cases all tree nodes are copied into the flattenedTrees array.
     */
    fillFlattenedTree(items) {
        if (this.siTreeViewService.flatTree === false) {
            this.flattenedTrees = [];
            if (this.siTreeViewService.groupedList) {
                this.fillFlattenedTreeRecursive(items, 2, 0);
            }
            else {
                this.fillFlattenedTreeRecursive(items);
            }
        }
        else {
            this.flattenedTrees = items;
            this.groupItemsCount = 0;
        }
        this.itemsTotal = items.length;
    }
    fillFlattenedTreeRecursive(items, noOfLevels, stackLevel) {
        if (noOfLevels !== undefined && stackLevel !== undefined && noOfLevels <= stackLevel) {
            return;
        }
        stackLevel = stackLevel ?? 0;
        for (const current of items) {
            this.flattenedTrees.push(current);
            if (this.siTreeViewService.isGroupedItem(current)) {
                this.groupItemsCount++;
            }
            if (current.state === 'expanded') {
                this.fillFlattenedTreeRecursive(current.children ?? [], noOfLevels, stackLevel + 1);
            }
        }
    }
}
SiTreeViewConverterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewConverterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTreeViewConverterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewConverterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewConverterService, decorators: [{
            type: Injectable
        }] });

/**
 * This service provides helper functionality for the tree view to read the height of the tree/list items and
 * the group list items out of the DOM.
 *
 * Important: The coupling to the TreeViewComponent is very high as the service contains knowledge on the HTML structure!!!
 * Thus the service is provided only by the TreeViewComponent.
 *
 * Constraint: The height of the items (DOM elements) must be all the same; The height of the grouped items (DOM elements)
 * must be all the same;
 */
class SiTreeViewItemHeightService {
    constructor() {
        this.siTreeViewService = inject(SiTreeViewService);
    }
    /**
     * Updates the height of a list item, if an appropriate item is in the DOM.
     *
     * @returns The new height of the list item; or undefined if no list item is in the DOM yet.
     */
    updateItemHeight(elementList, flattenedTrees, itemsBase, virtualizedItems) {
        let newHeight;
        let itemsTop = itemsBase + virtualizedItems;
        if (itemsTop > flattenedTrees.length) {
            itemsTop = flattenedTrees.length;
        }
        for (let i = itemsBase; i < itemsTop; i++) {
            if (this.siTreeViewService.isGroupedItem(flattenedTrees[i]) === false) {
                newHeight = this.getItemHeightOfListElement(elementList, i - itemsBase);
                break;
            }
        }
        if (newHeight !== undefined) {
            this.itemHeight = newHeight;
        }
        return newHeight;
    }
    /**
     * Updates the height of a group item, if an appropriate item is in the DOM.
     *
     * @returns The new height of the group item; or undefined if no group item is in the DOM yet.
     */
    updateGroupedItemHeight(elementList, flattenedTrees, itemsBase, virtualizedItems) {
        let newHeight;
        let itemsTop = itemsBase + virtualizedItems;
        if (itemsTop > flattenedTrees.length) {
            itemsTop = flattenedTrees.length;
        }
        for (let i = itemsBase; i < itemsTop; i++) {
            if (this.siTreeViewService.isGroupedItem(flattenedTrees[i])) {
                newHeight = this.getItemHeightOfListElement(elementList, i - itemsBase);
                break;
            }
        }
        if (newHeight !== undefined) {
            this.groupItemHeight = newHeight;
        }
        return newHeight;
    }
    getItemHeightOfListElement(elementList, index) {
        const items = elementList.querySelectorAll('si-tree-view-item');
        if (index < items.length) {
            return items.item(index).getBoundingClientRect().height;
        }
        return undefined;
    }
}
SiTreeViewItemHeightService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemHeightService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTreeViewItemHeightService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemHeightService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewItemHeightService, decorators: [{
            type: Injectable
        }] });

/**
 * Provides helper functionality to virtualize a list of items.
 * The number of virtualized items is the product of the pageSize and the pagesVirtualized (no of pages virtualized);
 * The paging algorithm works as follows:
 * If scrolling down, between two and three pages are kept above the 'visible' area;
 * The trigger is therefore if more than three pages are above the visible area, one page is removed from the top and added at the bottom.
 * If scrolling up, between one and two pages are kept above the 'visible' area;
 * The trigger is therefore if less than one page is above the visible area, one page is added on top and removed from the bottom.
 * All remaining items which are not virtualized, need to be simulated with two div-elements whose height can be set with the calculated
 * properties heightBefore and heightAfter.
 * See the test client for further information on the  usage.
 * Constraint:
 * The virtualization calculation works correct only when all list items are of the same height!
 * If the 'group' mode is enabled, the corresponding group-items can have a different height, but for all group-items it must be the same.
 */
class SiTreeViewVirtualizationService {
    constructor() {
        /**
         * The number of list items of a virtualized page.
         * Choose a value, which is roughly half of the number of items you expect to be displayed on the visible screen.
         */
        this.pageSize = 10;
        /**
         * The number of pages to be virtualized.
         */
        this.pagesVirtualized = 6;
        this.itemsVirtualizedChanged = new Subject();
        /**
         * The total height of the 'simulated' list items which are ordered before the virtualized list items.
         */
        this._heightBefore = 0;
        /**
         * The total height of the 'simulated' list items which are ordered after the virtualized list items.
         */
        this._heightAfter = 0;
        this._pageBase = 0;
        this.scrollTopPrev = 0;
        this._itemsVirtualized = [];
        this.groupItemsCountBefore = 0;
        this.listItemsCountBefore = 0;
        this.groupItemsCountAfter = 0;
        this.listItemsCountAfter = 0;
        this.groupItemsCountVirtualized = 0;
        this.listItemsCountVirtualized = 0;
        this.listItemsPerVirtPage = [];
        this.groupItemsPerVirtPage = [];
        this.siTreeViewService = inject(SiTreeViewService);
        this.siTreeViewItemHeightService = inject(SiTreeViewItemHeightService);
    }
    /**
     * Returns the virtualized items.
     */
    get itemsVirtualized() {
        return this._itemsVirtualized;
    }
    /**
     * The total height of the 'simulated' list items which are ordered before the virtualized list items.
     */
    get heightBefore() {
        return this._heightBefore;
    }
    /**
     * The total height of the 'simulated' list items which are ordered after the virtualized list items.
     */
    get heightAfter() {
        return this._heightAfter;
    }
    /**
     * The index of the first (base) page being virtualized.
     */
    get pageBase() {
        return this._pageBase;
    }
    /**
     * The list item index of the base page.
     */
    get itemBaseIdx() {
        return this._pageBase * this.pageSize;
    }
    /**
     * Returns the number of virtualized items.
     */
    get itemsVirtualizedCount() {
        return this.itemsVirtualized.length;
    }
    /**
     * Call this method upon scroll handler notifications!
     *
     * @param scrollTop; the scrollTop value retrieved form the event target.
     * @param itemsAll; all items of the list;
     */
    handleScroll(scrollTop, itemsAll) {
        if (this.siTreeViewService.groupedList) {
            this.handleScrollGrouped(scrollTop, itemsAll);
        }
        else {
            this.handleScrollSimple(scrollTop, itemsAll);
        }
    }
    /**
     * Resets the virtualization to 'show' the items of the first page onwards.
     */
    resetVirtualizedItemList(itemsAll) {
        this._pageBase = 0;
        this.updateVirtualizedItemListIntern(itemsAll);
    }
    /**
     * Updates the virtualized items. Call this method for use cases such as:
     * - The list items array contains added or removed items which could be in the 'visible' area
     * - The item height has changed.
     */
    updateVirtualizedItemList(itemsAll) {
        this.updateVirtualizedItemListIntern(itemsAll);
    }
    /**
     * Checks if the specified item is currently virtualized.
     * If not, the virtualization is recalculated in order that the item is part of the virtualized items.
     */
    virtualizeItem(item, itemsAll) {
        if (this.checkIfItemDisplayed(item) === false) {
            this.setBasePageForItemToDisplay(item, itemsAll);
        }
    }
    /**
     * Calculates the first visible tree item based on current scroll position.
     */
    calculateFirstVisibleTreeItem() {
        let item;
        let totalItemHeight = 0;
        // Approx Y-position of the middle of the first visible (top) tree item
        const topItemPos = this.scrollTopPrev + ((this.siTreeViewItemHeightService.itemHeight ?? 0) / 2) - this.heightBefore;
        for (let idx = 0; idx < this._itemsVirtualized.length && totalItemHeight < topItemPos; idx++) {
            item = this._itemsVirtualized[idx];
            totalItemHeight += this.isGroupItem(item)
                ? (this.siTreeViewItemHeightService.groupItemHeight ?? 0)
                : (this.siTreeViewItemHeightService.itemHeight ?? 0);
        }
        return item;
    }
    get listItemsCountTotal() {
        return this.listItemsCountBefore + this.listItemsCountVirtualized + this.listItemsCountAfter;
    }
    get groupItemsCountTotal() {
        return this.groupItemsCountBefore + this.groupItemsCountVirtualized + this.groupItemsCountAfter;
    }
    get itemsCountTotal() {
        return this.listItemsCountTotal + this.groupItemsCountTotal;
    }
    get itemsCountVirtualizedMax() {
        return this.pageSize * this.pagesVirtualized;
    }
    updateVirtualizedItemListIntern(itemsAll, scrolledPages) {
        // Note!!!:
        // The order of functionality within this method must be maintained as state of this object is changed!!!
        const itemsVirtualizedOld = this._itemsVirtualized.slice();
        this._itemsVirtualized = itemsAll.slice(this.pageBase * this.pageSize, this.itemsCountVirtualizedMax + this.pageBase * this.pageSize);
        this.updateItemsTypeCount(itemsAll, scrolledPages);
        if (this.siTreeViewService.groupedList) {
            this.calculateBeforeAndAfterForGroupList();
        }
        else {
            this.calculateBeforeAndAfter();
        }
        this.maintainVirtualizationNotifies(itemsVirtualizedOld);
    }
    /**
     * Notifies a consumer about changes of virtualized tree/list items in case of a pageBase change.
     * => One notification is sent for every tree/list item which went out of the virtualized items list or came into the virtualized items.
     */
    maintainVirtualizationNotifies(itemsVirtualizedOld) {
        const itemsAdded = [];
        const itemsRemoved = [];
        for (const item of itemsVirtualizedOld) {
            if (!this._itemsVirtualized.includes(item)) {
                itemsRemoved.push(item);
            }
        }
        for (const item of this._itemsVirtualized) {
            if (!itemsVirtualizedOld.includes(item)) {
                itemsAdded.push(item);
            }
        }
        if (itemsRemoved.length > 0) {
            this.itemsVirtualizedChanged.next(new ItemsVirtualizedArgs(itemsRemoved, false));
        }
        if (itemsAdded.length > 0) {
            this.itemsVirtualizedChanged.next(new ItemsVirtualizedArgs(itemsAdded, true));
        }
    }
    calculateBasePage(idx) {
        this._pageBase = Math.floor((idx / this.pageSize) - (this.pagesVirtualized / 2));
        if (this.pageBase < 0) {
            this._pageBase = 0;
        }
    }
    /**
     * Calculates the height [px] for the divs before and after the tree list in case of 'grouped' mode!
     */
    calculateBeforeAndAfterForGroupList() {
        const groupItemHeight = this.siTreeViewItemHeightService.groupItemHeight ?? 0;
        const listItemHeight = this.siTreeViewItemHeightService.itemHeight ?? 0;
        const scrollHeight = this.listItemsCountTotal * listItemHeight + this.groupItemsCountTotal * groupItemHeight;
        this._heightBefore =
            this.listItemsCountBefore * listItemHeight + this.groupItemsCountBefore * groupItemHeight;
        this._heightAfter = scrollHeight - this.heightBefore -
            (this.listItemsCountVirtualized * listItemHeight + this.groupItemsCountVirtualized * groupItemHeight);
    }
    /**
     * Calculates the height [px] for the divs before and after the tree list in case of 'non-grouped' mode!
     */
    calculateBeforeAndAfter() {
        if ((this.siTreeViewItemHeightService.itemHeight === undefined) || (this.siTreeViewItemHeightService.itemHeight === 0)) {
            return;
        }
        const scrollHeight = this.itemsCountTotal * this.siTreeViewItemHeightService.itemHeight;
        this._heightBefore = this.pageBase * this.pageSize * this.siTreeViewItemHeightService.itemHeight;
        this._heightAfter = scrollHeight - this.heightBefore - (this.itemsVirtualizedCount * this.siTreeViewItemHeightService.itemHeight);
    }
    checkIfItemDisplayed(currentItem) {
        for (const item of this._itemsVirtualized) {
            if (item === currentItem) {
                return true;
            }
        }
        return false;
    }
    setBasePageForItemToDisplay(item, itemsAll) {
        for (let i = 0; i < itemsAll.length; i++) {
            if (itemsAll[i] === item) {
                this.calculateBasePage(i);
                this.updateVirtualizedItemListIntern(itemsAll);
                return;
            }
        }
    }
    /**
     * Updates the number of grouped items and list items for the whole list for the different areas:
     * - The not virtualized items on top of the list (represented by the 'beforeHeight')
     * - The virtualized items
     * - The not virtualized items on the bottom of the list (represented by the 'afterHeight')
     */
    updateItemsTypeCount(itemsAll, scrolledPages) {
        if (this.siTreeViewService.groupedList) {
            this.updateItemsTypeCountForGroupList(itemsAll, scrolledPages);
        }
        else {
            this.groupItemsCountBefore = 0;
            this.groupItemsCountVirtualized = 0;
            this.groupItemsCountAfter = 0;
            this.listItemsCountBefore = this.itemBaseIdx;
            this.listItemsCountVirtualized = this.itemsVirtualizedCount;
            this.listItemsCountAfter = itemsAll.length - this.listItemsCountBefore - this.listItemsCountVirtualized;
        }
        this.updateItemsTypeCountPerVirtPage();
        if (this.itemsCountTotal !== itemsAll.length) {
            console.error('TreeListVirtualizationService: updateVirtualizedItemList().updateListItemsTypeCount() calculation error!');
        }
    }
    /**
     * Updates the number of grouped items and list items per virtualized page.
     */
    updateItemsTypeCountPerVirtPage() {
        this.listItemsPerVirtPage = [];
        this.groupItemsPerVirtPage = [];
        for (let basePgIdx = 0; basePgIdx < this.pagesVirtualized; basePgIdx++) {
            const baseIdx = basePgIdx * this.pageSize;
            let groupItemsCount = 0;
            let listItemsCount = 0;
            for (let idx = baseIdx; idx < baseIdx + this.pageSize; idx++) {
                if (idx < this.itemsVirtualized.length) {
                    const item = this.itemsVirtualized[idx];
                    if (this.isGroupItem(item)) {
                        groupItemsCount++;
                    }
                    else {
                        listItemsCount++;
                    }
                }
                else {
                    break;
                }
            }
            this.listItemsPerVirtPage.push(listItemsCount);
            this.groupItemsPerVirtPage.push(groupItemsCount);
        }
    }
    calculateVirtPageHeight(index) {
        if (index >= this.groupItemsPerVirtPage.length) {
            return 0;
        }
        if (this.siTreeViewService.groupedList) {
            if (this.siTreeViewItemHeightService.itemHeight !== undefined) {
                return this.listItemsPerVirtPage[index] * this.siTreeViewItemHeightService.itemHeight +
                    this.groupItemsPerVirtPage[index] * (this.siTreeViewItemHeightService.groupItemHeight ?? 0);
            }
            else {
                return this.groupItemsPerVirtPage[index] * (this.siTreeViewItemHeightService.groupItemHeight ?? 0);
            }
        }
        else {
            return this.listItemsPerVirtPage[index] * (this.siTreeViewItemHeightService.itemHeight ?? 0);
        }
    }
    updateItemsTypeCountForGroupList(itemsAll, scrolledPages) {
        if (scrolledPages !== undefined && scrolledPages !== 0) {
            this.updateItemsTypeCountForGroupListScrolling(itemsAll, scrolledPages);
        }
        else {
            this.updateListItemsTypeCountForGroupListFull(itemsAll);
        }
    }
    updateItemsTypeCountForGroupListScrolling(itemsAll, scrolledPages) {
        // this method optimizes the update of the type counters in regards of performance in case of scrolling
        // the method considers the difference of the pageBase for the calculation of the counters.
        scrolledPages = scrolledPages ?? 0;
        const oldBaseIndex = (this.pageBase - scrolledPages) * this.pageSize;
        const newBaseIndex = this.pageBase * this.pageSize;
        this.groupItemsCountAfter = this.groupItemsCountAfter + this.groupItemsCountVirtualized;
        this.listItemsCountAfter = this.listItemsCountAfter + this.listItemsCountVirtualized;
        this.groupItemsCountVirtualized = 0;
        this.listItemsCountVirtualized = 0;
        if (scrolledPages > 0) {
            for (let idx = oldBaseIndex; idx < newBaseIndex; idx++) {
                if (this.isGroupItem(itemsAll[idx])) {
                    this.groupItemsCountBefore++;
                    this.groupItemsCountAfter--;
                }
                else {
                    this.listItemsCountBefore++;
                    this.listItemsCountAfter--;
                }
            }
        }
        else {
            for (let idx = newBaseIndex; idx < oldBaseIndex; idx++) {
                if (this.isGroupItem(itemsAll[idx])) {
                    this.groupItemsCountBefore--;
                    this.groupItemsCountAfter++;
                }
                else {
                    this.listItemsCountBefore--;
                    this.listItemsCountAfter++;
                }
            }
        }
        const upper = newBaseIndex + this.itemsVirtualizedCount;
        for (let vi = newBaseIndex; vi < upper; vi++) {
            if (this.isGroupItem(itemsAll[vi])) {
                this.groupItemsCountVirtualized++;
            }
            else {
                this.listItemsCountVirtualized++;
            }
        }
        this.groupItemsCountAfter = this.groupItemsCountAfter - this.groupItemsCountVirtualized;
        this.listItemsCountAfter = this.listItemsCountAfter - this.listItemsCountVirtualized;
    }
    updateListItemsTypeCountForGroupListFull(itemsAll) {
        // does fully update the counters for the whole list.
        this.groupItemsCountBefore = 0;
        this.groupItemsCountVirtualized = 0;
        this.groupItemsCountAfter = 0;
        const virtTop = this.itemBaseIdx + this.itemsVirtualizedCount;
        for (let i = 0; i < itemsAll.length; i++) {
            if (this.isGroupItem(itemsAll[i])) {
                if (i < this.itemBaseIdx) {
                    this.groupItemsCountBefore++;
                }
                else if (i < virtTop) {
                    this.groupItemsCountVirtualized++;
                }
                else {
                    this.groupItemsCountAfter++;
                }
            }
        }
        this.listItemsCountBefore = this.itemBaseIdx - this.groupItemsCountBefore;
        this.listItemsCountVirtualized = this.itemsVirtualizedCount - this.groupItemsCountVirtualized;
        this.listItemsCountAfter = itemsAll.length - this.listItemsCountBefore - this.listItemsCountVirtualized
            - this.groupItemsCountBefore - this.groupItemsCountVirtualized - this.groupItemsCountAfter;
    }
    isGroupItem(item) {
        return !item.parent && this.siTreeViewService.groupedList;
    }
    handleScrollSimple(scrollTop, itemsAll) {
        // Notes:
        // This method is used in case all tree list items are of the same height.
        // This allows for an optimized and proper calculation of the pageBase.
        // pageBase stores the index of the first page which is virtualized.
        // totalDeltaScrollVirt corresponds to the total virtualized items height above the visible are!
        const totalDeltaScrollVirt = scrollTop - this.heightBefore;
        let updateVirtList = false;
        const pageHeight = this.pageSize * (this.siTreeViewItemHeightService.itemHeight ?? 0);
        const pageBaseOld = this.pageBase;
        const pageChangeDownTrigger = 3;
        if (this.scrollTopPrev < scrollTop) {
            // Scroll down:
            // We do keep always between two and three pages virtualized above the view, when scrolling down.
            // => We remove pages from the top only when more than three pages are 'above' the visible view
            // => It is mandatory, that the trigger points are calculated exactly in order to get a nice scrolling behavior for all situations
            if (totalDeltaScrollVirt > (pageChangeDownTrigger * pageHeight)) {
                const pagesOutsideView = totalDeltaScrollVirt / pageHeight;
                // Two pages shall remain 'above' the view;
                // It is important that this number is higher than the trigger when scrolling up (which is one page, see the next else statement)
                // Thus we have a hysteresis of one page in order to avoid scrollbar jitters!!
                this._pageBase = this.pageBase + Math.floor(pagesOutsideView) - (pageChangeDownTrigger - 1);
                updateVirtList = true;
            }
        }
        else if (this.scrollTopPrev > scrollTop) {
            // scroll up
            if (totalDeltaScrollVirt < pageHeight) {
                // we add pages to the top as soon as only one page (or smaller) is 'above' the visible view
                const pagesOutsideView = totalDeltaScrollVirt / pageHeight;
                this._pageBase = this.pageBase + Math.floor(pagesOutsideView) - (pageChangeDownTrigger - 2);
                if (this.pageBase < 0) {
                    this._pageBase = 0; // pageBase cannot be lower than 0; if pageBase=0 then we have no pages above the visible view.
                }
                if (this._pageBase !== pageBaseOld) {
                    updateVirtList = true;
                }
            }
        }
        this.scrollTopPrev = scrollTop;
        if (updateVirtList) {
            this.updateVirtualizedItemListIntern(itemsAll, this.pageBase - pageBaseOld);
        }
    }
    handleScrollGrouped(scrollTop, itemsAll) {
        // Notes:
        // This method is used in case the tree list is grouped => the group items have a different height than the other items.
        // This allows for an optimized and avaeraged calculation of the new pageBase.
        // pageBase stores the index of the first page which is virtualized.
        // totalDeltaScrollVirt corresponds to the total virtualized items height above the visible are!
        const totalDeltaScrollVirt = scrollTop - this.heightBefore;
        const pageHeightFirst3 = this.calculateVirtPageHeight(0) + this.calculateVirtPageHeight(1) + this.calculateVirtPageHeight(2);
        let updateVirtList = false;
        const pageHeightFirst1 = this.calculateVirtPageHeight(0);
        const pageHeightAvg = pageHeightFirst3 / 3;
        const pageBaseOld = this.pageBase;
        if (this.scrollTopPrev < scrollTop) {
            // Scroll down:
            // We do keep always between two and three pages virtualized above the view, when scrolling down.
            // => We remove pages from the top only when more than three pages are 'above' the visible view.
            // => It is mandatory, that the trigger points are calculated exactly in order to get a nice scrolling behavior for all situations.
            if (totalDeltaScrollVirt > (pageHeightFirst3)) {
                // Next calculation is averaged. It seems not needed to properly calculate it based on the all the real items.
                const pagesOutsideView = totalDeltaScrollVirt / pageHeightAvg;
                // Two pages shall remain 'above' the view;
                // It is important that this number is higher than the trigger when scrolling up (which is one page, see next else).
                // Thus we have a hysteresis of one page in order to avoid scrollbar jitters!!
                this._pageBase = this.pageBase + Math.floor(pagesOutsideView) - 2;
                updateVirtList = true;
            }
        }
        else if (this.scrollTopPrev > scrollTop) {
            // scroll up
            if (totalDeltaScrollVirt < pageHeightFirst1) {
                // we add pages to the top as soon as only one page (or smaller) is 'above' the visible view
                const pagesOutsideView = totalDeltaScrollVirt / pageHeightAvg;
                this._pageBase = this.pageBase + Math.floor(pagesOutsideView) - 1;
                if (this.pageBase < 0) {
                    this._pageBase = 0; // pageBase cannot be lower than 0; if pageBase=0 then we have no pages above the visible view.
                }
                if (this._pageBase !== pageBaseOld) {
                    updateVirtList = true;
                }
            }
        }
        this.scrollTopPrev = scrollTop;
        if (updateVirtList) {
            this.updateVirtualizedItemListIntern(itemsAll, this.pageBase - pageBaseOld);
        }
    }
}
SiTreeViewVirtualizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewVirtualizationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiTreeViewVirtualizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewVirtualizationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewVirtualizationService, decorators: [{
            type: Injectable
        }] });

/*
 * WARNING: Parts of this component are made to be used in conjunction with Bootstrap.
 * Bootstrap is not included with this package and has to be installed separately.
 */
/**
 * Implements a tree view with the following main capabilities:
 * Showing multiple trees. The data of the trees is set via the input property 'items'.
 * Showing the trees in a flat mode with navigation/bread/crumb on top.
 * A tree/list item is composed of a 'folder' icon, any item icon, the label, two additional datafields and a context menu button.
 * Children of parent nodes are lazy loaded upon request (in case they are not yet available in model.)
 * Context menu support via context menu item: the context menu items can be set via input properties.
 * Multi selection and focus support.
 * Checkboxes and / or option boxes per tree node.
 * Virtualization support: Input properties allow to set the page size and the number of pages to be virtualized.
 * Current limitation is roughly half a million of nodes. This is due to some 'strange' behavior with flex containers.
 * Set the input properties 'pageSize' and 'pagesVirtualized' to a reasonable amount of virtualized items.
 * The number of virtualized items is the product of the pageSize and the pagesVirtualized (no of pages virtualized).
 * Choose a value in the area of 50 virtualized items, dependent on the screen size. Check at runtime if appropriate!
 * Grouped List support: The component does also support grouped lists.
 * See the test client implementation of this package for all functionality.
 */
class SiTreeViewComponent {
    constructor() {
        /**
         * Needs to be called by the consumer, if a tree model gets added or removed
         * items by the consumer. The tree list component needs to know, as it has to
         * update the flattened tree and the optional virtualization.
         * @deprecated Use the {@link refresh} method instead.
         */
        this.updateTreeDisplay = new Subject();
        /**
         * Push the tree item to be scrolled into view. Tree items will be expanded if
         * required.
         * @deprecated Use the {@link scrollItemIntoView} method instead.
         */
        this.scrollIntoView = new Subject();
        /**
         * The context menu items which are bound to the context menu of all tree items,
         * or a menu item provider function that is invoked for each tree item once.
         */
        this.contextMenuItems = [];
        /**
         * The indentation in pixel for the children in respect to its parent.
         */
        this.childrenIndentation = DEFAULT_CHILDREN_INDENTATION;
        /**
         * Enable horizontal scrolling. When disabled (the default), an ellipsis is used for overflowing text
         */
        this.horizontalScrolling = false;
        /**
         * Enable the compact mode, making it more vertically compact.
         */
        this.compactMode = false;
        /**
         * Enable buttons for collapse and expand all.
         * Does not work when `flatTree` is enabled.
         */
        this.expandCollapseAll = false;
        /**
         * Tooltip text shown for the expand all icon.
         */
        this.expandAllTooltip = 'Expand all';
        /**
         * Tooltip text shown for the collapse all icon.
         */
        this.collapseAllTooltip = 'Collapse all';
        /**
         * Text or translation key for aria-label of context menu dropdown. Needed for a11y.
         */
        this.contextMenuAriaLabel = 'Open context menu';
        /**
         * Triggered upon virtualization (or unvirtualization) of a tree item.
         */
        this.itemsVirtualizedChanged = new EventEmitter();
        /**
         * Triggered upon clicking the folder of a tree item.
         */
        this.treeItemFolderClicked = new EventEmitter();
        /**
         * Triggered upon a state change of the folder of a tree item.
         */
        this.treeItemFolderStateChanged = new EventEmitter();
        /**
         * Triggered upon clicking the label of a tree item.
         */
        this.treeItemClicked = new EventEmitter();
        /**
         * Triggered upon clicking the checkbox of a tree item.
         */
        this.treeItemCheckboxClicked = new EventEmitter();
        /**
         * Triggered upon the request of loading the children of a tree item.
         */
        this.loadChildren = new EventEmitter();
        /**
         * Triggered upon the selection of a tree item (multi selection supported).
         */
        this.treeItemsSelected = new EventEmitter();
        /**
         * Track-by function for tree items. By default, items are tracked by their identity.
         */
        this.trackByFunction = buildTrackByIdentity();
        this.initialized = false;
        this.scrollHandlerAdded = false;
        this.updateTreeItemHeightRequired = true; // must be set to true so the height is initially calculated once at least
        this.updateGroupItemHeightRequired = true; // must be set to true so the height is initially calculated once at least
        this.selectedTreeItems = [];
        this._items = [];
        this.breadCrumbTreeItems = [];
        this.subscriptions = [];
        this._multiSelectionActive = false;
        this.element = inject(ElementRef);
        this.siTreeViewService = inject(SiTreeViewService);
        this.siTreeViewConverterService = inject(SiTreeViewConverterService);
        this.siTreeViewVirtualizationService = inject(SiTreeViewVirtualizationService);
        this.siTreeViewItemHeightService = inject(SiTreeViewItemHeightService);
        this.cdRef = inject(ChangeDetectorRef);
        this.scrollChildIntoView = new Subject();
        this.childrenLoaded = new Subject();
        this.hasAnyChildren = true;
        this.expandItem = {
            icon: 'element-expand-all',
            action: () => {
                this.expandAll();
            },
            tooltip: this.expandAllTooltip
        };
        this.collapseItem = {
            icon: 'element-collapse-all',
            action: () => {
                this.collapseAll();
            },
            tooltip: this.collapseAllTooltip
        };
        this.scrollFunction = (event) => this.onScroll(event);
    }
    /**
     * Number of rows per page. Used for the virtualization of rows (number of
     * rows per page).
     * @defaultref {@link SiTreeViewVirtualizationService#pageSize}
     */
    set pageSize(value) {
        this.siTreeViewVirtualizationService.pageSize = (value < 5) ? 5 : value;
        this.resetVirtualizedItemList();
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Number of pages which are virtualized.
     * @defaultref {@link SiTreeViewVirtualizationService#pagesVirtualized}
     */
    set pagesVirtualized(value) {
        this.siTreeViewVirtualizationService.pagesVirtualized = (value < 5) ? 5 : value;
        this.resetVirtualizedItemList();
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets the height of the div above the virtualized rows.
     */
    get heightBefore() {
        return this.siTreeViewVirtualizationService.heightBefore + 'px';
    }
    /**
     * Gets the height of the div below the virtualized rows.
     */
    get heightAfter() {
        return this.siTreeViewVirtualizationService.heightAfter + 'px';
    }
    /**
     * Returns the virtualized items in the tree list uses virtualization, otherwise all items are returned as a flat list.
     * Bind these to the HTML.
     */
    get itemsVirtualized() {
        return this.isVirtualized ? this.siTreeViewVirtualizationService.itemsVirtualized :
            this.siTreeViewConverterService.flattenedTrees;
    }
    ngOnInit() {
        this.subscriptions.push(this.siTreeViewService.clickEvent.subscribe(event => this.onItemClicked(event)));
        this.subscriptions.push(this.siTreeViewService.contextMenuOpenEvent.subscribe(event => this.onContextMenuOpenClicked(event)));
        this.subscriptions.push(this.siTreeViewService.folderClickEvent.subscribe(event => this.onItemFolderClicked(event)));
        this.subscriptions.push(this.siTreeViewService.checkboxClickEvent.subscribe(event => this.onItemCheckboxClicked(event)));
        this.subscriptions.push(this.siTreeViewService.loadChildrenEvent.subscribe(event => this.onLoadChildren(event)));
        this.subscriptions.push(this.siTreeViewService.scrollIntoViewEvent.subscribe(event => this.onScrollIntoView(event)));
        this.subscriptions.push(this.updateTreeDisplay.subscribe(() => this.refresh()));
        this.subscriptions.push(this.scrollIntoView.subscribe(item => this.onScrollIntoViewByConsumer(item)));
        this.subscriptions.push(this.siTreeViewVirtualizationService.itemsVirtualizedChanged
            .subscribe((event) => {
            this.itemsVirtualizedChanged.next(event);
            this.cdRef.markForCheck();
        }));
        this.initialized = true;
        this.handleTreeMode();
        this.icons = {
            ...DEFAULT_TREE_ICON_SET,
            ...this.icons
        };
    }
    ngOnDestroy() {
        this.subscriptions.filter(subscription => !!subscription).forEach(subscription => subscription.unsubscribe());
        this.removeScrollHandler();
    }
    ngOnChanges(changes) {
        this.expandItem.tooltip = this.expandAllTooltip;
        this.collapseItem.tooltip = this.collapseAllTooltip;
        if (changes.icons) {
            this.icons = {
                ...DEFAULT_TREE_ICON_SET,
                ...this.icons
            };
        }
    }
    ngAfterViewInit() {
        this.addScrollHandler();
    }
    ngAfterViewChecked() {
        if (this.updateTreeItemHeightRequired && (this.siTreeViewConverterService.flattenedTrees.length > 0)) {
            const oldHeight = this.siTreeViewItemHeightService.itemHeight;
            const elementList = this.getTreeViewElement();
            let newHeight;
            if (this.siTreeViewService.isVirtualized) {
                newHeight = this.siTreeViewItemHeightService.updateItemHeight(elementList, this.siTreeViewConverterService.flattenedTrees, this.siTreeViewVirtualizationService.itemBaseIdx, this.siTreeViewVirtualizationService.itemsVirtualizedCount);
            }
            else {
                newHeight = this.siTreeViewItemHeightService.updateItemHeight(elementList, this.siTreeViewConverterService.flattenedTrees, 0, this.siTreeViewConverterService.flattenedTrees.length);
            }
            if (newHeight !== undefined && newHeight > 0) {
                this.updateTreeItemHeightRequired = false;
                if (oldHeight !== newHeight) {
                    const task = () => {
                        this.updateVirtualizedItemList();
                        this.cdRef.markForCheck();
                    };
                    asyncScheduler.schedule(task, 0);
                }
            }
        }
        if (this.updateGroupItemHeightRequired && (this.siTreeViewConverterService.flattenedTrees.length > 0)) {
            const oldHeight = this.siTreeViewItemHeightService.groupItemHeight;
            const elementList = this.getTreeViewElement();
            let newHeight;
            if (this.siTreeViewService.isVirtualized) {
                newHeight = this.siTreeViewItemHeightService.updateGroupedItemHeight(elementList, this.siTreeViewConverterService.flattenedTrees, this.siTreeViewVirtualizationService.itemBaseIdx, this.siTreeViewVirtualizationService.itemsVirtualizedCount);
            }
            else {
                newHeight = this.siTreeViewItemHeightService.updateGroupedItemHeight(elementList, this.siTreeViewConverterService.flattenedTrees, 0, this.siTreeViewConverterService.flattenedTrees.length);
            }
            if (newHeight !== undefined) {
                this.updateGroupItemHeightRequired = false;
                if (oldHeight !== newHeight) {
                    const task = () => {
                        this.updateVirtualizedItemList();
                        this.cdRef.markForCheck();
                    };
                    asyncScheduler.schedule(task, 0);
                }
            }
        }
    }
    /**
     * @defaultref {@link SiTreeViewService#folderStateStart}
     * @deprecated Use `folderStateStart` instead.
     */
    set folderStateLeft(value) {
        this.folderStateStart = value;
    }
    get folderStateLeft() {
        return this.folderStateStart;
    }
    /**
     * Sets if the folder state icon shall be shown on the left (in LTR) or on the right (in LTR) side
     * of the tree item. Per default the icon will be shown on the left (in LTR). Has no
     * effect if flatTree is enabled.
     * @defaultref {@link SiTreeViewService#folderStateStart}
     */
    set folderStateStart(value) {
        this.siTreeViewService.folderStateStart = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the folder state icon is shown on the left (in LTR) of the right (in LTR) side of the item
     */
    get folderStateStart() {
        return this.siTreeViewService.folderStateStart;
    }
    /**
     * Sets if the tree list shall virtualize the tree items.
     * This input field must be set at startup and shall not be changed afterwards.
     * @defaultref {@link SiTreeViewService#isVirtualized}
     */
    set isVirtualized(value) {
        this.siTreeViewService.isVirtualized = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree list is virtualized.
     */
    get isVirtualized() {
        return this.siTreeViewService.isVirtualized;
    }
    /**
     * Sets the root tree items of all the trees (Required).
     * @defaultref {@link _items}
     */
    set items(items) {
        this._items = items;
        // Create a virtual root node so there is just a single root node. This makes sure the tree
        // can be fully traversed starting from any node. This is needed e.g. for recursively
        // removing the "active" state from other nodes.
        const virtualRoot = {
            label: 'root',
            level: ROOT_LEVEL,
            children: items,
            state: 'expanded',
            selectable: false
        };
        this.setTreeItemDefaults(virtualRoot);
        this.initializeTreeItemsRecursive(this._items, virtualRoot);
        this.fillFlattenedTree(this._items, true);
        this.latestFolderChanged = this._items[0];
    }
    /**
     * Gets the root items of the tree view.
     */
    get items() {
        return this._items;
    }
    /**
     * Sets the tree item to be selected.
     */
    set selectedItem(treeItem) {
        const isMultipleTreeItems = Array.isArray(treeItem);
        if (!isMultipleTreeItems && treeItem && !treeItem.selectable) {
            return;
        }
        for (const element of this._items) {
            selectRecursive(element, false);
        }
        this.selectedTreeItems = [];
        if (!treeItem) {
            this.siTreeViewService.triggerMarkForCheck.next();
            return;
        }
        if (!isMultipleTreeItems) {
            treeItem.selected = true;
            this.selectedTreeItems.push(treeItem);
            this.setFlatTreeSelectedFolder(treeItem.parent);
            if (this.isVirtualized) {
                this.siTreeViewVirtualizationService.virtualizeItem(treeItem, this.siTreeViewConverterService.flattenedTrees);
            }
            // wait one cycle until the new children (in case of page virtualization) are created
            const task = () => {
                this.scrollChildIntoView.next(treeItem);
                this.cdRef.markForCheck();
                this.siTreeViewService.triggerMarkForCheck.next();
            };
            asyncScheduler.schedule(task, 0);
        }
        else if (!this.singleSelectMode) {
            const task = () => {
                for (const item of treeItem) {
                    if (item.selectable === false) {
                        continue;
                    }
                    item.selected = true;
                    this.selectedTreeItems.push(item);
                }
                this.cdRef.markForCheck();
                this.siTreeViewService.triggerMarkForCheck.next();
            };
            asyncScheduler.schedule(task, 0);
        }
    }
    /**
     * Sets if the tree shall force single tree item selection.
     * @defaultref {@link SiTreeViewService#singleSelectMode}
     */
    set singleSelectMode(value) {
        this.siTreeViewService.singleSelectMode = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree view is set to single selection only.
     */
    get singleSelectMode() {
        return this.siTreeViewService.singleSelectMode;
    }
    /**
     * Shows or hides additional information below the label.
     * @defaultref {@link SiTreeViewService#enableDataField1}
     */
    set enableDataField1(value) {
        this.siTreeViewService.enableDataField1 = value;
        // Note:
        // We need to update the tree item height not earlier than when the corresponding DOM element is rendered (the layout renderer did run).
        // Thus, we set only a flag and check if update is needed within the ngAfterViewChecked hook.
        this.updateTreeItemHeightRequired = true;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the dataField1 is shown/hidden.
     */
    get enableDataField1() {
        return this.siTreeViewService.enableDataField1;
    }
    /**
     * Shows or hides additional information below the label.
     * @defaultref {@link SiTreeViewService#enableDataField2}
     */
    set enableDataField2(value) {
        this.siTreeViewService.enableDataField2 = value;
        // Note:
        // We need to update the tree item height not earlier than when the corresponding DOM element is
        // rendered (the layout renderer did run). Thus, we set only a flag and check if update is needed
        // within the ngAfterViewChecked hook.
        this.updateTreeItemHeightRequired = true;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the dataField2 is shown/hidden.
     */
    get enableDataField2() {
        return this.siTreeViewService.enableDataField2;
    }
    /**
     * Shows or hides state pipe.
     * @defaultref {@link SiTreeViewService#enableStateIndicator}
     */
    set enableStateIndicator(value) {
        this.siTreeViewService.enableStateIndicator = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the state pipe is shown/hidden.
     */
    get enableStateIndicator() {
        return this.siTreeViewService.enableStateIndicator;
    }
    /**
     * Shows or hides icon
     * @defaultref {@link SiTreeViewService#enableIcon}
     */
    set enableIcon(value) {
        this.siTreeViewService.enableIcon = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the icon is shown/hidden.
     */
    get enableIcon() {
        return this.siTreeViewService.enableIcon;
    }
    /**
     * Shows or hides context menu button.
     * @defaultref {@link SiTreeViewService#enableContextMenuButton}
     */
    set enableContextMenuButton(value) {
        this.siTreeViewService.enableContextMenuButton = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the context menu button is shown/hidden.
     */
    get enableContextMenuButton() {
        return this.siTreeViewService.enableContextMenuButton;
    }
    /**
     * Allows / disabled selecting of tree items by clicking on them.
     * @defaultref {@link SiTreeViewService#enableSelection}
     */
    set enableSelection(value) {
        this.siTreeViewService.enableSelection = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree item selection is enabled/disabled.
     */
    get enableSelection() {
        return this.siTreeViewService.enableSelection;
    }
    /**
     * Sets if children are deleted upon collapsing tree items. This feature might
     * be used, if children shall be lazy loaded always upon expanding a tree
     * item.
     * @defaultref {@link SiTreeViewService#deleteChildrenOnCollapse}
     */
    set deleteChildrenOnCollapse(value) {
        this.siTreeViewService.deleteChildrenOnCollapse = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if children are deleted upon collapsing tree items.
     */
    get deleteChildrenOnCollapse() {
        return this.siTreeViewService.deleteChildrenOnCollapse;
    }
    /**
     * Sets if the tree view shall be displayed as flat tree list with a
     * breadcrumb. A flat tree only shows the one level at the time and lists the
     * tree items of the current level as a list.
     * @defaultref {@link SiTreeViewService#flatTree}
     */
    set flatTree(value) {
        const old = this.siTreeViewService.flatTree;
        this.siTreeViewService.flatTree = value;
        if (old !== this.siTreeViewService.flatTree) {
            this.handleTreeMode();
        }
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree view is displayed as flat tree list with a breadcrumb.
     */
    get flatTree() {
        return this.siTreeViewService.flatTree;
    }
    /**
     * Sets if the tree view is displayed as a grouped list.
     * Important: In this mode, only the first two hierarchies of the tree model
     * are considered!
     * @defaultref {@link SiTreeViewService#groupedList}
     */
    set groupedList(value) {
        const old = this.siTreeViewService.groupedList;
        this.siTreeViewService.groupedList = value;
        if (this.siTreeViewService.groupedList) {
            // Note:
            // We need to update the tree item height not earlier than when the corresponding DOM element
            // is rendered (the layout renderer did run). Thus, we set only a flag and check if update is
            // needed within the ngAfterViewChecked hook.
            this.updateGroupItemHeightRequired = true;
        }
        if (old !== this.siTreeViewService.groupedList) {
            this.fillFlattenedTree(this._items, true);
        }
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree view is displayed displayed as a grouped list.
     * Important: In this mode, only the first two hierarchies of the tree model
     * are considered!
     */
    get groupedList() {
        return this.siTreeViewService.groupedList;
    }
    /**
     * Sets if the tree items shall show a checkbox.
     * @defaultref {@link SiTreeViewService#enableCheckbox}
     */
    set enableCheckbox(value) {
        this.siTreeViewService.enableCheckbox = value;
        for (const element of this._items) {
            enableCheckboxRecursive(element, this.siTreeViewService.enableCheckbox);
        }
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree item show a checkbox.
     */
    get enableCheckbox() {
        return this.siTreeViewService.enableCheckbox;
    }
    /**
     * Sets if the tree items shall show an optionbox.
     * @defaultref {@link SiTreeViewService#enableOptionbox}
     */
    set enableOptionbox(value) {
        this.siTreeViewService.enableOptionbox = value;
        for (const element of this._items) {
            enableOptionboxRecursive(element, this.siTreeViewService.enableOptionbox);
        }
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the tree items shall show an optionbox.
     */
    get enableOptionbox() {
        return this.siTreeViewService.enableOptionbox;
    }
    /**
     * Sets if the checkbox state of a tree item is inherited to its children/parent.
     * @defaultref {@link SiTreeViewService#inheritChecked}
     */
    set inheritChecked(value) {
        this.siTreeViewService.inheritChecked = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets if the checkbox state of a tree item is inherited to its children/parent.
     */
    get inheritChecked() {
        return this.siTreeViewService.inheritChecked;
    }
    /**
     * Returns the last breadcrumb tree item. Can be null in case the flat tree
     * shows the root.
     */
    get lastBreadCrumbItem() {
        const item = this.breadCrumbTreeItems[this.breadCrumbTreeItems.length - 1];
        return item ? item : null;
    }
    /**
     * Returns true if the header shows the root/home of the tree.
     */
    get headerShowsRoot() {
        return this.lastBreadCrumbItem == null;
    }
    /**
     * Sets the string to be shown when there are no content actions.
     * @defaultref {@link SiTreeViewService#noActionsString}
     */
    set noActionsString(value) {
        this.siTreeViewService.noActionsString = value;
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    /**
     * Gets the string to be shown when there are no content actions.
     */
    get noActionsString() {
        return this.siTreeViewService.noActionsString;
    }
    /**
     * Called by the owner of the tree upon returning the retrieved children of a
     * 'load children' request.
     */
    childrenLoadingDone(item, children) {
        const oldState = item.state ?? 'collapsed';
        if (item?.state === 'expanding') {
            this.initializeTreeItemsRecursive(children, item, (item.level ?? 0) + 1);
            item.children = children;
            item.state = 'expanded';
        }
        const newState = item.state ?? 'collapsed';
        this.updateBreadCrumb(this.evalLowestBreadCrumbNode());
        if (this.flatTree) {
            this.fillFlattenedTree(children);
        }
        else {
            this.fillFlattenedTree(this._items);
        }
        if (oldState !== newState) {
            this.treeItemFolderStateChanged.emit(new FolderStateEventArgs(item, oldState, newState));
        }
        this.childrenLoaded.next(item);
        this.cdRef.markForCheck();
    }
    onFlatTreeNavigateUp() {
        if (this.lastBreadCrumbItem) {
            this.onBreadCrumbItemClicked(this.lastBreadCrumbItem.parent);
        }
    }
    onFlatTreeNavigateHome() {
        this.onBreadCrumbItemClicked();
    }
    onTreeArrowKeydown(event) {
        const rtlCorrectedKey = correctKeyRTL(event.key);
        if (rtlCorrectedKey === 'ArrowLeft' && this.flatTree) {
            if (this.lastBreadCrumbItem) {
                this.onFlatTreeNavigateUp();
            }
            else {
                this.onFlatTreeNavigateHome();
            }
            setTimeout(() => {
                if (this.treeViewInnerElement) {
                    this.treeViewInnerElement.nativeElement.focus();
                }
            });
        }
    }
    /**
     * Updates the tree to visualize the provided tree item by expanding all its parent items, collapsing all
     * other first level tree items and scroll to the provided item.
     * @param item The tree item that shall be visible.
     */
    showTreeItem(item) {
        this.doShowTreeItem(item);
        const task = () => {
            this.scrollChildIntoView.next(item);
            this.cdRef.markForCheck();
        };
        asyncScheduler.schedule(task, 0);
    }
    doShowTreeItem(item, collapseOtherNodes = true) {
        if (item.parent && item.parent.level !== ROOT_LEVEL) {
            this.expandTreeItem(item.parent);
            if (item.parent.parent && item.parent.parent.level !== ROOT_LEVEL) {
                this.doShowTreeItem(item.parent, collapseOtherNodes);
            }
            else if (collapseOtherNodes) {
                const myRoot = item.parent;
                for (const root of this.items) {
                    if (myRoot !== root) {
                        this.collapseTreeItem(root);
                    }
                }
            }
        }
    }
    /**
     * Expands all tree items.
     * @param items Optional param for recursion, will expand these items. If empty, all tree items will be expanded.
     */
    expandAll(items) {
        (items ?? this._items).forEach(item => {
            if (hasChildren(item) && childrenLoaded(item)) {
                if (item.state === 'collapsed') {
                    this.expandTreeItem(item);
                }
                this.expandAll(item.children);
            }
        });
    }
    /**
     * Expands the provided tree item.
     * @param item The tree item to be expanded.
     */
    expandTreeItem(item) {
        const oldState = removeUndefinedState(item.state);
        expand(item);
        this.latestFolderChanged = item;
        this.siTreeViewService.folderClickEvent.next(new FolderStateEventArgs(item, oldState, removeUndefinedState(item.state)));
    }
    /**
     * Collapses all tree items.
     * @param items Optional param for recursion, will collapse these items. If empty, all tree items will be collapsed.
     */
    collapseAll(items) {
        (items ?? this._items).forEach(item => {
            if (hasChildren(item)) {
                if (item.state === 'expanded' || item.state === 'expanding') {
                    this.collapseTreeItem(item);
                }
                this.collapseAll(item.children);
            }
        });
    }
    /**
     * Collapses the provided tree item.
     * @param item The tree item to be collapsed.
     */
    collapseTreeItem(item) {
        const oldState = item.state ?? 'collapsed';
        collapse(item);
        this.latestFolderChanged = item;
        this.siTreeViewService.folderClickEvent.next(new FolderStateEventArgs(item, oldState, removeUndefinedState(item.state)));
    }
    getTreeViewElement() {
        return this.element.nativeElement.querySelector('.si-tree-view');
    }
    /**
     * Forces a refresh of the view considering the current tree item model. Needs
     * to be called when tree items have been added, removed, or updated via model
     * in ways that do not trigger an automatic update of the view.
     */
    refresh() {
        if (!this.flatTree) {
            this.fillFlattenedTree(this._items);
        }
        else {
            this.fillFlattenedTree(this.evalListItemsForFlatTreeMode());
        }
        this.cdRef.markForCheck();
    }
    /**
     * Scrolls the specified tree item into view. Tree items will be expanded if
     * required.
     */
    scrollItemIntoView(treeItem) {
        this.onScrollIntoViewByConsumer(treeItem);
    }
    evalListItemsForFlatTreeMode() {
        return this.lastBreadCrumbItem ? this.lastBreadCrumbItem.children ?? [] : this._items;
    }
    collapseChildrenFlatTreeModeOnly(items) {
        this.collapseChildren(items, true);
    }
    collapseChildren(items, flatModeOnly = false) {
        if (flatModeOnly && !this.flatTree) {
            return;
        }
        for (const item of items) {
            if (item.state === 'expanded' || item.state === 'expanding') {
                const oldState = item.state;
                collapse(item);
                this.latestFolderChanged = item;
                this.treeItemFolderStateChanged.next(new FolderStateEventArgs(item, oldState, item.state));
            }
        }
    }
    addScrollHandler() {
        if (!this.scrollHandlerAdded && this.isVirtualized) {
            // TODO: other solution as .addEventListener
            this.getTreeViewElement().addEventListener('scroll', this.scrollFunction, false);
            this.scrollHandlerAdded = true;
        }
    }
    removeScrollHandler() {
        if (this.scrollHandlerAdded) {
            this.getTreeViewElement().removeEventListener('scroll', this.scrollFunction, false);
            this.scrollHandlerAdded = false;
        }
    }
    onKeyUpShift(event) {
        if (this._multiSelectionActive) {
            this._multiSelectionActive = false;
            this.emitSelectedItems();
        }
    }
    onKeyUpCtrl(event) {
        if (this._multiSelectionActive) {
            this._multiSelectionActive = false;
            this.emitSelectedItems();
        }
    }
    onKeyUpMeta(event) {
        if (this._multiSelectionActive) {
            this._multiSelectionActive = false;
            this.emitSelectedItems();
        }
    }
    onMouseLeave(event) {
        if (this._multiSelectionActive) {
            this._multiSelectionActive = false;
            this.emitSelectedItems();
        }
    }
    onContextMenuOpenClicked(event) {
        setActive(event.target, true);
        const listRect = this.element.nativeElement.getBoundingClientRect();
        this.siTreeViewService.contextMenuDropUp = (event.itemTop + (3 * (this.siTreeViewItemHeightService.itemHeight ?? 0)) > listRect.bottom);
        this.cdRef.markForCheck();
    }
    onItemClicked(event) {
        let skipFocus = false;
        if (!event.target.selectable) {
            return;
        }
        if ((event.mouseEvent.ctrlKey || (navigator.platform.match('Mac') && event.mouseEvent.metaKey)) && !this.singleSelectMode) {
            this._multiSelectionActive = true;
            this.multiSelectionStart = event.target;
            if (event.target.selected) {
                skipFocus = true;
            }
            event.target.selected = !event.target.selected;
            if (event.target.selected && !this.singleSelectMode) {
                this.selectedTreeItems.push(event.target);
            }
            else {
                const idx = this.selectedTreeItems.indexOf(event.target);
                if (idx !== -1) {
                    this.selectedTreeItems.splice(idx, 1);
                }
            }
        }
        else if (event.mouseEvent.shiftKey && !this.singleSelectMode) {
            this._multiSelectionActive = true;
            // If the anchor has not been established, set it to the first item in the selected
            // items list if defined ELSE the first item in the tree
            if (!this.multiSelectionStart) {
                this.multiSelectionStart = this.selectedTreeItems?.[0] ?? this._items?.[0];
            }
            // If the tree is in flat mode, ensure the anchor is inside the currently selected folder
            if (this.flatTree) {
                const treeItems = (event.target.parent ? event.target.parent.children : this._items) ?? [];
                if (treeItems.findIndex(ti => ti === this.multiSelectionStart) < 0) {
                    // Move anchor to top-level item in current folder!
                    this.multiSelectionStart = treeItems[0];
                }
            }
            for (const element of this._items) {
                selectRecursive(element, false);
            }
            this.multiSelectionStart.selected = true;
            const selectedItems = selectItemsBetween(this._items, this.multiSelectionStart, event.target);
            this.selectedTreeItems = [this.multiSelectionStart];
            if (selectedItems) {
                this.selectedTreeItems.push(...selectedItems);
            }
        }
        else {
            this.multiSelectionStart = event.target;
            for (const element of this._items) {
                selectRecursive(element, false);
            }
            event.target.selected = true;
            this.selectedTreeItems = [event.target];
        }
        for (const element of this._items) {
            resetActive(element);
        }
        if (!skipFocus) {
            setActive(event.target, true);
        }
        this.treeItemClicked.emit(event.target);
        if (!this._multiSelectionActive) {
            this.emitSelectedItems();
            // Note, if multi selection would be active, we would emit the selected items only upon 'keyup' event for Shift and Control
            // and upon document.mouseleave event
        }
        this.cdRef.markForCheck();
        this.siTreeViewService.triggerMarkForCheck.next();
    }
    emitSelectedItems() {
        if (this.enableSelection) {
            if (this.siTreeViewService.groupedList) {
                const filtered = this.selectedTreeItems.filter(item => !this.siTreeViewService.isGroupedItem(item));
                this.treeItemsSelected.emit(filtered);
            }
            else {
                this.treeItemsSelected.emit(this.selectedTreeItems);
            }
        }
    }
    handleTreeMode() {
        let ti;
        if (this.isVirtualized) {
            ti = this.siTreeViewVirtualizationService.calculateFirstVisibleTreeItem();
        }
        if (this.flatTree) {
            this.siTreeViewService.childrenIndentation = 0;
            if (!this.latestFolderChanged) {
                // no folder state change of any tree item so far => display the root item in the list
                this.fillFlattenedTree(this._items, true);
            }
            else if (this.latestFolderChanged.state === 'expanded') {
                this.fillFlattenedTree(this.latestFolderChanged.children ?? [], true);
            }
            else if (this.latestFolderChanged.parent) {
                this.fillFlattenedTree(this.latestFolderChanged.parent?.children ?? [], true);
            }
            else {
                this.fillFlattenedTree(this._items, true);
            }
            this.updateBreadCrumb(this.evalLowestBreadCrumbNode());
            if (ti && !this.siTreeViewConverterService.flattenedTrees.includes(ti, 0)) {
                ti = this.siTreeViewConverterService.flattenedTrees[0];
            }
        }
        else {
            this.siTreeViewService.childrenIndentation = this.childrenIndentation;
            this.fillFlattenedTree(this._items, true);
        }
        this.onScrollIntoViewByConsumer(ti);
    }
    evalLowestBreadCrumbNode() {
        if (this.latestFolderChanged) {
            return this.latestFolderChanged.state === 'expanded' ? this.latestFolderChanged : this.latestFolderChanged.parent;
        }
        return undefined;
    }
    onItemFolderClicked(eventArgs) {
        this.treeItemFolderClicked.emit(eventArgs);
        this.treeItemFolderStateChanged.emit(eventArgs);
        this.latestFolderChanged = eventArgs.treeItem;
        this.updateBreadCrumb(this.evalLowestBreadCrumbNode());
        if (!this.flatTree) {
            this.fillFlattenedTree(this._items);
        }
        else if (this.latestFolderChanged.state === 'expanded') {
            this.fillFlattenedTree(this.latestFolderChanged.children ?? []);
            setTimeout(() => {
                this.treeViewInnerElement?.nativeElement.focus();
            });
        }
    }
    updateBreadCrumb(treeItem) {
        if (!treeItem || treeItem.level === ROOT_LEVEL) {
            this.breadCrumbTreeItems = [];
        }
        else {
            this.breadCrumbTreeItems = [treeItem];
            this.pushParentItemsIntoBreadCrumbRecursive(treeItem);
            this.breadCrumbTreeItems = this.breadCrumbTreeItems.reverse();
        }
    }
    pushParentItemsIntoBreadCrumbRecursive(treeItem) {
        if (treeItem?.parent && treeItem?.parent?.level !== ROOT_LEVEL) {
            this.breadCrumbTreeItems.push(treeItem.parent);
            this.pushParentItemsIntoBreadCrumbRecursive(treeItem.parent);
        }
    }
    onItemCheckboxClicked(eventArgs) {
        this.siTreeViewService.triggerMarkForCheck.next();
        this.treeItemCheckboxClicked.emit(eventArgs);
    }
    onLoadChildren(treeItem) {
        this.loadChildren.emit(new LoadChildrenEventArgs(treeItem, (item, children) => this.childrenLoadingDone(item, children)));
    }
    setFlatTreeSelectedFolder(treeItem) {
        if (this.flatTree) {
            this.latestFolderChanged = treeItem;
            if (treeItem) {
                expand(treeItem);
            }
            this.onBreadCrumbItemClicked(treeItem);
        }
    }
    onBreadCrumbItemClicked(treeItem) {
        if (treeItem && treeItem.level !== ROOT_LEVEL) {
            this.fillFlattenedTree(treeItem.children ?? [], true);
        }
        else {
            this.fillFlattenedTree(this._items, true);
        }
        this.updateBreadCrumb(treeItem);
    }
    onScroll(event) {
        if (this.siTreeViewService.isVirtualized) {
            const scrollTop = event.target.scrollTop;
            this.siTreeViewVirtualizationService.handleScroll(scrollTop, this.siTreeViewConverterService.flattenedTrees);
        }
    }
    /**
     * Called by the consumer when he wants a node to be scrolled into view.
     */
    onScrollIntoViewByConsumer(treeItem) {
        if (treeItem) {
            if (this.isVirtualized) {
                this.siTreeViewVirtualizationService.virtualizeItem(treeItem, this.siTreeViewConverterService.flattenedTrees);
            }
            // wait one cycle until the new children (in case of page virtualization) are created
            const task = () => {
                this.doShowTreeItem(treeItem, false); // keep previously opened nodes as it is
                asyncScheduler.schedule(() => {
                    this.scrollChildIntoView.next(treeItem);
                    this.cdRef.markForCheck();
                }, 0);
            };
            asyncScheduler.schedule(task, 0);
        }
    }
    onScrollIntoView(elementTreeItem) {
        const clientRectNode = elementTreeItem.nativeElement.getBoundingClientRect();
        const clientRect = this.element.nativeElement.getBoundingClientRect();
        if ((clientRectNode.top < clientRect.top) || (clientRectNode.top + this.siTreeViewItemHeightService.itemHeight > clientRect.bottom)) {
            elementTreeItem.nativeElement.scrollIntoView();
        }
        this.cdRef.markForCheck();
    }
    fillFlattenedTree(items, resetVirtualization = false) {
        this.collapseChildrenFlatTreeModeOnly(items);
        this.siTreeViewConverterService.fillFlattenedTree(items);
        if (resetVirtualization) {
            this.resetVirtualizedItemList();
        }
        else {
            this.updateVirtualizedItemList();
        }
    }
    updateHasChildren() {
        this.hasAnyChildren = !!this.siTreeViewConverterService.flattenedTrees?.some(item => hasChildren(item));
    }
    updateVirtualizedItemList() {
        this.updateHasChildren();
        if (this.isVirtualized && this.initialized) {
            this.siTreeViewVirtualizationService.updateVirtualizedItemList(this.siTreeViewConverterService.flattenedTrees);
            this.cdRef.markForCheck();
        }
    }
    resetVirtualizedItemList() {
        this.updateHasChildren();
        if (this.isVirtualized && this.initialized) {
            this.siTreeViewVirtualizationService.resetVirtualizedItemList(this.siTreeViewConverterService.flattenedTrees);
        }
    }
    initializeTreeItemsRecursive(items, parent, level) {
        level ?? (level = 0);
        for (const item of items) {
            item.parent = parent;
            item.level = level;
            this.setTreeItemDefaults(item);
            if (item.children?.length) {
                this.initializeTreeItemsRecursive(item.children, item, level + 1);
            }
        }
    }
    setTreeItemDefaults(item) {
        item.children ?? (item.children = []);
        item.state ?? (item.state = 'collapsed');
        item.active ?? (item.active = false);
        item.checked ?? (item.checked = 'unchecked');
        item.selected ?? (item.selected = false);
        item.selectable ?? (item.selectable = true);
        item.showCheckbox ?? (item.showCheckbox = this.siTreeViewService.enableCheckbox ?? false);
        item.showOptionbox ?? (item.showOptionbox = this.siTreeViewService.enableOptionbox ?? false);
    }
}
SiTreeViewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTreeViewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTreeViewComponent, selector: "si-tree-view", inputs: { updateTreeDisplay: "updateTreeDisplay", scrollIntoView: "scrollIntoView", contextMenuItems: "contextMenuItems", childrenIndentation: "childrenIndentation", horizontalScrolling: "horizontalScrolling", compactMode: "compactMode", expandCollapseAll: "expandCollapseAll", expandAllTooltip: "expandAllTooltip", collapseAllTooltip: "collapseAllTooltip", contextMenuAriaLabel: "contextMenuAriaLabel", icons: "icons", trackByFunction: "trackByFunction", pageSize: "pageSize", pagesVirtualized: "pagesVirtualized", folderStateLeft: "folderStateLeft", folderStateStart: "folderStateStart", isVirtualized: "isVirtualized", items: "items", selectedItem: "selectedItem", singleSelectMode: "singleSelectMode", enableDataField1: "enableDataField1", enableDataField2: "enableDataField2", enableStateIndicator: "enableStateIndicator", enableIcon: "enableIcon", enableContextMenuButton: "enableContextMenuButton", enableSelection: "enableSelection", deleteChildrenOnCollapse: "deleteChildrenOnCollapse", flatTree: "flatTree", groupedList: "groupedList", enableCheckbox: "enableCheckbox", enableOptionbox: "enableOptionbox", inheritChecked: "inheritChecked", noActionsString: "noActionsString" }, outputs: { itemsVirtualizedChanged: "itemsVirtualizedChanged", treeItemFolderClicked: "treeItemFolderClicked", treeItemFolderStateChanged: "treeItemFolderStateChanged", treeItemClicked: "treeItemClicked", treeItemCheckboxClicked: "treeItemCheckboxClicked", loadChildren: "loadChildren", treeItemsSelected: "treeItemsSelected" }, host: { listeners: { "document:keyup.shift": "onKeyUpShift($event)", "document:keyup.control": "onKeyUpCtrl($event)", "document:keyup.meta": "onKeyUpMeta($event)", "document:mouseleave": "onMouseLeave($event)" } }, providers: [
        SiTreeViewConverterService,
        SiTreeViewItemHeightService,
        SiTreeViewService,
        SiTreeViewVirtualizationService
    ], queries: [{ propertyName: "templates", predicate: SiTreeViewItemTemplateDirective }], viewQueries: [{ propertyName: "treeViewInnerElement", first: true, predicate: ["treeViewInner"], descendants: true, read: ElementRef, static: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  *ngIf=\"!flatTree && expandCollapseAll\"\n  class=\"si-tree-view-expand-collapse-container\"\n  role=\"menu\"\n>\n  <si-menu-item\n    class=\"si-tree-view-expand-collapse\"\n    [item]=\"expandItem\"\n    itemClass=\"btn btn-circle btn-tertiary\"\n    [disableListInteraction]=\"true\"\n    role=\"menuitem\"\n  ></si-menu-item>\n  <si-menu-item\n    class=\"si-tree-view-expand-collapse\"\n    [item]=\"collapseItem\"\n    itemClass=\"btn btn-circle btn-tertiary\"\n    [disableListInteraction]=\"true\"\n    role=\"menuitem\"\n  ></si-menu-item>\n</div>\n<div *ngIf=\"flatTree\" class=\"si-tree-view-header\" >\n  <span *ngIf=\"!lastBreadCrumbItem\"\n    class=\"si-tree-view-header-btn\"\n    [class.disabled]=\"headerShowsRoot\"\n    [ngClass]=\"icons.headerHome\"\n    (click)=\"onFlatTreeNavigateHome()\"\n    (keydown.enter)=\"onFlatTreeNavigateHome()\"\n    tabindex=\"0\"\n  ></span>\n  <span *ngIf=\"lastBreadCrumbItem\"\n    class=\"si-tree-view-header-btn\"\n    [class.disabled]=\"headerShowsRoot\"\n    [ngClass]=\"icons.headerArrow\"\n    (click)=\"onFlatTreeNavigateUp()\"\n    (keydown.enter)=\"onFlatTreeNavigateUp()\"\n    tabindex=\"0\"\n  ></span>\n  <span *ngIf=\"lastBreadCrumbItem\" class=\"text-center text-truncate\">{{ (lastBreadCrumbItem.label ?? '') | translate }}</span>\n</div>\n<div\n  #treeViewInner\n  class=\"si-tree-view focus-inside\"\n  [class.si-tree-horizontal-scroll]=\"horizontalScrolling\"\n  [class.si-tree-view-flat]=\"flatTree\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  (keydown)=\"onTreeArrowKeydown($event)\"\n>\n  <div *ngIf=\"isVirtualized\" [class.w-100]=\"horizontalScrolling\" [style.height]=\"heightBefore\"></div>\n  <div *ngIf=\"itemsVirtualized\" class=\"si-tree-view-root-ul\">\n    <si-tree-view-item\n      *ngFor=\"let treeItem of itemsVirtualized; trackBy: trackByFunction\"\n      class=\"focus-none\"\n      [class.si-tree-ellipsis]=\"!horizontalScrolling\"\n      [class.si-tree-view-top-level-item]=\"!compactMode && (flatTree || (treeItem.level ?? 0) < 2)\"\n      [treeItem]=\"treeItem\"\n      [templates]=\"templates\"\n      [scrollIntoView]=\"scrollChildIntoView\"\n      [childrenLoaded]=\"childrenLoaded\"\n      [indentLevel]=\"treeItem.level ?? 0\"\n      [icons]=\"icons\"\n      [contextMenuItemsProvider]=\"contextMenuItems\"\n      [stickyEndItems]=\"horizontalScrolling\"\n      [displayFolderState]=\"hasAnyChildren\"\n      [contextMenuAriaLabel]=\"contextMenuAriaLabel\"\n      siListInteractionItem\n    >\n    </si-tree-view-item>\n  </div>\n  <div *ngIf=\"isVirtualized\" [class.w-100]=\"horizontalScrolling\" [style.height]=\"heightAfter\"></div>\n</div>\n", styles: ["@keyframes si-spin{0%{transform:rotate(0)}to{transform:rotate(359deg)}}:host{--si-tree-view-background: var(--element-base-1);--si-tree-view-border-color: var(--element-ui-4);--si-tree-view-icon-size: 24px;--si-tree-view-padding-base-horizontal: 8px;--si-tree-view-padding-base-vertical: 8px;--si-tree-view-expand-collapse-height: 32px;--si-tree-view-expand-collapse-padding-horizontal: 6px;--si-tree-view-expand-collapse-padding-vertical: 4px;--si-tree-view-expand-collapse-border-radius: var(--element-radius-1);--si-tree-view-item-hover-color: var(--element-base-1-hover);--si-tree-view-item-select-color: var(--element-base-1-selected);--si-tree-view-item-line-height: 1.143;--si-tree-view-item-min-height: 40px;--si-tree-view-item-object-data-field-1-color: var(--element-text-secondary);--si-tree-view-item-object-data-h5-font-size: .875rem;--si-tree-view-item-object-data-h5-font-weight: normal;display:flex;flex-direction:column;background-color:var(--si-tree-view-background)}:host.tree-sm{--si-tree-view-item-min-height: 32px;--si-tree-view-padding-base-vertical: 0px}:host.tree-xs{--si-tree-view-item-min-height: 24px;--si-tree-view-padding-base-vertical: 0px}.si-tree-view-expand-collapse-container{margin-block:var(--si-tree-view-padding-base-vertical) calc(-.5 * var(--si-tree-view-padding-base-vertical));margin-inline:var(--si-tree-view-padding-base-horizontal)}.si-tree-view-expand-collapse{display:inline-block;margin-inline-start:calc(var(--si-tree-view-padding-base-vertical) / 2)}.si-tree-view-expand-collapse:first-child{margin-inline-start:0}.si-tree-view-expand-collapse ::ng-deep .dropdown-item{padding-block:var(--si-tree-view-expand-collapse-padding-vertical);padding-inline:var(--si-tree-view-expand-collapse-padding-horizontal);block-size:calc(var(--si-tree-view-expand-collapse-height) - var(--si-tree-view-expand-collapse-padding-vertical));border-radius:var(--si-tree-view-expand-collapse-border-radius)}.si-tree-view-expand-collapse ::ng-deep .dropdown-item .item-icon{margin-inline:0}.si-tree-view-header{padding:calc(var(--si-tree-view-padding-base-horizontal) / 2);border-block-end:1px solid var(--si-tree-view-border-color)}.si-tree-view-header>*{align-self:center}.si-tree-view-header-btn{cursor:pointer;margin-inline-end:calc(var(--si-tree-view-padding-base-horizontal) / 2);font-size:var(--si-tree-view-icon-size)}.si-tree-view{overflow:auto;flex:1 0 0;min-block-size:0}.si-tree-view.si-tree-horizontal-scroll{display:flex;flex-wrap:wrap;align-content:flex-start}.si-tree-view-root-ul{list-style:none;padding:0;margin:0;flex:1 1 auto}@supports (-webkit-touch-callout: none){.si-tree-horizontal-scroll{transform:translateZ(0)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }, { kind: "component", type: SiTreeViewItemComponent, selector: "si-tree-view-item", inputs: ["indentLevel", "treeItem", "scrollIntoView", "childrenLoaded", "contextMenuAriaLabel", "templates", "contextMenuItemsProvider", "stickyRightItems", "stickyEndItems", "displayFolderState", "icons"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-tree-view', providers: [
                        SiTreeViewConverterService,
                        SiTreeViewItemHeightService,
                        SiTreeViewService,
                        SiTreeViewVirtualizationService
                    ], template: "<div\n  *ngIf=\"!flatTree && expandCollapseAll\"\n  class=\"si-tree-view-expand-collapse-container\"\n  role=\"menu\"\n>\n  <si-menu-item\n    class=\"si-tree-view-expand-collapse\"\n    [item]=\"expandItem\"\n    itemClass=\"btn btn-circle btn-tertiary\"\n    [disableListInteraction]=\"true\"\n    role=\"menuitem\"\n  ></si-menu-item>\n  <si-menu-item\n    class=\"si-tree-view-expand-collapse\"\n    [item]=\"collapseItem\"\n    itemClass=\"btn btn-circle btn-tertiary\"\n    [disableListInteraction]=\"true\"\n    role=\"menuitem\"\n  ></si-menu-item>\n</div>\n<div *ngIf=\"flatTree\" class=\"si-tree-view-header\" >\n  <span *ngIf=\"!lastBreadCrumbItem\"\n    class=\"si-tree-view-header-btn\"\n    [class.disabled]=\"headerShowsRoot\"\n    [ngClass]=\"icons.headerHome\"\n    (click)=\"onFlatTreeNavigateHome()\"\n    (keydown.enter)=\"onFlatTreeNavigateHome()\"\n    tabindex=\"0\"\n  ></span>\n  <span *ngIf=\"lastBreadCrumbItem\"\n    class=\"si-tree-view-header-btn\"\n    [class.disabled]=\"headerShowsRoot\"\n    [ngClass]=\"icons.headerArrow\"\n    (click)=\"onFlatTreeNavigateUp()\"\n    (keydown.enter)=\"onFlatTreeNavigateUp()\"\n    tabindex=\"0\"\n  ></span>\n  <span *ngIf=\"lastBreadCrumbItem\" class=\"text-center text-truncate\">{{ (lastBreadCrumbItem.label ?? '') | translate }}</span>\n</div>\n<div\n  #treeViewInner\n  class=\"si-tree-view focus-inside\"\n  [class.si-tree-horizontal-scroll]=\"horizontalScrolling\"\n  [class.si-tree-view-flat]=\"flatTree\"\n  siListInteraction\n  listInteractionOrientation=\"vert\"\n  (keydown)=\"onTreeArrowKeydown($event)\"\n>\n  <div *ngIf=\"isVirtualized\" [class.w-100]=\"horizontalScrolling\" [style.height]=\"heightBefore\"></div>\n  <div *ngIf=\"itemsVirtualized\" class=\"si-tree-view-root-ul\">\n    <si-tree-view-item\n      *ngFor=\"let treeItem of itemsVirtualized; trackBy: trackByFunction\"\n      class=\"focus-none\"\n      [class.si-tree-ellipsis]=\"!horizontalScrolling\"\n      [class.si-tree-view-top-level-item]=\"!compactMode && (flatTree || (treeItem.level ?? 0) < 2)\"\n      [treeItem]=\"treeItem\"\n      [templates]=\"templates\"\n      [scrollIntoView]=\"scrollChildIntoView\"\n      [childrenLoaded]=\"childrenLoaded\"\n      [indentLevel]=\"treeItem.level ?? 0\"\n      [icons]=\"icons\"\n      [contextMenuItemsProvider]=\"contextMenuItems\"\n      [stickyEndItems]=\"horizontalScrolling\"\n      [displayFolderState]=\"hasAnyChildren\"\n      [contextMenuAriaLabel]=\"contextMenuAriaLabel\"\n      siListInteractionItem\n    >\n    </si-tree-view-item>\n  </div>\n  <div *ngIf=\"isVirtualized\" [class.w-100]=\"horizontalScrolling\" [style.height]=\"heightAfter\"></div>\n</div>\n", styles: ["@keyframes si-spin{0%{transform:rotate(0)}to{transform:rotate(359deg)}}:host{--si-tree-view-background: var(--element-base-1);--si-tree-view-border-color: var(--element-ui-4);--si-tree-view-icon-size: 24px;--si-tree-view-padding-base-horizontal: 8px;--si-tree-view-padding-base-vertical: 8px;--si-tree-view-expand-collapse-height: 32px;--si-tree-view-expand-collapse-padding-horizontal: 6px;--si-tree-view-expand-collapse-padding-vertical: 4px;--si-tree-view-expand-collapse-border-radius: var(--element-radius-1);--si-tree-view-item-hover-color: var(--element-base-1-hover);--si-tree-view-item-select-color: var(--element-base-1-selected);--si-tree-view-item-line-height: 1.143;--si-tree-view-item-min-height: 40px;--si-tree-view-item-object-data-field-1-color: var(--element-text-secondary);--si-tree-view-item-object-data-h5-font-size: .875rem;--si-tree-view-item-object-data-h5-font-weight: normal;display:flex;flex-direction:column;background-color:var(--si-tree-view-background)}:host.tree-sm{--si-tree-view-item-min-height: 32px;--si-tree-view-padding-base-vertical: 0px}:host.tree-xs{--si-tree-view-item-min-height: 24px;--si-tree-view-padding-base-vertical: 0px}.si-tree-view-expand-collapse-container{margin-block:var(--si-tree-view-padding-base-vertical) calc(-.5 * var(--si-tree-view-padding-base-vertical));margin-inline:var(--si-tree-view-padding-base-horizontal)}.si-tree-view-expand-collapse{display:inline-block;margin-inline-start:calc(var(--si-tree-view-padding-base-vertical) / 2)}.si-tree-view-expand-collapse:first-child{margin-inline-start:0}.si-tree-view-expand-collapse ::ng-deep .dropdown-item{padding-block:var(--si-tree-view-expand-collapse-padding-vertical);padding-inline:var(--si-tree-view-expand-collapse-padding-horizontal);block-size:calc(var(--si-tree-view-expand-collapse-height) - var(--si-tree-view-expand-collapse-padding-vertical));border-radius:var(--si-tree-view-expand-collapse-border-radius)}.si-tree-view-expand-collapse ::ng-deep .dropdown-item .item-icon{margin-inline:0}.si-tree-view-header{padding:calc(var(--si-tree-view-padding-base-horizontal) / 2);border-block-end:1px solid var(--si-tree-view-border-color)}.si-tree-view-header>*{align-self:center}.si-tree-view-header-btn{cursor:pointer;margin-inline-end:calc(var(--si-tree-view-padding-base-horizontal) / 2);font-size:var(--si-tree-view-icon-size)}.si-tree-view{overflow:auto;flex:1 0 0;min-block-size:0}.si-tree-view.si-tree-horizontal-scroll{display:flex;flex-wrap:wrap;align-content:flex-start}.si-tree-view-root-ul{list-style:none;padding:0;margin:0;flex:1 1 auto}@supports (-webkit-touch-callout: none){.si-tree-horizontal-scroll{transform:translateZ(0)}}\n"] }]
        }], propDecorators: { updateTreeDisplay: [{
                type: Input
            }], scrollIntoView: [{
                type: Input
            }], contextMenuItems: [{
                type: Input
            }], childrenIndentation: [{
                type: Input
            }], horizontalScrolling: [{
                type: Input
            }], compactMode: [{
                type: Input
            }], expandCollapseAll: [{
                type: Input
            }], expandAllTooltip: [{
                type: Input
            }], collapseAllTooltip: [{
                type: Input
            }], contextMenuAriaLabel: [{
                type: Input
            }], icons: [{
                type: Input
            }], itemsVirtualizedChanged: [{
                type: Output
            }], treeItemFolderClicked: [{
                type: Output
            }], treeItemFolderStateChanged: [{
                type: Output
            }], treeItemClicked: [{
                type: Output
            }], treeItemCheckboxClicked: [{
                type: Output
            }], loadChildren: [{
                type: Output
            }], treeItemsSelected: [{
                type: Output
            }], templates: [{
                type: ContentChildren,
                args: [SiTreeViewItemTemplateDirective]
            }], trackByFunction: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pagesVirtualized: [{
                type: Input
            }], folderStateLeft: [{
                type: Input
            }], folderStateStart: [{
                type: Input
            }], isVirtualized: [{
                type: Input
            }], items: [{
                type: Input
            }], selectedItem: [{
                type: Input
            }], singleSelectMode: [{
                type: Input
            }], enableDataField1: [{
                type: Input
            }], enableDataField2: [{
                type: Input
            }], enableStateIndicator: [{
                type: Input
            }], enableIcon: [{
                type: Input
            }], enableContextMenuButton: [{
                type: Input
            }], enableSelection: [{
                type: Input
            }], deleteChildrenOnCollapse: [{
                type: Input
            }], flatTree: [{
                type: Input
            }], groupedList: [{
                type: Input
            }], enableCheckbox: [{
                type: Input
            }], enableOptionbox: [{
                type: Input
            }], inheritChecked: [{
                type: Input
            }], noActionsString: [{
                type: Input
            }], onKeyUpShift: [{
                type: HostListener,
                args: ['document:keyup.shift', ['$event']]
            }], onKeyUpCtrl: [{
                type: HostListener,
                args: ['document:keyup.control', ['$event']]
            }], onKeyUpMeta: [{
                type: HostListener,
                args: ['document:keyup.meta', ['$event']]
            }], onMouseLeave: [{
                type: HostListener,
                args: ['document:mouseleave', ['$event']]
            }], treeViewInnerElement: [{
                type: ViewChild,
                args: ['treeViewInner', { read: ElementRef, static: true }]
            }] } });

class SiTreeViewModule {
}
SiTreeViewModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiTreeViewModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewModule, declarations: [SiTreeViewComponent,
        SiTreeViewItemComponent,
        SiTreeViewItemTemplateDirective], imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiLoadingSpinnerModule,
        SiMenuModule,
        SiTranslateModule], exports: [SiTreeViewComponent,
        SiTreeViewItemComponent,
        SiTreeViewItemTemplateDirective] });
SiTreeViewModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewModule, imports: [CommonModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiLoadingSpinnerModule,
        SiMenuModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTreeViewModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiDropdownModule,
                        SiListInteractionModule,
                        SiLoadingSpinnerModule,
                        SiMenuModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiTreeViewComponent,
                        SiTreeViewItemComponent,
                        SiTreeViewItemTemplateDirective
                    ],
                    exports: [
                        SiTreeViewComponent,
                        SiTreeViewItemComponent,
                        SiTreeViewItemTemplateDirective
                    ]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiWizardStepComponent {
    constructor() {
        this.heading = '';
        this.isValid = true;
        this.isNextNavigable = true;
        this.failed = false;
        this.next = new EventEmitter();
        this.back = new EventEmitter();
        this.save = new EventEmitter();
        this.cdRef = inject(ChangeDetectorRef);
        this._isActive = false;
    }
    get isActive() {
        return this._isActive;
    }
    set isActive(value) {
        this._isActive = value;
        this.cdRef.markForCheck();
    }
    ngOnChanges() {
        this.parent?.notifyChildrenUpdated();
    }
    registerParent(parent) {
        this.parent = parent;
    }
}
SiWizardStepComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardStepComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiWizardStepComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiWizardStepComponent, selector: "si-wizard-step", inputs: { heading: "heading", isValid: "isValid", isNextNavigable: "isNextNavigable", failed: "failed" }, outputs: { next: "next", back: "back", save: "save" }, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"isActive\">\n  <ng-content></ng-content>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardStepComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-wizard-step', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div *ngIf=\"isActive\">\n  <ng-content></ng-content>\n</div>\n" }]
        }], propDecorators: { heading: [{
                type: Input
            }], isValid: [{
                type: Input
            }], isNextNavigable: [{
                type: Input
            }], failed: [{
                type: Input
            }], next: [{
                type: Output
            }], back: [{
                type: Output
            }], save: [{
                type: Output
            }] } });

class SiWizardComponent {
    constructor() {
        /**
         * Description of back button.
         */
        this.backText = 'Back';
        /**
         * Description of next button.
         */
        this.nextText = 'Next';
        /**
         * Description of save button.
         */
        this.saveText = 'Save';
        /**
         * Text shown if you complete the wizard.
         */
        this.completionText = 'Wizard completed!';
        /**
         * Description of cancel button.
         */
        this.cancelText = 'Cancel';
        /**
         * Show cancel bottom
         */
        this.hasCancel = false;
        /**
         * Display a predefined page by the end of the wizard.
         */
        this.enableCompletionPage = false;
        /**
         * Define how many milliseconds the completion page is visible.
         */
        this.completionPageVisibleTime = 3000;
        /**
         * Class name of icon shown for current and upcoming steps.
         */
        this.stepIcon = 'element-not-checked';
        /**
         * Class name of icon shown when a step was completed.
         */
        this.stepCompletedIcon = 'element-checked-filled';
        /**
         * Class name of icon shown when a step had an error.
         */
        this.stepFailedIcon = 'element-warning-filled';
        /**
         * Callback function triggered after the wizard has been completed.
         */
        this.completionAction = new EventEmitter();
        /**
         * Callback function triggered if the wizard has been canceled.
         */
        this.cancel = new EventEmitter();
        this.index = 0;
        this.steps = [];
        this.allStepsAreVisible = true;
        this.visibleSteps = 0;
        this.showCompletionPage = false;
        this.trackByIndex = (index) => index;
        this.isSmall = false;
        this.destroyer = new Subject();
        this.cdRef = inject(ChangeDetectorRef);
    }
    ngAfterContentInit() {
        this.updateSteps();
        this.currentStep = this.steps[0];
        this.cdRef.markForCheck();
        setTimeout(() => {
            if (this.steps.length > 0) {
                this.steps[0].isActive = true;
                this.cdRef.markForCheck();
            }
        });
        this.wizardSteps.changes.pipe(takeUntil(this.destroyer)).subscribe(() => {
            this.updateSteps();
            if (!this.wizardSteps.some(step => step === this.currentStep)) {
                this.currentStep = this.wizardSteps.first;
                this.index = 0;
            }
            else {
                this.index = this.steps.indexOf(this.currentStep);
            }
            this.cdRef.markForCheck();
            setTimeout(() => {
                this.activate(this.currentStep);
                this.calculateNumberOfVisibleSteps();
                this.cdRef.markForCheck();
            });
        });
    }
    ngOnDestroy() {
        this.destroyer.next();
        this.destroyer.complete();
    }
    notifyChildrenUpdated() {
        this.cdRef.markForCheck();
    }
    canActivate(stepIndex) {
        if (stepIndex < 0) {
            return false;
        }
        // Can always activate previous steps
        if (stepIndex < this.index) {
            return true;
        }
        // We are already in the step. Nothing to activate.
        if (stepIndex === this.index) {
            return false;
        }
        // Fast-forward: check all steps if they are valid
        for (let i = this.index; i < stepIndex; i++) {
            const theStep = this.steps[i];
            if (!theStep.isValid) {
                return false;
            }
        }
        return true;
    }
    activateStep(event, stepIndex) {
        event.preventDefault();
        if (this.canActivate(stepIndex)) {
            if (stepIndex > this.index) {
                this.next(stepIndex - this.index);
            }
            if (stepIndex < this.index) {
                this.back(this.index - stepIndex);
            }
        }
    }
    getStateClass(stepIndex) {
        if (stepIndex === this.index) {
            return 'active';
        }
        if (!this.canActivate(stepIndex)) {
            return 'disabled';
        }
        if (stepIndex < this.index) {
            return 'completed';
        }
        return '';
    }
    getAriaDisabled(stepIndex) {
        if (!this.canActivate(stepIndex)) {
            return 'true';
        }
        return 'false';
    }
    getAriaCurrent(stepIndex) {
        if (stepIndex === this.index) {
            return 'step';
        }
        return 'false';
    }
    getStepClass(stepIndex) {
        const classes = ['step'];
        if (this.isVisibleStep(stepIndex + 1)) {
            classes.push('directly-following');
        }
        else if (stepIndex !== this.steps.length - 1) {
            classes.push('not-directly-following');
        }
        if (stepIndex !== 0 && !this.isVisibleStep(stepIndex - 1)) {
            classes.push('not-first-step');
        }
        if (this.isVisibleStep(stepIndex + 1)) {
            classes.push('line-' + this.getStateClass(stepIndex + 1));
        }
        return classes;
    }
    next(delta) {
        if (this.index === this.steps.length) {
            return;
        }
        const stepIndex = this.index + delta;
        const nextStep = this.steps[stepIndex];
        if (this.canActivate(stepIndex)) {
            this.currentStep.next.emit();
            if (this.currentStep.isNextNavigable) {
                this.activate(nextStep);
            }
        }
    }
    back(delta) {
        if (this.index === 0) {
            return;
        }
        this.currentStep.back.emit();
        this.activate(this.steps[this.index - delta]);
    }
    save() {
        this.currentStep.save.emit();
        if (this.enableCompletionPage && this.completionPageVisibleTime > 0) {
            this.showCompletionPage = true;
            setTimeout(() => {
                this.showCompletionPage = false;
                this.completionAction.emit();
                this.cdRef.markForCheck();
            }, this.completionPageVisibleTime);
        }
        else {
            this.completionAction.emit();
        }
    }
    getState(step, stepIndex) {
        if (step.failed === true) {
            return `title-warning ${this.stepFailedIcon}`;
        }
        else {
            const txtStyle = step.isActive ? 'wizard-active' : '';
            return stepIndex >= this.index ? `${this.stepIcon} ${txtStyle}` : `text-primary ${this.stepCompletedIcon}`;
        }
    }
    isVisibleStep(stepIndex) {
        if (this.allStepsAreVisible) {
            return true;
        }
        if (this.index === stepIndex) {
            return true;
        }
        let visibleStepsDelta = (this.visibleSteps - 1) / 2;
        for (let i = visibleStepsDelta; i > 0; i--) {
            if ((this.index - i < 0) || (this.index + i > this.steps.length - 1)) {
                visibleStepsDelta = this.visibleSteps - i;
            }
        }
        for (let i = visibleStepsDelta; i > 0; i--) {
            if (stepIndex === this.index - i || stepIndex === this.index + i) {
                return true;
            }
        }
        return false;
    }
    activate(step) {
        if (this.currentStep) {
            this.currentStep.isActive = false;
        }
        step.isActive = true;
        this.currentStep = step;
        this.index = this.steps.indexOf(step);
    }
    calculateNumberOfVisibleSteps() {
        const containerWidth = this.wizardContainerElement.nativeElement.clientWidth;
        const prevNextWidth = this.wizardButtonContainerElement.nativeElement.clientWidth;
        const cancelWidth = this.hasCancel ? this.wizardCancelButtonContainerElement.nativeElement.clientWidth : 0;
        const oneStepWidth = this.wizardContainerElement.nativeElement.querySelector('.step')?.clientWidth;
        // visible steps can be only 3, 5, 7,..
        const allButtonsWidth = 2 * prevNextWidth + cancelWidth;
        let placeableSteps = oneStepWidth ? Math.floor((containerWidth - allButtonsWidth) / oneStepWidth) : 1;
        this.allStepsAreVisible = placeableSteps >= this.steps.length;
        if (!this.allStepsAreVisible && (placeableSteps % 2) === 0) {
            placeableSteps--;
        }
        if (containerWidth < (540 + allButtonsWidth)) {
            this.isSmall = true;
            this.visibleSteps = 3;
        }
        else {
            this.isSmall = false;
            this.visibleSteps = placeableSteps;
        }
    }
    updateSteps() {
        this.steps = this.wizardSteps.toArray();
        this.steps.forEach(step => step.registerParent(this));
    }
}
SiWizardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiWizardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiWizardComponent, selector: "si-wizard", inputs: { backText: "backText", nextText: "nextText", saveText: "saveText", completionText: "completionText", cancelText: "cancelText", hasCancel: "hasCancel", enableCompletionPage: "enableCompletionPage", completionPageVisibleTime: "completionPageVisibleTime", stepIcon: "stepIcon", stepCompletedIcon: "stepCompletedIcon", stepFailedIcon: "stepFailedIcon" }, outputs: { completionAction: "completionAction", cancel: "cancel" }, queries: [{ propertyName: "wizardSteps", predicate: SiWizardStepComponent }], viewQueries: [{ propertyName: "wizardContainerElement", first: true, predicate: ["container"], descendants: true }, { propertyName: "wizardCancelButtonContainerElement", first: true, predicate: ["cancelButtonContainer"], descendants: true }, { propertyName: "wizardButtonContainerElement", first: true, predicate: ["buttonContainer"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"!showCompletionPage\">\n  <div\n    #container\n    class=\"container-wizard\"\n    [class.display-one-step]=\"visibleSteps === 1\"\n    (siResizeObserver)=\"calculateNumberOfVisibleSteps()\"\n  >\n    <div class=\"me-7\" *ngIf=\"hasCancel\" #cancelButtonContainer>\n      <div class=\"wizard-btn-container\" (click)=\"cancel.emit()\">\n        <button type=\"button\" class=\"btn btn-circle btn-sm btn-secondary element-cancel mt-2 mb-4\">\n          <span class=\"visually-hidden\">{{ cancelText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\">{{ cancelText | translate }}</div>\n      </div>\n    </div>\n    <div class=\"wizard-btn-container\" #buttonContainer>\n      <div class=\"back\" [class.invisible]=\"index === 0\" (click)=\"back(1)\">\n        <button type=\"button\" class=\"btn btn-circle btn-secondary flip-rtl element-left-4 mb-2\">\n          <span class=\"visually-hidden\">{{ backText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\">{{ backText | translate }}</div>\n      </div>\n    </div>\n    <div class=\"container-steps\" [class.small]=\"isSmall\">\n      <ng-container *ngFor=\"let step of steps; let stepIndex = index; trackBy: trackByIndex\">\n        <div *ngIf=\"isVisibleStep(stepIndex)\" [ngClass]=\"getStepClass(stepIndex)\" [class.small]=\"isSmall\">\n          <a (click)=\"activateStep($event, stepIndex)\"\n            class=\"focus-inside\"\n            [ngClass]=\"getStateClass(stepIndex)\"\n            [attr.aria-current]=\"getAriaCurrent(stepIndex)\"\n            [attr.href]=\"!canActivate(stepIndex) || !currentStep.isNextNavigable ? null : '#'\">\n            <i [ngClass]=\"[getState(step, stepIndex)]\"></i>\n            <div class=\"title text-center\" [attr.aria-disabled]=\"getAriaDisabled(stepIndex)\">{{ step.heading | translate }}</div>\n          </a>\n        </div>\n      </ng-container>\n    </div>\n    <div class=\"wizard-btn-container\" [class.wizard-text-deactivate]=\"!currentStep.isValid\">\n      <div class=\"next\" [class.invisible]=\"index === steps.length - 1\" (click)=\"next(1)\">\n        <button type=\"button\" class=\"btn btn-circle btn-primary flip-rtl element-right-4 mb-2\" [disabled]=\"!currentStep.isValid\">\n          <span class=\"visually-hidden\">{{ nextText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\" [class.disabled]=\"!currentStep.isValid\" [attr.aria-disabled]=\"!currentStep.isValid\">{{ nextText | translate }}</div>\n      </div>\n    </div>\n  </div>\n  <div>\n    <ng-content></ng-content>\n  </div>\n  <div class=\"container-wizard\">\n    <button\n      *ngIf=\"index === steps.length - 1\"\n      type=\"button\"\n      class=\"btn btn-info save\"\n      (click)=\"save()\"\n      [disabled]=\"!currentStep.isValid || !currentStep.isNextNavigable\"\n    >{{ saveText | translate }}</button>\n  </div>\n</ng-container>\n\n<div *ngIf=\"showCompletionPage\" class=\"text-center\">\n  <i class=\"display-1 text-success element-checked\"></i>\n  <p class=\"si-title-1\"> {{ completionText | translate }}</p>\n</div>\n", styles: [".title-secondary{color:var(--element-text-secondary)}.title-warning{color:var(--element-status-warning)}.container-steps{display:flex;justify-content:center;padding-block:0;padding-inline:6px}.container-steps.small{inline-size:100%}.directly-following:not(:last-child):after{background-color:var(--element-ui-2);content:\"\";position:absolute;block-size:2px;inline-size:calc(100% - 1.5rem - 8px);inset-block-start:calc(.75rem - 1px);inset-inline-start:calc(50% + .75rem + 4px)}.not-directly-following:last-child:after,.not-first-step:first-child:before{border-block-start:2px var(--element-ui-2) dashed;content:\"\";position:absolute;block-size:2px;inline-size:1.875rem;inset-block-start:calc(.75rem - 1px);inset-inline-start:calc(50% + .75rem + 4px)}.not-first-step:first-child:before{inset-inline-start:calc(50% - 2.625rem - 4px);border-color:var(--element-ui-0)}.wizard-active{color:var(--element-ui-0)}.step{position:relative;text-align:center;margin-block-start:8px;padding-block:0;padding-inline:16px}.step i{font-size:1.5rem}.step a{color:var(--element-ui-2)}.step a .title{color:var(--element-text-secondary)}.step a .title:hover{text-decoration:none}.step a .title{inline-size:9.75rem;margin-block-start:12px}.step .active,.step .active .title{color:var(--element-ui-0)}.step .disabled,.step .disabled:hover{opacity:.5;text-decoration:none}.step a:not(.disabled):hover,.step a:not(.disabled):hover i,.step a:not(.disabled):hover .title{color:var(--element-ui-0-hover)!important;text-decoration:none}.step .completed .title{color:var(--element-text-primary)}.step.line-completed:after{background-color:var(--element-ui-0)}.step.line-active:after{background-color:var(--element-ui-0)}.step.line-disabled:after{opacity:.5}.step.small,.step.small a .title{inline-size:100%}.title{font-size:.875rem;line-height:20px}.container-wizard{inline-size:100%;display:flex;flex-wrap:nowrap;justify-content:center;padding-block:8px 12px;padding-inline:0}.container-wizard.display-one-step{justify-content:space-between}.wizard-btn-container{max-inline-size:50px;min-inline-size:40px;text-align:center;cursor:pointer}.wizard-btn-container.wizard-text-deactivate:hover{cursor:not-allowed;color:var(--element-text-secondary)}.wizard-btn-container .disabled{opacity:.5}.wizard-btn-container .invisible{visibility:hidden}.wizard-btn-container:hover .text-primary{color:var(--element-ui-0-hover)!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: ["resizeThrottle"], outputs: ["siResizeObserver"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-wizard', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngIf=\"!showCompletionPage\">\n  <div\n    #container\n    class=\"container-wizard\"\n    [class.display-one-step]=\"visibleSteps === 1\"\n    (siResizeObserver)=\"calculateNumberOfVisibleSteps()\"\n  >\n    <div class=\"me-7\" *ngIf=\"hasCancel\" #cancelButtonContainer>\n      <div class=\"wizard-btn-container\" (click)=\"cancel.emit()\">\n        <button type=\"button\" class=\"btn btn-circle btn-sm btn-secondary element-cancel mt-2 mb-4\">\n          <span class=\"visually-hidden\">{{ cancelText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\">{{ cancelText | translate }}</div>\n      </div>\n    </div>\n    <div class=\"wizard-btn-container\" #buttonContainer>\n      <div class=\"back\" [class.invisible]=\"index === 0\" (click)=\"back(1)\">\n        <button type=\"button\" class=\"btn btn-circle btn-secondary flip-rtl element-left-4 mb-2\">\n          <span class=\"visually-hidden\">{{ backText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\">{{ backText | translate }}</div>\n      </div>\n    </div>\n    <div class=\"container-steps\" [class.small]=\"isSmall\">\n      <ng-container *ngFor=\"let step of steps; let stepIndex = index; trackBy: trackByIndex\">\n        <div *ngIf=\"isVisibleStep(stepIndex)\" [ngClass]=\"getStepClass(stepIndex)\" [class.small]=\"isSmall\">\n          <a (click)=\"activateStep($event, stepIndex)\"\n            class=\"focus-inside\"\n            [ngClass]=\"getStateClass(stepIndex)\"\n            [attr.aria-current]=\"getAriaCurrent(stepIndex)\"\n            [attr.href]=\"!canActivate(stepIndex) || !currentStep.isNextNavigable ? null : '#'\">\n            <i [ngClass]=\"[getState(step, stepIndex)]\"></i>\n            <div class=\"title text-center\" [attr.aria-disabled]=\"getAriaDisabled(stepIndex)\">{{ step.heading | translate }}</div>\n          </a>\n        </div>\n      </ng-container>\n    </div>\n    <div class=\"wizard-btn-container\" [class.wizard-text-deactivate]=\"!currentStep.isValid\">\n      <div class=\"next\" [class.invisible]=\"index === steps.length - 1\" (click)=\"next(1)\">\n        <button type=\"button\" class=\"btn btn-circle btn-primary flip-rtl element-right-4 mb-2\" [disabled]=\"!currentStep.isValid\">\n          <span class=\"visually-hidden\">{{ nextText | translate }}</span>\n        </button>\n        <div class=\"si-title-2 text-primary\" [class.disabled]=\"!currentStep.isValid\" [attr.aria-disabled]=\"!currentStep.isValid\">{{ nextText | translate }}</div>\n      </div>\n    </div>\n  </div>\n  <div>\n    <ng-content></ng-content>\n  </div>\n  <div class=\"container-wizard\">\n    <button\n      *ngIf=\"index === steps.length - 1\"\n      type=\"button\"\n      class=\"btn btn-info save\"\n      (click)=\"save()\"\n      [disabled]=\"!currentStep.isValid || !currentStep.isNextNavigable\"\n    >{{ saveText | translate }}</button>\n  </div>\n</ng-container>\n\n<div *ngIf=\"showCompletionPage\" class=\"text-center\">\n  <i class=\"display-1 text-success element-checked\"></i>\n  <p class=\"si-title-1\"> {{ completionText | translate }}</p>\n</div>\n", styles: [".title-secondary{color:var(--element-text-secondary)}.title-warning{color:var(--element-status-warning)}.container-steps{display:flex;justify-content:center;padding-block:0;padding-inline:6px}.container-steps.small{inline-size:100%}.directly-following:not(:last-child):after{background-color:var(--element-ui-2);content:\"\";position:absolute;block-size:2px;inline-size:calc(100% - 1.5rem - 8px);inset-block-start:calc(.75rem - 1px);inset-inline-start:calc(50% + .75rem + 4px)}.not-directly-following:last-child:after,.not-first-step:first-child:before{border-block-start:2px var(--element-ui-2) dashed;content:\"\";position:absolute;block-size:2px;inline-size:1.875rem;inset-block-start:calc(.75rem - 1px);inset-inline-start:calc(50% + .75rem + 4px)}.not-first-step:first-child:before{inset-inline-start:calc(50% - 2.625rem - 4px);border-color:var(--element-ui-0)}.wizard-active{color:var(--element-ui-0)}.step{position:relative;text-align:center;margin-block-start:8px;padding-block:0;padding-inline:16px}.step i{font-size:1.5rem}.step a{color:var(--element-ui-2)}.step a .title{color:var(--element-text-secondary)}.step a .title:hover{text-decoration:none}.step a .title{inline-size:9.75rem;margin-block-start:12px}.step .active,.step .active .title{color:var(--element-ui-0)}.step .disabled,.step .disabled:hover{opacity:.5;text-decoration:none}.step a:not(.disabled):hover,.step a:not(.disabled):hover i,.step a:not(.disabled):hover .title{color:var(--element-ui-0-hover)!important;text-decoration:none}.step .completed .title{color:var(--element-text-primary)}.step.line-completed:after{background-color:var(--element-ui-0)}.step.line-active:after{background-color:var(--element-ui-0)}.step.line-disabled:after{opacity:.5}.step.small,.step.small a .title{inline-size:100%}.title{font-size:.875rem;line-height:20px}.container-wizard{inline-size:100%;display:flex;flex-wrap:nowrap;justify-content:center;padding-block:8px 12px;padding-inline:0}.container-wizard.display-one-step{justify-content:space-between}.wizard-btn-container{max-inline-size:50px;min-inline-size:40px;text-align:center;cursor:pointer}.wizard-btn-container.wizard-text-deactivate:hover{cursor:not-allowed;color:var(--element-text-secondary)}.wizard-btn-container .disabled{opacity:.5}.wizard-btn-container .invisible{visibility:hidden}.wizard-btn-container:hover .text-primary{color:var(--element-ui-0-hover)!important}\n"] }]
        }], propDecorators: { wizardSteps: [{
                type: ContentChildren,
                args: [SiWizardStepComponent]
            }], wizardContainerElement: [{
                type: ViewChild,
                args: ['container', { static: false }]
            }], wizardCancelButtonContainerElement: [{
                type: ViewChild,
                args: ['cancelButtonContainer', { static: false }]
            }], wizardButtonContainerElement: [{
                type: ViewChild,
                args: ['buttonContainer', { static: false }]
            }], backText: [{
                type: Input
            }], nextText: [{
                type: Input
            }], saveText: [{
                type: Input
            }], completionText: [{
                type: Input
            }], cancelText: [{
                type: Input
            }], hasCancel: [{
                type: Input
            }], enableCompletionPage: [{
                type: Input
            }], completionPageVisibleTime: [{
                type: Input
            }], stepIcon: [{
                type: Input
            }], stepCompletedIcon: [{
                type: Input
            }], stepFailedIcon: [{
                type: Input
            }], completionAction: [{
                type: Output
            }], cancel: [{
                type: Output
            }] } });

class SiWizardModule {
}
SiWizardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiWizardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiWizardModule, declarations: [SiWizardComponent,
        SiWizardStepComponent], imports: [CommonModule,
        SiResizeObserverModule,
        SiTranslateModule], exports: [SiWizardComponent,
        SiWizardStepComponent] });
SiWizardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardModule, imports: [CommonModule,
        SiResizeObserverModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiWizardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiResizeObserverModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiWizardComponent,
                        SiWizardStepComponent
                    ],
                    exports: [
                        SiWizardComponent,
                        SiWizardStepComponent
                    ]
                }]
        }] });

// ------------------------------------------------------------------------------

class SiCardComponent {
    constructor() {
        this.classCard = true;
        /**
         * Input list of primary action items. Supports up to **4** actions and omits additional ones.
         */
        this.primaryActions = [];
        /**
         * Input list of secondary action items.
         */
        this.secondaryActions = [];
        /**
         * The view type of the content action bar of the card. Default is `collapsible`
         * for dashboards. However, in some cases you might need to change to `expanded`
         * or `mobile`.
         */
        this.actionBarViewType = 'collapsible';
        /**
         * Optional setting of html title attribute for the content action bar.
         * Helpful for a11y when only one action is configured in expand mode.
         */
        this.actionBarTitle = '';
    }
    get classElevation() { return true; }
    /**
     * Returns `true` when primary or secondary actions are set.
     */
    get displayContentActionBar() {
        return this.primaryActions?.length > 0 || this.secondaryActions?.length > 0;
    }
}
SiCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiCardComponent, selector: "si-card", inputs: { heading: "heading", primaryActions: "primaryActions", secondaryActions: "secondaryActions", actionBarViewType: "actionBarViewType", actionBarTitle: "actionBarTitle" }, host: { properties: { "class.card": "this.classCard", "class.elevation-1": "this.classElevation" } }, ngImport: i0, template: "<div class=\"card-header d-flex justify-content-between\">\n  <ng-content #cardHeaderIcon select=\"[headerIcon]\"></ng-content>\n  <div class=\"text-truncate\">{{ heading | translate }}</div>\n  <div class=\"cab d-flex ms-6 my-n4 me-n5\">\n    <si-content-action-bar\n      *ngIf=\"displayContentActionBar\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"secondaryActions\"\n      [viewType]=\"actionBarViewType\"\n      class=\"ms-auto\"\n      [attr.title]=\"actionBarTitle | translate\">\n    </si-content-action-bar>\n  </div>\n</div>\n<ng-content #cardBody select=\"[body]\"></ng-content>\n", styles: [".card-header{block-size:40px}.text-truncate{flex:0 1 auto}.cab{flex:1 0 0;min-inline-size:40px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiContentActionBarComponent, selector: "si-content-action-bar", inputs: ["primaryActions", "secondaryActions", "actionParam", "viewType", "focusOnLoad", "toggleItemLabel", "preventIconsInDropdownMenus"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
__decorate([
    Required()
], SiCardComponent.prototype, "heading", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-card', template: "<div class=\"card-header d-flex justify-content-between\">\n  <ng-content #cardHeaderIcon select=\"[headerIcon]\"></ng-content>\n  <div class=\"text-truncate\">{{ heading | translate }}</div>\n  <div class=\"cab d-flex ms-6 my-n4 me-n5\">\n    <si-content-action-bar\n      *ngIf=\"displayContentActionBar\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"secondaryActions\"\n      [viewType]=\"actionBarViewType\"\n      class=\"ms-auto\"\n      [attr.title]=\"actionBarTitle | translate\">\n    </si-content-action-bar>\n  </div>\n</div>\n<ng-content #cardBody select=\"[body]\"></ng-content>\n", styles: [".card-header{block-size:40px}.text-truncate{flex:0 1 auto}.cab{flex:1 0 0;min-inline-size:40px}\n"] }]
        }], propDecorators: { classCard: [{
                type: HostBinding,
                args: ['class.card']
            }], classElevation: [{
                type: HostBinding,
                args: ['class.elevation-1']
            }], heading: [{
                type: Input
            }], primaryActions: [{
                type: Input
            }], secondaryActions: [{
                type: Input
            }], actionBarViewType: [{
                type: Input
            }], actionBarTitle: [{
                type: Input
            }] } });

class SiCardModule {
}
SiCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiCardModule, declarations: [SiCardComponent], imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiTranslateModule], exports: [SiCardComponent] });
SiCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCardModule, imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiCardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        PortalModule,
                        SiContentActionBarModule,
                        SiTranslateModule
                    ],
                    declarations: [SiCardComponent],
                    exports: [SiCardComponent]
                }]
        }] });

/**
 * The component shall be use to indicate that an authenticated
 * user does not have the required permissions.
 */
class SiUnauthorizedPageComponent {
    constructor() {
        /** The element no access icon. */
        this.icon = 'element-no-access';
    }
}
SiUnauthorizedPageComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiUnauthorizedPageComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiUnauthorizedPageComponent, selector: "si-unauthorized-page", inputs: { icon: "icon", heading: "heading", subHeading: "subHeading", description: "description", link: "link" }, ngImport: i0, template: "<div class=\"h-100 d-flex flex-column justify-content-center align-items-center p-6\">\n  <span class=\"state-icon\" [ngClass]=\"icon\"></span>\n  <h1 *ngIf=\"heading\" class=\"my-8\">{{ heading | translate }}</h1>\n  <h2 *ngIf=\"subHeading\" class=\"mb-6\">{{ subHeading | translate }}</h2>\n  <p\n    *ngIf=\"description\"\n    class=\"text-pre-wrap text-center si-body-1 mb-8\"\n  >{{ description | translate }}</p>\n  <a\n    *ngIf=\"link\"\n    [siLink]=\"link\"\n    class=\"btn btn-primary\"\n  >{{ link.title ? link.title : 'Go back' | translate }}</a>\n  <ng-content></ng-content>\n</div>\n", styles: [".state-icon{color:var(--element-status-danger);font-size:96px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-unauthorized-page', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"h-100 d-flex flex-column justify-content-center align-items-center p-6\">\n  <span class=\"state-icon\" [ngClass]=\"icon\"></span>\n  <h1 *ngIf=\"heading\" class=\"my-8\">{{ heading | translate }}</h1>\n  <h2 *ngIf=\"subHeading\" class=\"mb-6\">{{ subHeading | translate }}</h2>\n  <p\n    *ngIf=\"description\"\n    class=\"text-pre-wrap text-center si-body-1 mb-8\"\n  >{{ description | translate }}</p>\n  <a\n    *ngIf=\"link\"\n    [siLink]=\"link\"\n    class=\"btn btn-primary\"\n  >{{ link.title ? link.title : 'Go back' | translate }}</a>\n  <ng-content></ng-content>\n</div>\n", styles: [".state-icon{color:var(--element-status-danger);font-size:96px}\n"] }]
        }], propDecorators: { icon: [{
                type: Input
            }], heading: [{
                type: Input
            }], subHeading: [{
                type: Input
            }], description: [{
                type: Input
            }], link: [{
                type: Input
            }] } });

class SiUnauthorizedPageModule {
}
SiUnauthorizedPageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiUnauthorizedPageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageModule, declarations: [SiUnauthorizedPageComponent], imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiTranslateModule], exports: [SiUnauthorizedPageComponent] });
SiUnauthorizedPageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageModule, imports: [CommonModule,
        RouterModule,
        SiLinkModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiUnauthorizedPageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiUnauthorizedPageComponent],
                    imports: [
                        CommonModule,
                        RouterModule,
                        SiLinkModule,
                        SiTranslateModule
                    ],
                    exports: [SiUnauthorizedPageComponent]
                }]
        }] });

class SiDatatableInteractionDirective {
    constructor() {
        /**
         * The selection type of the datatable, will automatically be set if set for datatable.
         */
        this.selectionType = '';
        /**
         * Automatically select every row or cell that is navigated trough.
         * Is ignored unless `selectionType` is `single` or `cell`.
         */
        this.datatableInteractionAutoSelect = false;
        this.tabIndex = '0';
        this.element = inject(ElementRef).nativeElement;
        this.isMousedown = false;
    }
    onKeydown(event) {
        if (event.key === 'ArrowDown') {
            const first = this.selectionType === 'cell'
                ? this.element.querySelector('.datatable-row-wrapper > .datatable-body-row .datatable-body-cell')
                : this.element.querySelector('.datatable-row-wrapper > .datatable-body-row');
            if (first) {
                first.focus();
            }
        }
        else if (event.key === 'ArrowUp') {
            const last = this.selectionType === 'cell'
                ? this.element.querySelector('.datatable-row-wrapper:last-child > .datatable-body-row .datatable-body-cell')
                : this.element.querySelector('.datatable-row-wrapper:last-child > .datatable-body-row');
            if (last) {
                last.focus();
            }
        }
    }
    onMousedown(event) {
        this.isMousedown = true;
    }
    onMouseup(event) {
        this.isMousedown = false;
    }
    onFocusin(event) {
        const target = event.target;
        if (!target) {
            return;
        }
        clearTimeout(this.autoSelectTimeout);
        if (!this.isMousedown && this.datatableInteractionAutoSelect && (this.selectionType === 'single' || this.selectionType === 'cell')) {
            this.autoSelectTimeout = setTimeout(() => {
                const enterEvent = new KeyboardEvent('keydown', { key: 'enter' });
                Object.defineProperty(enterEvent, 'keyCode', { get: () => 13 });
                target.dispatchEvent(enterEvent);
            }, 100);
        }
        if (this.element.classList.contains('virtualized')) {
            if (this.tableBody) {
                const lastList = this.selectionType === 'cell'
                    ? this.tableBody.querySelectorAll('.datatable-row-wrapper:last-child > .datatable-body-row .datatable-body-cell')
                    : this.tableBody.querySelectorAll('.datatable-row-wrapper:last-child > .datatable-body-row');
                if (Array.from(lastList).includes(target)) {
                    this.tableBody.scrollTop = this.tableBody.scrollTop + lastList[0].clientHeight;
                }
                else {
                    const firstList = this.selectionType === 'cell'
                        ? this.tableBody.querySelectorAll('.datatable-row-wrapper:first-child > .datatable-body-row .datatable-body-cell')
                        : this.tableBody.querySelectorAll('.datatable-row-wrapper:first-child > .datatable-body-row');
                    if (Array.from(firstList).includes(target)) {
                        this.tableBody.scrollTop = this.tableBody.scrollTop - firstList[0].clientHeight;
                    }
                }
            }
        }
    }
    ngOnInit() {
        this.tableBody = this.element.querySelector('datatable-body');
        if (this.tableBody) {
            this.tableBody.tabIndex = -1;
        }
    }
    ngOnDestroy() {
        clearTimeout(this.autoSelectTimeout);
    }
}
SiDatatableInteractionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableInteractionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiDatatableInteractionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDatatableInteractionDirective, selector: "[siDatatableInteraction]", inputs: { selectionType: "selectionType", datatableInteractionAutoSelect: "datatableInteractionAutoSelect" }, host: { listeners: { "keydown": "onKeydown($event)", "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focusin": "onFocusin($event)" }, properties: { "attr.tabindex": "this.tabIndex" } }, exportAs: ["si-datatable-interaction"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableInteractionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDatatableInteraction]',
                    exportAs: 'si-datatable-interaction'
                }]
        }], propDecorators: { selectionType: [{
                type: Input
            }], datatableInteractionAutoSelect: [{
                type: Input
            }], tabIndex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocusin: [{
                type: HostListener,
                args: ['focusin', ['$event']]
            }] } });

class SiDatatableModule {
}
SiDatatableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiDatatableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableModule, declarations: [SiDatatableInteractionDirective], imports: [CommonModule], exports: [SiDatatableInteractionDirective] });
SiDatatableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDatatableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SiDatatableInteractionDirective
                    ],
                    exports: [SiDatatableInteractionDirective]
                }]
        }] });

const SI_DATATABLE_CONFIG = {
    cssClasses: {
        sortAscending: 'icon element-sort-up text-primary',
        sortDescending: 'icon element-sort-down text-primary',
        pagerLeftArrow: 'icon element-left-2 flip-rtl',
        pagerRightArrow: 'icon element-right-2 flip-rtl',
        pagerPrevious: 'icon element-double-left flip-rtl',
        pagerNext: 'icon element-double-right flip-rtl'
    },
    headerHeight: 40,
    footerHeight: 40,
    rowHeight: 64,
    rowHeightSmall: 48,
    rowHeightExtraSmall: 32,
    rowHeightTiny: 24
};

class SiLocaleStore {
}
const SI_LOCALE_LOCAL_STORAGE_KEY = 'lang';
class SiDefaultLocaleStore extends SiLocaleStore {
    constructor(isBrowser) {
        super();
        this.isBrowser = isBrowser;
    }
    get locale() {
        if (this.isBrowser) {
            return localStorage.getItem(SI_LOCALE_LOCAL_STORAGE_KEY) ?? undefined;
        }
        else {
            return undefined;
        }
    }
    saveLocale(locale) {
        if (this.isBrowser) {
            localStorage.setItem(SI_LOCALE_LOCAL_STORAGE_KEY, locale);
            return of(true);
        }
        else {
            return of(false);
        }
    }
}

const SI_LOCALE_STORE = new InjectionToken('SI_LOCALE_STORE');
const SI_LOCALE_CONFIG = new InjectionToken('SI_LOCALE_CONFIG');
// this is a function because Angular compiler exports arrows for no good reason
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function defaultLocaleInitializer(localeId) {
    if (localeId === 'en') {
        return Promise.resolve();
    }
    else {
        return Promise.reject();
    }
}
class SiLocaleService {
    constructor() {
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.translate = inject(SiTranslateService);
        this.localeStore = inject(SiLocaleStore, { optional: true }) ?? new SiDefaultLocaleStore(this.isBrowser);
        this.config = inject(SI_LOCALE_CONFIG, { optional: true }) ?? {
            availableLocales: ['en'],
            defaultLocale: 'en',
            localeInitializer: defaultLocaleInitializer,
            fallbackEnabled: false,
            dynamicLanguageChange: false
        };
        if (!this.config.defaultLocale) {
            if (this.config.availableLocales && this.config.availableLocales.length > 0) {
                this.config.defaultLocale = this.config.availableLocales[0];
            }
            else {
                this.config.defaultLocale = 'en';
            }
        }
        if (!this.config.availableLocales || this.config.availableLocales.length === 0) {
            this.config.availableLocales = [this.config.defaultLocale];
        }
        // Also adds all locales to the translate service to enable
        // components working with the translate service directly to still work.
        this.translate.availableLanguages = this.config.availableLocales;
        if (!this.config.localeInitializer) {
            this.config.localeInitializer = defaultLocaleInitializer;
        }
        if (!this.config.fallbackEnabled) {
            this.config.fallbackEnabled = false;
        }
        if (!this.config.dynamicLanguageChange) {
            this.config.dynamicLanguageChange = false;
        }
        const savedLocale = this.localeStore.locale;
        // The following check is important. We do not control the store and when it comes from
        // a remove backend, someone might give us a locale that we do not understand. In this
        // case we switch to the default.
        let initialLocale;
        const browserCultureLang = getBrowserCultureLanguage();
        const browserLang = getBrowserLanguage();
        if (this.hasLocale(savedLocale)) {
            initialLocale = savedLocale;
        }
        else if (this.translate.currentLanguage) {
            initialLocale = this.translate.currentLanguage;
        }
        else if (this.hasLocale(browserCultureLang)) {
            initialLocale = browserCultureLang;
        }
        else if (this.hasLocale(browserLang)) {
            initialLocale = browserLang;
        }
        else {
            initialLocale = this.config.defaultLocale;
        }
        this.locale$ = new BehaviorSubject(initialLocale);
        this.doSetLocale(initialLocale);
        // If a user changes the language on the translate service directly,
        // we synchronize the change again.
        this.translate.translationChange.subscribe(() => {
            this.locale = this.translate.currentLanguage;
        });
        if (this.config.fallbackEnabled) {
            this.translate.setDefaultLanguage(this.config.defaultLocale);
        }
    }
    /**
     * Sets a new locale to the locale service and also to the translate
     * service.
     * @throws An error if the new value is not configured in the available locales
     * or if the new locale cannot be saved, an error is thrown.
     */
    set locale(value) {
        if (value === this.locale$.value || value === this._nextLocale) {
            return;
        }
        if (!this.hasLocale(value)) {
            throw new Error(`The value ${value} does not exist in the available locales.`);
        }
        this.localeStore.saveLocale(value).pipe(first()).subscribe((saveSucceed) => {
            if (saveSucceed) {
                if (this.config.dynamicLanguageChange) {
                    this.doSetLocale(value);
                }
                else if (this.isBrowser) {
                    window.location.reload();
                }
            }
            else {
                throw new Error(`Could not save new locale ${value}.`);
            }
        }, () => {
            throw new Error(`Could not save new locale ${value}.`);
        });
    }
    get locale() {
        return this.locale$.value;
    }
    doSetLocale(value) {
        this._nextLocale = value;
        this.config.localeInitializer(value).then(() => {
            this.translate.setCurrentLanguage(value).pipe(first()).subscribe(() => {
                if (this.locale$.value !== value) {
                    this.locale$.next(value);
                }
            });
        }, () => {
            console.error(`Could not initialize new locale ${value}. Setting default locale ${this.config.defaultLocale}`);
            // Initialization of locale rejected. Setting default locale.
            this.translate.setCurrentLanguage(this.config.defaultLocale).pipe(first()).subscribe(() => {
                if (this.locale$.value !== this.config.defaultLocale) {
                    this.locale$.next(this.config.defaultLocale);
                }
            });
        });
    }
    /**
     * Test if the given locale is part of the available locales.
     * @param locale The locale to be tested.
     */
    hasLocale(locale) {
        if (locale) {
            return this.config.availableLocales.includes(locale);
        }
        return false;
    }
}
SiLocaleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiLocaleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SiLocaleId extends String {
    // the constructor is needed for unknown reasons. w/o the inject() fails
    constructor() {
        super();
        this.service = inject(SiLocaleService);
    }
    toString() {
        return (this.service.locale === 'template') ? 'en' : this.service.locale;
    }
}
SiLocaleId.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleId, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiLocaleId.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleId });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocaleId, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class SiLocalizationModule {
}
SiLocalizationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocalizationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLocalizationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiLocalizationModule, imports: [CommonModule,
        SiTranslateModule] });
SiLocalizationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocalizationModule, imports: [CommonModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiLocalizationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        CommonModule,
                        SiTranslateModule
                    ],
                    exports: []
                }]
        }] });

// -------------------------------------------------------------------------------

class SiTabComponent {
    constructor() {
        /** Hides the tab item completely if set. */
        this.hidden = false;
        this.active = false;
    }
    get isHidden() { return this.hidden || !this.active ? true : null; }
    registerParent(parent) {
        this.parent = parent;
    }
    ngOnChanges() {
        this.parent?.notifyChildrenChanged();
    }
}
SiTabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTabComponent, selector: "si-tab", inputs: { heading: "heading", icon: "icon", iconAltText: "iconAltText", badgeContent: "badgeContent", badgeColor: "badgeColor", hidden: "hidden" }, host: { properties: { "attr.hidden": "this.isHidden" } }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'si-tab',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { heading: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconAltText: [{
                type: Input
            }], badgeContent: [{
                type: Input
            }], badgeColor: [{
                type: Input
            }], hidden: [{
                type: Input
            }], isHidden: [{
                type: HostBinding,
                args: ['attr.hidden']
            }] } });

const SCROLL_INCREMENT = 55;
class SiTabsetComponent {
    constructor() {
        this.initialized = false;
        /**
         * Component variable to indicate if scrolling is necessary or the container is big enough to display all tabs.
         */
        this.scrollable = false;
        this.xPos = 0;
        this.endArrowDisabled = false;
        /**
         * If selectDefaultTab is passed as 'false', this implies no default tab selection
         * i.e. on initial load of tabset component no tab gets selected.
         */
        this.selectDefaultTab = true;
        /**
         * Event emitter to notify about selected tab index changes. You can either
         * use bi-directional binding with [(selectedTabIndex)] or separate both with
         * [selectedTabIndex]=... and (selectedTabIndexChange)=...
         */
        this.selectedTabIndexChange = new EventEmitter();
        /**
         * Event emitter to notify when a tab became inactive.
         */
        this.deselect = new EventEmitter();
        this.initTabIndex = 0;
        this.changeDetectorRef = inject(ChangeDetectorRef);
        this.trackByIndex = (index) => index;
    }
    /**
     * Sets a selected tab index. This will activate the tab of the provided
     * index, activates the tab and fires a notification about the change.
     * If index is passed as -1 i.e. `selectedTabIndex = -1`, this implies to clear all tab selection.
     * @defaultref {@link _selectedTabIndex}
     */
    set selectedTabIndex(tabIndex) {
        if (this.initialized && this.tabs.get(tabIndex)) {
            this.selectTab(this.tabs.get(tabIndex));
        }
        else {
            this.initTabIndex = tabIndex;
        }
    }
    /**
     * Returns the currently selected tab index.
     */
    get selectedTabIndex() {
        return this.tabs.toArray().findIndex(tab => tab.active);
    }
    ngAfterViewInit() {
        this.initialized = true;
        this.subscription = this.tabs.changes.subscribe(() => {
            this.tabs.forEach(tab => tab.registerParent(this));
            this.changeDetectorRef.markForCheck();
        });
        this.tabs.notifyOnChanges();
        if (this.selectDefaultTab) {
            if (this.tabs.length) {
                setTimeout(() => this.selectTab(this.tabs.get(this.initTabIndex)));
            }
            else {
                // no tabs are there yet. But maybe there will be some soon, so let's wait for it
                this.subscription.add(this.tabs.changes
                    .pipe(first(() => !!this.tabs.length), observeOn(asyncScheduler))
                    .subscribe(() => {
                    this.selectTab(this.tabs.get(this.initTabIndex));
                    this.changeDetectorRef.markForCheck();
                }));
            }
        }
        setTimeout(() => {
            this.resize();
        });
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }
    notifyChildrenChanged() {
        this.changeDetectorRef.markForCheck();
    }
    resize() {
        this.scrollable = Math.round(this.tabContainer.nativeElement.offsetWidth) < this.innerTabContainer.nativeElement.scrollWidth;
        this.scroll(0);
    }
    /**
     * Finds the index of the provided tab and sets the index as new selected tab index
     *
     * @param selectedTab The tab to be selected. This must already be part of the container.
     */
    selectTab(selectedTab) {
        const tabs = this.tabs.toArray();
        const newTabIndex = tabs.indexOf(selectedTab);
        const currentTabIndex = tabs.findIndex(tab => tab.active);
        let continueWithSelection = newTabIndex !== currentTabIndex;
        if (continueWithSelection && currentTabIndex !== -1) {
            const currentTab = tabs[currentTabIndex];
            const deselectEvent = {
                target: currentTab,
                tabIndex: currentTabIndex,
                cancel: () => {
                    continueWithSelection = false;
                    currentTab.active = true;
                }
            };
            currentTab.active = false;
            this.deselect.emit(deselectEvent);
        }
        if (continueWithSelection) {
            selectedTab.active = true;
            this.selectedTabIndexChange.emit(newTabIndex);
        }
    }
    /**
     * Scrolls the tab headers to the end (right in LTR).
     */
    scrollEnd() {
        this.scroll(SCROLL_INCREMENT);
    }
    /**
     * Scrolls the tab headers to the start (left in LTR).
     */
    scrollStart() {
        this.scroll(-SCROLL_INCREMENT);
    }
    scroll(inc) {
        this.xPos += inc;
        this.xPos = Math.max(0, Math.min(this.innerTabContainer.nativeElement.scrollWidth -
            this.innerTabContainer.nativeElement.offsetWidth, this.xPos));
        this.endArrowDisabled =
            this.xPos + this.tabContainer.nativeElement.offsetWidth >=
                this.innerTabContainer.nativeElement.scrollWidth;
        this.innerTabContainer.nativeElement.style.transform = `translateX(${this.xPos * (isRTL() ? 1 : -1)}px)`;
    }
    mouseScroll(event) {
        if (event.deltaY < 0) {
            this.scroll(-SCROLL_INCREMENT);
        }
        else {
            this.scroll(SCROLL_INCREMENT);
        }
        if (this.xPos || !this.endArrowDisabled) {
            event.preventDefault();
        }
    }
}
SiTabsetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabsetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiTabsetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiTabsetComponent, selector: "si-tabset", inputs: { selectDefaultTab: "selectDefaultTab", selectedTabIndex: "selectedTabIndex" }, outputs: { selectedTabIndexChange: "selectedTabIndexChange", deselect: "deselect" }, queries: [{ propertyName: "tabs", predicate: SiTabComponent }], viewQueries: [{ propertyName: "tabContainer", first: true, predicate: ["tabContainer"], descendants: true, static: true }, { propertyName: "innerTabContainer", first: true, predicate: ["innerTabContainer"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"nav nav-tabs tab-container-header focus-inside\"\n  [class.is-scrollable]=\"scrollable\"\n  siListInteraction\n  listInteractionPosition=\"adjacent\"\n  (siResizeObserver)=\"resize()\"\n>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control focus-inside\"\n    [hidden]=\"!xPos\"\n    tabindex=\"-1\"\n    (keydown.enter)=\"scrollStart()\"\n    (mousedown)=\"scrollStart()\"\n  >\n    <i class=\"icon element-left-3 flip-rtl\"></i>\n  </button>\n\n  <div class=\"tab-container-buttonbar\" #tabContainer (wheel)=\"mouseScroll($event)\">\n    <ul\n      class=\"tab-container-buttonbar-list focus-inside\"\n      #innerTabContainer\n      role=\"tablist\"\n    >\n      <li\n        *ngFor=\"let tab of tabs; trackBy: trackByIndex\"\n        [attr.hidden]=\"tab.hidden || null\"\n        [class.active]=\"tab.active\"\n        class=\"nav-item\"\n        role=\"presentation\"\n      >\n        <div\n          siListInteractionItem\n          role=\"tab\"\n          tabindex=\"0\"\n          class=\"nav-link focus-inside\"\n          [class.active]=\"tab.active\"\n          [attr.aria-selected]=\"tab.active\"\n          (keydown.enter)=\"selectTab(tab)\"\n          (click)=\"selectTab(tab)\"\n        >\n          <span class=\"text-truncate\">{{ tab.heading! | translate }}</span>\n          <si-icon *ngIf=\"tab.icon\" [icon]=\"tab.icon\" [alt]=\"tab.iconAltText\"></si-icon>\n          <span *ngIf=\"tab.badgeContent\" class=\"badge\" [ngClass]=\"'bg-' + tab.badgeColor\">\n            {{ tab.badgeContent | translate }}\n          </span>\n        </div>\n      </li>\n    </ul>\n  </div>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control is-end focus-inside\"\n    [hidden]=\"endArrowDisabled\"\n    tabindex=\"-1\"\n    (keydown.enter)=\"scrollEnd()\"\n    (mousedown)=\"scrollEnd()\"\n  >\n    <i class=\"icon element-right-3 flip-rtl\"></i>\n  </button>\n</div>\n\n<ng-content></ng-content>\n", styles: [":host{display:block;flex-direction:column}*:focus{outline:0}.tab-container-header{overflow:hidden;display:flex}.tab-container-buttonbar{flex:1;overflow:hidden}.tab-container-buttonbar-list{display:flex;margin:0;padding:0;list-style-type:none;transition:calc(.25s * var(--element-animations-enabled, 1))}.tab-container-buttonbar-list-item{flex:none}.tab-container-control{inline-size:40px;block-size:38px;background:transparent;border:0;-webkit-user-select:none;user-select:none}.tab-container-control i{font-size:30px}.tab-container-control:hover{color:var(--element-text-active)}.tab-container-control:disabled{color:var(--element-text-disabled);cursor:default}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "directive", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: ["listInteractionSubListLeft", "listInteractionSubListStart", "listInteractionFocusOnLoad", "listInteractionFocus", "listInteractionFocusLastFromParent", "listInteractionAutofocusFromParent", "listInteractionIsSubList", "listInteractionParentItem", "listInteractionPosition", "listInteractionOrientation", "listInteractionRefocusAfterEnter", "listInteractionShowSubListOnParentFocus", "listInteractionCloseOnClickOut"], exportAs: ["si-list-interaction"] }, { kind: "directive", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: ["listInteractionItemDisabled", "listInteractionItemDropdownToggle"], exportAs: ["si-list-interaction-item"] }, { kind: "directive", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: ["resizeThrottle"], outputs: ["siResizeObserver"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabsetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-tabset', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"nav nav-tabs tab-container-header focus-inside\"\n  [class.is-scrollable]=\"scrollable\"\n  siListInteraction\n  listInteractionPosition=\"adjacent\"\n  (siResizeObserver)=\"resize()\"\n>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control focus-inside\"\n    [hidden]=\"!xPos\"\n    tabindex=\"-1\"\n    (keydown.enter)=\"scrollStart()\"\n    (mousedown)=\"scrollStart()\"\n  >\n    <i class=\"icon element-left-3 flip-rtl\"></i>\n  </button>\n\n  <div class=\"tab-container-buttonbar\" #tabContainer (wheel)=\"mouseScroll($event)\">\n    <ul\n      class=\"tab-container-buttonbar-list focus-inside\"\n      #innerTabContainer\n      role=\"tablist\"\n    >\n      <li\n        *ngFor=\"let tab of tabs; trackBy: trackByIndex\"\n        [attr.hidden]=\"tab.hidden || null\"\n        [class.active]=\"tab.active\"\n        class=\"nav-item\"\n        role=\"presentation\"\n      >\n        <div\n          siListInteractionItem\n          role=\"tab\"\n          tabindex=\"0\"\n          class=\"nav-link focus-inside\"\n          [class.active]=\"tab.active\"\n          [attr.aria-selected]=\"tab.active\"\n          (keydown.enter)=\"selectTab(tab)\"\n          (click)=\"selectTab(tab)\"\n        >\n          <span class=\"text-truncate\">{{ tab.heading! | translate }}</span>\n          <si-icon *ngIf=\"tab.icon\" [icon]=\"tab.icon\" [alt]=\"tab.iconAltText\"></si-icon>\n          <span *ngIf=\"tab.badgeContent\" class=\"badge\" [ngClass]=\"'bg-' + tab.badgeColor\">\n            {{ tab.badgeContent | translate }}\n          </span>\n        </div>\n      </li>\n    </ul>\n  </div>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control is-end focus-inside\"\n    [hidden]=\"endArrowDisabled\"\n    tabindex=\"-1\"\n    (keydown.enter)=\"scrollEnd()\"\n    (mousedown)=\"scrollEnd()\"\n  >\n    <i class=\"icon element-right-3 flip-rtl\"></i>\n  </button>\n</div>\n\n<ng-content></ng-content>\n", styles: [":host{display:block;flex-direction:column}*:focus{outline:0}.tab-container-header{overflow:hidden;display:flex}.tab-container-buttonbar{flex:1;overflow:hidden}.tab-container-buttonbar-list{display:flex;margin:0;padding:0;list-style-type:none;transition:calc(.25s * var(--element-animations-enabled, 1))}.tab-container-buttonbar-list-item{flex:none}.tab-container-control{inline-size:40px;block-size:38px;background:transparent;border:0;-webkit-user-select:none;user-select:none}.tab-container-control i{font-size:30px}.tab-container-control:hover{color:var(--element-text-active)}.tab-container-control:disabled{color:var(--element-text-disabled);cursor:default}\n"] }]
        }], propDecorators: { tabs: [{
                type: ContentChildren,
                args: [SiTabComponent]
            }], tabContainer: [{
                type: ViewChild,
                args: ['tabContainer', { static: true }]
            }], innerTabContainer: [{
                type: ViewChild,
                args: ['innerTabContainer', { static: true }]
            }], selectDefaultTab: [{
                type: Input
            }], selectedTabIndex: [{
                type: Input
            }], selectedTabIndexChange: [{
                type: Output
            }], deselect: [{
                type: Output
            }] } });

const components = [SiTabsetComponent, SiTabComponent];
class SiTabsModule {
}
SiTabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiTabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiTabsModule, declarations: [SiTabsetComponent, SiTabComponent], imports: [CommonModule,
        SiIconModule,
        SiListInteractionModule,
        SiResizeObserverModule,
        SiTranslateModule], exports: [SiTabsetComponent, SiTabComponent] });
SiTabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabsModule, imports: [CommonModule,
        SiIconModule,
        SiListInteractionModule,
        SiResizeObserverModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SiIconModule,
                        SiListInteractionModule,
                        SiResizeObserverModule,
                        SiTranslateModule
                    ],
                    declarations: components,
                    exports: components
                }]
        }] });

// ------------------------------------------------------------------------------

/**
 * SiThemeStore object is used by the theme service to load and
 * store the themes. You can inject your own implementation to provide
 * a backend implementation. Otherwise a localStorage based implementation
 * is used.
 */
class SiThemeStore {
}
const SI_THEME_LOCAL_STORAGE_KEY = 'si-themes';
class SiDefaultThemeStore extends SiThemeStore {
    constructor(isBrowser) {
        super();
        this.isBrowser = isBrowser;
    }
    loadActiveTheme() {
        if (!this.isBrowser) {
            return of(undefined);
        }
        const store = this.loadStore();
        if (store.activeTheme) {
            return of(store.themes[store.activeTheme]);
        }
        else {
            return of(undefined);
        }
    }
    activateTheme(name) {
        if (!this.isBrowser) {
            return of(false);
        }
        const store = this.loadStore();
        if (store.themes[name]) {
            store.activeTheme = name;
            this.saveStore(store);
            return of(true);
        }
        else {
            return of(false);
        }
    }
    deactivateTheme() {
        if (!this.isBrowser) {
            return of(false);
        }
        const store = this.loadStore();
        store.activeTheme = undefined;
        this.saveStore(store);
        return of(true);
    }
    loadThemeNames() {
        if (!this.isBrowser) {
            return of([]);
        }
        const store = this.loadStore();
        return of(Array.from(Object.keys(store.themes)));
    }
    saveTheme(theme) {
        if (!this.isBrowser) {
            return of(false);
        }
        const store = this.loadStore();
        store.themes[theme.name] = theme;
        this.saveStore(store);
        return of(true);
    }
    loadTheme(name) {
        if (!this.isBrowser) {
            return of(undefined);
        }
        const store = this.loadStore();
        return of(store.themes[name]);
    }
    deleteTheme(name) {
        if (!this.isBrowser) {
            return of(false);
        }
        const store = this.loadStore();
        delete store.themes[name];
        if (store.activeTheme === name) {
            store.activeTheme = undefined;
        }
        this.saveStore(store);
        return of(true);
    }
    loadStore() {
        const storeStr = localStorage.getItem(SI_THEME_LOCAL_STORAGE_KEY);
        if (storeStr) {
            return JSON.parse(storeStr);
        }
        else {
            return { activeTheme: undefined, themes: {} };
        }
    }
    saveStore(store) {
        localStorage.setItem(SI_THEME_LOCAL_STORAGE_KEY, JSON.stringify(store));
    }
}

const ELEMENT_THEME_NAME = 'element';
const elementTheme = {
    name: 'element',
    groups: [
        {
            name: 'Branding',
            description: 'Configure the brand logo.',
            properties: [
                { name: '--element-brand-logo-url', usage: 'Enter a custom URL to a brand logo.', type: 'url' },
                { name: '--element-brand-logo-text', usage: 'Enter a custom accessibility text for the brand logo.', type: 'text' }
            ]
        },
        {
            name: 'Basic definitions',
            description: 'Basic definitions',
            properties: [
                { name: '--element-body-font-family', usage: 'The theme only uses one font everywhere.', type: 'font' },
                { name: '--element-button-radius', usage: 'Button border radius', type: 'text' },
                { name: '--element-button-focus-width', usage: 'Focus width', type: 'text' },
                { name: '--element-button-focus-overlay-width', usage: 'Focus overlay width', type: 'text' },
                { name: '--element-button-focus-overlay-color', usage: 'Focus overlay color', type: 'text' },
                { name: '--element-input-radius', usage: 'Input field border radius', type: 'text' },
                { name: '--element-logo-color', usage: 'Logo color', type: 'text' },
                { name: '--element-radius-1', usage: 'Border radius-1', type: 'text' },
                { name: '--element-radius-2', usage: 'Border radius-2', type: 'text' },
                { name: '--element-radius-3', usage: 'Border radius-3', type: 'text' }
            ]
        },
        {
            name: 'UI',
            description: 'UI colors are used on structural properties and icons and provide good contrast when used over any background.',
            properties: [
                { name: '--element-ui-0', usage: 'Logo, selected (active) elements', type: 'color' },
                { name: '--element-ui-0-hover', usage: 'Selected hover', type: 'color' },
                { name: '--element-ui-1', usage: 'Primary icons', type: 'color' },
                { name: '--element-ui-2', usage: 'Secondary icons', type: 'color' },
                { name: '--element-ui-3', usage: 'Disabled', type: 'color' },
                { name: '--element-ui-4', usage: 'Borders', type: 'color' },
                { name: '--element-ui-5', usage: 'Inverse', type: 'color' },
                { name: '--element-ui-6', usage: 'Shadows', type: 'color' },
                { name: '--element-box-shadow-color-1', usage: 'Box shadow color 1', type: 'color' },
                { name: '--element-box-shadow-color-2', usage: 'Box shadow color 2', type: 'color' },
                { name: '--element-focus-default', usage: 'Default focus color', type: 'color' }
            ]
        },
        {
            name: 'Base',
            description: 'Base colors are used as backgrounds of containers.',
            properties: [
                { name: '--element-base-0', usage: 'Page background, row background', type: 'color' },
                { name: '--element-base-1', usage: 'Header, navigation, card, table background', type: 'color' },
                { name: '--element-base-1-hover', usage: 'Hover on base-1 backgrounds, like table, tree, or menu', type: 'color' },
                { name: '--element-base-1-selected', usage: 'Selected on base-1 backgrounds, like table, tree, or menu', type: 'color' },
                { name: '--element-base-2', usage: 'Page background with higher contrast pages in dark mode', type: 'color' },
                { name: '--element-base-information', usage: 'Informational component background for e.g. badges', type: 'gradient' },
                { name: '--element-base-success', usage: 'Success component background for e.g. badges', type: 'gradient' },
                { name: '--element-base-caution', usage: 'Caution component background for e.g. badges', type: 'gradient' },
                { name: '--element-base-warning', usage: 'Warning component background for e.g. badges', type: 'gradient' },
                { name: '--element-base-danger', usage: 'Danger component background for e.g. badges', type: 'gradient' },
                { name: '--element-base-translucent-1', usage: 'Translucent background, e.g. backdrop', type: 'gradient' },
                { name: '--element-base-translucent-2', usage: 'Slightly translucent background, e.g. toasts', type: 'gradient' }
            ]
        },
        {
            name: 'Actions',
            description: 'Action colors are used to indicate actions that users can perform.',
            properties: [
                { name: '--element-action-primary', usage: 'Primary interaction', type: 'gradient' },
                { name: '--element-action-primary-hover', usage: 'Primary action background on hover', type: 'gradient' },
                { name: '--element-action-primary-text', usage: 'Primary text color', type: 'color' },
                { name: '--element-action-secondary', usage: 'Secondary interaction', type: 'gradient' },
                { name: '--element-action-secondary-hover', usage: 'Secondary action background on hover', type: 'gradient' },
                { name: '--element-action-secondary-text', usage: 'Secondary text color', type: 'color' },
                { name: '--element-action-secondary-text-hover', usage: 'Secondary text color on hover', type: 'color' },
                { name: '--element-action-secondary-border', usage: 'Secondary border color', type: 'color' },
                { name: '--element-action-secondary-border-hover', usage: 'Secondary border color on hover', type: 'color' },
                { name: '--element-action-warning', usage: 'Warning', type: 'gradient' },
                { name: '--element-action-warning-hover', usage: 'Warning action background on hover', type: 'gradient' },
                { name: '--element-action-warning-text', usage: 'Warning text color', type: 'color' },
                { name: '--element-action-danger', usage: 'Danger', type: 'gradient' },
                { name: '--element-action-danger-hover', usage: 'Danger action background on hover', type: 'gradient' },
                { name: '--element-action-danger-text', usage: 'Danger text color', type: 'color' },
                { name: '--element-action-disabled-opacity', usage: 'Disabled action opacity', type: 'number' }
            ]
        },
        {
            name: 'Text',
            description: 'Similarly, categories for typography colors are also defined in this system.',
            properties: [
                { name: '--element-text-primary', usage: 'Primary', type: 'color' },
                { name: '--element-text-secondary', usage: 'Secondary', type: 'color' },
                { name: '--element-text-disabled', usage: 'Disabled', type: 'color' },
                { name: '--element-text-inverse', usage: 'Inverse', type: 'color' },
                { name: '--element-text-active', usage: 'Active', type: 'color' },
                { name: '--element-text-information', usage: 'Informational', type: 'color' },
                { name: '--element-text-success', usage: 'Success', type: 'color' },
                { name: '--element-text-caution', usage: 'Caution', type: 'color' },
                { name: '--element-text-warning', usage: 'Warning', type: 'color' },
                { name: '--element-text-danger', usage: 'Danger', type: 'color' }
            ]
        },
        {
            name: 'Status',
            description: 'Status colors are used to describe and/or report on the status of different things.',
            properties: [
                { name: '--element-status-information', usage: 'Informational', type: 'color' },
                { name: '--element-status-information-contrast', usage: 'Informational contrast', type: 'color' },
                { name: '--element-status-success', usage: 'Success', type: 'color' },
                { name: '--element-status-success-contrast', usage: 'Success contrast', type: 'color' },
                { name: '--element-status-caution', usage: 'Caution', type: 'color' },
                { name: '--element-status-caution-contrast', usage: 'Caution contrast', type: 'color' },
                { name: '--element-status-warning', usage: 'Warning', type: 'color' },
                { name: '--element-status-warning-contrast', usage: 'Warning contrast', type: 'color' },
                { name: '--element-status-danger', usage: 'Danger', type: 'color' },
                { name: '--element-status-danger-contrast', usage: 'Danger contrast', type: 'color' }
            ]
        }
    ]
};

class SiThemeService {
    get resolvedColorScheme() {
        return this._resolvedColorScheme;
    }
    get themeNames() {
        return this._themeNames;
    }
    constructor() {
        /**
         * The current color scheme. (e.g. light or dark).
         */
        this._resolvedColorScheme = 'light';
        this.resolvedColorSchemeSub = new ReplaySubject(1);
        this.resolvedColorScheme$ = this.resolvedColorSchemeSub.asObservable();
        /**
         * All available theme names, including element theme name.
         */
        this._themeNames = [ELEMENT_THEME_NAME];
        this.themeNamesSub = new ReplaySubject(1);
        this.themeNames$ = this.themeNamesSub.asObservable();
        /**
         * Emits events when the currently applied theme changes. Either by
         * changing to another theme or by re-applying a theme with updated
         * properties. When switching to default theme element, `undefined`
         * is emitted.
         */
        this.themeChange = new EventEmitter();
        this.themes = new Map();
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.themeStore = inject(SiThemeStore, { optional: true }) ?? new SiDefaultThemeStore(this.isBrowser);
        this.meta = inject(Meta);
        this.resolvedColorScheme$.subscribe(scheme => this._resolvedColorScheme = scheme);
        this.themeNames$.subscribe(names => this._themeNames = names);
        if (this.isBrowser) {
            this.darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            this.mediaQueryListener = () => this.toggleDark(this.darkMediaQuery.matches);
        }
        this.getActiveTheme().pipe(take(1)).subscribe(theme => this.applyThemeStyle(theme, this._resolvedColorScheme), error => console.error('Cannot load active theme', error));
        this.themeStore.loadThemeNames().pipe(take(1)).subscribe(names => {
            names.push(ELEMENT_THEME_NAME);
            this.themeNamesSub.next(names);
        }, error => console.error('Cannot load theme names', error));
    }
    hasTheme(name) {
        return this._themeNames.includes(name);
    }
    /**
     * Returns a clone of the theme with the given name or `undefined` if not
     * available or name is `element`.
     * @param name The name of the theme to be returned.
     * @returns The theme with the given name and `undefined` if name is `element`.
     */
    getTheme(name) {
        if (name === ELEMENT_THEME_NAME || !this.hasTheme(name)) {
            return of(undefined);
        }
        if (this.themes.has(name)) {
            const theme = this.themes.get(name);
            // return a clone to avoid changes as a side effect
            return of(this.cloneTheme(theme));
        }
        return this.themeStore.loadTheme(name).pipe(tap(theme => {
            if (theme) {
                this.themes.set(theme.name, this.cloneTheme(theme));
            }
        }));
    }
    /**
     * Loads and returns the currently active theme. Returns undefined if no custom theme is used.
     * @returns A custom theme or `undefined` if the default element theme is used.
     */
    getActiveTheme() {
        if (this.activeThemeName) {
            return of(this.themes.get(this.activeThemeName));
        }
        return this.themeStore.loadActiveTheme().pipe(map(theme => {
            if (theme) {
                this.activeThemeName = theme.name;
                this.themes.set(theme.name, theme);
                this.applyTheme(theme, this._resolvedColorScheme);
                return theme;
            }
            this.activeThemeName = ELEMENT_THEME_NAME;
            return undefined;
        }));
    }
    addOrUpdateTheme(theme) {
        const result = new ReplaySubject(1);
        this.themeStore.saveTheme(theme).subscribe(saveResult => {
            if (saveResult) {
                this.themes.set(theme.name, this.cloneTheme(theme));
                // Update list of theme names when this is a new one
                if (!this.hasTheme(theme.name)) {
                    const names = this._themeNames.concat(theme.name);
                    this.themeNamesSub.next(names);
                }
            }
            result.next(saveResult);
            result.complete();
        }, error => result.error(error), () => result.complete());
        return result;
    }
    deleteTheme(name) {
        const result = new ReplaySubject(1);
        if (!this.hasTheme(name) || name === ELEMENT_THEME_NAME) {
            result.next(false);
            result.complete();
        }
        else {
            this.removeThemeCSS(name);
            this.themeStore.deleteTheme(name).subscribe(deleteResult => {
                if (deleteResult) {
                    this.themes.delete(name);
                    const names = this._themeNames.filter(themeName => themeName !== name);
                    this.themeNamesSub.next(names);
                }
                result.next(deleteResult);
                result.complete();
            }, error => result.error(error), () => result.complete());
        }
        return result;
    }
    resetPreview() {
        this.previewTheme = { name: '__preview', schemes: { dark: {}, light: {} } };
        this.removeThemeCSS(this.previewTheme.name);
    }
    setActiveTheme(name, type) {
        const theType = type ?? this._resolvedColorScheme ?? 'auto';
        const theName = name ?? this.activeThemeName ?? ELEMENT_THEME_NAME;
        if (theName === this.activeThemeName) {
            return of(true);
        }
        if (!this.hasTheme(theName)) {
            return of(false);
        }
        const result = new ReplaySubject(1);
        this.activeThemeName = theName;
        // Make the change in the remove store
        const store = theName !== ELEMENT_THEME_NAME
            ? this.themeStore.activateTheme(theName)
            : this.themeStore.deactivateTheme();
        store.pipe(
        // On success, load the theme
        switchMap(storeResult => {
            if (storeResult) {
                return this.getTheme(theName);
            }
            else {
                return throwError('Cannot change active theme in theme store.');
            }
        }), 
        // Only take one to avoid unsubscription issues
        take(1)).subscribe(theme => {
            this.applyTheme(theme, theType);
            result.next(true);
        }, error => {
            console.error(`Activating theme ${name} failed`, error);
            result.next(false);
        }, () => result.complete());
        return result;
    }
    applyThemeType(type) {
        if (type === this._resolvedColorScheme || !this.darkMediaQuery || !this.mediaQueryListener) {
            return;
        }
        // need to use the deprecated version because of Safari
        this.darkMediaQuery.removeListener(this.mediaQueryListener);
        if (type === 'light') {
            this.toggleDark(false);
        }
        else if (type === 'dark') {
            this.toggleDark(true);
        }
        else {
            this.mediaQueryListener();
            this.darkMediaQuery.addListener(this.mediaQueryListener);
        }
    }
    applyTheme(theme, type, overwrite) {
        if (theme) {
            this.applyThemeStyle(theme, type, overwrite);
        }
        else {
            this.getActiveTheme().pipe(take(1)).subscribe(loadedTheme => this.applyThemeStyle(loadedTheme, type), error => console.error('Cannot load active theme', error));
        }
    }
    updateProperty(name, value, type) {
        if (!this.previewTheme) {
            this.resetPreview();
        }
        this.previewTheme.schemes[type][name] = value;
        this.createThemeCSS(this.previewTheme);
        document.documentElement.classList.add('theme-__preview');
        this.dispatchThemeSwitchEvent();
    }
    isThemeValid(data) {
        const theme = data;
        return data && typeof data === 'object' && 'name' in data && 'schemes' in data
            && typeof theme.name === 'string' && typeof theme.schemes === 'object'
            && this.isThemeTypeValid(theme, 'light')
            && this.isThemeTypeValid(theme, 'dark');
    }
    isThemeTypeValid(theme, type) {
        return !(type in theme.schemes)
            || (typeof theme.schemes[type] === 'object'
                && Object.values(theme.schemes[type]).every(item => typeof item === 'string')
                && Object.keys(theme.schemes[type]).every(item => typeof item === 'string'));
    }
    applyThemeStyle(theme, type, overwrite) {
        if (!this.isBrowser) {
            return;
        }
        this.resetPreview();
        if (theme && theme.name !== ELEMENT_THEME_NAME && (overwrite || !this.hasThemeCSS(theme))) {
            this.createThemeCSS(theme);
        }
        this.activateTheme(theme);
        if (type && type !== this._resolvedColorScheme) {
            this.applyThemeType(type);
        }
        this.themeChange.emit(theme);
    }
    activateTheme(theme) {
        const classList = document.documentElement.classList;
        classList.forEach(c => {
            if (c.startsWith('theme-')) {
                classList.remove(c);
            }
        });
        if (theme) {
            classList.add(`theme-${theme.name}`);
        }
    }
    hasThemeCSS(theme) {
        const id = `__theme-${theme.name}`;
        return !!document.getElementById(id);
    }
    createThemeCSS(theme) {
        let css = '';
        const themeSelector = `:root.theme-${theme.name}`;
        if (theme.schemes.light) {
            css = this.createThemeVariantCSS(css, themeSelector, theme.schemes.light);
        }
        if (theme.schemes.dark) {
            css = this.createThemeVariantCSS(css, themeSelector + '.app--dark', theme.schemes.dark);
        }
        this.removeThemeCSS(theme.name);
        const cssElement = document.createElement('style');
        cssElement.id = `__theme-${theme.name}`;
        cssElement.textContent = css;
        document.body.appendChild(cssElement);
    }
    createThemeVariantCSS(css, selector, scheme) {
        css += `${selector} {\n`;
        for (const key of Object.keys(scheme)) {
            css += `${key}: ${scheme[key]};\n`;
        }
        css += '}\n';
        return css;
    }
    removeThemeCSS(name) {
        const id = `__theme-${name}`;
        document.getElementById(id)?.remove();
        document.documentElement.classList.remove(`theme-${name}`);
    }
    toggleDark(dark) {
        if (!this.isBrowser) {
            return;
        }
        document.documentElement.classList.toggle('app--dark', dark);
        const colorScheme = dark ? 'dark' : 'light';
        this.meta.updateTag({ name: 'color-scheme', content: colorScheme });
        this.resolvedColorSchemeSub.next(colorScheme);
        this.dispatchThemeSwitchEvent();
    }
    cloneTheme(theme) {
        const clone = { ...theme, schemes: {} };
        if (theme.schemes.light) {
            clone.schemes.light = { ...theme.schemes.light };
        }
        if (theme.schemes.dark) {
            clone.schemes.dark = { ...theme.schemes.dark };
        }
        return clone;
    }
    dispatchThemeSwitchEvent() {
        window.dispatchEvent(new CustomEvent('theme-switch', { detail: {
                dark: (this._resolvedColorScheme === 'dark'),
                colorScheme: this._resolvedColorScheme
            }
        }));
    }
}
SiThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThemeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThemeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

/**
 * @deprecated use SiThemeService instead.
 */
class ThemeSwitcherService {
    constructor() {
        this.themeService = inject(SiThemeService);
        this.darkThemeToggle$ = this.themeService.resolvedColorScheme$.pipe(map(colorScheme => colorScheme === 'dark'));
    }
    setTheme(theme) {
        this.themeService.applyThemeType(theme);
    }
}
ThemeSwitcherService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ThemeSwitcherService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ThemeSwitcherService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ThemeSwitcherService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ThemeSwitcherService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SiSidePanelService {
    constructor() {
        this.contentSubject = new BehaviorSubject(undefined);
        this.content$ = this.contentSubject.asObservable();
        this.openSubject = new BehaviorSubject(false);
        this.isOpen$ = this.openSubject.asObservable();
        this.tempContentSubject = new BehaviorSubject(undefined);
        this.tempContent$ = this.tempContentSubject.asObservable();
    }
    setSidePanelContent(portal) {
        this.contentSubject.next(portal);
    }
    open() {
        this.hideTemporaryContent();
        this.openSubject.next(true);
    }
    close() {
        if (this.hideTemporaryContent()) {
            return;
        }
        this.openSubject.next(false);
    }
    toggle() {
        this.hideTemporaryContent();
        this.openSubject.next(!this.openSubject.value);
    }
    isOpen() {
        return this.openSubject.value;
    }
    isTemporaryOpen() {
        return !!this.tempContentSubject.value;
    }
    showTemporaryContent(portal) {
        this.hideTemporaryContent();
        this.tempContentSubject.next(portal);
        if (portal) {
            this.tempContentClosed = new Subject();
            return this.tempContentClosed.asObservable();
        }
        return EMPTY;
    }
    hideTemporaryContent() {
        if (!this.isTemporaryOpen()) {
            return false;
        }
        if (this.tempContentClosed) {
            const sub = this.tempContentClosed;
            this.tempContentClosed = undefined;
            sub.next();
            sub.complete();
        }
        this.tempContentSubject.next(undefined);
        return true;
    }
}
SiSidePanelService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SiSidePanelService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SiSidePanelComponent {
    get isCollapsible() { return this.collapsible && !this.showTempContent; }
    get isCollapsibleTemp() { return this.collapsible && this.showTempContent; }
    get isScrollMode() { return this.mode === 'scroll'; }
    get isOverMode() { return this.mode === 'over'; }
    get isNormalSize() { return this.size === 'regular'; }
    get isWideSize() { return this.size === 'wide'; }
    constructor() {
        /*
         * If true, the panel is collapsed instead of completely hidden, just like the vertical navbar
         */
        this.collapsible = false;
        /**
         * Header of side panel
         */
        this.heading = '';
        /**
         * Mode of side panel
         * (ignored below a certain width)
         */
        this.mode = 'over';
        /**
         * Size of side-panel
         */
        this.size = 'regular';
        /**
         * Aria label for close button. Needed for a11y
         */
        this.closeButtonLabel = 'close';
        /**
         * Toggle icon aria-label, required for a11y
         */
        this.toggleItemLabel = 'toggle';
        /**
         * Emits when the panel is closed
         */
        this.closed = new EventEmitter();
        /**
         * Emits whenever the content is resized due to opening and closing or parent resize.
         */
        this.contentResize = new EventEmitter();
        this.isXs = false;
        this.isSm = false;
        this.isMd = true;
        this.isLg = false;
        this.isXl = false;
        this.isCollapsed = false;
        this.ready = false;
        this.isHidden = false;
        this.showTempContent = false;
        /**
         * The $rpanel-transition-duration in the style is 0.5 seconds.
         * Sending the resize event after resize need to wait until resize is done.
         */
        this.resizeEventDelay = 500;
        this.resizeEvent = new Subject();
        this.openingOrClosing = false;
        this.previousContentDimensions = { width: 0, height: 0 };
        this.isCollapsedInternal = false; // same as the other one, except w/o timeout for animation
        this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.element = inject(ElementRef);
        this.resizeObserver = inject(ResizeObserverService);
        this.service = inject(SiSidePanelService);
        this.cdRef = inject(ChangeDetectorRef);
        if (this.isBrowser) {
            this.subscription = this.resizeEvent
                .asObservable()
                .pipe(debounceTime(this.resizeEventDelay))
                .subscribe(() => {
                this.openingOrClosing = false;
                this.emitResizeOutputs();
                if (this.isCollapsedInternal && !this.collapsible) {
                    this.isHidden = true;
                }
                this.cdRef.markForCheck();
            });
        }
    }
    ngOnInit() {
        // handle initial state to avoid flicker
        const collapsed = this.collapsed ?? !this.service.isOpen();
        this.isCollapsedInternal = collapsed;
        this.isHidden = collapsed;
        this.isCollapsed = collapsed;
        this.resizeSubs = this.resizeObserver.observe(this.element.nativeElement, 100, true)
            .subscribe(dim => {
            this.setBreakpoints(dim.width, dim.height);
            if (!this.ready) {
                // delay because the initial sizing needs to settle
                setTimeout(() => {
                    this.ready = true;
                    this.cdRef.markForCheck();
                }, 100);
            }
            if (!this.openingOrClosing) {
                this.emitResizeOutputs();
            }
        });
        if (this.subscription) {
            this.subscription.add(this.service.content$.subscribe(portal => this.attachContent(portal)));
            this.subscription.add(this.service.tempContent$.subscribe(portal => this.attachTempContent(portal)));
            this.subscription.add(this.service.isOpen$.subscribe(state => this.openClose(state)));
        }
    }
    ngOnDestroy() {
        this.portalOutlet.detach();
        this.resizeSubs?.unsubscribe();
        this.subscription?.unsubscribe();
    }
    ngOnChanges(changes) {
        if (changes.collapsed) {
            if (this.collapsed) {
                this.service.close();
            }
            else {
                this.service.open();
            }
        }
    }
    toggleSidePanel() {
        if (this.collapsible) {
            this.service.toggle();
        }
        else {
            this.service.close();
        }
    }
    emitResizeOutputs() {
        const contentDimensions = this.getContentDimensions();
        if (contentDimensions.width !== this.previousContentDimensions.width
            || contentDimensions.height !== this.previousContentDimensions.height) {
            this.previousContentDimensions = contentDimensions;
            this.contentResize.emit(contentDimensions);
        }
    }
    getContentDimensions() {
        if (!this.isCollapsedInternal && this.isXs) {
            return { width: 0, height: 0 };
        }
        const rect = this.contentElement.nativeElement.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
    }
    setBreakpoints(width, height) {
        if (!width && !height) {
            // element is not visible, no point in changing anything
            return;
        }
        const breakpoints = this.breakpoints ?? BOOTSTRAP_BREAKPOINTS;
        this.isXs = width < breakpoints.smMinimum;
        this.isSm = width >= breakpoints.smMinimum && width < breakpoints.mdMinimum;
        this.isMd = width >= breakpoints.mdMinimum && width < breakpoints.lgMinimum;
        this.isLg = width >= breakpoints.lgMinimum && width < breakpoints.xlMinimum;
        this.isXl = width >= breakpoints.xlMinimum;
        this.cdRef.markForCheck();
    }
    sendResize() {
        if (this.isScrollMode || this.element.nativeElement.style.paddingRight !== '0') {
            this.openingOrClosing = true;
            this.resizeEvent.next();
        }
    }
    attachContent(portal) {
        this.portalOutlet.detach();
        if (portal) {
            this.portalOutlet.attach(portal);
        }
    }
    attachTempContent(portal) {
        this.tmpPortalOutlet.detach();
        if (portal) {
            this.tmpPortalOutlet.attach(portal);
            this.showTempContent = true;
            this.openClose(true, true);
        }
        else if (this.showTempContent) {
            this.showTempContent = false;
            this.openClose(this.service.isOpen(), true);
        }
    }
    openClose(open, regainFocus = false) {
        if (open !== this.isCollapsedInternal) {
            this.moveFocusInside(open && regainFocus);
            return;
        }
        this.isCollapsedInternal = !open;
        if (open) {
            this.isHidden = false;
        }
        setTimeout(() => this.doOpenClose(open));
    }
    doOpenClose(open) {
        this.moveFocusInside(open);
        this.isCollapsed = !open;
        this.collapsed = this.isCollapsed;
        if (this.isCollapsedInternal) {
            this.closed.emit();
        }
        this.sendResize();
        this.cdRef.markForCheck();
    }
    moveFocusInside(open) {
        if (open) {
            // moves the keyboard focus inside the panel so that the next tab is somewhere useful
            this.panelElement.nativeElement.focus();
            queueMicrotask(() => this.panelElement.nativeElement.blur());
        }
    }
}
SiSidePanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSidePanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSidePanelComponent, selector: "si-side-panel", inputs: { collapsible: "collapsible", collapsed: "collapsed", heading: "heading", mode: "mode", size: "size", closeButtonLabel: "closeButtonLabel", toggleItemLabel: "toggleItemLabel", breakpoints: "breakpoints" }, outputs: { closed: "closed", contentResize: "contentResize" }, host: { properties: { "class.collapsible": "this.isCollapsible", "class.collapsible-temp": "this.isCollapsibleTemp", "class.rpanel-mode--scroll": "this.isScrollMode", "class.rpanel-mode--over": "this.isOverMode", "class.rpanel-size--regular": "this.isNormalSize", "class.rpanel-size--wide": "this.isWideSize", "class.rpanel-resize-xs": "this.isXs", "class.rpanel-resize-sm": "this.isSm", "class.rpanel-resize-md": "this.isMd", "class.rpanel-resize-lg": "this.isLg", "class.rpanel-resize-xl": "this.isXl", "class.rpanel-collapsed": "this.isCollapsed", "class.ready": "this.ready", "class.rpanel-hidden": "this.isHidden" }, classAttribute: "si-layout-inner" }, viewQueries: [{ propertyName: "panelElement", first: true, predicate: ["sidePanel"], descendants: true, static: true }, { propertyName: "contentElement", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "portalOutlet", first: true, predicate: ["portalOutlet"], descendants: true, read: CdkPortalOutlet, static: true }, { propertyName: "tmpPortalOutlet", first: true, predicate: ["tmpPortalOutlet"], descendants: true, read: CdkPortalOutlet, static: true }], usesOnChanges: true, ngImport: i0, template: "<div #sidePanel class=\"side-panel elevation-1\" tabindex=\"-1\">\n  <div class=\"inner\" [class.d-none]=\"showTempContent\">\n    <ng-content select=\"si-side-panel-content, si-right-panel-content\"></ng-content>\n    <ng-container #portalOutlet cdkPortalOutlet></ng-container>\n  </div>\n  <div class=\"inner\" [class.d-none]=\"!showTempContent\">\n    <ng-container #tmpPortalOutlet cdkPortalOutlet></ng-container>\n  </div>\n</div>\n<div #content class=\"main-content si-layout-inner\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{--rpanel-collapsed-padding: 0;--rpanel-size: 390px;display:block;padding-inline-end:var(--rpanel-size)}:host.ready{transition:padding-inline-end calc(.5s * var(--element-animations-enabled, 1))}:host.ready .side-panel{transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}:host .side-panel,:host .side-panel>.inner{inline-size:var(--rpanel-size)}:host.collapsible,:host.collapsible-temp{--rpanel-collapsed-width: 48px}:host.rpanel-size--wide{--rpanel-size: 500px}:host.rpanel-resize-sm.rpanel-mode--over,:host.rpanel-resize-md.rpanel-mode--over,:host.rpanel-resize-lg.rpanel-mode--over,:host.rpanel-resize-xl.rpanel-mode--over{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-lg.rpanel-mode--scroll.rpanel-size--wide{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-sm.rpanel-mode--scroll,:host.rpanel-resize-md.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs.rpanel-mode--over,:host.rpanel-resize-xs.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs:not(.collapsible) .side-panel{inset-block-start:0;z-index:1035}:host.rpanel-resize-xs.collapsible .side-panel{z-index:1030}:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel,:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel>.inner{inline-size:100%}:host.rpanel-hidden:not(.collapsible) .side-panel{display:none}:host.rpanel-collapsed{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-collapsed:not(.collapsible) .side-panel{inline-size:0;overflow:hidden}:host.rpanel-collapsed.collapsible .side-panel{inline-size:var(--rpanel-collapsed-width);overflow:hidden}.side-panel{position:fixed;z-index:1029;inset-block:48px 0;inset-inline-end:0;overflow-x:hidden;background:var(--element-base-1);outline:0}.side-panel>.inner{display:flex;flex-direction:column;position:absolute;inset-block:0;inset-inline-start:0}\n"], dependencies: [{ kind: "directive", type: i1$3.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-side-panel', host: { class: 'si-layout-inner' }, template: "<div #sidePanel class=\"side-panel elevation-1\" tabindex=\"-1\">\n  <div class=\"inner\" [class.d-none]=\"showTempContent\">\n    <ng-content select=\"si-side-panel-content, si-right-panel-content\"></ng-content>\n    <ng-container #portalOutlet cdkPortalOutlet></ng-container>\n  </div>\n  <div class=\"inner\" [class.d-none]=\"!showTempContent\">\n    <ng-container #tmpPortalOutlet cdkPortalOutlet></ng-container>\n  </div>\n</div>\n<div #content class=\"main-content si-layout-inner\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{--rpanel-collapsed-padding: 0;--rpanel-size: 390px;display:block;padding-inline-end:var(--rpanel-size)}:host.ready{transition:padding-inline-end calc(.5s * var(--element-animations-enabled, 1))}:host.ready .side-panel{transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}:host .side-panel,:host .side-panel>.inner{inline-size:var(--rpanel-size)}:host.collapsible,:host.collapsible-temp{--rpanel-collapsed-width: 48px}:host.rpanel-size--wide{--rpanel-size: 500px}:host.rpanel-resize-sm.rpanel-mode--over,:host.rpanel-resize-md.rpanel-mode--over,:host.rpanel-resize-lg.rpanel-mode--over,:host.rpanel-resize-xl.rpanel-mode--over{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-lg.rpanel-mode--scroll.rpanel-size--wide{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-sm.rpanel-mode--scroll,:host.rpanel-resize-md.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs.rpanel-mode--over,:host.rpanel-resize-xs.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs:not(.collapsible) .side-panel{inset-block-start:0;z-index:1035}:host.rpanel-resize-xs.collapsible .side-panel{z-index:1030}:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel,:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel>.inner{inline-size:100%}:host.rpanel-hidden:not(.collapsible) .side-panel{display:none}:host.rpanel-collapsed{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-collapsed:not(.collapsible) .side-panel{inline-size:0;overflow:hidden}:host.rpanel-collapsed.collapsible .side-panel{inline-size:var(--rpanel-collapsed-width);overflow:hidden}.side-panel{position:fixed;z-index:1029;inset-block:48px 0;inset-inline-end:0;overflow-x:hidden;background:var(--element-base-1);outline:0}.side-panel>.inner{display:flex;flex-direction:column;position:absolute;inset-block:0;inset-inline-start:0}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { collapsible: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], heading: [{
                type: Input
            }], mode: [{
                type: Input
            }], size: [{
                type: Input
            }], closeButtonLabel: [{
                type: Input
            }], toggleItemLabel: [{
                type: Input
            }], breakpoints: [{
                type: Input
            }], closed: [{
                type: Output
            }], contentResize: [{
                type: Output
            }], isCollapsible: [{
                type: HostBinding,
                args: ['class.collapsible']
            }], isCollapsibleTemp: [{
                type: HostBinding,
                args: ['class.collapsible-temp']
            }], isScrollMode: [{
                type: HostBinding,
                args: ['class.rpanel-mode--scroll']
            }], isOverMode: [{
                type: HostBinding,
                args: ['class.rpanel-mode--over']
            }], isNormalSize: [{
                type: HostBinding,
                args: ['class.rpanel-size--regular']
            }], isWideSize: [{
                type: HostBinding,
                args: ['class.rpanel-size--wide']
            }], isXs: [{
                type: HostBinding,
                args: ['class.rpanel-resize-xs']
            }], isSm: [{
                type: HostBinding,
                args: ['class.rpanel-resize-sm']
            }], isMd: [{
                type: HostBinding,
                args: ['class.rpanel-resize-md']
            }], isLg: [{
                type: HostBinding,
                args: ['class.rpanel-resize-lg']
            }], isXl: [{
                type: HostBinding,
                args: ['class.rpanel-resize-xl']
            }], isCollapsed: [{
                type: HostBinding,
                args: ['class.rpanel-collapsed']
            }], ready: [{
                type: HostBinding,
                args: ['class.ready']
            }], isHidden: [{
                type: HostBinding,
                args: ['class.rpanel-hidden']
            }], panelElement: [{
                type: ViewChild,
                args: ['sidePanel', { static: true }]
            }], contentElement: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], portalOutlet: [{
                type: ViewChild,
                args: ['portalOutlet', { read: CdkPortalOutlet, static: true }]
            }], tmpPortalOutlet: [{
                type: ViewChild,
                args: ['tmpPortalOutlet', { read: CdkPortalOutlet, static: true }]
            }] } });

/**
 * @deprecated Use the `SiSidePanelComponent` instead.
 */
class SiRightPanelComponent extends SiSidePanelComponent {
}
SiRightPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SiRightPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiRightPanelComponent, selector: "si-right-panel", host: { classAttribute: "si-layout-inner" }, usesInheritance: true, ngImport: i0, template: "<div #sidePanel class=\"side-panel elevation-1\" tabindex=\"-1\">\n  <div class=\"inner\" [class.d-none]=\"showTempContent\">\n    <ng-content select=\"si-side-panel-content, si-right-panel-content\"></ng-content>\n    <ng-container #portalOutlet cdkPortalOutlet></ng-container>\n  </div>\n  <div class=\"inner\" [class.d-none]=\"!showTempContent\">\n    <ng-container #tmpPortalOutlet cdkPortalOutlet></ng-container>\n  </div>\n</div>\n<div #content class=\"main-content si-layout-inner\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{--rpanel-collapsed-padding: 0;--rpanel-size: 390px;display:block;padding-inline-end:var(--rpanel-size)}:host.ready{transition:padding-inline-end calc(.5s * var(--element-animations-enabled, 1))}:host.ready .side-panel{transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}:host .side-panel,:host .side-panel>.inner{inline-size:var(--rpanel-size)}:host.collapsible,:host.collapsible-temp{--rpanel-collapsed-width: 48px}:host.rpanel-size--wide{--rpanel-size: 500px}:host.rpanel-resize-sm.rpanel-mode--over,:host.rpanel-resize-md.rpanel-mode--over,:host.rpanel-resize-lg.rpanel-mode--over,:host.rpanel-resize-xl.rpanel-mode--over{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-lg.rpanel-mode--scroll.rpanel-size--wide{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-sm.rpanel-mode--scroll,:host.rpanel-resize-md.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs.rpanel-mode--over,:host.rpanel-resize-xs.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs:not(.collapsible) .side-panel{inset-block-start:0;z-index:1035}:host.rpanel-resize-xs.collapsible .side-panel{z-index:1030}:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel,:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel>.inner{inline-size:100%}:host.rpanel-hidden:not(.collapsible) .side-panel{display:none}:host.rpanel-collapsed{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-collapsed:not(.collapsible) .side-panel{inline-size:0;overflow:hidden}:host.rpanel-collapsed.collapsible .side-panel{inline-size:var(--rpanel-collapsed-width);overflow:hidden}.side-panel{position:fixed;z-index:1029;inset-block:48px 0;inset-inline-end:0;overflow-x:hidden;background:var(--element-base-1);outline:0}.side-panel>.inner{display:flex;flex-direction:column;position:absolute;inset-block:0;inset-inline-start:0}\n"], dependencies: [{ kind: "directive", type: i1$3.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-right-panel', host: { class: 'si-layout-inner' }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #sidePanel class=\"side-panel elevation-1\" tabindex=\"-1\">\n  <div class=\"inner\" [class.d-none]=\"showTempContent\">\n    <ng-content select=\"si-side-panel-content, si-right-panel-content\"></ng-content>\n    <ng-container #portalOutlet cdkPortalOutlet></ng-container>\n  </div>\n  <div class=\"inner\" [class.d-none]=\"!showTempContent\">\n    <ng-container #tmpPortalOutlet cdkPortalOutlet></ng-container>\n  </div>\n</div>\n<div #content class=\"main-content si-layout-inner\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{--rpanel-collapsed-padding: 0;--rpanel-size: 390px;display:block;padding-inline-end:var(--rpanel-size)}:host.ready{transition:padding-inline-end calc(.5s * var(--element-animations-enabled, 1))}:host.ready .side-panel{transition:inline-size calc(.5s * var(--element-animations-enabled, 1))}:host .side-panel,:host .side-panel>.inner{inline-size:var(--rpanel-size)}:host.collapsible,:host.collapsible-temp{--rpanel-collapsed-width: 48px}:host.rpanel-size--wide{--rpanel-size: 500px}:host.rpanel-resize-sm.rpanel-mode--over,:host.rpanel-resize-md.rpanel-mode--over,:host.rpanel-resize-lg.rpanel-mode--over,:host.rpanel-resize-xl.rpanel-mode--over{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-lg.rpanel-mode--scroll.rpanel-size--wide{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-sm.rpanel-mode--scroll,:host.rpanel-resize-md.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs.rpanel-mode--over,:host.rpanel-resize-xs.rpanel-mode--scroll{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-resize-xs:not(.collapsible) .side-panel{inset-block-start:0;z-index:1035}:host.rpanel-resize-xs.collapsible .side-panel{z-index:1030}:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel,:host.rpanel-resize-xs:not(.rpanel-collapsed) .side-panel>.inner{inline-size:100%}:host.rpanel-hidden:not(.collapsible) .side-panel{display:none}:host.rpanel-collapsed{padding-inline-end:var(--rpanel-collapsed-width)}:host.rpanel-collapsed:not(.collapsible) .side-panel{inline-size:0;overflow:hidden}:host.rpanel-collapsed.collapsible .side-panel{inline-size:var(--rpanel-collapsed-width);overflow:hidden}.side-panel{position:fixed;z-index:1029;inset-block:48px 0;inset-inline-end:0;overflow-x:hidden;background:var(--element-base-1);outline:0}.side-panel>.inner{display:flex;flex-direction:column;position:absolute;inset-block:0;inset-inline-start:0}\n"] }]
        }] });

class SiSidePanelContentComponent {
    constructor() {
        /*
         * If true, the panel is collapsed instead of completely hidden, just like the vertical navbar
         */
        this.collapsible = false;
        /**
         * Header of side panel
         */
        this.heading = '';
        /**
         * Input list of primary action items
         */
        this.primaryActions = [];
        /**
         * Input list of secondary action items.
         */
        this.secondaryActions = [];
        /**
         * Status icons/actions
         */
        this.statusActions = [];
        /**
         * Toggles search bar
         */
        this.searchable = false;
        /**
         * Placeholder text for search
         */
        this.searchPlaceholder = 'Search...';
        /**
         * Aria label for close button. Needed for a11y
         */
        this.closeButtonLabel = 'close';
        /**
         * Toggle icon aria-label, required for a11y
         */
        this.toggleItemLabel = 'toggle';
        /**
         * Output for search bar input
         */
        this.searchEvent = new EventEmitter();
        this.isCollapsed = false;
        this.trackByIndex = (index) => index;
        this.service = inject(SiSidePanelService);
        const accordionHcollapse = inject(SiAccordionHCollapseService);
        this.subscription = this.service.isOpen$.subscribe(state => {
            this.isCollapsed = !state;
            accordionHcollapse.hcollapsed$.next(!state);
        });
        this.subscription.add(accordionHcollapse.open$.subscribe(() => this.service.open()));
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    toggleSidePanel(event) {
        event?.target?.blur();
        if (this.service.isTemporaryOpen()) {
            this.service.hideTemporaryContent();
        }
        else {
            this.service.toggle();
        }
    }
}
SiSidePanelContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSidePanelContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSidePanelContentComponent, selector: "si-side-panel-content", inputs: { collapsible: "collapsible", heading: "heading", primaryActions: "primaryActions", secondaryActions: "secondaryActions", statusActions: "statusActions", searchable: "searchable", searchPlaceholder: "searchPlaceholder", closeButtonLabel: "closeButtonLabel", toggleItemLabel: "toggleItemLabel" }, outputs: { searchEvent: "searchEvent" }, host: { properties: { "class.collapsed": "this.isCollapsed" } }, providers: [SiAccordionHCollapseService], ngImport: i0, template: "<div class=\"rpanel-header pt-5\">\n  <p class=\"si-title-2 my-0 ms-6 auto-hide text-truncate\">{{ heading | translate }}</p>\n  <si-content-action-bar\n    *ngIf=\"primaryActions?.length || secondaryActions?.length\"\n    class=\"auto-hide ms-auto border-end\"\n    [primaryActions]=\"primaryActions\"\n    [secondaryActions]=\"secondaryActions\"\n    [toggleItemLabel]=\"toggleItemLabel\"\n    viewType=\"expanded\"\n  ></si-content-action-bar>\n  <button\n    type=\"button\"\n    class=\"close-button btn btn-circle btn-sm btn-ghost element-cancel me-4\"\n    [attr.aria-label]=\"closeButtonLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n  <button\n    type=\"button\"\n    class=\"collapse-toggle flip-rtl btn btn-circle btn-sm btn-ghost mx-4\"\n    [class.element-double-right]=\"!isCollapsed\"\n    [class.element-double-left]=\"isCollapsed\"\n    [attr.aria-label]=\"toggleItemLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n</div>\n<div class=\"rpanel-wrapper d-flex flex-column\">\n  <div *ngIf=\"statusActions?.length\" class=\"rpanel-header rpanel-statusactions px-5 pb-4\">\n    <ng-container *ngFor=\"let dp of statusActions; trackBy: trackByIndex\">\n      <a\n        *ngIf=\"!dp.disabled\"\n        class=\"rpanel-statusaction position-relative focus-inside\"\n        [attr.aria-label]=\"!isCollapsed ? '' : dp.title! | translate\"\n        [siLink]=\"dp\"\n      >\n        <i class=\"icon\" [ngClass]=\"dp.icon!\" aria-hidden=\"true\"></i>\n        <i *ngIf=\"dp.overlayIcon\" class=\"icon position-absolute\" [ngClass]=\"dp.overlayIcon\" aria-hidden=\"true\"></i>\n        <span class=\"ms-2 auto-hide\">{{ dp.title! | translate }}</span>\n      </a>\n      <div *ngIf=\"dp.disabled\" class=\"rpanel-statusaction dot-outer\" aria-hidden=\"true\">\n        <div class=\"dot text-muted text-center\">&bull;</div>\n      </div>\n    </ng-container>\n  </div>\n  <div *ngIf=\"searchable\" class=\"nav-search px-5 pt-2 auto-hide\">\n    <si-search-bar\n      class=\"m-0\"\n      [colorVariant]=\"'base-0'\"\n      #searchBar\n      [placeholder]=\"searchPlaceholder | translate\"\n      (searchChange)=\"searchEvent.emit($event)\"\n      [debounceTime]=\"400\"\n      [showIcon]=\"true\"\n      [prohibitedCharacters]=\"'*?'\"\n    ></si-search-bar>\n  </div>\n  <div class=\"rpanel-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;flex:1}:host-context(.collapsible) .close-button{display:none}:host-context(.collapsible) .collapse-toggle{display:block}:host-context(.collapsible) :host.collapsed .auto-hide{display:none}:host-context(.collapsible) :host.collapsed .rpanel-wrapper{inline-size:var(--rpanel-collapsed-width)}:host-context(.collapsible) :host.collapsed .rpanel-content{flex:0 0 auto;margin-block-start:8px}:host-context(.collapsible) :host.collapsed .rpanel-statusactions{order:3;flex-direction:column;align-items:flex-start;padding:0!important}:host-context(.collapsible) :host.collapsed .rpanel-statusaction{padding-block:8px;padding-inline:12px}:host-context(.collapsible) :host.collapsed .rpanel-statusaction:not(.dot-outer):hover{background:var(--element-base-1-hover)}.collapse-toggle{display:none}.rpanel-header,.rpanel-statusaction{display:flex;align-items:center}.rpanel-header{justify-content:space-between}.rpanel-content{margin-block-start:12px}.rpanel-wrapper,.rpanel-content{min-block-size:0;flex:1 0 0;overflow-x:hidden;overflow-y:auto}.rpanel-statusaction{text-decoration:none}.rpanel-statusaction .dot{inline-size:24px;line-height:24px}.rpanel-statusaction span{font-size:12px;white-space:pre-wrap;color:var(--element-text-primary)}.rpanel-statusaction:hover span{color:var(--element-text-active)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiContentActionBarComponent, selector: "si-content-action-bar", inputs: ["primaryActions", "secondaryActions", "actionParam", "viewType", "focusOnLoad", "toggleItemLabel", "preventIconsInDropdownMenus"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "component", type: SiSearchBarComponent, selector: "si-search-bar", inputs: ["debounceTime", "prohibitedCharacters", "placeholder", "showIcon", "tabbable", "value", "disabled", "readonly", "colorVariant"], outputs: ["searchChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-side-panel-content', providers: [SiAccordionHCollapseService], template: "<div class=\"rpanel-header pt-5\">\n  <p class=\"si-title-2 my-0 ms-6 auto-hide text-truncate\">{{ heading | translate }}</p>\n  <si-content-action-bar\n    *ngIf=\"primaryActions?.length || secondaryActions?.length\"\n    class=\"auto-hide ms-auto border-end\"\n    [primaryActions]=\"primaryActions\"\n    [secondaryActions]=\"secondaryActions\"\n    [toggleItemLabel]=\"toggleItemLabel\"\n    viewType=\"expanded\"\n  ></si-content-action-bar>\n  <button\n    type=\"button\"\n    class=\"close-button btn btn-circle btn-sm btn-ghost element-cancel me-4\"\n    [attr.aria-label]=\"closeButtonLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n  <button\n    type=\"button\"\n    class=\"collapse-toggle flip-rtl btn btn-circle btn-sm btn-ghost mx-4\"\n    [class.element-double-right]=\"!isCollapsed\"\n    [class.element-double-left]=\"isCollapsed\"\n    [attr.aria-label]=\"toggleItemLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n</div>\n<div class=\"rpanel-wrapper d-flex flex-column\">\n  <div *ngIf=\"statusActions?.length\" class=\"rpanel-header rpanel-statusactions px-5 pb-4\">\n    <ng-container *ngFor=\"let dp of statusActions; trackBy: trackByIndex\">\n      <a\n        *ngIf=\"!dp.disabled\"\n        class=\"rpanel-statusaction position-relative focus-inside\"\n        [attr.aria-label]=\"!isCollapsed ? '' : dp.title! | translate\"\n        [siLink]=\"dp\"\n      >\n        <i class=\"icon\" [ngClass]=\"dp.icon!\" aria-hidden=\"true\"></i>\n        <i *ngIf=\"dp.overlayIcon\" class=\"icon position-absolute\" [ngClass]=\"dp.overlayIcon\" aria-hidden=\"true\"></i>\n        <span class=\"ms-2 auto-hide\">{{ dp.title! | translate }}</span>\n      </a>\n      <div *ngIf=\"dp.disabled\" class=\"rpanel-statusaction dot-outer\" aria-hidden=\"true\">\n        <div class=\"dot text-muted text-center\">&bull;</div>\n      </div>\n    </ng-container>\n  </div>\n  <div *ngIf=\"searchable\" class=\"nav-search px-5 pt-2 auto-hide\">\n    <si-search-bar\n      class=\"m-0\"\n      [colorVariant]=\"'base-0'\"\n      #searchBar\n      [placeholder]=\"searchPlaceholder | translate\"\n      (searchChange)=\"searchEvent.emit($event)\"\n      [debounceTime]=\"400\"\n      [showIcon]=\"true\"\n      [prohibitedCharacters]=\"'*?'\"\n    ></si-search-bar>\n  </div>\n  <div class=\"rpanel-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;flex:1}:host-context(.collapsible) .close-button{display:none}:host-context(.collapsible) .collapse-toggle{display:block}:host-context(.collapsible) :host.collapsed .auto-hide{display:none}:host-context(.collapsible) :host.collapsed .rpanel-wrapper{inline-size:var(--rpanel-collapsed-width)}:host-context(.collapsible) :host.collapsed .rpanel-content{flex:0 0 auto;margin-block-start:8px}:host-context(.collapsible) :host.collapsed .rpanel-statusactions{order:3;flex-direction:column;align-items:flex-start;padding:0!important}:host-context(.collapsible) :host.collapsed .rpanel-statusaction{padding-block:8px;padding-inline:12px}:host-context(.collapsible) :host.collapsed .rpanel-statusaction:not(.dot-outer):hover{background:var(--element-base-1-hover)}.collapse-toggle{display:none}.rpanel-header,.rpanel-statusaction{display:flex;align-items:center}.rpanel-header{justify-content:space-between}.rpanel-content{margin-block-start:12px}.rpanel-wrapper,.rpanel-content{min-block-size:0;flex:1 0 0;overflow-x:hidden;overflow-y:auto}.rpanel-statusaction{text-decoration:none}.rpanel-statusaction .dot{inline-size:24px;line-height:24px}.rpanel-statusaction span{font-size:12px;white-space:pre-wrap;color:var(--element-text-primary)}.rpanel-statusaction:hover span{color:var(--element-text-active)}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { collapsible: [{
                type: Input
            }], heading: [{
                type: Input
            }], primaryActions: [{
                type: Input
            }], secondaryActions: [{
                type: Input
            }], statusActions: [{
                type: Input
            }], searchable: [{
                type: Input
            }], searchPlaceholder: [{
                type: Input
            }], closeButtonLabel: [{
                type: Input
            }], toggleItemLabel: [{
                type: Input
            }], searchEvent: [{
                type: Output
            }], isCollapsed: [{
                type: HostBinding,
                args: ['class.collapsed']
            }] } });

/**
 * @deprecated Use the `SiSidePanelContentComponent` instead.
 */
class SiRightPanelContentComponent extends SiSidePanelContentComponent {
}
SiRightPanelContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelContentComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SiRightPanelContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiRightPanelContentComponent, selector: "si-right-panel-content", providers: [SiAccordionHCollapseService], usesInheritance: true, ngImport: i0, template: "<div class=\"rpanel-header pt-5\">\n  <p class=\"si-title-2 my-0 ms-6 auto-hide text-truncate\">{{ heading | translate }}</p>\n  <si-content-action-bar\n    *ngIf=\"primaryActions?.length || secondaryActions?.length\"\n    class=\"auto-hide ms-auto border-end\"\n    [primaryActions]=\"primaryActions\"\n    [secondaryActions]=\"secondaryActions\"\n    [toggleItemLabel]=\"toggleItemLabel\"\n    viewType=\"expanded\"\n  ></si-content-action-bar>\n  <button\n    type=\"button\"\n    class=\"close-button btn btn-circle btn-sm btn-ghost element-cancel me-4\"\n    [attr.aria-label]=\"closeButtonLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n  <button\n    type=\"button\"\n    class=\"collapse-toggle flip-rtl btn btn-circle btn-sm btn-ghost mx-4\"\n    [class.element-double-right]=\"!isCollapsed\"\n    [class.element-double-left]=\"isCollapsed\"\n    [attr.aria-label]=\"toggleItemLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n</div>\n<div class=\"rpanel-wrapper d-flex flex-column\">\n  <div *ngIf=\"statusActions?.length\" class=\"rpanel-header rpanel-statusactions px-5 pb-4\">\n    <ng-container *ngFor=\"let dp of statusActions; trackBy: trackByIndex\">\n      <a\n        *ngIf=\"!dp.disabled\"\n        class=\"rpanel-statusaction position-relative focus-inside\"\n        [attr.aria-label]=\"!isCollapsed ? '' : dp.title! | translate\"\n        [siLink]=\"dp\"\n      >\n        <i class=\"icon\" [ngClass]=\"dp.icon!\" aria-hidden=\"true\"></i>\n        <i *ngIf=\"dp.overlayIcon\" class=\"icon position-absolute\" [ngClass]=\"dp.overlayIcon\" aria-hidden=\"true\"></i>\n        <span class=\"ms-2 auto-hide\">{{ dp.title! | translate }}</span>\n      </a>\n      <div *ngIf=\"dp.disabled\" class=\"rpanel-statusaction dot-outer\" aria-hidden=\"true\">\n        <div class=\"dot text-muted text-center\">&bull;</div>\n      </div>\n    </ng-container>\n  </div>\n  <div *ngIf=\"searchable\" class=\"nav-search px-5 pt-2 auto-hide\">\n    <si-search-bar\n      class=\"m-0\"\n      [colorVariant]=\"'base-0'\"\n      #searchBar\n      [placeholder]=\"searchPlaceholder | translate\"\n      (searchChange)=\"searchEvent.emit($event)\"\n      [debounceTime]=\"400\"\n      [showIcon]=\"true\"\n      [prohibitedCharacters]=\"'*?'\"\n    ></si-search-bar>\n  </div>\n  <div class=\"rpanel-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;flex:1}:host-context(.collapsible) .close-button{display:none}:host-context(.collapsible) .collapse-toggle{display:block}:host-context(.collapsible) :host.collapsed .auto-hide{display:none}:host-context(.collapsible) :host.collapsed .rpanel-wrapper{inline-size:var(--rpanel-collapsed-width)}:host-context(.collapsible) :host.collapsed .rpanel-content{flex:0 0 auto;margin-block-start:8px}:host-context(.collapsible) :host.collapsed .rpanel-statusactions{order:3;flex-direction:column;align-items:flex-start;padding:0!important}:host-context(.collapsible) :host.collapsed .rpanel-statusaction{padding-block:8px;padding-inline:12px}:host-context(.collapsible) :host.collapsed .rpanel-statusaction:not(.dot-outer):hover{background:var(--element-base-1-hover)}.collapse-toggle{display:none}.rpanel-header,.rpanel-statusaction{display:flex;align-items:center}.rpanel-header{justify-content:space-between}.rpanel-content{margin-block-start:12px}.rpanel-wrapper,.rpanel-content{min-block-size:0;flex:1 0 0;overflow-x:hidden;overflow-y:auto}.rpanel-statusaction{text-decoration:none}.rpanel-statusaction .dot{inline-size:24px;line-height:24px}.rpanel-statusaction span{font-size:12px;white-space:pre-wrap;color:var(--element-text-primary)}.rpanel-statusaction:hover span{color:var(--element-text-active)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiContentActionBarComponent, selector: "si-content-action-bar", inputs: ["primaryActions", "secondaryActions", "actionParam", "viewType", "focusOnLoad", "toggleItemLabel", "preventIconsInDropdownMenus"] }, { kind: "directive", type: SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "component", type: SiSearchBarComponent, selector: "si-search-bar", inputs: ["debounceTime", "prohibitedCharacters", "placeholder", "showIcon", "tabbable", "value", "disabled", "readonly", "colorVariant"], outputs: ["searchChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-right-panel-content', providers: [SiAccordionHCollapseService], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"rpanel-header pt-5\">\n  <p class=\"si-title-2 my-0 ms-6 auto-hide text-truncate\">{{ heading | translate }}</p>\n  <si-content-action-bar\n    *ngIf=\"primaryActions?.length || secondaryActions?.length\"\n    class=\"auto-hide ms-auto border-end\"\n    [primaryActions]=\"primaryActions\"\n    [secondaryActions]=\"secondaryActions\"\n    [toggleItemLabel]=\"toggleItemLabel\"\n    viewType=\"expanded\"\n  ></si-content-action-bar>\n  <button\n    type=\"button\"\n    class=\"close-button btn btn-circle btn-sm btn-ghost element-cancel me-4\"\n    [attr.aria-label]=\"closeButtonLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n  <button\n    type=\"button\"\n    class=\"collapse-toggle flip-rtl btn btn-circle btn-sm btn-ghost mx-4\"\n    [class.element-double-right]=\"!isCollapsed\"\n    [class.element-double-left]=\"isCollapsed\"\n    [attr.aria-label]=\"toggleItemLabel | translate\"\n    (click)=\"toggleSidePanel($event)\"\n  ></button>\n</div>\n<div class=\"rpanel-wrapper d-flex flex-column\">\n  <div *ngIf=\"statusActions?.length\" class=\"rpanel-header rpanel-statusactions px-5 pb-4\">\n    <ng-container *ngFor=\"let dp of statusActions; trackBy: trackByIndex\">\n      <a\n        *ngIf=\"!dp.disabled\"\n        class=\"rpanel-statusaction position-relative focus-inside\"\n        [attr.aria-label]=\"!isCollapsed ? '' : dp.title! | translate\"\n        [siLink]=\"dp\"\n      >\n        <i class=\"icon\" [ngClass]=\"dp.icon!\" aria-hidden=\"true\"></i>\n        <i *ngIf=\"dp.overlayIcon\" class=\"icon position-absolute\" [ngClass]=\"dp.overlayIcon\" aria-hidden=\"true\"></i>\n        <span class=\"ms-2 auto-hide\">{{ dp.title! | translate }}</span>\n      </a>\n      <div *ngIf=\"dp.disabled\" class=\"rpanel-statusaction dot-outer\" aria-hidden=\"true\">\n        <div class=\"dot text-muted text-center\">&bull;</div>\n      </div>\n    </ng-container>\n  </div>\n  <div *ngIf=\"searchable\" class=\"nav-search px-5 pt-2 auto-hide\">\n    <si-search-bar\n      class=\"m-0\"\n      [colorVariant]=\"'base-0'\"\n      #searchBar\n      [placeholder]=\"searchPlaceholder | translate\"\n      (searchChange)=\"searchEvent.emit($event)\"\n      [debounceTime]=\"400\"\n      [showIcon]=\"true\"\n      [prohibitedCharacters]=\"'*?'\"\n    ></si-search-bar>\n  </div>\n  <div class=\"rpanel-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;flex:1}:host-context(.collapsible) .close-button{display:none}:host-context(.collapsible) .collapse-toggle{display:block}:host-context(.collapsible) :host.collapsed .auto-hide{display:none}:host-context(.collapsible) :host.collapsed .rpanel-wrapper{inline-size:var(--rpanel-collapsed-width)}:host-context(.collapsible) :host.collapsed .rpanel-content{flex:0 0 auto;margin-block-start:8px}:host-context(.collapsible) :host.collapsed .rpanel-statusactions{order:3;flex-direction:column;align-items:flex-start;padding:0!important}:host-context(.collapsible) :host.collapsed .rpanel-statusaction{padding-block:8px;padding-inline:12px}:host-context(.collapsible) :host.collapsed .rpanel-statusaction:not(.dot-outer):hover{background:var(--element-base-1-hover)}.collapse-toggle{display:none}.rpanel-header,.rpanel-statusaction{display:flex;align-items:center}.rpanel-header{justify-content:space-between}.rpanel-content{margin-block-start:12px}.rpanel-wrapper,.rpanel-content{min-block-size:0;flex:1 0 0;overflow-x:hidden;overflow-y:auto}.rpanel-statusaction{text-decoration:none}.rpanel-statusaction .dot{inline-size:24px;line-height:24px}.rpanel-statusaction span{font-size:12px;white-space:pre-wrap;color:var(--element-text-primary)}.rpanel-statusaction:hover span{color:var(--element-text-active)}\n"] }]
        }] });

/**
 * @deprecated Use the `SiSidePanelModule` instead.
 */
class SiRightPanelModule {
}
SiRightPanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiRightPanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelModule, declarations: [SiRightPanelComponent,
        SiRightPanelContentComponent], imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiLinkModule,
        SiSearchBarModule,
        SiTranslateModule], exports: [SiRightPanelComponent,
        SiRightPanelContentComponent] });
SiRightPanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelModule, imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiLinkModule,
        SiSearchBarModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiRightPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        PortalModule,
                        SiContentActionBarModule,
                        SiLinkModule,
                        SiSearchBarModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiRightPanelComponent,
                        SiRightPanelContentComponent
                    ],
                    exports: [
                        SiRightPanelComponent,
                        SiRightPanelContentComponent
                    ]
                }]
        }] });

class SiSidePanelModule {
}
SiSidePanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSidePanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelModule, declarations: [SiSidePanelComponent,
        SiSidePanelContentComponent], imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiLinkModule,
        SiSearchBarModule,
        SiTranslateModule], exports: [SiSidePanelComponent,
        SiSidePanelContentComponent] });
SiSidePanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelModule, imports: [CommonModule,
        PortalModule,
        SiContentActionBarModule,
        SiLinkModule,
        SiSearchBarModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSidePanelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        PortalModule,
                        SiContentActionBarModule,
                        SiLinkModule,
                        SiSearchBarModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiSidePanelComponent,
                        SiSidePanelContentComponent
                    ],
                    exports: [
                        SiSidePanelComponent,
                        SiSidePanelContentComponent
                    ]
                }]
        }] });

/**
 * Input options strategy base class.
 */
class SiSelectOptionsStrategy {
    constructor() {
        this.optionsSubject$ = new BehaviorSubject([]);
        this.rows$ = this.optionsSubject$.asObservable();
    }
}
SiSelectOptionsStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionsStrategy, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiSelectOptionsStrategy.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectOptionsStrategy, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionsStrategy, decorators: [{
            type: Directive
        }] });

/**
 * Selection strategy base class.
 */
class SiSelectSelectionStrategy {
    constructor() {
        this.onTouched = () => { };
        this.onChange = () => { };
        this._disabled = false;
        this.stateChange$ = new Subject();
        this.selectionChange$ = new Subject();
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChange$.next();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
        this.stateChange$.next();
    }
    ngOnDestroy() {
        this.stateChange$.complete();
        this.selectionChange$.complete();
    }
}
SiSelectSelectionStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSelectionStrategy, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiSelectSelectionStrategy.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectSelectionStrategy, inputs: { disabled: "disabled" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSelectionStrategy, decorators: [{
            type: Directive
        }], propDecorators: { disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }] } });

/**
 * The directive allows to template/customize the group option rendering.
 * This requires to use the {@link SiSelectComplexOptionsDirective} to specify complex options as input.
 *
 * @example
 * <pre><code>
 * <si-select [complexOptions]="{ g1: ['g1.i1', 'g1.i2'], g2: ['g2.i1'] }" >
 *   <ng-template siSiSelectGroupTemplate let-group>{{ group.key | uppercase }}</ng-template>
 * </si-select>
 * </code></pre>
 */
class SiSelectGroupTemplateDirective {
    constructor() {
        // We need to have the templateRef here, so that the templates variables are properly typed
        this.templateRef = inject(TemplateRef);
    }
}
SiSelectGroupTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectGroupTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiSelectGroupTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectGroupTemplateDirective, selector: "[siSiSelectGroupTemplate]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectGroupTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSiSelectGroupTemplate]'
                }]
        }] });

/**
 * The directive allows to template/customize the value option rendering.
 * This requires to use the {@link SiSelectComplexOptionsDirective} to specify complex options as input.
 *
 * @example
 * <pre><code>
 * <si-select [options]="[{ id: 'good', title: 'Good' }, { id: 'fair', title: 'Fair' }, { id: 'bad', title: 'Bad' }]" >
 *   <ng-template siSiSelectOptionTemplate let-option>{{ option.value | uppercase }}</ng-template>
 * </si-select>
 * </code></pre>
 */
class SiSelectOptionTemplateDirective {
    constructor() {
        // We need to have the templateRef here, so that the templates variables are properly typed
        this.templateRef = inject(TemplateRef);
    }
}
SiSelectOptionTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiSelectOptionTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectOptionTemplateDirective, selector: "[siSiSelectOptionTemplate]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSiSelectOptionTemplate]'
                }]
        }] });

class SiSelectOptionRowTemplateDirective {
    constructor() {
        this.templateRef = inject(TemplateRef);
    }
}
SiSelectOptionRowTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionRowTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SiSelectOptionRowTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectOptionRowTemplateDirective, selector: "[siSiSelectOptionRowTemplate]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectOptionRowTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSiSelectOptionRowTemplate]'
                }]
        }] });

class SiSelectComponent {
    get readonly() {
        return this._readonly;
    }
    /**
     * Readonly state. Similar to disabled but with higher contrast
     * @defaultref {@link _readonly}
     **/
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
        this.changeDetector.markForCheck();
    }
    constructor() {
        this.classDropdown = true;
        /**
         * Unique identifier.
         */
        this.id = `__si-select-${SiSelectComponent.idCounter++}`;
        /** Aria label of the select. */
        this.ariaLabel = null;
        /** Aria labelledby of the select. */
        this.labelledby = null;
        this._readonly = false;
        this.dropdownClose = new EventEmitter();
        this.rows = [];
        this.trackByItem = (index, item) => item;
        this.selectedRows = [];
        this.open = false;
        this.destroyer = new Subject();
        this.backdropClicked = false;
        this.changeDetector = inject(ChangeDetectorRef);
        this.selectOptions = inject((SiSelectOptionsStrategy));
        this.selectionStrategy = inject((SiSelectSelectionStrategy));
        this.selectOptions.rows$.pipe(takeUntil(this.destroyer)).subscribe(options => {
            this.rows = options;
            this.refreshSelectedRows();
            this.selectionStrategy.updateArrayValue(this.selectedRows.map(row => row.value));
        });
        this.selectionStrategy.selectionChange$.subscribe(() => this.refreshSelectedRows());
        this.selectionStrategy.stateChange$.pipe(takeUntil(this.destroyer)).subscribe(() => this.changeDetector.markForCheck());
    }
    ngOnDestroy() {
        this.destroyer.next();
        this.destroyer.complete();
    }
    refreshSelectedRows() {
        this.selectedRows = this.rows.flatMap(row => row.type === 'option' ? [row] : row.options)
            .filter(row => this.selectionStrategy.arrayValue?.some(value => this.selectOptions.optionEqualCheckFn(row.value, value)));
        this.changeDetector.markForCheck();
    }
    listBoxValueChange(changeEvent) {
        this.selectionStrategy.updateArrayValue(changeEvent.value.slice(), true);
        this.refreshSelectedRows();
    }
    openListBox() {
        if (this._readonly || this.selectionStrategy.disabled) {
            return;
        }
        this.open = true;
        // It is necessary to wait for the overlay before we can update the focus
        setTimeout(() => this.listbox?.nativeElement.focus());
    }
    overlayDetach() {
        this.open = false;
        if (!this.backdropClicked) {
            this.trigger.nativeElement.focus();
        }
        else {
            this.backdropClicked = false;
            this.selectionStrategy.onTouched();
        }
        this.dropdownClose.emit();
    }
    backdropClick() {
        this.backdropClicked = true;
        this.open = false;
    }
    closeListBoxIfSingle() {
        if (!this.selectionStrategy.allowMultiple) {
            this.open = false;
        }
    }
    blur() {
        if (!this.open) {
            this.selectionStrategy.onTouched();
        }
    }
}
SiSelectComponent.idCounter = 0;
SiSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectComponent, selector: "si-select", inputs: { id: "id", ariaLabel: "ariaLabel", labelledby: "labelledby", readonly: "readonly" }, outputs: { dropdownClose: "dropdownClose" }, host: { properties: { "class.dropdown": "this.classDropdown", "class.readonly": "this.readonly", "class.open": "this.open" } }, queries: [{ propertyName: "optionTemplate", first: true, predicate: SiSelectOptionTemplateDirective, descendants: true, read: TemplateRef }, { propertyName: "groupTemplate", first: true, predicate: SiSelectGroupTemplateDirective, descendants: true, read: TemplateRef }], viewQueries: [{ propertyName: "listbox", first: true, predicate: CdkListbox, descendants: true, read: ElementRef }, { propertyName: "trigger", first: true, predicate: CdkOverlayOrigin, descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div\n  cdkOverlayOrigin\n  #trigger=\"cdkOverlayOrigin\"\n  role=\"combobox\"\n  class=\"select focus-none dropdown-toggle d-flex align-items-center ps-4 pe-5\"\n  [tabindex]=\"selectionStrategy.disabled ? '-1' : '0'\"\n  [attr.aria-labelledby]=\"(id + '-aria-label') + ' ' + labelledby + ' ' + (id + '-content')\"\n  [class.active]=\"open\"\n  [attr.aria-disabled]=\"selectionStrategy.disabled\"\n  [attr.aria-controls]=\"id + '-listbox'\"\n  aria-autocomplete=\"none\"\n  aria-haspopup=\"listbox\"\n  [attr.aria-expanded]=\"open\"\n  (blur)=\"blur()\"\n  (click)=\"openListBox()\"\n  (keydown.arrowUp)=\"openListBox()\"\n  (keydown.arrowDown)=\"openListBox()\"\n  (keydown.space)=\"openListBox()\"\n  (keydown.enter)=\"openListBox()\"\n>\n  <span *ngIf=\"ariaLabel\" [id]=\"id + '-aria-label'\" class=\"visually-hidden\">{{ariaLabel}}</span>\n  <div\n    [id]=\"id + '-content'\"\n    [siAutoCollapsableList]=\"selectionStrategy.allowMultiple\"\n    class=\"d-flex align-items-center flex-grow-1\"\n  >\n    <div\n      *ngFor=\"let activeOption of selectedRows; let first = first; trackBy trackByItem\"\n      siAutoCollapsableListItem\n      class=\"d-flex align-items-center\"\n    >\n      <span *ngIf=\"!first\">,&nbsp;</span>\n      <ng-container\n        [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: activeOption }\"\n      ></ng-container>\n    </div>\n    <div *ngIf=\"selectionStrategy.allowMultiple\"\n         siAutoCollapsableListOverflowItem\n         #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    >\n      <div class=\"overflow-item\">\n        {{ overflowItem.hiddenItemCount }}+\n      </div>\n    </div>\n  </div>\n  <i class=\"dropdown-caret icon flip-rtl element-down-2\" aria-hidden=\"true\" [class.text-muted]=\"readonly\"></i>\n</div>\n\n<ng-template\n  cdkConnectedOverlay\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayHasBackdrop]=\"true\"\n  [cdkConnectedOverlayOrigin]=\"trigger\"\n  [cdkConnectedOverlayOpen]=\"open\"\n  [cdkConnectedOverlayFlexibleDimensions]=\"true\"\n  (backdropClick)=\"backdropClick()\"\n  (detach)=\"overlayDetach()\"\n>\n  <div\n    [id]=\"id + '-listbox'\"\n    class=\"dropdown-menu show position-static mh-100 overflow-auto\"\n    cdkListbox\n    [cdkListboxMultiple]=\"selectionStrategy.allowMultiple\"\n    [cdkListboxValue]=\"selectionStrategy.arrayValue\"\n    (cdkListboxValueChange)=\"listBoxValueChange($event)\"\n    [attr.aria-labelledby]=\"(id + '-aria-label') + ' ' + labelledby + ' ' + (id + '-content')\"\n    (keydown.tab)=\"open = false\"\n    (click)=\"closeListBoxIfSingle()\"\n    (keydown.enter)=\"closeListBoxIfSingle()\"\n    (keydown.space)=\"closeListBoxIfSingle()\"\n  >\n    <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n      <div\n        *ngIf=\"item.type === 'group'\"\n        role=\"group\"\n        class=\"ps-0\"\n        aria-disabled=\"false\"\n        [attr.aria-labelledby]=\"id + '-' + index + '-group-header'\"\n      >\n        <span class=\"dropdown-header\" [id]=\"id + '-' + index + '-group-header'\" role=\"presentation\">\n          <ng-container\n            [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n            [ngTemplateOutletContext]=\"{ $implicit: item }\"\n          ></ng-container>\n        </span>\n        <ng-container\n          *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n          [ngTemplateOutlet]=\"optionRowTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n        ></ng-container>\n      </div>\n      <ng-container\n        *ngIf=\"item.type === 'option'\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: item }\"\n      ></ng-container>\n\n    </ng-container>\n    <ng-template siSiSelectOptionRowTemplate let-option #optionRowTemplate>\n      <div\n        [cdkOption]=\"option.value\"\n        [cdkOptionDisabled]=\"option.disabled\"\n        [cdkOptionTypeaheadLabel]=\"option.typeaheadLabel! | translate\"\n        class=\"dropdown-item focus-inside pe-4\"\n        [class.active]=\"cdkOption.isActive()\"\n        [class.disabled]=\"option.disabled\"\n        [attr.data-id]=\"option.value\"\n        #cdkOption=\"cdkOption\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: option }\"\n        ></ng-container>\n        <span\n          *ngIf=\"cdkOption.isSelected()\"\n          class=\"ms-auto selected-marker\"\n          aria-hidden=\"true\"\n        >\n          <span class=\"element-ok\"></span>\n        </span>\n      </div>\n    </ng-template>\n  </div>\n</ng-template>\n\n<ng-template siSiSelectOptionTemplate let-item #defaultOptionTemplate>\n  <i\n    *ngIf=\"item.icon\"\n    class=\"icon me-2\"\n    aria-hidden=\"true\"\n    [ngClass]=\"[item.icon, (!item.disabled && item.color) || '']\"\n  ></i>\n  <span *ngIf=\"item.label\">{{ item.label | translate }}</span>\n</ng-template>\n\n<ng-template\n  siSiSelectGroupTemplate let-group\n  #defaultGroupTemplate\n>{{ (group.label ?? group.key) | translate }}</ng-template>\n", styles: [":host{display:inline-block;padding:0;block-size:2rem}:host:focus-within,:host.open{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.form-control .select{padding-block:3px}.select{font-weight:700;min-inline-size:72px;block-size:100%;color:var(--element-text-primary);padding-block:4px}:host:not(.form-control):not(.disabled) .select{cursor:pointer}:host:not(.form-control):not(.disabled) .select:hover{background:var(--element-base-1-hover)}:host:not(.form-control):not(.disabled) .select.active{background:var(--element-base-1-selected)}:host.readonly,:host.disabled{pointer-events:none}:host.disabled .select{color:var(--element-text-disabled)}:host .dropdown-item:not(.disabled){cursor:pointer}.selected-marker>span{font-size:1.5rem;margin-inline-end:-.25rem}.overflow-item{border-radius:var(--element-radius-3);background:var(--element-base-0);margin-inline-start:4px;padding-inline:8px}.dropdown-item.active:focus{background-color:var(--element-base-1-selected);box-shadow:none!important}\n"], dependencies: [{ kind: "directive", type: i1$2.CdkListbox, selector: "[cdkListbox]", inputs: ["id", "tabindex", "cdkListboxValue", "cdkListboxMultiple", "cdkListboxDisabled", "cdkListboxUseActiveDescendant", "cdkListboxOrientation", "cdkListboxCompareWith", "cdkListboxNavigationWrapDisabled", "cdkListboxNavigatesDisabledOptions"], outputs: ["cdkListboxValueChange"], exportAs: ["cdkListbox"] }, { kind: "directive", type: i1$2.CdkOption, selector: "[cdkOption]", inputs: ["id", "cdkOption", "cdkOptionTypeaheadLabel", "cdkOptionDisabled", "tabindex"], exportAs: ["cdkOption"] }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i3$1.CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }, { kind: "directive", type: i3$1.CdkOverlayOrigin, selector: "[cdk-overlay-origin], [overlay-origin], [cdkOverlayOrigin]", exportAs: ["cdkOverlayOrigin"] }, { kind: "directive", type: SiAutoCollapsableListDirective, selector: "[siAutoCollapsableList]", inputs: ["siAutoCollapsableList"], exportAs: ["siAutoCollapsableList"] }, { kind: "directive", type: SiAutoCollapsableListItemDirective, selector: "[siAutoCollapsableListItem]", exportAs: ["siAutoCollapsableListItem"] }, { kind: "directive", type: SiAutoCollapsableListOverflowItemDirective, selector: "[siAutoCollapsableListOverflowItem]", exportAs: ["siAutoCollapsableListOverflowItem"] }, { kind: "directive", type: SiSelectGroupTemplateDirective, selector: "[siSiSelectGroupTemplate]" }, { kind: "directive", type: SiSelectOptionRowTemplateDirective, selector: "[siSiSelectOptionRowTemplate]" }, { kind: "directive", type: SiSelectOptionTemplateDirective, selector: "[siSiSelectOptionTemplate]" }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  cdkOverlayOrigin\n  #trigger=\"cdkOverlayOrigin\"\n  role=\"combobox\"\n  class=\"select focus-none dropdown-toggle d-flex align-items-center ps-4 pe-5\"\n  [tabindex]=\"selectionStrategy.disabled ? '-1' : '0'\"\n  [attr.aria-labelledby]=\"(id + '-aria-label') + ' ' + labelledby + ' ' + (id + '-content')\"\n  [class.active]=\"open\"\n  [attr.aria-disabled]=\"selectionStrategy.disabled\"\n  [attr.aria-controls]=\"id + '-listbox'\"\n  aria-autocomplete=\"none\"\n  aria-haspopup=\"listbox\"\n  [attr.aria-expanded]=\"open\"\n  (blur)=\"blur()\"\n  (click)=\"openListBox()\"\n  (keydown.arrowUp)=\"openListBox()\"\n  (keydown.arrowDown)=\"openListBox()\"\n  (keydown.space)=\"openListBox()\"\n  (keydown.enter)=\"openListBox()\"\n>\n  <span *ngIf=\"ariaLabel\" [id]=\"id + '-aria-label'\" class=\"visually-hidden\">{{ariaLabel}}</span>\n  <div\n    [id]=\"id + '-content'\"\n    [siAutoCollapsableList]=\"selectionStrategy.allowMultiple\"\n    class=\"d-flex align-items-center flex-grow-1\"\n  >\n    <div\n      *ngFor=\"let activeOption of selectedRows; let first = first; trackBy trackByItem\"\n      siAutoCollapsableListItem\n      class=\"d-flex align-items-center\"\n    >\n      <span *ngIf=\"!first\">,&nbsp;</span>\n      <ng-container\n        [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: activeOption }\"\n      ></ng-container>\n    </div>\n    <div *ngIf=\"selectionStrategy.allowMultiple\"\n         siAutoCollapsableListOverflowItem\n         #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    >\n      <div class=\"overflow-item\">\n        {{ overflowItem.hiddenItemCount }}+\n      </div>\n    </div>\n  </div>\n  <i class=\"dropdown-caret icon flip-rtl element-down-2\" aria-hidden=\"true\" [class.text-muted]=\"readonly\"></i>\n</div>\n\n<ng-template\n  cdkConnectedOverlay\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayHasBackdrop]=\"true\"\n  [cdkConnectedOverlayOrigin]=\"trigger\"\n  [cdkConnectedOverlayOpen]=\"open\"\n  [cdkConnectedOverlayFlexibleDimensions]=\"true\"\n  (backdropClick)=\"backdropClick()\"\n  (detach)=\"overlayDetach()\"\n>\n  <div\n    [id]=\"id + '-listbox'\"\n    class=\"dropdown-menu show position-static mh-100 overflow-auto\"\n    cdkListbox\n    [cdkListboxMultiple]=\"selectionStrategy.allowMultiple\"\n    [cdkListboxValue]=\"selectionStrategy.arrayValue\"\n    (cdkListboxValueChange)=\"listBoxValueChange($event)\"\n    [attr.aria-labelledby]=\"(id + '-aria-label') + ' ' + labelledby + ' ' + (id + '-content')\"\n    (keydown.tab)=\"open = false\"\n    (click)=\"closeListBoxIfSingle()\"\n    (keydown.enter)=\"closeListBoxIfSingle()\"\n    (keydown.space)=\"closeListBoxIfSingle()\"\n  >\n    <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n      <div\n        *ngIf=\"item.type === 'group'\"\n        role=\"group\"\n        class=\"ps-0\"\n        aria-disabled=\"false\"\n        [attr.aria-labelledby]=\"id + '-' + index + '-group-header'\"\n      >\n        <span class=\"dropdown-header\" [id]=\"id + '-' + index + '-group-header'\" role=\"presentation\">\n          <ng-container\n            [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n            [ngTemplateOutletContext]=\"{ $implicit: item }\"\n          ></ng-container>\n        </span>\n        <ng-container\n          *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n          [ngTemplateOutlet]=\"optionRowTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n        ></ng-container>\n      </div>\n      <ng-container\n        *ngIf=\"item.type === 'option'\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: item }\"\n      ></ng-container>\n\n    </ng-container>\n    <ng-template siSiSelectOptionRowTemplate let-option #optionRowTemplate>\n      <div\n        [cdkOption]=\"option.value\"\n        [cdkOptionDisabled]=\"option.disabled\"\n        [cdkOptionTypeaheadLabel]=\"option.typeaheadLabel! | translate\"\n        class=\"dropdown-item focus-inside pe-4\"\n        [class.active]=\"cdkOption.isActive()\"\n        [class.disabled]=\"option.disabled\"\n        [attr.data-id]=\"option.value\"\n        #cdkOption=\"cdkOption\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: option }\"\n        ></ng-container>\n        <span\n          *ngIf=\"cdkOption.isSelected()\"\n          class=\"ms-auto selected-marker\"\n          aria-hidden=\"true\"\n        >\n          <span class=\"element-ok\"></span>\n        </span>\n      </div>\n    </ng-template>\n  </div>\n</ng-template>\n\n<ng-template siSiSelectOptionTemplate let-item #defaultOptionTemplate>\n  <i\n    *ngIf=\"item.icon\"\n    class=\"icon me-2\"\n    aria-hidden=\"true\"\n    [ngClass]=\"[item.icon, (!item.disabled && item.color) || '']\"\n  ></i>\n  <span *ngIf=\"item.label\">{{ item.label | translate }}</span>\n</ng-template>\n\n<ng-template\n  siSiSelectGroupTemplate let-group\n  #defaultGroupTemplate\n>{{ (group.label ?? group.key) | translate }}</ng-template>\n", styles: [":host{display:inline-block;padding:0;block-size:2rem}:host:focus-within,:host.open{box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.form-control .select{padding-block:3px}.select{font-weight:700;min-inline-size:72px;block-size:100%;color:var(--element-text-primary);padding-block:4px}:host:not(.form-control):not(.disabled) .select{cursor:pointer}:host:not(.form-control):not(.disabled) .select:hover{background:var(--element-base-1-hover)}:host:not(.form-control):not(.disabled) .select.active{background:var(--element-base-1-selected)}:host.readonly,:host.disabled{pointer-events:none}:host.disabled .select{color:var(--element-text-disabled)}:host .dropdown-item:not(.disabled){cursor:pointer}.selected-marker>span{font-size:1.5rem;margin-inline-end:-.25rem}.overflow-item{border-radius:var(--element-radius-3);background:var(--element-base-0);margin-inline-start:4px;padding-inline:8px}.dropdown-item.active:focus{background-color:var(--element-base-1-selected);box-shadow:none!important}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { classDropdown: [{
                type: HostBinding,
                args: ['class.dropdown']
            }], id: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], labelledby: [{
                type: Input
            }], readonly: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.readonly']
            }], dropdownClose: [{
                type: Output
            }], optionTemplate: [{
                type: ContentChild,
                args: [SiSelectOptionTemplateDirective, { read: TemplateRef }]
            }], groupTemplate: [{
                type: ContentChild,
                args: [SiSelectGroupTemplateDirective, { read: TemplateRef }]
            }], open: [{
                type: HostBinding,
                args: ['class.open']
            }], listbox: [{
                type: ViewChild,
                args: [CdkListbox, { read: ElementRef }]
            }], trigger: [{
                type: ViewChild,
                args: [CdkOverlayOrigin, { read: ElementRef, static: true }]
            }] } });

/**
 * The directive allows to pass SelectOption options.
 * Otherwise use the {@link SiSelectComplexOptionsDirective} directive.
 *
 * @example
 * <pre><code>
 * <si-select [options]="[{ id: 'one', title: 'One' }, { id: 'two', title: 'Two' }]"></si-select>
 * </code></pre>
 */
class SiSelectSimpleOptionsDirective extends SiSelectOptionsStrategy {
    constructor() {
        super(...arguments);
        this.trackBy = buildTrackByIdentity();
        this.optionEqualCheckFn = (a, b) => a === b;
    }
    ngOnChanges(changes) {
        this.optionsSubject$.next(this.options.map(option => ({
            type: 'option',
            value: option.id,
            label: option.title,
            color: option.color,
            icon: option.icon,
            disabled: option.disabled
        })));
    }
}
SiSelectSimpleOptionsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSimpleOptionsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiSelectSimpleOptionsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectSimpleOptionsDirective, selector: "si-select[options]", inputs: { options: "options" }, providers: [{ provide: SiSelectOptionsStrategy, useExisting: SiSelectSimpleOptionsDirective }], usesInheritance: true, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSimpleOptionsDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[options]',
                    providers: [{ provide: SiSelectOptionsStrategy, useExisting: SiSelectSimpleOptionsDirective }]
                }]
        }], propDecorators: { options: [{
                type: Input
            }] } });

/**
 * The directive allows to pass custom options.
 * Otherwise use the {@link SiSelectSimpleOptionsDirective} directive.
 *
 * @example
 * <pre><code>
 * <si-select [complexOptions]="{ g1: ['g1.i1', 'g1.i2'], g2: ['g2.i1']}"></si-select>
 * </code></pre>
 */
class SiSelectComplexOptionsDirective extends SiSelectOptionsStrategy {
    constructor() {
        super(...arguments);
        this.trackBy = buildTrackByIdentity();
        /**
         * By default, values are check on equality by reference. Override to customize the behavior.
         */
        this.optionEqualCheckFn = (a, b) => a === b;
        /**
         * The valueProvider is used to extract the display text of a value.
         */
        this.valueProvider = () => undefined;
        /**
         * Provides Value for the display text of the dropdown group
         */
        this.groupProvider = () => undefined;
        /**
         * The disabledProvider is used to display menu items as disabled.
         */
        this.disabledProvider = () => false;
    }
    ngOnChanges(changes) {
        if (this.complexOptions instanceof Array) {
            this.optionsSubject$.next(this.convertOptionsArray(this.complexOptions));
        }
        else if (this.complexOptions) {
            this.optionsSubject$.next(Object.entries(this.complexOptions).map(([key, value]) => ({
                type: 'group',
                key,
                label: this.groupProvider(key) ?? key,
                options: this.convertOptionsArray(value)
            })));
        }
    }
    convertOptionsArray(options) {
        return options.map(option => ({
            type: 'option',
            value: option,
            label: this.valueProvider(option) ?? option + '',
            typeaheadLabel: this.valueProvider(option),
            disabled: this.disabledProvider(option)
        }));
    }
}
SiSelectComplexOptionsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectComplexOptionsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiSelectComplexOptionsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectComplexOptionsDirective, selector: "si-select[complexOptions]", inputs: { complexOptions: "complexOptions", trackBy: "trackBy", optionEqualCheckFn: "optionEqualCheckFn", valueProvider: "valueProvider", groupProvider: "groupProvider", disabledProvider: "disabledProvider" }, providers: [{ provide: SiSelectOptionsStrategy, useExisting: SiSelectComplexOptionsDirective }], usesInheritance: true, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectComplexOptionsDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[complexOptions]',
                    providers: [{ provide: SiSelectOptionsStrategy, useExisting: SiSelectComplexOptionsDirective }]
                }]
        }], propDecorators: { complexOptions: [{
                type: Input
            }], trackBy: [{
                type: Input
            }], optionEqualCheckFn: [{
                type: Input
            }], valueProvider: [{
                type: Input
            }], groupProvider: [{
                type: Input
            }], disabledProvider: [{
                type: Input
            }] } });

/**
 * The directive enables the single-select behavior.
 * Otherwise use the {@link SiSelectMultiValueDirective} directive.
 *
 * @example
 * <pre><code>
 * <si-select [(value)]="selectedValue" [options]="[
 *  { id: 'good', title: 'Good' },
 *  { id: 'average', title: 'Average' },
 *  { id: 'poor', title: 'Poor' }
 * ]"></si-select>
 * </code></pre>
 */
class SiSelectSingleValueDirective extends SiSelectSelectionStrategy {
    constructor() {
        super(...arguments);
        this.allowMultiple = false;
        /** Emitted when the selection is changed */
        this.valueChange = new EventEmitter();
    }
    /**
     * Set selected value
     */
    set value(value) {
        // Never call valueChange.emit on an input, which causes an ExpressionChangedAfterItHasBeenCheckedError
        if (this._value !== value) {
            this._value = value;
            this.selectionChange$.next();
            this.onChange(this._value);
        }
    }
    get arrayValue() {
        return this._value ? [this._value] : [];
    }
    updateArrayValue([value], triggerByUser) {
        this._value = value;
        if (triggerByUser) {
            this.onChange(this._value);
            this.selectionChange$.next();
            this.valueChange.emit(this._value);
        }
    }
    writeValue(obj) {
        this.value = obj;
        this.valueChange.emit(this._value);
    }
}
SiSelectSingleValueDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSingleValueDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiSelectSingleValueDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectSingleValueDirective, selector: "si-select:not([multi])", inputs: { value: "value" }, outputs: { valueChange: "valueChange" }, providers: [
        { provide: SiSelectSelectionStrategy, useExisting: SiSelectSingleValueDirective },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiSelectSingleValueDirective,
            multi: true
        }
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectSingleValueDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select:not([multi])',
                    providers: [
                        { provide: SiSelectSelectionStrategy, useExisting: SiSelectSingleValueDirective },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSelectSingleValueDirective,
                            multi: true
                        }
                    ]
                }]
        }], propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

/**
 * The directive enables the multi-select behavior.
 * Otherwise use the {@link SiSelectSingleValueDirective} directive.
 *
 * @example
 * <pre><code>
 * <si-select multi [(value)]="multiValue" [options]="[
 *  { id: 'good', title: 'Good' },
 *  { id: 'average', title: 'Average' },
 *  { id: 'poor', title: 'Poor' }
 * ]"></si-select>
 * </code></pre>
 */
class SiSelectMultiValueDirective extends SiSelectSelectionStrategy {
    constructor() {
        super(...arguments);
        this._value = [];
        this.allowMultiple = true;
        /** Emitted when the selection is changed */
        this.valueChange = new EventEmitter();
    }
    /**
     * Set selected values
     */
    set value(value) {
        // Never call valueChange.emit on an input, which causes an ExpressionChangedAfterItHasBeenCheckedError
        if (this._value !== value) {
            this._value = value ?? [];
            this.selectionChange$.next();
            this.onChange(this._value);
        }
    }
    get arrayValue() {
        return this._value;
    }
    updateArrayValue(value, triggerByUser) {
        this._value = value;
        if (triggerByUser) {
            this.onChange(this._value);
            this.selectionChange$.next();
            this.valueChange.emit(this._value);
        }
    }
    writeValue(obj) {
        this.value = obj;
        this.valueChange.emit(this._value);
    }
}
SiSelectMultiValueDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectMultiValueDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SiSelectMultiValueDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiSelectMultiValueDirective, selector: "si-select[multi]", inputs: { value: "value" }, outputs: { valueChange: "valueChange" }, providers: [
        { provide: SiSelectSelectionStrategy, useExisting: SiSelectMultiValueDirective },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SiSelectMultiValueDirective,
            multi: true
        }
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectMultiValueDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[multi]',
                    providers: [
                        { provide: SiSelectSelectionStrategy, useExisting: SiSelectMultiValueDirective },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSelectMultiValueDirective,
                            multi: true
                        }
                    ]
                }]
        }], propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class SiSelectModule {
}
SiSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiSelectModule, declarations: [SiSelectComplexOptionsDirective,
        SiSelectComponent,
        SiSelectGroupTemplateDirective,
        SiSelectMultiValueDirective,
        SiSelectOptionRowTemplateDirective,
        SiSelectOptionTemplateDirective,
        SiSelectSimpleOptionsDirective,
        SiSelectSingleValueDirective], imports: [CdkListboxModule,
        CommonModule,
        OverlayModule,
        SiAutoCollapsableListModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiTranslateModule], exports: [SiSelectComplexOptionsDirective,
        SiSelectComponent, SiSelectGroupTemplateDirective,
        SiSelectMultiValueDirective,
        SiSelectOptionTemplateDirective,
        SiSelectSimpleOptionsDirective,
        SiSelectSingleValueDirective] });
SiSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectModule, imports: [CdkListboxModule,
        CommonModule,
        OverlayModule,
        SiAutoCollapsableListModule,
        SiDropdownModule,
        SiListInteractionModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CdkListboxModule,
                        CommonModule,
                        OverlayModule,
                        SiAutoCollapsableListModule,
                        SiDropdownModule,
                        SiListInteractionModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiSelectComplexOptionsDirective,
                        SiSelectComponent,
                        SiSelectGroupTemplateDirective,
                        SiSelectMultiValueDirective,
                        SiSelectOptionRowTemplateDirective,
                        SiSelectOptionTemplateDirective,
                        SiSelectSimpleOptionsDirective,
                        SiSelectSingleValueDirective
                    ],
                    exports: [
                        SiSelectComplexOptionsDirective,
                        SiSelectComponent, SiSelectGroupTemplateDirective,
                        SiSelectMultiValueDirective,
                        SiSelectOptionTemplateDirective,
                        SiSelectSimpleOptionsDirective,
                        SiSelectSingleValueDirective
                    ]
                }]
        }] });

class SiThresholdComponent {
    constructor() {
        /** Options to be shown in select dropdown */
        this.options = [];
        /** The thresholds */
        this.thresholdSteps = [];
        /** The unit to show */
        this.unit = '';
        /** The min. value for the threshold value */
        this.minValue = 0;
        /** The max. value for the threshold value */
        this.maxValue = 100;
        /** The step size for the threshold value */
        this.stepSize = 1;
        /** Max. number of steps, 0 for no hard limit */
        this.maxSteps = 0;
        /** Do validation? */
        this.validation = true;
        /** When disabled, steps cannot be added/removed */
        this.canAddRemoveSteps = true;
        /** Use horizontal layout? */
        this.horizontalLayout = false;
        /** Show dec/inc buttons? */
        this.showDecIncButtons = true;
        /** The obvious */
        this.readonly = false;
        /** The aria-label for delete button */
        this.deleteAriaLabel = 'delete step';
        /** The aria-label for add button */
        this.addAriaLabel = 'add step';
        /** The aria-label for input field */
        this.inputAriaLabel = 'threshold value';
        /** The aria-label for status selection */
        this.statusAriaLabel = 'status';
        /** Fired when the thresholds have changed */
        this.thresholdStepsChange = new EventEmitter();
        /** Fired when validation status changes */
        this.validChange = new EventEmitter();
        this.trackByItem = (index, item) => item;
        this.colors = [];
        this.valid = true;
        this.element = inject(ElementRef);
    }
    ngOnChanges() {
        this.calcColor();
        this.validate();
    }
    deleteStep(index) {
        this.thresholdSteps.splice(index, 1);
        this.optionChanged();
    }
    addStep(index) {
        const newStep = { ...this.thresholdSteps[index], value: undefined };
        this.thresholdSteps.splice(index + 1, 0, newStep);
        this.optionChanged();
        setTimeout(() => this.element.nativeElement.querySelectorAll('input.form-control')[index]?.focus());
    }
    optionChanged() {
        this.calcColor();
        this.emitChange();
    }
    emitChange() {
        this.validate();
        this.thresholdStepsChange.emit(this.thresholdSteps);
    }
    calcColor() {
        const colorMap = new Map();
        for (const opt of this.options) {
            colorMap.set(opt.id, opt.color ?? '');
        }
        this.colors = this.thresholdSteps.map(ths => colorMap.get(ths.optionValue) ?? '');
    }
    validate() {
        const prevValid = this.valid;
        this.valid = true;
        for (let i = 1; i < this.thresholdSteps.length; i++) {
            const step = this.thresholdSteps[i];
            if (this.validation) {
                const prev = this.thresholdSteps[i - 1];
                const next = this.thresholdSteps[i + 1];
                // valid: withing min/max, each step is lower than next step with step size between
                step.valid = step.value != null && step.value >= this.minValue && step.value <= this.maxValue
                    && (prev.value == null || step.value - this.stepSize >= prev.value)
                    && (next?.value == null || step.value + this.stepSize <= next.value);
                this.valid && (this.valid = step.valid);
            }
            else {
                step.valid = true;
            }
        }
        if (this.valid !== prevValid) {
            this.validChange.emit(this.valid);
        }
    }
}
SiThresholdComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SiThresholdComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiThresholdComponent, selector: "si-threshold", inputs: { options: "options", thresholdSteps: "thresholdSteps", unit: "unit", minValue: "minValue", maxValue: "maxValue", stepSize: "stepSize", maxSteps: "maxSteps", validation: "validation", canAddRemoveSteps: "canAddRemoveSteps", horizontalLayout: "horizontalLayout", showDecIncButtons: "showDecIncButtons", readonly: "readonly", deleteAriaLabel: "deleteAriaLabel", addAriaLabel: "addAriaLabel", inputAriaLabel: "inputAriaLabel", statusAriaLabel: "statusAriaLabel" }, outputs: { thresholdStepsChange: "thresholdStepsChange", validChange: "validChange" }, host: { properties: { "class.add-remove": "this.canAddRemoveSteps", "class.horizontal": "this.horizontalLayout", "class.dec-inc-buttons": "this.showDecIncButtons" } }, usesOnChanges: true, ngImport: i0, template: "<div\n  *ngFor=\"let step of thresholdSteps; first as first; last as last; index as index; trackBy: trackByItem\"\n  class=\"ths-step\"\n>\n  <div *ngIf=\"!first\" class=\"ths-value d-flex align-items-center\">\n    <button\n      *ngIf=\"canAddRemoveSteps && !readonly\"\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-ghost element-delete m-4\"\n      [attr.aria-label]=\"deleteAriaLabel | translate\"\n      (click)=\"deleteStep(index)\"\n    ></button>\n    <div *ngIf=\"!canAddRemoveSteps || readonly\" class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\">\n      <div class=\"segment\" [ngClass]=\"colors[index - 1]\"></div>\n      <div class=\"dot\"></div>\n      <div class=\"segment\" [ngClass]=\"colors[index]\"></div>\n    </div>\n    <div class=\"d-flex align-items-center text-nowrap m-4\">\n      <si-number-input\n        class=\"form-control text-end\"\n        [class.is-invalid]=\"step.valid === false\"\n        [aria-label]=\"inputAriaLabel | translate\"\n        [readonly]=\"readonly\"\n        [(ngModel)]=\"step.value\"\n        [ngModelOptions]=\"{ standalone: true }\"\n        #valueInput=\"ngModel\"\n        [min]=\"minValue\"\n        [max]=\"maxValue\"\n        [step]=\"stepSize\"\n        [unit]=\"unit\"\n        [showButtons]=\"showDecIncButtons\"\n        [required]=\"true\"\n        (ngModelChange)=\"emitChange()\"\n      ></si-number-input>\n    </div>\n  </div>\n  <ng-container\n    *ngIf=\"first && horizontalLayout\"\n    [ngTemplateOutlet]=\"startend\"\n    [ngTemplateOutletContext]=\"{ color: colors[index] }\">\n  </ng-container>\n  <div class=\"ths-option d-flex align-items-center\">\n    <button\n      *ngIf=\"canAddRemoveSteps && !readonly\"\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-secondary element-plus m-4\"\n      [disabled]=\"maxSteps && thresholdSteps.length >= maxSteps\"\n      [attr.aria-label]=\"addAriaLabel | translate\"\n      (click)=\"addStep(index)\"\n    ></button>\n    <div *ngIf=\"!canAddRemoveSteps || readonly\" class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\" [ngClass]=\"colors[index]\">\n      <div class=\"segment\"></div>\n    </div>\n    <si-select\n      [ariaLabel]=\"statusAriaLabel\"\n      class=\"my-4\"\n      [(value)]=\"step.optionValue\"\n      [readonly]=\"readonly\"\n      (valueChange)=\"optionChanged()\"\n      [options]=\"options\"\n    ></si-select>\n  </div>\n  <ng-container\n    *ngIf=\"last && horizontalLayout\"\n    [ngTemplateOutlet]=\"startend\"\n    [ngTemplateOutletContext]=\"{ color: colors[index] }\">\n  </ng-container>\n</div>\n\n<ng-template #startend let-color=\"color\">\n  <div class=\"ths-value d-flex align-items-center startend\">\n    <div class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\">\n      <div class=\"segment\" [ngClass]=\"color\"></div>\n    </div>\n    <div class=\"py-3 my-4\">&#8203;</div>\n  </div>\n</ng-template>\n", styles: [":host{--direction-main: column;--direction-option: row;--direction-value: row;--input-width: calc(var(--input-base-width, 90px) + var(--buttons-width, 0px))}:host.horizontal{--direction-main: row;--direction-option: column;--direction-value: column-reverse;--value-margin: -24px;padding-inline:calc(var(--value-margin) * -1)}:host.dec-inc-buttons{--buttons-width: 48px}:host,.ths-step{display:flex;flex-direction:var(--direction-main)}.ths-option{flex-direction:var(--direction-option);min-inline-size:var(--option-width, 160px)}.ths-value{flex-direction:var(--direction-value);margin-inline:var(--value-margin)}.form-control{inline-size:var(--input-width);block-size:calc(1rem + 16px)}.line{align-self:stretch;flex-basis:16px;display:flex;flex-direction:var(--direction-main);align-items:center;color:var(--element-ui-2);gap:4px}.segment{flex:1;border:1px solid currentColor}.dot{inline-size:6px;block-size:6px;border:1px solid var(--element-ui-2);border-radius:50%}.startend{inline-size:calc(var(--input-width) / 2)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: SiNumberInputComponent, selector: "si-number-input", inputs: ["min", "max", "step", "value", "unit", "showButtons", "aria-label", "inputId", "disabled", "readonly", "required"], outputs: ["valueChange"] }, { kind: "component", type: SiSelectComponent, selector: "si-select", inputs: ["id", "ariaLabel", "labelledby", "readonly"], outputs: ["dropdownClose"] }, { kind: "directive", type: SiSelectSimpleOptionsDirective, selector: "si-select[options]", inputs: ["options"] }, { kind: "directive", type: SiSelectSingleValueDirective, selector: "si-select:not([multi])", inputs: ["value"], outputs: ["valueChange"] }, { kind: "pipe", type: SiTranslatePipe, name: "translate" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-threshold', template: "<div\n  *ngFor=\"let step of thresholdSteps; first as first; last as last; index as index; trackBy: trackByItem\"\n  class=\"ths-step\"\n>\n  <div *ngIf=\"!first\" class=\"ths-value d-flex align-items-center\">\n    <button\n      *ngIf=\"canAddRemoveSteps && !readonly\"\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-ghost element-delete m-4\"\n      [attr.aria-label]=\"deleteAriaLabel | translate\"\n      (click)=\"deleteStep(index)\"\n    ></button>\n    <div *ngIf=\"!canAddRemoveSteps || readonly\" class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\">\n      <div class=\"segment\" [ngClass]=\"colors[index - 1]\"></div>\n      <div class=\"dot\"></div>\n      <div class=\"segment\" [ngClass]=\"colors[index]\"></div>\n    </div>\n    <div class=\"d-flex align-items-center text-nowrap m-4\">\n      <si-number-input\n        class=\"form-control text-end\"\n        [class.is-invalid]=\"step.valid === false\"\n        [aria-label]=\"inputAriaLabel | translate\"\n        [readonly]=\"readonly\"\n        [(ngModel)]=\"step.value\"\n        [ngModelOptions]=\"{ standalone: true }\"\n        #valueInput=\"ngModel\"\n        [min]=\"minValue\"\n        [max]=\"maxValue\"\n        [step]=\"stepSize\"\n        [unit]=\"unit\"\n        [showButtons]=\"showDecIncButtons\"\n        [required]=\"true\"\n        (ngModelChange)=\"emitChange()\"\n      ></si-number-input>\n    </div>\n  </div>\n  <ng-container\n    *ngIf=\"first && horizontalLayout\"\n    [ngTemplateOutlet]=\"startend\"\n    [ngTemplateOutletContext]=\"{ color: colors[index] }\">\n  </ng-container>\n  <div class=\"ths-option d-flex align-items-center\">\n    <button\n      *ngIf=\"canAddRemoveSteps && !readonly\"\n      type=\"button\"\n      class=\"btn btn-circle btn-sm btn-secondary element-plus m-4\"\n      [disabled]=\"maxSteps && thresholdSteps.length >= maxSteps\"\n      [attr.aria-label]=\"addAriaLabel | translate\"\n      (click)=\"addStep(index)\"\n    ></button>\n    <div *ngIf=\"!canAddRemoveSteps || readonly\" class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\" [ngClass]=\"colors[index]\">\n      <div class=\"segment\"></div>\n    </div>\n    <si-select\n      [ariaLabel]=\"statusAriaLabel\"\n      class=\"my-4\"\n      [(value)]=\"step.optionValue\"\n      [readonly]=\"readonly\"\n      (valueChange)=\"optionChanged()\"\n      [options]=\"options\"\n    ></si-select>\n  </div>\n  <ng-container\n    *ngIf=\"last && horizontalLayout\"\n    [ngTemplateOutlet]=\"startend\"\n    [ngTemplateOutletContext]=\"{ color: colors[index] }\">\n  </ng-container>\n</div>\n\n<ng-template #startend let-color=\"color\">\n  <div class=\"ths-value d-flex align-items-center startend\">\n    <div class=\"py-3 my-4\">&#8203;</div>\n    <div class=\"line\">\n      <div class=\"segment\" [ngClass]=\"color\"></div>\n    </div>\n    <div class=\"py-3 my-4\">&#8203;</div>\n  </div>\n</ng-template>\n", styles: [":host{--direction-main: column;--direction-option: row;--direction-value: row;--input-width: calc(var(--input-base-width, 90px) + var(--buttons-width, 0px))}:host.horizontal{--direction-main: row;--direction-option: column;--direction-value: column-reverse;--value-margin: -24px;padding-inline:calc(var(--value-margin) * -1)}:host.dec-inc-buttons{--buttons-width: 48px}:host,.ths-step{display:flex;flex-direction:var(--direction-main)}.ths-option{flex-direction:var(--direction-option);min-inline-size:var(--option-width, 160px)}.ths-value{flex-direction:var(--direction-value);margin-inline:var(--value-margin)}.form-control{inline-size:var(--input-width);block-size:calc(1rem + 16px)}.line{align-self:stretch;flex-basis:16px;display:flex;flex-direction:var(--direction-main);align-items:center;color:var(--element-ui-2);gap:4px}.segment{flex:1;border:1px solid currentColor}.dot{inline-size:6px;block-size:6px;border:1px solid var(--element-ui-2);border-radius:50%}.startend{inline-size:calc(var(--input-width) / 2)}\n"] }]
        }], propDecorators: { options: [{
                type: Input
            }], thresholdSteps: [{
                type: Input
            }], unit: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], stepSize: [{
                type: Input
            }], maxSteps: [{
                type: Input
            }], validation: [{
                type: Input
            }], canAddRemoveSteps: [{
                type: HostBinding,
                args: ['class.add-remove']
            }, {
                type: Input
            }], horizontalLayout: [{
                type: HostBinding,
                args: ['class.horizontal']
            }, {
                type: Input
            }], showDecIncButtons: [{
                type: HostBinding,
                args: ['class.dec-inc-buttons']
            }, {
                type: Input
            }], readonly: [{
                type: Input
            }], deleteAriaLabel: [{
                type: Input
            }], addAriaLabel: [{
                type: Input
            }], inputAriaLabel: [{
                type: Input
            }], statusAriaLabel: [{
                type: Input
            }], thresholdStepsChange: [{
                type: Output
            }], validChange: [{
                type: Output
            }] } });

class SiThresholdModule {
}
SiThresholdModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiThresholdModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdModule, declarations: [SiThresholdComponent], imports: [CommonModule,
        FormsModule,
        SiNumberInputModule,
        SiSelectModule,
        SiTranslateModule], exports: [SiThresholdComponent] });
SiThresholdModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdModule, imports: [CommonModule,
        FormsModule,
        SiNumberInputModule,
        SiSelectModule,
        SiTranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiThresholdModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SiNumberInputModule,
                        SiSelectModule,
                        SiTranslateModule
                    ],
                    declarations: [
                        SiThresholdComponent
                    ],
                    exports: [SiThresholdComponent]
                }]
        }] });

/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-redeclare */
const DateFormat = {
    DF_DDMMYYYY_DOT: 'dd.mm.yyyy',
    DF_DDMMYYYY_FSLASH: 'dd/mm/yyyy',
    DF_DDMMYYYY_DASH: 'dd-mm-yyyy',
    DF_YYYYMMDD_DOT: 'yyyy.mm.dd',
    DF_YYYYMMDD_FSLASH: 'yyyy/mm/dd',
    DF_YYYYMMDD_DASH: 'yyyy-mm-dd',
    DF_MMDDYYYY_DASH: 'mm-dd-yyyy',
    DF_MMDDYYYY_FSLASH: 'mm/dd/yyyy'
};

/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-redeclare */
const TimeFormat = {
    TWENTY_FOUR: 'hh:mm:ss',
    TWELVE: 'hh:mm:ss tt'
};
var MeridianDesignation;
(function (MeridianDesignation) {
    MeridianDesignation[MeridianDesignation["AM"] = 0] = "AM";
    MeridianDesignation[MeridianDesignation["PM"] = 1] = "PM";
})(MeridianDesignation || (MeridianDesignation = {}));

// -------------------------------------------------------------------------------

class SimplElementNgModule {
}
SimplElementNgModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SimplElementNgModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SimplElementNgModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SimplElementNgModule, exports: [SiAboutModule,
        SiAccordionModule,
        SiActionDialogModule,
        SiBreadcrumbModule,
        SiCardModule,
        SiCircleStatusModule,
        SiColumnSelectionModalModule,
        SiComfortScoreModule,
        SiCommissioningStatusModule,
        SiConnectionStrengthModule,
        SiContentActionBarModule,
        SiDatatableModule,
        SiDatepickerModule,
        SiDropdownModule,
        SiElectrontitlebarModule,
        SiEmptyStateModule,
        SiFileUploaderModule,
        SiFilterBarModule,
        SiFilteredSearchModule,
        SiFooterModule,
        SiFormModule,
        SiIconModule,
        SiInlineNotificationModule,
        SiLandingPageModule,
        SiLanguageSwitcherModule,
        SiLinkModule,
        SiListInteractionModule,
        SiLoadingSpinnerModule,
        SiLocalizationModule,
        SiMasterDetailContainerModule,
        SiMenuModule,
        SiModalModule,
        SiNavbarModule,
        SiNavbarSecondaryModule,
        SiNavbarVerticalModule,
        SiNumberInputModule,
        SiPaginationModule,
        SiPasswordStrengthModule,
        SiPopoverModule,
        SiProgressbarModule,
        SiPromptDialogButtonsModule,
        SiResizeObserverModule,
        SiResultDetailsListModule,
        SiRightPanelModule,
        SiSearchBarModule,
        SiSelectModule,
        SiSidePanelModule,
        SiSliderModule,
        SiSortBarModule,
        SiSplitModule,
        SiStatusBarModule,
        SiSwitchModule,
        SiTabsModule,
        SiThresholdModule,
        SiToastNotificationModule,
        SiTooltipModule,
        SiTreeViewModule,
        SiTypeaheadModule,
        SiUnauthorizedPageModule,
        SiWizardModule] });
SimplElementNgModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SimplElementNgModule, imports: [SiAboutModule,
        SiAccordionModule,
        SiActionDialogModule,
        SiBreadcrumbModule,
        SiCardModule,
        SiCircleStatusModule,
        SiColumnSelectionModalModule,
        SiComfortScoreModule,
        SiCommissioningStatusModule,
        SiConnectionStrengthModule,
        SiContentActionBarModule,
        SiDatatableModule,
        SiDatepickerModule,
        SiDropdownModule,
        SiElectrontitlebarModule,
        SiEmptyStateModule,
        SiFileUploaderModule,
        SiFilterBarModule,
        SiFilteredSearchModule,
        SiFooterModule,
        SiFormModule,
        SiIconModule,
        SiInlineNotificationModule,
        SiLandingPageModule,
        SiLanguageSwitcherModule,
        SiLinkModule,
        SiListInteractionModule,
        SiLoadingSpinnerModule,
        SiLocalizationModule,
        SiMasterDetailContainerModule,
        SiMenuModule,
        SiModalModule,
        SiNavbarModule,
        SiNavbarSecondaryModule,
        SiNavbarVerticalModule,
        SiNumberInputModule,
        SiPaginationModule,
        SiPasswordStrengthModule,
        SiPopoverModule,
        SiProgressbarModule,
        SiPromptDialogButtonsModule,
        SiResizeObserverModule,
        SiResultDetailsListModule,
        SiRightPanelModule,
        SiSearchBarModule,
        SiSelectModule,
        SiSidePanelModule,
        SiSliderModule,
        SiSortBarModule,
        SiSplitModule,
        SiStatusBarModule,
        SiSwitchModule,
        SiTabsModule,
        SiThresholdModule,
        SiToastNotificationModule,
        SiTooltipModule,
        SiTreeViewModule,
        SiTypeaheadModule,
        SiUnauthorizedPageModule,
        SiWizardModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SimplElementNgModule, decorators: [{
            type: NgModule,
            args: [{
                    // Since this a library, which does not have any pages, imports are not needed
                    exports: [
                        SiAboutModule,
                        SiAccordionModule,
                        SiActionDialogModule,
                        SiBreadcrumbModule,
                        SiCardModule,
                        SiCircleStatusModule,
                        SiColumnSelectionModalModule,
                        SiComfortScoreModule,
                        SiCommissioningStatusModule,
                        SiConnectionStrengthModule,
                        SiContentActionBarModule,
                        SiDatatableModule,
                        SiDatepickerModule,
                        SiDropdownModule,
                        SiElectrontitlebarModule,
                        SiEmptyStateModule,
                        SiFileUploaderModule,
                        SiFilterBarModule,
                        SiFilteredSearchModule,
                        SiFooterModule,
                        SiFormModule,
                        SiIconModule,
                        SiInlineNotificationModule,
                        SiLandingPageModule,
                        SiLanguageSwitcherModule,
                        SiLinkModule,
                        SiListInteractionModule,
                        SiLoadingSpinnerModule,
                        SiLocalizationModule,
                        SiMasterDetailContainerModule,
                        SiMenuModule,
                        SiModalModule,
                        SiNavbarModule,
                        SiNavbarSecondaryModule,
                        SiNavbarVerticalModule,
                        SiNumberInputModule,
                        SiPaginationModule,
                        SiPasswordStrengthModule,
                        SiPopoverModule,
                        SiProgressbarModule,
                        SiPromptDialogButtonsModule,
                        SiResizeObserverModule,
                        SiResultDetailsListModule,
                        SiRightPanelModule,
                        SiSearchBarModule,
                        SiSelectModule,
                        SiSidePanelModule,
                        SiSliderModule,
                        SiSortBarModule,
                        SiSplitModule,
                        SiStatusBarModule,
                        SiSwitchModule,
                        SiTabsModule,
                        SiThresholdModule,
                        SiToastNotificationModule,
                        SiTooltipModule,
                        SiTreeViewModule,
                        SiTypeaheadModule,
                        SiUnauthorizedPageModule,
                        SiWizardModule
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXIS_X, AXIS_Y, AlertDialogResult, BOOTSTRAP_BREAKPOINTS, BOUNDING_RECT_WINDOW, BlinkService, CheckboxClickEventArgs, ClickEventArgs, ClickEventArgs2, ConfirmationDialogResult, DEFAULT_CHILDREN_INDENTATION, DEFAULT_TREE_ICON_SET, DateFormat, DeleteConfirmationDialogResult, ELEMENT_THEME_NAME, EditAbortDialogResult, FolderStateEventArgs, ItemsVirtualizedArgs, LoadChildrenEventArgs, MeridianDesignation, ModalRef, PopoverComponent, ROOT_LEVEL, Required, ResizeObserverService, ResultDetailStepState, SI_DATATABLE_CONFIG, SI_LINK_DEFAULT_NAVIGATION_EXTRA, SI_LOCALE_CONFIG, SI_LOCALE_LOCAL_STORAGE_KEY, SI_LOCALE_STORE, SI_THEME_LOCAL_STORAGE_KEY, SI_TOAST_AUTO_HIDE_DELAY, SI_UI_STATES_STORAGE_KEY, STATUS_ICON, ScrollbarHelper, SiAboutComponent, SiAboutModule, SiAccordionComponent, SiAccordionHCollapseService, SiAccordionModule, SiAccordionService, SiActionDialogModule, SiActionDialogService, SiAlertDialogComponent, SiAriaCurrentDirective, SiAriaCurrentModule, SiAutoCollapsableListAdditionalContentDirective, SiAutoCollapsableListDirective, SiAutoCollapsableListItemDirective, SiAutoCollapsableListMeasurable, SiAutoCollapsableListModule, SiAutoCollapsableListOverflowItemDirective, SiBreadcrumbComponent, SiBreadcrumbModule, SiCardComponent, SiCardModule, SiCircleStatusComponent, SiCircleStatusModule, SiCollapsiblePanelComponent, SiColumnSelectionDialogComponent, SiColumnSelectionDialogService, SiColumnSelectionModalModule, SiComfortScoreComponent, SiComfortScoreModule, SiComfortScoreStatusComponent, SiCommissioningStatusComponent, SiCommissioningStatusModule, SiConfirmationDialogComponent, SiConnectionStrengthComponent, SiConnectionStrengthModule, SiContentActionBarComponent, SiContentActionBarMenuComponent, SiContentActionBarModule, SiDatatableInteractionDirective, SiDatatableModule, SiDateInputDirective, SiDateRangeComponent, SiDatepickerComponent, SiDatepickerDirective, SiDatepickerModule, SiDatepickerOverlayComponent, SiDefaultLocaleStore, SiDefaultThemeStore, SiDeleteConfirmationDialogComponent, SiDropdownContainerComponent, SiDropdownDirective, SiDropdownMenuDirective, SiDropdownModule, SiDropdownToggleDirective, SiEditAbortDialogComponent, SiElectrontitlebarComponent, SiElectrontitlebarModule, SiEmptyStateComponent, SiEmptyStateModule, SiFileDropzoneComponent, SiFileUploaderComponent, SiFileUploaderModule, SiFilterBarComponent, SiFilterBarModule, SiFilterPillComponent, SiFilteredSearchComponent, SiFilteredSearchModule, SiFooterComponent, SiFooterModule, SiFormContainerComponent, SiFormItemComponent, SiFormModule, SiIconComponent, SiIconModule, SiInlineNotificationComponent, SiInlineNotificationModule, SiLandingPageComponent, SiLandingPageModule, SiLanguageSwitcherComponent, SiLanguageSwitcherModule, SiLinkActionService, SiLinkDirective, SiLinkModule, SiListInteractionDirective, SiListInteractionItemDirective, SiListInteractionModule, SiListInteractionService, SiLoadingSpinnerComponent, SiLoadingSpinnerModule, SiLocaleId, SiLocaleService, SiLocaleStore, SiLocalizationModule, SiMasterDetailContainerComponent, SiMasterDetailContainerModule, SiMenuComponent, SiMenuItemComponent, SiMenuModule, SiModalComponent, SiModalModule, SiModalService, SiNavbarBrandComponent, SiNavbarItemComponent, SiNavbarModule, SiNavbarPrimaryComponent, SiNavbarProfileMenuComponent, SiNavbarSecondaryComponent, SiNavbarSecondaryModule, SiNavbarVerticalComponent, SiNavbarVerticalFlyoutComponent, SiNavbarVerticalItemComponent, SiNavbarVerticalModule, SiNumberInputComponent, SiNumberInputModule, SiPaginationComponent, SiPaginationModule, SiPasswordStrengthComponent, SiPasswordStrengthDirective, SiPasswordStrengthModule, SiPopoverDirective, SiPopoverModule, SiProgressbarComponent, SiProgressbarModule, SiPromptDialogButtonsComponent, SiPromptDialogButtonsModule, SiResizeObserverDirective, SiResizeObserverModule, SiResponsiveContainerDirective, SiResultDetailsListComponent, SiResultDetailsListModule, SiRightPanelComponent, SiRightPanelContentComponent, SiRightPanelModule, SiSearchBarComponent, SiSearchBarModule, SiSelectComplexOptionsDirective, SiSelectComponent, SiSelectGroupTemplateDirective, SiSelectModule, SiSelectMultiValueDirective, SiSelectOptionTemplateDirective, SiSelectSimpleOptionsDirective, SiSelectSingleValueDirective, SiSidePanelComponent, SiSidePanelContentComponent, SiSidePanelModule, SiSidePanelService, SiSliderComponent, SiSliderModule, SiSortBarComponent, SiSortBarModule, SiSplitComponent, SiSplitModule, SiSplitPartComponent, SiStatusBarComponent, SiStatusBarItemComponent, SiStatusBarModule, SiSwitchComponent, SiSwitchModule, SiTabComponent, SiTabsModule, SiTabsetComponent, SiThemeService, SiThemeStore, SiThresholdComponent, SiThresholdModule, SiTimepickerComponent, SiToastNotificationComponent, SiToastNotificationDrawerComponent, SiToastNotificationModule, SiToastNotificationService, SiTooltipDirective, SiTooltipModule, SiTranslateModule, SiTranslatePipe, SiTranslateService, SiTranslateServiceBuilder, SiTreeViewComponent, SiTreeViewConverterService, SiTreeViewItemComponent, SiTreeViewItemHeightService, SiTreeViewItemTemplateDirective, SiTreeViewModule, SiTreeViewService, SiTreeViewVirtualizationService, SiTypeaheadComponent, SiTypeaheadDirective, SiTypeaheadModule, SiUIStateService, SiUnauthorizedPageComponent, SiUnauthorizedPageModule, SiWizardComponent, SiWizardModule, SiWizardStepComponent, SimplElementNgModule, TextMeasureService, ThemeSwitcherService, TimeFormat, ToLabelPipe, TooltipComponent, addChildItem, areAnimationsDisabled, boxClicked, childrenLoaded, collapse, correctKeyRTL, deleteItem, doFolderStateChange, elementTheme, enableCheckboxRecursive, enableOptionboxRecursive, expand, expandRecursive, getBrowserCultureLanguage, getBrowserLanguage, getContentPositionString, getDatepickerFormat, hasChildren, isRTL, parentCountRecursive, removeUndefinedState, resetActive, resolveReference, responsivelyCheckDirection, runsInElectron, selectItemsBetween, selectMenuItemById, selectRecursive, setActive, setBoxStateRecursive, setSelectable };
